import React, { useState, useRef, useEffect, memo, useCallback, Fragment, useMemo } from "react";
import { createPortal } from "react-dom";
import { fetchLiveGames, getMockGames, fetchLiveScoresForPicks, mergeLiveScoresIntoGames, fetchLiveOdds, fetchGameMarkets, clearCache, getTeamLogoUrl, sportHasProps } from "./sportsApi";
import { submitPicksSecure, submitPicks, loadPicks, loadPendingPicks, loadLeaguePicks } from "./picks";
import { getUserBalance, calcPayout, applyBalanceDelta, WEEKLY_PAYDAY } from "./settlement";
import { updateUserStats, loadLeaderboard, loadUserStats } from "./leaderboard";
import { createLeague, joinLeagueByCode, loadMyLeagues, loadLeagueStandings, leaveLeague, addLeagueToUser, loadPublicLeagues, writeLeagueActivity, loadLeagueActivity, deductLeagueWager, deleteLeague, updateLeagueMemberBalance, subscribeToMemberDoc, runLeaguePaydays, restartLeague, endLeague, isLeagueEnded } from "./leagues";
import { LEAGUE_FORMAT as WC2026_FORMAT, isWorldCup2026League, TEAMS as WC_TEAMS, SCORING as WC_SCORING, KNOCKOUT_UNLOCK_DATES as ROUND_UNLOCKS, WORLD_CUP_2026, GROUP_LETTERS as WC_GROUP_LETTERS } from "./worldCup2026Config";
import { useWorldCupLiveStandings, computeGroupStageScore, computeKnockoutScore, computeGoldenBootScore, hasAnyKnockoutData } from "./worldCup2026Scoring";
import WorldCupBracket from "./WorldCupBracket";
import { requestTorchLoan, respondToTorchLoan, linkTorchLoanPick, loadIncomingTorchRequests, loadMyActiveTorchLoan, loadTorchLoanById, expireStaleLoans, cancelTorchLoan, offerTorchLoan, respondToTorchOffer, loadIncomingTorchOffers, TORCH_MIN_LOAN } from "./torchLoans";
import { registerFCMToken, notifySettlement, listenForForegroundMessages, shouldPromptForNotifications, markNotificationPromptDismissed, incrementSettlementCount, requestNotificationPermission } from "./notifications";
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  sendPasswordResetEmail,
  fetchSignInMethodsForEmail,
  deleteUser,
  reauthenticateWithCredential,
  EmailAuthProvider,
  reauthenticateWithPopup,
  OAuthProvider,
  updateProfile,
} from "firebase/auth";
import app, { auth, googleProvider, storage } from "./firebase";
import { registerIosPushNotifications } from './pushNotifications';

// ── Firebase Analytics — lightweight event logger ────────────────────────────
// Lazy-loads the analytics module so it doesn't bloat the initial bundle.
let _analytics = null;
async function logTorchEvent(eventName, params = {}) {
  try {
    if (!_analytics) {
      const { getAnalytics } = await import("firebase/analytics");
      _analytics = getAnalytics(app);
    }
    const { logEvent } = await import("firebase/analytics");
    logEvent(_analytics, eventName, params);
  } catch {} // silently fail — analytics should never break the app
}

// ── Meta Pixel (web conversion tracking) ─────────────────────────────────────
// Safe wrapper around window.fbq. No-ops if the Pixel base snippet (in
// public/index.html) hasn't loaded or was blocked, so a missing/blocked Pixel
// can never break the app. Fires standard + custom events at real success
// points (not button clicks). Web funnel only — the iOS app is not covered by
// the web Pixel (see marketing notes).
function torchFbq(eventName, params = {}, opts = {}) {
  try {
    if (typeof window === "undefined" || typeof window.fbq !== "function") return;
    // Standard events use trackSingle-style 'track'; custom events use 'trackCustom'.
    window.fbq(opts.custom ? "trackCustom" : "track", eventName, params);
  } catch {} // never let tracking break the app
}


// ── UTM ATTRIBUTION HELPERS ─────────────────────────────────────────────────
// Reads UTM data captured at landing-time by the TorchApp UTM-capture
// useEffect. Returns {} if nothing's stored, so spreading into event params
// is always safe. Used by:
//   • signup_attributed event (full UTM payload + capture→signup delta)
//   • existing sign_up events (utm_source only, for method×source breakdowns)
//   • invite_shared event (so shares from UTM'd sessions can be back-traced)
// Source of truth is localStorage under "torch_utm" — see capture comment.
function readStoredUtm() {
  try {
    const raw = localStorage.getItem("torch_utm");
    if (!raw) return {};
    const data = JSON.parse(raw);
    return data && typeof data === "object" ? data : {};
  } catch { return {}; }
}

// Normalise loadPicks() result — handles both array and {id:data} object formats
function normalizePicks(result) {
  if (!result) return [];
  if (Array.isArray(result)) return result;
  return Object.entries(result).map(([id, p]) => ({ id, ...p }));
}

// ── Haptic feedback helper ───────────────────────────────────────────────────
// Lightweight vibration for key interactions. Uses navigator.vibrate (Android)
// and will use Capacitor Haptics plugin when the native shell is added.
// Falls back to no-op gracefully on browsers that don't support it.
// ── useCountUp ──────────────────────────────────────────────────────────────
// Smoothly animates a number from its previous value to a target value over
// `duration` ms. Returns a number you can render directly. Skips the animation
// on first render (so initial page loads don't visibly count up from 0 — that
// would feel theatrical/Vegas, not tactile).
//
// Use case: balance updates after wagering/winning. The snap from 900 → 800
// reads as a system event ("the database changed"). The 400ms count-down reads
// as the points being deducted ("you spent them"). Subtle but profound.
function useCountUp(target, duration = 450) {
  const safeTarget = Number.isFinite(target) ? target : 0;
  const [value, setValue] = React.useState(safeTarget);
  const fromRef = React.useRef(safeTarget);
  const isFirstRef = React.useRef(true);

  React.useEffect(() => {
    if (isFirstRef.current) {
      isFirstRef.current = false;
      fromRef.current = safeTarget;
      setValue(safeTarget);
      return;
    }
    const from = fromRef.current;
    const to = safeTarget;
    if (from === to) return;
    const start = performance.now();
    let raf;
    const tick = (now) => {
      const t = Math.min(1, (now - start) / duration);
      // easeOutQuart — fast start, gentle settle. Feels snappier than easeOutCubic.
      const eased = 1 - Math.pow(1 - t, 4);
      const cur = Math.round(from + (to - from) * eased);
      setValue(cur);
      if (t < 1) raf = requestAnimationFrame(tick);
      else fromRef.current = to;
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [safeTarget, duration]);

  return value;
}

// Tiny wrapper — `<AnimatedNumber value={balance} />` renders the toLocaleString'd
// number with smooth count-up animation between value changes. Default for cases
// where you just want the number to feel tactile when it updates.
function AnimatedNumber({ value, duration = 450 }) {
  const v = useCountUp(value, duration);
  return v.toLocaleString();
}

// `<AnimatedFromZero value={95} />` — starts at 0 and counts up to value on FIRST
// render. Use this for one-shot reveal moments (e.g., "you just won 95 pts" pick
// card animation). Different from AnimatedNumber, which skips first render so it
// doesn't feel theatrical on every page load.
function AnimatedFromZero({ value, duration = 650 }) {
  const safe = Number.isFinite(value) ? value : 0;
  const [val, setVal] = React.useState(0);
  React.useEffect(() => {
    if (safe === 0) { setVal(0); return; }
    const start = performance.now();
    let raf;
    const tick = (now) => {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 4);  // easeOutQuart
      setVal(Math.round(safe * eased));
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [safe, duration]);
  return val.toLocaleString();
}


const haptic = {
  light:  () => { try { navigator.vibrate?.(10); } catch {} },
  medium: () => { try { navigator.vibrate?.(20); } catch {} },
  heavy:  () => { try { navigator.vibrate?.(35); } catch {} },
  success:() => { try { navigator.vibrate?.([10, 50, 20]); } catch {} },
  error:  () => { try { navigator.vibrate?.([30, 100, 30]); } catch {} },
};

// ── tsToDate ───────────────────────────────────────────────────────────────
// Convert anything-that-might-be-a-timestamp into a native Date (or null).
// Handles: Firestore Timestamp instances (toDate method), serialized
// Timestamp shapes ({seconds, nanoseconds} or {_seconds, _nanoseconds}),
// existing Date objects, ISO strings, and epoch milliseconds.
//
// ⚠️ Why this exists: `new Date(firestoreTimestamp)` returns Invalid Date
// (silently). Math on Invalid Date returns NaN, comparisons evaluate false,
// and guards that look right become no-ops. We hit this on the server with
// the v1 settlement date-drift fix and it bit again on the client
// `canHopByState` gate -- "game already started" was always false because
// publicPicks.commenceTime is a Timestamp.
//
// Use this whenever pick.commenceTime / createdAt / submittedAt / settledAt
// arithmetic is involved -- never `new Date(field)` directly on Firestore-
// sourced data.
function tsToDate(ts) {
  if (!ts) return null;
  if (ts instanceof Date) return isNaN(ts) ? null : ts;
  if (typeof ts.toDate === "function") {
    try { const d = ts.toDate(); return isNaN(d) ? null : d; } catch { return null; }
  }
  if (typeof ts.seconds === "number") return new Date(ts.seconds * 1000);
  if (typeof ts._seconds === "number") return new Date(ts._seconds * 1000);
  if (typeof ts === "number") { const d = new Date(ts); return isNaN(d) ? null : d; }
  if (typeof ts === "string") { const d = new Date(ts); return isNaN(d) ? null : d; }
  return null;
}

// Numeric-millis companion to tsToDate. Returns a millisecond timestamp or null.
// IMPORTANT: this exists to kill a recurring precedence bug. Expressions like
//   x?.toMillis?.() ?? x?.seconds * 1000 ?? 0
// are WRONG: `*` binds tighter than `??`, so when x lacks `.seconds`,
// `undefined * 1000` is NaN, and `NaN ?? 0` is NaN (NaN isn't null/undefined),
// so the `?? 0` fallback never fires. That NaN then silently corrupts any sort
// comparator it feeds (every comparison with NaN is false → unstable ordering).
// Always route timestamp→millis through this helper instead.
function tsToMillis(ts) {
  const d = tsToDate(ts);
  return d ? d.getTime() : null;
}

// ── Knockout prediction shape helpers ─────────────────────────────────────────
// Phase 2B: predictions.knockouts[round] is stored as an OBJECT keyed by
// matchSlot string, e.g. { "R32-1": "MEX", "R32-2": "BRA" } — NOT an array.
// This matches the writer in WorldCupBracket.jsx and the scorer in
// worldCup2026Scoring.js. All readers in this file must count object values,
// not array elements. (A previous version assumed arrays, which silently broke
// completion detection + member-card display once R32 unlocked.)
//
// Ordered slot IDs per round, used to render winners in bracket order.
const KO_SLOT_IDS = {
  R32: Array.from({ length: 16 }, (_, i) => `R32-${i + 1}`),
  R16: Array.from({ length: 8 },  (_, i) => `R16-${i + 1}`),
  QF:  Array.from({ length: 4 },  (_, i) => `QF-${i + 1}`),
  SF:  Array.from({ length: 2 },  (_, i) => `SF-${i + 1}`),
  FINAL: ["FINAL-1"],
};
// Count how many slots in a round have a truthy string pick.
function koRoundFilledCount(roundObj) {
  if (!roundObj || typeof roundObj !== "object") return 0;
  let n = 0;
  for (const v of Object.values(roundObj)) {
    if (typeof v === "string" && v.trim().length > 0) n++;
  }
  return n;
}
// Return the round's winners as an ordered array of team codes (slot order).
// Empty/unfilled slots are dropped, so the result length === filled count.
function koRoundCodes(roundObj, round) {
  if (!roundObj || typeof roundObj !== "object") return [];
  const ids = KO_SLOT_IDS[round] || Object.keys(roundObj);
  return ids.map((slot) => roundObj[slot]).filter((c) => typeof c === "string" && c.trim().length > 0);
}
// True if any knockout round has at least one pick.
function koHasAnyPicks(koObj) {
  if (!koObj || typeof koObj !== "object") return false;
  return Object.values(koObj).some((r) => koRoundFilledCount(r) > 0);
}

// ── useModalOpen ──────────────────────────────────────────────────────────────
// Tracks how many modals are currently open (refcount) and toggles a body class
// `modal-open` whenever count > 0. The BottomNav reads this class and slides
// itself off-screen, so bottom-anchored sheets never get clipped by the nav.
//
// Why a refcount: nested modals (e.g. confirm dialog over a sheet) — when the
// inner one closes, the nav must stay hidden until the outer one closes too.
//
// Why this exists: previously every bottom sheet had to manually pad itself for
// the nav (calc(80px + safe-area-inset-bottom)). Easy to forget on new sheets.
// This pattern follows iOS/Android native — when a sheet opens, the tab bar
// gets out of the way.
let __modalOpenCount = 0;
// ── HIDDEN-PAGE FREEZE (Aug 29, 2026) ───────────────────────────────────────
// The mount-once router keeps every visited page mounted, which made tab
// switches free — but it also meant EVERY App-level state change (e.g. an
// odds tap mutating `picks`) reconciled all visited page trees, none of them
// memoized, on the main thread. On WKWebView that's the multi-frame "blink"
// when selecting a spread. This wrapper freezes a page while it's hidden:
// during an active render it caches the element; while inactive it returns
// that SAME cached element, so React bails out of the entire subtree by
// reference identity. The mounted component keeps its state, effects, and
// listeners — it just stops receiving new props until it's active again,
// at which point it re-renders with fresh ones. Net effect: an odds tap
// reconciles ONE page instead of five.
// Behavior note: prop-driven refreshes aimed at a hidden page (e.g.
// picksRefreshKey) now land when the user opens that tab, not while it's
// invisible — deferred, not lost.
function StaticWhenHidden({ active, children }) {
  const lastRef = useRef(null);
  if (active) lastRef.current = children;
  return lastRef.current;
}

// ── SHEET EXIT SYSTEM (Aug 29, 2026) ────────────────────────────────────────
// Every modal animated IN and closed by instant unmount — a single-frame
// teardown of a blurred backdrop that reads as a flash (the bug fixed
// bespoke in PickSlipDrawer Aug 28, found in 16 more components on audit).
// This hook is that fix, reusable: requestClose plays a short CSS exit
// (drive backdrop/panel `animation` off `closing`), then calls the real
// onClose. Route EVERY close trigger (backdrop tap, X, escape, post-action
// callbacks) through requestClose — a direct onClose call still teleports.
// Motion vocabulary: entries 0.26s cubic-bezier(0.32,0.72,0,1), exits 0.2s
// cubic-bezier(0.4,0,1,1) both, backdrops slipFadeOut 0.18s.
// For CONDITIONALLY MOUNTED modals only — unmount at onClose resets the
// guard. A persistent singleton (ConfirmDialog) needs the self-resetting
// inline variant instead (see that component).
function useSheetClose(onClose, duration = 200) {
  const [closing, setClosing] = useState(false);
  const closingRef = useRef(false);
  const requestClose = React.useCallback((after) => {
    if (closingRef.current) return;
    closingRef.current = true;
    setClosing(true);
    setTimeout(() => {
      onClose?.();
      if (typeof after === "function") after();
    }, duration);
  }, [onClose, duration]);
  return { closing, requestClose };
}

function useModalOpen(open) {
  React.useEffect(() => {
    if (!open) return;
    __modalOpenCount++;
    document.body.classList.add("modal-open");
    return () => {
      __modalOpenCount = Math.max(0, __modalOpenCount - 1);
      if (__modalOpenCount === 0) document.body.classList.remove("modal-open");
    };
  }, [open]);
}

// ── WC2026 Member-Brackets Helpers + UI ──────────────────────────────────────
// Renders the "Brackets" tab in WC bracket-pool league rooms — a roster view
// that surfaces every league member's predictions ONCE THEIR BRACKET IS LOCKED.
// In-progress members are shown as locked placeholders ("hidden until they
// finish picking") so late deciders can't copy the leader's picks.
//
// Lives at module scope (not nested in TorchApp) so the components can be
// reused if we ever build a "compare brackets" view or share-out flow later.

// Extracts the headline picks from a predictions doc. Returns null fields
// for anything not yet locked, so the card can degrade gracefully.
//
// Phase-aware: "complete" status fires when everything CURRENTLY LOCKABLE
// is locked. Pre-tournament that's groups + boot + tiebreaker. Post-R32
// unlock that's also R32 winners. Etc. Mirrors WorldCupBracket's overallProgress.
function summarizeMemberBracket(predictions) {
  if (!predictions) return { status: "none", groupsCount: 0 };

  const groupsObj = predictions.groups || {};
  const groupsCount = ["A","B","C","D","E","F","G","H","I","J","K","L"].filter(L =>
    Array.isArray(groupsObj[L]) && groupsObj[L].length === 4
  ).length;

  const koObj = predictions.knockouts || {};
  const koSlots = { R32: 16, R16: 8, QF: 4, SF: 2, FINAL: 1 };
  // Same phase-aware check as getBracketCompletionStatus — only require
  // picks for rounds that have actually unlocked. Knockouts are OBJECTS keyed
  // by matchSlot, so count filled slots, not array length.
  const now = new Date();
  const unlockedRounds = ["R32","R16","QF","SF","FINAL"].filter(r => {
    const ts = ROUND_UNLOCKS[r];
    return ts ? now >= new Date(ts) : false;
  });
  const koDone = unlockedRounds.every((r) =>
    koRoundFilledCount(koObj[r]) === koSlots[r]
  );
  const gbDone = typeof predictions.goldenBoot === "string" && predictions.goldenBoot.trim().length > 0;
  const tbDone = Number.isFinite(Number(predictions.totalGoals)) && Number(predictions.totalGoals) > 0;
  const fullyLocked = groupsCount === 12 && koDone && gbDone && tbDone;

  // Headline picks (only meaningful when fully locked; we still surface what
  // we have so the in-progress / hidden states render right). Pre-tournament
  // championCode/runnerUpCode will be null since FINAL is locked.
  // Knockouts are objects keyed by matchSlot: champion = FINAL["FINAL-1"],
  // SF winners = SF["SF-1"] / SF["SF-2"], runner-up = the SF winner who isn't
  // the champion.
  const championCode = (koObj.FINAL && typeof koObj.FINAL["FINAL-1"] === "string" && koObj.FINAL["FINAL-1"]) || null;
  let runnerUpCode = null;
  if (championCode && koObj.SF && typeof koObj.SF === "object") {
    const sfWinners = ["SF-1", "SF-2"].map((s) => koObj.SF[s]).filter(Boolean);
    runnerUpCode = sfWinners.find((c) => c && c !== championCode) || null;
  }

  return {
    status: fullyLocked ? "complete" : (groupsCount > 0 || koHasAnyPicks(koObj) || gbDone || tbDone ? "in_progress" : "none"),
    groupsCount,
    championCode,
    runnerUpCode,
    goldenBoot: gbDone ? predictions.goldenBoot.trim() : null,
    totalGoals: tbDone ? Number(predictions.totalGoals) : null,
    fullyLocked,
  };
}

// ── Cross-platform flag rendering ─────────────────────────────────────────
// Country flag emojis (🇲🇽, 🇧🇷, etc.) are built from regional-indicator code
// points (e.g. 🇲🇽 = U+1F1F2 + U+1F1FD = "MX"). iOS, macOS, and Android ship
// emoji fonts that render these as actual flags. Most Windows browsers and
// some Linux desktops do NOT, and instead show the raw two-letter pair
// (e.g. "mx", "br") as text fallback.
//
// To get consistent flags on all platforms, we render via flagcdn.com SVG
// images instead of relying on the host emoji font. The flagcdn URL is built
// by decoding the emoji back to its ISO 3166-1 alpha-2 letters.
//
// Two edge cases: England and Scotland use Unicode TAG sequences (not the
// standard regional-indicator pair) since they're sub-national. flagcdn has
// dedicated `gb-eng` and `gb-sct` paths for these.
const FLAG_ISO_OVERRIDES = { ENG: "gb-eng", SCO: "gb-sct", WAL: "gb-wls", NIR: "gb-nir" };
function teamFlagSrc(team) {
  if (!team) return null;
  if (FLAG_ISO_OVERRIDES[team.abbr]) {
    return `https://flagcdn.com/h20/${FLAG_ISO_OVERRIDES[team.abbr]}.png`;
  }
  if (team.flag) {
    try {
      const cps = [...team.flag].map(c => c.codePointAt(0));
      if (cps.length === 2 && cps.every(cp => cp >= 0x1F1E6 && cp <= 0x1F1FF)) {
        const iso = cps.map(cp => String.fromCharCode(cp - 0x1F1E6 + 65)).join("").toLowerCase();
        return `https://flagcdn.com/h20/${iso}.png`;
      }
    } catch {}
  }
  return null;
}
function TeamFlag({ team, size = 16, style = {} }) {
  const src = teamFlagSrc(team);
  if (!src) return null;
  // 4:3 is the flagcdn normalized aspect ratio.
  return (
    <img
      src={src}
      alt=""
      width={Math.round(size * 4 / 3)}
      height={size}
      loading="lazy"
      decoding="async"
      style={{
        display: "inline-block", verticalAlign: "middle",
        borderRadius: 2, marginRight: 5,
        ...style,
      }}
    />
  );
}

// Single member card. Three render paths:
//   complete    → champion flag + name + final + boot + goals (tappable)
//   in_progress → locked placeholder + progress count
//   none        → "Hasn't started yet" placeholder
function MemberBracketCard({ member, summary, isMe, onTap }) {
  const champion = summary.championCode ? WC_TEAMS[summary.championCode] : null;
  const runnerUp = summary.runnerUpCode ? WC_TEAMS[summary.runnerUpCode] : null;

  // Hidden state — bracket exists but isn't fully locked. Critical for fairness:
  // late deciders shouldn't be able to copy the early lockers' picks.
  if (summary.status !== "complete") {
    return (
      <div style={{
        padding:"14px 16px", marginBottom:10,
        background:"rgba(255,255,255,0.02)",
        border:`1px solid ${T.border}`,
        borderRadius:14,
        display:"flex", alignItems:"center", gap:14,
        opacity:0.85,
      }}>
        <div style={{
          width:48, height:48, borderRadius:14, flexShrink:0,
          background:"rgba(255,255,255,0.04)",
          border:`1px solid ${T.border}`,
          display:"flex", alignItems:"center", justifyContent:"center",
          fontSize:22, color:"rgba(255,255,255,0.40)",
        }}>🔒</div>
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{
            fontSize:14, fontWeight:700, color:"#fff",
            display:"flex", alignItems:"center", gap:6,
            marginBottom:2,
          }}>
            <span style={{
              whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis",
            }}>{member.username || "Unknown"}</span>
            {isMe && (
              <span style={{
                fontSize:9, fontWeight:800, color:T.coral,
                background:T.coralDim, padding:"2px 5px", borderRadius:4,
                letterSpacing:"0.06em", textTransform:"uppercase",
              }}>You</span>
            )}
          </div>
          <div style={{ fontSize:11.5, color:"rgba(255,255,255,0.45)", lineHeight:1.4 }}>
            {summary.status === "none"
              ? "Hasn't picked yet · waiting on them"
              : `Bracket in progress · ${summary.groupsCount}/12 groups · hidden until locked`}
          </div>
        </div>
      </div>
    );
  }

  // Complete state — show champion + summary, tappable for full details.
  return (
    <button
      onClick={onTap}
      className="no-press"
      style={{
        width:"100%", padding:"14px 16px", marginBottom:10,
        background:"rgba(20,184,166,0.04)",
        border:`1px solid rgba(20,184,166,0.18)`,
        borderRadius:14, cursor:"pointer", textAlign:"left",
        display:"flex", alignItems:"center", gap:14,
        transition:"background 0.15s, transform 0.1s",
      }}
      onTouchStart={(e) => { e.currentTarget.style.transform="scale(0.99)"; }}
      onTouchEnd={(e)   => { e.currentTarget.style.transform="scale(1)"; }}
      onMouseLeave={(e) => { e.currentTarget.style.transform="scale(1)"; }}
    >
      {/* Champion flag — the visual anchor */}
      <div style={{
        width:54, height:54, borderRadius:14, flexShrink:0,
        background:"rgba(229,180,89,0.06)",
        border:"1px solid rgba(229,180,89,0.20)",
        display:"flex", alignItems:"center", justifyContent:"center",
        overflow:"hidden",
      }}>
        {champion
          ? <TeamFlag team={champion} size={32} style={{ marginRight:0 }} />
          : (
            <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="#E5B459" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
              <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6"/>
              <path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18"/>
              <path d="M4 22h16"/>
              <path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22"/>
              <path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22"/>
              <path d="M18 2H6v7a6 6 0 0 0 12 0V2Z"/>
            </svg>
          )}
      </div>

      {/* Name + summary */}
      <div style={{ flex:1, minWidth:0 }}>
        <div style={{
          fontSize:14, fontWeight:700, color:"#fff",
          display:"flex", alignItems:"center", gap:6,
          marginBottom:3,
        }}>
          <span style={{
            whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis",
          }}>{member.username || "Unknown"}</span>
          {isMe && (
            <span style={{
              fontSize:9, fontWeight:800, color:T.coral,
              background:T.coralDim, padding:"2px 5px", borderRadius:4,
              letterSpacing:"0.06em", textTransform:"uppercase",
            }}>You</span>
          )}
        </div>
        <div style={{
          fontSize:11.5, color:"#E5B459", fontWeight:600,
          marginBottom:3, letterSpacing:"-0.005em",
          whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis",
        }}>
          {champion ? (
            <>
              {champion.name}
              {runnerUp && <span style={{ color:"rgba(255,255,255,0.45)", fontWeight:500 }}> · vs <TeamFlag team={runnerUp} size={12} />{runnerUp.name}</span>}
            </>
          ) : (
            // Pre-tournament: no champion to surface (FINAL is locked).
            // Lead with the commitment they HAVE made — full group placements.
            <>12/12 groups locked</>
          )}
        </div>
        <div style={{
          fontSize:10.5, color:"rgba(255,255,255,0.42)",
          whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis",
        }}>
          <span style={{ color:"rgba(255,255,255,0.42)", fontWeight:700, marginRight:4, letterSpacing:"0.06em" }}>BOOT</span>{summary.goldenBoot || "—"}
          <span style={{ color:"rgba(255,255,255,0.32)" }}>  ·  </span>
          <span style={{ color:"rgba(255,255,255,0.42)", fontWeight:700, marginRight:4, letterSpacing:"0.06em" }}>FINAL</span>{summary.totalGoals ?? "—"} goals
        </div>
      </div>

      {/* Tap chevron */}
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.25)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink:0 }}>
        <polyline points="9 18 15 12 9 6"/>
      </svg>
    </button>
  );
}

// Read-only modal showing one member's full bracket. Bottom-sheet style,
// matches the rest of the WC modal aesthetic.
function MemberBracketDetailsModal({ open, onClose, member, summary, predictions }) {
  useModalOpen(open);
  if (!open || !member) return null;
  const champion = summary.championCode ? WC_TEAMS[summary.championCode] : null;

  // Defensive helper — Firestore can return arrays back as sparse objects,
  // and `|| []` only handles null/undefined, not truthy non-arrays. Use this
  // anywhere we expect a stored array but can't trust the runtime type.
  const safeArr = (v) => Array.isArray(v) ? v : [];

  // Build the 12 group rows from predictions.groups
  const groupRows = ["A","B","C","D","E","F","G","H","I","J","K","L"].map((L) => {
    const ranking = safeArr(predictions?.groups?.[L]);
    return { letter: L, teams: ranking.map((c) => WC_TEAMS[c]).filter(Boolean) };
  });

  // Build knockout winners list (round-by-round, just the winners).
  // Knockouts are objects keyed by matchSlot — read in slot order.
  const koObj = predictions?.knockouts || {};
  const koDisplay = [
    { round: "R32",   label: "Round of 32",   winners: koRoundCodes(koObj.R32,   "R32"  ).map((c) => WC_TEAMS[c]).filter(Boolean) },
    { round: "R16",   label: "Round of 16",   winners: koRoundCodes(koObj.R16,   "R16"  ).map((c) => WC_TEAMS[c]).filter(Boolean) },
    { round: "QF",    label: "Quarterfinals", winners: koRoundCodes(koObj.QF,    "QF"   ).map((c) => WC_TEAMS[c]).filter(Boolean) },
    { round: "SF",    label: "Semifinals",    winners: koRoundCodes(koObj.SF,    "SF"   ).map((c) => WC_TEAMS[c]).filter(Boolean) },
    { round: "FINAL", label: "Champion",      winners: koRoundCodes(koObj.FINAL, "FINAL").map((c) => WC_TEAMS[c]).filter(Boolean) },
  ];

  return (
    <div
      onClick={onClose}
      style={{
        position:"fixed", inset:0, zIndex:9999,
        background:"rgba(0,0,0,0.65)",
        display:"flex", alignItems:"flex-end",
        WebkitBackdropFilter:"blur(8px)", backdropFilter:"blur(8px)",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width:"100%", maxWidth:640, margin:"0 auto",
          maxHeight:"calc(100vh - env(safe-area-inset-top, 0px) - 24px)",
          background:T.card,
          borderTop:`1px solid ${T.borderHi}`,
          borderRadius:"20px 20px 0 0",
          display:"flex", flexDirection:"column",
        }}
      >
        {/* Sticky header */}
        <div style={{
          flexShrink:0, padding:"10px 18px 14px",
          borderBottom:`1px solid ${T.border}`,
        }}>
          <div style={{ width:40, height:4, borderRadius:4, background:T.borderHi, margin:"0 auto 14px" }}/>
          <div style={{ display:"flex", alignItems:"center", gap:14 }}>
            <div style={{
              width:54, height:54, borderRadius:14, flexShrink:0,
              background:"rgba(229,180,89,0.06)",
              border:"1px solid rgba(229,180,89,0.20)",
              display:"flex", alignItems:"center", justifyContent:"center",
              overflow:"hidden",
            }}>
              {champion
                ? <TeamFlag team={champion} size={32} style={{ marginRight:0 }} />
                : (
                  <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="#E5B459" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6"/>
                    <path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18"/>
                    <path d="M4 22h16"/>
                    <path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22"/>
                    <path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22"/>
                    <path d="M18 2H6v7a6 6 0 0 0 12 0V2Z"/>
                  </svg>
                )}
            </div>
            <div style={{ flex:1, minWidth:0 }}>
              <div style={{
                fontSize:9.5, fontWeight:800, color:"#E5B459",
                letterSpacing:"0.16em", textTransform:"uppercase",
                marginBottom:3,
              }}>{member.username}'s Bracket</div>
              <div style={{
                fontSize:18, fontWeight:800, color:"#fff",
                letterSpacing:"-0.01em", lineHeight:1.1,
                whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis",
              }}>{champion ? `${champion.name} to win it all` : "Locked in"}</div>
            </div>
            <button
              aria-label="Close"
              onClick={onClose}
              style={{
                width:32, height:32, borderRadius:10, flexShrink:0,
                background:"rgba(255,255,255,0.04)",
                border:`1px solid ${T.border}`,
                color:"#fff", fontSize:18, lineHeight:1,
                cursor:"pointer",
                display:"flex", alignItems:"center", justifyContent:"center",
              }}
            >×</button>
          </div>
        </div>

        {/* Scrollable body */}
        <div style={{
          flex:1, minHeight:0, overflowY:"auto",
          WebkitOverflowScrolling:"touch",
          padding:"16px 18px 20px",
        }}>
          {/* Headline strip */}
          <div style={{
            display:"grid", gridTemplateColumns:"1fr 1fr", gap:8,
            marginBottom:18,
          }}>
            <div style={{
              padding:"10px 12px", borderRadius:11,
              background:"rgba(255,255,255,0.025)",
              border:`1px solid ${T.border}`,
            }}>
              <div style={{ fontSize:9.5, fontWeight:800, color:"rgba(255,255,255,0.45)", letterSpacing:"0.10em", marginBottom:4 }}>GOLDEN BOOT</div>
              <div style={{ fontSize:13, fontWeight:700, color:"#fff", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{summary.goldenBoot || "—"}</div>
            </div>
            <div style={{
              padding:"10px 12px", borderRadius:11,
              background:"rgba(255,255,255,0.025)",
              border:`1px solid ${T.border}`,
            }}>
              <div style={{ fontSize:9.5, fontWeight:800, color:"rgba(255,255,255,0.45)", letterSpacing:"0.10em", marginBottom:4 }}>FINAL GOALS</div>
              <div style={{ fontSize:13, fontWeight:700, color:"#fff" }}>{summary.totalGoals ?? "—"}</div>
            </div>
          </div>

          {/* Knockout winners — quick scan. Pre-tournament every row would be
              an em-dash (real matchups aren't known until R32 unlocks Jun 28,
              so no one can pick), which made the modal look like it failed to
              load. Replace the empty table with a single explanatory chip.
              Once any knockout round has picks, fall back to the row layout. */}
          <div style={{
            fontSize:9.5, fontWeight:800, color:"rgba(255,255,255,0.45)",
            letterSpacing:"0.16em", textTransform:"uppercase",
            marginBottom:8,
          }}>Knockout Path</div>
          {(() => {
            const hasAnyKo = koDisplay.some(r => r.winners.length > 0);
            if (!hasAnyKo) {
              return (
                <div style={{
                  marginBottom:18,
                  padding:"12px 14px", borderRadius:10,
                  background:"rgba(20,184,166,0.04)",
                  border:`1px solid rgba(20,184,166,0.18)`,
                  fontSize:11, color:"rgba(255,255,255,0.55)", lineHeight:1.5,
                }}>
                  Knockout picks unlock as each round's matchups are decided. Round of 32 opens June 28.
                </div>
              );
            }
            return (
              <div style={{ marginBottom:18 }}>
                {koDisplay.map((row) => (
                  <div key={row.round} style={{
                    display:"flex", alignItems:"flex-start", gap:10, padding:"8px 0",
                    borderBottom:`1px solid rgba(255,255,255,0.04)`,
                  }}>
                    <div style={{
                      flexShrink:0, fontSize:10, fontWeight:800, color:"rgba(255,255,255,0.62)",
                      letterSpacing:"0.06em", textTransform:"uppercase", width:90, paddingTop:1,
                    }}>{row.label}</div>
                    <div style={{ flex:1, fontSize:12, color:"#fff", lineHeight:1.5 }}>
                      {row.winners.length === 0
                        ? <span style={{ color:"rgba(255,255,255,0.42)" }}>—</span>
                        : row.winners.map((t, i) => (
                            <span key={t.abbr} style={{ marginRight: i < row.winners.length - 1 ? 10 : 0 }}>
                              <TeamFlag team={t} size={14} />{t.abbr}
                            </span>
                          ))}
                    </div>
                  </div>
                ))}
              </div>
            );
          })()}

          {/* Group placements */}
          <div style={{
            fontSize:9.5, fontWeight:800, color:"rgba(255,255,255,0.45)",
            letterSpacing:"0.16em", textTransform:"uppercase",
            marginBottom:8,
          }}>Group Stage</div>
          <div>
            {groupRows.map((row) => (
              <div key={row.letter} style={{
                display:"flex", alignItems:"flex-start", gap:10, padding:"8px 0",
                borderBottom:`1px solid rgba(255,255,255,0.04)`,
              }}>
                <div style={{
                  flexShrink:0, fontSize:11, fontWeight:800, color:"#E5B459",
                  letterSpacing:"0.04em", width:30, paddingTop:1,
                }}>{row.letter}</div>
                <div style={{ flex:1, fontSize:11.5, color:"#fff", lineHeight:1.5 }}>
                  {row.teams.length === 0
                    ? <span style={{ color:"rgba(255,255,255,0.42)" }}>—</span>
                    : row.teams.map((t, i) => (
                        <span key={t.abbr}>
                          <span style={{ color:"rgba(255,255,255,0.45)", fontWeight:600, marginRight:3 }}>{i + 1}.</span>
                          <span><TeamFlag team={t} size={13} />{t.abbr}</span>
                          {i < row.teams.length - 1 && <span style={{ color:"rgba(255,255,255,0.36)", margin:"0 6px" }}>·</span>}
                        </span>
                      ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Error Boundary ─────────────────────────────────────────────────────────────
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, info) {
    console.error('[Torch] Unhandled error:', error, info?.componentStack);
  }
  render() {
    if (!this.state.hasError) return this.props.children;
    return (
      <div style={{
        minHeight:'100vh', display:'flex', flexDirection:'column',
        alignItems:'center', justifyContent:'center',
        background:'#0D0E12', color:'#F2F3F7',
        padding:'32px 24px', textAlign:'center', fontFamily:FB,
      }}>
        <div style={{ marginBottom:20 }}>
          <svg width="52" height="52" viewBox="0 0 24 24">
            <defs><filter id="errGlow" x="-50%" y="-50%" width="200%" height="200%"><feGaussianBlur stdDeviation="2.5" result="b"/><feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge></filter>
            <linearGradient id="errFlame" x1="0%" y1="100%" x2="50%" y2="0%"><stop offset="0%" stopColor="#ff5500"/><stop offset="100%" stopColor="#fbbf24"/></linearGradient></defs>
            <path d="M12 2c.5 4-3 6-3 10a5 5 0 0 0 8.5 3.5c.5-.5-1 2-4.5 4.5-2-1-3.5-3-3.5-5.5 0-2.5 2-4 2-6s-1.5-4 .5-6.5z" fill="url(#errFlame)" fillOpacity="0.3" stroke="url(#errFlame)" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" filter="url(#errGlow)"/>
          </svg>
        </div>
        <div style={{ fontSize:22, fontWeight:700, marginBottom:10, letterSpacing:'-0.3px' }}>
          Something went sideways
        </div>
        <div style={{ fontSize:14, color:'rgba(255,255,255,0.45)', lineHeight:1.7, maxWidth:280, marginBottom:32 }}>
          Torch hit an unexpected error. Your picks and balance are safe — this is just a display glitch.
        </div>
        <button
          onClick={() => window.location.reload()}
          style={{
            padding:'13px 28px', borderRadius:12, border:'none', cursor:'pointer',
            background:'linear-gradient(135deg,#FF6B3D,#E05522)',
            color:'#fff', fontSize:15, fontWeight:700,
            boxShadow:'0 6px 20px rgba(255,107,61,0.35)',
          }}
        >
          Reload Torch
        </button>
        {this.state.error && (
          <pre style={{
            marginTop:24, fontSize:11, color:'rgba(255,255,255,0.2)',
            textAlign:'left', maxWidth:360, overflow:'auto',
            whiteSpace:'pre-wrap', wordBreak:'break-word',
          }}>
            {this.state.error.toString()}
          </pre>
        )}
      </div>
    );
  }
}

// ── UGC Moderation — Apple Guideline 1.2 compliance ─────────────────────────

// Basic profanity / objectionable content filter
const BLOCKED_WORDS = [
  "fuck","shit","bitch","nigger","nigga","faggot","retard","cunt","dick","asshole",
  "whore","slut","cock","pussy","bastard","damn","piss","kike","spic","chink","wetback",
  "tranny","kill yourself","kys","die","rape",
];
const BLOCKED_REGEX = new RegExp(
  BLOCKED_WORDS.map(w => w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join("|"), "gi"
);
function containsObjectionableContent(text) {
  if (!text || typeof text !== "string") return false;
  return BLOCKED_REGEX.test(text);
}
function filterObjectionableContent(text) {
  if (!text || typeof text !== "string") return text;
  return text.replace(BLOCKED_REGEX, m => "★".repeat(m.length));
}

// Report / flag content — writes to Firestore "reports" collection
async function reportContent({ reporterUid, reporterName, contentId, contentType, contentText, reportedUserId, reportedUsername, leagueId, reason }) {
  try {
    const { getFirestore, collection, addDoc, serverTimestamp } = await import("firebase/firestore");
    const db = getFirestore(app);
    await addDoc(collection(db, "reports"), {
      reporterUid, reporterName, contentId, contentType, contentText: (contentText || "").slice(0, 500),
      reportedUserId, reportedUsername, leagueId, reason,
      status: "pending", createdAt: serverTimestamp(),
    });
    return true;
  } catch (e) { console.error("[Torch] Report failed:", e); return false; }
}

// Block user — writes to Firestore "users/{uid}/blockedUsers/{blockedUid}"
async function blockUser(currentUid, blockedUid, blockedUsername) {
  try {
    const { getFirestore, doc, setDoc, serverTimestamp } = await import("firebase/firestore");
    const db = getFirestore(app);
    await setDoc(doc(db, "users", currentUid, "blockedUsers", blockedUid), {
      username: blockedUsername, blockedAt: serverTimestamp(),
    });
    // Also file a report to notify developer
    await reportContent({
      reporterUid: currentUid, reporterName: "", contentId: blockedUid,
      contentType: "block_user", contentText: `Blocked user: ${blockedUsername}`,
      reportedUserId: blockedUid, reportedUsername: blockedUsername,
      leagueId: "", reason: "User blocked",
    });
    return true;
  } catch (e) { console.error("[Torch] Block failed:", e); return false; }
}

// Unblock user
async function unblockUser(currentUid, blockedUid) {
  try {
    const { getFirestore, doc, deleteDoc } = await import("firebase/firestore");
    const db = getFirestore(app);
    await deleteDoc(doc(db, "users", currentUid, "blockedUsers", blockedUid));
    return true;
  } catch (e) { console.error("[Torch] Unblock failed:", e); return false; }
}

// Load blocked user list
async function loadBlockedUsers(uid) {
  try {
    const { getFirestore, collection, getDocs } = await import("firebase/firestore");
    const db = getFirestore(app);
    const snap = await getDocs(collection(db, "users", uid, "blockedUsers"));
    const blocked = {};
    snap.forEach(d => { blocked[d.id] = d.data(); });
    return blocked;
  } catch { return {}; }
}

// EULA acceptance — stores in Firestore users/{uid} + localStorage
async function acceptEula(uid) {
  try {
    const { getFirestore, doc, updateDoc, serverTimestamp } = await import("firebase/firestore");
    const db = getFirestore(app);
    await updateDoc(doc(db, "users", uid), { eulaAccepted: true, eulaAcceptedAt: serverTimestamp() });
    try { localStorage.setItem(`torch_eula_${uid}`, "true"); } catch {}
    return true;
  } catch (e) { console.error("[Torch] EULA accept failed:", e); return false; }
}
function hasAcceptedEula(uid) {
  try { return localStorage.getItem(`torch_eula_${uid}`) === "true"; } catch { return false; }
}

/* ===========================================
   PULSE FEED — public pick mirror
   ============================================
   Every pick a user submits gets a slim "mirror" copy written to
   /publicPicks/{pickId}. The user's source pick at /users/{uid}/picks/{pickId}
   stays the system of record; the mirror is the read surface for the
   social feed. Mirror writes are fire-and-forget — feed failure must
   never block pick submission.

   Schema is intentionally denormalized so feed rendering is one query
   with zero joins. Username + leagueName are cached at submission. If
   the user later renames, the feed will show the old name on old picks
   until a reconciliation job (Phase 3b) backfills.

   Settlement updates the mirror IF the user is in-app when settlement
   runs. Cron-settled picks (settleAllPicks every 30m) skip mirror
   sync until the user reopens the app and re-renders MyPicksPage.
   The CF trigger that closes this gap is part of the Phase 3b deploy.
=========================================== */

// Slim mirror shape for the feed. Strips ML/spread internals down to
// what a card actually renders — keeps the doc small and fast to read.
function buildPulseMirrorDoc(pick, user, username, leagueInfo, userStats) {
  return {
    pickId: pick.pickId || null,           // back-reference to users/{uid}/picks/{id}
    uid: user.uid,
    username: username || "Anonymous",
    // Rank snapshot — wins/losses captured at submit time so the feed
    // card can render rank without an N+1 user-stats fetch. Stale by
    // design; if a user rank-changes mid-day, old feed cards keep their
    // old rank until a backfill job touches them.
    submitterWins: userStats?.wins ?? 0,
    submitterLosses: userStats?.losses ?? 0,
    // Pick details
    label: pick.label || "",
    type: pick.type || "",
    side: pick.side || "",
    line: pick.line ?? null,
    odds: pick.odds ?? null,
    wager: pick.wager ?? 0,
    expectedPayout: pick.expectedPayout ?? 0,
    isLiveBet: !!pick.isLiveBet,
    isParlay: !!pick.isParlay,
    // PICK-IDENTITY (fix 1.1, 2026-09-28): grading identity. A hop copies its pick from this
    // mirror, so without these a hopped team total or prop lost them.
    ...(pick.playerName ? { playerName: pick.playerName } : {}),
    ...(pick.statType   ? { statType:   pick.statType }   : {}),
    ...(pick.teamSide   ? { teamSide:   pick.teamSide }   : {}),
    // Parlay legs -- source picks may have this field under either name
    // depending on submit path. Client-side submission writes `parlayLegs`;
    // server-side submitPicksSecure writes `legs` + `legCount`. Try both
    // so the mirror always captures the leg array regardless of origin.
    parlayLegs: pick.parlayLegs || pick.legs || null,
    legCount:   pick.legCount ?? (pick.parlayLegs?.length || pick.legs?.length || 0),
    // Game context
    sport: pick.sport || "",
    sportKey: pick.sportKey || "",
    gameLabel: pick.gameLabel || "",
    commenceTime: pick.commenceTime || null,
    homeTeamFull: pick.homeTeamFull || "",
    awayTeamFull: pick.awayTeamFull || "",
    // League context
    leagueId: leagueInfo?.id || null,
    leagueName: leagueInfo?.name || null,
    leagueIsPrivate: !!leagueInfo?.isPrivate,
    // Status — pending until settlement mirrors over
    status: "pending",
    points: 0,
    // Timestamps — submittedAt uses server time so cross-device ordering is stable
    settledAt: null,
    // Social counters — incremented by hop/comment writes (Phase 3b/c)
    hopCount: 0,
    commentCount: 0,
  };
}

// Fire-and-forget mirror write. Awaited only inside Promise.allSettled
// in the caller — any failure logs and falls through. Submission UX
// must never wait on this or fail because of this.
// ── PICK PRIVACY (Jake / office-pool leagues, Aug 2026) ─────────────────────
// Leagues with `pickPrivacy: "until_start"` on their doc hide members' picks
// from each other until the game kicks off (your OWN picks are always
// visible). Reveal is time-based per pick, so a hidden pick appears on the
// next render after kickoff without a refetch. Gates: league Picks tab,
// Den feed pick events, captain insights, profile modals, and the Pulse
// mirror (flagged-league picks never mirror — Pulse exists for tailing,
// which only works pre-kickoff, so delay would be pointless). Absent flag =
// existing open behavior; existing leagues are untouched.
// NOTE: app-level privacy only — Firestore rules still allow authed reads
// of pick docs (locking that is Phase 3 read-model work).
const pickPrivacyStartMs = (t) => {
  if (!t) return null;
  if (t.toDate) return t.toDate().getTime();
  if (t.seconds) return t.seconds * 1000;
  const d = new Date(t);
  return Number.isFinite(d.getTime()) ? d.getTime() : null;
};
const isPickHiddenByPrivacy = (pick, league, viewerUid) => {
  if (!league || league.pickPrivacy !== "until_start") return false;
  if (!pick || pick.userId === viewerUid) return false;
  const start = pickPrivacyStartMs(pick.commenceTime);
  // No usable start time (old den events, odd docs): stay hidden while
  // pending, reveal at settle — the conservative direction.
  if (start == null) return pick.status === "pending";
  return Date.now() < start;
};

async function mirrorPickToPulse(pick, user, username, leagueInfo, userStats) {
  // ── PICKEM/SURVIVOR ARE NOT PULSE CONTENT ────────────────────────────────
  // Pulse is a WAGER feed: every card renders "WAGERED / COULD RETURN" in
  // points and the Top Pickers board ranks by win% on staked picks. A pickem
  // pick has no stake and no payout, so it mirrors as "0 pts / 0 pts" — which
  // reads as a broken or worthless wager rather than a straight-up pick. It
  // also pollutes a public firehose with what is, by design, a private league's
  // week: 16 identical-looking zero-stake cards per member per week would bury
  // the actual wager feed.
  //
  // Gated HERE rather than at the four call sites (straight, parlay, hop,
  // duplicate-league) so a fifth call site added later inherits the rule
  // instead of silently leaking. Returns false like any other skip — callers
  // are fire-and-forget and never branch on the result.
  if (leagueInfo?.scoringMode && leagueInfo.scoringMode !== "wager") return false;
  // Privacy-flagged league picks never hit the public Pulse feed (see the
  // pick-privacy block above). NOTE: leagueInfo is a hand-built projection at
  // every call site, NOT the league doc — pickPrivacy must be copied into it
  // there. All three call sites (straight, parlay, hop-on) do; a new call
  // site that omits it silently disables this guard.
  if (leagueInfo?.pickPrivacy === "until_start") return false;
  if (!user?.uid || !pick?.pickId) {
    console.warn("[Torch] mirrorPickToPulse skipped — missing user.uid or pick.pickId", {
      hasUid: !!user?.uid, hasPickId: !!pick?.pickId, pickIdType: typeof pick?.pickId,
    });
    return false;
  }
  try {
    const { getFirestore, doc, setDoc, serverTimestamp } = await import("firebase/firestore");
    const db = getFirestore(app);
    const docData = buildPulseMirrorDoc(pick, user, username, leagueInfo, userStats);
    docData.submittedAt = serverTimestamp();
    await setDoc(doc(db, "publicPicks", pick.pickId), docData);
    return true;
  } catch (e) {
    console.warn("[Torch] Pulse mirror write failed (non-blocking):", e.message, {
      pickId: pick.pickId,
      pickIdType: typeof pick.pickId,
      uid: user.uid,
      uidType: typeof user.uid,
      errorCode: e.code,
    });
    return false;
  }
}

// Settlement mirror update — called from MyPicksPage's runSettlement when
// a pick transitions pending → won/lost/push. Mirror picks marked won
// will display as won on the feed for everyone, with the points earned.
async function updatePulseMirrorStatus(pickId, status, points = 0) {
  if (!pickId) return false;
  try {
    const { getFirestore, doc, updateDoc, serverTimestamp } = await import("firebase/firestore");
    const db = getFirestore(app);
    await updateDoc(doc(db, "publicPicks", pickId), {
      status, points: points ?? 0, settledAt: serverTimestamp(),
    });
    return true;
  } catch (e) {
    // Doc may not exist (mirror write failed at submit, or pick predates the
    // feature) — that's expected and not actionable. Log at debug only.
    return false;
  }
}

// isPickLive: returns true if the pick's underlying game is currently in
// progress. Drives the Pulse Live tab filter -- a pick only counts as "live"
// while its game is happening, NOT while it's pre-game or post-game waiting
// on cron settlement. Both of those latter cases land in Settled.
//
// In-progress heuristic: pending status AND commenceTime is in [now - 5h, now].
// 5 hours is conservative enough to cover basketball/football with OT, hockey,
// long MLB extra-inning games, and MMA card windows. Edge: a fight that goes
// 17 seconds will show as Live for the full 5 hours until grading kicks it to
// Settled -- acceptable. Edge the other way: a marathon NBA 3-OT game past 5
// hours will move to Settled before grading; rare enough to ignore.
function isPickLive(pick) {
  if (!pick) return false;
  if (pick.status && pick.status !== "pending") return false;

  const ct = pick.commenceTime;
  let commence = null;
  if (ct && typeof ct.toDate === "function") commence = ct.toDate();
  else if (ct && typeof ct.seconds === "number") commence = new Date(ct.seconds * 1000);
  else if (ct) commence = new Date(ct);

  if (!commence || isNaN(commence.getTime())) return false;

  const elapsedMs = Date.now() - commence.getTime();
  const FIVE_HOURS_MS = 5 * 60 * 60 * 1000;
  return elapsedMs >= 0 && elapsedMs < FIVE_HOURS_MS;
}

// One-time fetch + onSnapshot subscription on the public feed. Returns
// { picks, loading, hasMore, loadMore } so PulsePage can render +
// paginate. Real-time updates prepend newest first.
function usePulseFeed({ pageSize = 25, filter = "all" } = {}) {
  // Raw picks fetched from Firestore (no filter applied). Filtering happens
  // client-side below — chosen over server-side where() because the latter
  // requires composite indexes for status+submittedAt combinations and at
  // current scale (35 users, <100 picks/day) the cost of fetching all and
  // filtering in memory is negligible. Revisit at thousands of picks.
  const [rawPicks, setRawPicks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(true);
  const [lastDoc, setLastDoc] = useState(null);
  const unsubRef = useRef(null);

  // Fetch larger window when a filter is active so we don't end up showing
  // 0 picks when all the most-recent N are the wrong status. 3x pageSize is
  // a reasonable cushion for typical pending/settled distributions.
  const fetchLimit = filter === "all" ? pageSize : pageSize * 3;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setRawPicks([]);
    setLastDoc(null);
    setHasMore(true);

    (async () => {
      try {
        const { getFirestore, collection, query, orderBy, limit, onSnapshot } = await import("firebase/firestore");
        const db = getFirestore(app);
        const q = query(
          collection(db, "publicPicks"),
          orderBy("submittedAt", "desc"),
          limit(fetchLimit),
        );

        // onSnapshot for the first page — real-time prepends as picks come in.
        // Subsequent pages use getDocs in loadMore so the listener doesn't grow
        // unboundedly with pagination depth.
        unsubRef.current = onSnapshot(q, (snap) => {
          if (cancelled) return;
          const next = [];
          snap.forEach(d => next.push({ id: d.id, ...d.data() }));
          setRawPicks(next);
          setLastDoc(snap.docs[snap.docs.length - 1] || null);
          setHasMore(snap.docs.length >= fetchLimit);
          setLoading(false);
        }, (err) => {
          console.warn("[Torch] Pulse feed snapshot error:", err.message);
          if (!cancelled) { setLoading(false); setRawPicks([]); }
        });
      } catch (e) {
        console.error("[Torch] Pulse feed init failed:", e.message);
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      if (unsubRef.current) { try { unsubRef.current(); } catch {} unsubRef.current = null; }
    };
  }, [fetchLimit]);

  const loadMore = useCallback(async () => {
    if (!lastDoc || !hasMore) return;
    try {
      const { getFirestore, collection, query, orderBy, limit, startAfter, getDocs } = await import("firebase/firestore");
      const db = getFirestore(app);
      const q = query(
        collection(db, "publicPicks"),
        orderBy("submittedAt", "desc"),
        startAfter(lastDoc),
        limit(fetchLimit),
      );
      const snap = await getDocs(q);
      const next = [];
      snap.forEach(d => next.push({ id: d.id, ...d.data() }));
      setRawPicks(prev => [...prev, ...next]);
      setLastDoc(snap.docs[snap.docs.length - 1] || lastDoc);
      setHasMore(snap.docs.length >= fetchLimit);
    } catch (e) {
      console.warn("[Torch] Pulse loadMore failed:", e.message);
    }
  }, [lastDoc, hasMore, fetchLimit]);

  // Client-side filter applied to the raw window. Three tabs:
  //
  //   LIVE     -- game currently in progress (uses isPickLive helper).
  //               Has the green dot indicator. This is "stuff happening
  //               right now."
  //
  //   PENDING  -- pending status BUT game isn't in progress. Covers two
  //               sub-cases: pre-game (scheduled, hasn't tipped off yet)
  //               and post-game ungraded (game ended, cron hasn't settled
  //               yet). Both sit in this middle bucket so users can see
  //               their open picks at a glance.
  //
  //   SETTLED  -- graded terminal status: won, lost, or push. Auto-voided
  //               picks land here too (autoVoidPick writes status="push").
  //
  // Voided/cashed picks fall out of all three (no place for them in feed).
  const picks = (() => {
    if (filter === "all") return rawPicks;
    if (filter === "live") {
      return rawPicks.filter(isPickLive);
    }
    if (filter === "pending") {
      return rawPicks.filter(p =>
        (!p.status || p.status === "pending") && !isPickLive(p)
      );
    }
    if (filter === "settled") {
      return rawPicks.filter(p =>
        p.status === "won" || p.status === "lost" || p.status === "push"
      );
    }
    return rawPicks;
  })();

  return { picks, loading, hasMore, loadMore };
}

/* ============== useTopPickers ========================================
   Aggregates the Top N pickers from /publicPicks. Counts won/lost docs
   per uid across the most recent `fetchSize` mirror docs, applies a
   `minSettled` gate (so a 1-of-1 doesn't show up at 100%), sorts by
   win rate with settled-count as the tiebreaker, returns the top N.

   Cached at module scope for 5 minutes so navigating away from Pulse
   and back doesn't refetch hundreds of docs. Same TTL as the rank
   cache for consistency.

   Username comes from the first (newest) doc seen for each uid because
   submittedAt is DESC, so renames propagate naturally without a join.
=========================================== */
const _topPickersCache = { data: null, ts: 0, signature: null };
const TOP_PICKERS_CACHE_TTL = 5 * 60 * 1000; // 5 minutes

function useTopPickers({ limit: maxResults = 3, minSettled = 5, fetchSize = 500 } = {}) {
  const [topPickers, setTopPickers] = useState(() => {
    // Hydrate from cache on mount so the section doesn't flash empty on tab switches.
    const sig = `${maxResults}:${minSettled}:${fetchSize}`;
    if (_topPickersCache.signature === sig &&
        _topPickersCache.data &&
        Date.now() - _topPickersCache.ts < TOP_PICKERS_CACHE_TTL) {
      return _topPickersCache.data;
    }
    return [];
  });
  const [loading, setLoading] = useState(() => {
    const sig = `${maxResults}:${minSettled}:${fetchSize}`;
    return !(_topPickersCache.signature === sig &&
             _topPickersCache.data &&
             Date.now() - _topPickersCache.ts < TOP_PICKERS_CACHE_TTL);
  });

  useEffect(() => {
    let cancelled = false;
    const signature = `${maxResults}:${minSettled}:${fetchSize}`;

    // Cache hit — already hydrated above, nothing to do.
    if (_topPickersCache.signature === signature &&
        _topPickersCache.data &&
        Date.now() - _topPickersCache.ts < TOP_PICKERS_CACHE_TTL) {
      return;
    }

    (async () => {
      try {
        const { getFirestore, collection, query, orderBy, limit, getDocs } = await import("firebase/firestore");
        const db = getFirestore(app);
        const q = query(
          collection(db, "publicPicks"),
          orderBy("submittedAt", "desc"),
          limit(fetchSize),
        );
        const snap = await getDocs(q);
        if (cancelled) return;

        // Aggregate by uid. Only won/lost count; push is a no-op for record.
        const byUid = new Map();
        snap.forEach(d => {
          const data = d.data();
          const status = data.status;
          if (status !== "won" && status !== "lost") return;
          const uid = data.uid;
          if (!uid) return;
          let entry = byUid.get(uid);
          if (!entry) {
            // First (newest) doc per uid wins the username — DESC order
            // means subsequent doc reads are older and skipped.
            entry = { uid, username: data.username || "Anonymous", wins: 0, losses: 0 };
            byUid.set(uid, entry);
          }
          if (status === "won")  entry.wins++;
          else                   entry.losses++;
        });

        // Apply min-settled gate, compute rate, sort.
        const ranked = [];
        for (const e of byUid.values()) {
          const settledCount = e.wins + e.losses;
          if (settledCount < minSettled) continue;
          ranked.push({ ...e, settled: settledCount, winRate: e.wins / settledCount });
        }
        ranked.sort((a, b) => {
          if (b.winRate !== a.winRate) return b.winRate - a.winRate;
          return b.settled - a.settled; // tiebreaker: bigger sample wins
        });

        const top = ranked.slice(0, maxResults);
        _topPickersCache.data = top;
        _topPickersCache.ts = Date.now();
        _topPickersCache.signature = signature;
        if (!cancelled) { setTopPickers(top); setLoading(false); }
      } catch (e) {
        console.warn("[Torch] Top pickers fetch failed:", e.message);
        if (!cancelled) { setTopPickers([]); setLoading(false); }
      }
    })();

    return () => { cancelled = true; };
  }, [maxResults, minSettled, fetchSize]);

  return { topPickers, loading };
}

/* ============== useCommentsThread (Phase 3c) ===========================
   Subscribes to /publicPicks/{pickId}/comments ordered oldest-first.
   Chronological order on purpose — conversation flow reads better than
   reverse-chron for trash talk back-and-forth. New comments appear at
   the bottom; UI auto-scrolls there unless the user has scrolled up.

   No pagination yet — typical thread is <50 comments. If a pick gets
   viral and accumulates hundreds, add a "load older" cursor at the top
   later. Premature optimization for current scale.
========================================================================*/
function useCommentsThread(pickId) {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!pickId) {
      setComments([]);
      setLoading(false);
      return;
    }
    // Race protection: the async firebase/firestore import below means the
    // effect's cleanup can fire before onSnapshot is attached. Without the
    // `cancelled` flag, a fast unmount/remount (e.g., closing and reopening
    // a comments drawer quickly) would leave the late-arriving onSnapshot
    // orphaned -- still running, still calling setState on an unmounted
    // component. Matches the pattern used at the practice-pending listener
    // and the pulse feed init.
    let cancelled = false;
    let unsub = () => {};
    (async () => {
      try {
        const { getFirestore, collection, query, orderBy, onSnapshot } = await import("firebase/firestore");
        if (cancelled) return;
        const db = getFirestore(app);
        const q = query(
          collection(db, "publicPicks", pickId, "comments"),
          orderBy("createdAt", "asc"),
        );
        unsub = onSnapshot(q, (snap) => {
          if (cancelled) return;
          const next = snap.docs.map(d => ({ id: d.id, ...d.data() }));
          setComments(next);
          setLoading(false);
        }, (err) => {
          if (cancelled) return;
          console.warn("[Torch] Comments subscription error:", err.message);
          setLoading(false);
        });
      } catch (e) {
        if (cancelled) return;
        console.warn("[Torch] Comments thread init failed:", e.message);
        setLoading(false);
      }
    })();
    return () => { cancelled = true; unsub(); };
  }, [pickId]);

  return { comments, loading };
}

// EULA / Terms Modal — Apple Guideline 1.2 requires EULA before UGC access
function EulaModal({ onAccept }) {
  const [scrolledToBottom, setScrolledToBottom] = useState(false);
  const scrollRef = useRef(null);
  const handleScroll = () => {
    const el = scrollRef.current;
    if (el && el.scrollTop + el.clientHeight >= el.scrollHeight - 20) setScrolledToBottom(true);
  };
  return (
    <div style={{
      position:"fixed", inset:0, zIndex:500,
      background:"rgba(8,9,13,0.95)", backdropFilter:"blur(20px)",
      display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center",
      padding:"24px 16px",
    }}>
      <div style={{
        width:"100%", maxWidth:440, background:"#14161F",
        border:"1px solid rgba(255,255,255,0.08)", borderRadius:24,
        padding:"32px 24px 24px", maxHeight:"85vh", display:"flex", flexDirection:"column",
        boxShadow:"0 32px 100px rgba(0,0,0,0.7)",
        animation:"fadeUp 0.32s cubic-bezier(0.32,0.72,0,1.18) both",
      }}>
        <div style={{ textAlign:"center", marginBottom:12 }}>
          <svg width="32" height="32" viewBox="0 0 24 24" style={{ display:"inline-block" }}>
            <defs><filter id="tosGlow" x="-50%" y="-50%" width="200%" height="200%"><feGaussianBlur stdDeviation="1.5" result="b"/><feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge></filter></defs>
            <rect x="5" y="3" width="14" height="18" rx="2" fill="none" stroke="rgba(255,255,255,0.5)" strokeWidth="1.3" filter="url(#tosGlow)"/>
            <line x1="9" y1="8" x2="15" y2="8" stroke="rgba(255,255,255,0.3)" strokeWidth="1" strokeLinecap="round"/>
            <line x1="9" y1="11" x2="15" y2="11" stroke="rgba(255,255,255,0.3)" strokeWidth="1" strokeLinecap="round"/>
            <line x1="9" y1="14" x2="13" y2="14" stroke="rgba(255,255,255,0.3)" strokeWidth="1" strokeLinecap="round"/>
          </svg>
        </div>
        <div style={{ fontSize:20, fontWeight:700, color:"#fff", textAlign:"center", marginBottom:6 }}>Terms of Use</div>
        <div style={{ fontSize:13, color:"rgba(255,255,255,0.45)", textAlign:"center", marginBottom:20 }}>
          Please read and accept before continuing
        </div>
        <div ref={scrollRef} onScroll={handleScroll} style={{
          flex:1, overflow:"auto", padding:"16px", borderRadius:12,
          background:"rgba(255,255,255,0.03)", border:"1px solid rgba(255,255,255,0.06)",
          fontSize:13, color:"rgba(255,255,255,0.65)", lineHeight:1.7, marginBottom:20,
          maxHeight:320, WebkitOverflowScrolling:"touch",
        }}>
          <p style={{ fontWeight:700, color:"#fff", marginBottom:8 }}>Torch Community Guidelines & Terms</p>
          <p style={{ marginBottom:12 }}>By using Torch, you agree to the following terms. Violation may result in removal of content and/or suspension of your account.</p>
          <p style={{ fontWeight:700, color:"#fff", marginBottom:4 }}>1. No Objectionable Content</p>
          <p style={{ marginBottom:12 }}>You may not post content that is offensive, abusive, harassing, threatening, hateful, discriminatory, sexually explicit, violent, or otherwise objectionable. There is zero tolerance for objectionable content.</p>
          <p style={{ fontWeight:700, color:"#fff", marginBottom:4 }}>2. No Abusive Behavior</p>
          <p style={{ marginBottom:12 }}>You may not bully, intimidate, harass, or abuse other users. Respect fellow league members and keep the trash talk friendly.</p>
          <p style={{ fontWeight:700, color:"#fff", marginBottom:4 }}>3. Content Moderation</p>
          <p style={{ marginBottom:12 }}>Torch reserves the right to review and remove any user-generated content at any time. Users can report objectionable content and block abusive users. Reported content will be reviewed and acted upon within 24 hours.</p>
          <p style={{ fontWeight:700, color:"#fff", marginBottom:4 }}>4. Account Enforcement</p>
          <p style={{ marginBottom:12 }}>Users who post objectionable content or engage in abusive behavior will have their content removed and may be permanently banned from Torch.</p>
          <p style={{ fontWeight:700, color:"#fff", marginBottom:4 }}>5. No Real Money</p>
          <p style={{ marginBottom:12 }}>Torch is NOT a gambling app. No real money is involved. Points have zero monetary value and cannot be redeemed for cash, prizes, or anything of value.</p>
          <p style={{ fontWeight:700, color:"#fff", marginBottom:4 }}>6. Privacy</p>
          <p>Your data is handled in accordance with our Privacy Policy. We collect only what is necessary to provide the service.</p>
        </div>
        <button onClick={onAccept} style={{
          width:"100%", padding:"14px", borderRadius:12, border:"none", cursor: "pointer",
          background: "linear-gradient(135deg, #FF6B3D, #E05522)",
          color:"#fff", fontSize:15, fontWeight:700,
          boxShadow:"0 4px 20px rgba(255,107,61,0.3)",
          opacity: 1,
        }}>
          I Agree — Continue
        </button>
        <div style={{ fontSize:11, color:"rgba(255,255,255,0.40)", textAlign:"center", marginTop:10, lineHeight:1.5 }}>
          By tapping "I Agree" you accept these Terms of Use and our Community Guidelines.
        </div>
      </div>
    </div>
  );
}

// Report Content Modal — lets users flag objectionable content
function ReportContentModal({ contentId, contentText, reportedUserId, reportedUsername, leagueId, currentUser, onClose, onReported }) {
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const reasons = ["Offensive or hateful content", "Harassment or bullying", "Spam or self-promotion", "Sexually explicit content", "Violence or threats", "Other"];
  const handleSubmit = async () => {
    if (!reason) return;
    setSubmitting(true);
    const ok = await reportContent({
      reporterUid: currentUser.uid,
      reporterName: currentUser.displayName || currentUser.email?.split("@")[0] || "",
      contentId, contentType: "den_activity", contentText,
      reportedUserId, reportedUsername, leagueId, reason,
    });
    setSubmitting(false);
    if (ok) { setDone(true); onReported?.(); setTimeout(onClose, 1500); }
  };
  return (
    <div onClick={onClose} style={{
      position:"fixed", inset:0, zIndex:400,
      background:"rgba(0,0,0,0.7)", backdropFilter:"blur(8px)",
      display:"flex", alignItems:"center", justifyContent:"center", padding:20,
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        width:"100%", maxWidth:380, background:"#14161F",
        border:"1px solid rgba(255,255,255,0.08)", borderRadius:20,
        padding:"24px 20px",
        animation:"fadeUp 0.28s cubic-bezier(0.32,0.72,0,1.18) both",
      }}>
        {done ? (
          <div style={{ textAlign:"center", padding:"20px 0" }}>
            <div style={{ fontSize:36, marginBottom:12 }}>✓</div>
            <div style={{ fontSize:16, fontWeight:700, color:"#4ADE80" }}>Report submitted</div>
            <div style={{ fontSize:13, color:"rgba(255,255,255,0.4)", marginTop:6, lineHeight:1.6 }}>
              Your report has been sent to the Torch moderation team. We will review this within 24 hours and take appropriate action, including removing content and suspending abusive users.
            </div>
          </div>
        ) : (
          <>
            <div style={{ fontSize:16, fontWeight:700, color:"#fff", marginBottom:4 }}>Report Content</div>
            <div style={{ fontSize:12, color:"rgba(255,255,255,0.4)", marginBottom:16 }}>Why are you reporting this?</div>
            {reasons.map(r => (
              <button key={r} onClick={() => setReason(r)} style={{
                width:"100%", padding:"11px 14px", marginBottom:6, borderRadius:10,
                border: reason === r ? "1px solid rgba(255,107,61,0.4)" : "1px solid rgba(255,255,255,0.06)",
                background: reason === r ? "rgba(255,107,61,0.10)" : "rgba(255,255,255,0.03)",
                color: reason === r ? "#FF6B3D" : "rgba(255,255,255,0.6)",
                fontSize:13, fontWeight:600, cursor:"pointer", textAlign:"left",
              }}>{r}</button>
            ))}
            <div style={{ display:"flex", gap:8, marginTop:14 }}>
              <button onClick={onClose} style={{
                flex:1, padding:"12px", borderRadius:10, border:"1px solid rgba(255,255,255,0.08)",
                background:"transparent", color:"rgba(255,255,255,0.5)", fontSize:13, fontWeight:600, cursor:"pointer",
              }}>Cancel</button>
              <button onClick={handleSubmit} disabled={!reason || submitting} style={{
                flex:1, padding:"12px", borderRadius:10, border:"none",
                background: reason ? "linear-gradient(135deg, #FF6B3D, #E05522)" : "rgba(255,255,255,0.06)",
                color:"#fff", fontSize:13, fontWeight:700, cursor: reason ? "pointer" : "default",
                opacity: submitting ? 0.6 : 1,
              }}>{submitting ? "Sending…" : "Submit Report"}</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ── Offline Banner ─────────────────────────────────────────────────────────────
function OfflineBanner() {
  const [isOnline, setIsOnline] = useState(() => navigator.onLine);
  const [showReconnected, setShowReconnected] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    const handleOffline = () => {
      setIsOnline(false);
      setShowReconnected(false);
      if (timerRef.current) clearTimeout(timerRef.current);
    };
    const handleOnline = () => {
      setIsOnline(true);
      setShowReconnected(true);
      timerRef.current = setTimeout(() => setShowReconnected(false), 2500);
    };
    window.addEventListener('offline', handleOffline);
    window.addEventListener('online', handleOnline);
    return () => {
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('online', handleOnline);
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  const visible = !isOnline || showReconnected;
  if (!visible) return null;

  const isBack = isOnline && showReconnected;
  return (
    <div style={{
      position:'fixed', top:0, left:0, right:0, zIndex:99999,
      background: isBack ? '#16a34a' : '#1e1e24',
      borderBottom:`1px solid ${isBack ? 'rgba(74,222,128,0.3)' : 'rgba(255,107,61,0.25)'}`,
      display:'flex', alignItems:'center', justifyContent:'center', gap:8,
      padding:'10px 16px',
      paddingTop:'max(10px, env(safe-area-inset-top))',
      animation:'fadeUp 0.2s ease',
    }}>
      <span style={{ fontSize:14 }}>{isBack ? '✅' : '📵'}</span>
      <span style={{
        fontSize:13, fontWeight:700,
        color: isBack ? '#bbf7d0' : 'rgba(255,255,255,0.85)',
      }}>
        {isBack ? 'Back online' : 'No connection — picks are paused'}
      </span>
    </div>
  );
}


// -- Team logo helpers ---------------------------------------------------------
// Maps team nickname → ESPN abbreviation, split by sport to avoid collisions.
// ESPN CDN pattern: https://a.espncdn.com/i/teamlogos/{sport}/{size}/{abbrev}.png
const NBA_ABBREVS = {
  Hawks:"atl",Celtics:"bos",Nets:"bkn",Hornets:"cha",Bulls:"chi",Cavaliers:"cle",
  Mavericks:"dal",Nuggets:"den",Pistons:"det",Warriors:"gs",Rockets:"hou",
  Pacers:"ind",Clippers:"lac",Lakers:"lal",Grizzlies:"mem",Heat:"mia",
  Bucks:"mil",Timberwolves:"min",Pelicans:"no",Knicks:"ny",Thunder:"okc",
  Magic:"orl","76ers":"phi",Suns:"phx",Blazers:"por",Kings:"sac",Spurs:"sa",
  Raptors:"tor",Jazz:"utah",Wizards:"wsh",
  // full-name fallbacks
  "Atlanta Hawks":"atl","Boston Celtics":"bos","Brooklyn Nets":"bkn",
  "Charlotte Hornets":"cha","Chicago Bulls":"chi","Cleveland Cavaliers":"cle",
  "Dallas Mavericks":"dal","Denver Nuggets":"den","Detroit Pistons":"det",
  "Golden State Warriors":"gs","Houston Rockets":"hou","Indiana Pacers":"ind",
  "LA Clippers":"lac","Los Angeles Clippers":"lac","Los Angeles Lakers":"lal",
  "Memphis Grizzlies":"mem","Miami Heat":"mia","Milwaukee Bucks":"mil",
  "Minnesota Timberwolves":"min","New Orleans Pelicans":"no","New York Knicks":"ny",
  "Oklahoma City Thunder":"okc","Orlando Magic":"orl","Philadelphia 76ers":"phi",
  "Phoenix Suns":"phx","Portland Trail Blazers":"por","Sacramento Kings":"sac",
  "San Antonio Spurs":"sa","Toronto Raptors":"tor","Utah Jazz":"utah",
  "Washington Wizards":"wsh",
};
const NFL_ABBREVS = {
  Cardinals:"ari",Falcons:"atl",Ravens:"bal",Bills:"buf",Panthers:"car",
  Bears:"chi",Bengals:"cin",Browns:"cle",Cowboys:"dal",Broncos:"den",
  Lions:"det",Packers:"gb",Texans:"hou",Colts:"ind",Jaguars:"jax",
  Chiefs:"kc",Raiders:"lv",Chargers:"lac",Rams:"lar",Dolphins:"mia",
  Vikings:"min",Patriots:"ne",Saints:"no",Giants:"nyg",Jets:"nyj",
  Eagles:"phi",Steelers:"pit",Seahawks:"sea","49ers":"sf",Buccaneers:"tb",
  Titans:"ten",Commanders:"wsh",
  // full-name fallbacks
  "Arizona Cardinals":"ari","Atlanta Falcons":"atl","Baltimore Ravens":"bal",
  "Buffalo Bills":"buf","Carolina Panthers":"car","Chicago Bears":"chi",
  "Cincinnati Bengals":"cin","Cleveland Browns":"cle","Dallas Cowboys":"dal",
  "Denver Broncos":"den","Detroit Lions":"det","Green Bay Packers":"gb",
  "Houston Texans":"hou","Indianapolis Colts":"ind","Jacksonville Jaguars":"jax",
  "Kansas City Chiefs":"kc","Las Vegas Raiders":"lv","Los Angeles Chargers":"lac",
  "Los Angeles Rams":"lar","Miami Dolphins":"mia","Minnesota Vikings":"min",
  "New England Patriots":"ne","New Orleans Saints":"no","New York Giants":"nyg",
  "New York Jets":"nyj","Philadelphia Eagles":"phi","Pittsburgh Steelers":"pit",
  "Seattle Seahawks":"sea","San Francisco 49ers":"sf","Tampa Bay Buccaneers":"tb",
  "Tennessee Titans":"ten","Washington Commanders":"wsh",
};
const MLB_ABBREVS = {
  Diamondbacks:"ari",Braves:"atl",Orioles:"bal","Red Sox":"bos",RedSox:"bos",
  "White Sox":"cws",WhiteSox:"cws",Cubs:"chc",Reds:"cin",Guardians:"cle",
  "Blue Jays":"tor",BlueJays:"tor",Marlins:"mia",Astros:"hou",
  Yankees:"nyy",Mets:"nym",Phillies:"phi",Pirates:"pit",Padres:"sd",
  Mariners:"sea",Cardinals:"stl",Rays:"tb",Rangers:"tex",Twins:"min",
  Rockies:"col",Tigers:"det",Royals:"kc",Angels:"laa",Athletics:"oak",
  Nationals:"wsh",Brewers:"mil",Giants:"sf",Dodgers:"lad",
  // full-name fallbacks
  "Arizona Diamondbacks":"ari","Atlanta Braves":"atl","Baltimore Orioles":"bal",
  "Boston Red Sox":"bos","Chicago White Sox":"cws","Chicago Cubs":"chc",
  "Cincinnati Reds":"cin","Cleveland Guardians":"cle","Colorado Rockies":"col",
  "Detroit Tigers":"det","Houston Astros":"hou","Kansas City Royals":"kc",
  "Los Angeles Angels":"laa","Los Angeles Dodgers":"lad","Miami Marlins":"mia",
  "Milwaukee Brewers":"mil","Minnesota Twins":"min","New York Yankees":"nyy",
  "New York Mets":"nym","Oakland Athletics":"oak","Philadelphia Phillies":"phi",
  "Pittsburgh Pirates":"pit","San Diego Padres":"sd","San Francisco Giants":"sf",
  "Seattle Mariners":"sea","St. Louis Cardinals":"stl","Tampa Bay Rays":"tb",
  "Texas Rangers":"tex","Toronto Blue Jays":"tor","Washington Nationals":"wsh",
  "Sacramento Athletics":"oak",
};
const NHL_ABBREVS = {
  Ducks:"ana",Coyotes:"ari",Sabres:"buf",Flames:"cgy",Hurricanes:"car",
  Blackhawks:"chi",Avalanche:"col",Jackets:"cbj",Stars:"dal",
  Oilers:"edm",Panthers:"fla",Kings:"lak",Wild:"min",Canadiens:"mtl",
  Predators:"nsh",Devils:"njd",Islanders:"nyi",Rangers:"nyr",Senators:"ott",
  Flyers:"phi",Penguins:"pit",Blues:"stl",Lightning:"tb","Maple Leafs":"tor",
  Canucks:"van","Golden Knights":"vgk",Capitals:"wsh",Jets:"wpg",Sharks:"sjs",
  Kraken:"sea","Red Wings":"det","Blue Jackets":"cbj",Bruins:"bos",
  // full-name fallbacks
  "Anaheim Ducks":"ana","Arizona Coyotes":"ari","Buffalo Sabres":"buf",
  "Calgary Flames":"cgy","Carolina Hurricanes":"car","Chicago Blackhawks":"chi",
  "Colorado Avalanche":"col","Columbus Blue Jackets":"cbj","Dallas Stars":"dal",
  "Edmonton Oilers":"edm","Florida Panthers":"fla","Los Angeles Kings":"lak",
  "Minnesota Wild":"min","Montreal Canadiens":"mtl","Nashville Predators":"nsh",
  "New Jersey Devils":"njd","New York Islanders":"nyi","New York Rangers":"nyr",
  "Ottawa Senators":"ott","Philadelphia Flyers":"phi","Pittsburgh Penguins":"pit",
  "St. Louis Blues":"stl","Tampa Bay Lightning":"tb","Toronto Maple Leafs":"tor",
  "Vancouver Canucks":"van","Vegas Golden Knights":"vgk","Washington Capitals":"wsh",
  "Winnipeg Jets":"wpg","San Jose Sharks":"sjs","Seattle Kraken":"sea",
  "Detroit Red Wings":"det","Boston Bruins":"bos","Utah Hockey Club":"uta",
};
const SPORT_ABBREVS = { NBA:NBA_ABBREVS, NFL:NFL_ABBREVS, MLB:MLB_ABBREVS, NHL:NHL_ABBREVS };
const ESPN_SPORT = { NBA:"nba", NFL:"nfl", MLB:"mlb", NHL:"nhl" };

// Sports without a "two opposing teams" concept. These should NEVER fuzzy-match
// against TheSportsDB -- the fuzzy match defaults to Arsenal on no-match, which
// produces wrong-sport crests on golf, tennis, MMA, and the Derby. If we add a
// new individual / outright / event-style sport, add it here.
const NON_TEAM_SPORTS = new Set([
  "Derby", "Golf", "Tennis", "MMA", "NASCAR", "Cricket", "Boxing",
]);

// NCAA teams use numeric ESPN IDs — CDN pattern: /i/teamlogos/ncaa/500/{id}.png
const NCAA_IDS = {
  // Top NCAAB + NCAAF programs (ESPN team ID)
  "Alabama":333,"Crimson Tide":333,"Arizona":12,"Wildcats":12,"Auburn":2,"Tigers":2,
  "Baylor":239,"Bears":239,"BYU":252,"Cougars":252,"Clemson":228,
  "Colorado":38,"Buffaloes":38,"Connecticut":41,"UConn":41,"Huskies":41,
  "Creighton":156,"Bluejays":156,"Duke":150,"Blue Devils":150,
  "Florida":57,"Gators":57,"Florida State":52,"Seminoles":52,
  "Georgetown":46,"Hoyas":46,"Georgia":61,"Bulldogs":61,
  "Gonzaga":2250,"Houston":248,
  "Illinois":356,"Fighting Illini":356,"Indiana":84,"Hoosiers":84,
  "Iowa":2294,"Hawkeyes":2294,"Iowa State":66,"Cyclones":66,
  "Kansas":2305,"Jayhawks":2305,"Kentucky":96,
  "Louisville":97,"Cardinals":97,"LSU":99,
  "Marquette":269,"Golden Eagles":269,"Maryland":120,"Terrapins":120,
  "Memphis":235,"Michigan":130,"Wolverines":130,
  "Michigan State":127,"Spartans":127,"Minnesota":135,"Golden Gophers":135,
  "Mississippi State":344,"Missouri":142,
  "North Carolina":153,"Tar Heels":153,"NC State":152,"Wolfpack":152,
  "Notre Dame":87,"Fighting Irish":87,
  "Ohio State":194,"Buckeyes":194,"Oklahoma":201,"Sooners":201,
  "Oregon":2483,"Ducks":2483,"Penn State":213,"Nittany Lions":213,
  "Pittsburgh":221,"Panthers":221,"Purdue":2509,"Boilermakers":2509,
  "San Diego State":21,"Aztecs":21,"Seton Hall":2550,"Pirates":2550,
  "Stanford":24,"Syracuse":183,"Orange":183,
  "TCU":2628,"Horned Frogs":2628,"Tennessee":2633,"Volunteers":2633,
  "Texas":251,"Longhorns":251,"Texas A&M":245,"Aggies":245,
  "Texas Tech":2641,"Red Raiders":2641,
  "UCLA":26,"Bruins":26,"USC":30,"Trojans":30,
  "Vanderbilt":238,"Commodores":238,"Villanova":2679,
  "Virginia":258,"Cavaliers":258,"Virginia Tech":259,"Hokies":259,
  "Wake Forest":154,"Demon Deacons":154,
  "West Virginia":277,"Mountaineers":277,"Wisconsin":275,"Badgers":275,
  "Xavier":2752,"Musketeers":2752,"Arkansas":8,"Razorbacks":8,
  "Colorado State":36,"Rams":36,"Dayton":2248,"Flyers":2248,
  "Drake":2181,"Florida Atlantic":2229,"Owls":2229,
  "Furman":231,"Paladins":231,"Grand Canyon":2253,
  "James Madison":256,"Dukes":256,"Kansas State":2306,
  "Kennesaw State":338,"Kent State":2309,
  "Liberty":2335,"Flames":2335,"Loyola Chicago":2326,"Ramblers":2326,
  "McNeese":2377,"Miami":2390,"Hurricanes":2390,"Mississippi":145,"Ole Miss":145,"Rebels":145,
  "Nevada":2440,"Wolf Pack":2440,"New Mexico":167,"Lobos":167,
  "Northwestern":77,"Oklahoma State":197,"Cowboys":197,
  "Oregon State":204,"Beavers":204,"Providence":2507,"Friars":2507,
  "Rutgers":164,"Scarlet Knights":164,"Saint Mary's":2608,"Gaels":2608,
  "SMU":2567,"Mustangs":2567,"South Carolina":2579,
  "Gamecocks":2579,"St. John's":2599,"Red Storm":2599,
  "Utah":254,"Utes":254,"Washington":264,"Wichita State":2724,"Shockers":2724,

  // Full name lookups
  "Alabama Crimson Tide":333,"Arizona Wildcats":12,"Auburn Tigers":2,
  "Baylor Bears":239,"BYU Cougars":252,"Clemson Tigers":228,
  "Colorado Buffaloes":38,"Connecticut Huskies":41,"UConn Huskies":41,
  "Creighton Bluejays":156,"Duke Blue Devils":150,
  "Florida Gators":57,"Florida State Seminoles":52,
  "Georgetown Hoyas":46,"Georgia Bulldogs":61,"Gonzaga Bulldogs":2250,
  "Houston Cougars":248,"Illinois Fighting Illini":356,"Indiana Hoosiers":84,
  "Iowa Hawkeyes":2294,"Iowa State Cyclones":66,
  "Kansas Jayhawks":2305,"Kentucky Wildcats":96,
  "Louisville Cardinals":97,"LSU Tigers":99,
  "Marquette Golden Eagles":269,"Maryland Terrapins":120,
  "Memphis Tigers":235,"Michigan Wolverines":130,
  "Michigan State Spartans":127,"Minnesota Golden Gophers":135,
  "Mississippi State Bulldogs":344,"Missouri Tigers":142,
  "North Carolina Tar Heels":153,"NC State Wolfpack":152,
  "Notre Dame Fighting Irish":87,"Ohio State Buckeyes":194,
  "Oklahoma Sooners":201,"Oregon Ducks":2483,
  "Penn State Nittany Lions":213,"Pittsburgh Panthers":221,
  "Purdue Boilermakers":2509,"San Diego State Aztecs":21,
  "Stanford Cardinal":24,"Syracuse Orange":183,
  "TCU Horned Frogs":2628,"Tennessee Volunteers":2633,
  "Texas Longhorns":251,"Texas A&M Aggies":245,
  "Texas Tech Red Raiders":2641,"UCLA Bruins":26,"USC Trojans":30,
  "Vanderbilt Commodores":238,"Villanova Wildcats":2679,
  "Virginia Cavaliers":258,"Virginia Tech Hokies":259,
  "Wake Forest Demon Deacons":154,"West Virginia Mountaineers":277,
  "Wisconsin Badgers":275,"Xavier Musketeers":2752,
  "Arkansas Razorbacks":8,"Miami Hurricanes":2390,
  "Mississippi Rebels":145,"Ole Miss Rebels":145,
  "Oklahoma State Cowboys":197,"Oregon State Beavers":204,
  "Rutgers Scarlet Knights":164,"South Carolina Gamecocks":2579,
  "Utah Utes":254,"Washington Huskies":264,
};

function getTeamLogo(fullName, sport) {
  if (!fullName || !sport) return null;

  // NCAA — use ESPN numeric team ID CDN
  if (sport === 'NCAAB' || sport === 'NCAAF') {
    const parts = fullName.trim().split(" ");
    const nickname = parts[parts.length - 1];
    const schoolName = parts.length > 1 ? parts.slice(0, -1).join(" ") : parts[0];
    const id = NCAA_IDS[fullName] || NCAA_IDS[nickname] || NCAA_IDS[schoolName];
    if (id) return `https://a.espncdn.com/i/teamlogos/ncaa/500/${id}.png`;
    return null;
  }

  // Pro sports — use abbreviation-based CDN
  const key = ESPN_SPORT[sport];
  if (!key) return null;
  const map = SPORT_ABBREVS[sport] || {};
  const parts = fullName.trim().split(" ");
  const nickname = parts[parts.length - 1];
  // Only look up within the SAME sport. Falling back to other sports' maps
  // (e.g. EPL's Arsenal/Chelsea) would produce wrong-sport logo URLs and,
  // worse, the sportsApi fallback in TeamLogo would then fuzzy-match to
  // whatever it thinks is closest -- resulting in Arsenal crests on hockey
  // games. If we don't know the abbrev for this sport, return null and let
  // the initials placeholder render instead.
  const abbrev = map[fullName] || map[nickname];
  if (!abbrev) return null;
  return `https://a.espncdn.com/i/teamlogos/${key}/500/${abbrev}.png`;
}

function TeamLogo({ name, sport, size = 28, style: s = {}, logoUrl }) {
  const [err, setErr] = useState(false);
  const [, forceUpdate] = useState(0);
  const scheduled = useRef(false);

  // Reject inputs that don't look like a single team name. This prevents the
  // sportsApi fallback (TheSportsDB) from fuzzy-matching garbage like
  // "ORL · LAL @ HOU" (parlay gameLabels), "BUF" (raw abbreviations from
  // legacy events), or empty strings to a wrong logo (notoriously Arsenal,
  // which seems to be its default-on-no-match return).
  // A real team name has at least 4 chars, mixed case (not all-caps abbrev),
  // and contains no parlay separators. We also reject obvious event/race/
  // contestant strings -- "#18 Further Ado", "2026 Kentucky Derby", "Run for
  // the Roses" -- which would otherwise get Arsenal'd by the fuzzy fallback.
  const EVENT_KEYWORDS = /\b(?:Derby|Cup|Tournament|Open|Championship|Roses|Masters|Stakes|Race|Grand Prix|World\s+Cup|Final\s+Four)\b/i;
  const isLikelyTeamName = (n) => {
    if (!n || typeof n !== "string") return false;
    const trimmed = n.trim();
    if (trimmed.length < 4) return false;                    // too short -- likely abbrev
    if (trimmed === trimmed.toUpperCase() && trimmed.length <= 4) return false; // all-caps abbrev
    if (trimmed.includes(" · ") || trimmed.includes(" @ ")) return false; // parlay / multi-game label
    if (trimmed.includes("+") || trimmed.includes("/")) return false;     // parlay leg joiner / over-under
    if (trimmed.startsWith("#")) return false;               // horse post number, e.g. "#18 Further Ado"
    if (/^\d{4}\b/.test(trimmed)) return false;              // year-prefixed event, e.g. "2026 Kentucky Derby"
    if (EVENT_KEYWORDS.test(trimmed)) return false;          // event/race name, not a team
    return true;
  };
  const validName = isLikelyTeamName(name) ? name : null;

  // The sportsApi fallback (TheSportsDB) only knows team sports. For
  // non-team sports it returns its no-match default (Arsenal). Only allow
  // the fallback when sport is unknown OR is plausibly a team sport.
  const fuzzyFallbackOk = !sport || !NON_TEAM_SPORTS.has(sport);

  // Priority: direct prop → ESPN CDN abbreviation → sportsApi cache (ESPN + TheSportsDB)
  // Only consult lookup tables if the name is plausibly a real team.
  const src = logoUrl || (validName && (getTeamLogo(validName, sport) || (fuzzyFallbackOk && getTeamLogoUrl(validName))));

  // If no src yet but sportsApi might fetch one async, re-render after a delay (once only)
  useEffect(() => {
    if (!src && validName && !scheduled.current) {
      scheduled.current = true;
      const t = setTimeout(() => forceUpdate(n => n + 1), 1500);
      return () => clearTimeout(t);
    }
    if (src) scheduled.current = false; // reset if src becomes available (name changed)
  }, [src, validName]);

  const initials = (name || "?").split(" ").map(w => w[0]).slice(-2).join("").toUpperCase();
  if (!src || err) {
    return (
      <div style={{
        width:size, height:size, borderRadius:"50%", flexShrink:0,
        background:"rgba(255,255,255,0.08)", display:"flex", alignItems:"center",
        justifyContent:"center", fontSize:size*0.36, fontWeight:700,
        color:"rgba(255,255,255,0.4)", ...s,
      }}>{initials}</div>
    );
  }
  return (
    <img src={src} alt={name} width={size} height={size} loading="lazy"
      onError={() => setErr(true)}
      style={{ borderRadius:"50%", objectFit:"contain", flexShrink:0, ...s }}
    />
  );
}


/* ===========================================
   APP VERSION
=========================================== */
const APP_VERSION = "1.0.0";
const timeAgo = (ts) => {
  const d = typeof ts === 'number' ? ts : ts?.toDate ? ts.toDate() : ts?.seconds ? new Date(ts.seconds * 1000) : new Date(ts || Date.now());
  const date = d instanceof Date ? d : new Date(d);
  const secs = (Date.now() - date.getTime()) / 1000;

  // Fresh times stay precise — when you're checking live picks, "5m ago" is more
  // useful than "moments ago." Switch to conversational once we cross "today."
  if (secs < 60) return "just now";
  if (secs < 300) return "moments ago";       // 1-5 min
  if (secs < 3600) return `${Math.floor(secs/60)}m ago`;
  if (secs < 86400) return `${Math.floor(secs/3600)}h ago`;

  // Beyond 24h, switch to calendar-relative phrasing — feels more like how
  // a person tells a story than "27h ago."
  const now = new Date();
  const sameDay = (a, b) =>
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();

  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (sameDay(date, yesterday)) return "yesterday";

  const daysAgo = Math.floor((now.getTime() - date.getTime()) / 86400000);
  if (daysAgo < 7) {
    // 2-6 days ago: name the weekday ("Monday", "Tuesday")
    return date.toLocaleDateString("en-US", { weekday: "long" });
  }
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
};


// ── Daily Streak System ──────────────────────────────────────────
const STREAK_KEY = "torch_daily_streak";
const getStreak = () => {
  try {
    const raw = JSON.parse(localStorage.getItem(STREAK_KEY) || "{}");
    const today = new Date().toLocaleDateString("en-CA");
    const yesterday = new Date(Date.now() - 86400000).toLocaleDateString("en-CA");
    if (raw.lastPickDate === today) return { count: raw.count || 1, pickedToday: true };
    if (raw.lastPickDate === yesterday) return { count: raw.count || 0, pickedToday: false };
    return { count: 0, pickedToday: false }; // streak broken
  } catch { return { count: 0, pickedToday: false }; }
};
const recordDailyPick = () => {
  try {
    const today = new Date().toLocaleDateString("en-CA");
    const raw = JSON.parse(localStorage.getItem(STREAK_KEY) || "{}");
    if (raw.lastPickDate === today) return; // already counted today
    const yesterday = new Date(Date.now() - 86400000).toLocaleDateString("en-CA");
    const newCount = raw.lastPickDate === yesterday ? (raw.count || 0) + 1 : 1;
    localStorage.setItem(STREAK_KEY, JSON.stringify({ count: newCount, lastPickDate: today }));
    if (newCount === 7) window.torchToast?.("7-day streak.", "success");
    if (newCount === 30) window.torchToast?.("30-day streak.", "success");
  } catch {}
};


// ── Welcome Back System ──────────────────────────────────────────
const LAST_VISIT_KEY = "torch_last_visit";
const getWelcomeBack = (userStats, userLeagues) => {
  try {
    const now = Date.now();
    const last = parseInt(localStorage.getItem(LAST_VISIT_KEY) || "0");
    localStorage.setItem(LAST_VISIT_KEY, String(now));
    if (!last || now - last < 3600000) return null; // less than 1 hour = not "away"
    const hoursAway = Math.floor((now - last) / 3600000);
    return { hoursAway };
  } catch { return null; }
};

const __DEV__ = typeof window !== "undefined" && (window.location?.hostname === "localhost" || window.location?.hostname === "127.0.0.1");


const SPORT_FILTER_MAP = {
  "basketball_nba":"NBA","basketball_wnba":"WNBA","basketball_ncaab":"NCAAB",
  "americanfootball_nfl":"NFL","americanfootball_ncaaf":"NCAAF",
  "baseball_mlb":"MLB","icehockey_nhl":"NHL",
  "soccer":"Soccer","soccer_epl":"Soccer","soccer_usa_mls":"Soccer","soccer_spain_la_liga":"Soccer",
  "soccer_italy_serie_a":"Soccer","soccer_germany_bundesliga":"Soccer",
  "soccer_france_ligue_one":"Soccer","soccer_uefa_champs_league":"Soccer",
  "soccer_fifa_world_cup":"Soccer",
  "mma_mixed_martial_arts":"MMA","golf":"Golf","golf_pga_tour":"Golf","tennis":"Tennis",
};

// ── Player-prop stat ordering ──────────────────────────────────
// The book returns prop stat-groups in arbitrary order. For a full NFL slate
// that buries the props people actually want (QB passing, skill-position yards)
// below niche ones. This map ranks the marquee stats first per sport; anything
// not listed sorts after, alphabetically, so ordering is always stable and
// predictable. Keys are the LOWERCASED display label (statLabel), matching how
// propsByStat groups. Lower number = higher on the list.
const PROP_STAT_PRIORITY = {
  americanfootball_nfl: {
    "pass yds": 1, "passing yards": 1, "pass yards": 1,
    "pass tds": 2, "passing tds": 2, "passing touchdowns": 2,
    "rush yds": 3, "rushing yards": 3, "rush yards": 3,
    "receiving yds": 4, "receiving yards": 4, "reception yds": 4,
    "receptions": 5,
    "rush tds": 6, "rushing tds": 6,
    "receiving tds": 7,
    "pass completions": 8, "completions": 8,
    "pass attempts": 9, "interceptions": 10,
    "rush attempts": 11,
  },
  americanfootball_ncaaf: {
    "pass yds": 1, "passing yards": 1, "pass tds": 2, "passing tds": 2,
    "rush yds": 3, "rushing yards": 3, "receiving yds": 4, "receiving yards": 4,
    "receptions": 5,
  },
  basketball_nba: {
    "points": 1, "rebounds": 2, "assists": 3,
    "pts + reb + ast": 4, "points + rebounds + assists": 4,
    "threes": 5, "3-pointers": 5, "steals": 6, "blocks": 7, "turnovers": 8,
  },
  baseball_mlb: {
    "hits": 1, "total bases": 2, "home runs": 3, "rbis": 4,
    "runs": 5, "strikeouts": 6, "pitcher strikeouts": 6,
  },
  icehockey_nhl: {
    "points": 1, "goals": 2, "assists": 3, "shots on goal": 4, "saves": 5,
  },
};
// Returns a sort rank for a stat label under a given sport. Unknown → large
// number so it falls to the end; ties broken alphabetically by the caller.
function propStatRank(sportKey, statLabel) {
  const table = PROP_STAT_PRIORITY[sportKey];
  if (!table) return 999;
  return table[String(statLabel).toLowerCase()] ?? 999;
}

// ── Seasonal League Templates ──────────────────────────────────
const SEASON_TEMPLATES = [
  { id:"us-open-2026", name:"US Open", emoji:"⛳", eventSport:"golf",
    startDate:"2026-06-18", endDate:"2026-06-21",
    desc:"Shinnecock Hills — tournament winner & matchup picks",
    accent:"#4ADE80", gradient:"linear-gradient(135deg, #064E3B, #065F46)" },
  { id:"nhl-playoffs-2026", name:"NHL Playoffs", emoji:"🏒", eventSport:"icehockey_nhl",
    startDate:"2026-04-18", endDate:"2026-06-21",
    desc:"Stanley Cup Playoff predictions",
    accent:"#4ADE80", gradient:"linear-gradient(135deg, #0C4A6E, #075985)" },
  { id:"nba-playoffs-2026", name:"NBA Playoffs", emoji:"🏀", eventSport:"basketball_nba",
    startDate:"2026-04-18", endDate:"2026-06-30",
    desc:"Playoff bracket picks — first round through the Finals",
    accent:"#4ADE80", gradient:"linear-gradient(135deg, #7C2D12, #9A3412)" },
  { id:"mlb-2026", name:"MLB Season", emoji:"⚾", eventSport:"baseball_mlb",
    startDate:"2026-03-26", endDate:"2026-10-01",
    desc:"Daily baseball picks all season long",
    accent:"#4ADE80", gradient:"linear-gradient(135deg, #1E3A5F, #1E40AF)" },
  // Bracket pool — see ./worldCup2026Config for groups, teams, scoring.
  // The `format` field flows into the league doc via handleCreate; downstream
  // UI branches on `format === "bracket_pool"` to render the bracket experience.
  { id:"world-cup-2026", name:"World Cup 2026", emoji:"🏆", eventSport:"soccer",
    startDate:"2026-06-11", endDate:"2026-07-19",
    desc:"FIFA World Cup bracket pool — predict every round, win up to 710 pts",
    accent:"#4ADE80", gradient:"linear-gradient(135deg, #6B21A8, #7C3AED)",
    format: WC2026_FORMAT },
  { id:"nfl-2026", name:"NFL Season", emoji:"🏈", eventSport:"americanfootball_nfl",
    // Week 1 kickoff is Thu Sep 9 2026 — must match NFL_2026_WEEK_STARTS[1].
    // Kept as a bare YYYY-MM-DD because this value feeds a <input type="date">
    // via setCreateStartDate(); an ISO string with an offset would break it.
    // Since the startDate submission gate was removed, this is a scheduling
    // label only and is never used as a pick lock.
    startDate:"2026-09-09", endDate:"2027-02-15",
    desc:"Full 18-week season — pick every Sunday, Monday, Thursday",
    accent:"#4ADE80", gradient:"linear-gradient(135deg, #14532D, #166534)" },
  { id:"epl-2026", name:"Premier League", emoji:"⚽", eventSport:"soccer_epl",
    startDate:"2026-08-14", endDate:"2027-05-31",
    desc:"English Premier League — matchweek picks",
    accent:"#4ADE80", gradient:"linear-gradient(135deg, #4C1D95, #5B21B6)" },
  { id:"custom", name:"Custom League", emoji:"🔥", eventSport:null,
    startDate:null, endDate:"",
    desc:"Set your own rules — any sport, any duration",
    accent:"#FF5722", gradient:"linear-gradient(135deg, #7C2D12, #B91C1C)" },
];

// ROUND 11: hide templates whose event has already ended. Without this the
// wizard offered US Open / NBA Playoffs / World Cup 2026 months after they
// finished -- and because leagues auto-end when endDate passes, tapping one
// created a league the auto-end cron closed almost immediately ("Season
// over!" with a champion declared on day one). Upcoming events stay visible:
// creating a league ahead of a season is the whole NFL play. endDate is a
// bare YYYY-MM-DD, so compare against end-of-day to keep a template alive
// through its final day.
function isTemplateLive(t) {
  if (!t.endDate) return true;
  return new Date(t.endDate + "T23:59:59") >= new Date();
}

// ── NFL LEAGUE-FORMATION WINDOW ──────────────────────────────────────────────
// During the late-July → Week-1 window, NFL is the headline reason people come
// to Torch (the acquisition campaign sends commissioners here to "start your
// NFL league"). We surface a featured NFL tile at the top of the create flow
// during this window so those commissioners don't have to dig the NFL template
// out of the "event" sub-picker. Outside the window it's hidden and the create
// flow is unchanged. Mirrors the landing-page seasonal flag.
//
// Window: ~6 weeks before kickoff (campaign ramp) through end of Week 1, when
// the league-formation impulse is live. A commissioner who hasn't started by
// then mostly won't. Override with window.__TORCH_NFL_FEATURE for manual flips
// (parallels NFL_SEASON_MESSAGING on the landing page).
const NFL_FEATURE_WINDOW = {
  startMs: new Date("2026-07-28T00:00:00Z").getTime(), // campaign ramp begins
  endMs:   new Date("2026-09-15T00:00:00Z").getTime(), // ~end of Week 1
};
const NFL_TEMPLATE = SEASON_TEMPLATES.find(t => t.id === "nfl-2026") || null;
function isNflFeatureWindow() {
  try {
    if (typeof window !== "undefined" && typeof window.__TORCH_NFL_FEATURE === "boolean") {
      return window.__TORCH_NFL_FEATURE; // manual override (true=force on, false=force off)
    }
  } catch {}
  const now = Date.now();
  return now >= NFL_FEATURE_WINDOW.startMs && now <= NFL_FEATURE_WINDOW.endMs;
}

// ── NFL SEASON STATE ─────────────────────────────────────────────────────────
// Pure helper: given a date, returns where we are in the 2026 NFL season so the
// league UI can show real context. Three phases: "preseason" (carries
// daysToKickoff), "week" (1–18), "offseason".
//
// 2026-07 FIX: week numbers are now a LOOKUP TABLE of the real week-start dates,
// not flat 7-day arithmetic. The NFL schedule is NOT a clean 18×7: Week 1 opens
// Wed Sep 9, Thanksgiving compresses Week 11→12 to 6 days and stretches 12→13 to
// 8, and the season ends Sun Jan 10 2027. Flat math drifted ±1 week by December
// (users would see the wrong week). Table verified against the official schedule
// incl. Thanksgiving (W12) and Christmas (W16) anchors.
//
// Boundaries are the START (00:00 ET) of each week; week N is current from
// NFL_2026_WEEK_STARTS[N] (inclusive) until [N+1] (exclusive). Offseason begins
// NFL_2026_SEASON_END (day after the final Sunday, Jan 10 2027).
const NFL_2026_WEEK_STARTS = [
  null,
  "2026-09-09T00:00:00-04:00", "2026-09-17T00:00:00-04:00", "2026-09-24T00:00:00-04:00",
  "2026-10-01T00:00:00-04:00", "2026-10-08T00:00:00-04:00", "2026-10-15T00:00:00-04:00",
  "2026-10-22T00:00:00-04:00", "2026-10-29T00:00:00-04:00", "2026-11-05T00:00:00-05:00",
  "2026-11-12T00:00:00-05:00", "2026-11-19T00:00:00-05:00", "2026-11-25T00:00:00-05:00",
  "2026-12-03T00:00:00-05:00", "2026-12-10T00:00:00-05:00", "2026-12-17T00:00:00-05:00",
  "2026-12-24T00:00:00-05:00", "2026-12-31T00:00:00-05:00", "2027-01-07T00:00:00-05:00",
].map((s) => (s ? new Date(s).getTime() : null));
const NFL_2026_KICKOFF = NFL_2026_WEEK_STARTS[1];               // Wed, Week 1 (ET)
const NFL_2026_SEASON_END = new Date("2027-01-11T00:00:00-05:00").getTime(); // offseason begins
const NFL_2026_WEEKS = 18;
function getNflSeasonState(now = new Date()) {
  const t = (now instanceof Date ? now.getTime() : Number(now));
  const DAY = 86400000;
  if (t < NFL_2026_KICKOFF) {
    return { phase: "preseason", daysToKickoff: Math.ceil((NFL_2026_KICKOFF - t) / DAY), week: null };
  }
  if (t >= NFL_2026_SEASON_END) {
    return { phase: "offseason", daysToKickoff: null, week: null };
  }
  // Walk weeks high→low; first start we're at/after is the current week.
  for (let n = NFL_2026_WEEKS; n >= 1; n--) {
    if (t >= NFL_2026_WEEK_STARTS[n]) {
      return { phase: "week", daysToKickoff: null, week: n };
    }
  }
  // Shouldn't reach here (t >= kickoff == week1 start), but be safe.
  return { phase: "week", daysToKickoff: null, week: 1 };
}

// Pure week lookup for an ARBITRARY game time — NOT the same thing as
// getNflSeasonState(), which answers "what week is it right now".
//
// getNflSeasonState CANNOT be reused here. It gates on the current instant:
// it early-returns {week:null} for "preseason" (t < kickoff) and "offseason"
// (t >= season end) before it ever walks the week table. Feeding it a game
// time works only while that game happens to sit inside the live season —
// during the preseason window (which is exactly when NFL leagues are being
// created) EVERY game would come back null and no header would ever render.
// A wrong-but-plausible reuse of that helper parses and tests clean, so this
// is called out explicitly: keep the two functions separate.
//
// Returns 1–18 for a time inside the 2026 season, or null outside it (and for
// unparseable input). Same reverse-walk over the same table as the state
// helper, so the two can never disagree about a boundary.
function getNflWeekForTime(ms) {
  const t = (ms instanceof Date) ? ms.getTime() : Number(ms);
  if (!Number.isFinite(t)) return null;
  if (t < NFL_2026_KICKOFF || t >= NFL_2026_SEASON_END) return null;
  for (let n = NFL_2026_WEEKS; n >= 1; n--) {
    if (t >= NFL_2026_WEEK_STARTS[n]) return n;
  }
  return null;
}

// Which week should a board DEFAULT to right now?
//
// getNflWeekForTime(Date.now()) is null for ~7 months of the year, so a board
// that filtered on it directly would show an EMPTY slate during the preseason —
// exactly when leagues are being formed and commissioners are looking at the
// board to decide whether the app works. This resolver never returns null
// in-or-near season:
//   before kickoff  → 1  (show the Week 1 slate people are signing up for)
//   during season   → the live week
//   after season end→ 18 (the last real slate, rather than nothing)
// Returns null only when the table itself can't answer — no 2026 season data.
function getNflActiveWeek(now = Date.now()) {
  const t = (now instanceof Date) ? now.getTime() : Number(now);
  if (!Number.isFinite(t)) return 1;
  if (t < NFL_2026_KICKOFF) return 1;
  if (t >= NFL_2026_SEASON_END) return NFL_2026_WEEKS;
  return getNflWeekForTime(t) ?? 1;
}

// PICKEM-WEEK-TABS (fix 2026-10-08): which week a board shows.
//   picked    -- the week the user tapped, or null to follow the calendar
//   available -- weeks the feed actually has games for (sorted)
//   active    -- getNflActiveWeek() right now
// The board used to hold the week it mounted with forever (pages stay mounted
// in the background), so a board opened during Week 4 still said Week 4 on
// Thursday of Week 5 -- and because Week 4 had left the feed, the window fell
// back to showing EVERY game. Now: an available pick wins; otherwise the
// current week; otherwise the next week that has games; otherwise the last
// one. Only returns a week the feed has, unless the feed has none.
function resolveBoardNflWeek(picked, available, active) {
  const av = Array.isArray(available) ? available : [];
  if (av.length === 0) return active ?? null;
  if (picked != null && av.includes(picked)) return picked;
  if (active != null && av.includes(active)) return active;
  const next = av.find(w => active == null || w > active);
  return next != null ? next : av[av.length - 1];
}

// True when a league is the NFL-season template (drives the season banner).
function isNflSeasonLeague(leagueObj) {
  if (!leagueObj) return false;
  return leagueObj.templateId === "nfl-2026" ||
         leagueObj.eventSport === "americanfootball_nfl" ||
         leagueObj.sportFilter === "americanfootball_nfl";
}

// ── NFL SEASON BANNER ────────────────────────────────────────────────────────
// Flat status strip shown inside an NFL-template league. Preseason → countdown
// to kickoff; in-season → "Week N of 18" with the slate framing. Design-system
// compliant: solid flat surface, single coral left-rule, no gradient/glow,
// fontWeight ≤700, tabular numerals. Renders null in the offseason so it never
// lingers as dead chrome.
function NflSeasonBanner({ style = {} }) {
  const s = getNflSeasonState();
  if (s.phase === "offseason") return null;

  const CORAL = "#FF6B3D";
  const wrap = {
    display: "flex", alignItems: "center", gap: 12,
    padding: "10px 14px",
    background: "rgba(255,107,61,0.06)",
    borderLeft: `3px solid ${CORAL}`,
    borderRadius: "0 10px 10px 0",
    ...style,
  };
  const num = { fontWeight: 700, fontVariantNumeric: "tabular-nums", color: "#fff", fontSize: 15 };
  const label = { fontSize: 12, color: "rgba(255,255,255,0.55)", fontWeight: 600 };

  if (s.phase === "preseason") {
    const d = s.daysToKickoff;
    // Derive the kickoff day/date from NFL_2026_WEEK_STARTS[1] rather than
    // hardcoding it. A hardcoded day name silently rots the moment the date
    // changes: this line previously read "Thursday, Sept 10", was corrected to
    // Sept 9 when the real kickoff was confirmed, and kept saying "Thursday"
    // even though 2026-09-09 is a WEDNESDAY. Formatting from the same constant
    // the countdown uses means the two can never disagree again.
    const kickoffLabel = new Date(NFL_2026_KICKOFF).toLocaleDateString("en-US", {
      weekday: "long", month: "short", day: "numeric", timeZone: "America/New_York",
    });
    // Subtitle does double duty: it names the real kickoff AND explains the
    // empty slate. The Odds API only returns games inside its own rolling
    // window, so an NFL league created in July has no games to show yet.
    // Without this line that reads as a broken app rather than a schedule that
    // simply hasn't posted.
    return (
      <div style={wrap}>
        <span style={{ fontSize: 16 }}>🏈</span>
        <div style={{ display: "flex", flexDirection: "column", lineHeight: 1.3 }}>
          <span style={num}>
            Kickoff in {d} day{d === 1 ? "" : "s"}
          </span>
          <span style={label}>Week 1 · {kickoffLabel} — invite your crew now, games appear when the odds post</span>
        </div>
      </div>
    );
  }

  // in-season
  return (
    <div style={wrap}>
      <span style={{ fontSize: 16 }}>🏈</span>
      <div style={{ display: "flex", flexDirection: "column", lineHeight: 1.3 }}>
        <span style={num}>
          Week {s.week} <span style={{ color: "rgba(255,255,255,0.45)", fontWeight: 600 }}>of {NFL_2026_WEEKS}</span>
        </span>
        <span style={label}>Thursday · Sunday · Monday slate</span>
      </div>
    </div>
  );
}

// ── SPECIAL EVENTS ─────────────────────────────────────────────────────────
// One-off events not covered by the Odds API (e.g. Kentucky Derby).
// Modeled identically to outright tournament games (like the Masters) so the
// existing pick UI, slip flow, and admin tournament-settle tab all "just work".
//
// To settle: open Admin Panel → Tournament tab → enter the winning horse name.
// Picks are filtered into the tournament tab by sportKey containing "_winner".
// Auto-settlement is skipped because the sportKey contains "_winner".
//
// To deactivate after the race: set `enabled: false` and redeploy hosting.
const KENTUCKY_DERBY_2026 = {
  enabled: false, // race over (May 2, 2026) -- flipped to hide from home + games. Keep config so next year's edition just needs date/field updates.
  raceTimeMs:    new Date('2026-05-02T22:57:00Z').getTime(), // 6:57 PM ET post time
  showFromMs:    new Date('2026-04-25T00:00:00Z').getTime(), // appear ~1 week before
  hideAfterMs:   new Date('2026-05-04T00:00:00Z').getTime(), // hide 2 days after race
  // Field as drawn (pre-Silent-Tactic scratch shift). Sorted by morning-line
  // odds so favorites surface first in the picker. American odds format.
  // Source: NBC Sports / America's Best Racing / Heavy.com (April 25 post draw)
  field: [
    { name: 'Renegade',         post:  1, odds:   400 },
    { name: 'Commandment',      post:  6, odds:   600 },
    { name: 'Further Ado',      post: 18, odds:   600 },
    { name: 'Chief Wallabee',   post: 12, odds:   800 },
    { name: 'The Puma',         post:  9, odds:  1000 },
    { name: 'So Happy',         post:  8, odds:  1500 },
    { name: 'Emerging Market',  post: 15, odds:  1500 },
    { name: 'Danon Bourbon',    post:  7, odds:  2000 },
    { name: 'Incredibolt',      post: 11, odds:  2000 },
    { name: 'Potente',          post: 14, odds:  2000 },
    { name: 'Fulleffort',       post: 20, odds:  2000 },
    { name: 'Albus',            post:  2, odds:  3000 },
    { name: 'Litmus Test',      post:  4, odds:  3000 },
    { name: 'Right to Party',   post:  5, odds:  3000 },
    { name: 'Wonder Dean',      post: 10, odds:  3000 },
    { name: 'Pavlovian',        post: 16, odds:  3000 },
    { name: 'Golden Tempo',     post: 19, odds:  3000 },
    { name: 'Intrepido',        post:  3, odds:  5000 },
    { name: 'Six Speed',        post: 17, odds:  5000 },
    { name: 'Great White',      post: 21, odds:  5000 }, // also-elig, drew in for Silent Tactic
  ],
};

// Build a synthetic raw-game object that adaptGame() can normalize, OR null if
// the event is disabled / outside its display window. Returns the same shape
// that fetchLiveGames() returns (pre-adaptation).
function buildKentuckyDerbyRawGame() {
  const D = KENTUCKY_DERBY_2026;
  if (!D.enabled) return null;
  const now = Date.now();
  if (now < D.showFromMs || now > D.hideAfterMs) return null;
  // Mark completed once 30 min past post time (race is 2 minutes, settlement happens later)
  const completed = now > D.raceTimeMs + 30 * 60 * 1000;
  // Derive Place (top 2) and Show (top 3) odds from Win odds.
  // Heuristic for a 20-horse field: place ≈ win/2, show ≈ win/3, rounded to nearest 10
  // and floored at +100 (no horse in a 20-horse field is ever shorter than +100 to show).
  const round10 = (n) => Math.max(100, Math.round(n / 10) * 10);
  return {
    id: 'kentucky_derby_2026',
    sport: 'Derby',
    sportKey: 'horseracing_kentucky_derby_winner', // contains "_winner" → admin-settle path
    homeTeam: '2026 Kentucky Derby',
    awayTeam: 'Run for the Roses',
    homeTeamAbbr: 'DERBY',
    awayTeamAbbr: 'ROSES',
    time: new Date(D.raceTimeMs).toISOString(),
    isLive: false,
    completed,
    isOutright: true,
    outrightMarkets: [
      { key: 'win',   label: 'Win',   shortLabel: 'WIN',   subtext: 'Finish 1st' },
      { key: 'place', label: 'Place', shortLabel: 'PLACE', subtext: 'Finish top 2' },
      { key: 'show',  label: 'Show',  shortLabel: 'SHOW',  subtext: 'Finish top 3' },
    ],
    outrightOutcomes: D.field.map(h => ({
      name: `#${h.post} ${h.name}`,
      odds: h.odds,                  // win odds (kept on `odds` for back-compat)
      placeOdds: round10(h.odds / 2),
      showOdds:  round10(h.odds / 3),
    })),
    // Custom labels picked up by the outright UI
    outrightHeader: '🌹 Run for the Roses — Win / Place / Show',
    outrightEntryLabel: 'horses',
  };
}

// Inject all active special events into a raw games array.
// Called from every fetchLiveGames() consumer so the Derby shows up consistently.
function injectSpecialEvents(rawGames) {
  if (!Array.isArray(rawGames)) rawGames = [];
  const derby = buildKentuckyDerbyRawGame();
  if (derby && !rawGames.some(g => g?.id === derby.id)) {
    return [derby, ...rawGames];
  }
  return rawGames;
}

// ── Template state helpers — used to surface "LIVE NOW" vs "STARTS [MONTH]" ──
// pills on the create-league preset grid. A template is "in season" if today
// falls between its startDate and endDate. We also expose a short upcoming
// label ("STARTS JUN") for presets whose startDate is still in the future.
function isInSeason(tmpl, nowMs = Date.now()) {
  if (!tmpl?.startDate || !tmpl?.endDate) return false;
  const start = new Date(tmpl.startDate).getTime();
  const end   = new Date(tmpl.endDate).getTime();
  return Number.isFinite(start) && Number.isFinite(end) && nowMs >= start && nowMs <= end;
}
function upcomingMonthLabel(tmpl, nowMs = Date.now()) {
  if (!tmpl?.startDate) return null;
  const start = new Date(tmpl.startDate).getTime();
  if (!Number.isFinite(start) || start <= nowMs) return null;
  const months = ["JAN","FEB","MAR","APR","MAY","JUN","JUL","AUG","SEP","OCT","NOV","DEC"];
  const d = new Date(start);
  return `STARTS ${months[d.getMonth()]}`;
}
// Returns an ISO date string N days from today (YYYY-MM-DD). Used as the
// default end date for Custom leagues and as a fallback floor.
function defaultCustomEndDateISO(daysFromNow = 90) {
  const d = new Date();
  d.setDate(d.getDate() + daysFromNow);
  return d.toISOString().slice(0, 10);
}

// ── Cyberpunk Sport Icons — neon glow, thin lines, futuristic feel ──────────
const SPORT_ACCENTS = {
  golf:                { primary: "#4ade80", secondary: "#22d3ee", glow: "rgba(74,222,128,0.6)" },
  icehockey_nhl:       { primary: "#00d4ff", secondary: "#a855f7", glow: "rgba(0,212,255,0.6)" },
  basketball_nba:      { primary: "#ff6b2b", secondary: "#fbbf24", glow: "rgba(255,107,43,0.6)" },
  baseball_mlb:        { primary: "#f43f5e", secondary: "#fb923c", glow: "rgba(244,63,94,0.6)" },
  soccer:              { primary: "#a855f7", secondary: "#ec4899", glow: "rgba(168,85,247,0.6)" },
  soccer_epl:          { primary: "#a855f7", secondary: "#ec4899", glow: "rgba(168,85,247,0.6)" },
  americanfootball_nfl:{ primary: "#22d3ee", secondary: "#3b82f6", glow: "rgba(34,211,238,0.6)" },
  default:             { primary: "#fbbf24", secondary: "#f97316", glow: "rgba(251,191,36,0.6)" },
};

const SportIcon = ({ sport, size = 28, color }) => {
  const acc = SPORT_ACCENTS[sport] || SPORT_ACCENTS.default;
  const c = color || acc.primary;
  const uid = `si_${sport || "def"}_${Math.random().toString(36).slice(2,6)}`;
  const s = { width: size, height: size, display: "block", margin: "0 auto" };
  const glowFilter = (
    <defs>
      <filter id={`glow_${uid}`} x="-50%" y="-50%" width="200%" height="200%">
        <feGaussianBlur stdDeviation="1.5" result="blur"/>
        <feMerge><feMergeNode in="blur"/><feMergeNode in="SourceGraphic"/></feMerge>
      </filter>
      <linearGradient id={`grad_${uid}`} x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor={acc.primary}/>
        <stop offset="100%" stopColor={acc.secondary}/>
      </linearGradient>
    </defs>
  );
  const p = { fill: "none", stroke: `url(#grad_${uid})`, strokeWidth: 1.4, strokeLinecap: "round", strokeLinejoin: "round", filter: `url(#glow_${uid})` };

  switch (sport) {
    case "golf":
      return (<svg viewBox="0 0 24 24" style={s}>{glowFilter}
        <path d="M11 18V4l7.5 3.8L11 11.5" {...p} fill={acc.primary} fillOpacity="0.12"/>
        <circle cx="11" cy="20.5" r="1.5" {...p} strokeWidth="1.2"/>
        <path d="M6.5 22c0-1.8 2-2.8 4.5-2.8s4.5 1 4.5 2.8" {...p} strokeWidth="1"/>
        {/* Circuit accent */}
        <line x1="18" y1="2" x2="18" y2="5" stroke={acc.secondary} strokeWidth="0.5" opacity="0.35"/>
        <circle cx="18" cy="5.5" r="0.6" fill={acc.secondary} opacity="0.3"/>
        <line x1="20" y1="4" x2="22" y2="4" stroke={acc.secondary} strokeWidth="0.4" opacity="0.25"/>
      </svg>);
    case "icehockey_nhl":
      return (<svg viewBox="0 0 24 24" style={s}>{glowFilter}
        {/* Stick */}
        <path d="M6 3l4 14h4L18 3" {...p} strokeWidth="1.3"/>
        <path d="M10 17l-3 3h10l-3-3" {...p} fill={acc.primary} fillOpacity="0.08"/>
        {/* Puck */}
        <ellipse cx="12" cy="21" rx="4" ry="1.2" {...p} fill={acc.primary} fillOpacity="0.15" strokeWidth="1.2"/>
        {/* Ice crystal accents */}
        <line x1="2" y1="10" x2="4" y2="10" stroke={acc.secondary} strokeWidth="0.5" opacity="0.3"/>
        <line x1="3" y1="9" x2="3" y2="11" stroke={acc.secondary} strokeWidth="0.5" opacity="0.3"/>
        <circle cx="21" cy="8" r="0.5" fill={acc.secondary} opacity="0.25"/>
      </svg>);
    case "basketball_nba":
      return (<svg viewBox="0 0 24 24" style={s}>{glowFilter}
        <circle cx="12" cy="12" r="9" {...p}/>
        <path d="M12 3v18" {...p} strokeWidth="1"/>
        <path d="M3 12h18" {...p} strokeWidth="1"/>
        <path d="M5.2 5.5c3.2 1.8 4.8 5 5.2 8.8" {...p} strokeWidth="1"/>
        <path d="M18.8 5.5c-3.2 1.8-4.8 5-5.2 8.8" {...p} strokeWidth="1"/>
        {/* Digital scan lines */}
        <line x1="3.5" y1="8" x2="5.5" y2="8" stroke={acc.secondary} strokeWidth="0.4" opacity="0.3"/>
        <line x1="18.5" y1="16" x2="20.5" y2="16" stroke={acc.secondary} strokeWidth="0.4" opacity="0.3"/>
      </svg>);
    case "baseball_mlb":
      return (<svg viewBox="0 0 24 24" style={s}>{glowFilter}
        <circle cx="12" cy="12" r="9" {...p}/>
        {/* Stitching */}
        <path d="M6.8 4.2c.8 2.2.7 5-.3 7.5s-1.2 4.2-.2 6.2" {...p} strokeWidth="1.2" strokeDasharray="1.5 1.5"/>
        <path d="M17.2 4.2c-.8 2.2-.7 5 .3 7.5s1.2 4.2.2 6.2" {...p} strokeWidth="1.2" strokeDasharray="1.5 1.5"/>
        {/* Digital accent */}
        <circle cx="12" cy="12" r="2" stroke={acc.secondary} strokeWidth="0.4" fill="none" opacity="0.2"/>
      </svg>);
    case "soccer":
    case "soccer_epl":
      return (<svg viewBox="0 0 24 24" style={s}>{glowFilter}
        <circle cx="12" cy="12" r="9" {...p}/>
        {/* Pentagon pattern */}
        <polygon points="12,5.5 15,8.5 14,12 10,12 9,8.5" {...p} fill={acc.primary} fillOpacity="0.12" strokeWidth="1"/>
        <line x1="12" y1="3" x2="12" y2="5.5" {...p} strokeWidth="0.8"/>
        <line x1="15" y1="8.5" x2="20" y2="7.5" {...p} strokeWidth="0.8"/>
        <line x1="14" y1="12" x2="18" y2="16" {...p} strokeWidth="0.8"/>
        <line x1="10" y1="12" x2="6" y2="16" {...p} strokeWidth="0.8"/>
        <line x1="9" y1="8.5" x2="4" y2="7.5" {...p} strokeWidth="0.8"/>
      </svg>);
    case "americanfootball_nfl":
      return (<svg viewBox="0 0 24 24" style={s}>{glowFilter}
        <ellipse cx="12" cy="12" rx="9" ry="5.5" transform="rotate(-30 12 12)" {...p}/>
        {/* Laces */}
        <path d="M10 9.5l4 5" {...p} strokeWidth="1.2"/>
        <path d="M9.5 8.2l.8.6M11 10.5l.8.6M12.5 12.8l.8.6M14 15.2l.8.6" {...p} strokeWidth="1"/>
        {/* Circuit nodes */}
        <circle cx="4" cy="6" r="0.5" fill={acc.secondary} opacity="0.25"/>
        <line x1="4" y1="6.5" x2="4" y2="9" stroke={acc.secondary} strokeWidth="0.4" opacity="0.2"/>
      </svg>);
    default:
      return (<svg viewBox="0 0 24 24" style={s}>{glowFilter}
        {/* Trophy / star hybrid */}
        <path d="M12 2l2.2 6.8H21l-5.5 4 2.2 6.8L12 15.5l-5.7 4.1 2.2-6.8L3 8.8h6.8z" {...p} fill={acc.primary} fillOpacity="0.1"/>
        <circle cx="12" cy="10" r="2" stroke={acc.secondary} strokeWidth="0.5" fill="none" opacity="0.3"/>
      </svg>);
  }
};

// ── Empty State SVG Icons (replaces emoji) ──────────────────────────────────
const EmptyStateIcon = ({ type = "default", size = 36 }) => {
  const s = { width: size, height: size, display: "block" };
  const accent = "#00d4ff";
  const dim = "rgba(0,212,255,0.3)";
  const filter = (id) => (
    <defs>
      <filter id={id} x="-50%" y="-50%" width="200%" height="200%">
        <feGaussianBlur stdDeviation="2" result="blur"/>
        <feMerge><feMergeNode in="blur"/><feMergeNode in="SourceGraphic"/></feMerge>
      </filter>
    </defs>
  );
  const uid = `esi_${Math.random().toString(36).slice(2,6)}`;
  const p = { fill: "none", stroke: accent, strokeWidth: 1.5, strokeLinecap: "round", strokeLinejoin: "round", filter: `url(#${uid})` };

  switch (type) {
    case "live": // replaces 📡
      return (<svg viewBox="0 0 24 24" style={s}>{filter(uid)}
        {/* Radar dish */}
        <path d="M12 14v6" {...p}/>
        <path d="M8 20h8" {...p}/>
        <circle cx="12" cy="12" r="2" {...p} fill={accent} fillOpacity="0.15"/>
        {/* Signal waves */}
        <path d="M8 8a5.7 5.7 0 0 1 8 0" {...p} strokeWidth="1.2"/>
        <path d="M5.5 5.5a9.2 9.2 0 0 1 13 0" {...p} strokeWidth="1" opacity="0.6"/>
        <path d="M3.5 3.5a12.5 12.5 0 0 1 17 0" {...p} strokeWidth="0.8" opacity="0.3"/>
      </svg>);
    case "schedule": // replaces 📅
      return (<svg viewBox="0 0 24 24" style={s}>{filter(uid)}
        <rect x="3" y="4" width="18" height="17" rx="3" {...p}/>
        <path d="M3 9h18" {...p} strokeWidth="1"/>
        <path d="M8 2v4M16 2v4" {...p}/>
        {/* Grid dots */}
        <circle cx="8" cy="13" r="0.8" fill={accent} opacity="0.5"/>
        <circle cx="12" cy="13" r="0.8" fill={accent} opacity="0.5"/>
        <circle cx="16" cy="13" r="0.8" fill={accent} opacity="0.3"/>
        <circle cx="8" cy="17" r="0.8" fill={accent} opacity="0.3"/>
        <circle cx="12" cy="17" r="0.8" fill={accent} opacity="0.3"/>
      </svg>);
    case "picks": // replaces →
      return (<svg viewBox="0 0 24 24" style={s}>{filter(uid)}
        <path d="M9 3l-4 9h6l-2 9 10-12h-6l3-6z" {...p} fill={accent} fillOpacity="0.1"/>
      </svg>);
    default:
      return (<svg viewBox="0 0 24 24" style={s}>{filter(uid)}
        <circle cx="12" cy="12" r="9" {...p} opacity="0.4"/>
        <circle cx="12" cy="12" r="4" {...p}/>
        <circle cx="12" cy="12" r="1" fill={accent} opacity="0.6"/>
      </svg>);
  }
};


const getHotSeasons = () => {
  const now = new Date();
  const cutoff = new Date(now.getTime() + 90 * 24 * 60 * 60 * 1000);
  return SEASON_TEMPLATES.filter(t => {
    if (!t.startDate || t.id === "custom") return false;
    const start = new Date(t.startDate);
    const end = t.endDate ? new Date(t.endDate) : new Date("2099-01-01");
    return (now >= start && now <= end) || (start > now && start <= cutoff);
  });
};

/* ===========================================
   PULL-TO-REFRESH HOOK
=========================================== */
function usePullToRefresh(onRefresh, { enabled = true } = {}) {
  const [pulling, setPulling] = useState(false);
  const [pullDistance, setPullDistance] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const startY = useRef(0);
  const isDragging = useRef(false);
  const pullDistanceRef = useRef(0); // ref mirror for use inside event handlers
  const onRefreshRef = useRef(onRefresh);
  useEffect(() => { onRefreshRef.current = onRefresh; }, [onRefresh]);
  const THRESHOLD = 80;

  useEffect(() => {
    if (!enabled) return;

    const onTouchStart = (e) => {
      // Only trigger when scrolled to top AND not inside a modal/bottom-sheet
      if (window.scrollY > 5) return;
      // Don't interfere with modal scrolling
      const target = e.target;
      if (target?.closest?.('[style*="position: fixed"]') || target?.closest?.('[style*="position:fixed"]')) return;
      startY.current = e.touches[0].clientY;
      isDragging.current = true;
    };

    const onTouchMove = (e) => {
      if (!isDragging.current) return;
      const dy = e.touches[0].clientY - startY.current;
      if (dy < 0) { isDragging.current = false; pullDistanceRef.current = 0; setPullDistance(0); return; }
      // Dampen the pull (feels more natural)
      const dampened = Math.min(dy * 0.4, THRESHOLD * 1.5);
      pullDistanceRef.current = dampened;
      setPullDistance(dampened);
      setPulling(dampened > 10);
      if (dampened > 20) e.preventDefault();
    };

    const onTouchEnd = async () => {
      if (!isDragging.current) return;
      isDragging.current = false;
      if (pullDistanceRef.current >= THRESHOLD) {
        setRefreshing(true);
        pullDistanceRef.current = THRESHOLD * 0.6;
        setPullDistance(THRESHOLD * 0.6);
        try { await onRefreshRef.current(); } catch {}
        setRefreshing(false);
      }
      setPulling(false);
      pullDistanceRef.current = 0;
      setPullDistance(0);
    };

    window.addEventListener("touchstart", onTouchStart, { passive: true });
    window.addEventListener("touchmove", onTouchMove, { passive: false });
    window.addEventListener("touchend", onTouchEnd);
    return () => {
      window.removeEventListener("touchstart", onTouchStart);
      window.removeEventListener("touchmove", onTouchMove);
      window.removeEventListener("touchend", onTouchEnd);
    };
  }, [enabled]);

  return { pulling, pullDistance, refreshing };
}

function PullToRefreshIndicator({ pullDistance, refreshing }) {
  if (pullDistance < 5 && !refreshing) return null;
  const pct = Math.min(pullDistance / 80, 1);
  return (
    <div style={{
      position: "fixed", top: 0, left: 0, right: 0, zIndex: 100,
      display: "flex", justifyContent: "center",
      paddingTop: Math.max(pullDistance - 10, 0),
      transition: refreshing ? "none" : "padding-top 0.2s ease",
      pointerEvents: "none",
    }}>
      <div style={{
        width: 32, height: 32, borderRadius: "50%",
        background: T.card, border: `1px solid ${T.border}`,
        display: "flex", alignItems: "center", justifyContent: "center",
        boxShadow: "0 4px 16px rgba(0,0,0,0.3)",
        opacity: pct,
        transform: `scale(${0.5 + pct * 0.5}) rotate(${refreshing ? 0 : pct * 180}deg)`,
        transition: refreshing ? "none" : "transform 0.1s ease",
      }}>
        {refreshing ? (
          <div style={{
            width: 16, height: 16, border: "2px solid transparent",
            borderTop: `2px solid ${T.coral}`, borderRadius: "50%",
            animation: "spin 0.6s linear infinite",
          }}/>
        ) : (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={T.coral} strokeWidth="2.5" strokeLinecap="round">
            <polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"/>
          </svg>
        )}
      </div>
    </div>
  );
}


/* ===========================================
   DESIGN TOKENS
=========================================== */
// ── ADMIN ────────────────────────────────────────────────────────────────────
// ADMIN-UID-GATE (fix 3.9, 2026-09-28): gate on the account UID, the same
// check firestore.rules isAdmin() and every admin Cloud Function use. The old
// email check showed the admin panel to any account signed up with one of
// those addresses (email/password sign-up doesn't prove you own the inbox);
// the server still refused its actions, but the panel and its reads were
// exposed.
const ADMIN_UID = "HTPWLwsbY7UCcftuCrlor5Hoj1f2";
const isAdmin = (user) => !!user?.uid && user.uid === ADMIN_UID;

const T = {
  // ---- BACKGROUNDS: warmer near-black, three elevations -------------------
  bg:         "#08080A",          // deepest base — near-black with subtle warmth
  bgWarm:     "#0E0E12",          // surface lift
  card:       "#0E0E12",          // card baseline
  cardAlt:    "#15151B",          // elevated card / hover
  cardHover:  "#1C1C24",          // highest elevation
  border:     "rgba(255,255,255,0.06)",
  borderMid:  "rgba(255,255,255,0.10)",
  borderHi:   "rgba(255,255,255,0.16)",

  // ---- ACCENT: ember orange (warmer than #FF5722, less neon) ---------------
  coral:      "#FF6B3D",
  coralDim:   "rgba(255,107,61,0.10)",
  coralGlow:  "rgba(255,107,61,0.06)",
  coralHot:   "rgba(255,107,61,0.22)",
  coralDeep:  "#E5562C",          // deep ember for gradients
  ember:      "#FF6B3D",
  emberDim:   "rgba(255,107,61,0.10)",
  emberGlow:  "rgba(255,107,61,0.06)",
  emberHot:   "rgba(255,107,61,0.22)",

  // ---- TEXT — 4 levels (refined contrast) ---------------------------------
  ink:        "#F4F4F6",
  inkSec:     "rgba(244,244,246,0.62)",
  inkMuted:   "rgba(244,244,246,0.36)",
  inkFaint:   "rgba(244,244,246,0.16)",

  // ---- NAVY aliases (kept for backwards compat) ---------------------------
  navy:       "#F4F4F6",
  navyLight:  "rgba(244,244,246,0.62)",
  navyDim:    "rgba(244,244,246,0.16)",

  // ---- GOLD (muted — Masters/trophies, not decoration) --------------------
  gold:       "#D9A441",
  goldDim:    "rgba(217,164,65,0.10)",
  goldBright: "#E5B459",

  // ---- STATUS — muted sage and brick (kept naming) ------------------------
  green:      "#4DA672",
  greenDim:   "rgba(77,166,114,0.14)",
  red:        "#C76561",
  redDim:     "rgba(199,101,97,0.12)",
  blue:       "#5BA0E5",
  blueDim:    "rgba(91,160,229,0.14)",
  purple:     "#8B5CF6",

  // ---- TEAL — WC2026 bracket-pool accent (matches WorldCupBracket.jsx) -----
  // Used for the "Picks Locked In" badge + locked-state CTA gradient. Previously
  // referenced (T.teal, T.tealDeep) but undefined → gradient rendered as
  // `linear-gradient(180deg, undefined 0%, undefined 100%)` which is invalid
  // CSS, leaving the button background transparent and the label inherited-white.
  teal:       "#14B8A6",
  tealDeep:   "#0D9488",
  tealDim:    "rgba(20,184,166,0.12)",

  // ---- FLAME ---------------------------------------------------------------
  flameHot:   "#FF6B3D",
  flameTip:   "#FF8A65",

  // ---- SCI-FI WHITE — luminous spaceship interior panels -------------------
  // Not pure white — slightly warm porcelain with cool undertone
  luma:       "#F0EEF5",          // primary luminous white
  lumaWarm:   "#E8E4ED",          // warm porcelain
  lumaDim:    "rgba(240,238,245,0.52)", // subtle glow surface
  lumaMid:    "rgba(240,238,245,0.65)", // medium intensity panel
  lumaBright: "rgba(240,238,245,0.78)", // bright panel surface
  lumaGlow:   "rgba(240,238,245,0.38)", // ambient glow
  lumaBorder: "rgba(240,238,245,0.56)", // panel edge
  lumaBorderHi: "rgba(240,238,245,0.78)", // bright panel edge
};

// ── SPORT ACCENT COLORS — each sport gets its own energy ─────────────────────
const SPORT_COLOR = {
  NBA:     { accent: "#1D7CF2", glow: "rgba(29,124,242,0.35)", dim: "rgba(29,124,242,0.60)", border: "rgba(29,124,242,0.50)" },
  WNBA:    { accent: "#FF6B2B", glow: "rgba(255,107,43,0.35)", dim: "rgba(255,107,43,0.60)", border: "rgba(255,107,43,0.50)" },
  NHL:     { accent: "#00D4FF", glow: "rgba(0,212,255,0.35)", dim: "rgba(0,212,255,0.60)", border: "rgba(0,212,255,0.50)" },
  MLB:     { accent: "#2ECC71", glow: "rgba(46,204,113,0.35)", dim: "rgba(46,204,113,0.60)", border: "rgba(46,204,113,0.50)" },
  NFL:     { accent: "#E74C3C", glow: "rgba(231,76,60,0.35)", dim: "rgba(231,76,60,0.60)", border: "rgba(231,76,60,0.50)" },
  NCAAF:   { accent: "#E74C3C", glow: "rgba(231,76,60,0.35)", dim: "rgba(231,76,60,0.60)", border: "rgba(231,76,60,0.50)" },
  NCAAB:   { accent: "#F39C12", glow: "rgba(243,156,18,0.35)", dim: "rgba(243,156,18,0.60)", border: "rgba(243,156,18,0.50)" },
  WNCAAB:  { accent: "#E67E22", glow: "rgba(230,126,34,0.35)", dim: "rgba(230,126,34,0.60)", border: "rgba(230,126,34,0.50)" },
  Soccer:  { accent: "#2ECC71", glow: "rgba(46,204,113,0.35)", dim: "rgba(46,204,113,0.60)", border: "rgba(46,204,113,0.50)" },
  MMA:     { accent: "#E74C3C", glow: "rgba(231,76,60,0.35)", dim: "rgba(231,76,60,0.60)", border: "rgba(231,76,60,0.50)" },
  Boxing:  { accent: "#FFD700", glow: "rgba(255,215,0,0.35)", dim: "rgba(255,215,0,0.60)", border: "rgba(255,215,0,0.50)" },
  Golf:    { accent: "#27AE60", glow: "rgba(39,174,96,0.35)", dim: "rgba(39,174,96,0.60)", border: "rgba(39,174,96,0.50)" },
  GOLF:    { accent: "#27AE60", glow: "rgba(39,174,96,0.35)", dim: "rgba(39,174,96,0.60)", border: "rgba(39,174,96,0.50)" },
  Masters: { accent: "#006747", glow: "rgba(0,103,71,0.40)", dim: "rgba(0,103,71,0.65)", border: "rgba(0,103,71,0.55)" },
  MASTERS: { accent: "#006747", glow: "rgba(0,103,71,0.40)", dim: "rgba(0,103,71,0.65)", border: "rgba(0,103,71,0.55)" },
  "US Open": { accent: "#003366", glow: "rgba(0,51,102,0.40)", dim: "rgba(0,51,102,0.65)", border: "rgba(0,51,102,0.55)" },
  Derby:   { accent: "#C8102E", glow: "rgba(200,16,46,0.40)", dim: "rgba(200,16,46,0.20)", border: "rgba(200,16,46,0.55)" },
  DERBY:   { accent: "#C8102E", glow: "rgba(200,16,46,0.40)", dim: "rgba(200,16,46,0.20)", border: "rgba(200,16,46,0.55)" },
  Tennis:  { accent: "#C8E620", glow: "rgba(200,230,32,0.35)", dim: "rgba(200,230,32,0.60)", border: "rgba(200,230,32,0.50)" },
  default: { accent: "#FF6B3D", glow: "rgba(255,107,61,0.35)", dim: "rgba(255,107,61,0.25)", border: "rgba(255,107,61,0.40)" },
};
const getSportColor = (sport) => {
  if (!sport) return SPORT_COLOR.default;
  const s = sport.toUpperCase().replace(/\s/g, "");
  // Check for soccer variants
  if (s.includes("SOCCER") || s.includes("EPL") || s.includes("LIGA") || s.includes("SERIE") || s.includes("LIGUE") || s.includes("MLS") || s.includes("CHAMP")) return SPORT_COLOR.Soccer;
  return SPORT_COLOR[s] || SPORT_COLOR[sport] || SPORT_COLOR.default;
};

// ── SPACING SCALE (4px grid) ─────────────────────────────────────────────────
const S = {
  xs: 4,    // tight internal gaps
  sm: 8,    // small gaps, icon padding
  md: 12,   // medium gaps
  lg: 16,   // standard padding, card internal
  xl: 20,   // section padding horizontal
  xxl: 24,  // section spacing vertical
  page: 20, // page horizontal padding (consistent everywhere)
  section: 24, // vertical space between sections
  card: 16, // card internal padding
  cardLg: 20, // large card internal padding
  radius: 14,  // standard border radius
  radiusLg: 20, // large border radius (cards, modals)
  radiusSm: 10, // small radius (buttons, badges)
};

// ── FONT FAMILY CONSTANTS ────────────────────────────────────────────────────
// MUST be declared before TY (typography scale) because TY.mono/numeric/score
// reference FM in their value expressions. const bindings are in the temporal
// dead zone until their declaration line executes, so any out-of-order use at
// module load time throws "Cannot access 'FM' before initialization".
const FD = "'Roboto', -apple-system, sans-serif"; // display headings
const FB = "'Roboto', -apple-system, 'Segoe UI', sans-serif";
const FN = "'Roboto', -apple-system, 'Segoe UI', sans-serif"; // tabular numbers via CSS
const FM = "'Roboto Mono', 'SF Mono', 'Fira Code', monospace"; // data/numeric accent
// FS = display font for big scores/balances
const FS = "'Roboto', -apple-system, sans-serif";

// ── TYPOGRAPHY SCALE ─────────────────────────────────────────────────────────
const TY = {
  display:  { fontSize: 38, fontWeight: 800, letterSpacing: "-0.02em", lineHeight: 1.05 },
  hero:     { fontSize: 28, fontWeight: 700, letterSpacing: "-0.015em", lineHeight: 1.1 },
  h1:       { fontSize: 22, fontWeight: 700, letterSpacing: "-0.012em", lineHeight: 1.2 },
  h2:       { fontSize: 17, fontWeight: 700, letterSpacing: "-0.008em", lineHeight: 1.3 },
  h3:       { fontSize: 15, fontWeight: 700, letterSpacing: "-0.005em", lineHeight: 1.35 },
  body:     { fontSize: 14, fontWeight: 400, lineHeight: 1.5 },
  bodyBold: { fontSize: 14, fontWeight: 600, lineHeight: 1.5 },
  small:    { fontSize: 12, fontWeight: 500, lineHeight: 1.4 },
  tiny:     { fontSize: 10, fontWeight: 600, lineHeight: 1.3 },
  label:    { fontSize: 10, fontWeight: 700, letterSpacing: "0.10em", textTransform: "uppercase" },
  mono:     { fontSize: 13, fontWeight: 600, fontFamily: FM, letterSpacing: "0" },
  numeric:  { fontSize: 38, fontWeight: 700, fontFamily: FM, letterSpacing: "-0.02em",
              fontVariantNumeric: "tabular-nums" },
  score:    { fontSize: 24, fontWeight: 700, fontFamily: FM, letterSpacing: "-0.01em",
              fontVariantNumeric: "tabular-nums" },
};

// ── SURFACE HELPERS — flat, data-forward (NHL-app direction) ──────────────────
// Single source of truth for the refreshed look. Replaces per-component frosted
// gradients + 24px backdrop blur + spring shadows with flat surfaces on the
// near-black base. One accent per surface via accentLeft(); emphasis comes from
// size + tabular numerals, not blanket fontWeight:800.
const tnum = { fontVariantNumeric: "tabular-nums" };
// Flat card. Pass accent to add a single coral (or custom) left rule.
const flatCard = (accent = null) => ({
  background: T.card,
  border: `1px solid ${T.border}`,
  ...(accent ? { borderLeft: `3px solid ${accent}`, borderRadius: "0 12px 12px 0" } : { borderRadius: 12 }),
});
// Stat cell — big tabular number over a quiet uppercase label. Use in rows.
const statNum = (color = "#fff") => ({
  fontSize: 18, fontWeight: 700, color, letterSpacing: "-0.02em", lineHeight: 1.05, ...tnum,
});
const statLabel = {
  fontSize: 9, fontWeight: 600, color: "rgba(255,255,255,0.35)",
  textTransform: "uppercase", letterSpacing: "0.06em", marginTop: 2,
};
// Divider used between inline stats in a hero strip.
const statDivider = { width: 1, alignSelf: "stretch", background: "rgba(255,255,255,0.06)" };


// ── ICON SCALE — five sizes, no improvisation ────────────────────────────────
const I = {
  xs: 14,   // inline meta (chevrons, dots, decoration)
  sm: 16,   // chip icons, in-button icons
  md: 20,   // standard UI icons
  lg: 22,   // bottom nav, primary action
  xl: 24,   // hero/avatar slot only
};
const STROKE = 1.6;        // outlined icons
const STROKE_ACTIVE = 1.8; // when filled, stroke matches fill weight

// --- Native app detection (Capacitor iOS/Android) ---------------------------
// True when running inside the native iOS/Android shell (not a browser).
// Used to hide Google sign-in on iOS where Firebase signInWithPopup hangs
// inside WKWebView. Web users on torchpicks.com still see Google normally.
const IS_NATIVE_APP = typeof window !== "undefined"
  && !!(window.Capacitor && typeof window.Capacitor.isNativePlatform === "function"
        && window.Capacitor.isNativePlatform());

// --- Safe-area / WebView extension defenses (iOS Capacitor) -----------------
// These run at module load BEFORE React renders. They address a bundle of
// related symptoms where iOS WKWebView's frame doesn't extend through the
// status bar and/or home indicator zones, leaving white strips visible.
//
// (1) Re-assert viewport-fit=cover. iOS aggressively caches index.html, so
//     edits to the viewport meta tag don't always apply on subsequent app
//     launches. The JS bundle has hash-based cache busting and IS getting
//     updated, so this is our most reliable injection point.
// (2) If the @capacitor/status-bar plugin is installed, ask it to overlay
//     the WebView and use a dark style. This is what tells iOS the status
//     bar should be transparent over the WebView (not push the WebView
//     down). Silently no-ops if the plugin isn't installed.
if (typeof document !== "undefined") {
  try {
    const meta = document.querySelector('meta[name="viewport"]');
    if (meta) {
      const content = meta.getAttribute("content") || "";
      if (!/viewport-fit\s*=\s*cover/i.test(content)) {
        meta.setAttribute("content", content + (content ? ", " : "") + "viewport-fit=cover");
      }
    } else {
      const m = document.createElement("meta");
      m.name = "viewport";
      m.content = "width=device-width, initial-scale=1, viewport-fit=cover";
      document.head.appendChild(m);
    }
  } catch {}
}
if (IS_NATIVE_APP && typeof window !== "undefined") {
  try {
    const StatusBar = window.Capacitor?.Plugins?.StatusBar;
    if (StatusBar) {
      // Make the status bar transparent so the WebView extends behind it;
      // dark style means white text/icons (visible on our dark theme).
      Promise.resolve().then(() => {
        StatusBar.setOverlaysWebView?.({ overlay: true }).catch(() => {});
        StatusBar.setStyle?.({ style: "DARK" }).catch(() => {});
        StatusBar.setBackgroundColor?.({ color: "#08080A" }).catch(() => {});
      });
    }
  } catch {}
}

// --- Universal Links / deep-link handler (Capacitor App plugin) -------------
// Listens for incoming https://torchpicks.com URLs that open the app via
// iOS Universal Links (Associated Domains entitlement). Required for league
// invite shares — including the World Cup Special — to open the installed
// app instead of bouncing through Safari.
//
// REQUIRES (one-time setup, mostly Mac-side):
//   1. public/.well-known/apple-app-site-association deployed via Firebase
//      Hosting with Content-Type application/json (handled in firebase.json)
//   2. iOS app entitlement `com.apple.developer.associated-domains` =
//      `applinks:torchpicks.com` (Xcode → Signing & Capabilities)
//   3. npm install @capacitor/app && npx cap sync ios
//   4. Rebuild + resubmit to App Store
//
// Until all four are in place, this listener is a harmless no-op — the
// Capacitor App plugin never fires `appUrlOpen`, so the inner block never
// runs. Web users on torchpicks.com keep the existing URL-param flow.
if (IS_NATIVE_APP && typeof window !== "undefined") {
  try {
    const CapApp = window.Capacitor?.Plugins?.App;
    if (CapApp?.addListener) {
      CapApp.addListener("appUrlOpen", (data) => {
        try {
          if (!data?.url) return;
          const url = new URL(data.url);
          // Guard against arbitrary schemes — only handle our own domain.
          if (url.hostname !== "torchpicks.com" && url.hostname !== "www.torchpicks.com") return;

          // ?join=CODE — seed sessionStorage with the same key the existing
          // App-level useState init reads (see activePage init around L20905).
          const joinCode = url.searchParams.get("join");
          if (joinCode) {
            try { sessionStorage.setItem("torch_pending_join", joinCode); } catch {}
          }

          // /wc or /wc/* — seed the World Cup template intent, matching the
          // pattern handleWcCta() uses on the public landing page so a single
          // post-auth handler can route both web CTA and deep-link entries.
          if (url.pathname === "/wc" || url.pathname.startsWith("/wc/")) {
            try {
              localStorage.setItem("torch_den_intent", "create");
              localStorage.setItem("torch_template_id", "world-cup-2026");
            } catch {}
          }

          // Navigate within the local bundle so the App-level init re-runs
          // with the new URL params + storage signals. Cold-launch already
          // has the URL intact at boot; this call covers the warm-launch case
          // (app backgrounded → user taps a link → app foregrounds with the
          // OLD URL still in window.location).
          const target = url.pathname + url.search;
          const current = window.location.pathname + window.location.search;
          if (target !== current) {
            window.location.assign(target);
          }
        } catch (e) {
          console.warn("[torch] appUrlOpen handler failed:", e);
        }
      });
    }
  } catch {}
}

// --- Shared formatting utils -------------------------------------------------
const fmtOdds      = (o) => o == null ? "" : o > 0 ? `+${o}` : `${o}`;
const fmtSpreadVal = (n) => n == null ? "PK" : n > 0 ? `+${n}` : `${n}`;

/**
 * Expand cryptic sportsbook shorthand on a pick's label into something
 * a non-bettor can read on the social feed. Designed to be safe: if the
 * pick doesn't match a known shorthand pattern, return the original
 * label unchanged.
 *
 * Cases handled:
 *   "U 6.5"   → "Under 6.5"
 *   "O 220.5" → "Over 220.5"
 *
 * Cases that already read well and pass through:
 *   "Buffalo Sabres" (moneyline → team name)
 *   "BUF +2.5"       (spread)
 *   "LeBron James 25.5+ points" (player prop, pre-formatted)
 */
function humanizePickLabel(pick) {
  if (!pick) return "Pick";
  const label = (pick.label || "").trim();
  const type = (pick.type || "").toLowerCase();

  // ── ML & Spread: reconstruct team name from gameLabel + side ──
  // The raw `label` field is inconsistent across pick-entry surfaces:
  //   - Inline game-card buttons save label as just the odds ("-218")
  //   - GameDetailModal saves "{short} {odds}" ("COL -218")
  // Neither tells a Pulse viewer which team was picked, since the headline
  // gets clipped down to just digits. The pick has `gameLabel` ("Vegas Golden
  // Knights @ Carolina Hurricanes") and `side` ("home"/"away") -- enough to
  // reconstruct the picked team unambiguously. Game matchup still shows in
  // the meta row below, so this is the "which side" signal that was missing.
  const isML = type === "ml" || type === "ml_1h";
  const isSpread = type === "spread" || type === "spread_1h";
  if ((isML || isSpread) && pick.side && pick.gameLabel) {
    const parts = pick.gameLabel.split(" @ ");
    if (parts.length === 2) {
      const team = pick.side === "home" ? parts[1].trim()
                 : pick.side === "away" ? parts[0].trim()
                 : "";
      if (team) {
        // ML: append "ML" so the bet type is readable inline. Spread already
        // shows the line number ("Thunder +1.5") which functions as its own
        // type indicator, so no suffix needed there — adding "SPREAD" would
        // be redundant and crowd the headline. Totals get "Over"/"Under" via
        // the U/O expansion below.
        if (isML) return `${team} ML`;
        if (isSpread) {
          // Prefer pick.line as the spread number; fall back to stripping a
          // team-short prefix from the label (handles "COL -3.5"-style stores).
          const lineNum = (typeof pick.line === "number") ? pick.line : null;
          let spreadStr = "";
          if (lineNum != null) {
            spreadStr = lineNum > 0 ? `+${lineNum}` : `${lineNum}`;
          } else {
            spreadStr = label.replace(/^[A-Z]{2,4}\s+/, "").trim();
          }
          return spreadStr ? `${team} ${spreadStr}` : team;
        }
      }
    }
  }

  if (!label) return "Pick";

  // Totals: bare U/O prefix is jargon. "U 6.5" → "Under 6.5".
  if (type === "total" || type === "totals") {
    if (/^U\s/i.test(label)) return label.replace(/^U\s+/i, "Under ");
    if (/^O\s/i.test(label)) return label.replace(/^O\s+/i, "Over ");
  }
  // Defensive: even outside "total" type, expand obvious U/O prefixes
  if (/^U\s\d/i.test(label)) return label.replace(/^U\s+/i, "Under ");
  if (/^O\s\d/i.test(label)) return label.replace(/^O\s+/i, "Over ");

  return label;
}

// --- TORCH LOGO (image) ------------------------------------------------------
// Uses flame-only icon at small sizes, full monogram at large sizes
function TorchLogo({ size = 48, style: extraStyle = {}, forceIcon = false }) {
  // forceIcon kept for back-compat — no longer affects rendering since the
  // new flame logo is icon-only (no wordmark monogram variant exists).
  // /torch-icon.png is tightly padded (~4% margin) so the flame fills most
  // of the slot even at small render sizes (14-32px). Square container
  // with natural-aspect flame centered inside means inline alignment in
  // flex layouts stays consistent across all call sites.
  return <img
    src="/torch-icon.png"
    alt="Torch"
    style={{
      width: size,
      height: size,
      objectFit: "contain",
      ...extraStyle,
    }}
  />;
}


/* ===========================================
   SEED DATA
=========================================== */
// --- GAMES DATA HOOK ----------------------------------------------------------
// --- FORMAT GAME TIME ---------------------------------------------------------
// Shows date context so old games are never confused with today.
// Today: "7:30 PM"   Tomorrow: "Tomorrow 7:30 PM"   Later: "Fri Mar 8 · 7:30 PM"
const formatGameTime = (isoString) => {
  if (!isoString) return '—';
  const d   = new Date(isoString);
  const now = new Date();
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const tomorrowStart = new Date(todayStart.getTime() + 86_400_000);
  const twoDaysStart  = new Date(todayStart.getTime() + 2 * 86_400_000);
  const timeStr = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  if (d >= todayStart    && d < tomorrowStart) return timeStr;
  if (d >= tomorrowStart && d < twoDaysStart)  return `Tomorrow · ${timeStr}`;
  return `${d.toLocaleDateString('en-US', { weekday:'short', month:'short', day:'numeric' })} · ${timeStr}`;
};

// --- ADAPT GAME ---------------------------------------------------------------
// Fetches from The Odds API (or falls back to mock data)
// and normalizes into a consistent display shape.


// --- LIVE TIME REMAINING CALCULATOR ------------------------------------------
// Returns { minsLeft: number|null, display: string|null, urgent: bool }
// urgent = true when < 5 mins remain (drives red coloring in UI)
const calcTimeRemaining = (sport, period, clock) => {
  if (!sport || !period) return { minsLeft: null, display: null, urgent: false };
  const s = sport.toUpperCase();

  // Parse "MM:SS" clock string → decimal minutes remaining on the clock
  const parseClock = (c) => {
    if (!c) return null;
    const parts = String(c).split(':');
    if (parts.length < 2) return null;
    return parseInt(parts[0], 10) + parseInt(parts[1], 10) / 60;
  };

  // Extract leading period/quarter/half number from strings like "Q3", "2nd Period", "H2"
  const parsePeriod = (p) => {
    const m = String(p).match(/([0-9]+)/);
    return m ? parseInt(m[1], 10) : null;
  };

  const periodNum = parsePeriod(period);
  const clockMins = parseClock(clock);

  const fmt = (minsLeft) => {
    const mm = Math.floor(minsLeft);
    const ss = Math.round((minsLeft - mm) * 60);
    return { minsLeft, display: `~${mm}:${String(ss).padStart(2, '0')} left`, urgent: minsLeft < 5 };
  };

  // NBA — 4 quarters x 12 min
  if (s === 'NBA') {
    const isHalves = /half/i.test(period);
    const periodLen = isHalves ? 24 : 12; // OT halves can vary; default quarter is 12
    const total     = isHalves ? 2  : 4;
    if (periodNum === null) return { minsLeft: null, display: null, urgent: false };
    // Overtime -- each OT period is 5 min sudden-life, regardless of how
    // many OTs have been played (1OT, 2OT, 3OT all 5 min each in NBA).
    // Without this branch, the regulation formula returns negative values
    // for periodNum > 4 and the UI shows "0:00" mid-OT.
    if (!isHalves && periodNum > 4) {
      const remaining = clockMins ?? 5;
      return fmt(Math.max(0, remaining));
    }
    const remaining = (clockMins ?? periodLen) + (total - periodNum) * periodLen;
    return fmt(Math.max(0, remaining));
  }
  // NCAAB — 2 halves x 20 min (ESPN returns period as "1" or "2", NOT "1st Half")
  if (s === 'NCAAB') {
    const isHalves = /half/i.test(period) || (!isNaN(Number(period)) && Number(period) <= 2);
    if (isHalves) {
      const halfNum = periodNum ?? 1;
      const remaining = (clockMins ?? 20) + (2 - halfNum) * 20;
      return fmt(Math.max(0, remaining));
    }
    // OT in college basketball — 5 min periods
    const remaining = clockMins ?? 5;
    return fmt(Math.max(0, remaining));
  }

  // NFL / NCAAF — 4 quarters x 15 min
  if (s === 'NFL' || s === 'NCAAF') {
    if (periodNum === null) return { minsLeft: null, display: null, urgent: false };
    const remaining = (clockMins ?? 15) + (4 - periodNum) * 15;
    return fmt(Math.max(0, remaining));
  }

  // NHL — 3 periods x 20 min; OT = 5 min sudden death (period 4 from ESPN)
  if (s === 'NHL') {
    if (periodNum === null) return { minsLeft: null, display: null, urgent: false };
    if (periodNum >= 4) {
      // Overtime — 5 min sudden death
      const remaining = clockMins ?? 5;
      return fmt(Math.max(0, remaining));
    }
    const remaining = (clockMins ?? 20) + (3 - periodNum) * 20;
    return fmt(Math.max(0, remaining));
  }

  // Soccer — clock counts UP; 2 halves x 45 min + ~4 min stoppage
  const SOCCER = new Set(['MLS','EPL','UCL','UEL','LA LIGA','SERIE A','BUNDESLIGA',
    'LIGUE 1','EREDIVISIE','PRIMEIRA','BRASILEIRAO','LIGA MX','COPA LIB','SOCCER']);
  if (SOCCER.has(s)) {
    const halfLen    = 49; // 45 + ~4 stoppage
    const elapsed    = clockMins ?? 0;
    const halfLeft   = Math.max(0, halfLen - elapsed);
    const remaining  = halfLeft + (2 - (periodNum ?? 1)) * halfLen;
    const minsLeft   = Math.max(0, remaining);
    return { minsLeft, display: `~${Math.round(minsLeft)} min left`, urgent: minsLeft < 5 };
  }

  // MLB, Golf, Tennis, MMA — no game clock
  return { minsLeft: null, display: null, urgent: false };
};

// --- LIVE PERIOD FORMATTER ----------------------------------------------------
// Takes raw period/clock strings from the API and returns clean display labels.
// periodLabel: short period name ("Q3", "P2", "2nd Half", "Bot 6", "67'")
// clockLabel:  clock if meaningful ("4:22"), null for sports without game clocks
// ── denPickLabel ─ pick label for the league feed (2026-07-27) ─────────────
// Pick labels are generated for WAGER mode, where the bet type carries real
// meaning: "Kansas City Chiefs ML" is a moneyline at a price, distinct from a
// spread or a total on the same game. In pick'em there is only one thing you
// can do — pick the winner — so the "ML" suffix is jargon describing a
// distinction that does not exist in that league, sitting next to a price that
// will never pay out.
//
// Display-only. The stored label is untouched: settlement reads pick.type and
// pick.side, never this string, but the label is also what the ledger and any
// exported history show, so rewriting it at rest would edit history to fix a
// rendering problem.
//
// Only the trailing bet-type token is removed, and only when it is the last
// word, so a team whose name legitimately ends in one of these (none today,
// but "OT" and "TD" are live in other label shapes) can't be truncated. Player
// props and any label we don't recognise pass through untouched.
const DEN_WAGER_SUFFIXES = /\s+(ML|ML 1H|1H ML|MONEYLINE|SPREAD|TOTAL|ALT SPREAD|ALT TOTAL)$/i;
function denPickLabel(label, isNonWager) {
  if (!isNonWager || !label) return label;
  const stripped = String(label).replace(DEN_WAGER_SUFFIXES, "").trim();
  return stripped || label;   // never render an empty label
}

const formatLivePeriod = (sport, period, clock) => {
  if (!sport || !period || period === 'Live') return { periodLabel: null, clockLabel: null };
  const s   = sport.toUpperCase();
  const p   = String(period).trim();
  const num = p.match(/(\d+)/)?.[1];

  // Halftime / intermission — sport-aware labels, no clock shown
  if (/^halftime$/i.test(p)) {
    const label = (s === 'NHL') ? 'Intermission' : 'Halftime';
    return { periodLabel: label, clockLabel: null };
  }
  if (/^end\s*(\d+)$/i.test(p)) {
    const pNum = p.match(/\d+/)?.[0];
    if (s === 'NHL') {
      const pMap = { '1':'1st','2':'2nd','3':'3rd' };
      return { periodLabel: `${pMap[pNum] || pNum} Intermission`, clockLabel: null };
    }
    return { periodLabel: `End Q${pNum}`, clockLabel: null };
  }
  if (/^intermission$/i.test(p)) {
    return { periodLabel: 'Intermission', clockLabel: null };
  }

  // Clean up clock — strip leading zeros on minutes only
  const fmtClock = (c) => {
    if (!c) return null;
    const parts = String(c).split(':');
    if (parts.length < 2) return null;
    const mm = parseInt(parts[0], 10);
    const ss = parts[1].padStart(2, '0');
    return `${mm}:${ss}`;
  };
  const clk = fmtClock(clock);

  // NBA — quarters, or halves if explicitly labeled
  if (s === 'NBA') {
    const qMap = { '1': 'Q1', '2': 'Q2', '3': 'Q3', '4': 'Q4' };
    if (/half/i.test(p)) return { periodLabel: /1/.test(p) ? '1st Half' : '2nd Half', clockLabel: clk };
    if (/overtime|ot/i.test(p)) return { periodLabel: 'OT', clockLabel: clk };
    if (num && qMap[num]) return { periodLabel: qMap[num], clockLabel: clk };
    // Numeric OT -- ESPN sends "5" for 1st OT, "6" for 2nd OT, etc. Without
    // this branch the label disappears entirely once a game goes past
    // regulation. "OT" for first, "2OT", "3OT" for subsequent.
    const numInt = parseInt(num, 10);
    if (!isNaN(numInt) && numInt >= 5) {
      const otNum = numInt - 4;
      return { periodLabel: otNum === 1 ? 'OT' : `${otNum}OT`, clockLabel: clk };
    }
  }
  // NCAAB — 2 halves; ESPN returns period as bare "1" or "2"
  if (s === 'NCAAB') {
    if (/overtime|ot/i.test(p)) return { periodLabel: 'OT', clockLabel: clk };
    if (/half/i.test(p)) return { periodLabel: /1/.test(p) ? '1st Half' : '2nd Half', clockLabel: clk };
    const halfMap = { '1': '1st Half', '2': '2nd Half' };
    if (num && halfMap[num]) return { periodLabel: halfMap[num], clockLabel: clk };
  }

  // NFL / NCAAF — quarters
  if (s === 'NFL' || s === 'NCAAF') {
    const qMap = { '1': 'Q1', '2': 'Q2', '3': 'Q3', '4': 'Q4' };
    if (/overtime|ot/i.test(p)) return { periodLabel: 'OT', clockLabel: clk };
    if (num && qMap[num]) return { periodLabel: qMap[num], clockLabel: clk };
    // Numeric OT — ESPN sends status.period as a bare NUMBER, and sportsApi's
    // non-MLB branch passes it through as String(periodNum). So overtime
    // arrives here as "5" (and "6" for a second OT in college), never as the
    // word "overtime" — the regex above can't fire. Without this branch the
    // fallthrough at the bottom returned the raw digit and an NFL overtime
    // rendered as "5". NBA and NHL already had their equivalents; football
    // was the gap. NCAAF can run multiple OT periods, so keep counting.
    const numInt = parseInt(num, 10);
    if (!isNaN(numInt) && numInt >= 5) {
      const otNum = numInt - 4;
      return { periodLabel: otNum === 1 ? 'OT' : `${otNum}OT`, clockLabel: clk };
    }
  }

  // NHL — periods 1-3, OT (may come as "4" from ESPN)
  if (s === 'NHL') {
    if (/overtime|ot/i.test(p) || num === '4' || num === '5') return { periodLabel: 'OT', clockLabel: clk };
    const pMap = { '1': '1st', '2': '2nd', '3': '3rd' };
    if (num && pMap[num]) return { periodLabel: `${pMap[num]} Period`, clockLabel: clk };
  }

  // MLB — top/bottom of inning; no clock
  if (s === 'MLB') {
    const inningMap = { '1':'1st','2':'2nd','3':'3rd','4':'4th','5':'5th',
      '6':'6th','7':'7th','8':'8th','9':'9th','10':'10th','11':'11th','12':'12th' };
    const isTop = /top|t\d/i.test(p);
    const isBot = /bot|b\d/i.test(p);
    if (num && inningMap[num]) {
      const half = isTop ? '▲' : isBot ? '▼' : '';
      return { periodLabel: `${half} ${inningMap[num]}`.trim(), clockLabel: null };
    }
  }

  // Soccer — clock counts up; show as "NN'"
  const SOCCER = new Set(['MLS','EPL','UCL','UEL','LA LIGA','SERIE A','BUNDESLIGA',
    'LIGUE 1','EREDIVISIE','PRIMEIRA','BRASILEIRAO','LIGA MX','COPA LIB','SOCCER']);
  if (SOCCER.has(s)) {
    if (/half/i.test(p)) return { periodLabel: /1/.test(p) ? '1st Half' : '2nd Half', clockLabel: null };
    // If clock is a count-up number like "67" or "67:00", show as "67'"
    if (clock) {
      const mins = parseInt(String(clock).split(':')[0], 10);
      if (!isNaN(mins)) return { periodLabel: `${mins}'`, clockLabel: null };
    }
  }

  // MMA — rounds
  if (s === 'MMA') {
    if (num) return { periodLabel: `Rd ${num}`, clockLabel: clk };
  }

  // Fallback — return raw period string, stripped of redundant words
  const cleaned = p.replace(/quarter|period/gi, '').replace(/\s+/g, ' ').trim();
  return { periodLabel: cleaned || p, clockLabel: clk };
};

// ── LIVE IN-GAME SITUATION ───────────────────────────────────────────────────
// Renders the rich live state DraftKings-style, driven by game.liveState
// (populated from ESPN's situation object in sportsApi). Flat, no glows:
// a small bases diamond + balls-strikes count + outs pips for baseball, and
// a "3rd & 7" down-and-distance chip for football. Returns null when there's
// nothing live to show, so callers can drop it in unconditionally.

// Small SVG bases diamond. Each base fills coral when a runner is on it.
// Drawn as three rotated squares around a home-plate apex (1B right, 2B top,
// 3B left), matching the standard scoreboard layout.
function BasesDiamond({ onFirst, onSecond, onThird, size = 26 }) {
  const on  = T.coral;
  const off = "rgba(255,255,255,0.14)";
  const b   = size * 0.30;                 // base square side
  const cx  = size / 2;
  const r   = size * 0.30;                 // distance from center to each base
  const Base = ({ x, y, filled }) => (
    <rect
      x={x - b / 2} y={y - b / 2} width={b} height={b}
      transform={`rotate(45 ${x} ${y})`}
      fill={filled ? on : off}
      rx="1.5"
    />
  );
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ display:"block", flexShrink:0 }} aria-hidden="true">
      <Base x={cx + r} y={cx}     filled={onFirst}  /> {/* 1B right */}
      <Base x={cx}     y={cx - r} filled={onSecond} /> {/* 2B top   */}
      <Base x={cx - r} y={cx}     filled={onThird}  /> {/* 3B left  */}
    </svg>
  );
}

// Outs as up-to-2 pips (3rd out ends the half-inning, so max shown is 2).
function OutsPips({ outs = 0 }) {
  return (
    <span style={{ display:"inline-flex", gap:3, alignItems:"center" }}>
      {[0,1].map(i => (
        <span key={i} style={{
          width:5, height:5, borderRadius:"50%",
          background: i < (outs ?? 0) ? T.coral : "rgba(255,255,255,0.18)",
        }}/>
      ))}
    </span>
  );
}

// Sport-aware live situation. `compact` trims it for the game-row (no labels);
// the full version (modal header) adds count/outs text.
function LiveSituation({ liveState, compact = false }) {
  if (!liveState) return null;

  if (liveState.kind === "baseball") {
    const { balls, strikes, outs, onFirst, onSecond, onThird } = liveState;
    const hasCount = balls != null && strikes != null;
    return (
      <div style={{ display:"inline-flex", alignItems:"center", gap: compact ? 6 : 10 }}>
        <BasesDiamond onFirst={onFirst} onSecond={onSecond} onThird={onThird} size={compact ? 18 : 24} />
        {hasCount && (
          <span style={{
            fontSize: compact ? 10 : 11.5, fontWeight:700, color:"rgba(255,255,255,0.75)",
            fontVariantNumeric:"tabular-nums", fontFamily:FB, letterSpacing:"-0.01em",
          }}>
            {balls}-{strikes}
          </span>
        )}
        {outs != null && (
          <span style={{ display:"inline-flex", alignItems:"center", gap: compact ? 3 : 5 }}>
            <OutsPips outs={outs} />
            {!compact && (
              <span style={{ fontSize:10.5, fontWeight:600, color:"rgba(255,255,255,0.5)", fontFamily:FB }}>
                {outs} out{outs === 1 ? "" : "s"}
              </span>
            )}
          </span>
        )}
      </div>
    );
  }

  if (liveState.kind === "football") {
    // Prefer ESPN's preformatted "3rd & 7" text; fall back to down+distance.
    const label = liveState.downDistance
      || (liveState.down != null && liveState.distance != null
          ? `${ordinal(liveState.down)} & ${liveState.distance}`
          : null);
    if (!label) return null;
    return (
      <span style={{
        display:"inline-flex", alignItems:"center", gap:5,
        fontSize: compact ? 10 : 11.5, fontWeight:700,
        color: liveState.isRedZone ? T.coral : "rgba(255,255,255,0.75)",
        fontVariantNumeric:"tabular-nums", fontFamily:FB, letterSpacing:"-0.01em",
      }}>
        {label}
      </span>
    );
  }

  return null;
}

// Small ordinal helper for football downs (1→1st, 2→2nd, …).
function ordinal(n) {
  const s = ["th","st","nd","rd"], v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
}

const adaptGame = (g) => {
  // Full name comes straight from the API — use as-is for display
  // Short label: use the pre-computed abbr from sportsApi, or derive from last word
  const makeShort = (full, abbr) => {
    if (abbr && abbr !== full) return abbr;
    const words = full?.trim().split(' ') || [];
    const last = words[words.length - 1] || full;
    return last.length <= 5 ? last.toUpperCase() : last.slice(0, 4).toUpperCase();
  };

  const homeFull  = g.homeTeam || '';
  const awayFull  = g.awayTeam || '';
  const homeShort = makeShort(homeFull, g.homeTeamAbbr);
  const awayShort = makeShort(awayFull, g.awayTeamAbbr);

  const fmtML     = (n) => n == null ? '—' : n > 0 ? `+${n}` : `${n}`;
  const fmtSpread = (n) => n == null ? 'PK' : n > 0 ? `+${n}` : `${n}`;
  const awaySpread = g.spread != null ? -g.spread : null;

  const normalizeSport = (s) => {
    if (!s) return 'Other';
    if (s.toLowerCase().startsWith('tennis')) return 'Tennis';
    return s;
  };

  // Only treat period as real data if it's a number or a meaningful non-"Live" string
  const rawPeriod = (g.period && g.period !== 'Live' && g.period !== 'live') ? g.period : null;
  const rawClock  = rawPeriod ? (g.clock || null) : null;

  return {
    id:       g.id,
    sport:    normalizeSport(g.sport),
    sportKey: g.sportKey || '',
    homeTeamFull: homeFull,
    awayTeamFull: awayFull,
    isLive:   g.isLive || false,
    period:   rawPeriod,
    clock:    rawClock,
    liveTime: rawPeriod ? `${rawPeriod}${rawClock ? ' ' + rawClock : ''}` : null,
    livePeriod: formatLivePeriod(g.sport, rawPeriod, rawClock),
    liveState: g.liveState ?? null,
    liveTimeRemaining: (g.isLive && rawPeriod)
      ? calcTimeRemaining(g.sport, rawPeriod, rawClock)
      : { minsLeft: null, display: null, urgent: false },
    away: { full: awayFull, short: awayShort, score: g.awayScore ?? null },
    home: { full: homeFull, short: homeShort, score: g.homeScore ?? null },
    homeLogo: g.homeLogo || null,
    awayLogo: g.awayLogo || null,
    time:         formatGameTime(g.time),
    commenceTime: g.time,
    picks: [
      { key:'awaySpread', label: awaySpread != null ? fmtSpread(awaySpread) : 'PK', vig: g.awaySpreadOdds, subLabel:'Spread', type:'spread', side:'away', line: awaySpread  },
      { key:'homeSpread', label: g.spread   != null ? fmtSpread(g.spread)   : 'PK', vig: g.homeSpreadOdds, subLabel:'Spread', type:'spread', side:'home', line: g.spread    },
      { key:'over',       label: g.total    != null ? `O ${g.total}`        : '—',  vig: g.overOdds,       subLabel:'Total',  type:'total',  side:'over', line: g.total     },
      { key:'under',      label: g.total    != null ? `U ${g.total}`        : '—',  vig: g.underOdds,      subLabel:'Total',  type:'total',  side:'under',line: g.total     },
      { key:'awayML',     label: fmtML(g.awayML),  vig: null, subLabel:'ML', type:'ml', side:'away', line: null, odds: g.awayML },
      { key:'homeML',     label: fmtML(g.homeML),  vig: null, subLabel:'ML', type:'ml', side:'home', line: null, odds: g.homeML },
      // 3-way moneyline: a Draw pick only exists when the book prices one
      // (soccer/WC). Appended LAST and only when present, so the fixed
      // 6-element positional shape other sports rely on is preserved — soccer
      // simply carries a 7th element. settlePick already grades side:'draw'
      // (true level score, no ET/PK advancement) so the settlement path is ready.
      ...(g.drawML != null
        ? [{ key:'drawML', label: fmtML(g.drawML), vig: null, subLabel:'ML', type:'ml', side:'draw', line: null, odds: g.drawML, isDraw: true }]
        : []),
    ],
    why: null,
    oddsUpdatedAt: g.oddsUpdatedAt || null,
    completed: g.completed || false,
    // MLB extras (populated via ESPN scoreboard in mergeLiveScoresIntoGames)
    homePitcher: g.homePitcher || null,
    awayPitcher: g.awayPitcher || null,
    homeRecord:  g.homeRecord  || null,
    awayRecord:  g.awayRecord  || null,
    // Playoff series (NBA/NHL/WNBA/MLB/NFL). When present, the game card
    // displays "EC Semis · Leads 2-0" instead of the regular-season record.
    // Populated by sportsApi.js's ESPN scoreboard parser.
    playoff:     g.playoff     || null,
    // Outright/futures (e.g. Masters Tournament Winner, Kentucky Derby)
    isOutright:       g.isOutright || false,
    outrightOutcomes: g.outrightOutcomes || null,
    outrightMarkets:  g.outrightMarkets  || null,  // [{key,label,subtext}] — multi-market events (e.g. Derby W/P/S)
    outrightHeader:   g.outrightHeader   || null,  // e.g. "🌹 Run for the Roses — Win / Place / Show"
    outrightEntryLabel: g.outrightEntryLabel || null, // e.g. "horses" — used in "more horses ▼"
  };
};

// ── TBD opponent collapse ──────────────────────────────────────────────────
// The Odds API publishes conditional matchups before bracketed series resolve
// (e.g. Knicks vs OKC AND Knicks vs Spurs both posted at the same tip-off
// while OKC and Spurs are still playing the WCF). Two cards with the same
// team on the same slot is misleading — odds for a fictional matchup. When
// we detect a team appearing in 2+ upcoming games within a ~6h window, keep
// one card with the determined team and replace the opponent with TBD.
function collapseTBDOpponents(arr) {
  if (!Array.isArray(arr) || arr.length < 2) return arr;
  const WINDOW_MS = 6 * 60 * 60 * 1000;

  // Build team -> [{ index, ts }] for upcoming, non-live games only.
  const teamSlots = new Map();
  arr.forEach((g, i) => {
    if (!g || g.completed || g.isLive || g.isOutright) return;
    const ts = g.commenceTime ? new Date(g.commenceTime).getTime() : 0;
    if (!ts) return;
    [g.home?.full, g.away?.full].forEach(name => {
      if (!name) return;
      if (!teamSlots.has(name)) teamSlots.set(name, []);
      teamSlots.get(name).push({ i, ts });
    });
  });

  // For each team with multiple games in a window, mark the cluster.
  // representativeByCluster: clusterKey -> { keepIdx, knownTeam }
  // dropIdx: Set of indices to drop entirely
  const representativeByCluster = new Map();
  const dropIdx = new Set();
  for (const [team, slots] of teamSlots) {
    if (slots.length < 2) continue;
    slots.sort((a, b) => a.ts - b.ts);
    let i = 0;
    while (i < slots.length) {
      const cluster = [slots[i]];
      let j = i + 1;
      while (j < slots.length && slots[j].ts - cluster[0].ts <= WINDOW_MS) {
        cluster.push(slots[j]); j++;
      }
      if (cluster.length >= 2) {
        const clusterKey = `${team}|${cluster[0].ts}`;
        // If this game is already the rep for another team's cluster (rare:
        // both teams of the same game appear in their own conflicts), skip.
        const keepIdx = cluster[0].i;
        representativeByCluster.set(clusterKey, { keepIdx, knownTeam: team });
        for (let k = 1; k < cluster.length; k++) dropIdx.add(cluster[k].i);
      }
      i = j;
    }
  }

  if (representativeByCluster.size === 0) return arr;

  // Build a map: keepIdx -> knownTeam (the team that stays as itself).
  const tbdMods = new Map();
  for (const { keepIdx, knownTeam } of representativeByCluster.values()) {
    tbdMods.set(keepIdx, knownTeam);
  }

  return arr
    .map((g, i) => {
      if (dropIdx.has(i)) return null;
      const knownTeam = tbdMods.get(i);
      if (!knownTeam) return g;
      // Determine which side is TBD (the side that isn't the known team).
      const homeIsKnown = g.home?.full === knownTeam;
      const tbdSide = homeIsKnown ? "away" : "home";
      const placeholder = { full: "TBD", abbr: "TBD", short: "TBD", name: "TBD" };
      // Null the TBD side's playoff status if present — the original status
      // referenced the conditional opponent's series and would be misleading.
      const modifiedPlayoff = g.playoff
        ? { ...g.playoff, [`${tbdSide}Status`]: null }
        : null;
      return tbdSide === "away"
        ? { ...g, opponentTBD: true, tbdSide: "away", away: placeholder, awayLogo: null, awayRecord: null, awayPitcher: null, playoff: modifiedPlayoff }
        : { ...g, opponentTBD: true, tbdSide: "home", home: placeholder, homeLogo: null, homeRecord: null, homePitcher: null, playoff: modifiedPlayoff };
    })
    .filter(Boolean);
}

function useGames() {
  const [games, setGames]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null); // { message } | null — set when live games fetch fails
  const [retryKey, setRetryKey] = useState(0);

  const retry = useCallback(() => {
    setError(null);
    setRetryKey(k => k + 1);
  }, []);

  useEffect(() => {
    let cancelled = false;
    // Seed ref with current state so polls never skip on first run
    const gamesRef = { current: [] };

    const updateGames = (next) => {
      gamesRef.current = next;
      if (!cancelled) setGames(next);
    };

    let pollsStarted = false;
    const startPolls = () => {
      if (pollsStarted) return;
      pollsStarted = true;
      scheduleLiveRefresh();
    };

    const load = async () => {
      if (gamesRef.current.length === 0 && !cancelled) setLoading(true);
      try {
        const raw     = injectSpecialEvents(await fetchLiveGames());
        const adapted = raw.map(adaptGame);
        // Safety-net dedup — catch duplicates that slip through sportsApi dedup
        const seenGames = new Set();
        const deduped = adapted.filter(g => {
          const day = new Date(g.commenceTime || 0).toLocaleDateString('en-CA');
          const key = [g.away?.full, g.home?.full].sort().join('|') + '|' + day;
          if (seenGames.has(key)) return false;
          seenGames.add(key);
          return true;
        });
        if (!cancelled) {
          updateGames(collapseTBDOpponents(deduped));
          setLoading(false);
          setError(null); // clear any prior error
          // Start polling immediately — don't wait for scores merge
          startPolls();
        }
        // Merge live scores in background — non-blocking, fills in after first paint.
        // Pass the un-collapsed array to mergeLiveScoresIntoGames so ESPN lookups
        // still have real team names; re-collapse after enrichment.
        mergeLiveScoresIntoGames(deduped).then(withScores => {
          if (cancelled) return;
          const withPeriods = withScores.map(g => {
            if (!g.isLive) return g;
            const p = (g.period && g.period !== 'Live' && g.period !== 'live') ? g.period : null;
            const c = p ? (g.clock || null) : null;
            return {
              ...g,
              period: p,
              clock:  c,
              liveTime: p ? `${p}${c ? ' ' + c : ''}` : null,
              livePeriod: formatLivePeriod(g.sport, p, c),
              liveTimeRemaining: p ? calcTimeRemaining(g.sport, p, c) : { minsLeft: null, display: null, urgent: false },
            };
          });
          updateGames(collapseTBDOpponents(withPeriods));
        }).catch(() => {});
      } catch (err) {
        if (!cancelled) {
          // No mock/fallback games — showing fake matchups risks users making picks
          // against games that don't exist. Surface the error to the UI instead.
          // Preserve any previously-loaded games (polling failure mid-session should
          // not wipe the list); only the fresh-load case ends up with games.length === 0.
          const msg = err?.message || "Can't load games right now";
          console.warn("[Torch] fetchLiveGames failed:", msg);
          setError({ message: msg });
          setLoading(false);
          // Still start polls in case existing games can be refreshed by score merges.
          startPolls();
        }
      }
    };
    load();

    // -- Live score refresh with exponential backoff ------------------------
    // After 5 consecutive failures, pause for 5 min before retrying.
    let liveTimer = null;
    let liveFailCount = 0;
    // LIVE-REFRESH-TIMERS (fix 3.6, 2026-09-28): the 5-minute backoff timer is
    // now tracked and cleared on cleanup, a reschedule after cleanup is a
    // no-op, and only one refresh runs at a time. Before, a refresh still in
    // flight at cleanup could start a fresh interval nothing would ever clear,
    // the backoff timeout fired after unmount, and with a slow ESPN response
    // (>15s) a second request started on top of the first -- whichever landed
    // last won, so a score could briefly roll back.
    let backoffTimer = null;
    let liveInFlight = false;
    const MAX_LIVE_FAILS = 5;
    // One refresh pass: fetch fresh scores, recompute period/clock, diff, and
    // re-render only on real change. Extracted so we can fire it IMMEDIATELY on
    // (re)schedule instead of waiting a full interval — previously the first
    // score update lagged a whole cycle behind odds (which already fire on
    // mount), which is the main reason scores felt slower than odds.
    const runLiveRefresh = async () => {
      if (!gamesRef.current.length || cancelled || document.hidden || liveInFlight) return;
      liveInFlight = true;
      try {
        const merged = await mergeLiveScoresIntoGames(gamesRef.current);
        // Recompute period data from fresh scores — filter out literal "Live" string
        const recomputed = merged.map(g => {
          if (!g.isLive) return g;
          const p = (g.period && g.period !== 'Live' && g.period !== 'live') ? g.period : null;
          const c = p ? (g.clock || null) : null;
          return {
            ...g,
            period: p,
            clock:  c,
            liveTime: p ? `${p}${c ? ' ' + c : ''}` : null,
            livePeriod: formatLivePeriod(g.sport, p, c),
            liveTimeRemaining: p ? calcTimeRemaining(g.sport, p, c) : { minsLeft: null, display: null, urgent: false },
          };
        });
        if (!cancelled) {
          // Diff check: only trigger re-render if scores/status actually changed
          const prev = gamesRef.current;
          const changed = recomputed.length !== prev.length || recomputed.some((g, i) => {
            const p = prev[i];
            return !p || g.isLive !== p.isLive || g.completed !== p.completed
              || g.home?.score !== p.home?.score || g.away?.score !== p.away?.score
              || g.period !== p.period || g.clock !== p.clock;
          });
          gamesRef.current = recomputed;
          if (changed) setGames(recomputed);
        }
        liveFailCount = 0; // reset on success
        // Re-schedule at correct interval if live status changed
        const nowLive = recomputed.some(g => g.isLive);
        return nowLive;
      } catch {
        liveFailCount++;
        if (liveFailCount >= MAX_LIVE_FAILS) {
          // Back off — stop polling for 5 minutes then restart
          clearInterval(liveTimer);
          liveFailCount = 0;
          clearTimeout(backoffTimer);
          if (!cancelled) backoffTimer = setTimeout(scheduleLiveRefresh, 5 * 60 * 1000);
        }
        return null;
      } finally {
        liveInFlight = false;
      }
    };
    const scheduleLiveRefresh = () => {
      if (cancelled) return;
      clearInterval(liveTimer);
      clearTimeout(backoffTimer);
      const hasLive = gamesRef.current.some(g => g.isLive);
      // Live: poll every 15s. ESPN scores are free with no rate limit, and the
      // ESPN cache TTL (~15-20s) means tighter polling mostly hits warm cache
      // rather than ESPN itself — so this cuts perceived score lag roughly in
      // half without meaningfully more upstream traffic. Idle: 90s is plenty.
      const baseInterval = hasLive ? 15 * 1000 : 90 * 1000;
      // Fire one pass immediately so scores update right away, not a cycle later.
      runLiveRefresh().then((nowLive) => {
        if (nowLive != null && nowLive !== hasLive && !cancelled) scheduleLiveRefresh();
      });
      liveTimer = setInterval(async () => {
        const nowLive = await runLiveRefresh();
        if (nowLive != null && nowLive !== hasLive) scheduleLiveRefresh();
      }, baseInterval);
    };

    // Full game list refresh every 10 minutes — skip if tab is hidden
    const fullRefresh = setInterval(() => { if (!document.hidden) load(); }, 10 * 60 * 1000);

    return () => {
      cancelled = true;
      clearInterval(liveTimer);
      clearTimeout(backoffTimer);
      clearInterval(fullRefresh);
    };
  }, [retryKey]);

  return { games, loading, error, retry };
}

// FIX #7: cache the global leaderboard rank so we don't fire a 200-doc read
// on every settlement. Rank is re-fetched at most once every 5 minutes.
const _rankCache = { uid: null, rank: "—", ts: 0 };
const RANK_CACHE_TTL = 5 * 60 * 1000; // 5 minutes

function useUserStats(userId) {
  const [stats, setStats]     = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    if (!userId) { setLoading(false); return; }
    setLoading(true);
    try {
      // Use cached rank if fresh enough to avoid repeated 200-doc reads
      const now = Date.now();
      let rank;
      if (_rankCache.uid === userId && now - _rankCache.ts < RANK_CACHE_TTL) {
        rank = _rankCache.rank;
      } else {
        try {
          const board = await loadLeaderboard(200);
          rank = board.findIndex(p => p.id === userId) + 1 || "—";
        } catch {
          // Firestore permissions may not allow global leaderboard reads — graceful fallback
          rank = "—";
        }
        _rankCache.uid = userId;
        _rankCache.rank = rank;
        _rankCache.ts = now;
      }
      const userDoc = await loadUserStats(userId);
      setStats(userDoc ? { ...userDoc, rank } : null);
    } catch {
      setStats(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, [userId]);

  return { stats, loading, refresh };
}







/* ===========================================
   SHARED COMPONENTS
=========================================== */

// Universal Card shell
function Card({ children, style = {}, onClick, hoverable = false }) {
  const [hov, setHov] = useState(false);
  return (
    <div
      onClick={onClick}
      onMouseEnter={() => hoverable && setHov(true)}
      onMouseLeave={() => hoverable && setHov(false)}
      style={{
        background: hov
          ? `linear-gradient(165deg, rgba(240,238,245,0.56) 0%, rgba(16,18,26,0.85) 100%)`
          : `linear-gradient(165deg, rgba(240,238,245,0.36) 0%, rgba(16,18,26,0.80) 100%)`,
        border: `1px solid ${hov ? T.lumaBorderHi : T.lumaBorder}`,
        borderTop: `1px solid ${hov ? "rgba(240,238,245,0.24)" : "rgba(240,238,245,0.74)"}`,
        borderRadius: 14,
        backdropFilter: "blur(24px) saturate(160%)",
        boxShadow: hov
          ? `0 8px 28px rgba(0,0,0,0.35), 0 1px 0 ${T.lumaGlow} inset, 0 0 24px rgba(240,238,245,0.03)`
          : `0 2px 8px rgba(0,0,0,0.18), 0 1px 0 ${T.lumaGlow} inset`,
        transition: "all 0.25s cubic-bezier(0.34,1.56,0.64,1)",
        cursor: onClick ? "pointer" : "default",
        ...style,
      }}
    >{children}</div>
  );
}

function StatusBadge({ status }) {
  const map = {
    live:    { bg: T.greenDim,  color: T.green,    label: "● LIVE",    },
    won:     { bg: T.greenDim,  color: T.green,    label: "✓ WON",     },
    lost:    { bg: T.redDim,    color: T.red,      label: "✕ LOST",    },
    pending: { bg: T.emberDim,  color: T.ember,    label: "◷ PENDING", },
    push:    { bg: T.blueDim,   color: T.blue,     label: "– PUSH",    },
    voided:  { bg: T.cardAlt,   color: T.inkMuted, label: "↩ VOIDED",  },
  };
  const s = map[status] || map.pending;
  return (
    <span style={{
      fontSize: 10, fontWeight: 700, letterSpacing: "0.08em",
      fontFamily: FB,
      color: s.color, background: s.bg,
      padding: "3px 8px", borderRadius: 5,
      border: `1px solid ${s.color}22`,
    }}>{s.label}</span>
  );
}

// Section header
function SectionHeader({ title, subtitle, action, index }) {
  return (
    <div style={{ marginBottom: 16 }}>
      {/* Thin ruled divider line */}
      <div style={{
        display: "flex", alignItems: "center", gap: 12, marginBottom: 10,
      }}>
        <div style={{ flex: 1, height: 1, background: T.border }} />
        {index && (
          <span style={{
            fontFamily: FB, fontSize: 9, fontWeight: 700, letterSpacing: "0.18em",
            color: T.inkMuted, textTransform: "uppercase",
          }}>{index}</span>
        )}
        <div style={{ flex: 1, height: 1, background: T.border }} />
      </div>
      {/* Title row */}
      <div style={{ display:"flex", alignItems:"baseline", justifyContent:"space-between" }}>
        <div style={{ display: "flex", alignItems: "baseline", gap: 10 }}>
          <span style={{
            fontFamily: FD, fontSize: 22, fontWeight: 700, letterSpacing: "-0.4px",
            color: T.ink,
          }}>{title}</span>
          {subtitle && (
            <span style={{ fontSize: 11, color: T.inkMuted, letterSpacing: "0.04em" }}>
              {subtitle}
            </span>
          )}
        </div>
        {action}
      </div>
    </div>
  );
}

// Empty state
function EmptyState({ icon, title, sub, cta, onCta }) {
  // Determine icon type for SVG rendering
  const iconType = icon === "📡" ? "live" : icon === "📅" ? "schedule" : icon === "→" ? "picks" : "default";
  const useEmoji = typeof icon === "string" && icon.length <= 2 && !["📡","📅","→"].includes(icon);
  return (
    <div style={{ padding: "56px 24px", textAlign: "center" }}>
      {/* ── Empty-state icon chip ──
          Was: cyan radial gradient with 30px aqua glow (clashed with the
          redesign direction — every empty state looked like a tech demo).
          Now: neutral elevated chip with a subtle ambient ember tint, matching
          the rest of the surfaces. */}
      <div style={{
        position:"relative", width:72, height:72, borderRadius:22, margin:"0 auto 18px",
        background:"rgba(255,107,61,0.04)",
        border:"1px solid rgba(255,255,255,0.06)",
        display:"flex", alignItems:"center", justifyContent:"center",
        boxShadow:"0 1px 0 rgba(255,255,255,0.04) inset, 0 8px 24px -8px rgba(0,0,0,0.4)",
        overflow:"hidden",
      }}>
        {/* top-left highlight for that "physical surface" feel */}
        <div style={{ position:"absolute", inset:0, background:"radial-gradient(circle at 30% 25%, rgba(255,255,255,0.08), transparent 55%)", pointerEvents:"none" }}/>
        <div style={{ position:"relative", color:"rgba(255,255,255,0.55)" }}>
          {useEmoji ? <span style={{ fontSize:32 }}>{icon}</span> : <EmptyStateIcon type={iconType} size={34} />}
        </div>
      </div>
      <div style={{ fontSize:17, fontWeight:700, marginBottom:8, color:"#fff", fontFamily:FD, letterSpacing:"-0.3px" }}>{title}</div>
      <div style={{ fontSize:13, color:"rgba(255,255,255,0.45)", lineHeight:1.6, maxWidth:280, margin:"0 auto" }}>{sub}</div>
      {cta && onCta && (
        <button onClick={onCta} className="no-press" style={{
          marginTop:22, padding:"12px 24px",
          background:"linear-gradient(180deg, #FF6A2F 0%, #FF5A1F 50%, #E84E15 100%)",
          border:"none", borderRadius:14,
          color:"#fff", fontSize:13, fontWeight:700, cursor:"pointer",
          boxShadow:"0 1px 0 rgba(255,255,255,0.20) inset, 0 4px 14px -2px rgba(255,107,61,0.40)",
          letterSpacing:"-0.005em",
          transition:"transform 0.12s",
        }}
          onMouseDown={(e)=>(e.currentTarget.style.transform="scale(0.97)")}
          onMouseUp={(e)=>(e.currentTarget.style.transform="scale(1)")}
          onMouseLeave={(e)=>(e.currentTarget.style.transform="scale(1)")}
          onTouchStart={(e)=>(e.currentTarget.style.transform="scale(0.97)")}
          onTouchEnd={(e)=>(e.currentTarget.style.transform="scale(1)")}
        >{cta}</button>
      )}
    </div>
  );
}

// -- Toast notification system --------------------------------------------------
// Usage: window.torchToast("Message", "success" | "error" | "info")
/* ===========================================
   SKELETON LOADING
=========================================== */
function Skeleton({ width = "100%", height = 16, radius = 8, style: s = {} }) {
  return (
    <div style={{
      width, height, borderRadius: radius,
      background: "linear-gradient(90deg, rgba(255,255,255,0.04) 25%, rgba(255,255,255,0.08) 50%, rgba(255,255,255,0.04) 75%)",
      backgroundSize: "200% 100%",
      animation: "shimmer 1.5s ease infinite",
      ...s,
    }}/>
  );
}

function SkeletonCard({ lines = 3, style: s = {} }) {
  return (
    <div style={{
      borderRadius: S.radius, padding: S.card,
      background: "rgba(255,255,255,0.02)",
      border: `1px solid ${T.border}`,
      ...s,
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14 }}>
        <Skeleton width={40} height={40} radius={12} />
        <div style={{ flex: 1 }}>
          <Skeleton width="60%" height={14} style={{ marginBottom: 8 }} />
          <Skeleton width="40%" height={10} />
        </div>
      </div>
      {Array.from({ length: lines }).map((_, i) => (
        <Skeleton key={i} width={`${90 - i * 15}%`} height={12} style={{ marginBottom: i < lines - 1 ? 8 : 0 }} />
      ))}
    </div>
  );
}

function GameCardSkeleton() {
  return (
    <div style={{
      borderRadius: 22, padding: "14px 16px",
      background: "linear-gradient(155deg, rgba(22,24,36,1) 0%, rgba(14,14,18,1) 40%, rgba(10,12,18,1) 100%)",
      border: `1px solid ${T.border}`,
      marginBottom: 14,
    }}>
      <Skeleton width={60} height={18} radius={6} style={{ marginBottom: 14 }} />
      <div style={{ display: "flex", alignItems: "center", gap: 14, padding: "10px 0" }}>
        <Skeleton width={36} height={36} radius={18} />
        <Skeleton width="50%" height={16} />
        <div style={{ marginLeft: "auto" }}><Skeleton width={32} height={26} /></div>
      </div>
      <Skeleton width="100%" height={1} style={{ margin: "4px 0" }} />
      <div style={{ display: "flex", alignItems: "center", gap: 14, padding: "10px 0" }}>
        <Skeleton width={36} height={36} radius={18} />
        <Skeleton width="45%" height={16} />
        <div style={{ marginLeft: "auto" }}><Skeleton width={32} height={26} /></div>
      </div>
      <Skeleton width="100%" height={1} style={{ margin: "6px 0 12px" }} />
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 6 }}>
        {Array.from({ length: 6 }).map((_, i) => (
          <Skeleton key={i} height={44} radius={10} />
        ))}
      </div>
    </div>
  );
}

/* ===========================================
   TORCH LOADER
=========================================== */
// ── PHASE B SKELETONS (Aug 29, 2026) ────────────────────────────────────────
// Content-shaped placeholders for the remaining spinner sites (Leagues list,
// My Picks, Pulse feed, comments). Same Skeleton primitive + shimmer as
// GameCardSkeleton; shapes mirror the real cards so the load reads as the
// page assembling rather than a wait.
function LeagueCardSkeleton() {
  return (
    <div style={{
      borderRadius:20, padding:"18px 16px 14px",
      background:T.card, border:`1px solid ${T.border}`, marginBottom:14,
    }}>
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:14 }}>
        <Skeleton width={140} height={18} />
        <Skeleton width={56} height={20} radius={10} />
      </div>
      <div style={{ display:"flex", gap:6, marginBottom:14 }}>
        {[0,1,2,3].map(i => <Skeleton key={i} width={26} height={26} radius={13} />)}
      </div>
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:8 }}>
        {[0,1,2].map(i => <Skeleton key={i} height={44} radius={10} />)}
      </div>
    </div>
  );
}

function PickCardSkeleton() {
  return (
    <div style={{
      borderRadius:16, padding:"14px 16px",
      background:T.card, border:`1px solid ${T.border}`, marginBottom:12,
    }}>
      <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:12 }}>
        <Skeleton width={34} height={34} radius={10} />
        <Skeleton width={110} height={12} />
        <div style={{ marginLeft:"auto" }}><Skeleton width={44} height={10} /></div>
      </div>
      <Skeleton width="70%" height={18} style={{ marginBottom:8 }} />
      <Skeleton width="45%" height={11} style={{ marginBottom:14 }} />
      <Skeleton width="100%" height={54} radius={12} />
    </div>
  );
}

function CommentRowSkeleton() {
  return (
    <div style={{ display:"flex", gap:10, padding:"10px 0" }}>
      <Skeleton width={30} height={30} radius={15} />
      <div style={{ flex:1 }}>
        <Skeleton width={90} height={10} style={{ marginBottom:8 }} />
        <Skeleton width="85%" height={12} />
      </div>
    </div>
  );
}

function TorchLoader({ message = "Loading…", inline = false, skeleton = false }) {
  const [frame, setFrame] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setFrame(f => (f + 1) % 4), 350);
    return () => clearInterval(t);
  }, []);
  const dots = ["", ".", "..", "..."][frame];

  if (inline && skeleton) return (
    <div style={{ padding: `${S.lg}px ${S.page}px` }}>
      {Array.from({ length: 3 }).map((_, i) => (
        <GameCardSkeleton key={i} />
      ))}
    </div>
  );

  if (inline) return (
    <div style={{
      display:"flex", flexDirection:"column", alignItems:"center",
      justifyContent:"center", gap:14, padding:`48px ${S.page}px`,
    }}>
      <TorchLogo size={64} />
      <span style={{
        ...TY.small, color:"rgba(255,255,255,0.45)",
        letterSpacing:"0.05em",
        fontFamily:FB,
      }}>{message}{dots}</span>
    </div>
  );

  return (
    <div style={{
      position:"fixed", inset:0, zIndex:200,
      background:"#0D0E12",
      display:"flex", flexDirection:"column",
      alignItems:"center", justifyContent:"center", gap:18,
    }}>
      <TorchLogo size={80} />
      <span style={{
        ...TY.body, fontWeight:600, color:"rgba(255,255,255,0.5)",
        letterSpacing:"0.06em",
        fontFamily:FB,
      }}>{message}{dots}</span>
    </div>
  );
}

function ToastContainer() {
  const [toasts, setToasts] = useState([]);
  useEffect(() => {
    window.torchToast = (msg, type = "info") => {
      const id = Date.now();
      setToasts(prev => [...prev, { id, msg, type }]);
      setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 3200);
      // Haptic feedback on toasts
      if (type === "success" || type === "payday") haptic.success();
      else if (type === "error") haptic.error();
      else haptic.light();
    };
    return () => { delete window.torchToast; };
  }, []);

  // ── Toast palette ──
  // Was: saturated colored gradients with heavy glows (every toast looked like
  // an alert, even simple "info" messages). Now: clean dark elevated surface
  // with a colored 3px left accent bar — the message stays primary, status
  // stays semantic.
  const colors = {
    success: {
      accent: "#34d399",
      tint: "rgba(52,211,153,0.06)",
      icon: (<svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M20 6L9 17l-5-5" stroke="#34d399" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"/></svg>),
    },
    error: {
      accent: "#f87171",
      tint: "rgba(248,113,113,0.06)",
      icon: (<svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M18 6L6 18M6 6l12 12" stroke="#f87171" strokeWidth="2.4" strokeLinecap="round"/></svg>),
    },
    info: {
      accent: "rgba(255,255,255,0.45)",
      tint: "rgba(255,255,255,0.02)",
      icon: (<svg width="18" height="18" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="9" stroke="rgba(255,255,255,0.50)" strokeWidth="1.6"/><path d="M12 8v0M12 12v4" stroke="rgba(255,255,255,0.65)" strokeWidth="2" strokeLinecap="round"/></svg>),
    },
    payday: {
      accent: "#fbbf24",
      tint: "rgba(251,191,36,0.06)",
      icon: (<svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M12 5v14M5 12h14" stroke="#fbbf24" strokeWidth="2.4" strokeLinecap="round"/></svg>),
    },
  };

  return (
    <div style={{
      position:"fixed", top:"calc(70px + env(safe-area-inset-top, 0px))", left:"50%", transform:"translateX(-50%)",
      zIndex:9999, display:"flex", flexDirection:"column", alignItems:"center", gap:8,
      pointerEvents:"none", width:"100%", maxWidth:400, padding:"0 16px",
    }}>
      {toasts.map(({ id, msg, type }) => {
        const c = colors[type] || colors.info;
        return (
          <div key={id} style={{
            position:"relative",
            display:"flex", alignItems:"center", gap:11,
            padding:"12px 16px 12px 18px", borderRadius:14, width:"100%",
            background:"#1A1C26", color:"#fff",
            border:"1px solid rgba(255,255,255,0.06)",
            fontSize:13.5, fontWeight:500,
            letterSpacing:"-0.005em",
            boxShadow:`0 1px 0 rgba(255,255,255,0.04) inset, 0 12px 32px -8px rgba(0,0,0,0.5)`,
            animation:"fadeUp 0.25s cubic-bezier(0.32,0.72,0,1)",
            pointerEvents:"auto",
            overflow:"hidden",
          }}>
            {/* Left accent bar — semantic status color */}
            <div style={{ position:"absolute", left:0, top:0, bottom:0, width:3, background:c.accent }}/>
            {/* Subtle status tint wash */}
            <div style={{ position:"absolute", inset:0, background:`linear-gradient(90deg, ${c.tint}, transparent 40%)`, pointerEvents:"none" }}/>
            <span style={{ position:"relative", flexShrink:0, display:"flex", alignItems:"center" }}>{c.icon}</span>
            <span style={{ position:"relative", flex:1 }}>{msg}</span>
          </div>
        );
      })}
    </div>
  );
}

// -- Confirm dialog (replaces window.confirm) ----------------------------------
// Usage: window.torchConfirm("Are you sure?", onConfirm, { danger: true })
function ConfirmDialog() {
  const [state, setState] = useState(null); // { msg, onConfirm, danger, confirmLabel }
  useEffect(() => {
    window.torchConfirm = (msg, onConfirm, opts = {}) => {
      setState({ msg, onConfirm, danger: opts.danger ?? false, confirmLabel: opts.confirmLabel ?? "Confirm" });
    };
    return () => { delete window.torchConfirm; };
  }, []);

  const [closing, setClosing] = useState(false);
  const closingRef = useRef(false);

  if (!state) return null;
  const close = () => setState(null);
  // Self-resetting exit: this singleton never unmounts, so unlike
  // useSheetClose the guard must clear itself for the next confirm.
  const requestClose = (after) => {
    if (closingRef.current) return;
    closingRef.current = true;
    setClosing(true);
    setTimeout(() => {
      close();
      setClosing(false);
      closingRef.current = false;
      if (typeof after === "function") after();
    }, 200);
  };

  return (
    <>
      <div onClick={() => requestClose()} style={{
        position:"fixed", inset:0, background:"rgba(15,23,42,0.5)",
        backdropFilter:"blur(4px)", zIndex:400,
        animation: closing ? "slipFadeOut 0.18s ease both" : "fadeIn 0.2s ease both",
        pointerEvents: closing ? "none" : "auto",
      }}/>
      <div style={{
        position:"fixed", bottom:0, left:"50%", transform:"translateX(-50%)",
        width:"100%", maxWidth:500, zIndex:401,
        background:"#131520", borderRadius:"24px 24px 0 0",
        border:"1px solid rgba(255,255,255,0.06)",
        borderBottom:"none",
        padding:"20px 20px 24px",
        paddingBottom:"calc(24px + env(safe-area-inset-bottom, 0px))",
        boxShadow:"0 1px 0 rgba(255,255,255,0.06) inset, 0 -16px 60px -8px rgba(0,0,0,0.6)",
        // slipEnterUp keeps translateX(-50%) throughout — the duplicate
        // `slideUp` keyframes (later def wins, no translateX) were animating
        // this centered sheet off-center for 300ms then snapping. Same fix
        // as PickSlipDrawer.
        animation: closing ? "slipExitDown 0.2s cubic-bezier(0.4,0,1,1) both" : "slipEnterUp 0.26s cubic-bezier(0.32,0.72,0,1)",
        pointerEvents: closing ? "none" : "auto",
      }}>
        <div style={{ width:40, height:4, borderRadius:2, background:"rgba(255,255,255,0.10)", margin:"0 auto 18px" }}/>
        <p style={{ fontSize:16, fontWeight:600, color:"#fff", textAlign:"center",
          lineHeight:1.5, marginBottom:24, letterSpacing:"-0.005em", padding:"0 8px" }}>{state.msg}</p>
        <div style={{ display:"flex", gap:10 }}>
          <button onClick={() => requestClose()} className="no-press" style={{
            flex:1, padding:"14px",
            background:"rgba(255,255,255,0.04)",
            border:"1px solid rgba(255,255,255,0.08)", borderRadius:14,
            fontSize:14, fontWeight:600, color:"rgba(255,255,255,0.75)", cursor:"pointer",
            letterSpacing:"-0.005em",
            transition:"background 0.15s, transform 0.12s",
          }}
            onMouseDown={(e)=>(e.currentTarget.style.transform="scale(0.98)")}
            onMouseUp={(e)=>(e.currentTarget.style.transform="scale(1)")}
            onMouseLeave={(e)=>(e.currentTarget.style.transform="scale(1)")}
          >Cancel</button>
          <button onClick={() => { state.onConfirm(); requestClose(); }} className="no-press" style={{
            flex:1, padding:"14px",
            background: state.danger
              ? "linear-gradient(180deg, #F87171 0%, #EF4444 50%, #DC2626 100%)"
              : "linear-gradient(180deg, #FF6A2F 0%, #FF5A1F 50%, #E84E15 100%)",
            border:"none", borderRadius:14,
            fontSize:14, fontWeight:700, color:"#fff", cursor:"pointer",
            letterSpacing:"-0.005em",
            boxShadow: state.danger
              ? "0 1px 0 rgba(255,255,255,0.20) inset, 0 4px 16px -2px rgba(239,68,68,0.40)"
              : "0 1px 0 rgba(255,255,255,0.20) inset, 0 4px 16px -2px rgba(255,107,61,0.40)",
            transition:"transform 0.12s",
          }}
            onMouseDown={(e)=>(e.currentTarget.style.transform="scale(0.98) translateY(1px)")}
            onMouseUp={(e)=>(e.currentTarget.style.transform="scale(1)")}
            onMouseLeave={(e)=>(e.currentTarget.style.transform="scale(1)")}
            onTouchStart={(e)=>(e.currentTarget.style.transform="scale(0.98) translateY(1px)")}
            onTouchEnd={(e)=>(e.currentTarget.style.transform="scale(1)")}
          >{state.confirmLabel}</button>
        </div>
      </div>
    </>
  );
}

// Mini sparkline
function Sparkline({ data, width = 120, height = 36 }) {
  // FIX #4: guard single data point — (i / (len-1)) = 0/0 = NaN coordinates
  if (!data || data.length < 2) return null;
  const max = Math.max(...data.map(Math.abs)) || 1;
  const points = data.map((v, i) => {
    const x = (i / (data.length - 1)) * width;
    const y = height / 2 - (v / max) * (height / 2 - 4);
    return `${x},${y}`;
  }).join(" ");
  const lastPositive = data[data.length - 1] >= 0;
  return (
    <svg width={width} height={height} style={{ overflow: "visible" }}>
      <polyline
        points={points}
        fill="none"
        stroke={lastPositive ? T.green : T.red}
        strokeWidth={1.5}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// Win-rate ring
function WinRing({ pct, size = 56 }) {
  const r = size / 2 - 5;
  const circ = 2 * Math.PI * r;
  const dash = (pct / 100) * circ;
  return (
    <svg width={size} height={size} style={{ transform: "rotate(-90deg)" }}>
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={T.border} strokeWidth={4} />
      <circle cx={size/2} cy={size/2} r={r} fill="none"
        stroke={T.blue} strokeWidth={4}
        strokeDasharray={`${dash} ${circ}`}
        strokeLinecap="round"
      />
    </svg>
  );
}

// Balance history line chart
// FIX #5: unique gradient ID per instance — a shared "bgc" id means the second
// chart on the page overwrites the first's gradient, showing the wrong color.
let _balChartSeq = 0;
function BalanceChart({ data, loading: histLoading = false, width = "100%", height = 100 }) {
  const gradId = useRef(`bgc_${++_balChartSeq}`).current;
  if (histLoading) return (
    <div style={{ height, display:"flex", alignItems:"center", justifyContent:"center" }}>
      <div style={{ width:"100%", height:Math.round(height*0.4), borderRadius:6,
        background:T.border, animation:"pulse 1.4s ease infinite" }}/>
    </div>
  );
  if (!data || data.length < 2) return (
    <div style={{ height, display:"flex", alignItems:"center", justifyContent:"center" }}>
      <span style={{ fontSize:12, color:T.inkMuted }}>Make more picks to see your chart</span>
    </div>
  );

  const vals  = data.map(d => d.balance);
  const first = vals[0];
  const last  = vals[vals.length - 1];
  const min   = Math.min(...vals);
  const max   = Math.max(...vals);
  const range = max - min || 1;
  const net   = last - first;
  const up    = net >= 0;
  const color = up ? T.green : T.red;

  // Canvas coords — left margin reserved for y-axis labels
  const W = 420; const H = height;
  const LM = 46; const RM = 6; const chartW = W - LM - RM;
  const PAD_TOP = 18; const PAD_BOT = 4;

  const toY = (v) => PAD_TOP + ((max - v) / range) * (H - PAD_TOP - PAD_BOT);
  const pts = vals.map((v, i) => {
    const x = LM + (i / (vals.length - 1)) * chartW;
    const y = toY(v);
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(" ");

  const firstPt = pts.split(" ")[0];
  const lastPt  = pts.split(" ").pop();
  const [lx, ly] = lastPt.split(",");
  const area = `M${firstPt} ${pts.split(" ").slice(1).map(p=>`L${p}`).join(" ")} L${lx},${H} L${firstPt.split(",")[0]},${H} Z`;

  // Y-axis ticks: max, midpoint, min — skip mid if too close to others
  const mid = Math.round((max + min) / 2);
  const yTicks = [max, mid, min].filter((v, i, arr) => {
    if (i === 0 || i === arr.length - 1) return true;
    return Math.abs(toY(v) - toY(arr[0])) > 14 && Math.abs(toY(v) - toY(arr[arr.length-1])) > 14;
  });

  return (
    <div>
      {/* Net change summary row */}
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8 }}>
        <span style={{ fontSize:11, color:T.inkMuted }}>{data.length} data points</span>
        <span style={{
          fontSize:12, fontWeight:700, padding:"3px 10px", borderRadius:20,
          background: up ? "rgba(22,163,74,0.10)" : "rgba(220,38,38,0.09)",
          color,
        }}>
          {up ? "+" : ""}{net.toLocaleString()} pts overall
        </span>
      </div>

      <svg viewBox={`0 0 ${W} ${H}`} width={width} height={height} style={{ overflow:"visible", display:"block" }}>
        <defs>
          <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%"   stopColor={color} stopOpacity="0.18"/>
            <stop offset="100%" stopColor={color} stopOpacity="0"/>
          </linearGradient>
        </defs>

        {/* Y-axis gridlines + labels */}
        {yTicks.map((v, i) => {
          const y = toY(v);
          const isMin = v === min; const isMax = v === max;
          return (
            <g key={i}>
              <line x1={LM} y1={y} x2={W - RM} y2={y}
                stroke={T.border} strokeWidth="1" strokeDasharray="3,5"/>
              <text x={LM - 5} y={y + 4} textAnchor="end"
                fontSize="10" fill={isMax ? color : isMin ? color : T.inkMuted}
                fontWeight={isMax || isMin ? "700" : "400"}
                fontFamily={FB}>
                {v.toLocaleString()}
              </text>
            </g>
          );
        })}

        {/* Area fill */}
        <path d={area} fill={`url(#${gradId})`}/>

        {/* Line */}
        <polyline points={pts} fill="none" stroke={color}
          strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>

        {/* Start dot + label */}
        {(() => {
          const [fx, fy] = firstPt.split(",");
          return (
            <g>
              <circle cx={fx} cy={fy} r="3.5" fill={T.inkMuted} stroke="white" strokeWidth="1.5"/>
              <text x={Number(fx)} y={Number(fy) - 8} textAnchor="start"
                fontSize="10" fill={T.inkMuted}
                fontFamily={FB}>
                {first.toLocaleString()}
              </text>
            </g>
          );
        })()}

        {/* End dot + label (current balance — prominent) */}
        <g>
          <circle cx={lx} cy={ly} r="5" fill={color} stroke="white" strokeWidth="2"/>
          <text x={Number(lx)} y={Number(ly) - 9} textAnchor="end"
            fontSize="11" fontWeight="700" fill={color}
            fontFamily={FB}>
            {last.toLocaleString()}
          </text>
        </g>
      </svg>
    </div>
  );
}

/* ===========================================
   NOTIFICATION PERMISSION PROMPT BANNER
=========================================== */
function NotificationPromptBanner({ userId, onDismiss }) {
  const [loading, setLoading] = useState(false);
  const [granted, setGranted] = useState(false);

  const handleEnable = async () => {
    setLoading(true);
    try {
      const perm = await requestNotificationPermission();
      if (perm === "granted") {
        await registerFCMToken(userId);
        setGranted(true);
        setTimeout(onDismiss, 1600);
      } else {
        onDismiss();
      }
    } catch {
      onDismiss();
    } finally { setLoading(false); }
  };

  return (
    <div style={{
      position:"fixed", bottom:80, left:"50%", transform:"translateX(-50%)",
      width:"calc(100% - 32px)", maxWidth:420, zIndex:300,
      background:T.navy, borderRadius:16, padding:"16px 18px",
      boxShadow:"0 8px 32px rgba(0,0,0,0.25)",
      border:"1px solid rgba(255,255,255,0.1)",
      // Centered element — needs slipEnterUp, not the collided slideUp
      // (see PickSlipDrawer comment).
      animation:"slipEnterUp 0.3s cubic-bezier(0.32,0.72,0,1)",
      display:"flex", alignItems:"center", gap:14,
    }}>
      <div style={{
        width:44, height:44, borderRadius:12, flexShrink:0,
        background:"rgba(255,107,61,0.2)",
        display:"flex", alignItems:"center", justifyContent:"center", fontSize:22,
      }}>🔔</div>
      <div style={{ flex:1, minWidth:0 }}>
        {granted ? (
          <div style={{ fontSize:14, fontWeight:700, color:"#fff" }}>✅ Notifications on!</div>
        ) : (
          <>
            <div style={{ fontSize:14, fontWeight:700, color:"#fff", marginBottom:2 }}>
              Get notified when picks settle
            </div>
            <div style={{ fontSize:12, color:"rgba(255,255,255,0.6)", lineHeight:1.4 }}>
              Never miss a big win — even when the app is closed.
            </div>
          </>
        )}
      </div>
      {!granted && (
        <div style={{ display:"flex", flexDirection:"column", gap:6, flexShrink:0 }}>
          <button onClick={handleEnable} disabled={loading} style={{
            padding:"7px 14px", background:T.coral, border:"none", borderRadius:8,
            color:"#fff", fontSize:12, fontWeight:700, cursor:"pointer",
            opacity:loading ? 0.7 : 1, whiteSpace:"nowrap",
          }}>{loading ? "…" : "Turn on"}</button>
          <button onClick={() => { markNotificationPromptDismissed(); onDismiss(); }} style={{
            padding:"4px", background:"none", border:"none",
            color:"rgba(255,255,255,0.4)", fontSize:11, cursor:"pointer",
          }}>Not now</button>
        </div>
      )}
    </div>
  );
}

/* ===========================================
   SHARE PICK MODAL
=========================================== */
function SharePickModal({ pick, user, onClose }) {
  const { closing, requestClose } = useSheetClose(onClose, 200);
  const canvasRef = useRef(null);
  const [copied, setCopied] = useState(false);
  const [imgUrl, setImgUrl] = useState(null);

  const isWon     = pick.status === "won";
  const isLost    = pick.status === "lost";
  const resultEmoji = isWon ? "🔥" : isLost ? "💀" : "⏳";
  const resultLabel = isWon ? "HIT" : isLost ? "MISS" : "PENDING";
  const resultColor = isWon ? "#16a34a" : isLost ? "#dc2626" : "#2563eb";
  const pts = isWon
    ? `+${pick.points ?? pick.expectedPayout ?? ""} pts`
    : isLost
    ? `-${pick.wager ?? ""} pts`
    : `${pick.wager ?? ""} pts wagered`;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const W = 600, H = 320;
    // Hi-DPI rendering — draw at 2x physical pixels for crisp text on retina screens
    const dpr = window.devicePixelRatio || 1;
    canvas.width  = W * dpr;
    canvas.height = H * dpr;
    canvas.style.width  = `${W}px`;
    canvas.style.height = `${H}px`;
    ctx.scale(dpr, dpr);

    // Background
    const bg = ctx.createLinearGradient(0, 0, W, H);
    bg.addColorStop(0, "#0f172a");
    bg.addColorStop(1, "#1e3a5f");
    ctx.fillStyle = bg;
    if (ctx.roundRect) ctx.roundRect(0, 0, W, H, 20); else ctx.rect(0, 0, W, H);
    ctx.fill();

    // Grid texture
    ctx.strokeStyle = "rgba(255,255,255,0.04)";
    ctx.lineWidth = 1;
    for (let x = 0; x < W; x += 32) { ctx.beginPath(); ctx.moveTo(x,0); ctx.lineTo(x,H); ctx.stroke(); }
    for (let y = 0; y < H; y += 32) { ctx.beginPath(); ctx.moveTo(0,y); ctx.lineTo(W,y); ctx.stroke(); }

    // Glow orb
    const orb = ctx.createRadialGradient(W*0.82, H*0.18, 0, W*0.82, H*0.18, 240);
    orb.addColorStop(0, isWon ? "rgba(22,163,74,0.22)" : isLost ? "rgba(220,38,38,0.22)" : "rgba(255,107,61,0.18)");
    orb.addColorStop(1, "transparent");
    ctx.fillStyle = orb;
    ctx.fillRect(0, 0, W, H);

    // Torch wordmark
    ctx.fillStyle = "#ff4500";
    ctx.font = "bold 16px -apple-system,system-ui,sans-serif";
    ctx.fillText("torch", 54, 46);
    ctx.fillStyle = "rgba(255,255,255,0.9)";
    ctx.font = "14px -apple-system,system-ui,sans-serif";
    ctx.fillText("🔥", 32, 46);

    // Username
    const uname = user?.displayName || user?.email?.split("@")[0] || "Anonymous";
    ctx.fillStyle = "rgba(255,255,255,0.45)";
    ctx.font = "12px -apple-system,system-ui,sans-serif";
    ctx.fillText(`@${uname}`, 32, 66);

    // Pick label
    ctx.fillStyle = "#ffffff";
    ctx.font = "bold 30px -apple-system,system-ui,sans-serif";
    const label = pick.label || "Pick";
    ctx.fillText(label.length > 28 ? label.slice(0,26)+"…" : label, 32, 126);

    // Game context
    ctx.fillStyle = "rgba(255,255,255,0.5)";
    ctx.font = "13px -apple-system,system-ui,sans-serif";
    const gl = pick.gameLabel || "";
    ctx.fillText(gl.length > 44 ? gl.slice(0,42)+"…" : gl, 32, 154);

    // Sport / type / odds
    ctx.fillStyle = "rgba(255,255,255,0.35)";
    ctx.font = "12px -apple-system,system-ui,sans-serif";
    const oddsStr = pick.odds ? (pick.odds > 0 ? `+${pick.odds}` : `${pick.odds}`) : "";
    ctx.fillText(`${pick.sport || ""} · ${(pick.type||"").toUpperCase()} ${oddsStr}`, 32, 178);

    // Result pill
    const px = 32, py = 202, pw = 124, ph = 38;
    ctx.fillStyle = isWon ? "rgba(22,163,74,0.18)" : isLost ? "rgba(220,38,38,0.18)" : "rgba(37,99,235,0.18)";
    ctx.beginPath();
    if (ctx.roundRect) ctx.roundRect(px, py, pw, ph, 19); else ctx.rect(px, py, pw, ph);
    ctx.fill();
    ctx.strokeStyle = resultColor; ctx.lineWidth = 1.5;
    ctx.beginPath();
    if (ctx.roundRect) ctx.roundRect(px, py, pw, ph, 19); else ctx.rect(px, py, pw, ph);
    ctx.stroke();
    ctx.fillStyle = resultColor;
    ctx.font = "bold 14px -apple-system,system-ui,sans-serif";
    ctx.textAlign = "center";
    ctx.fillText(`${resultEmoji} ${resultLabel}`, px + pw/2, py + 25);
    ctx.textAlign = "left";

    // Points value
    ctx.fillStyle = resultColor;
    ctx.font = "bold 26px -apple-system,system-ui,sans-serif";
    ctx.textAlign = "right";
    ctx.fillText(pts, W - 32, 232);
    ctx.textAlign = "left";

    // Divider
    ctx.strokeStyle = "rgba(255,255,255,0.1)"; ctx.lineWidth = 1;
    ctx.beginPath(); ctx.moveTo(32, 264); ctx.lineTo(W-32, 264); ctx.stroke();

    // Footer
    ctx.fillStyle = "rgba(255,255,255,0.28)";
    ctx.font = "11px -apple-system,system-ui,sans-serif";
    ctx.fillText("torchpicks.com · Free sports picks. No money. Just bragging rights.", 32, 287);

    setImgUrl(canvas.toDataURL("image/png"));
  }, [pick, user]);

  const oddsDisp = pick.odds ? (pick.odds > 0 ? `+${pick.odds}` : `${pick.odds}`) : "";
  const shareText = `${resultEmoji} ${pick.label} ${oddsDisp} — ${resultLabel} ${pts}\n${pick.gameLabel || ""}\n\nFree picks on torchpicks.com 🔥`;

  const handleShare = async () => {
    if (imgUrl && navigator.canShare) {
      try {
        const blob = await (await fetch(imgUrl)).blob();
        const file = new File([blob], "torch-pick.png", { type: "image/png" });
        if (navigator.canShare({ files: [file] })) {
          await navigator.share({ files: [file], text: shareText });
          return;
        }
      } catch {}
    }
    if (navigator.share) {
      try { await navigator.share({ text: shareText, url: "https://torchpicks.com" }); return; } catch {}
    }
    navigator.clipboard?.writeText(shareText).then(() => {
      setCopied(true); setTimeout(() => setCopied(false), 2500);
    }).catch(() => {});
  };

  const handleDownload = () => {
    if (!imgUrl) return;
    const a = document.createElement("a");
    a.href = imgUrl; a.download = "torch-pick.png"; a.click();
  };

  return (
    <>
      <div onClick={() => requestClose()} style={{
        position:"fixed", inset:0, background:"rgba(0,0,0,0.65)",
        backdropFilter:"blur(6px)", zIndex:190,
        animation: closing ? "slipFadeOut 0.18s ease both" : "fadeIn 0.2s ease both",
        pointerEvents: closing ? "none" : "auto",
      }}/>
      <div style={{
        position:"fixed", bottom:0, left:"50%", transform:"translateX(-50%)",
        width:"100%", maxWidth:500,
        background:T.card, borderRadius:"20px 20px 0 0",
        border:`1px solid ${T.border}`, zIndex:200,
        padding:"20px 20px calc(env(safe-area-inset-bottom,0px) + 24px)",
        // Centered sheet — needs slipEnterUp, not the collided slideUp
        // (see PickSlipDrawer comment).
        animation: closing ? "slipExitDown 0.2s cubic-bezier(0.4,0,1,1) both" : "slipEnterUp 0.26s cubic-bezier(0.32,0.72,0,1)",
        pointerEvents: closing ? "none" : "auto",
        boxShadow:"0 -12px 48px rgba(0,0,0,0.25)",
      }}>
        <div style={{ width:36, height:4, borderRadius:2, background:T.border, margin:"0 auto 16px" }}/>
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:14 }}>
          <div style={{ fontFamily:FD, fontSize:18, fontWeight:700 }}>Share Pick</div>
          <button onClick={() => requestClose()} style={{ background:"none", border:"none", cursor:"pointer",
            color:T.inkMuted, fontSize:22, lineHeight:1 }} aria-label="Close">×</button>
        </div>

        <canvas ref={canvasRef} style={{ display:"none" }}/>
        {imgUrl ? (
          <img src={imgUrl} alt="Pick card" style={{
            width:"100%", borderRadius:12, marginBottom:14,
            boxShadow:"0 4px 24px rgba(0,0,0,0.18)", display:"block",
          }}
          onError={() => setImgUrl("")}/>
        ) : (
          <div style={{ height:160, borderRadius:12, background:T.cardAlt,
            marginBottom:14, display:"flex", alignItems:"center", justifyContent:"center" }}>
            <div style={{ color:T.inkMuted, fontSize:13 }}>Generating card…</div>
          </div>
        )}

        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
          <button onClick={handleShare} style={{
            padding:"13px", background:T.coral, border:"none", borderRadius:12,
            color:"#fff", fontSize:14, fontWeight:700, cursor:"pointer",
            display:"flex", alignItems:"center", justifyContent:"center", gap:8,
          }}>
            {copied ? (
              <span style={{ display:"flex", alignItems:"center", gap:6 }}><span>✓</span> Copied!</span>
            ) : (
              <>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/>
                  <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
                </svg>
                Share
              </>
            )}
          </button>
          <button onClick={handleDownload} style={{
            padding:"13px", background:T.cardAlt,
            border:`1.5px solid ${T.border}`, borderRadius:12,
            color:T.ink, fontSize:14, fontWeight:700, cursor:"pointer",
            display:"flex", alignItems:"center", justifyContent:"center", gap:8,
          }}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={T.ink} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
            </svg>
            Save Image
          </button>
        </div>
      </div>
    </>
  );
}

// ── FAQ Banner + Modal ───────────────────────────────────────────────────────
function FAQModal({ onClose }) {
  const faqs = [
    {
      q: "What is Torch?",
      a: "Torch is a free sports prediction app — like fantasy sports meets a pick'em league. You make picks on real games using virtual points. No real money, no gambling. Just bragging rights with friends."
    },
    {
      q: "How do I get started?",
      a: "You start with 100 virtual points. Head to the Games tab, browse live odds, and add picks to your slip. Submit your picks to any league you've joined, and watch the scores roll in."
    },
    {
      q: "What can I predict?",
      a: "Spreads, totals (over/under), and moneylines — across NBA, NFL, MLB, NHL, college basketball, college football, and more. Torch pulls real-time odds so you're always picking against real lines."
    },
    {
      q: "How do leagues work?",
      a: "Create a private league and share the invite code with friends (or join a public one). Every pick you submit goes against your league balance. Leaderboards track who's actually good — and who just talks a big game."
    },
    {
      q: "Is this real money?",
      a: "Absolutely not. Torch uses virtual points only — real games, real odds, fake stakes, bragging rights. All the fun of making picks, none of the financial risk."
    },
    {
      q: "How do points work?",
      a: "You wager virtual points on each pick. Payouts follow real American odds — just like a sportsbook. Your league captain sets the payday schedule and amount to keep competition going."
    },
    {
      q: "When do picks settle?",
      a: "Picks settle automatically once the game ends and final scores are confirmed. You'll see results on your My Picks page — wins go green, losses go red."
    },
    {
      q: "What is the Captain role?",
      a: "Each league has a Captain (the creator). Captains can manage league settings, remove members, and delete the league. It's your league — run it how you want."
    },
    {
      q: "What is Pass the Torch?",
      a: "Split a pick with a league mate. You both put up the same amount of points on the same pick. Win it, split the payout. Lose it, both take the hit. It's how friends actually bet together — real skin in the game on both sides."
    },
  ];

  const [openIdx, setOpenIdx] = useState(null);

  return (
    <div onClick={onClose} style={{
      position:"fixed", inset:0, zIndex:400,
      background:"rgba(10,12,18,0.92)",
      backdropFilter:"blur(16px)",
      display:"flex", alignItems:"center", justifyContent:"center",
      padding:20, animation:"fadeUp 0.2s ease",
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background:"#16171D", borderRadius:20,
        border:"1px solid rgba(255,255,255,0.08)",
        width:"100%", maxWidth:520, maxHeight:"calc(85vh - env(safe-area-inset-top, 0px))",
        overflow:"auto", WebkitOverflowScrolling:"touch", overscrollBehavior:"contain", padding:"28px 24px",
        boxShadow:"0 24px 80px rgba(0,0,0,0.7)",
      }}>
        {/* Header */}
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:24 }}>
          <div style={{ display:"flex", alignItems:"center", gap:10 }}>
            <TorchLogo size={22} />
            <span style={{ fontFamily:FD, fontSize:20, fontWeight:700, color:"#fff", letterSpacing:"-0.03em" }}>
              How Torch Works
            </span>
          </div>
          <button onClick={onClose} style={{
            background:"rgba(255,255,255,0.06)", border:"1px solid rgba(255,255,255,0.08)",
            borderRadius:10, width:32, height:32, display:"flex",
            alignItems:"center", justifyContent:"center", cursor:"pointer",
            fontSize:15, color:"rgba(255,255,255,0.5)", transition:"all 0.15s",
          }} aria-label="Close">✕</button>
        </div>

        {/* FAQ Items */}
        <div style={{ display:"flex", flexDirection:"column", gap:2 }}>
          {faqs.map((faq, i) => {
            const isOpen = openIdx === i;
            return (
              <div key={i} style={{
                borderRadius:12,
                background: isOpen ? "rgba(255,255,255,0.04)" : "transparent",
                transition:"background 0.15s",
              }}>
                <button onClick={() => setOpenIdx(isOpen ? null : i)} style={{
                  width:"100%", display:"flex", alignItems:"center", justifyContent:"space-between",
                  padding:"14px 14px", background:"none", border:"none",
                  cursor:"pointer", textAlign:"left", gap:12,
                }}>
                  <span style={{
                    fontSize:14, fontWeight:600, color: isOpen ? "#fff" : "rgba(255,255,255,0.75)",
                    letterSpacing:"-0.01em", lineHeight:1.4, flex:1,
                  }}>{faq.q}</span>
                  <span style={{
                    fontSize:11, color:"rgba(255,255,255,0.42)", flexShrink:0,
                    transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
                    transition:"transform 0.2s ease",
                  }}>▼</span>
                </button>
                {isOpen && (
                  <div style={{
                    padding:"0 14px 14px",
                    fontSize:13, lineHeight:1.7, color:"rgba(255,255,255,0.55)",
                    animation:"fadeUp 0.15s ease",
                  }}>{faq.a}</div>
                )}
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div style={{
          marginTop:20, paddingTop:16,
          borderTop:"1px solid rgba(255,255,255,0.06)",
          textAlign:"center",
        }}>
          <div style={{ fontSize:12, color:"rgba(255,255,255,0.42)", lineHeight:1.6 }}>
            Questions? Feedback? Reach out to your league Captain or drop us a line.
          </div>
        </div>
      </div>
    </div>
  );
}

function FAQBanner({ onOpenFAQ }) {
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem("torch_faq_dismissed") === "1"; } catch { return false; }
  });

  if (dismissed) return null;

  return (
    <div style={{
      background:"linear-gradient(135deg, rgba(255,107,61,0.10) 0%, rgba(255,107,61,0.04) 100%)",
      borderBottom:"1px solid rgba(255,107,61,0.12)",
      padding:"10px 16px",
      display:"flex", alignItems:"center", justifyContent:"center", gap:10,
      animation:"fadeUp 0.25s ease",
    }}>
      <TorchLogo size={14} />
      <span style={{
        fontSize:13, fontWeight:600, color:"rgba(255,255,255,0.8)",
        letterSpacing:"-0.01em",
      }}>
        New to Torch?
      </span>
      <button onClick={onOpenFAQ} style={{
        padding:"4px 12px", borderRadius:6,
        background:"rgba(255,107,61,0.15)", border:"1px solid rgba(255,107,61,0.25)",
        color:T.coral, fontSize:12, fontWeight:700, cursor:"pointer",
        transition:"all 0.15s", letterSpacing:"-0.01em",
      }}
      onMouseEnter={e => { e.currentTarget.style.background = "rgba(255,107,61,0.25)"; }}
      onMouseLeave={e => { e.currentTarget.style.background = "rgba(255,107,61,0.15)"; }}
      >
        Learn how it works
      </button>
      <button onClick={() => {
        setDismissed(true);
        try { localStorage.setItem("torch_faq_dismissed", "1"); } catch {}
      }} style={{
        background:"none", border:"none", cursor:"pointer",
        color:"rgba(255,255,255,0.42)", fontSize:14, padding:"2px 6px",
        marginLeft:4, lineHeight:1,
      }}
      title="Dismiss"
       aria-label="Close">✕</button>
    </div>
  );
}

const TopNav = memo(function TopNav({ activePage, setActivePage, pickCount, setSlipOpen, user, userStats, userLeagues = [], balance = 0, pendingPickCount = 0 }) {
  // Den is the leftmost / landing tab. Hub was deleted in cut #5 — avatar
  // tap routes directly to Profile, no dropdown.
  const pages = ["Den","Pulse","Games","Picks"];

  const displayName = userStats?.username || user?.displayName || user?.email?.split("@")[0] || "You";

  return (
    <>
    <header style={{
      position:"sticky", top:0, zIndex:50,
      background:"rgba(6,7,12,0.82)", backdropFilter:"blur(48px) saturate(200%)",
      WebkitBackdropFilter:"blur(48px) saturate(200%)",
      borderBottom:"1px solid rgba(255,255,255,0.06)",
      boxShadow:"0 1px 0 rgba(255,255,255,0.03) inset, 0 8px 32px rgba(0,0,0,0.4)",
      paddingTop:"env(safe-area-inset-top, 0px)",
    }}>
      {/* Main nav row */}
      <div style={{ height:56, display:"flex", alignItems:"center", padding:"0 16px", gap:0 }}>
        {/* Logo */}
        <button onClick={() => { haptic.light(); setActivePage("Pulse"); }} style={{
          background:"none", border:"none", cursor:"pointer",
          display:"flex", alignItems:"center", gap:8, marginRight:24, flexShrink:0, padding:0,
          minHeight:44,
        }}>
          <TorchLogo size={22} />
          <span style={{ fontFamily:FD, fontSize:17, fontWeight:700, letterSpacing:"-0.04em", color:"#fff", textShadow:"0 0 12px rgba(255,107,61,0.5), 0 0 30px rgba(255,107,61,0.2)" }}>torch</span>
        </button>

        {/* Desktop nav links */}
        <nav className="desktopNav" style={{ display:"flex", gap:2, flex:1 }}>
          {pages.map(p => (
            <button key={p} onClick={() => {
              haptic.light();
              if (p === "Den") { try { localStorage.removeItem("torch_active_league"); localStorage.removeItem("torch_den_intent"); } catch {} }
              setActivePage(p);
            }} style={{
              padding:"7px 16px", border:"none", cursor:"pointer",
              fontSize:13, fontWeight: activePage===p ? 600 : 400,
              color: activePage===p ? "#fff" : "rgba(255,255,255,0.35)",
              borderRadius:10, transition:"all 0.2s",
              background: activePage===p ? "rgba(255,255,255,0.06)" : "transparent",
              letterSpacing:"-0.01em", fontFamily:FB,
              position:"relative",
            }}>
              {p === "Den" ? "Leagues" : p === "Picks" ? "My Picks" : p}
              {activePage===p && <div style={{
                position:"absolute", bottom:-1, left:"50%", transform:"translateX(-50%)",
                width:16, height:2, borderRadius:1,
                background:`linear-gradient(90deg, ${T.coral}, ${T.coralDeep})`,
              }}/>}
            </button>
          ))}
        </nav>

        {/* Mobile-only spacer — pushes the pick counter and avatar to the right
            edge on small screens, where .desktopNav (which has flex:1) is hidden.
            Without this, the avatar hugs the logo and the profile dropdown
            (positioned with right:0) flows off the left side of the screen. */}
        <div className="mobileNavSpacer" style={{ flex: 1 }}/>

        {/* Pick counter */}
        {pickCount > 0 && (
          <button key={pickCount} onClick={() => { haptic.light(); setSlipOpen(true); }} aria-label="Open pick slip" style={{
            padding:"5px 14px", borderRadius:10,
            background:T.coral,
            border:"none", color:"#fff",
            fontSize:12, fontWeight:700, cursor:"pointer",
            boxShadow:`0 4px 16px rgba(255,107,61,0.3)`,
            marginRight:6, transition:"all 0.2s",
            animation:"countPop 0.3s ease",
          }}>
            {pickCount} pick{pickCount !== 1 ? "s" : ""} →
          </button>
        )}

        {/* User avatar — tap routes directly to Profile. The old dropdown
            (Hub/Profile/Help/Sign Out) was retired: Hub is gone, FAQ and
            Sign Out live on the Profile page itself. One tap, one destination. */}
        <button
          aria-label="Profile"
          onClick={() => { haptic.light(); setActivePage("Profile"); }}
          title={displayName}
          style={{
            width:38, height:38, borderRadius:12, flexShrink:0,
            background:`linear-gradient(135deg, #FF7B47 0%, ${T.coral} 50%, ${T.coralDeep} 100%)`,
            border:"none", cursor:"pointer",
            display:"flex", alignItems:"center", justifyContent:"center",
            fontFamily:FD, fontSize:13, fontWeight:800, color:"#fff",
            letterSpacing:"-0.5px",
            boxShadow:`0 1px 0 rgba(255,255,255,0.20) inset, 0 4px 14px -2px rgba(255,107,61,0.30)`,
            overflow:"hidden",
          }}
        >
          {user?.photoURL
            ? <img src={user.photoURL} alt="" style={{ width:"100%", height:"100%", objectFit:"cover", borderRadius:12 }}
                onError={(e) => { e.currentTarget.style.display = "none"; }}/>
            : (displayName?.[0] || "?").toUpperCase()}
        </button>
      </div>

      {/* Search bar removed — was wired to dead state with no filter logic.
          Reinstate as a proper feature later if cross-page search becomes a need. */}

    </header>

    </>
  );
});

/* ===========================================
   BOTTOM NAV (mobile)
=========================================== */
const BottomNav = memo(function BottomNav({ activePage, setActivePage, pickCount, setSlipOpen, onResetLeagues, hasLiveGames = false, wcOnly = false }) {
  // Refined nav: icons at I.lg (22px), single active signal (ember dot above icon),
  // no drop-shadow glow, no white pill background. Function signature and behavior
  // unchanged — purely visual refinement.
  //
  // wcOnly: when true, the user is ONLY in World Cup bracket-pool leagues.
  // In that mode they have no use for Games (no league to bet against) or
  // Picks (no game picks possible) — collapse the nav to Home + Leagues
  // so the UI matches their actual capabilities.
  const allItems = [
    { page:"Den", label:"Leagues", icon: (on) => (
      <svg width={I.lg} height={I.lg} viewBox="0 0 24 24" fill={on ? "currentColor" : "none"} stroke="currentColor" strokeWidth={on ? STROKE_ACTIVE : STROKE} strokeLinecap="round" strokeLinejoin="round">
        <circle cx="9" cy="8" r="3.2"/>
        <circle cx="17" cy="9.5" r="2.4"/>
        <path d="M3 20c0-3 2.7-5 6-5s6 2 6 5"/>
        <path d="M15 20c0-2 1.5-3.5 3-3.5s3 1.5 3 3.5"/>
      </svg>
    )},
    { page:"Pulse", label:"Pulse", icon: (on) => (
      // Stacked rounded lines suggesting a feed/stream — distinct from the
      // ticket-stub Picks icon and the old house glyph. Active state fills
      // the bars; inactive shows just the outlines.
      <svg width={I.lg} height={I.lg} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={on ? STROKE_ACTIVE : STROKE} strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="5" width="18" height="3.2" rx="1.4" fill={on ? "currentColor" : "none"}/>
        <rect x="3" y="10.4" width="13" height="3.2" rx="1.4" fill={on ? "currentColor" : "none"} opacity={on ? 0.75 : 1}/>
        <rect x="3" y="15.8" width="18" height="3.2" rx="1.4" fill={on ? "currentColor" : "none"} opacity={on ? 0.5 : 1}/>
      </svg>
    )},
    { page:"Games", label:"Games", icon: (on) => (
      <svg width={I.lg} height={I.lg} viewBox="0 0 24 24" fill={on ? "currentColor" : "none"} stroke="currentColor" strokeWidth={on ? STROKE_ACTIVE : STROKE} strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="9"/>
        <path d="M3 12h18"/>
        <path d="M12 3a13 13 0 0 0 0 18"/>
        <path d="M12 3a13 13 0 0 1 0 18"/>
      </svg>
    )},
    { page:"Picks", label:"Picks", icon: (on) => (
      <svg width={I.lg} height={I.lg} viewBox="0 0 24 24" fill={on ? "currentColor" : "none"} stroke="currentColor" strokeWidth={on ? STROKE_ACTIVE : STROKE} strokeLinecap="round" strokeLinejoin="round">
        <path d="M6 3h12v18l-3-2-3 2-3-2-3 2Z"/>
        <path d="M9 8h6"/>
        <path d="M9 12h6"/>
      </svg>
    )},
  ];
  const items = wcOnly
    ? allItems.filter(it => it.page !== "Games" && it.page !== "Picks")
    : allItems;

  return (
    <nav className="bottomNav" style={{
      position:"fixed", bottom:0, left:0, right:0, zIndex:40,
      background:"rgba(8,8,10,0.86)", backdropFilter:"blur(40px) saturate(180%)",
      WebkitBackdropFilter:"blur(40px) saturate(180%)",
      borderTop:`1px solid ${T.border}`,
      display:"flex", padding:"0 0 env(safe-area-inset-bottom,8px)",
      // Slide-down animation handled by global CSS rule on `body.modal-open .bottomNav`
      // so any modal that opts in via useModalOpen() automatically gets the nav out of the way.
      transition:"transform 0.22s cubic-bezier(0.32, 0.72, 0, 1), opacity 0.18s ease",
      willChange:"transform",
    }}>
      {items.map(({ page, icon, label }) => {
        const active = activePage === page;
        return (
          <button key={page} onClick={() => {
            haptic.light();
            // When tapping Leagues in bottom nav, always show league list (not a specific den)
            if (page === "Den") { try { localStorage.removeItem("torch_active_league"); localStorage.removeItem("torch_den_intent"); } catch {}; onResetLeagues?.(); }
            setActivePage(page);
          }} style={{
            flex:1, background:"none", border:"none", cursor:"pointer",
            display:"flex", flexDirection:"column", alignItems:"center", gap:4,
            padding:"10px 0 6px", position:"relative",
            // Color cascades to icon (currentColor) and label.
            color: active ? T.ink : T.inkMuted,
            transition:"color 0.18s ease",
          }}>
            {/* ── Active dot ──
                Single signal — 4px ember dot above the icon. Replaces the
                stacked white-pill + drop-shadow glow combo. Reads cleaner
                and matches iOS conventions. */}
            {active && (
              <span style={{
                position:"absolute", top:4, left:"50%", transform:"translateX(-50%)",
                width:4, height:4, borderRadius:"50%",
                background: T.ember,
                animation:"fadeIn 0.18s ease both",
                pointerEvents:"none",
              }}/>
            )}
            <div style={{
              display:"flex", alignItems:"center", justifyContent:"center",
              position:"relative",
              // No drop-shadow filter. Active state carried by fill swap + ember dot.
            }}>
              {icon(active)}
              {/* Live games dot — kept, but smaller and unobtrusive */}
              {page === "Games" && hasLiveGames && !active && (
                <span style={{
                  position:"absolute", top:-1, right:-3,
                  width:6, height:6, borderRadius:"50%",
                  background:"#4ade80",
                  animation:"pulse 1.4s ease infinite",
                }}/>
              )}
            </div>
            <span style={{
              fontSize:10, fontWeight: active ? 600 : 500,
              letterSpacing:"0.005em",
              transition:"font-weight 0.15s",
              fontFamily:FB,
              position:"relative",
            }}>{label}</span>
            {/* Pick-count badge — flat ember instead of gradient (gradient invisible at 18px) */}
            {page === "Games" && pickCount > 0 && (
              <span
                key={pickCount}
                onClick={e => { e.stopPropagation(); haptic.light(); setSlipOpen?.(true); }}
                style={{
                position:"absolute", top:4, right:"calc(50% - 24px)",
                background: T.ember,
                borderRadius:"50%", width:18, height:18,
                fontSize:9, fontWeight:700, color:"#fff",
                display:"flex", alignItems:"center", justifyContent:"center",
                cursor:"pointer", border:`2px solid ${T.bg}`,
                animation:"countPop 0.3s ease",
              }}>{pickCount}</span>
            )}
          </button>
        );
      })}
    </nav>
  );
});

/* ===========================================
   PICK BUTTON — press + glow interaction
=========================================== */
// PrizePicks-inspired individual rounded cells
// rowIndex: 0 = away (top row), 1 = home (bottom row)
const PickButton = memo(function PickButton({ active, topVal, btmVal, rowIndex, onClick, disabled = false, isLiveOdds = false, isML = false }) {
  const [pressed, setPressed] = useState(false);
  // Track inactive → active transition to fire the "just selected" beat. The ref
  // remembers the previous active value so we don't fire on initial mount of an
  // already-active button (which would happen if you re-open the Games tab with
  // existing slip picks). We only want the celebration on the actual user tap.
  const prevActiveRef = useRef(active);
  const [justSelected, setJustSelected] = useState(false);
  useEffect(() => {
    if (active && !prevActiveRef.current) {
      setJustSelected(true);
      const t = setTimeout(() => setJustSelected(false), 600);
      prevActiveRef.current = active;
      return () => clearTimeout(t);
    }
    prevActiveRef.current = active;
  }, [active]);

  const accentColor = active && isLiveOdds ? "#10B981" : T.coral;
  const accentDim = active && isLiveOdds ? "rgba(16,185,129," : "rgba(255,107,61,";

  return (
    <button
      onClick={(e) => { haptic.medium(); onClick?.(e); }}
      disabled={disabled}
      onTouchStart={() => !disabled && setPressed(true)}
      onTouchEnd={() => setPressed(false)}
      onMouseDown={() => !disabled && setPressed(true)}
      onMouseUp={() => setPressed(false)}
      onMouseLeave={() => setPressed(false)}
      className="no-press"
      style={{
        padding: isML ? "12px 6px 10px" : "10px 4px 9px",
        borderRadius:10,
        border: disabled ? "1px solid rgba(255,255,255,0.02)"
          : active ? `1.5px solid ${accentColor}`
          : isLiveOdds ? "1px solid rgba(16,185,129,0.12)"
          : "1px solid rgba(255,255,255,0.08)",
        background: disabled ? "rgba(255,255,255,0.01)"
          : active ? `${accentDim}0.14)`
          : isLiveOdds ? "rgba(16,185,129,0.04)"
          : pressed ? "rgba(255,255,255,0.08)" : "rgba(255,255,255,0.03)",
        cursor: disabled ? "default" : "pointer",
        textAlign:"center",
        opacity: disabled ? 0.15 : 1,
        transform: pressed ? "scale(0.93)" : "scale(1)",
        transition:"transform 0.12s ease, background 0.15s, border-color 0.15s",
        outline:"none", position:"relative", overflow:"hidden",
        width:"100%",
      }}>
      {/* Top glow line on active — single static accent at the top edge.
          The previous design also had a perpetual shimmer sweep across the
          button surface, which combined with multiple active picks created
          a busy "casino" feel. The static top line + moment-of-tap flash
          is enough; the active state reads clearly without continuous motion. */}
      {active && <div style={{ position:"absolute", top:0, left:"15%", right:"15%", height:1, background:`linear-gradient(90deg,transparent,${accentColor},transparent)`, opacity:0.6 }}/>}
      {/* "Just selected" beat — radial flash that emits from the button center
          and fades to nothing in 500ms. Plus a tiny corner checkmark that draws
          itself in. Together they add the missing MOMENT of selection without
          being theatrical. Only fires on inactive→active transition. */}
      {justSelected && (
        <>
          <div style={{
            position:"absolute", inset:0, borderRadius:10, pointerEvents:"none",
            background:`radial-gradient(circle at 50% 50%, ${accentDim}0.45) 0%, ${accentDim}0.0) 60%)`,
            animation:"oddsFlash 0.55s cubic-bezier(0.32,0.72,0,1) forwards",
          }}/>
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke={accentColor} strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" style={{
            position:"absolute", top:5, right:5, pointerEvents:"none",
            animation:"checkPop 0.42s cubic-bezier(0.32,0.72,0,1.32) both",
            filter:`drop-shadow(0 0 4px ${accentDim}0.5))`,
          }}>
            <polyline points="20 6 9 17 4 12" style={{ strokeDasharray:30, strokeDashoffset:30, animation:"checkDraw 0.32s 0.05s cubic-bezier(0.65,0,0.35,1) forwards" }}/>
          </svg>
        </>
      )}
      <div style={{
        fontSize: isML ? 16 : 13, fontWeight:700, lineHeight:1.2,
        letterSpacing:"-0.3px",
        fontFamily:FM,
        color: disabled ? "rgba(255,255,255,0.08)"
          : active ? accentColor
          : isML ? "#4ade80"
          : "rgba(255,255,255,0.92)",
        position:"relative",
      }}>{topVal}</div>
      {btmVal && (
        <div style={{
          fontSize:10, marginTop:3, fontWeight:700, letterSpacing:"0.02em",
          fontFamily:FM,
          color: disabled ? "rgba(255,255,255,0.04)"
            : active ? `${accentDim}0.70)`
            : "#4ade80",
          position:"relative",
        }}>{btmVal}</div>
      )}
    </button>
  );
});

/* ===========================================
   PAGE: HOME (Dashboard)
=========================================== */

// ── Education-module data — drives "Reading a line" on the Home page.
// Five bet types, each shown as a real-looking sportsbook line with a plain-
// English translation and the actual risk/win point math. Numbers chosen
// to be illustrative (not literal pricing) — they match standard American
// odds conventions so a user who DOES come from sportsbook land recognizes
// the math, and a new user learns the math by example.
const HOME_BET_TYPES = [
  {
    key: "ml",
    label: "ML",
    example: "Lakers ML",
    odds: "−150",
    meaning: "Pick who wins. That's it.",
    risk: "150",
    win: "100",
    note: "Favorites cost more, underdogs pay more. Bigger the upset, bigger the payout.",
  },
  {
    key: "spread",
    label: "SPREAD",
    example: "Chiefs −3.5",
    odds: "−110",
    meaning: "Chiefs win by 4 or more. The number's the cushion.",
    risk: "110",
    win: "100",
    note: "Always −110 unless noted. Half-points kill pushes — no ties.",
  },
  {
    key: "total",
    label: "TOTAL",
    example: "Over 224.5",
    odds: "−110",
    meaning: "Combined score lands above 224. Either team scoring counts.",
    risk: "110",
    win: "100",
    note: "The line moves with the market. Lock it early if you like the number.",
  },
  {
    key: "parlay",
    label: "PARLAY",
    example: "3-leg parlay",
    odds: "+625",
    meaning: "Stack picks together. All of them have to hit.",
    risk: "100",
    win: "625",
    note: "Odds multiply across legs. One loss kills the whole ticket. High variance.",
  },
  {
    key: "props",
    label: "PROPS",
    example: "LeBron Over 24.5 PTS",
    odds: "−115",
    meaning: "LeBron's points only — no one else on his team counts.",
    risk: "115",
    win: "100",
    note: "Available for points, rebounds, assists, threes, and more. Each market priced on its own.",
  },
];

/* ===========================================
   USER RANK SYSTEM — sports progression
   ============================================
   Five tiers gated by settled pick count and win rate:

     <5 settled                → Rookie    (gray, dotted ring)
     5+ settled, 0–25% wr      → Pro       (blue, solid dot)
     5+ settled, 25–50% wr     → Veteran   (sage green, shield)
     5+ settled, 50–75% wr     → All-Star  (ember, star)
     5+ settled, 75–100% wr    → Legend    (gold, flame)

   The 5-pick gate exists so a user who goes 1-for-1 doesn't flash Legend.
   It mirrors Tom's earlier insight about minimum-pick gates fixing the
   frontrunner metric problem.

   Icons are simple, distinct shapes drawn as inline SVG, NOT generic
   decorative emoji. The point is: ranks should feel earned, not given
   out. Flame ties to the Torch brand and to universal sports talk
   ("they're on fire").
=========================================== */
function getUserRank(stats) {
  const wins = stats?.wins ?? 0;
  const losses = stats?.losses ?? 0;
  const settled = wins + losses;

  // 5-pick gate. Until you've actually been tested, no rank.
  if (settled < 5) {
    return {
      id: "rookie",
      name: "Rookie",
      color: T.inkSec,
      bg: "rgba(244,244,246,0.05)",
      border: "rgba(244,244,246,0.12)",
      picksToRank: 5 - settled,
    };
  }

  const pct = (wins / settled) * 100;
  if (pct < 25) return { id:"pro",      name:"Pro",      color: T.blue,   bg: T.blueDim,    border: "rgba(91,160,229,0.34)" };
  if (pct < 50) return { id:"veteran",  name:"Veteran",  color: T.green,  bg: T.greenDim,   border: "rgba(77,166,114,0.34)" };
  if (pct < 75) return { id:"allstar",  name:"All-Star", color: T.ember,  bg: T.emberDim,   border: "rgba(255,107,61,0.34)" };
  return            { id:"legend",   name:"Legend",   color: T.gold,   bg: T.goldDim,    border: "rgba(217,164,65,0.40)" };
}

// SVG insignia. Each takes a color prop and a stroke prop so the badge
// Monochrome sport glyphs for the league-creation picker and pills. These are
// generic line icons, NOT league logos — real NFL/NBA/MLB marks are trademarked
// and would risk App Store rejection. The single-stroke style reads cleaner and
// more "data-forward" than the emoji it replaces.
function SportGlyph({ sport, color = "currentColor", size = 22 }) {
  const c = { width:size, height:size, viewBox:"0 0 24 24", fill:"none", stroke:color, strokeWidth:1.8, strokeLinecap:"round", strokeLinejoin:"round", style:{ display:"block" } };
  switch (sport) {
    case "americanfootball_nfl":
    case "americanfootball_ncaaf":
      return <svg {...c}><ellipse cx="12" cy="12" rx="9" ry="5.5" transform="rotate(-30 12 12)"/><line x1="8.5" y1="14.5" x2="15.5" y2="9.5"/><line x1="10" y1="11.5" x2="11" y2="12.5"/><line x1="12" y1="10" x2="13" y2="11"/></svg>;
    case "basketball_nba":
    case "basketball_ncaab":
    case "basketball_wnba":
      return <svg {...c}><circle cx="12" cy="12" r="9"/><path d="M3 12h18M12 3v18"/><path d="M5.5 5.5c4 2.5 4 10.5 0 13M18.5 5.5c-4 2.5-4 10.5 0 13"/></svg>;
    case "baseball_mlb":
      return <svg {...c}><circle cx="12" cy="12" r="9"/><path d="M7 5.5c2 3 2 10 0 13M17 5.5c-2 3-2 10 0 13"/></svg>;
    case "icehockey_nhl":
      return <svg {...c}><ellipse cx="12" cy="16" rx="7" ry="2.5"/><path d="M5 16v-2M19 16v-2"/><line x1="6" y1="4" x2="9" y2="12"/></svg>;
    case "soccer":
    case "soccer_epl":
      return <svg {...c}><circle cx="12" cy="12" r="9"/><polygon points="12,8 15,10.2 13.8,13.8 10.2,13.8 9,10.2"/><path d="M12 3v2M4.5 9l1.8 1.3M19.5 9l-1.8 1.3M7 19l1.2-2M17 19l-1.2-2"/></svg>;
    case "golf":
      return <svg {...c}><line x1="9" y1="3" x2="9" y2="18"/><path d="M9 4l7 2.5L9 9"/><ellipse cx="12" cy="20" rx="6" ry="1.5"/></svg>;
    case "tennis":
      return <svg {...c}><circle cx="12" cy="12" r="9"/><path d="M5 5c4 3 4 11 0 14M19 5c-4 3-4 11 0 14"/></svg>;
    case "mma_mixed_martial_arts":
      return <svg {...c}><rect x="5" y="9" width="11" height="9" rx="3"/><path d="M16 11h2a2 2 0 0 1 0 4h-2"/><path d="M7 9V7.5a1.5 1.5 0 0 1 3 0V9M10 9V6.5a1.5 1.5 0 0 1 3 0V9"/></svg>;
    case "all":
      return <svg {...c}><circle cx="12" cy="12" r="9"/><ellipse cx="12" cy="12" rx="4" ry="9"/><path d="M3 12h18M4.5 7.5h15M4.5 16.5h15"/></svg>;
    default:
      return <svg {...c}><circle cx="12" cy="12" r="9"/></svg>;
  }
}

function RankInsignia({ rankId, color, size = 12 }) {
  const stroke = 1.8;
  const common = { width:size, height:size, viewBox:"0 0 24 24", fill:"none", stroke:color, strokeWidth:stroke, strokeLinecap:"round", strokeLinejoin:"round", style:{ display:"block" } };
  if (rankId === "rookie") {
    // Dotted ring — placeholder state, not yet a rank
    return (
      <svg {...common}>
        <circle cx="12" cy="12" r="7" strokeDasharray="2 3" opacity="0.7"/>
      </svg>
    );
  }
  if (rankId === "pro") {
    // Solid filled dot — you've earned your roster spot
    return (
      <svg {...common}>
        <circle cx="12" cy="12" r="5" fill={color} stroke="none"/>
      </svg>
    );
  }
  if (rankId === "veteran") {
    // Shield — proven, defends their record
    return (
      <svg {...common}>
        <path d="M12 3 L19 6 V12 C19 16 16 19 12 21 C8 19 5 16 5 12 V6 Z" fill={color} fillOpacity="0.18" stroke={color} strokeWidth={stroke}/>
      </svg>
    );
  }
  if (rankId === "allstar") {
    // Star — the literal All-Star icon
    return (
      <svg {...common}>
        <path d="M12 3l2.6 5.6 6.1.6-4.6 4.1 1.4 6L12 16.3 6.5 19.3l1.4-6L3.3 9.2l6.1-.6L12 3z" fill={color} stroke={color} strokeWidth="0.6" strokeLinejoin="round"/>
      </svg>
    );
  }
  if (rankId === "legend") {
    // Flame — Torch brand tie-in, "on fire"
    return (
      <svg {...common}>
        <path d="M12 2.5 C12 6 15.5 7.5 15.5 11 C15.5 12.5 15 13.5 14 14 C14.5 12.5 14 11 13 10.5 C13.5 12.5 12.5 13.5 12 14 C12 11.5 9.5 11 9.5 8 C9.5 6 11 4.5 12 2.5 Z M7 14 C7 17.5 9 21 12 21 C15 21 17 17.5 17 14 C17 16 16 17 14.5 17 C15.5 15.5 14.5 13.5 13 13 C13.5 14.5 12.5 15.5 12 16 C12 14 9.5 13.5 9.5 11 C8 12 7 13 7 14 Z" fill={color} stroke={color} strokeWidth="0.5" strokeLinejoin="round"/>
      </svg>
    );
  }
  return null;
}

// Badge pill — small chevron/bar/star icon + rank name in a colored
// pill. Size variants: sm (compact, used inline), md (default), lg
// (prominent, used in Hub welcome card). When `onTap` is supplied,
// renders as a button so users can drill into the rank explainer.
function UserRank({ stats, size = "sm", showName = true, style = {}, onTap = null }) {
  const rank = getUserRank(stats);
  const dims = size === "lg"
    ? { padV:6, padH:10, gap:6, font:11, icon:14, letter:"0.08em" }
    : size === "md"
    ? { padV:4, padH:8,  gap:5, font:10, icon:12, letter:"0.08em" }
    : { padV:3, padH:7,  gap:4, font:9,  icon:10, letter:"0.10em" };
  const Component = onTap ? "button" : "span";
  const componentProps = onTap ? {
    onClick: (e) => { e.stopPropagation?.(); haptic.light(); onTap(); },
    "aria-label": `Your rank: ${rank.name}. Tap to learn about ranks.`,
  } : {};
  return (
    <Component {...componentProps} style={{
      display:"inline-flex", alignItems:"center", gap:dims.gap,
      padding:`${dims.padV}px ${dims.padH}px`, borderRadius:999,
      background: rank.bg, border:`1px solid ${rank.border}`,
      fontFamily:FB, fontSize:dims.font, fontWeight:700, color: rank.color,
      letterSpacing: dims.letter, textTransform:"uppercase",
      lineHeight:1, whiteSpace:"nowrap",
      ...(onTap ? { cursor:"pointer" } : {}),
      ...style,
    }}>
      <RankInsignia rankId={rank.id} color={rank.color} size={dims.icon} />
      {showName && rank.name}
    </Component>
  );
}

/* ===========================================
   RANK INFO MODAL — explainer sheet
   ============================================
   Bottom-sheet modal that lays out the 5 ranks, their thresholds, and
   the user's current standing. Opens when any UserRank pill with onTap
   is tapped. Keeping the copy tight so it doesn't read like a help-doc
   page — each row is one line, no marketing fluff. The 5-pick gate is
   surfaced as part of the Rookie row, not a footnote, because that's
   where the confusion lives ("why am I still Rookie at 4-0?").
=========================================== */
// RankInfoModal deleted in cut #10 — its only trigger (Pulse stats strip) was removed in cut #1.

/* ===========================================
   FEED PICK CARD — single pick on the Pulse firehose
   ============================================
   Renders one publicPicks doc as a card. Caller wires onProfile (tap
   username → open profile), onComment (open comments sheet), and
   onHopOn (open hop drawer). Hop button gates itself based on:

     - Pick belongs to the viewer → hide (can't hop on yourself)
     - Pick is settled → hide
     - Game has started → hide
     - Viewer has no league for this sport → gray out + tooltip

   Settled picks show their W/L pill with points earned/lost. Live picks
   show "LIVE" indicator when the game is in progress.
=========================================== */
// Robust parlay leg count. Submit paths populate different fields (client →
// parlayLegs[], server → legs[]+legCount), and a feed item may carry only
// gameLabel (a " · "-joined matchup string). Count from the best source
// available so a multi-leg parlay never renders as "0-leg".
// Human-readable headline for a stored pick.
//
// pick.label for a moneyline is the PRICE, not the pick — the board's pickOption
// label is built as fmtM(homeML) → "-625", which works as a button face under a
// "Winner" column but becomes the title on every downstream surface (My Picks,
// league feed, share sheet). A card headlined "-625" doesn't say who was picked.
//
// Picks submitted after the pick-label fix store "Kansas City Chiefs ML" and
// need no help. Picks submitted before it have a bare price in Firestore
// forever, so this reconstructs the team from `side` + the stored team names.
// Detects rather than assumes, so it's safe to call on either era.
function pickHeadline(pick) {
  const label = pick?.label || "";
  if (!pick || pick.type !== "ml") return label;
  const team = pick.side === "home" ? pick.homeTeamFull
             : pick.side === "away" ? pick.awayTeamFull
             : null;
  if (!team) return label;
  if (label.toLowerCase().includes(team.toLowerCase())) return label; // already named
  const short = team.split(" ").pop();
  if (short && label.toLowerCase().includes(short.toLowerCase())) return label;
  return `${team} ML`;
}

function parlayLegCountOf(pick) {
  if (!pick) return 0;
  const fromCount = Number(pick.legCount) || 0;
  if (fromCount > 0) return fromCount;
  const fromArr = (pick.parlayLegs?.length || pick.legs?.length || 0);
  if (fromArr > 0) return fromArr;
  if (typeof pick.gameLabel === "string" && pick.gameLabel.includes(" · ")) {
    return pick.gameLabel.split(" · ").filter(Boolean).length;
  }
  return 0;
}

function FeedPickCard({ pick, viewer, viewerLeagues = [], onHopOn, onComment, onProfile }) {
  // ── Time formatting ──
  // submittedAt is a Firestore Timestamp (or null during the brief window
  // between client-side write and server confirmation). Format defensively.
  const submittedMs = tsToMillis(pick.submittedAt);
  const ago = submittedMs ? timeAgo(submittedMs) : "just now";

  // ── Hop eligibility ──
  const isOwn = viewer?.uid === pick.uid;
  const isSettled = pick.status === "won" || pick.status === "lost" || pick.status === "push";
  // tsToDate handles Firestore Timestamp objects. Picks written by the
  // server (via callable hop, etc.) have Timestamp commenceTime; picks
  // written directly by the client are ISO strings. `new Date()` only
  // works for the latter -- using it on a Timestamp returns Invalid Date
  // (NaN ms), making gameStarted always false and showing the "Hop on"
  // button on picks whose games have already started.
  const gameDate = tsToDate(pick.commenceTime);
  const gameMs = gameDate ? gameDate.getTime() : null;
  const gameStarted = gameMs ? gameMs <= Date.now() : false;
  const canHopByState = !isOwn && !isSettled && !gameStarted;

  // Sport-match check — does the viewer have at least one active, non-WC
  // league that accepts this pick's sport? Empty sport filter on a league
  // means "all sports", which always matches. WC bracket pools never
  // accept regular game picks.
  const sportMatch = (() => {
    if (!viewerLeagues || viewerLeagues.length === 0) return false;
    const pickSport = pick.sportKey || pick.sport || "";
    for (const l of viewerLeagues) {
      if (l.isEnded || l.status === "ended") continue;
      if (isWorldCup2026League?.(l)) continue;
      const lSport = l.sportFilter || l.eventSport;
      if (!lSport) return true; // all-sports league
      if (!pickSport) return true; // pick has no sport — let it through
      if (lSport === pickSport || lSport.includes(pickSport) || pickSport.includes(lSport)) return true;
    }
    return false;
  })();
  const hopEnabled = canHopByState && sportMatch;
  const hopReason = !canHopByState
    ? (isOwn ? "your pick" : isSettled ? "settled" : gameStarted ? "game started" : "")
    : !sportMatch ? `no ${pick.sport || "sport"} league` : "";

  // ── Status pill style ──
  // WON: "+475" = net profit (pick.points is winnings minus stake).
  // LOST: "-50" = loss = full wager. pick.points is 0 for a loss because
  // points-scored is the win column, so we'd be showing "LOST 0" which
  // tells the viewer nothing. Showing -wager mirrors the WON pill format
  // and communicates magnitude.
  // PUSH: no number -- it's a wash, refund only.
  const statusPill = (() => {
    if (pick.status === "won") {
      return { label: `WON +${(pick.points ?? 0).toLocaleString()}`, color: T.green, bg: T.greenDim, border:"rgba(77,166,114,0.34)" };
    }
    if (pick.status === "lost") {
      return { label: `LOST -${(pick.wager ?? 0).toLocaleString()}`, color: T.red, bg: T.redDim, border:"rgba(199,101,97,0.34)" };
    }
    if (pick.status === "push") {
      return { label: "PUSH", color: T.inkSec, bg:"rgba(255,255,255,0.04)", border: T.border };
    }
    return null; // pending — no pill
  })();

  // ── Pick line display ──
  // Show the bet label + odds inline. For parlays, show "X-leg Parlay" + total odds.
  // For totals, expand "U 6.5"/"O 220.5" → "Under 6.5"/"Over 220.5" — the bare
  // letter prefix is opaque to anyone not fluent in sportsbook shorthand, which
  // is exactly the audience Torch is built for.
  const oddsStr = fmtOdds(pick.odds);
  const parlayLegCount = parlayLegCountOf(pick);
  const primaryLine = pick.isParlay
    ? (parlayLegCount > 0 ? `${parlayLegCount}-Leg Parlay` : "Parlay")
    : humanizePickLabel(pick);

  return (
    <div style={{
      borderRadius:16, padding:"14px 14px 12px", marginBottom:10,
      background: T.card, border:`1px solid ${T.border}`,
      transition:"border-color 0.18s",
    }}>
      {/* Header row — picker identity + timestamp */}
      <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:12 }}>
        <button
          onClick={() => { haptic.light(); onProfile?.(pick.uid, pick.username); }}
          className="no-press"
          style={{
            width:32, height:32, borderRadius:10, flexShrink:0,
            background:"rgba(255,107,61,0.12)",
            border:"1px solid rgba(255,107,61,0.22)", cursor:"pointer",
            display:"flex", alignItems:"center", justifyContent:"center",
            fontFamily:FD, fontSize:13, fontWeight:700, color:"#FF8A65",
            letterSpacing:"-0.02em",
          }}
          aria-label={`Open ${pick.username}'s profile`}
        >
          {(pick.username?.[0] || "T").toUpperCase()}
        </button>
        <div style={{ minWidth:0, flex:1 }}>
          <div style={{ display:"flex", alignItems:"center", gap:7, marginBottom:1 }}>
            <button
              onClick={() => { haptic.light(); onProfile?.(pick.uid, pick.username); }}
              className="no-press"
              style={{
                background:"none", border:"none", padding:0, cursor:"pointer",
                fontFamily:FB, fontSize:13.5, fontWeight:700, color:T.ink,
                letterSpacing:"-0.005em", maxWidth:160,
                overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap",
                textAlign:"left",
              }}
            >
              {pick.username || "Anonymous"}
            </button>
            {/* Rank pill from submit-time snapshot */}
            <UserRank stats={{ wins: pick.submitterWins ?? 0, losses: pick.submitterLosses ?? 0 }} size="sm" />
          </div>
          <div style={{ fontSize:10.5, color:T.inkMuted, fontFamily:FM, letterSpacing:"0.02em" }}>
            {ago}{pick.leagueName ? ` · ${pick.leagueName}` : pick.leagueId ? "" : " · Practice"}
          </div>
        </div>
        {statusPill && (
          <span style={{
            flexShrink:0,
            display:"inline-flex", alignItems:"center",
            padding:"4px 9px", borderRadius:999,
            background: statusPill.bg, border:`1px solid ${statusPill.border}`,
            fontFamily:FM, fontSize:10, fontWeight:700, color: statusPill.color,
            letterSpacing:"0.06em",
          }}>
            {statusPill.label}
          </span>
        )}
      </div>

      {/* Primary pick line + odds */}
      <div style={{ display:"flex", alignItems:"baseline", justifyContent:"space-between", gap:10, marginBottom:6 }}>
        <div style={{ fontFamily:FD, fontSize:17, fontWeight:700, color:T.ink, letterSpacing:"-0.01em", minWidth:0, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
          {primaryLine}
        </div>
        {oddsStr && (
          <span style={{
            flexShrink:0,
            fontFamily:FM, fontSize:13, fontWeight:700,
            color: pick.odds > 0 ? T.green : T.ink,
            fontVariantNumeric:"tabular-nums",
          }}>
            {oddsStr}
          </span>
        )}
      </div>

      {/* Meta row: type · sport · game */}
      <div style={{ fontSize:11.5, color:T.inkMuted, lineHeight:1.45, marginBottom:12, fontFamily:FB }}>
        <span style={{ textTransform:"uppercase", letterSpacing:"0.08em", fontWeight:700, color:T.inkSec }}>
          {pick.type || (pick.isParlay ? "Parlay" : "Pick")}
        </span>
        {pick.sport && <> · {pick.sport}</>}
        {pick.gameLabel && <> · {pick.gameLabel}</>}
      </div>

      {/* Wager block */}
      <div style={{
        display:"flex", justifyContent:"space-between", alignItems:"center",
        padding:"9px 12px", borderRadius:10,
        background:"rgba(255,255,255,0.025)", border:`1px solid ${T.border}`,
        marginBottom:12,
      }}>
        <div>
          <div style={{ fontSize:9, fontWeight:700, color:T.inkMuted, letterSpacing:"0.12em", textTransform:"uppercase", marginBottom:2 }}>Wagered</div>
          <div style={{ fontFamily:FM, fontSize:13, fontWeight:700, color:T.ink, fontVariantNumeric:"tabular-nums" }}>
            {(pick.wager ?? 0).toLocaleString()} pts
          </div>
        </div>
        <div style={{ textAlign:"right" }}>
          <div style={{ fontSize:9, fontWeight:700, color:T.inkMuted, letterSpacing:"0.12em", textTransform:"uppercase", marginBottom:2 }}>
            {!isSettled            ? "Could return"
            : pick.status === "won"  ? "Returned"
            : pick.status === "lost" ? "Lost"
            : pick.status === "push" ? "Refunded"
            :                          "Returned"}
          </div>
          <div style={{
            fontFamily:FM, fontSize:13, fontWeight:700,
            color: isSettled
              ? (pick.status === "won" ? T.green : pick.status === "lost" ? T.red : T.ink)
              : T.green,
            fontVariantNumeric:"tabular-nums",
          }}>
            {isSettled
              ? (pick.status === "won"
                  ? `${((pick.wager ?? 0) + (pick.points ?? 0)).toLocaleString()} pts`
                  : pick.status === "push"
                    ? `${(pick.wager ?? 0).toLocaleString()} pts`
                    : `-${(pick.wager ?? 0).toLocaleString()} pts`)
              : `${(pick.expectedPayout ?? 0).toLocaleString()} pts`}
          </div>
        </div>
      </div>

      {/* Action row — comments count + hop count + hop button */}
      <div style={{ display:"flex", alignItems:"center", gap:14, paddingTop:8, borderTop:`1px solid ${T.border}` }}>
        <button
          onClick={() => { haptic.light(); onComment?.(pick); }}
          className="no-press"
          style={{
            background:"none", border:"none", padding:0, cursor:"pointer",
            display:"inline-flex", alignItems:"center", gap:6,
            color: T.inkSec, fontFamily:FB, fontSize:12, fontWeight:600,
          }}
          aria-label="Comments"
        >
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
          </svg>
          {pick.commentCount ?? 0}
        </button>
        <div style={{
          display:"inline-flex", alignItems:"center", gap:6,
          color: T.inkSec, fontFamily:FB, fontSize:12, fontWeight:600,
        }}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
            <path d="M22 12h-4l-3 9L9 3l-3 9H2"/>
          </svg>
          {pick.hopCount ?? 0} hopped
        </div>
        {canHopByState && (
          <button
            onClick={() => { if (!hopEnabled) return; haptic.light(); onHopOn?.(pick); }}
            disabled={!hopEnabled}
            title={hopEnabled ? "Hop on this pick" : hopReason}
            className="no-press"
            style={{
              marginLeft:"auto", padding:"7px 14px", borderRadius:9,
              border: hopEnabled ? "1px solid transparent" : `1px solid ${T.border}`,
              background: hopEnabled ? T.ember : "rgba(255,255,255,0.04)",
              color: hopEnabled ? "#fff" : T.inkMuted,
              fontFamily:FB, fontSize:12, fontWeight:700, letterSpacing:"-0.005em",
              cursor: hopEnabled ? "pointer" : "not-allowed",
              display:"inline-flex", alignItems:"center", gap:6,
              transition:"transform 0.12s",
            }}
            onMouseDown={(e)=>{ if (hopEnabled) e.currentTarget.style.transform="scale(0.97)"; }}
            onMouseUp={(e)=>(e.currentTarget.style.transform="scale(1)")}
            onMouseLeave={(e)=>(e.currentTarget.style.transform="scale(1)")}
          >
            {hopEnabled
              ? <>Hop on <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg></>
              : hopReason || "Locked"}
          </button>
        )}
      </div>
    </div>
  );
}

/* ===========================================
   PULSE PAGE — the new social landing tab
   ============================================
   Phase 1 scaffolding. Replaces "Home" as the bottom-nav landing.
   The old home content lives at Hub (accessed via avatar dropdown).

   What's in Phase 1:
   - Compressed stats strip (balance · win rate · W-L)
   - World Cup 2026 banner pinned at top until tournament window closes
   - Top 3 leaderboard — placeholder, real data in Phase 4
   - Firehose feed — placeholder, real data in Phase 3

   What's coming next:
   - Phase 2: military rank badges on user identities everywhere
   - Phase 3: paginated firehose feed query of all public picks
   - Phase 4: real leaderboard query (top 3 by win rate w/ 5-pick gate)
   - Phase 5: hop-on action
   - Phase 6: comments + follow

   The placeholder copy here is intentionally dry, slightly self-aware,
   and written like a real human typed it. No "Unleash your potential"
   garbage, no emoji vomit. If you change copy in this file, match that
   voice or the whole page rots.
=========================================== */
/* ============== HopDrawer (Phase 3b) ==================================
   Modal bottom sheet for hopping on someone else's pick. Hopper picks
   their own wager (their balance, their call) and chooses which league
   to play it in. Sport-mismatched leagues are grayed out, not hidden —
   you can still see "Pickle Pool only accepts NFL" while looking at
   an NHL pick, makes it obvious why it's disabled.

   On submit:
   1. submitPicksSecure() — writes the new pick to /picks/{uid}/userPicks/,
      deducts league balance server-side, returns the new pickId
   2. mirrorPickToPulse() — creates the /publicPicks/{newId} mirror so the
      hopper's copy shows up in the feed
   3. increment hopCount on the original /publicPicks/{originalPickId}
      so the badge bumps for everyone watching the feed in real-time
   4. (optional later) notify the original picker that someone hopped

   Steps 2 + 3 are fire-and-forget — failures log but don't block the
   submission UX. The pick going through is what matters; the social
   sugar is cosmetic.
========================================================================*/
function HopDrawer({ originalPick, viewer, viewerStats, viewerLeagues, onClose, onHopped }) {
  // Hide the bottom tab bar while this drawer is open. Without this, the nav
  // (position:fixed, bottom:0, willChange:"transform") creates its own GPU
  // composite layer that, on iOS Safari, renders ABOVE the modal regardless
  // of z-index -- clipping the "Hop on for X pts" footer button. Every other
  // bottom sheet in the app (PickSlipDrawer, CommentsDrawer, GameDetailModal,
  // Torch loan modal) already calls this; HopDrawer was the one outlier.
  const { closing, requestClose } = useSheetClose(onClose, 200);
  useModalOpen(!closing);

  // Filter to active, non-WC leagues only. Hop into WC bracket pools is
  // meaningless (different scoring system, locked by kickoff). All-sports
  // leagues match every pick. Sport mismatch grays out the row.
  const pickSport = originalPick.sportKey || originalPick.sport || "";
  const eligibleLeagues = (viewerLeagues || [])
    // Non-wager leagues (pickem/survivor) are excluded: a hop is a WAGER on
    // someone else's pick, and those leagues have no ledger -- every member's
    // balance is 0, so any positive wager trips overBudget and canSubmit can
    // never light up. Showing the league is a dead end (the codebase's named
    // antipattern: offering what can't be accepted), and the server would
    // reject the pick anyway (non-wager guard in submitPickCallable).
    .filter(l => !l.isEnded && l.status !== "ended" && !isWorldCup2026League?.(l)
      && (l.scoringMode || "wager") === "wager")
    .map(l => {
      // HOP-SPORT-PRECEDENCE: allowedSports is AUTHORITATIVE when present —
      // same rule as the slip's compatibleLeagues and the CF submit guard
      // (Aug 28, 2026 fix). This modal was the one door still ANDing on the
      // legacy sportFilter alone, so a template league with sportFilter NFL +
      // allowedSports [nfl, mlb] greyed out MLB hops, and a wizard league with
      // allowedSports only (no sportFilter) let a wrong-sport hop through to a
      // CF rejection.
      const keys = (Array.isArray(l.allowedSports) && l.allowedSports.length > 0)
        ? l.allowedSports
        : (l.sportFilter || l.eventSport) ? [l.sportFilter || l.eventSport] : [];
      const ps = String(pickSport || "").toLowerCase();
      const matches = keys.length === 0 || !ps
        || keys.some(k => { const kl = String(k).toLowerCase(); return ps === kl || ps.includes(kl) || kl.includes(ps); });
      return { ...l, sportMatches: matches };
    });

  // Default selection: first sport-matching league. If none match, no
  // default — user has to pick "Practice" (just for fun) or close.
  const firstMatch = eligibleLeagues.find(l => l.sportMatches);
  const [selectedLeagueId, setSelectedLeagueId] = useState(firstMatch?.id || "__fun__");

  // Per-league wager bounds. The PickSlipDrawer enforces these for organic
  // picks; hops have to honor the same league rules or a hopper could bypass
  // a 200pt minimum by going through Pulse → Hop on with 100. League rules
  // apply to ALL pick entry surfaces.
  const selectedLeagueForBounds = eligibleLeagues.find(l => l.id === selectedLeagueId);
  const leagueMinWager = selectedLeagueId === "__fun__"
    ? 0
    : Math.max(0, selectedLeagueForBounds?.pickRequirements?.minWager || 0);
  const leagueMaxWager = selectedLeagueId === "__fun__"
    ? 0
    : Math.max(0, selectedLeagueForBounds?.pickRequirements?.maxWager || 0); // 0 = no cap

  // Seed wager at the selected league's min (or 100 for practice / unset).
  // Initial mount uses firstMatch's min so the input pre-fills correctly.
  // Clamp the seed by the user's available balance: if a 200pt-min league is
  // selected but the hopper only has 195pts left, seed at 195 so the all-in
  // wager pre-fills instead of an immediately-invalid value.
  const initialBalance = firstMatch && firstMatch.id !== "__fun__"
    ? Math.max(0, firstMatch?.balance ?? 0)
    : 0;
  const initialMin = firstMatch && firstMatch.id !== "__fun__"
    ? Math.max(0, firstMatch?.pickRequirements?.minWager || 0)
    : 0;
  const initialSeed = initialMin > 0
    ? (initialBalance > 0 && initialBalance < initialMin ? initialBalance : initialMin)
    : 100;
  const [wagerStr, setWagerStr] = useState(String(initialSeed));
  const [submitting, setSubmitting] = useState(false);

  // Re-seed wager when the user switches leagues. Always overwrite so the
  // input reflects the new league's rule — otherwise a hopper who lowered
  // the wager on a no-min league, then switches to a 200-min league, would
  // still see the lower value (and hit a submit-time block instead of
  // understanding the rule up front). Clamp by the new league's balance so
  // an underfunded hopper sees an all-in seed instead of an over-balance one.
  useEffect(() => {
    if (selectedLeagueId === "__fun__") return;
    const bal = Math.max(0, selectedLeagueForBounds?.balance ?? 0);
    if (leagueMinWager > 0) {
      const seed = bal > 0 && bal < leagueMinWager ? bal : leagueMinWager;
      setWagerStr(String(seed));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLeagueId]);

  const wager = Math.max(0, Math.floor(Number(wagerStr) || 0));
  const odds = originalPick.odds ?? null;
  const payout = wager > 0 && odds != null ? calcPayout(wager, odds) : 0;
  const profit = payout - wager;

  // Balance gate. For "__fun__" we check the global fun balance; for
  // a real league, the league's per-member balance lives on the league
  // object as `.balance`.
  const selectedLeague = eligibleLeagues.find(l => l.id === selectedLeagueId);
  const availableBalance = selectedLeagueId === "__fun__"
    ? (viewerStats?.balance ?? 0)
    : (selectedLeague?.balance ?? 0);
  const overBudget = wager > availableBalance;

  // League pick-requirements gates. These mirror what PickSlipDrawer enforces
  // on organic picks. underMin fires when the league has a minWager rule and
  // the wager falls short; overMax fires when the league has a maxWager cap.
  // All-in exception: if the hopper's full available balance is below the
  // league's min wager, allow a single wager equal to that balance — otherwise
  // they'd be locked out of hopping forever in a league they've drawn down on.
  const allInException = leagueMinWager > 0
    && availableBalance > 0
    && availableBalance < leagueMinWager
    && wager === availableBalance;
  const underMin = leagueMinWager > 0 && wager < leagueMinWager && !allInException;
  const overMax  = leagueMaxWager > 0 && wager > leagueMaxWager;
  const wagerRuleError = underMin
    ? (availableBalance > 0 && availableBalance < leagueMinWager
        ? `League min is ${leagueMinWager.toLocaleString()} pts — tap "All in" to wager your ${availableBalance.toLocaleString()}.`
        : `League min wager is ${leagueMinWager.toLocaleString()} pts`)
    : overMax
    ? `League max wager is ${leagueMaxWager.toLocaleString()} pts`
    : "";

  const canSubmit = wager > 0 && !overBudget && !underMin && !overMax && !submitting
    && (selectedLeagueId === "__fun__" || selectedLeague?.sportMatches);

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const leagueId = selectedLeagueId === "__fun__" ? null : selectedLeagueId;
      // Build the hopper's pick from the original's pick details. Strip
      // mirror-only fields; keep everything needed to settle as a regular
      // pick. wager + expectedPayout get overridden with hopper's choice.
      const hopperPick = {
        gameId:         originalPick.gameId || null,
        gameLabel:      originalPick.gameLabel || "",
        type:           originalPick.type || "",
        side:           originalPick.side || "",
        line:           originalPick.line ?? null,
        label:          originalPick.label || "",
        sport:          originalPick.sport || "",
        sportKey:       originalPick.sportKey || "",
        commenceTime:   originalPick.commenceTime || null,
        homeTeamFull:   originalPick.homeTeamFull || "",
        awayTeamFull:   originalPick.awayTeamFull || "",
        leagueId,
        wager,
        odds,
        expectedPayout: payout,
        // PICK-IDENTITY (fix 1.1, 2026-09-28)
        ...(originalPick.playerName ? { playerName: originalPick.playerName } : {}),
        ...(originalPick.statType   ? { statType:   originalPick.statType }   : {}),
        ...(originalPick.teamSide   ? { teamSide:   originalPick.teamSide }   : {}),
        ...(originalPick.isParlay ? {
          isParlay: true,
          legs: originalPick.parlayLegs || originalPick.legs || [],
          legCount: originalPick.parlayLegs?.length || originalPick.legs?.length || 0,
          combinedOdds: odds,
        } : {}),
        // Provenance — mark this pick as a hop so settlement + future
        // analytics can tell organic picks from hops without joining
        hoppedFrom: originalPick.pickId,
        hoppedFromUid: originalPick.uid,
      };

      const submitResult = await submitPicksSecure(viewer.uid, [hopperPick], wager, leagueId);
      const newPickId = submitResult?.pickIds?.[0];

      // Mirror the hopper's pick to Pulse so it shows up in the feed
      if (newPickId) {
        const uname = viewerStats?.username || viewer.displayName || viewer.email?.split("@")[0] || "Someone";
        const leagueInfo = selectedLeague ? { id: selectedLeague.id, name: selectedLeague.name, isPrivate: !(selectedLeague.isPublic ?? selectedLeague.public), /* LEAGUE-ISPUBLIC: field is isPublic; .public never existed */ scoringMode: selectedLeague.scoringMode || "wager", pickPrivacy: selectedLeague.pickPrivacy || null } : null;
        mirrorPickToPulse({ ...hopperPick, pickId: newPickId }, viewer, uname, leagueInfo, viewerStats).catch(() => {});
      }

      // Bump hopCount on the original's mirror doc. Real-time onSnapshot
      // means every viewer sees the badge tick up immediately.
      try {
        const { getFirestore, doc, updateDoc, increment } = await import("firebase/firestore");
        const db = getFirestore(app);
        await updateDoc(doc(db, "publicPicks", originalPick.pickId), { hopCount: increment(1) });
      } catch (e) {
        console.warn("[Torch] hopCount increment failed (non-blocking):", e.message);
      }

      window.torchToast?.(`Hopped on ${originalPick.username}'s pick.`, "success");
      haptic.success?.();
      onHopped?.(newPickId);
      requestClose();
    } catch (err) {
      const code = err?.code || "";
      if (code === "functions/resource-exhausted") {
        window.torchToast?.("Too many picks too fast. Slow down.", "error");
      } else if (code === "functions/failed-precondition") {
        window.torchToast?.(err.message || "Not enough balance.", "error");
      } else if (code === "functions/invalid-argument") {
        // SUBMIT-NO-FALLBACK-ON-REJECTION: say why (sport/bet type/odds/time).
        window.torchToast?.(err.message || "Couldn't hop on that pick.", "error");
      } else {
        window.torchToast?.("Couldn't hop on that pick. Try again.", "error");
      }
      console.error("[Torch] Hop submit failed:", err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      onClick={() => requestClose()}
      style={{
        position:"fixed", inset:0, zIndex:9000,
        background:"rgba(0,0,0,0.66)", backdropFilter:"blur(4px)",
        display:"flex", alignItems:"flex-end", justifyContent:"center",
        animation: closing ? "slipFadeOut 0.18s ease both" : "fadeIn 0.18s ease",
        pointerEvents: closing ? "none" : "auto",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width:"100%", maxWidth:520,
          // Use dynamic viewport height (`dvh`) where supported so the modal
          // shrinks correctly when the iOS browser chrome is showing. Falls
          // back to `vh` (less accurate but always defined) for older clients.
          // 88% caps it so the dimmed backdrop is always visible above the
          // sheet, preserving the "modal" affordance.
          maxHeight:"88dvh",
          background:T.bg,
          borderTopLeftRadius:20, borderTopRightRadius:20,
          border:`1px solid ${T.border}`, borderBottom:"none",
          // Flex column: scrollable content area on top, pinned action row
          // on bottom. Without this, the submit button sits at the END of a
          // single scrolling container and can be inaccessible on small
          // screens (iPhone mini + browser chrome eats the bottom 14vh).
          display:"flex", flexDirection:"column",
          // Prevent the scroll-chain to the body underneath -- on iOS, the
          // sheet's scroll will rubber-band into the page scroll otherwise,
          // making the modal feel broken.
          overscrollBehavior:"contain",
          animation: closing ? "sheetExitDown 0.2s cubic-bezier(0.4,0,1,1) both" : "sheetEnterUp 0.26s cubic-bezier(0.32,0.72,0,1)",
        }}
      >
        {/* Scrollable content region. flex:1 makes it absorb whatever space
            isn't claimed by the footer. WebkitOverflowScrolling for iOS
            momentum. */}
        <div style={{
          flex:"1 1 auto", minHeight:0, overflowY:"auto",
          padding:"18px 18px 4px",
          WebkitOverflowScrolling:"touch",
        }}>
        {/* Grab handle */}
        <div style={{
          width:36, height:4, borderRadius:2, background:T.border,
          margin:"0 auto 14px",
        }}/>

        {/* Header — picker's name + pick line + X close */}
        <div style={{ marginBottom:16, display:"flex", alignItems:"flex-start", gap:10 }}>
          <div style={{ flex:1, minWidth:0 }}>
            <div style={{
              fontFamily:FB, fontSize:11, fontWeight:700, letterSpacing:"0.08em",
              color:T.ember, textTransform:"uppercase", marginBottom:6,
            }}>
              Hop on
            </div>
            <div style={{
              fontFamily:FB, fontSize:18, fontWeight:700, color:T.ink,
              letterSpacing:"-0.01em", marginBottom:2,
            }}>
              {originalPick.username}'s pick
            </div>
            <div style={{
              fontFamily:FB, fontSize:13, color:T.inkSec, fontWeight:500,
            }}>
              {originalPick.isParlay
                ? (parlayLegCountOf(originalPick) > 0 ? `${parlayLegCountOf(originalPick)}-leg parlay` : "Parlay")
                : humanizePickLabel(originalPick)}{odds != null ? ` · ${fmtOdds(odds)}` : ""}
            </div>
            {originalPick.gameLabel && (
              <div style={{
                fontFamily:FB, fontSize:12, color:T.inkMuted, marginTop:4,
              }}>
                {originalPick.gameLabel}
              </div>
            )}
          </div>
          <button onClick={() => requestClose()} aria-label="Close" style={{
            flexShrink:0, width:32, height:32, borderRadius:10,
            background:"rgba(255,255,255,0.05)", border:`1px solid ${T.border}`,
            color:T.inkMuted, cursor:"pointer",
            display:"flex", alignItems:"center", justifyContent:"center",
          }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>

        {/* League selector */}
        <div style={{ marginBottom:14 }}>
          <div style={{
            fontFamily:FB, fontSize:11, fontWeight:700, letterSpacing:"0.06em",
            color:T.inkMuted, textTransform:"uppercase", marginBottom:8,
          }}>
            Play in
          </div>
          <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
            {/* Just for Fun option — always selectable */}
            <button
              onClick={() => setSelectedLeagueId("__fun__")}
              style={{
                display:"flex", alignItems:"center", justifyContent:"space-between",
                padding:"11px 13px", borderRadius:10,
                border: `1px solid ${selectedLeagueId === "__fun__" ? T.ember : T.border}`,
                background: selectedLeagueId === "__fun__" ? "rgba(255,90,31,0.08)" : T.card,
                cursor:"pointer", fontFamily:FB, textAlign:"left",
                transition:"all 0.14s",
              }}
            >
              <div>
                <div style={{ fontSize:14, fontWeight:700, color:T.ink }}>Just for Fun</div>
                <div style={{ fontSize:11, color:T.inkMuted, marginTop:2 }}>
                  No league · Practice balance
                </div>
              </div>
              <div style={{ fontSize:12, fontWeight:600, color:T.inkSec }}>
                {(viewerStats?.balance ?? 0).toLocaleString()} pts
              </div>
            </button>

            {eligibleLeagues.length === 0 && (
              <div style={{
                padding:"10px 12px", borderRadius:10, background:T.card,
                border:`1px solid ${T.border}`, fontFamily:FB, fontSize:12,
                color:T.inkMuted, textAlign:"center",
              }}>
                No leagues yet. Pick lands in practice for now.
              </div>
            )}

            {eligibleLeagues.map(l => {
              const isSelected = selectedLeagueId === l.id;
              const enabled = l.sportMatches;
              return (
                <button
                  key={l.id}
                  onClick={() => { if (enabled) setSelectedLeagueId(l.id); }}
                  disabled={!enabled}
                  title={enabled ? "" : `League doesn't accept ${originalPick.sport || pickSport || "this sport"}`}
                  style={{
                    display:"flex", alignItems:"center", justifyContent:"space-between",
                    padding:"11px 13px", borderRadius:10,
                    border: `1px solid ${isSelected ? T.ember : T.border}`,
                    background: isSelected ? "rgba(255,90,31,0.08)" : T.card,
                    cursor: enabled ? "pointer" : "not-allowed",
                    fontFamily:FB, textAlign:"left",
                    opacity: enabled ? 1 : 0.42,
                    transition:"all 0.14s",
                  }}
                >
                  <div>
                    <div style={{ fontSize:14, fontWeight:700, color:T.ink }}>{l.name}</div>
                    <div style={{ fontSize:11, color:T.inkMuted, marginTop:2 }}>
                      {enabled
                        ? `${(l.isPublic ?? l.public) ? "Public" : "Private"} · ${(l.balance ?? 0).toLocaleString()} pts`
                        : `Only accepts ${l.sportFilter || l.eventSport || "specific sport"}`}
                    </div>
                  </div>
                  {enabled && (
                    <div style={{ fontSize:12, fontWeight:600, color:T.inkSec }}>
                      {(l.balance ?? 0).toLocaleString()}
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Wager input */}
        <div style={{ marginBottom:14 }}>
          <div style={{
            display:"flex", alignItems:"center", justifyContent:"space-between",
            marginBottom:8,
          }}>
            <div style={{
              fontFamily:FB, fontSize:11, fontWeight:700, letterSpacing:"0.06em",
              color:T.inkMuted, textTransform:"uppercase",
            }}>
              Your wager
            </div>
            <div style={{
              fontFamily:FB, fontSize:11, color: overBudget ? T.red : T.inkMuted,
              fontWeight:600,
            }}>
              {availableBalance.toLocaleString()} available
            </div>
          </div>
          <input
            type="number"
            inputMode="numeric"
            value={wagerStr}
            onChange={(e) => setWagerStr(e.target.value.replace(/[^\d]/g, ""))}
            placeholder={leagueMinWager > 0 ? String(leagueMinWager) : "100"}
            style={{
              width:"100%", padding:"13px 14px", borderRadius:10,
              background:T.card, border:`1px solid ${(overBudget || underMin || overMax) ? T.red : T.border}`,
              color:T.ink, fontFamily:FB, fontSize:18, fontWeight:700,
              letterSpacing:"-0.01em", outline:"none",
              transition:"border-color 0.14s",
            }}
          />
          {/* League rule hint. Shows the minimum/maximum when violated so the
              hopper knows why submit is disabled (otherwise they'd hit a dead
              button with no explanation). */}
          {wagerRuleError && (
            <div style={{
              marginTop:6, fontFamily:FB, fontSize:11, color:T.red, fontWeight:600,
            }}>
              {wagerRuleError}
            </div>
          )}
          {/* Quick-wager chips */}
          <div style={{ display:"flex", gap:6, marginTop:8 }}>
            {[25, 100, 250, 500].map(amt => (
              <button
                key={amt}
                onClick={() => setWagerStr(String(amt))}
                style={{
                  flex:1, padding:"7px 0", borderRadius:8,
                  background:"rgba(255,255,255,0.04)", border:`1px solid ${T.border}`,
                  color:T.inkSec, fontFamily:FB, fontSize:12, fontWeight:600,
                  cursor:"pointer",
                }}
              >
                {amt}
              </button>
            ))}
          </div>
        </div>

        {/* Payout preview */}
        {wager > 0 && (
          <div style={{
            display:"flex", justifyContent:"space-between", alignItems:"center",
            padding:"10px 13px", marginBottom:14,
            background:"rgba(255,255,255,0.03)", borderRadius:10,
            border:`1px solid ${T.border}`,
          }}>
            <div style={{ fontFamily:FB, fontSize:12, color:T.inkSec, fontWeight:600 }}>
              To win
            </div>
            <div style={{ fontFamily:FB, fontSize:14, color:T.green, fontWeight:700 }}>
              +{profit.toLocaleString()} pts
            </div>
          </div>
        )}

        </div>
        {/* End scrollable region */}

        {/* Pinned action footer. NOT inside the scrollable area, so the
            submit button is always reachable regardless of content height
            or how far the user has scrolled. The borderTop + bg-same-as-
            modal gives it a subtle "footer" affordance. Bottom padding
            absorbs the iOS home-indicator safe area. */}
        <div style={{
          flex:"0 0 auto",
          padding:"12px 18px calc(14px + env(safe-area-inset-bottom, 0px))",
          borderTop:`1px solid ${T.border}`,
          background:T.bg,
          display:"flex", gap:8,
        }}>
          <button
            onClick={() => requestClose()}
            disabled={submitting}
            style={{
              padding:"13px 18px", borderRadius:10,
              background:"transparent", border:`1px solid ${T.border}`,
              color:T.inkSec, fontFamily:FB, fontSize:14, fontWeight:600,
              cursor: submitting ? "not-allowed" : "pointer",
              opacity: submitting ? 0.5 : 1,
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!canSubmit}
            style={{
              flex:1, padding:"13px 18px", borderRadius:10,
              background: canSubmit ? T.ember : "rgba(255,255,255,0.06)",
              border:"none",
              color: canSubmit ? "#fff" : T.inkMuted,
              fontFamily:FB, fontSize:14, fontWeight:700, letterSpacing:"-0.005em",
              cursor: canSubmit ? "pointer" : "not-allowed",
              transition:"all 0.14s",
            }}
          >
            {submitting
              ? "Locking in…"
              : overBudget
                ? "Not enough"
                : `Hop on for ${wager.toLocaleString()} pts`}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ============== CommentsDrawer (Phase 3c) =============================
   Bottom-sheet modal for the comment thread on a pick. Matches HopDrawer
   aesthetic so the feed feels cohesive. Flat threading (no replies),
   chronological order, no editing — comments can be deleted but never
   rewritten. Same logic as Twitter pre-Edit-Tweet: trash talk stays
   honest, or it doesn't post.

   Moderation reuse:
   - filterObjectionableContent runs client-side before write
   - Long-press / "..." menu on each row opens ReportContentModal
   - EULA gate from app shell still applies (user must have accepted)
   - Blocked users see only their own + replies to them? — V2.
     For now blocked == not rendered (filtered client-side)

   Delete semantics:
   - Comment author can delete their own comment
   - Pick owner can delete any comment on their pick (moderation)
   - Server-side rule enforces both
   - On delete, decrement commentCount on the parent pick

   Counter:
   - On create: increment(/publicPicks/{pickId}).commentCount by +1
   - On delete: decrement by -1
   - Fire-and-forget: thread still shows the source-of-truth count from
     onSnapshot, the parent counter on the feed card is a denormalized
     hint. Eventual consistency is fine here.
========================================================================*/
function CommentsDrawer({ pick, viewer, viewerStats, viewerBlockedIds = [], onClose }) {
  // ── Bottom-nav suppression ──
  // Missing here previously, which left the nav rendered above the drawer's
  // composer on mobile. Refcounted hook — safe to call alongside other modals.
  const { closing, requestClose } = useSheetClose(onClose, 200);
  useModalOpen(!closing);

  // ── pickId resolution + defensive logging ──
  // The thread key is the publicPicks doc id. Older mirror docs (or any pick
  // surfaced from a path that doesn't carry `pickId` as a field) only have
  // it as `id` after the usePulseFeed spread. Fall back so neither shape
  // breaks the thread query.
  const threadKey = pick?.pickId || pick?.id || null;
  useEffect(() => {
    if (!threadKey) {
      console.warn("[Torch] CommentsDrawer opened without a usable pickId/id", { pick });
    }
  }, [threadKey, pick]);

  const { comments: rawComments, loading } = useCommentsThread(threadKey);
  const [composerText, setComposerText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [reportTarget, setReportTarget] = useState(null);
  const scrollRef = useRef(null);
  const composerRef = useRef(null);

  // Filter out comments from blocked users. Done client-side; future
  // versions might filter server-side via custom claims for cost.
  const comments = rawComments.filter(c => !viewerBlockedIds?.includes?.(c.uid));

  // Auto-scroll to bottom on new comments — but only if the user is
  // already near the bottom. If they've scrolled up to read history,
  // don't yank them down when someone else posts. Industry standard
  // chat UX (Slack, iMessage, etc).
  const wasAtBottomRef = useRef(true);
  const handleScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    wasAtBottomRef.current = (el.scrollHeight - el.scrollTop - el.clientHeight) < 80;
  };
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    if (wasAtBottomRef.current) {
      requestAnimationFrame(() => { el.scrollTop = el.scrollHeight; });
    }
  }, [comments.length]);

  const trimmed = composerText.trim();
  const charCount = composerText.length;
  const overLimit = charCount > 500;
  const canSubmit = trimmed.length > 0 && !overLimit && !submitting && !!viewer?.uid;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const cleaned = filterObjectionableContent(trimmed);
      const { getFirestore, collection, addDoc, doc, updateDoc, increment, serverTimestamp } = await import("firebase/firestore");
      const db = getFirestore(app);
      const uname = viewerStats?.username || viewer.displayName || viewer.email?.split("@")[0] || "Someone";
      if (!threadKey) {
        console.error("[Torch] Comment submit blocked — no threadKey on pick");
        window.torchToast?.("Couldn't post — pick reference missing.", "error");
        return;
      }
      await addDoc(collection(db, "publicPicks", threadKey, "comments"), {
        uid: viewer.uid,
        username: uname,
        authorWins: viewerStats?.wins ?? 0,
        authorLosses: viewerStats?.losses ?? 0,
        text: cleaned,
        createdAt: serverTimestamp(),
        pickOwnerUid: pick.uid, // denormalized — picker can delete any comment
      });
      // Bump parent's commentCount. Fire-and-forget — onSnapshot in
      // usePulseFeed will pick up the change. If it fails, the thread's
      // own count is still correct via its own onSnapshot subscription.
      updateDoc(doc(db, "publicPicks", threadKey), {
        commentCount: increment(1),
      }).catch(e => console.warn("[Torch] commentCount inc failed (non-blocking):", e.message));
      setComposerText("");
      // Force auto-scroll on next render — your own comment should always
      // bring you to the bottom regardless of where you were scrolled.
      wasAtBottomRef.current = true;
      haptic.light?.();
    } catch (err) {
      console.error("[Torch] Comment submit failed:", err);
      window.torchToast?.("Couldn't post. Try again.", "error");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = (comment) => {
    if (!comment?.id) return;
    if (!threadKey) {
      console.warn("[Torch] Comment delete blocked — no threadKey on pick");
      return;
    }
    window.torchConfirm?.(
      "Delete this comment?",
      async () => {
        try {
          const { getFirestore, doc, deleteDoc, updateDoc, increment } = await import("firebase/firestore");
          const db = getFirestore(app);
          await deleteDoc(doc(db, "publicPicks", threadKey, "comments", comment.id));
          updateDoc(doc(db, "publicPicks", threadKey), {
            commentCount: increment(-1),
          }).catch(() => {});
          haptic.light?.();
        } catch (err) {
          console.error("[Torch] Comment delete failed:", err);
          window.torchToast?.("Couldn't delete. Try again.", "error");
        }
      },
      { danger: true, confirmLabel: "Delete" }
    );
  };

  // Keyboard: Enter submits, Shift+Enter newline. Mobile keyboards have
  // their own return-key conventions so this is desktop-mostly.
  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  return createPortal(
    <div
      onClick={() => requestClose()}
      style={{
        position:"fixed", inset:0, zIndex:9000,
        background:"rgba(0,0,0,0.66)", backdropFilter:"blur(4px)",
        display:"flex", alignItems:"flex-end", justifyContent:"center",
        animation: closing ? "slipFadeOut 0.18s ease both" : "fadeIn 0.18s ease",
        pointerEvents: closing ? "none" : "auto",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width:"100%", maxWidth:520, height:"86vh",
          display:"flex", flexDirection:"column",
          background:T.bg, borderTopLeftRadius:20, borderTopRightRadius:20,
          border:`1px solid ${T.border}`, borderBottom:"none",
          animation: closing ? "sheetExitDown 0.2s cubic-bezier(0.4,0,1,1) both" : "sheetEnterUp 0.26s cubic-bezier(0.32,0.72,0,1)",
        }}
      >
        {/* Grab handle */}
        <div style={{
          width:36, height:4, borderRadius:2, background:T.border,
          margin:"10px auto 8px", flexShrink:0,
        }}/>

        {/* Header — pick context + X close */}
        <div style={{ padding:"4px 18px 14px", borderBottom:`1px solid ${T.border}`, flexShrink:0, display:"flex", alignItems:"flex-start", gap:10 }}>
          <div style={{ flex:1, minWidth:0 }}>
            <div style={{
              fontFamily:FB, fontSize:11, fontWeight:700, letterSpacing:"0.08em",
              color:T.ember, textTransform:"uppercase", marginBottom:6,
            }}>
              Comments
            </div>
            <div style={{
              fontFamily:FB, fontSize:15, fontWeight:700, color:T.ink,
              letterSpacing:"-0.01em", marginBottom:2,
            }}>
              {pick.username}'s pick
            </div>
            <div style={{ fontFamily:FB, fontSize:12, color:T.inkSec }}>
              {pick.isParlay
                ? (parlayLegCountOf(pick) > 0 ? `${parlayLegCountOf(pick)}-leg parlay` : "Parlay")
                : humanizePickLabel(pick)}{pick.odds != null ? ` · ${fmtOdds(pick.odds)}` : ""}
            </div>
          </div>
          <button onClick={() => requestClose()} aria-label="Close" style={{
            flexShrink:0, width:32, height:32, borderRadius:10,
            background:"rgba(255,255,255,0.05)", border:`1px solid ${T.border}`,
            color:T.inkMuted, cursor:"pointer",
            display:"flex", alignItems:"center", justifyContent:"center",
          }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>

        {/* Thread — scrollable */}
        <div
          ref={scrollRef}
          onScroll={handleScroll}
          style={{
            flex:1, overflowY:"auto", padding:"14px 18px",
          }}
        >
          {loading ? (
            <div style={{ padding:"6px 0" }}>
              {[0,1,2].map(i => <CommentRowSkeleton key={i} />)}
            </div>
          ) : comments.length === 0 ? (
            <div style={{
              textAlign:"center", padding:"40px 12px",
              fontFamily:FB, color:T.inkMuted,
            }}>
              <div style={{ fontSize:14, fontWeight:600, color:T.inkSec, marginBottom:4 }}>
                No comments yet
              </div>
              <div style={{ fontSize:12 }}>
                Be first. Talk smack, congratulate, whatever.
              </div>
            </div>
          ) : (
            comments.map(c => {
              const canDelete = c.uid === viewer?.uid || pick.uid === viewer?.uid;
              const isOwn = c.uid === viewer?.uid;
              const tsMs = tsToMillis(c.createdAt);
              return (
                <CommentRow
                  key={c.id}
                  comment={c}
                  tsMs={tsMs}
                  isOwn={isOwn}
                  canDelete={canDelete}
                  onDelete={() => handleDelete(c)}
                  onReport={() => setReportTarget(c)}
                />
              );
            })
          )}
        </div>

        {/* Composer — sticky at bottom, accounts for iOS safe-area */}
        <div style={{
          padding:"10px 14px calc(10px + env(safe-area-inset-bottom, 0px))",
          borderTop:`1px solid ${T.border}`, background:T.bg, flexShrink:0,
        }}>
          <div style={{
            display:"flex", gap:8, alignItems:"flex-end",
          }}>
            <textarea
              ref={composerRef}
              value={composerText}
              onChange={(e) => setComposerText(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Say something…"
              rows={1}
              style={{
                flex:1, padding:"10px 12px", borderRadius:18,
                background:T.card, border:`1px solid ${overLimit ? T.red : T.border}`,
                color:T.ink, fontFamily:FB, fontSize:14, fontWeight:500,
                resize:"none", outline:"none", maxHeight:120, overflow:"auto",
                lineHeight:1.35,
              }}
            />
            <button
              onClick={handleSubmit}
              disabled={!canSubmit}
              style={{
                padding:"10px 16px", borderRadius:18,
                background: canSubmit ? T.ember : "rgba(255,255,255,0.06)",
                border:"none",
                color: canSubmit ? "#fff" : T.inkMuted,
                fontFamily:FB, fontSize:13, fontWeight:700,
                cursor: canSubmit ? "pointer" : "not-allowed",
                transition:"all 0.14s", flexShrink:0,
              }}
            >
              {submitting ? "…" : "Post"}
            </button>
          </div>
          {/* Character counter — shows only when getting close to limit */}
          {charCount > 400 && (
            <div style={{
              fontFamily:FB, fontSize:11, fontWeight:600,
              color: overLimit ? T.red : T.inkMuted,
              textAlign:"right", marginTop:4, paddingRight:60,
            }}>
              {charCount}/500
            </div>
          )}
        </div>
      </div>

      {/* Report modal — opens when a comment's "..." → Report tapped */}
      {reportTarget && (
        <ReportContentModal
          contentId={reportTarget.id}
          contentText={reportTarget.text}
          reportedUserId={reportTarget.uid}
          reportedUsername={reportTarget.username}
          leagueId={null}
          currentUser={viewer}
          onClose={() => setReportTarget(null)}
          onReported={() => {
            setReportTarget(null);
            window.torchToast?.("Reported. Thanks.", "success");
          }}
        />
      )}
    </div>,
    document.body
  );
}

/* CommentRow — single message in the thread.
   Renders username + rank pill, relative timestamp, text body, and an
   overflow menu (Delete if you can, Report if you can't). Own messages
   get a subtle accent so you can find yours quickly in a long thread. */
function CommentRow({ comment, tsMs, isOwn, canDelete, onDelete, onReport }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const ago = tsMs ? timeAgo(tsMs) : "just now";
  const authorStats = { wins: comment.authorWins ?? 0, losses: comment.authorLosses ?? 0 };

  return (
    <div style={{
      padding:"10px 0",
      borderBottom:`1px solid ${T.border}`,
      position:"relative",
    }}>
      <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:5 }}>
        <span style={{
          fontFamily:FB, fontSize:13, fontWeight:700,
          color: isOwn ? T.ember : T.ink, letterSpacing:"-0.005em",
        }}>
          {comment.username}
        </span>
        <UserRank stats={authorStats} size="sm" showName={false} />
        <span style={{
          fontFamily:FB, fontSize:11, color:T.inkMuted, fontWeight:500,
        }}>
          {ago}
        </span>
        <button
          onClick={() => setMenuOpen(v => !v)}
          className="no-press"
          style={{
            marginLeft:"auto", padding:"2px 6px", borderRadius:6,
            background:"transparent", border:"none", color:T.inkMuted,
            cursor:"pointer", fontFamily:FB, fontSize:16, fontWeight:700,
            lineHeight:1,
          }}
          title="More"
        >
          ···
        </button>
      </div>
      <div style={{
        fontFamily:FB, fontSize:14, color:T.ink, fontWeight:500,
        lineHeight:1.42, letterSpacing:"-0.003em",
        whiteSpace:"pre-wrap", wordBreak:"break-word",
      }}>
        {comment.text}
      </div>

      {/* Overflow menu */}
      {menuOpen && (
        <>
          <div
            onClick={() => setMenuOpen(false)}
            style={{ position:"fixed", inset:0, zIndex:50 }}
          />
          <div style={{
            position:"absolute", right:0, top:32, zIndex:60,
            background:T.bg, border:`1px solid ${T.border}`, borderRadius:10,
            boxShadow:"0 8px 24px rgba(0,0,0,0.4)",
            overflow:"hidden", minWidth:140,
          }}>
            {canDelete && (
              <button
                onClick={() => { setMenuOpen(false); onDelete(); }}
                style={{
                  display:"block", width:"100%", textAlign:"left",
                  padding:"10px 14px", background:"transparent", border:"none",
                  color:T.red, fontFamily:FB, fontSize:13, fontWeight:600,
                  cursor:"pointer",
                }}
              >
                Delete
              </button>
            )}
            {!isOwn && (
              <button
                onClick={() => { setMenuOpen(false); onReport(); }}
                style={{
                  display:"block", width:"100%", textAlign:"left",
                  padding:"10px 14px", background:"transparent", border:"none",
                  color:T.inkSec, fontFamily:FB, fontSize:13, fontWeight:600,
                  cursor:"pointer",
                }}
              >
                Report
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
}

/* ===========================================
   PULSE EXPLAINER MODAL
   ============================================
   Centralized "what is this?" surface for the Pulse page. Covers:
     1. What Pulse IS (firehose of picks across all of Torch — social feed)
     2. What you can DO (Hop on, talk trash in comments)
     3. What the RANK badges mean (Rookie → Legend tier breakdown)

   Single source of truth so we're not duplicating copy between an intro
   card, a separate rank legend, etc. Triggered from a small "?" button
   on the Pulse header. Closes on backdrop tap or X button. Scrollable
   on small screens.
=========================================== */
function PulseExplainerModal({ onClose }) {
  useModalOpen(true);

  // Tier rows defined inline rather than re-deriving from getUserRank so
  // the copy and visual styling can be tuned independently of the actual
  // rank-detection logic. If you change the gates in getUserRank, mirror
  // them here.
  const tiers = [
    { name: "Rookie",   pill: T.inkSec,                                bg: "rgba(244,244,246,0.05)", criteria: "Fewer than 5 settled picks" },
    { name: "Pro",      pill: T.blue,                                  bg: T.blueDim,                criteria: "5+ settled · 0–25% win rate" },
    { name: "Veteran",  pill: T.green,                                 bg: T.greenDim,               criteria: "5+ settled · 25–50% win rate" },
    { name: "All-Star", pill: T.ember,                                 bg: T.emberDim,               criteria: "5+ settled · 50–75% win rate" },
    { name: "Legend",   pill: T.gold,                                  bg: T.goldDim,                criteria: "5+ settled · 75%+ win rate" },
  ];

  return createPortal(
    <div
      onClick={onClose}
      style={{
        position:"fixed", inset:0, zIndex:9000,
        background:"rgba(0,0,0,0.66)", backdropFilter:"blur(4px)",
        display:"flex", alignItems:"flex-end", justifyContent:"center",
        animation:"fadeIn 0.18s ease",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width:"100%", maxWidth:520, maxHeight:"88dvh",
          display:"flex", flexDirection:"column",
          background:T.bg, borderTopLeftRadius:18, borderTopRightRadius:18,
          border:`1px solid ${T.border}`, borderBottom:"none",
          animation:"slideUp 0.22s cubic-bezier(0.32, 0.72, 0, 1)",
          overscrollBehavior:"contain",
        }}
      >
        {/* Header */}
        <div style={{
          flex:"0 0 auto",
          padding:"18px 18px 12px",
          display:"flex", alignItems:"center", justifyContent:"space-between",
          borderBottom:`1px solid ${T.border}`,
        }}>
          <div style={{
            fontFamily:FD, fontSize:18, fontWeight:800, color:T.ink,
            letterSpacing:"-0.01em",
          }}>
            About Pulse
          </div>
          <button onClick={onClose} aria-label="Close" style={{
            flexShrink:0, width:32, height:32, borderRadius:10,
            background:"rgba(255,255,255,0.05)", border:`1px solid ${T.border}`,
            color:T.inkMuted, cursor:"pointer",
            display:"flex", alignItems:"center", justifyContent:"center",
            padding:0,
          }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
              <path d="M18 6L6 18M6 6l12 12"/>
            </svg>
          </button>
        </div>

        {/* Scrollable body */}
        <div style={{
          flex:"1 1 auto", minHeight:0,
          overflowY:"auto", padding:"16px 18px 22px",
          overscrollBehavior:"contain",
        }}>
          {/* What is Pulse */}
          <div style={{ marginBottom:18 }}>
            <div style={{
              fontFamily:FD, fontSize:15, fontWeight:700, color:T.ink,
              marginBottom:6, letterSpacing:"-0.005em",
            }}>
              What is Pulse?
            </div>
            <div style={{
              fontFamily:FB, fontSize:13.5, lineHeight:1.55, color:T.inkSec,
            }}>
              Pulse is the live feed of picks from everyone on Torch. Tap{" "}
              <span style={{ color:T.ember, fontWeight:700 }}>Hop on</span>{" "}
              to copy any pick into one of your own leagues, or jump in the
              comments to talk trash. Use the <strong style={{ color:T.ink, fontWeight:700 }}>Live</strong> /{" "}
              <strong style={{ color:T.ink, fontWeight:700 }}>Locked</strong> /{" "}
              <strong style={{ color:T.ink, fontWeight:700 }}>Settled</strong> filter to focus the feed.
            </div>
          </div>

          {/* Top Pickers */}
          <div style={{ marginBottom:18 }}>
            <div style={{
              fontFamily:FD, fontSize:15, fontWeight:700, color:T.ink,
              marginBottom:6, letterSpacing:"-0.005em",
            }}>
              Top Pickers
            </div>
            <div style={{
              fontFamily:FB, fontSize:13.5, lineHeight:1.55, color:T.inkSec,
            }}>
              The leaderboard at the top ranks everyone on Torch by win rate.
              You need at least 25 settled picks to qualify — small samples
              don't earn a spot.
            </div>
          </div>

          {/* Rank tiers */}
          <div>
            <div style={{
              fontFamily:FD, fontSize:15, fontWeight:700, color:T.ink,
              marginBottom:10, letterSpacing:"-0.005em",
            }}>
              Rank tiers
            </div>
            <div style={{
              fontFamily:FB, fontSize:13, lineHeight:1.55, color:T.inkSec,
              marginBottom:10,
            }}>
              Every picker earns a rank based on settled picks and win rate.
              Ranks update automatically as you play.
            </div>

            <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
              {tiers.map(t => (
                <div key={t.name} style={{
                  display:"flex", alignItems:"center", justifyContent:"space-between",
                  padding:"10px 12px", borderRadius:10,
                  background:T.card, border:`1px solid ${T.border}`,
                }}>
                  <span style={{
                    display:"inline-flex", alignItems:"center",
                    padding:"4px 10px", borderRadius:999,
                    background: t.bg, color: t.pill,
                    fontFamily:FB, fontSize:11, fontWeight:800,
                    letterSpacing:"0.04em", textTransform:"uppercase",
                  }}>
                    {t.name}
                  </span>
                  <span style={{
                    fontFamily:FB, fontSize:12, color:T.inkMuted, fontWeight:600,
                    textAlign:"right",
                  }}>
                    {t.criteria}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

function PulsePage({ user, userStats, balance, userLeagues = [], setActivePage, refreshLeagues, onViewProfile }) {
  // Feed filter state — drives usePulseFeed query constraints below.
  // "All picks" was retired: with the firehose mixing settled and pending in
  // one list, users had to read each card's pill to know what they were
  // looking at. Two explicit modes (Live = in-play / Settled = archive) is
  // less ambiguous and matches how people actually want to browse — either
  // "what's still playing out" or "who hit / who didn't".
  const [feedFilter, setFeedFilter] = useState("live");
  // Hop drawer — null when closed, pick object when open. Lives at PulsePage
  // so it overlays the entire feed (not just one card) and so closing it
  // doesn't trigger a card-level re-render storm.
  const [hopTarget, setHopTarget] = useState(null);
  // Comments drawer — same pattern as Hop. Bottom-sheet over the whole
  // feed; thread subscribes to its own onSnapshot inside the drawer.
  const [commentsTarget, setCommentsTarget] = useState(null);
  // Real-time feed. Subscribes via onSnapshot so picks land without a manual
  // refresh the moment they're submitted anywhere on Torch.
  const { picks: feedPicks, loading: feedLoading, hasMore, loadMore } = usePulseFeed({ pageSize: 25, filter: feedFilter });
  // Top 3 leaderboard — aggregates won/lost from /publicPicks, 5-pick gate,
  // cached 5 min. Tap a row → opens UserProfileModal via onViewProfile.
  const { topPickers, loading: topPickersLoading } = useTopPickers({ limit: 3, minSettled: 25, fetchSize: 500 });
  // Explainer modal — "what is Pulse" + rank legend. Single entry point
  // accessible from a small "?" button on the top pickers header AND from
  // a one-line intro at the top of the page.
  const [showExplainer, setShowExplainer] = useState(false);

  return (
    <div style={{ maxWidth:640, margin:"0 auto", padding:"0 0 calc(90px + env(safe-area-inset-bottom, 0px))", background:T.bg, minHeight:"100vh" }}>

      {/* Hop drawer — bottom-sheet modal when hopping on a feed pick */}
      {hopTarget && (
        <HopDrawer
          originalPick={hopTarget}
          viewer={user}
          viewerStats={userStats}
          viewerLeagues={userLeagues}
          onClose={() => setHopTarget(null)}
          onHopped={() => refreshLeagues?.()}
        />
      )}

      {/* Comments drawer — bottom-sheet thread on a feed pick */}
      {commentsTarget && (
        <CommentsDrawer
          pick={commentsTarget}
          viewer={user}
          viewerStats={userStats}
          onClose={() => setCommentsTarget(null)}
        />
      )}

      {/* About-Pulse explainer modal — what Pulse is + rank legend */}
      {showExplainer && (
        <PulseExplainerModal onClose={() => setShowExplainer(false)} />
      )}

      {/* ── Page intro ──
           One-line orientation for new users. Single tap target opens the
           explainer modal which covers Pulse, top pickers, and rank tiers.
           Compact on purpose — power users barely see it; new users get a
           clear "what is this?" entry point. */}
      <div style={{ margin:"14px 16px 0" }}>
        <button
          onClick={() => setShowExplainer(true)}
          style={{
            width:"100%", display:"flex", alignItems:"center", justifyContent:"space-between",
            padding:"10px 13px", borderRadius:10,
            background:T.card, border:`1px solid ${T.border}`,
            cursor:"pointer", fontFamily:FB, textAlign:"left",
            transition:"border-color 0.14s, background 0.14s",
          }}
        >
          <span style={{ fontSize:12.5, color:T.inkSec, fontWeight:600, lineHeight:1.4 }}>
            <span style={{ color:T.ember, fontWeight:800, letterSpacing:"0.02em" }}>Pulse</span>
            {" — live picks from everyone on Torch. Hop on, talk trash."}
          </span>
          <span style={{
            flexShrink:0, marginLeft:10,
            fontSize:11, color:T.inkMuted, fontWeight:700,
            letterSpacing:"0.04em", textTransform:"uppercase",
          }}>
            Learn ›
          </span>
        </button>
      </div>

      {/* ── Top 3 leaderboard ──
           Real data via useTopPickers — aggregates won/lost across the most
           recent 500 /publicPicks docs, 25-pick gate (raised from 5 ahead of
           World Cup launch to sharpen the qualification bar — Legend should
           feel earned, not handed out at 4-1), win rate sorted with settled-
           count tiebreaker, cached 5 min. Tap any row → user profile modal.
           Empty/short states are quiet on purpose: at 35 users not everyone
           qualifies yet, and the section shouldn't apologize for it. */}
      <div style={{ margin:"18px 16px 0" }}>
        <div style={{ display:"flex", alignItems:"baseline", justifyContent:"space-between", marginBottom:8 }}>
          <div style={{ display:"flex", alignItems:"center", gap:6 }}>
            <span style={{ fontSize:10.5, fontWeight:700, color:T.inkMuted, letterSpacing:"0.10em", textTransform:"uppercase" }}>
              Overall Top Pickers
            </span>
            {/* Info icon — secondary entry point to the explainer modal,
                placed inline with the section header so users who scroll
                past the page-level intro still have a tap target. */}
            <button
              onClick={() => setShowExplainer(true)}
              aria-label="How rankings work"
              style={{
                width:16, height:16, padding:0, borderRadius:"50%",
                background:"rgba(255,255,255,0.06)", border:`1px solid ${T.border}`,
                color:T.inkMuted, cursor:"pointer",
                display:"inline-flex", alignItems:"center", justifyContent:"center",
                fontFamily:FB, fontSize:10, fontWeight:800, lineHeight:1,
              }}
            >
              ?
            </button>
          </div>
          <span style={{ fontSize:10.5, fontWeight:600, color:T.inkMuted }}>
            25+ settled
          </span>
        </div>
        <div style={{
          borderRadius:14, overflow:"hidden",
          background: T.card, border:`1px solid ${T.border}`,
        }}>
          {topPickersLoading ? (
            // Skeleton — three dim rows so the card doesn't collapse on load.
            [0,1,2].map(i => (
              <div key={`skel-${i}`} style={{
                display:"flex", alignItems:"center", gap:12,
                padding:"12px 14px",
                borderTop: i === 0 ? "none" : `1px solid ${T.border}`,
              }}>
                <div style={{ width:18, height:14, borderRadius:3, background:"rgba(255,255,255,0.05)" }}/>
                <div style={{ flex:1, height:14, borderRadius:4, background:"rgba(255,255,255,0.05)" }}/>
                <div style={{ width:60, height:14, borderRadius:4, background:"rgba(255,255,255,0.05)" }}/>
              </div>
            ))
          ) : topPickers.length === 0 ? (
            // Empty — nobody has 25+ settled yet. Stay short, stay punchy.
            <div style={{ padding:"16px", display:"flex", alignItems:"center", gap:12 }}>
              <div style={{
                width:38, height:38, borderRadius:10, flexShrink:0,
                background: T.goldDim, border:`1px solid rgba(217,164,65,0.30)`,
                display:"flex", alignItems:"center", justifyContent:"center",
              }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={T.gold} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6"/>
                  <path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18"/>
                  <path d="M4 22h16"/>
                  <path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22"/>
                  <path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22"/>
                  <path d="M18 2H6v7a6 6 0 0 0 12 0V2Z"/>
                </svg>
              </div>
              <div style={{ minWidth:0, flex:1 }}>
                <div style={{ fontFamily:FD, fontSize:13.5, fontWeight:700, color:T.ink, letterSpacing:"-0.005em", marginBottom:2 }}>
                  No qualifying pickers yet
                </div>
                <div style={{ fontSize:11.5, color:T.inkMuted, lineHeight:1.4 }}>
                  Needs 25 settled picks to qualify.
                </div>
              </div>
            </div>
          ) : (
            topPickers.map((p, i) => {
              const pctRaw = p.winRate * 100;
              const pct = Math.round(pctRaw);
              const rankInfo = getUserRank({ wins: p.wins, losses: p.losses });
              const isMe = user?.uid === p.uid;
              // Trophy colors for places: gold / silver / bronze on the index pill.
              const placeStyles = [
                { color: T.gold,    bg: T.goldDim,                border: "rgba(217,164,65,0.34)" },
                { color: "#C9CCD4", bg: "rgba(201,204,212,0.08)", border: "rgba(201,204,212,0.22)" },
                { color: "#CD8A5E", bg: "rgba(205,138,94,0.10)",  border: "rgba(205,138,94,0.28)" },
              ][i] || { color: T.inkSec, bg: "rgba(255,255,255,0.04)", border: T.border };
              return (
                <button
                  key={p.uid}
                  onClick={() => { haptic.light(); onViewProfile?.(p.uid, p.username); }}
                  className="no-press"
                  style={{
                    width:"100%", display:"flex", alignItems:"center", gap:12,
                    padding:"11px 14px",
                    background: isMe ? "rgba(255,107,61,0.04)" : "transparent",
                    border:"none",
                    borderTop: i === 0 ? "none" : `1px solid ${T.border}`,
                    cursor:"pointer", textAlign:"left",
                    fontFamily:FB,
                  }}
                >
                  {/* Place pill */}
                  <div style={{
                    width:22, height:22, borderRadius:6, flexShrink:0,
                    background: placeStyles.bg, border:`1px solid ${placeStyles.border}`,
                    display:"flex", alignItems:"center", justifyContent:"center",
                    fontFamily:FD, fontSize:11.5, fontWeight:700,
                    color: placeStyles.color, lineHeight:1,
                    fontVariantNumeric:"tabular-nums",
                  }}>
                    {i + 1}
                  </div>

                  {/* Username + rank pill */}
                  <div style={{ minWidth:0, flex:1, display:"flex", alignItems:"center", gap:8 }}>
                    <span style={{
                      fontFamily:FD, fontSize:13.5, fontWeight:700,
                      color: T.ink, letterSpacing:"-0.005em",
                      overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap",
                      maxWidth:140,
                    }}>
                      {p.username}{isMe ? " (you)" : ""}
                    </span>
                    <UserRank stats={{ wins: p.wins, losses: p.losses }} size="sm" />
                  </div>

                  {/* Record + win % */}
                  <div style={{ display:"flex", flexDirection:"column", alignItems:"flex-end", gap:1, flexShrink:0 }}>
                    <div style={{
                      fontFamily:FD, fontSize:13, fontWeight:700,
                      color: rankInfo.color,
                      letterSpacing:"-0.005em", lineHeight:1,
                      fontVariantNumeric:"tabular-nums",
                    }}>
                      {pct}%
                    </div>
                    <div style={{
                      fontSize:10.5, fontWeight:600, color: T.inkMuted,
                      lineHeight:1.1, fontVariantNumeric:"tabular-nums",
                    }}>
                      {p.wins}–{p.losses}
                    </div>
                  </div>
                </button>
              );
            })
          )}
        </div>
      </div>

      {/* ── Feed ──
           Real firehose: usePulseFeed (hoisted to top of component) subscribes
           to /publicPicks ordered newest-first with onSnapshot, so picks appear
           without a refresh the moment they're submitted anywhere on Torch.
           Filter chips narrow the view (All / Live / Settled). Hop/comment
           buttons are wired to toast stubs in Phase 3a; the drawer + thread
           land in 3b and 3c. */}
      <div className="hide-scrollbar" style={{
        display:"flex", gap:6, padding:"18px 16px 0", overflowX:"auto",
        WebkitOverflowScrolling:"touch", scrollbarWidth:"none",
      }}>
        {[
          { id:"live",    label:"Live" },
          { id:"pending", label:"Locked" },
          { id:"settled", label:"Settled" },
        ].map(chip => {
          const isActive = feedFilter === chip.id;
          return (
            <button
              key={chip.id}
              onClick={() => { haptic.light(); setFeedFilter(chip.id); }}
              className="no-press"
              style={{
                padding:"7px 13px", borderRadius:999, flexShrink:0,
                border:"1px solid transparent",
                background: isActive ? "#fff" : "rgba(255,255,255,0.04)",
                color: isActive ? "#08080A" : T.inkSec,
                fontSize:12, fontWeight: isActive ? 700 : 600,
                cursor:"pointer", letterSpacing:"-0.005em",
                transition:"all 0.15s",
              }}
            >
              {chip.id === "live" ? (
                <>
                  <svg
                    width="8" height="8" viewBox="0 0 10 10"
                    style={{ marginRight:6, verticalAlign:"middle" }}
                    aria-hidden="true"
                  >
                    <circle cx="5" cy="5" r="3.5" fill="#22c55e">
                      <animate attributeName="opacity" values="1;0.35;1" dur="1.4s" repeatCount="indefinite" />
                    </circle>
                  </svg>
                  Live
                </>
              ) : chip.label}
            </button>
          );
        })}
      </div>

      <div style={{ padding:"14px 16px 0" }}>
        {feedLoading && feedPicks.length === 0 ? (
          <>
            {[0,1,2].map(i => <PickCardSkeleton key={i} />)}
          </>
        ) : feedPicks.length === 0 ? (
          <div style={{
            borderRadius:16, padding:"28px 20px",
            background: T.card, border:`1px solid ${T.border}`,
            textAlign:"center",
          }}>
            <div style={{
              width:44, height:44, margin:"0 auto 12px", borderRadius:12,
              background:"rgba(255,107,61,0.10)", border:"1px solid rgba(255,107,61,0.18)",
              display:"flex", alignItems:"center", justifyContent:"center",
            }}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={T.ember} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 12h-4l-3 9L9 3l-3 9H2"/>
              </svg>
            </div>
            <div style={{ fontFamily:FD, fontSize:16, fontWeight:700, color:T.ink, marginBottom:6, letterSpacing:"-0.01em" }}>
              {feedFilter === "live"     ? "Nothing live right now"
              : feedFilter === "pending" ? "Nothing locked in"
              :                            "No settled picks"}
            </div>
            <div style={{ fontSize:13, color:T.inkMuted, lineHeight:1.5, maxWidth:340, margin:"0 auto" }}>
              {feedFilter === "live"
                ? "Picks show up here while their games are in progress."
              : feedFilter === "pending"
                ? "Picks lock in here while waiting on game time or grading."
                : "Settled picks show up here once games finish."}
            </div>
            {feedFilter === "live" && (
              <button
                onClick={() => { haptic.light(); setActivePage("Games"); }}
                style={{
                  marginTop:16, padding:"10px 18px", borderRadius:10,
                  background: T.ember, color:"#fff",
                  border:"none", cursor:"pointer",
                  fontSize:13, fontWeight:700, letterSpacing:"-0.005em",
                  fontFamily:FB,
                }}
              >
                Make a pick →
              </button>
            )}
          </div>
        ) : (
          <>
            {feedPicks.map(pick => (
              <FeedPickCard
                key={pick.id}
                pick={pick}
                viewer={user}
                viewerLeagues={userLeagues}
                onProfile={(uid, username) => onViewProfile?.(uid, username)}
                onComment={(pick) => { haptic.light(); setCommentsTarget(pick); }}
                onHopOn={(pick) => { haptic.light(); setHopTarget(pick); }}
              />
            ))}
            {hasMore && (
              <button
                onClick={() => { haptic.light(); loadMore(); }}
                style={{
                  width:"100%", padding:"12px", marginTop:4, marginBottom:8,
                  background:"rgba(255,255,255,0.03)", border:`1px solid ${T.border}`,
                  borderRadius:12, color: T.inkSec, fontFamily:FB,
                  fontSize:12, fontWeight:600, cursor:"pointer",
                }}
              >
                Load more
              </button>
            )}
          </>
        )}
      </div>

    </div>
  );
}


/* ===========================================
   PAGE: GAMES (Dashboard)
=========================================== */
/* ===========================================
   GAME DETAIL MODAL — sportsbook-style deep view
   Tabs: Main Lines | Game | Player Props
=========================================== */
function GameDetailModal({ game, onClose, picks, toggle, isPicked, oddsRefreshing, onOpenSlip, initialTab }) {
  const { closing, requestClose } = useSheetClose(onClose, 200);
  useModalOpen(!closing);  // Hide bottom nav while this modal is open
  useEffect(() => {
    const onEsc = () => requestClose();
    window.addEventListener("torch-escape", onEsc);
    return () => window.removeEventListener("torch-escape", onEsc);
  }, [requestClose]);
  const [tab, setTab] = useState(initialTab || "main");
  const [markets, setMarkets] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchGameMarkets(game.sportKey, game.id, game.homeTeamFull, game.awayTeamFull)
      .then(data => { if (!cancelled) { setMarkets(data); setLoading(false); } })
      .catch(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [game.id, game.sportKey, game.homeTeamFull, game.awayTeamFull]);

  const fmtOdds = (n) => n == null ? '—' : n > 0 ? `+${n}` : `${n}`;
  const fmtSpread = (n) => n == null ? 'PK' : n > 0 ? `+${n}` : `${n}`;

  // American odds → raw implied probability (includes the book's vig).
  const impliedProb = (american) => {
    if (american == null || isNaN(american)) return null;
    return american > 0 ? 100 / (american + 100) : (-american) / ((-american) + 100);
  };
  // De-vigged two-way win probabilities from both moneylines. Normalizing by the
  // sum strips the overround so the two sides total 100% — this is the "implied
  // win probability" split DK shows. Returns { away, home } as 0–100 ints, or null
  // if either ML is missing (e.g. a 3-way soccer market or lines not posted yet).
  // Defined as a function (not an IIFE) because awayML/homeML are parsed from
  // game.picks further down — calling at render time avoids a TDZ reference.
  const computeWinProbSplit = (aML, hML) => {
    const aRaw = impliedProb(aML?.odds);
    const hRaw = impliedProb(hML?.odds);
    if (aRaw == null || hRaw == null) return null;
    const sum = aRaw + hRaw;
    if (sum <= 0) return null;
    return { away: Math.round((aRaw / sum) * 100), home: Math.round((hRaw / sum) * 100) };
  };

  const awayScore = game.away?.score ?? null;
  const homeScore = game.home?.score ?? null;
  const showScore = game.isLive || game.completed;
  const awayWinning = showScore && awayScore != null && homeScore != null && awayScore > homeScore;
  const homeWinning = showScore && homeScore != null && awayScore != null && homeScore > awayScore;

  // Build pick option & call parent toggle
  const handlePick = (pickKey, label, odds, type, side, line, extra) => {
    if (game.completed) return;
    toggle(game, { key: pickKey, label, vig: odds, odds, type, side, line, subLabel: type, ...extra });
  };

  // Reusable odds button
  const OddsBtn = ({ pickKey, topLabel, btmLabel, odds, type, side, line, wide, extra }) => {
    const active = isPicked(game.id, pickKey);
    return (
      <button
        onClick={() => handlePick(pickKey, topLabel, odds, type, side, line, extra)}
        disabled={game.completed}
        style={{
          flex: wide ? "1 1 100%" : "1 1 0",
          minWidth: wide ? "auto" : 80,
          padding: "12px 8px",
          background: active
            ? "rgba(255,107,61,0.16)"
            : "rgba(255,255,255,0.04)",
          border: active ? "1.5px solid rgba(255,107,61,0.5)" : "1px solid rgba(255,255,255,0.08)",
          borderRadius: 12,
          cursor: game.completed ? "default" : "pointer",
          opacity: game.completed ? 0.4 : 1,
          textAlign: "center",
          transition: "all 0.15s",
        }}
      >
        <div style={{ fontSize: 14, fontWeight: 800, color: active ? T.coral : "#fff", lineHeight: 1.2 }}>
          {topLabel}
        </div>
        {btmLabel && (
          <div style={{ fontSize: 11, fontWeight: 600, color: active ? "rgba(255,107,61,0.7)" : "rgba(255,255,255,0.35)", marginTop: 2 }}>
            {btmLabel}
          </div>
        )}
      </button>
    );
  };

  // Section header
  const SectionHdr = ({ children }) => (
    <div style={{ fontSize: 12, fontWeight: 600, color: "rgba(255,255,255,0.5)", letterSpacing: "0.01em", margin: "20px 0 10px", padding: "0 2px" }}>
      {children}
    </div>
  );

  // Only show the "Game" tab when additional markets (alts, 1H, team totals) exist.
  // This avoids showing an empty "No additional game markets available" placeholder.
  const hasGameMarkets = markets && (
    (markets.altSpreads?.length ?? 0) > 0 ||
    (markets.altTotals?.length ?? 0) > 0 ||
    markets.firstHalf?.homeML != null ||
    (markets.teamTotals?.length ?? 0) > 0
  );

  // If the user was on the Game tab but it disappeared (new game has no alt markets),
  // snap back to Main Lines so they don't see stale content.
  useEffect(() => {
    if (tab === "game" && !loading && !hasGameMarkets) setTab("main");
  }, [tab, loading, hasGameMarkets]);

  const tabs = [
    { key: "main", label: "Main Lines" },
    ...(hasGameMarkets ? [{ key: "game", label: "Game" }] : []),
    { key: "players", label: "Player Props" },
  ];

  // Count picks for THIS game
  const gamePickCount = Object.values(picks).filter(p => p.gameId === game.id).length;
  const totalPickCount = Object.keys(picks).length;

  // Pre-compute main line pick options (same as game card)
  const mainPicks = game.picks || [];
  const awaySpread = mainPicks.find(p => p.key === 'awaySpread');
  const homeSpread = mainPicks.find(p => p.key === 'homeSpread');
  const over       = mainPicks.find(p => p.key === 'over');
  const under      = mainPicks.find(p => p.key === 'under');
  const awayML     = mainPicks.find(p => p.key === 'awayML');
  const homeML     = mainPicks.find(p => p.key === 'homeML');

  // Group player props by stat
  const propsByStat = useMemo(() => {
    if (!markets?.playerProps?.length) return {};
    const grouped = {};
    for (const p of markets.playerProps) {
      // Group by the DISPLAY label (statLabel) so headers read cleanly
      // ("Passing TDs"). Each prop still carries its settlement-safe `stat`
      // key, which is what gets written onto the pick — display and settlement
      // stay decoupled. Fall back to `stat` if an older payload lacks statLabel.
      const groupKey = p.statLabel || p.stat;
      if (!grouped[groupKey]) grouped[groupKey] = [];
      grouped[groupKey].push(p);
    }
    // Sort each group by line descending
    for (const k of Object.keys(grouped)) {
      grouped[k].sort((a, b) => (b.line ?? 0) - (a.line ?? 0));
    }
    return grouped;
  }, [markets?.playerProps]);

  return createPortal(
    <div style={{
      position: "fixed", top: 0, left: 0, right: 0, bottom: 0, zIndex: 120,
      height: "100dvh",
      background: "rgba(0,0,0,0.7)", backdropFilter: "blur(8px)",
      display: "flex", flexDirection: "column",
      animation: closing ? "fadeExitDown 0.2s cubic-bezier(0.4,0,1,1) both" : "fadeUp 0.2s ease",
      pointerEvents: closing ? "none" : "auto",
    }}>
      <div style={{
        flex: 1, maxWidth: 640, width: "100%", height: "100%", margin: "0 auto",
        background: "#0D0E12", display: "flex", flexDirection: "column",
        minHeight: 0,
      }}>
        {/* ── HEADER ── */}
        <div style={{
          padding: "calc(16px + env(safe-area-inset-top, 0px)) 16px 12px",
          background: "#15161D",
          borderBottom: "1px solid rgba(255,255,255,0.06)",
        }}>
          {/* Top bar */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
            <button onClick={() => requestClose()} className="no-press" style={{
              background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.06)",
              borderRadius: 10, padding: "7px 12px 7px 9px", cursor: "pointer",
              color: "rgba(255,255,255,0.75)", fontSize: 13, fontWeight: 600,
              display: "flex", alignItems: "center", gap: 5,
              letterSpacing:"-0.005em",
              transition:"background 0.15s, transform 0.12s",
            }}
              onMouseDown={(e)=>(e.currentTarget.style.transform="scale(0.97)")}
              onMouseUp={(e)=>(e.currentTarget.style.transform="scale(1)")}
              onMouseLeave={(e)=>(e.currentTarget.style.transform="scale(1)")}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round"><polyline points="15 18 9 12 15 6"/></svg>
              Games
            </button>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              {gamePickCount > 0 && (
                <span style={{
                  fontSize: 11, fontWeight: 700, padding: "4px 10px", borderRadius: 999,
                  background: "rgba(255,107,61,0.10)",
                  color: T.coral, border: "1px solid rgba(255,107,61,0.25)",
                  display: "flex", alignItems: "center", gap: 4,
                  animation: "fadeUp 0.2s ease",
                  fontVariantNumeric:"tabular-nums",
                }}>
                  {gamePickCount} {gamePickCount === 1 ? "pick" : "picks"}
                </span>
              )}
              {game.isLive && !game.completed ? (
                <span style={{
                  display: "flex", alignItems: "center", gap: 5,
                  background: "rgba(74,222,128,0.10)", border: "1px solid rgba(74,222,128,0.25)",
                  padding: "3px 10px 3px 8px", borderRadius: 999,
                }}>
                  <span style={{ position:"relative", width:6, height:6, borderRadius:"50%", background: "#4ade80" }}>
                    <span style={{ position:"absolute", inset:-3, borderRadius:"50%", background:"rgba(74,222,128,0.4)", animation:"sonarPing 1.6s ease-out infinite" }}/>
                  </span>
                  <span style={{ fontSize: 10.5, fontWeight: 800, color: "#4ade80", letterSpacing:"0.06em" }}>LIVE</span>
                </span>
              ) : (
                <span style={{
                  fontSize: 10, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase",
                  padding: "3px 9px 3px 7px", borderRadius: 6,
                  background: "rgba(255,255,255,0.05)",
                  color: "rgba(255,255,255,0.65)",
                  border: "1px solid rgba(255,255,255,0.06)",
                  display:"inline-flex", alignItems:"center", gap:5,
                  fontFamily:FB,
                }}>
                  <span style={{ width:5, height:5, borderRadius:"50%", background: T.coral, boxShadow:`0 0 6px ${T.coral}88` }}/>
                  {game.sport}
                </span>
              )}
            </div>
          </div>

          {/* Teams */}
          <div style={{ display: "flex", alignItems: "stretch", gap: 12 }}>
            {/* Away */}
            <div style={{ flex: 1, textAlign: "center", padding:"4px 0" }}>
              <div style={{ position:"relative", display:"inline-block", marginBottom:10 }}>
                <TeamLogo name={game.away.full} sport={game.sport} size={56} logoUrl={game.awayLogo}
                  style={{ position:"relative", border: "1px solid rgba(255,255,255,0.08)" }} />
              </div>
              <div style={{
                fontFamily:FD, fontSize: 14, fontWeight: 700,
                color: awayWinning ? "#fff" : "rgba(255,255,255,0.85)",
                lineHeight: 1.2, marginBottom: 4, letterSpacing:"-0.005em",
                padding:"0 4px", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap",
              }}>
                {game.away.full}
              </div>
              {(game.playoff || game.awayRecord) && (
                <div style={{
                  fontSize: 9.5, fontWeight: 700,
                  color: game.playoff ? "rgba(255,107,61,0.65)" : "rgba(255,255,255,0.35)",
                  textTransform: "uppercase", letterSpacing: "0.10em", fontFamily:FB,
                  fontVariantNumeric:"tabular-nums",
                }}>
                  {game.playoff
                    ? (game.playoff.round ? `${game.playoff.round} · ${game.playoff.awayStatus}` : game.playoff.awayStatus)
                    : game.awayRecord}
                </div>
              )}
              {game.awayPitcher?.name && !game.completed && (
                <div style={{ fontSize: 10, fontWeight: 600, color: "rgba(255,255,255,0.40)", marginTop: 4, lineHeight: 1.2 }}>
                  <span>{game.awayPitcher.name}</span>
                  {game.awayPitcher.era && <span style={{ color: "rgba(255,255,255,0.25)", marginLeft: 4, fontFamily:FM, fontVariantNumeric:"tabular-nums" }}>{game.awayPitcher.era}</span>}
                </div>
              )}
              {!game.awayPitcher?.name && !game.completed && (game.sport||"").toUpperCase() === "MLB" && (
                <div style={{ fontSize: 10, fontWeight: 600, color: "rgba(255,255,255,0.20)", marginTop: 4 }}>SP: TBD</div>
              )}
              {showScore && (
                <div style={{
                  fontFamily: FD, fontSize: 38, fontWeight: 700,
                  color: awayWinning ? "#fff" : "rgba(255,255,255,0.45)",
                  letterSpacing:"-1px", lineHeight:1, marginTop: 8, fontVariantNumeric:"tabular-nums",
                  /* score glow removed */
                }}>{awayScore ?? '—'}</div>
              )}
            </div>

            {/* Divider */}
            <div style={{
              display: "flex", flexDirection: "column", alignItems: "center", justifyContent:"center",
              gap: 6, flexShrink: 0, alignSelf:"center",
              minHeight:56,
            }}>
              {showScore ? (
                <span style={{ fontFamily:FD, fontSize: 18, fontWeight: 700, color: "rgba(255,255,255,0.20)", letterSpacing:"-0.5px" }}>·</span>
              ) : (
                <div style={{
                  width:32, height:32, borderRadius:"50%",
                  background:"rgba(255,255,255,0.04)", border:"1px solid rgba(255,255,255,0.06)",
                  display:"flex", alignItems:"center", justifyContent:"center",
                }}>
                  <span style={{ fontSize: 12, fontWeight: 600, color: "rgba(255,255,255,0.45)", fontFamily:FD }}>@</span>
                </div>
              )}
              <div style={{
                fontSize: 10.5, fontWeight: 600,
                color: game.isLive ? "#4ade80" : "rgba(255,255,255,0.45)",
                fontFamily:FB, letterSpacing:"-0.005em",
                whiteSpace:"nowrap", textAlign:"center",
              }}>
                {game.completed ? "Final" : game.isLive
                  ? (game.livePeriod?.periodLabel
                    ? `${game.livePeriod.periodLabel}${game.livePeriod.clockLabel ? " · " + game.livePeriod.clockLabel : ""}`
                    : "Live")
                  : game.time}
              </div>
              {game.isLive && game.liveState && (
                <div style={{ marginTop:8, display:"flex", justifyContent:"center" }}>
                  <LiveSituation liveState={game.liveState} />
                </div>
              )}
            </div>

            {/* Home */}
            <div style={{ flex: 1, textAlign: "center", padding:"4px 0" }}>
              <div style={{ position:"relative", display:"inline-block", marginBottom:10 }}>
                <TeamLogo name={game.home.full} sport={game.sport} size={56} logoUrl={game.homeLogo}
                  style={{ position:"relative", border: "1px solid rgba(255,255,255,0.08)" }} />
              </div>
              <div style={{
                fontFamily:FD, fontSize: 14, fontWeight: 700,
                color: homeWinning ? "#fff" : "rgba(255,255,255,0.85)",
                lineHeight: 1.2, marginBottom: 4, letterSpacing:"-0.005em",
                padding:"0 4px", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap",
              }}>
                {game.home.full}
              </div>
              {(game.playoff || game.homeRecord) && (
                <div style={{
                  fontSize: 9.5, fontWeight: 700,
                  color: game.playoff ? "rgba(255,107,61,0.65)" : "rgba(255,255,255,0.35)",
                  textTransform: "uppercase", letterSpacing: "0.10em", fontFamily:FB,
                  fontVariantNumeric:"tabular-nums",
                }}>
                  {game.playoff
                    ? (game.playoff.round ? `${game.playoff.round} · ${game.playoff.homeStatus}` : game.playoff.homeStatus)
                    : game.homeRecord}
                </div>
              )}
              {game.homePitcher?.name && !game.completed && (
                <div style={{ fontSize: 10, fontWeight: 600, color: "rgba(255,255,255,0.40)", marginTop: 4, lineHeight: 1.2 }}>
                  <span>{game.homePitcher.name}</span>
                  {game.homePitcher.era && <span style={{ color: "rgba(255,255,255,0.25)", marginLeft: 4, fontFamily:FM, fontVariantNumeric:"tabular-nums" }}>{game.homePitcher.era}</span>}
                </div>
              )}
              {!game.homePitcher?.name && !game.completed && (game.sport||"").toUpperCase() === "MLB" && (
                <div style={{ fontSize: 10, fontWeight: 600, color: "rgba(255,255,255,0.20)", marginTop: 4 }}>SP: TBD</div>
              )}
              {showScore && (
                <div style={{
                  fontFamily: FD, fontSize: 38, fontWeight: 700,
                  color: homeWinning ? "#fff" : "rgba(255,255,255,0.45)",
                  letterSpacing:"-1px", lineHeight:1, marginTop: 8, fontVariantNumeric:"tabular-nums",
                  /* score glow removed */
                }}>{homeScore ?? '—'}</div>
              )}
            </div>
          </div>
        </div>

        {/* ── TAB BAR ── */}
        <div style={{
          display: "flex", gap: 0, padding:"4px",
          borderBottom: "1px solid rgba(255,255,255,0.06)",
          background: "transparent",
          position:"relative",
        }}>
          {tabs.map(t => (
            <button key={t.key} onClick={() => setTab(t.key)} className="no-press" style={{
              flex: 1, padding: "11px 0", border: "none", cursor: "pointer",
              fontSize: 13, fontWeight: tab === t.key ? 700 : 500,
              color: tab === t.key ? "#fff" : "rgba(255,255,255,0.45)",
              letterSpacing:"-0.005em",
              background: "transparent", position: "relative",
              transition: "color 0.15s",
            }}>
              {t.label}
              {tab === t.key && (
                <div style={{
                  position: "absolute", bottom: 0, left: "20%", right: "20%", height: 2,
                  background: T.coral, borderRadius: 1,
                }}/>
              )}
            </button>
          ))}
        </div>

        {/* ── CONTENT ── */}
        <div style={{ flex: 1, overflow: "auto", padding: "0 16px 16px", minHeight: 0, WebkitOverflowScrolling: "touch", overscrollBehavior:"contain", touchAction:"pan-y" }}>
          {/* MAIN LINES TAB */}
          {tab === "main" && (() => {
            // Detect "void" state — when game.picks hasn't been populated yet,
            // every section is empty and we get a screen of pure black (the bug
            // visible in the TestFlight recording on the Main Lines tab).
            // Show a skeleton for a moment, then a friendly empty state if odds
            // genuinely aren't available.
            const hasAnyMain = !!(awaySpread || homeSpread || over || under || awayML || homeML);
            if (!hasAnyMain) {
              return (
                <div>
                  {loading ? (
                    // Skeleton: 3 sections of 2 buttons each, mimicking the real layout
                    <>
                      {[0,1,2].map(i => (
                        <div key={i} style={{ marginTop: i === 0 ? 16 : 22 }}>
                          <div style={{ height:11, width:80, borderRadius:4, background:"rgba(255,255,255,0.05)", animation:"skeletonPulse 1.4s ease infinite", animationDelay:`${i*120}ms`, marginBottom:10 }}/>
                          <div style={{ display:"flex", gap:8 }}>
                            <div style={{ flex:1, height:64, borderRadius:12, background:"rgba(255,255,255,0.04)", animation:"skeletonPulse 1.4s ease infinite", animationDelay:`${i*120 + 60}ms` }}/>
                            <div style={{ flex:1, height:64, borderRadius:12, background:"rgba(255,255,255,0.04)", animation:"skeletonPulse 1.4s ease infinite", animationDelay:`${i*120 + 120}ms` }}/>
                          </div>
                        </div>
                      ))}
                    </>
                  ) : (
                    <div style={{ padding:"56px 24px", textAlign:"center" }}>
                      <div style={{ fontSize:14, fontWeight:700, color:"rgba(255,255,255,0.75)", marginBottom:6 }}>Lines aren't posted yet</div>
                      <div style={{ fontSize:12, color:"rgba(255,255,255,0.40)", lineHeight:1.5, maxWidth:250, margin:"0 auto" }}>Odds for this game open closer to game time. Check back later.</div>
                    </div>
                  )}
                </div>
              );
            }
            return (
              <div>
                {/* ── PREVIEW: implied win probability ──────────────────────────
                    A de-vigged two-way win-probability bar derived from the
                    moneyline (DK image 2). Only renders for 2-way games where
                    both MLs are posted — skipped for 3-way soccer, futures, or
                    when lines aren't out. Records show beneath when available.
                    No fabricated stats (venue/PPG/etc.) — only data we actually
                    have flows in. */}
                {(() => {
                  const wp = computeWinProbSplit(awayML, homeML);
                  if (!wp) return null;
                  const awayName = game.away?.short || game.away?.full || "Away";
                  const homeName = game.home?.short || game.home?.full || "Home";
                  const favAway = wp.away >= wp.home;
                  return (
                    <div style={{ margin:"4px 0 8px" }}>
                      <div style={{ fontSize:11, fontWeight:600, color:"rgba(255,255,255,0.45)",
                        letterSpacing:"0.02em", textAlign:"center", marginBottom:9, fontFamily:FB }}>
                        Implied Win Probability
                      </div>
                      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"baseline", marginBottom:6 }}>
                        <span style={{ fontSize:12, fontWeight:700, color:"rgba(255,255,255,0.80)", fontFamily:FB }}>{awayName}</span>
                        <span style={{ fontSize:12, fontWeight:700, color:"rgba(255,255,255,0.80)", fontFamily:FB }}>{homeName}</span>
                      </div>
                      {/* Two-tone split bar. Coral = away, neutral track = home,
                          so the favorite's share reads at a glance. Flat fills,
                          no gradient, matching the card language. */}
                      <div style={{ display:"flex", height:8, borderRadius:5, overflow:"hidden",
                        background:"rgba(255,255,255,0.06)" }}>
                        <div style={{ width:`${wp.away}%`, background: T.coral, transition:"width 0.3s ease" }}/>
                        <div style={{ width:`${wp.home}%`, background:"rgba(255,255,255,0.22)", transition:"width 0.3s ease" }}/>
                      </div>
                      <div style={{ display:"flex", justifyContent:"space-between", marginTop:6 }}>
                        <span style={{ fontSize:13, fontWeight:800, fontFamily:FM, fontVariantNumeric:"tabular-nums",
                          color: favAway ? T.coral : "rgba(255,255,255,0.55)" }}>{wp.away}%</span>
                        <span style={{ fontSize:13, fontWeight:800, fontFamily:FM, fontVariantNumeric:"tabular-nums",
                          color: !favAway ? "#fff" : "rgba(255,255,255,0.55)" }}>{wp.home}%</span>
                      </div>
                      {(game.awayRecord || game.homeRecord) && (
                        <div style={{ display:"flex", justifyContent:"space-between", marginTop:8,
                          paddingTop:8, borderTop:"1px solid rgba(255,255,255,0.05)" }}>
                          <span style={{ fontSize:11, fontWeight:600, color:"rgba(255,255,255,0.42)", fontFamily:FB }}>{game.awayRecord || "—"}</span>
                          <span style={{ fontSize:11, fontWeight:600, color:"rgba(255,255,255,0.42)", fontFamily:FB }}>{game.homeRecord || "—"}</span>
                        </div>
                      )}
                    </div>
                  );
                })()}

                <SectionHdr>Spread</SectionHdr>
                <div style={{ display: "flex", gap: 8 }}>
                  {awaySpread && <OddsBtn pickKey="awaySpread" topLabel={awaySpread.label} btmLabel={fmtOdds(awaySpread.vig)} odds={awaySpread.vig} type="spread" side="away" line={awaySpread.line} />}
                  {homeSpread && <OddsBtn pickKey="homeSpread" topLabel={homeSpread.label} btmLabel={fmtOdds(homeSpread.vig)} odds={homeSpread.vig} type="spread" side="home" line={homeSpread.line} />}
                </div>

                <SectionHdr>Total (Over/Under)</SectionHdr>
                <div style={{ display: "flex", gap: 8 }}>
                  {over && <OddsBtn pickKey="over" topLabel={over.label} btmLabel={fmtOdds(over.vig)} odds={over.vig} type="total" side="over" line={over.line} />}
                  {under && <OddsBtn pickKey="under" topLabel={under.label} btmLabel={fmtOdds(under.vig)} odds={under.vig} type="total" side="under" line={under.line} />}
                </div>

                <SectionHdr>Moneyline</SectionHdr>
                <div style={{ display: "flex", gap: 8 }}>
                  {awayML && <OddsBtn pickKey="awayML" topLabel={`${game.away.short} ${awayML.label}`} odds={awayML.odds} type="ml" side="away" line={null} />}
                  {homeML && <OddsBtn pickKey="homeML" topLabel={`${game.home.short} ${homeML.label}`} odds={homeML.odds} type="ml" side="home" line={null} />}
                </div>
              </div>
            );
          })()}

          {/* GAME TAB — alt lines, 1H, team totals */}
          {tab === "game" && (
            <div>
              {loading ? (
                // Skeleton mirroring the alt-market layout (section label + a
                // pair of buttons), consistent with the Main and Players tabs
                // instead of a lone "Pulling odds…" line.
                <div style={{ paddingTop:16 }}>
                  {[0,1,2].map(i => (
                    <div key={i} style={{ marginTop: i === 0 ? 0 : 22 }}>
                      <div style={{ height:11, width:90, borderRadius:4, background:"rgba(255,255,255,0.05)", animation:"skeletonPulse 1.4s ease infinite", animationDelay:`${i*120}ms`, marginBottom:10 }}/>
                      <div style={{ display:"flex", gap:8 }}>
                        <div style={{ flex:1, height:64, borderRadius:12, background:"rgba(255,255,255,0.04)", animation:"skeletonPulse 1.4s ease infinite", animationDelay:`${i*120 + 60}ms` }}/>
                        <div style={{ flex:1, height:64, borderRadius:12, background:"rgba(255,255,255,0.04)", animation:"skeletonPulse 1.4s ease infinite", animationDelay:`${i*120 + 120}ms` }}/>
                      </div>
                    </div>
                  ))}
                </div>
              ) : !markets ? (
                <div style={{ padding:"56px 24px", textAlign:"center" }}>
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.30)" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom:10, display:"inline-block" }}>
                    <line x1="3" y1="9" x2="21" y2="9"/><line x1="9" y1="21" x2="9" y2="9"/><rect x="3" y="3" width="18" height="18" rx="2"/>
                  </svg>
                  <div style={{ fontSize:14, fontWeight:700, color:"rgba(255,255,255,0.75)", marginBottom:4 }}>No extra markets yet</div>
                  <div style={{ fontSize:12, color:"rgba(255,255,255,0.40)", lineHeight:1.5, maxWidth:240, margin:"0 auto" }}>Alternate lines and team totals open closer to game time.</div>
                </div>
              ) : (<>
                {/* First Half */}
                {(markets.firstHalf.homeML != null || markets.firstHalf.homeSpread != null) && (<>
                  <SectionHdr>1st Half Spread</SectionHdr>
                  {markets.firstHalf.homeSpread != null && (
                    <div style={{ display: "flex", gap: 8 }}>
                      <OddsBtn pickKey="1h_awaySpread" topLabel={fmtSpread(markets.firstHalf.homeSpread != null ? -markets.firstHalf.homeSpread : null)}
                        btmLabel={fmtOdds(markets.firstHalf.awaySpreadOdds)} odds={markets.firstHalf.awaySpreadOdds} type="spread_1h" side="away" line={markets.firstHalf.homeSpread ? -markets.firstHalf.homeSpread : null} />
                      <OddsBtn pickKey="1h_homeSpread" topLabel={fmtSpread(markets.firstHalf.homeSpread)}
                        btmLabel={fmtOdds(markets.firstHalf.homeSpreadOdds)} odds={markets.firstHalf.homeSpreadOdds} type="spread_1h" side="home" line={markets.firstHalf.homeSpread} />
                    </div>
                  )}

                  <SectionHdr>1st Half Total</SectionHdr>
                  {markets.firstHalf.total != null && (
                    <div style={{ display: "flex", gap: 8 }}>
                      <OddsBtn pickKey="1h_over" topLabel={`O ${markets.firstHalf.total}`} btmLabel={fmtOdds(markets.firstHalf.overOdds)} odds={markets.firstHalf.overOdds} type="total_1h" side="over" line={markets.firstHalf.total} />
                      <OddsBtn pickKey="1h_under" topLabel={`U ${markets.firstHalf.total}`} btmLabel={fmtOdds(markets.firstHalf.underOdds)} odds={markets.firstHalf.underOdds} type="total_1h" side="under" line={markets.firstHalf.total} />
                    </div>
                  )}

                  <SectionHdr>1st Half Moneyline</SectionHdr>
                  <div style={{ display: "flex", gap: 8 }}>
                    {markets.firstHalf.awayML != null && <OddsBtn pickKey="1h_awayML" topLabel={`${game.away.short} ${fmtOdds(markets.firstHalf.awayML)}`} odds={markets.firstHalf.awayML} type="ml_1h" side="away" line={null} />}
                    {markets.firstHalf.homeML != null && <OddsBtn pickKey="1h_homeML" topLabel={`${game.home.short} ${fmtOdds(markets.firstHalf.homeML)}`} odds={markets.firstHalf.homeML} type="ml_1h" side="home" line={null} />}
                    {markets.firstHalf.drawML != null && <OddsBtn pickKey="1h_drawML" topLabel={`Draw ${fmtOdds(markets.firstHalf.drawML)}`} odds={markets.firstHalf.drawML} type="ml_1h" side="draw" line={null} />}
                  </div>
                </>)}

                {/* Alternate Spreads */}
                {markets.altSpreads.length > 0 && (<>
                  <SectionHdr>Alternate Spreads</SectionHdr>
                  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    {/* Group alt spreads into home/away pairs by point value */}
                    {(() => {
                      const homeAlts = markets.altSpreads.filter(s => s.side === 'home').sort((a, b) => a.point - b.point);
                      return homeAlts.slice(0, 8).map((hs, i) => {
                        const awayLine = hs.point != null ? -hs.point : null;
                        const as = markets.altSpreads.find(s => s.side === 'away' && s.point === awayLine);
                        return (
                          <div key={i} style={{ display: "flex", gap: 8 }}>
                            <OddsBtn pickKey={`altSpread_away_${awayLine}`} topLabel={`${game.away.short} ${fmtSpread(awayLine)}`}
                              btmLabel={fmtOdds(as?.odds)} odds={as?.odds} type="alt_spread" side="away" line={awayLine} />
                            <OddsBtn pickKey={`altSpread_home_${hs.point}`} topLabel={`${game.home.short} ${fmtSpread(hs.point)}`}
                              btmLabel={fmtOdds(hs.odds)} odds={hs.odds} type="alt_spread" side="home" line={hs.point} />
                          </div>
                        );
                      });
                    })()}
                  </div>
                </>)}

                {/* Alternate Totals */}
                {markets.altTotals.length > 0 && (<>
                  <SectionHdr>Alternate Totals</SectionHdr>
                  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    {(() => {
                      const overs = markets.altTotals.filter(t => t.side === 'over').sort((a, b) => a.point - b.point);
                      return overs.slice(0, 8).map((ov, i) => {
                        const un = markets.altTotals.find(t => t.side === 'under' && t.point === ov.point);
                        return (
                          <div key={i} style={{ display: "flex", gap: 8 }}>
                            <OddsBtn pickKey={`altTotal_over_${ov.point}`} topLabel={`O ${ov.point}`}
                              btmLabel={fmtOdds(ov.odds)} odds={ov.odds} type="alt_total" side="over" line={ov.point} />
                            <OddsBtn pickKey={`altTotal_under_${ov.point}`} topLabel={`U ${ov.point}`}
                              btmLabel={fmtOdds(un?.odds)} odds={un?.odds} type="alt_total" side="under" line={ov.point} />
                          </div>
                        );
                      });
                    })()}
                  </div>
                </>)}

                {/* Team Totals */}
                {markets.teamTotals.length > 0 && (<>
                  <SectionHdr>Team Totals</SectionHdr>
                  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    {(() => {
                      const teams = [...new Set(markets.teamTotals.map(t => t.team))];
                      return teams.map((team, i) => {
                        const ov = markets.teamTotals.find(t => t.team === team && t.side === 'over');
                        const un = markets.teamTotals.find(t => t.team === team && t.side === 'under');
                        if (!ov && !un) return null;
                        // S-2 FIX: resolve which side of the matchup this team is
                        // (home/away) AT PICK TIME and persist it, rather than
                        // leaving settlement to infer it from the label string.
                        // The OddsAPI team_totals market names teams by full name;
                        // match against the game's home/away full names, with a
                        // substring fallback for minor naming differences. If we
                        // genuinely can't resolve it, teamSide stays null and the
                        // grader falls back to its legacy label inference.
                        const norm = (s) => (s || "").toLowerCase().trim();
                        const homeFull = game.homeTeamFull || game.home?.full || "";
                        const awayFull = game.awayTeamFull || game.away?.full || "";
                        let teamSide = null;
                        if (norm(team) === norm(homeFull)) teamSide = "home";
                        else if (norm(team) === norm(awayFull)) teamSide = "away";
                        else if (homeFull && norm(homeFull).includes(norm(team))) teamSide = "home";
                        else if (awayFull && norm(awayFull).includes(norm(team))) teamSide = "away";
                        else if (homeFull && norm(team).includes(norm(homeFull))) teamSide = "home";
                        else if (awayFull && norm(team).includes(norm(awayFull))) teamSide = "away";
                        const ttExtra = teamSide ? { teamSide } : undefined;
                        return (
                          <div key={i}>
                            <div style={{ fontSize: 11, fontWeight: 700, color: "rgba(255,255,255,0.45)", marginBottom: 6, marginTop: i > 0 ? 8 : 0 }}>{team}</div>
                            <div style={{ display: "flex", gap: 8 }}>
                              {ov && <OddsBtn pickKey={`teamTotal_${team}_over`} topLabel={`O ${ov.point}`} btmLabel={fmtOdds(ov.odds)} odds={ov.odds} type="team_total" side="over" line={ov.point} extra={ttExtra} />}
                              {un && <OddsBtn pickKey={`teamTotal_${team}_under`} topLabel={`U ${un.point}`} btmLabel={fmtOdds(un.odds)} odds={un.odds} type="team_total" side="under" line={un.point} extra={ttExtra} />}
                            </div>
                          </div>
                        );
                      });
                    })()}
                  </div>
                </>)}

                {markets.altSpreads.length === 0 && markets.altTotals.length === 0 && markets.firstHalf.homeML == null && markets.teamTotals.length === 0 && (
                  <div style={{ padding:"56px 24px", textAlign:"center" }}>
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.30)" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom:10, display:"inline-block" }}>
                      <line x1="3" y1="9" x2="21" y2="9"/><line x1="9" y1="21" x2="9" y2="9"/><rect x="3" y="3" width="18" height="18" rx="2"/>
                    </svg>
                    <div style={{ fontSize:14, fontWeight:700, color:"rgba(255,255,255,0.75)", marginBottom:4 }}>No extra markets yet</div>
                    <div style={{ fontSize:12, color:"rgba(255,255,255,0.40)", lineHeight:1.5, maxWidth:240, margin:"0 auto" }}>Alternate lines and team totals open closer to game time.</div>
                  </div>
                )}
              </>)}
            </div>
          )}

          {/* PLAYER PROPS TAB */}
          {tab === "players" && (
            <div>
              {loading ? (
                // Skeleton: 5 prop rows mimicking the real layout
                <div style={{ paddingTop:8 }}>
                  <div style={{ height:11, width:90, borderRadius:4, background:"rgba(255,255,255,0.05)", animation:"skeletonPulse 1.4s ease infinite", marginBottom:12 }}/>
                  {[0,1,2,3,4].map(i => (
                    <div key={i} style={{
                      display:"flex", alignItems:"center", gap:10,
                      background:"rgba(255,255,255,0.025)", borderRadius:14,
                      padding:"12px 14px", marginBottom:8,
                      animation:`skeletonPulse 1.4s ease infinite`, animationDelay:`${i*100}ms`,
                    }}>
                      <div style={{ flex:1 }}>
                        <div style={{ height:13, width:"55%", borderRadius:4, background:"rgba(255,255,255,0.06)", marginBottom:6 }}/>
                        <div style={{ height:18, width:36, borderRadius:4, background:"rgba(255,255,255,0.06)" }}/>
                      </div>
                      <div style={{ width:62, height:48, borderRadius:10, background:"rgba(255,255,255,0.04)" }}/>
                      <div style={{ width:62, height:48, borderRadius:10, background:"rgba(255,255,255,0.04)" }}/>
                    </div>
                  ))}
                </div>
              ) : Object.keys(propsByStat).length === 0 ? (
                <div style={{ padding:"56px 24px", textAlign:"center" }}>
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.30)" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom:10, display:"inline-block" }}>
                    <rect x="8" y="2" width="8" height="4" rx="1"/>
                    <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/>
                    <path d="M9 12h6M9 16h4"/>
                  </svg>
                  <div style={{ fontSize:14, fontWeight:700, color:"rgba(255,255,255,0.75)", marginBottom:4 }}>Player props aren't posted yet</div>
                  <div style={{ fontSize:12, color:"rgba(255,255,255,0.40)", lineHeight:1.5, maxWidth:240, margin:"0 auto" }}>They open closer to game time. Check back later.</div>
                </div>
              ) : (
                (() => {
                  // Compute the ordered stat groups ONCE, so the jump-chips and the
                  // sections below share the exact same order (sport-aware relevance,
                  // then A→Z). Chips scroll to their section via a stable anchor id.
                  const orderedEntries = Object.entries(propsByStat).sort(([a], [b]) => {
                    const ra = propStatRank(game.sportKey, a);
                    const rb = propStatRank(game.sportKey, b);
                    return ra !== rb ? ra - rb : a.localeCompare(b);
                  });
                  const anchorId = (stat) => `propgrp-${game.id}-${String(stat).replace(/[^a-zA-Z0-9]/g, "")}`;
                  const jumpTo = (stat) => {
                    const el = document.getElementById(anchorId(stat));
                    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
                  };
                  return (
                    <>
                      {/* Jump chips — only when there's more than one stat group to
                          justify them. Horizontal scroll, flat pills, coral accent,
                          matches the design system. Sticky so they stay reachable. */}
                      {orderedEntries.length > 1 && (
                        <div style={{
                          position:"sticky", top:0, zIndex:2,
                          display:"flex", gap:6, overflowX:"auto", padding:"2px 0 10px",
                          background:"#0D0E12",
                          WebkitOverflowScrolling:"touch", scrollbarWidth:"none",
                        }}>
                          {orderedEntries.map(([stat]) => (
                            <button
                              key={stat}
                              onClick={() => jumpTo(stat)}
                              className="no-press"
                              style={{
                                flexShrink:0, padding:"7px 12px", borderRadius:999,
                                background:"rgba(255,255,255,0.04)",
                                border:"1px solid rgba(255,255,255,0.08)",
                                color:"rgba(255,255,255,0.75)", fontFamily:FB,
                                fontSize:12, fontWeight:700, cursor:"pointer", whiteSpace:"nowrap",
                                letterSpacing:"-0.005em", transition:"background 0.15s, border-color 0.15s, color 0.15s",
                              }}
                              onMouseEnter={(e) => { e.currentTarget.style.color = T.coral; e.currentTarget.style.borderColor = "rgba(255,107,61,0.4)"; }}
                              onMouseLeave={(e) => { e.currentTarget.style.color = "rgba(255,255,255,0.75)"; e.currentTarget.style.borderColor = "rgba(255,255,255,0.08)"; }}
                            >
                              {stat}
                            </button>
                          ))}
                        </div>
                      )}
                      {orderedEntries.map(([stat, props]) => (
                        <div key={stat} id={anchorId(stat)} style={{ scrollMarginTop:8 }}>
                          <SectionHdr>{stat}</SectionHdr>
                          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                            {props.map((p, i) => {
                        // ── Player Props row redesign ──
                        // Was: player name big, line tiny — buried the actual betting threshold.
                        // Now: player name on top, line is the BOLD focal number with
                        // the stat unit beside it. Over/Under buttons are stacked label/odds
                        // with a subtle line callout, and the active state uses a flat
                        // coral tint instead of a gradient (matches the rest of the redesign).
                        const overKey = `prop_${p.player.replace(/\s+/g, '_')}_${stat}_over`;
                        const underKey = `prop_${p.player.replace(/\s+/g, '_')}_${stat}_under`;
                        const overActive = isPicked(game.id, overKey);
                        const underActive = isPicked(game.id, underKey);
                        const lineDisplay = p.line != null ? p.line : '—';
                        // Lower-case the section's stat name for inline use ("ASSISTS" → "assists")
                        const statInline = stat.toLowerCase();
                        return (
                          <div key={p.id || i} style={{
                            display: "flex", alignItems: "center", gap: 8,
                            background: "rgba(255,255,255,0.025)",
                            border: "1px solid rgba(255,255,255,0.05)",
                            borderRadius: 14, padding: "12px 12px 12px 14px",
                            transition:"border-color 0.15s, background 0.15s",
                          }}>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontSize: 13.5, fontWeight: 600, color: "rgba(255,255,255,0.92)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", letterSpacing:"-0.005em" }}>
                                {p.player}
                              </div>
                              <div style={{ display:"flex", alignItems:"baseline", gap:5, marginTop:2 }}>
                                <span style={{
                                  fontFamily:FM, fontSize:18, fontWeight:700, color:"#fff",
                                  letterSpacing:"-0.5px", lineHeight:1, fontVariantNumeric:"tabular-nums",
                                }}>{lineDisplay}</span>
                                <span style={{ fontSize:11, color:"rgba(255,255,255,0.35)", fontWeight:500 }}>{statInline}</span>
                              </div>
                            </div>
                            <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                              {p.overOdds != null && (
                                <button
                                  onClick={() => handlePick(overKey, `${p.player} O ${p.line} ${stat}`, p.overOdds, 'player_prop', 'over', p.line, { playerName: p.player, statType: p.stat })}
                                  disabled={game.completed}
                                  className="no-press"
                                  style={{
                                    width:62, padding:"8px 0", borderRadius:10,
                                    cursor: game.completed ? "default" : "pointer",
                                    background: overActive ? "rgba(255,107,61,0.14)" : "rgba(255,255,255,0.04)",
                                    border: overActive ? "1.5px solid rgba(255,107,61,0.55)" : "1px solid rgba(255,255,255,0.08)",
                                    transition:"transform 0.12s, background 0.15s, border-color 0.15s",
                                    transform: overActive ? "scale(1.02)" : "scale(1)",
                                    
                                    display:"flex", flexDirection:"column", alignItems:"center", gap:2,
                                  }}
                                  onMouseDown={(e) => !game.completed && (e.currentTarget.style.transform = "scale(0.95)")}
                                  onMouseUp={(e) => (e.currentTarget.style.transform = overActive ? "scale(1.02)" : "scale(1)")}
                                  onMouseLeave={(e) => (e.currentTarget.style.transform = overActive ? "scale(1.02)" : "scale(1)")}
                                  onTouchStart={(e) => !game.completed && (e.currentTarget.style.transform = "scale(0.95)")}
                                  onTouchEnd={(e) => (e.currentTarget.style.transform = overActive ? "scale(1.02)" : "scale(1)")}
                                >
                                  <span style={{ fontSize:9.5, fontWeight:700, color:"rgba(255,255,255,0.45)", letterSpacing:"0.06em", textTransform:"uppercase", lineHeight:1 }}>Over</span>
                                  <span style={{
                                    fontFamily:FM, fontSize:13, fontWeight:700, lineHeight:1.1, fontVariantNumeric:"tabular-nums",
                                    color: overActive ? T.coral : "#fff",
                                  }}>{fmtOdds(p.overOdds)}</span>
                                </button>
                              )}
                              {p.underOdds != null && (
                                <button
                                  onClick={() => handlePick(underKey, `${p.player} U ${p.line} ${stat}`, p.underOdds, 'player_prop', 'under', p.line, { playerName: p.player, statType: p.stat })}
                                  disabled={game.completed}
                                  className="no-press"
                                  style={{
                                    width:62, padding:"8px 0", borderRadius:10,
                                    cursor: game.completed ? "default" : "pointer",
                                    background: underActive ? "rgba(255,107,61,0.14)" : "rgba(255,255,255,0.04)",
                                    border: underActive ? "1.5px solid rgba(255,107,61,0.55)" : "1px solid rgba(255,255,255,0.08)",
                                    transition:"transform 0.12s, background 0.15s, border-color 0.15s",
                                    transform: underActive ? "scale(1.02)" : "scale(1)",
                                    
                                    display:"flex", flexDirection:"column", alignItems:"center", gap:2,
                                  }}
                                  onMouseDown={(e) => !game.completed && (e.currentTarget.style.transform = "scale(0.95)")}
                                  onMouseUp={(e) => (e.currentTarget.style.transform = underActive ? "scale(1.02)" : "scale(1)")}
                                  onMouseLeave={(e) => (e.currentTarget.style.transform = underActive ? "scale(1.02)" : "scale(1)")}
                                  onTouchStart={(e) => !game.completed && (e.currentTarget.style.transform = "scale(0.95)")}
                                  onTouchEnd={(e) => (e.currentTarget.style.transform = underActive ? "scale(1.02)" : "scale(1)")}
                                >
                                  <span style={{ fontSize:9.5, fontWeight:700, color:"rgba(255,255,255,0.45)", letterSpacing:"0.06em", textTransform:"uppercase", lineHeight:1 }}>Under</span>
                                  <span style={{
                                    fontFamily:FM, fontSize:13, fontWeight:700, lineHeight:1.1, fontVariantNumeric:"tabular-nums",
                                    color: underActive ? T.coral : "#fff",
                                  }}>{fmtOdds(p.underOdds)}</span>
                                </button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                        </div>
                      ))}
                    </>
                  );
                })()
              )}
            </div>
          )}
        </div>

        {/* Floating slip CTA inside modal */}
        {totalPickCount > 0 && (
          <div style={{
            flexShrink: 0,
            display: "flex", justifyContent: "center",
            padding: "12px 16px calc(16px + env(safe-area-inset-bottom, 0px))",
            background: "linear-gradient(0deg, rgba(13,14,18,1) 60%, rgba(13,14,18,0) 100%)",
          }}>
            <button onClick={onOpenSlip} style={{
              display: "flex", alignItems: "center", gap: 10,
              padding: "14px 28px", borderRadius: 20,
              background: T.coral,
              border: "none", color: "#fff", fontSize: 15, fontWeight: 700,
              boxShadow: "0 4px 14px rgba(0,0,0,0.35)",
              cursor: "pointer", whiteSpace: "nowrap",
              animation: "fadeUp 0.25s ease",
              fontFamily: FD, letterSpacing: "-0.01em",
            }}>
              <span style={{
                background: "rgba(255,255,255,0.2)", borderRadius: 12,
                padding: "2px 10px", fontSize: 13, fontWeight: 800,
                minWidth: 22, textAlign: "center",
              }}>{totalPickCount}</span>
              Review Slip
            </button>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}


function RefreshTicker({ since }) {
  const [, tick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => tick(n => n + 1), 15000);
    return () => clearInterval(t);
  }, []);
  const secs = Math.floor((Date.now() - since) / 1000);
  if (secs < 60) return "just now";
  return `${Math.floor(secs / 60)}m ago`;
}

// Sport keys + displaySport + SPORT_GROUPS hoisted from inside GamesPage
// — these were re-allocated on every render (including every 30s odds-poll
// tick), and ESLint correctly flagged them as unstable memo deps. They're
// pure constants with no component-scope dependencies, so module scope is
// the right home: stable identity, zero per-render cost, ESLint-clean memos.

// Soccer leagues to group under one "Soccer" tab
const SOCCER_KEYS = new Set(['MLS','EPL','UCL','UEL','UECL','La Liga','Serie A','Bundesliga','Ligue 1','Eredivisie','Primeira','Brasileirao','Brazil B','Liga MX','Copa Lib','Copa Sud','Turkey SL','Scotland','Belgium','Swiss SL','Denmark','Sweden','Norway','Finland','Greece','Poland','Championship','EFL L1','EFL L2','EFL Cup','FA Cup','J League','K League','A-League','China SL','World Cup','WCQ','Euro','Euro Qual','Copa America','Friendlies','Nations Lge','Soccer']);
const RUGBY_KEYS = new Set(['NRL','Six Nations','Super Rugby','Rugby WC']);
const CRICKET_KEYS = new Set(['Cricket','IPL']);
const MOTOR_KEYS = new Set(['F1','NASCAR']);

const displaySport = (sport) => {
  if (!sport) return 'Other';
  if (SOCCER_KEYS.has(sport)) return 'Soccer';
  if (RUGBY_KEYS.has(sport)) return 'Rugby';
  if (CRICKET_KEYS.has(sport)) return 'Cricket';
  if (MOTOR_KEYS.has(sport)) return 'Motorsport';
  return sport;
};

// `sportHasProps(sportKey)` is imported from ./sportsApi — single source of
// truth derived from getPropKeys, so the prop-coverage list can't drift between
// the fetch layer and the UI gate.

// Sport group definitions — order matters (shown left-to-right)
const SPORT_GROUPS = [
  { key:"Basketball", icon:"🏀", leagues:["NBA","WNBA","NCAAB"] },
  { key:"Football",   icon:"🏈", leagues:["NFL","NCAAF"] },
  { key:"Hockey",     icon:"🏒", leagues:["NHL"] },
  { key:"Baseball",   icon:"⚾", leagues:["MLB"] },
  { key:"Soccer",     icon:"⚽", leagues:["Soccer"] },
  { key:"Combat",     icon:"🥊", leagues:["MMA","Boxing"] },
  { key:"Golf",       icon:"⛳", leagues:["Golf","US Open"] },
  { key:"Horse Racing", icon:"🐎", leagues:["Derby"] },
  { key:"Tennis",     icon:"🎾", leagues:["Tennis"] },
  { key:"Motorsport", icon:"🏎️", leagues:["Motorsport"] },
  { key:"AFL",        icon:"🏉", leagues:["AFL"] },
  { key:"Rugby",      icon:"🏉", leagues:["Rugby"] },
  { key:"Cricket",    icon:"🏏", leagues:["Cricket"] },
];

function GamesPage({ picks, setPicks, onGamesLoaded, userStats, userLeagues = [], leaguesLoaded = false, setActivePage, setSlipOpen, activePage }) {
  const [filter, setFilter] = useState(null); // null = first available sport
  const preLiveFilterRef = useRef(null); // stores filter value before entering Live mode
  const [expanded, setExpanded] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const [sportPickerOpen, setSportPickerOpen] = useState(false);
  const [selectedGameDetail, setSelectedGameDetail] = useState(null);
  // Which tab GameDetailModal should open to. Set by row entry points (e.g. the
  // "Player Props" affordance opens straight to the props tab). Cleared on close.
  const [detailInitialTab, setDetailInitialTab] = useState("main");
  const openGameDetail = useCallback((g, tab = "main") => {
    setDetailInitialTab(tab);
    setSelectedGameDetail(g);
  }, []);
  const [showCompleted, setShowCompleted] = useState(false); // FIX: hoisted from IIFE — hooks can't be in callbacks
  const [teamSearch, setTeamSearch] = useState("");
  const [liveOddsMap, setLiveOddsMap] = useState({}); // { [gameId]: updatedOddsFields }
  const [oddsRefreshing, setOddsRefreshing] = useState(false);
  const [lastOddsUpdate, setLastOddsUpdate] = useState(null); // Date | null
  const { games, loading, error, retry } = useGames();
  const { user } = useAuth();
  const [lastRefresh, setLastRefresh] = useState(Date.now());
  useEffect(() => { setLastRefresh(Date.now()); }, [games.length]);

  // Keep the open GameDetailModal in sync with live polling.
  //
  // selectedGameDetail is a React state snapshot captured when the user
  // taps a game. Without this effect, the modal's score/period/clock
  // would freeze at the value present at open time -- even though the
  // parent's `games` array updates every 30s via the live-score poll.
  //
  // Real-world bug this fixes (5/19 Cavs/Knicks Game 1): user opened
  // the detail screen while game was 101-101 end of regulation; game
  // went into OT; modal kept showing 101-101 forever because nothing
  // re-read from the updated `games` array.
  //
  // Strategy: find the matching game by id, diff the volatile fields,
  // re-set selectedGameDetail only if something changed. Diff prevents
  // unnecessary re-renders (and the resulting infinite-update loop a
  // naive `setSelectedGameDetail(updated)` would cause).
  //
  // If the game disappears from `games` (rare -- only happens if the
  // API stops returning it), we leave the snapshot alone rather than
  // auto-closing the modal -- that would feel like a glitch.
  useEffect(() => {
    if (!selectedGameDetail?.id) return;
    const fresh = games.find(g => g.id === selectedGameDetail.id);
    if (!fresh) return;
    const same = (
      fresh.home?.score === selectedGameDetail.home?.score &&
      fresh.away?.score === selectedGameDetail.away?.score &&
      fresh.period     === selectedGameDetail.period &&
      fresh.clock      === selectedGameDetail.clock &&
      fresh.isLive     === selectedGameDetail.isLive &&
      fresh.completed  === selectedGameDetail.completed
    );
    if (!same) setSelectedGameDetail(fresh);
  }, [games, selectedGameDetail?.id]);

  const [leagueCtx, setLeagueCtx] = useState(null);
  // Resolve the scoring format of the league we're picking for. leagueCtx is a
  // localStorage snapshot ({id, name, sport}) written when the user taps into a
  // league — it deliberately does NOT carry scoringMode, because a snapshot goes
  // stale if the league changes. Look the live league up by id instead.
  //
  // In pickem the board must show ONLY the moneyline: handleCreate forces
  // allowedBetTypes to ["ml"] for these leagues, so rendering Spread/Total
  // columns offers picks the league rejects — the slip's bet-type enforcement
  // then blocks the whole slip at submit time with "This league allows
  // moneylines only". That's a dead end: the user builds a slip and only
  // discovers it's invalid after committing.
  const boardScoringMode = (() => {
    if (!leagueCtx?.id) return "wager";
    const l = (userLeagues || []).find(x => x.id === leagueCtx.id);
    // PICKEM-MAKE-PICKS: fall back to the mode carried in the handoff.
    return l?.scoringMode || leagueCtx.scoringMode || "wager";
  })();
  const boardIsPickem = boardScoringMode === "pickem" || boardScoringMode === "survivor";

  // ── LEAGUE SPORT LOCK ──────────────────────────────────────────────────────
  // When the board is opened from a sport-restricted league ("Make picks"), the
  // sport filter was only ever a DEFAULT: it preselected NFL, but every other
  // sport tile stayed live. Tapping "NBA" happily built a slip the league would
  // reject at submit ("This league is NFL only") — the user discovers the pick
  // is invalid only after committing to it. The codebase already calls this out
  // as a dead-end antipattern (see the boardScoringMode comment above re: the
  // spread/total columns). Same fix: don't offer what the league can't accept.
  //
  // Returns a Set of display names ("NFL") the league permits, or null for no
  // restriction. Reads all three shapes a league can express this in:
  //   allowedSports[] — the current multi-sport wizard field
  //   sportFilter     — legacy single sport, written from a template's eventSport
  //   eventSport      — template-native field on older docs
  // Order matters: allowedSports is authoritative when present and non-empty
  // (an empty array means "all sports" and must NOT lock anything).
  const leagueSportLock = useMemo(() => {
    if (!leagueCtx?.id) return null;
    // PICKEM-MAKE-PICKS: pick'em / survivor are NFL-only by definition (the
    // week-slate board and submitPickCallable both assume it), whatever the
    // league doc's sport fields say -- older pick'em leagues can predate the
    // wizard pinning allowedSports.
    if (boardIsPickem) return new Set([SPORT_FILTER_MAP["americanfootball_nfl"]]);
    const l = (userLeagues || []).find(x => x.id === leagueCtx.id);
    const keys = l
      ? ((Array.isArray(l.allowedSports) && l.allowedSports.length > 0)
          ? l.allowedSports
          : (l.sportFilter || l.eventSport)
            ? [l.sportFilter || l.eventSport]
            : [])
      : (Array.isArray(leagueCtx.sports) ? leagueCtx.sports : []);
    if (keys.length === 0) return null;
    const names = keys.map(k => SPORT_FILTER_MAP[k]).filter(Boolean);
    // If none of the keys map to a known display name, fail OPEN rather than
    // locking the user out of a board entirely.
    return names.length > 0 ? new Set(names) : null;
  }, [leagueCtx?.id, leagueCtx?.sports, userLeagues, boardIsPickem]);
  // Read the league→board handoff (sport filter + league context) that the
  // league page writes to localStorage before flipping to this tab.
  //
  // MUST key on activePage, NOT []. The app uses mount-once / toggle-visibility
  // routing (see VISITED PAGES in TorchApp): GamesPage mounts on first visit and
  // then stays mounted forever behind `display:none`. A []-dep effect therefore
  // fires exactly once — on first visit, when no handoff keys exist yet — and
  // never again. Every subsequent "Make picks" wrote keys that nothing read, so
  // leagueCtx stayed null, boardScoringMode fell back to "wager", and the board
  // rendered unfiltered and unwindowed: every sport pickable in a pickem league.
  //
  // Keying on activePage re-runs this each time Games becomes visible, which is
  // exactly when a handoff may be waiting. The keys are still consumed once
  // (removeItem), so returning to Games later without a handoff is a no-op and
  // preserves whatever filter the user last chose.
  useEffect(() => {
    if (activePage !== undefined && activePage !== "Games") return;
    try {
      const intent = localStorage.getItem("torch_games_filter");
      if (intent) { localStorage.removeItem("torch_games_filter"); setFilter(intent); }
      const ctx = localStorage.getItem("torch_games_league_ctx");
      if (ctx) {
        localStorage.removeItem("torch_games_league_ctx");
        try { setLeagueCtx(JSON.parse(ctx)); } catch {}
      } else {
        // No handoff waiting means the user reached Games some other way —
        // the bottom-nav tab. That's the unrestricted global board, so drop any
        // league context from a previous "Make picks".
        //
        // This is what makes the sport lock escapable now that "Show All" is
        // gone. leagueCtx has no other clear path (it's set here and nowhere
        // else), and the page never unmounts under visibility routing, so
        // without this a locked context would survive for the whole session:
        // tapping Games in the nav would keep showing the NFL-only board with
        // no way back to all sports.
        setLeagueCtx(null);
      }
    } catch {}
  }, [activePage]);

  // FIX #7: only propagate games to root when count changes, not on every
  // score/odds update. Previously every poll cycle re-rendered TorchApp + PickSlipDrawer.
  const prevGameCountRef = useRef(0);
  useEffect(() => {
    if (onGamesLoaded && games.length > 0 && games.length !== prevGameCountRef.current) {
      prevGameCountRef.current = games.length;
      onGamesLoaded(games);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [games.length]);

  // -- Live odds polling — 30s when live games present ------------------------
  // FIX #3: depend on live game *count* — not the full games array reference.
  // games is a new array object on every score update, which was tearing down
  // and restarting the 30s interval on every poll — effectively preventing it
  // from ever firing. A stable ref captures the latest games for use inside poll().
  const gamesForOddsRef = useRef(games);
  useEffect(() => { gamesForOddsRef.current = games; }, [games]);
  // liveGameCount drives the 30s odds-polling cadence below. Memoized so the
  // effect dep stays stable across renders that don't actually change the
  // live-game roster — otherwise score/odds updates would tear down and
  // restart the interval (see FIX #3 comment in the effect below).
  const liveGameCount = useMemo(
    () => games.filter(g => g.isLive).length,
    [games]
  );
  // Subtly different from liveGameCount above: excludes games anomalously
  // marked both live AND completed. Used by the LIVE pill in the filter bar.
  // Kept separate (rather than reusing liveGameCount) to preserve the
  // existing strict semantics of that pill's count.
  const liveActiveCount = useMemo(
    () => games.filter(g => g.isLive && !g.completed).length,
    [games]
  );

  useEffect(() => {
    if (liveGameCount === 0) return;

    const poll = async () => {
      const liveGames = gamesForOddsRef.current.filter(g => g.isLive);
      if (liveGames.length === 0) return;
      setOddsRefreshing(true);
      try {
        const map = await fetchLiveOdds(liveGames);
        if (Object.keys(map).length > 0) { setLiveOddsMap(prev => ({ ...prev, ...map })); setLastOddsUpdate(new Date()); }
      } catch { /* silent */ }
      finally { setOddsRefreshing(false); }
    };

    poll(); // immediate
    const interval = setInterval(poll, 30_000);
    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liveGameCount]);

  // Merge live odds into a game for rendering
  const mergeOdds = (game) => {
    const lo = liveOddsMap[game.id];
    if (!lo) return game;
    // Parse period/clock out of liveTime string for livePeriod recompute
    const rawPeriod = (lo.liveTime ?? '').split(' ')[0];
    const rawClock  = (lo.liveTime ?? '').split(' ')[1];
    const hasRealPeriod = rawPeriod && rawPeriod !== 'Live';

    // Merged odds values (live overrides stale)
    const spread         = lo.spread         ?? game.spread;
    const homeSpreadOdds = lo.homeSpreadOdds ?? game.homeSpreadOdds;
    const awaySpreadOdds = lo.awaySpreadOdds ?? game.awaySpreadOdds;
    const total          = lo.total          ?? game.total;
    const overOdds       = lo.overOdds       ?? game.overOdds;
    const underOdds      = lo.underOdds      ?? game.underOdds;
    const homeML         = lo.homeML         ?? game.homeML;
    const awayML         = lo.awayML         ?? game.awayML;
    const drawML         = lo.drawML         ?? game.drawML;
    const awaySpread     = spread != null ? -spread : null;
    const fmtS = (n) => n == null ? 'PK' : n > 0 ? `+${n}` : `${n}`;
    const fmtM = (n) => n == null ? '—'  : n > 0 ? `+${n}` : `${n}`;

    return {
      ...game,
      spread, homeSpreadOdds, awaySpreadOdds,
      total, overOdds, underOdds,
      homeML, awayML, drawML,
      liveTime:       hasRealPeriod ? `${rawPeriod}${rawClock ? ' ' + rawClock : ''}` : game.liveTime,
      livePeriod:     hasRealPeriod
        ? formatLivePeriod(game.sport, rawPeriod, rawClock)
        : game.livePeriod,
      liveTimeRemaining: hasRealPeriod
        ? calcTimeRemaining(game.sport, rawPeriod, rawClock)
        : game.liveTimeRemaining,
      homeScore:      lo.homeScore      ?? game.homeScore,
      awayScore:      lo.awayScore      ?? game.awayScore,
      isLiveOdds:     lo.isLiveOdds     ?? false,
      // FIX: Rebuild picks array so inline odds grid shows live values
      picks: [
        { key:'awaySpread', label: awaySpread != null ? fmtS(awaySpread) : 'PK', vig: awaySpreadOdds, subLabel:'Spread', type:'spread', side:'away', line: awaySpread  },
        { key:'homeSpread', label: spread     != null ? fmtS(spread)     : 'PK', vig: homeSpreadOdds, subLabel:'Spread', type:'spread', side:'home', line: spread      },
        { key:'over',       label: total      != null ? `O ${total}`     : '—',  vig: overOdds,       subLabel:'Total',  type:'total',  side:'over', line: total       },
        { key:'under',      label: total      != null ? `U ${total}`     : '—',  vig: underOdds,      subLabel:'Total',  type:'total',  side:'under',line: total       },
        { key:'awayML',     label: fmtM(awayML),  vig: null, subLabel:'ML', type:'ml', side:'away', line: null, odds: awayML },
        { key:'homeML',     label: fmtM(homeML),  vig: null, subLabel:'ML', type:'ml', side:'home', line: null, odds: homeML },
        // 3-way draw (soccer/WC) — appended last, only when priced. Keeps the
        // 6-element shape intact for other sports; settlePick grades side:'draw'.
        ...(drawML != null
          ? [{ key:'drawML', label: fmtM(drawML), vig: null, subLabel:'ML', type:'ml', side:'draw', line: null, odds: drawML, isDraw: true }]
          : []),
      ],
    };
  };

  // PERF: Memoize the entire derivation chain off `games`. Without this, every
  // render (including the 30s odds-poll cycle) recomputed availableSports →
  // availableGroups → activeGroup → groupLeagues → leaguesWithGames → filtered,
  // re-allocating arrays and re-running displaySport() for every game. Now
  // each step only re-runs when its actual dependency changes.
  // Applying the lock HERE, at the head of the chain, rather than at each
  // consumer: availableGroups → activeGroup → groupLeagues → leaguesWithGames →
  // activeFilter → filtered all derive from this. Restricting the source means
  // the group row, the sub-league tiles, the default filter, and the board all
  // honour the lock automatically — there's no path left where a locked sport
  // can be selected, and no fifth consumer to forget later.
  const availableSports = useMemo(
    () => {
      const all = [...new Set(games.map(g => displaySport(g.sport)))];
      if (!leagueSportLock) return all;
      // ── LOCKED BOARD NEVER FAILS OPEN (2026-08-13) ───────────────────────
      // This used to intersect the lock with `all` and, if that intersection
      // was empty, fall back to returning `all` — every sport currently in
      // the feed, lock or no lock. The comment's stated reason ("otherwise the
      // board is empty AND has no tiles") is a real concern for a transient
      // feed gap, but it fires on the ROUTINE case for an NFL pick'em league:
      // the Odds API doesn't post regular-season odds until roughly two weeks
      // out, so any pick'em league set up before then — which is exactly when
      // commissioners set them up, to get the invite out early — has zero NFL
      // games in `games` and therefore silently drops the lock.
      //
      // Concretely: activeFilter's resolution below checks
      // `availableSports.includes(filter)`. With the fail-open, "NFL" was
      // never IN availableSports when NFL had no games (the intersection that
      // produced it was empty by definition), so that check failed and
      // activeFilter cascaded through the fallback chain to whatever OTHER
      // sport happened to have games that day — NBA, MLB, NHL. The pill row,
      // which reads from this same array, showed those sports too. A pick'em
      // league locked to NFL was therefore showing NBA and MLB game boards.
      //
      // The lock is now absolute: a locked board's sport set is always exactly
      // the league's declared sports, whether or not they currently have
      // games. Zero games in the (correctly) sole available sport is not an
      // error state — it is "Quiet day for this sport", which the empty state
      // below already renders. A board with no tiles was never actually
      // possible here: the league's own sport is always a tile.
      return [...leagueSportLock];
    },
    [games, leagueSportLock]
  );

  // Which groups have at least one available league?
  const availableGroups = useMemo(
    () => SPORT_GROUPS.filter(g => g.leagues.some(l => availableSports.includes(l))),
    [availableSports]
  );

  // Determine active group based on current filter.
  //
  // NFL in-season default: when the user hasn't manually picked a filter
  // (filter === null) AND we're inside the NFL feature window AND NFL games
  // are actually available today, default the active group to Football. This
  // makes NFL the backdrop the moment someone opens the app during the season,
  // matching the acquisition campaign's promise ("start your NFL league").
  // Outside the window, or once the user taps any sport, this branch is skipped
  // and the original "first available group" behavior is unchanged.
  const activeGroup = useMemo(
    () => {
      if (filter == null && isNflFeatureWindow() && availableSports.includes("NFL")) {
        const football = availableGroups.find(g => g.leagues.includes("NFL"));
        if (football) return football;
      }
      return availableGroups.find(g => g.leagues.includes(filter || "")) || availableGroups[0] || null;
    },
    [availableGroups, filter, availableSports]
  );

  // Leagues available within the active group
  // Show ALL leagues in the active group in the sub-row (not just ones with games today).
  // This means NFL always appears under Football, NHL always under Hockey, etc.
  // Leagues without games are shown greyed out — clicking them shows an empty state.
  const groupLeagues = useMemo(
    () => activeGroup ? activeGroup.leagues : [],
    [activeGroup]
  );

  // Default active filter to the first league in the group that actually has games.
  // If filter is already set and valid for this group, keep it.
  const leaguesWithGames = useMemo(
    () => groupLeagues.filter(l => availableSports.includes(l)),
    [groupLeagues, availableSports]
  );
  // If filter matches a group sub-league but has no games (e.g. "Golf" when only "Masters" has games),
  // auto-fall to the first sub-league that actually has games
  const activeFilter = useMemo(() => {
    // NFL in-season default: no manual filter, in-window, NFL has games → NFL.
    // Sits ahead of the generic resolution so NFL wins over NCAAF within the
    // Football group. Skipped the moment the user taps any sport (filter set).
    if (filter == null && isNflFeatureWindow() && leaguesWithGames.includes("NFL")) {
      return "NFL";
    }
    return filter === "__LIVE__" ? "__LIVE__"
      : filter && availableSports.includes(filter)
        ? filter
        : filter && groupLeagues.includes(filter) && leaguesWithGames.length > 0
          ? leaguesWithGames[0]
          : filter && groupLeagues.includes(filter)
            ? filter
            : (leaguesWithGames[0] ?? groupLeagues[0] ?? "");
  }, [filter, availableSports, groupLeagues, leaguesWithGames]);

  const filtered = useMemo(() => {
    let base;
    if (activeFilter === "__LIVE__") {
      base = games.filter(g => g.isLive && !g.completed);
    } else if (activeFilter) {
      base = games.filter(g => displaySport(g.sport) === activeFilter);
    } else {
      const fallbackSport = availableSports[0] || null;
      base = fallbackSport
        ? games.filter(g => displaySport(g.sport) === fallbackSport)
        : [];
    }
    if (teamSearch.trim()) {
      const q = teamSearch.trim().toLowerCase();
      base = base.filter(g =>
        g.home?.full?.toLowerCase().includes(q) ||
        g.away?.full?.toLowerCase().includes(q) ||
        g.home?.abbr?.toLowerCase().includes(q) ||
        g.away?.abbr?.toLowerCase().includes(q) ||
        g.homeTeamFull?.toLowerCase().includes(q) ||
        g.awayTeamFull?.toLowerCase().includes(q)
      );
    }
    // Final lock enforcement. Restricting availableSports upstream removes the
    // TILES for locked sports, but three paths reach games without consulting
    // them, and each would leak a pick the league rejects at submit:
    //   1. activeFilter === "__LIVE__" filters `games` directly on isLive.
    //   2. activeFilter can resolve to "" (no groups/leagues with games), and
    //      the else-branch then assigns `base = games` — every sport.
    //   3. teamSearch matches team names across the whole feed.
    // This is the last gate before render, so enforcing here covers all three
    // regardless of how base was built.
    if (leagueSportLock) {
      base = base.filter(g => leagueSportLock.has(displaySport(g.sport)));
    }
    return base;
  }, [games, activeFilter, teamSearch, leagueSportLock]);
  // BOARD CAP LIFTED (2026-07) — the board previously truncated to the first 10
  // games with a "See N more" expander. An NFL week is 16 games, so in a pickem
  // league the cap silently ate a third of the slate; the same 10-game window is
  // arbitrary for wager boards on a heavy Sunday. Cap removed for every mode —
  // `visible` is now just `filtered`.
  //
  // Kept as a distinct binding rather than replacing `visible` with `filtered`
  // at its ~4 downstream uses: the alias keeps this a one-line change and leaves
  // the reintroduction of a cap (or a virtualized window) a one-line change too.
  // `showAll`/`setShowAll` are retained but inert — the expander they drove is
  // gone; nothing reads showAll now.
  // ── NFL WEEK WINDOW (pickem boards only) ───────────────────────────────────
  // The Odds API returns the whole posted NFL schedule, which by late August is
  // the full 272-game season. Grouping alone doesn't fix that — headers over 272
  // games is still 272 games to scroll. A pickem board shows exactly ONE week's
  // slate at a time, with a switcher to move between weeks.
  //
  // Defaults to getNflActiveWeek() rather than getNflWeekForTime(now): in the
  // preseason the latter is null and the board would render empty during the
  // exact window commissioners are evaluating the app. `null` here means "all
  // weeks" — used by non-NFL games and as the wager-board passthrough.
  // PICKEM-WEEK-TABS (fix 2026-10-08): store only the week the user TAPPED
  // (null = follow the calendar). The shown week is derived every render by
  // resolveBoardNflWeek below, so it rolls to the new week on Thursday without
  // a remount and never sits on a week that has left the feed.
  const [nflWeekPicked, setNflWeekPicked] = useState(null);

  // Back to following the calendar when the board becomes a pickem board
  // (e.g. the user switches leagues).
  useEffect(() => {
    setNflWeekPicked(null);
  }, [boardIsPickem]);

  // Which weeks actually have games in the current feed? Drives the switcher —
  // we only offer weeks the API has posted, so a July board offers Week 1 alone
  // rather than 18 tabs that are mostly empty.
  const nflWeeksAvailable = useMemo(() => {
    const s = new Set();
    for (const g of filtered) {
      if (displaySport(g.sport) !== "NFL") continue;
      const wk = getNflWeekForTime(new Date(g.commenceTime).getTime());
      if (wk != null) s.add(wk);
    }
    return [...s].sort((a, b) => a - b);
  }, [filtered]);

  // PICKEM-WEEK-TABS: the week actually shown (see resolveBoardNflWeek).
  const nflWeek = resolveBoardNflWeek(nflWeekPicked, nflWeeksAvailable, getNflActiveWeek());

  // Apply the window. Non-NFL games always pass through (a legacy mixed-sport
  // pickem league still shows its MLB games); only NFL games are windowed, and
  // only when the selected week is one the feed actually has.
  const windowed = useMemo(() => {
    if (nflWeek == null) return filtered;
    const hasWeek = filtered.some(g =>
      displaySport(g.sport) === "NFL" &&
      getNflWeekForTime(new Date(g.commenceTime).getTime()) === nflWeek
    );
    if (!hasWeek) return filtered;
    return filtered.filter(g => {
      if (displaySport(g.sport) !== "NFL") return true;
      return getNflWeekForTime(new Date(g.commenceTime).getTime()) === nflWeek;
    });
  }, [filtered, nflWeek]);

  const visible = windowed;

  // Single-pass split of `filtered` into live/completed/upcoming counts.
  // Previously the live/upcoming divider below called filtered.filter() FIVE
  // separate times for the live count and TWICE for the completed count —
  // 7 full passes over `filtered` per render, on a hot polling path.
  const filteredCounts = useMemo(() => {
    let live = 0, completed = 0;
    for (const g of filtered) {
      if (g.completed) completed++;
      else if (g.isLive) live++;
    }
    return { live, completed, upcoming: filtered.length - live - completed };
  }, [filtered]);

  // Trigger pick-add animation on the game card
  const [pickAnimId, setPickAnimId] = useState(null);
  const pickAnimTimerRef = useRef(null);
  const triggerPickAnim = (gameId) => {
    // Cancel a pending clear so a rapid second tap doesn't cut the new
    // card's bounce short (see BLINK FIX comment at the card render).
    if (pickAnimTimerRef.current) clearTimeout(pickAnimTimerRef.current);
    setPickAnimId(gameId);
    pickAnimTimerRef.current = setTimeout(() => setPickAnimId(null), 400);
  };

  const toggle = (game, pickOption) => {
    haptic.light();
    triggerPickAnim(game.id);
    const k = `${game.id}-${pickOption.key}`;
    setPicks(prev => {
      if (prev[k]) { const n = {...prev}; delete n[k]; return n; }
      // Remove conflicting picks on same market for same game
      // Player props: only conflict on same player+stat (over vs under)
      // Alt markets: only conflict on same alt line
      // Main markets: conflict on same type (spread vs spread, ml vs ml, etc.)
      const conflictKeys = Object.keys(prev).filter(ek => {
        const ep = prev[ek];
        if (ep.gameId !== game.id) return false;
        if (ep.type !== pickOption.type) return false;
        // Player props: only conflict if it's the same player+stat (over↔under)
        if (pickOption.type === 'player_prop') {
          // Extract player+stat identifier from the key
          // key format: prop_PlayerName_Stat_over/under
          const getBase = (key) => key.replace(/_(over|under)$/, '');
          return getBase(pickOption.key) === getBase(ek.replace(`${game.id}-`, ''));
        }
        // Alt markets: only conflict on same line value
        if (pickOption.type === 'alt_spread' || pickOption.type === 'alt_total') {
          return ep.line === pickOption.line;
        }
        // Team totals: only conflict on same team
        if (pickOption.type === 'team_total') {
          // key format: teamTotal_TeamName_over/under
          const getTeam = (key) => key.replace(`${game.id}-`, '').replace(/_(over|under)$/, '');
          return getTeam(ek) === getTeam(k);
        }
        return true;
      });
      const cleaned = { ...prev };
      conflictKeys.forEach(ck => delete cleaned[ck]);
      return {
        ...cleaned,
        [k]: {
          gameId:       game.id,
          gameLabel:    `${game.away.full} @ ${game.home.full}`,
          sport:        game.sport,
          sportKey:     game.sportKey,
          commenceTime: game.commenceTime,
          homeTeamFull: game.homeTeamFull,
          awayTeamFull: game.awayTeamFull,
          type:         pickOption.type,
          side:         pickOption.side,
          line:         pickOption.line,
          odds:         pickOption.odds ?? pickOption.vig ?? -110,
          // A moneyline pickOption's `label` is the PRICE, not the pick:
          // mergeOdds builds it as fmtM(awayML) → "+160". That's fine as a
          // button face (it sits under a "Winner" column header next to the
          // team's own row, so the context supplies the meaning), but it gets
          // stored as pick.label and becomes the headline everywhere the pick
          // is shown later — My Picks, the league feed, the Pulse mirror. The
          // result is a card titled "-625" with no indication of who was
          // picked. Spread/total labels survive that trip because they carry a
          // line ("-3.5", "O 47"); a bare moneyline price carries nothing.
          //
          // Resolve it here, where game + side are both in scope, into
          // "Kansas City Chiefs ML". Falls through to the original label for
          // every other market and whenever the team name isn't resolvable.
          label:        (() => {
            if (pickOption.type !== "ml") return pickOption.label;
            const team = pickOption.side === "home" ? game.home?.full
                       : pickOption.side === "away" ? game.away?.full
                       : null;
            return team ? `${team} ML` : pickOption.label;
          })(),
          isLiveBet:    game.isLive,
          ...(pickOption.playerName && { playerName: pickOption.playerName }),
          ...(pickOption.statType   && { statType:   pickOption.statType }),
          ...(pickOption.teamSide   && { teamSide:   pickOption.teamSide }),
        }
      };
    });
  };
  const isPicked = (gameId, pickKey) => !!picks[`${gameId}-${pickKey}`];

  // Hoisted outside JSX return so TSX parser stays happy — no IIFE needed
  // Memoized off `visible` to skip the double-pass on renders where visible
  // hasn't changed (e.g. odds/score updates that don't add or remove games).
  const _liveAndUpcoming = useMemo(() => visible.filter(g => !g.completed), [visible]);
  const _completed       = useMemo(() => visible.filter(g =>  g.completed), [visible]);
  const _toRender        = showCompleted ? visible : _liveAndUpcoming;

  // ── WEEK-SLATE ROWS (pickem only) ──────────────────────────────────────────
  // Splices {kind:"week"} marker rows into the render list so an NFL pickem
  // board reads as "Week 1 / Week 2" slates instead of a flat chronological run.
  //
  // Derived from `_toRender`, NOT `visible`: `_toRender` is what actually
  // renders (it drops completed games unless showCompleted). Building headers
  // off `visible` would emit a header for a week whose games were all completed
  // and therefore filtered out — a "Week 3" heading floating above nothing.
  //
  // Non-NFL games in a pickem league (a mixed-sport league still allows them on
  // legacy docs) pass through with NO header AND WITHOUT touching `lastWeek`.
  // Resetting the tracker on them is the bug this guards: an NFL W1 game, then
  // an MLB game, then another NFL W1 game would re-emit a duplicate "Week 1"
  // header after the MLB row. Only NFL games move the tracker.
  //
  // Wager boards: boardIsPickem is false → returns _toRender mapped 1:1 to game
  // rows with zero markers. Identical output to the previous flat list.
  // WEEK HEADERS RETIRED (superseded by the week window + switcher). When the
  // board showed the whole season, inline "Week N" markers were the only way to
  // parse it. Now exactly one week renders at a time and the switcher above the
  // board names it — a header row would just repeat the selected tab, once, at
  // the top of every board. `getNflWeekForTime` is still the week authority; it
  // now feeds the window filter and the switcher instead of marker rows.
  const ROW_RENDER_CAP = 150;
  const [_visibleRowCount, _setVisibleRowCount] = useState(ROW_RENDER_CAP);
  useEffect(() => { _setVisibleRowCount(ROW_RENDER_CAP); }, [activeFilter, showCompleted, teamSearch]);

  const _allRows = useMemo(
    () => _toRender.map(g => ({ kind: "game", game: g })),
    [_toRender]
  );
  const _rowsTruncated = _allRows.length > _visibleRowCount;
  const _rows = useMemo(
    () => (_rowsTruncated ? _allRows.slice(0, _visibleRowCount) : _allRows),
    [_allRows, _visibleRowCount, _rowsTruncated]
  );

  // Pull-to-refresh for Games page
  const [gamesRefreshing, setGamesRefreshing] = useState(false);
  const handleGamesRefresh = useCallback(async () => {
    setGamesRefreshing(true);
    try {
      const { fetchLiveGames } = await import("./sportsApi");
      const raw = injectSpecialEvents(await fetchLiveGames());
      if (raw?.length > 0) onGamesLoaded?.(raw);
      setLastRefresh(Date.now());
    } catch {}
    setGamesRefreshing(false);
  }, [onGamesLoaded]);

  return (
    <div style={{ maxWidth:640, margin:"0 auto", padding:"0 0 calc(90px + env(safe-area-inset-bottom, 0px))", background:T.bg, minHeight:"100vh" }}>

      {/* Admin diagnostic — shows per-sport ESPN coverage + names of
          teams missing records. Visible only to admin UID. */}
      {user?.uid === "HTPWLwsbY7UCcftuCrlor5Hoj1f2" && games.length > 0 && (() => {
        // Group games by sport, count record coverage per sport
        const bySport = {};
        for (const g of games) {
          const k = g.sport || '?';
          if (!bySport[k]) bySport[k] = { total: 0, withRec: 0, missing: [] };
          bySport[k].total++;
          if (g.awayRecord || g.homeRecord || g.playoff) {
            bySport[k].withRec++;
          } else {
            bySport[k].missing.push(`${g.away?.full || '?'} @ ${g.home?.full || '?'}`);
          }
        }
        const sportLines = Object.entries(bySport)
          .sort((a, b) => b[1].total - a[1].total)
          .map(([sport, s]) => {
            const pct = Math.round(s.withRec / s.total * 100);
            const sample = s.missing.slice(0, 2).join(' · ');
            const more = s.missing.length > 2 ? ` (+${s.missing.length - 2} more)` : '';
            return `${sport} ${s.withRec}/${s.total} (${pct}%) ${sample}${more}`;
          });
        const total = games.length;
        const withRec = games.filter(g => g.awayRecord || g.homeRecord || g.playoff).length;
        return (
          <div style={{
            margin:"8px 16px 0",
            padding:"8px 10px",
            background:"rgba(248,113,113,0.06)",
            border:"1px solid rgba(248,113,113,0.20)",
            borderRadius:8,
            fontSize:9.5, fontWeight:600, color:"rgba(248,113,113,0.85)",
            fontFamily:"ui-monospace,SFMono-Regular,Menlo,Monaco,monospace",
            letterSpacing:"0.02em", lineHeight:1.5,
          }}>
            <div style={{ marginBottom:4, fontWeight:700 }}>
              {total} games · rec {withRec}/{total} ({Math.round(withRec/total*100)}%)
            </div>
            {sportLines.map((line, i) => (
              <div key={i} style={{ opacity: 0.85 }}>{line}</div>
            ))}
          </div>
        );
      })()}

      {/* -- PRACTICE MODE BANNER — shown to users not yet in a league -- */}
      {/* Gated on `leaguesLoaded` (passed from root) — `loading` here is local
          games-loading, which flips false BEFORE the parent's loadMyLeagues()
          resolves. Without this gate, users in leagues saw a brief flash of
          "You're in practice mode" on every cold start. */}
      {!loading && leaguesLoaded && userLeagues.length === 0 && (
        <div style={{
          margin:"12px 16px 0",
          background:`rgba(255,107,61,0.07)`,
          border:`1px solid rgba(255,107,61,0.18)`,
          borderRadius:16, padding:"20px",
          display:"flex", flexDirection:"column", gap:12,
          animation:"fadeUp 0.3s ease both",
        }}>
          <div style={{ display:"flex", alignItems:"center", gap:12 }}>
            <div style={{
              width:40, height:40, borderRadius:12,
              background:"rgba(255,107,61,0.15)", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0,
            }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={T.coral} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 2C12 2 5 9 5 14a7 7 0 0014 0c0-5-7-12-7-12z"/>
              </svg>
            </div>
            <div>
              <div style={{ fontSize:15, fontWeight:700, color:"#fff", marginBottom:2 }}>
                You're in practice mode
              </div>
              <div style={{ fontSize:12, color:"rgba(255,255,255,0.6)", lineHeight:1.4 }}>
                Pick any game to learn the loop. Create or join a league when you're ready to play with friends.
              </div>
            </div>
          </div>
          <div style={{ display:"flex", gap:8 }}>
            <button
              onClick={() => { haptic.light(); try { localStorage.removeItem("torch_active_league"); localStorage.setItem("torch_den_intent", "create"); } catch {} setActivePage("Den"); }}
              style={{
                flex:1, padding:"11px", background:T.coral, border:"none", borderRadius:12,
                color:"#fff", fontSize:13, fontWeight:700, cursor:"pointer",
                display:"flex", alignItems:"center", justifyContent:"center", gap:6,
                boxShadow:"0 2px 8px rgba(0,0,0,0.3)",
              }}
            >
              Create a League
            </button>
            <button
              onClick={() => { haptic.light(); try { localStorage.removeItem("torch_active_league"); localStorage.setItem("torch_den_intent", "join"); } catch {} setActivePage("Den"); }}
              style={{
                flex:1, padding:"11px", background:"rgba(255,255,255,0.06)", border:"1px solid rgba(255,255,255,0.10)", borderRadius:12,
                color:"rgba(255,255,255,0.7)", fontSize:13, fontWeight:600, cursor:"pointer",
                display:"flex", alignItems:"center", justifyContent:"center", gap:6,
              }}
            >
              Join a League
            </button>
          </div>
        </div>
      )}

      {/* Stale odds warning — live data backed off after repeated failures */}
      {lastOddsUpdate && !oddsRefreshing && games.some(g => g.isLive) &&
        (Date.now() - lastOddsUpdate > 10 * 60 * 1000) && (
        <div style={{
          margin:"8px 16px 0", padding:"8px 14px", borderRadius:8,
          background:T.goldDim, border:`1px solid ${T.gold}44`,
          display:"flex", alignItems:"center", gap:8,
          fontSize:12, color:T.gold, fontWeight:600,
        }}>
          <span>⚠</span>
          <span>Odds may be delayed — live data temporarily unavailable</span>
        </div>
      )}

      {/* -- Sport filter bar -- */}
      <div style={{
        position:"sticky", top:"calc(56px + var(--torch-banner-h, 0px))", zIndex:30, background:"rgba(8,10,16,0.94)",
        backdropFilter:"blur(40px) saturate(200%)",
        borderBottom:"1px solid rgba(255,255,255,0.04)",
        boxShadow:"0 4px 24px rgba(0,0,0,0.25)",
        padding:"10px 14px",
        transition:"top 0.2s ease",
      }}>
        <div style={{ display:"flex", gap:6, alignItems:"center" }}>
          {/* Live games button — unified pill style matches sport pills below
              (was an outlined oval at a different height; now consistent). */}
          {(() => {
            // Was: games.filter(...).length on every render → use memoized count
            const liveCount = liveActiveCount;
            const isActive = filter === "__LIVE__";
            return (
              <button
                onClick={() => {
                  if (filter !== "__LIVE__") {
                    preLiveFilterRef.current = filter;
                    setFilter("__LIVE__");
                  } else {
                    setFilter(preLiveFilterRef.current);
                  }
                  setShowAll(false); setExpanded(null);
                }}
                style={{
                  display:"flex", alignItems:"center", gap:6,
                  padding:"8px 14px", borderRadius:22, flexShrink:0,
                  background: isActive
                    ? "rgba(74,222,128,0.14)"
                    : "rgba(255,255,255,0.04)",
                  border: isActive
                    ? "1px solid rgba(74,222,128,0.32)"
                    : "1px solid transparent",
                  cursor:"pointer", transition:"all 0.15s",
                  color: isActive ? "#4ade80" : liveCount > 0 ? "#4ade80" : "rgba(255,255,255,0.40)",
                  fontSize:12, fontWeight:700, fontFamily:FB,
                  opacity: liveCount > 0 || isActive ? 1 : 0.55,
                  boxShadow: isActive ? "0 0 16px rgba(74,222,128,0.10)" : "none",
                }}
              >
                <span style={{
                  position:"relative",
                  width:6, height:6, borderRadius:"50%",
                  background: liveCount > 0 ? "#4ade80" : "rgba(255,255,255,0.15)",
                }}>
                  {liveCount > 0 && (
                    <span style={{
                      position:"absolute", inset:-3, borderRadius:"50%",
                      background:"rgba(74,222,128,0.4)",
                      animation:"sonarPing 1.6s ease-out infinite",
                    }}/>
                  )}
                </span>
                Live{liveCount > 0 ? ` · ${liveCount}` : ""}
              </button>
            );
          })()}

          {/* Horizontal scrollable sport pills — Big 4 first, then seasonal, then other */}
          <div style={{ flex:1, display:"flex", gap:6, overflowX:"auto", WebkitOverflowScrolling:"touch", scrollbarWidth:"none", msOverflowStyle:"none", paddingBottom:2 }} className="hide-scrollbar">
            {(() => {
              const PILL_ORDER = {"NBA":1,"NHL":2,"MLB":3,"NFL":4,"US Open":5,"Golf":6,"Soccer":7,"Tennis":8,"MMA":9,"Boxing":10,"NCAAB":11,"NCAAF":12,"WNBA":13};
              const PILL_ICON = {"NBA":"🏀","NHL":"🏒","MLB":"⚾","NFL":"🏈","US Open":"⛳","Golf":"⛳","Soccer":"⚽","Tennis":"🎾","MMA":"🥊","Boxing":"🥊","NCAAB":"🏀","NCAAF":"🏈","WNBA":"🏀","CBB":"🏀","CFB":"🏈"};
              const allLeagues = availableGroups.flatMap(grp => grp.leagues.filter(l => availableSports.includes(l)));
              const sorted = [...allLeagues].sort((a,b) => (PILL_ORDER[a] ?? 99) - (PILL_ORDER[b] ?? 99));
              return sorted.map(league => {
                const isActive = activeFilter === league && filter !== "__LIVE__";
                const leagueGames = games.filter(g => displaySport(g.sport) === league);
                const count = leagueGames.length;
                const liveCt = leagueGames.filter(g => g.isLive && !g.completed).length;
                const label = league === "NCAAB" ? "CBB" : league === "NCAAF" ? "CFB" : league;
                const icon = PILL_ICON[league] || PILL_ICON[label] || "";
                return (
                  <button key={league} onClick={() => { haptic.light(); setFilter(league); setShowAll(false); setExpanded(null); }} style={{
                    flexShrink:0, padding:"8px 14px", borderRadius:22, cursor:"pointer",
                    // White-on-dark active state. Orange used to be used here too,
                    // but orange was already on the "+ pick" CTA, the avatar, and
                    // the floating slip button — five orange surfaces fighting for
                    // attention on one screen. Reserving orange for actions makes
                    // the rest of the UI breathe.
                    background: isActive ? "#fff" : "rgba(255,255,255,0.04)",
                    border: isActive ? "1px solid rgba(255,255,255,0.0)" : "1px solid transparent",
                    color: isActive ? "#08080A" : "rgba(255,255,255,0.55)",
                    fontSize:12, fontWeight:700, display:"flex", alignItems:"center", gap:6, transition:"all 0.15s",
                    boxShadow: isActive ? "0 4px 16px rgba(0,0,0,0.3), 0 1px 0 rgba(255,255,255,0.4) inset" : "none",
                  }}>
                    {icon && <span style={{ fontSize:13, lineHeight:1 }}>{icon}</span>}
                    <span style={{ letterSpacing:"-0.005em" }}>{label}</span>
                    <span style={{
                      fontSize:10, fontWeight:700, padding:"1.5px 6px", borderRadius:8, fontFamily:FM,
                      fontVariantNumeric:"tabular-nums",
                      background: isActive ? "rgba(14,14,18,0.10)" : "rgba(255,255,255,0.08)",
                      color: isActive ? "#08080A" : "rgba(255,255,255,0.45)",
                    }}>{count}</span>
                    {liveCt > 0 && <span style={{ width:6, height:6, borderRadius:"50%", background:"#4ade80", boxShadow:"0 0 8px rgba(74,222,128,0.8)", animation:"pulse 1.4s ease infinite", flexShrink:0 }}/>}
                  </button>
                );
              });
            })()}
          </div>
        </div>
      </div>



      <div style={{ padding:"10px 16px 10px" }}>
        <div style={{
          display:"flex", alignItems:"center", gap:10,
          background:"rgba(255,255,255,0.04)", border:"1px solid rgba(255,255,255,0.06)",
          borderRadius:12, padding:"10px 14px",
          transition:"border 0.15s",
        }}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.3)" strokeWidth="2" strokeLinecap="round">
            <circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/>
          </svg>
          <input
            value={teamSearch}
            onChange={e => setTeamSearch(e.target.value)}
            placeholder="Search teams…"
            style={{
              flex:1, background:"none", border:"none", outline:"none",
              fontSize:14, color:"rgba(255,255,255,0.85)",
              fontFamily:"inherit",
            }}
          />
          {teamSearch && (
            <button onClick={() => setTeamSearch("")} style={{
              background:"none", border:"none", cursor:"pointer",
              color:"rgba(255,255,255,0.42)", fontSize:16, padding:"0 2px", lineHeight:1,
            }} aria-label="Close">×</button>
          )}
        </div>
      </div>

      {/* Streak banner — positioned below sticky bar so it scrolls with content */}
      {!loading && userStats?.streak > 0 && (
        <div style={{
          margin:"8px 16px 0",
          padding:"9px 14px",
          background:"rgba(255,107,61,0.07)",
          border:"1px solid rgba(255,107,61,0.12)",
          borderLeft:"3px solid #DC2626",
          borderRadius:10,
          display:"flex", alignItems:"center", gap:10,
        }}>
          <TorchLogo size={28} style={{ marginRight: 2, flexShrink: 0 }} />
          <span style={{ fontSize:13, fontWeight:700, color:"rgba(255,255,255,0.85)" }}>
            {userStats.streak}-pick streak
          </span>
        </div>
      )}

      <div style={{ padding:"12px 16px 0" }}>
        {/* Sport header */}
        {!loading && (
          <div style={{ marginBottom: 16 }}>
            <div style={{ display:"flex", alignItems:"baseline", justifyContent:"space-between", marginBottom:10 }}>
              <div style={{ display:"flex", alignItems:"baseline", gap:10 }}>
                <span style={{
                  fontFamily:FD, fontSize:24, fontWeight:700, letterSpacing:"-0.03em",
                  color: activeFilter === "__LIVE__" ? "#4ade80" : "#fff", lineHeight:1,
                }}>{activeFilter === "__LIVE__" ? "Live Games" : activeFilter}</span>
                <span style={{
                  fontSize:10, fontWeight:700, color:"rgba(255,255,255,0.36)",
                  letterSpacing:"0.04em", fontFamily:FM, ...tnum,
                }}>
                  {new Date().toLocaleDateString("en-US",{month:"short", day:"numeric"})}
                </span>
              </div>
              <span style={{
                fontSize:11, fontWeight:700, color:"rgba(255,255,255,0.40)", fontFamily:FM, ...tnum,
                padding:"3px 10px", borderRadius:8,
                background:"rgba(255,255,255,0.03)", border:"1px solid rgba(255,255,255,0.04)",
              }}>{filtered.length}</span>
            </div>
          </div>
        )}

        {/* Live / upcoming divider */}
        {/* PERF: was calling filtered.filter() 7x per render (5x live + 2x
            completed) — replaced with single-pass filteredCounts memo above. */}
        {!loading && filtered.length > 0 && (
          <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:14 }}>
            {filteredCounts.live > 0 && (
              <span style={{
                fontSize:9, fontWeight:800, letterSpacing:"0.14em",
                textTransform:"uppercase", color:"#4ade80", fontFamily:FM,
                display:"flex", alignItems:"center", gap:5, flexShrink:0,
                padding:"3px 12px", borderRadius:20,
                background:"rgba(52,211,153,0.06)", border:"1px solid rgba(52,211,153,0.10)",
              }}>
                <span style={{ width:5, height:5, borderRadius:"50%", background:"#4ade80",
                  display:"inline-block", animation:"pulse 1.2s ease infinite", boxShadow:"0 0 6px rgba(74,222,128,0.4)" }}/>
                LIVE · {filteredCounts.live}
              </span>
            )}
            <div style={{ flex:1, height:"1px", background:"linear-gradient(90deg, rgba(255,255,255,0.06), transparent)" }} />
            {filteredCounts.upcoming > 0 && (
              <span style={{
                fontSize:9, fontWeight:700, letterSpacing:"0.10em",
                textTransform:"uppercase", color:"rgba(255,255,255,0.36)", flexShrink:0,
                fontFamily:FM,
              }}>
                {filteredCounts.upcoming} upcoming
              </span>
            )}
          </div>
        )}

        {loading && <TorchLoader message="Loading games" inline skeleton />}

        {/* Connection error — full empty state when no games to show.
            Replaces the old mock-games fallback: we never show fake matchups,
            because users could make picks against games that don't exist. */}
        {!loading && error && games.length === 0 && (
          <EmptyState
            icon="⚠️"
            title="Can't load games right now"
            sub="Check your connection and try again. We won't show games until we can confirm they're real."
            cta="Try again"
            onCta={() => { haptic.light(); retry(); }}
          />
        )}

        {/* Stale-data banner — shown above existing games when a refresh fails.
            Users keep seeing what they had, but are warned updates are paused. */}
        {!loading && error && games.length > 0 && (
          <div style={{
            margin:"0 14px 10px", padding:"10px 14px", borderRadius:12,
            background:"rgba(255,180,0,0.06)", border:"1px solid rgba(255,180,0,0.18)",
            display:"flex", alignItems:"center", justifyContent:"space-between", gap:10,
          }}>
            <div style={{ display:"flex", alignItems:"center", gap:8, minWidth:0 }}>
              <div style={{ width:6, height:6, borderRadius:"50%", background:"#ffb400", flexShrink:0 }}/>
              <span style={{ fontSize:12, color:"rgba(255,255,255,0.65)", lineHeight:1.4 }}>
                Connection issue — odds may be out of date.
              </span>
            </div>
            <button onClick={() => { haptic.light(); retry(); }} style={{
              background:"none", border:"1px solid rgba(255,180,0,0.3)", borderRadius:8,
              padding:"4px 10px", cursor:"pointer", fontSize:11, fontWeight:700,
              color:"#ffb400", flexShrink:0, fontFamily:FM, letterSpacing:"0.02em",
            }}>Retry</button>
          </div>
        )}

        {/* Updated timestamp + refresh */}
        {!loading && games.length > 0 && (
          <div style={{ padding:"4px 16px 0", display:"flex", justifyContent:"flex-end", alignItems:"center", gap:6 }}>
            <span style={{ fontSize:9, color:"rgba(255,255,255,0.32)", fontFamily:FM, fontWeight:600 }}>
              Updated <RefreshTicker since={lastRefresh} />
            </span>
            <button onClick={() => { haptic.light(); handleGamesRefresh(); }} disabled={gamesRefreshing} aria-label="Refresh odds" style={{
              background:"none", border:"none", cursor:"pointer", padding:2, opacity: gamesRefreshing ? 0.3 : 0.4,
              animation: gamesRefreshing ? "spin 0.8s linear infinite" : "none",
            }}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.4)" strokeWidth="2.5" strokeLinecap="round">
                <path d="M21 12a9 9 0 0 1-9 9m0 0a9 9 0 0 1-9-9m9 9V17m-9-5a9 9 0 0 1 9-9m0 0a9 9 0 0 1 9 9m-9-9v4"/>
              </svg>
            </button>
          </div>
        )}

        {leagueCtx && (
          <div style={{ margin:"0 14px 10px", padding:"10px 14px", borderRadius:12, background:"rgba(255,107,61,0.06)", border:"1px solid rgba(255,107,61,0.10)", display:"flex", alignItems:"center", justifyContent:"space-between" }}>
            <div style={{ display:"flex", alignItems:"center", gap:8, minWidth:0 }}>
              {/* Lock glyph when the league restricts sports — the board has no
                  other sport tiles in that case, and the icon is what explains
                  why rather than leaving them to read as missing. */}
              {leagueSportLock ? (
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={T.coral} strokeWidth="2.2" strokeLinecap="round" style={{ flexShrink:0 }}>
                  <rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                </svg>
              ) : (
                <div style={{ width:6, height:6, borderRadius:"50%", background:T.coral, flexShrink:0 }}/>
              )}
              <div style={{ minWidth:0 }}>
                <span style={{ fontSize:12, color:"rgba(255,255,255,0.55)" }}>
                  Picking for <span style={{ fontWeight:700, color:"#fff" }}>{leagueCtx.name}</span>
                  {leagueSportLock ? <span style={{ color:"rgba(255,255,255,0.38)" }}> — {[...leagueSportLock].join(", ")} only</span> : null}
                </span>
                {/* Say plainly what a pickem pick is. The board still shows odds
                    next to each team (useful for judging who's favored), and
                    without this line that reads as "wager at these odds" — which
                    is exactly the confusion this league format needs to avoid. */}
                {boardIsPickem && (
                  <div style={{ fontSize:10.5, color:"rgba(255,255,255,0.38)", marginTop:2, lineHeight:1.35 }}>
                    Pick the winner — 1 point each. Odds shown for reference only; nothing is wagered.
                  </div>
                )}
              </div>
            </div>
            {/* "Show All" REMOVED (2026-07-17). It called setLeagueCtx(null),
                which dropped the league context entirely — defeating the sport
                lock and dumping the user onto the unrestricted global board
                (WNBA et al) while they were mid-slip for an NFL-only league.
                Any pick made after that gets rejected at submit, which is the
                dead-end this lock exists to prevent. There's no legitimate
                reason to leave a league's board from inside its own picking
                flow: the bottom nav's Games tab is the unrestricted board, one
                tap away, and it doesn't silently invalidate a slip in progress. */}
          </div>
        )}

        {!loading && !error && filtered.length === 0 && (<>
          <EmptyState
            icon={activeFilter === "__LIVE__" ? "📡" : "📅"}
            title={activeFilter === "__LIVE__" ? "No live games right now"
              : availableSports.includes(activeFilter) ? "No games right now"
              : `${activeFilter} is off-season`}
            sub={activeFilter === "__LIVE__"
              ? "Nothing live this minute. Tap any sport above to see what's coming up."
              : leagueSportLock
                // Locked boards (pick'em/survivor) can't route anywhere else, so the
                // message should say WHY it's quiet rather than invite an escape
                // there isn't one for. This is the routine state for a league set up
                // before the Odds API has posted the sport's games, not an error.
                ? "Odds haven't posted for this league's sport yet. Check back closer to kickoff."
                : availableSports.includes(activeFilter)
                  ? "Quiet day for this sport. Odds update every 10 minutes."
                  : "This one's off-season. Try another sport up top."}
          />
          {/* ── "Browse other sports" hidden when locked (2026-08-13) ──────────────
              With the fix above, a locked board's availableSports is always exactly
              the league's own sport(s) — there is structurally nothing else to browse
              to. The button's own search (`s !== activeFilter && availableSports
              .includes(s)`) would therefore never find a candidate and silently do
              nothing on tap, which is indistinguishable from a broken button. Better
              to not offer an escape hatch that doesn't lead anywhere. */}
          {!leagueSportLock && availableSports.length > 0 && (
            <div style={{ textAlign:"center", marginTop:12 }}>
              <button onClick={() => { const popOrder = ["NBA","NFL","MLB","NHL","Soccer","US Open","Golf","MMA","Boxing","Tennis","NCAAB","NCAAF","WNBA"]; const next = popOrder.find(s => s !== activeFilter && availableSports.includes(s) && games.filter(g => displaySport(g.sport) === s).length > 0) || availableSports.find(s => s !== activeFilter && games.filter(g => displaySport(g.sport) === s).length > 0); if (next) { haptic.light(); setFilter(next); }}} style={{
                padding:"10px 20px", borderRadius:14, border:"none", cursor:"pointer",
                background:"rgba(255,107,61,0.08)", color:T.coral, fontSize:12, fontWeight:700,
              }}>Browse other sports →</button>
            </div>
          )}
        </>)}

        {/* First-run hint — only for new users with no picks yet */}
        {!loading && filtered.length > 0 && (userStats?.totalPicks ?? 0) === 0 && (
          <div style={{
            margin:"0 0 10px", padding:"11px 14px",
            background:"rgba(220,38,38,0.06)", border:"1px solid rgba(255,107,61,0.12)", borderRadius:10,
            display:"flex", alignItems:"center", gap:12,
          }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="rgba(255,107,61,0.7)" strokeWidth="2" strokeLinecap="round" style={{ flexShrink:0 }}>
              <path d="M12 19V5M5 12l7-7 7 7"/>
            </svg>
            <div style={{ fontSize:12, color:"rgba(255,255,255,0.6)", lineHeight:1.5 }}>
              Tap any odds to add to your pick slip. No real money — ever.
            </div>
          </div>
        )}

        {/* Game cards */}
        {/* NFL week switcher — pickem boards with a posted schedule. Horizontal
            scroller rather than a dropdown: 18 weeks is too many for a select on
            mobile, and the current week should be visible without a tap. Only
            weeks the feed has posted are offered. */}
        {/* PICKEM-WEEK-TABS: pickem boards always show the tabs (even a single
            week), so the week on screen is never a guess. */}
        {(nflWeeksAvailable.length > 1 || (boardIsPickem && nflWeeksAvailable.length === 1)) && (
          <div style={{
            display:"flex", gap:6, marginBottom:12, paddingBottom:2,
            overflowX:"auto", WebkitOverflowScrolling:"touch",
          }}>
            {nflWeeksAvailable.map(wk => {
              const on = nflWeek === wk;
              return (
                <button key={wk} onClick={() => { haptic.light(); setNflWeekPicked(wk); }} className="no-press" style={{
                  flexShrink:0, padding:"7px 13px", borderRadius:9, cursor:"pointer",
                  border: on ? `1.5px solid ${T.coral}` : "1px solid rgba(255,255,255,0.07)",
                  background: on ? "rgba(255,107,61,0.12)" : "rgba(255,255,255,0.025)",
                  color: on ? T.coral : T.inkSec,
                  fontSize:12.5, fontWeight: on ? 700 : 600, whiteSpace:"nowrap",
                  transition:"all 0.13s",
                }}>Week {wk}</button>
              );
            })}
          </div>
        )}

        {_rows.map((row, idx) => {
          const rawGame = row.game;
          const game = rawGame.isLive ? mergeOdds(rawGame) : rawGame;
          const cardDark = game.isLive
            ? "linear-gradient(160deg,rgba(52,211,153,0.04),rgba(13,14,18,0.95))"
            : "rgba(255,255,255,0.03)";
          const cardBorder = game.isLive
            ? "1.5px solid rgba(52,211,153,0.30)"
            : game.completed
              ? "1px solid rgba(255,255,255,0.04)"
              : "1px solid rgba(255,255,255,0.08)";
          const cardShadow = game.isLive
            ? "0 4px 32px rgba(52,211,153,0.07), 0 2px 16px rgba(0,0,0,0.5)"
            : "0 2px 12px rgba(0,0,0,0.35)";
          // -- per-card computed values (hoisted out of JSX to avoid IIFE/TSX issues) --
          const fmtVig = (v) => v == null ? '' : v > 0 ? `+${v}` : `${v}`;
          const showScore   = game.isLive || game.completed;
          const awayScore   = game.away.score ?? 0;
          const homeScore   = game.home.score ?? 0;
          const awayWinning = showScore && awayScore > homeScore;
          const homeWinning = showScore && homeScore > awayScore;
          // Use the global sport color system — vivid, high-energy per sport
          const sportC = getSportColor(game.sport);
          const sc = { bg: sportC.dim, color: sportC.accent, border: sportC.border };
          const gameMs = new Date(game.commenceTime || game.time).getTime();
          const minsLeft = Math.floor((gameMs - Date.now()) / 60000);
          const oddsAge = game.oddsUpdatedAt ? Math.floor((Date.now() - game.oddsUpdatedAt) / 1000) : null;
          const oddsAgeLabel = oddsAge === null ? null : oddsAge < 60 ? `${oddsAge}s ago` : `${Math.floor(oddsAge/60)}m ago`;
          const startingSoon = !game.isLive && !game.completed && minsLeft > 0 && minsLeft <= 60;
          const startingVSoon = !game.isLive && !game.completed && minsLeft > 0 && minsLeft <= 15;

          // ── OUTRIGHT / TOURNAMENT WINNER (e.g. Masters) ──
          if (game.isOutright && game.outrightOutcomes?.length > 0) {
            return (
              <div key={game.id} className="game-card" style={{
                marginBottom:14, animation:"fadeUp 0.35s ease both", animationDelay:`${idx*70}ms`,
                borderRadius:20, overflow:"hidden", position:"relative",
                background:`linear-gradient(155deg, ${sportC.dim.replace('0.10','0.06')} 0%, rgba(12,14,22,1) 40%)`,
                border:`1px solid ${sportC.border.replace('0.30','0.15')}`,
                borderLeft:`8px solid ${sportC.accent}`,
              }}>
                <div style={{ height:4, background:`linear-gradient(90deg,transparent 3%,${sc.color} 20%,${sc.color} 50%,${sc.color} 80%,transparent 97%)`, boxShadow:`0 0 18px ${sc.color}70` }}/>
                <div style={{ padding:"14px 14px 12px" }}>
                  <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:12 }}>
                    <span style={{ fontSize:9, fontWeight:800, letterSpacing:"0.10em", textTransform:"uppercase", color:sc.color, background:sc.bg, padding:"3px 10px", borderRadius:6, border:`1px solid ${sc.border}`, fontFamily:FM }}>{game.sport}</span>
                    <span style={{ fontSize:11, color:"rgba(255,255,255,0.40)", fontWeight:500 }}>{game.time}</span>
                    {startingVSoon && <span style={{ fontSize:9, fontWeight:800, color:"#FF6B3D", fontFamily:FM, padding:"2px 8px", borderRadius:6, background:"rgba(255,107,61,0.12)", border:"1px solid rgba(255,107,61,0.20)", animation:"livePulse 1.5s ease infinite" }}>SOON</span>}
                    {startingSoon && !startingVSoon && <span style={{ fontSize:9, fontWeight:700, color:"rgba(255,255,255,0.42)", fontFamily:FM, padding:"2px 6px", borderRadius:4, background:"rgba(255,255,255,0.04)" }}>{minsLeft}m</span>}
                  </div>
                  <div style={{ fontSize:14, fontWeight:700, color:"#fff", marginBottom:10 }}>{game.outrightHeader || "Tournament Winner"}</div>
                  {/* Multi-market header (Win / Place / Show) — only shown when game.outrightMarkets has 2+ markets */}
                  {game.outrightMarkets?.length > 1 && (
                    <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:8, padding:"0 4px" }}>
                      <span style={{ flex:1 }}/>
                      {game.outrightMarkets.map(m => (
                        <span key={m.key} style={{
                          width:64, textAlign:"center",
                          fontSize:9, fontWeight:800, letterSpacing:"0.10em",
                          color:"rgba(255,255,255,0.35)", fontFamily:FM,
                        }}>{m.shortLabel || m.label}</span>
                      ))}
                    </div>
                  )}
                  <div style={{ display:"flex", flexDirection:"column", gap:5 }}>
                    {game.outrightOutcomes.slice(0, expanded === game.id ? game.outrightOutcomes.length : 12).map((o, oi) => {
                      // ── Multi-market row (Derby: Win / Place / Show) ──
                      if (game.outrightMarkets?.length > 1) {
                        return (
                          <div key={oi} style={{
                            padding:"8px 10px", borderRadius:12,
                            background:"rgba(255,255,255,0.02)", border:"1px solid rgba(255,255,255,0.05)",
                            display:"flex", alignItems:"center", gap:6,
                          }}>
                            <span style={{ flex:1, fontSize:12, fontWeight:600, color:"#fff",
                              overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{o.name}</span>
                            {game.outrightMarkets.map(m => {
                              const oddsForMarket = m.key === 'win' ? o.odds : m.key === 'place' ? o.placeOdds : m.key === 'show' ? o.showOdds : null;
                              if (oddsForMarket == null) return <span key={m.key} style={{ width:64 }}/>;
                              const pickKey = `outright::${m.key}::${o.name}`;
                              const picked = isPicked(game.id, pickKey);
                              const verbCap = m.label; // "Win" | "Place" | "Show"
                              return (
                                <button key={m.key}
                                  onClick={() => toggle(game, { key: pickKey, type:"outright", market:m.key, side:o.name, odds:oddsForMarket, label:`${o.name} to ${verbCap}`, homeTeamFull:game.homeTeamFull, awayTeamFull:o.name })}
                                  style={{
                                    width:64, padding:"8px 0", borderRadius:9, cursor:"pointer",
                                    background: picked ? "rgba(255,107,61,0.14)" : "rgba(255,255,255,0.04)",
                                    border: picked ? "1px solid rgba(255,107,61,0.40)" : "1px solid rgba(255,255,255,0.06)",
                                    boxShadow: picked ? "0 0 10px rgba(255,107,61,0.18)" : "none",
                                    transition: "all 0.18s cubic-bezier(0.34,1.4,0.64,1)",
                                    fontSize:12, fontWeight:800, fontFamily:FM,
                                    color: picked ? T.coral : "rgba(255,255,255,0.75)",
                                  }}>
                                  {oddsForMarket > 0 ? `+${oddsForMarket}` : oddsForMarket}
                                </button>
                              );
                            })}
                          </div>
                        );
                      }
                      // ── Single-market row (Masters etc.) — original layout ──
                      const pickKey = `outright::${o.name}`;
                      const picked = isPicked(game.id, pickKey);
                      return (
                        <button key={oi}
                          onClick={() => toggle(game, { key: pickKey, type:"outright", side:o.name, odds:o.odds, label:`${o.name} to Win`, homeTeamFull:game.homeTeamFull, awayTeamFull:o.name })}
                          style={{
                            padding:"11px 14px", borderRadius:12, cursor:"pointer",
                            background: picked ? "rgba(255,107,61,0.12)" : "rgba(255,255,255,0.03)",
                            border: picked ? "1px solid rgba(255,107,61,0.35)" : "1px solid rgba(255,255,255,0.06)",
                            boxShadow: picked ? "0 0 12px rgba(255,107,61,0.15)" : "none",
                            transition: "all 0.2s cubic-bezier(0.34,1.4,0.64,1)",
                            display:"flex", justifyContent:"space-between", alignItems:"center",
                          }}>
                          <span style={{ fontSize:13, fontWeight:600, color: picked ? T.coral : "#fff", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", flex:1, textAlign:"left" }}>{o.name}</span>
                          <span style={{ fontSize:13, fontWeight:800, color: picked ? T.coral : "rgba(255,255,255,0.5)", fontFamily:FM, flexShrink:0, marginLeft:12 }}>{o.odds > 0 ? `+${o.odds}` : o.odds}</span>
                        </button>
                      );
                    })}
                  </div>
                  {game.outrightOutcomes.length > 12 && (
                    <button onClick={() => setExpanded(expanded === game.id ? null : game.id)} style={{
                      width:"100%", marginTop:8, padding:"8px 0", background:"none", border:"1px solid rgba(255,255,255,0.06)",
                      borderRadius:10, cursor:"pointer", fontSize:11, fontWeight:700, color:T.coral, fontFamily:FM,
                    }}>
                      {expanded === game.id ? "Show less" : `+${game.outrightOutcomes.length - 12} more ${game.outrightEntryLabel || "golfers"} ▼`}
                    </button>
                  )}
                </div>
              </div>
            );
          }

          const [awaySpread, homeSpread, over, under, awayML, homeML] = game.picks;
          // 3-way moneyline (soccer/WC): the draw pick is appended last when priced.
          const drawPick = game.picks.find(p => p.side === 'draw') || null;
          const isThreeWay = !!drawPick;
          // For 3-way games, ML moves out of the per-team grid into a full-width
          // strip below (Away / Draw / Home), mirroring the book's moneyline block.
          // 2-way games keep ML inline in the per-team column as before.
          //
          // Pickem: spreads and totals don't exist in this format (the league's
          // allowedBetTypes is ["ml"]), so the board drops those columns entirely
          // rather than showing picks that would be rejected at submit. The
          // moneyline stays — the odds are useful context for deciding who to
          // take, even though nothing is wagered.
          //
          // 3-way pickem (soccer): the draw strip below already renders
          // Away/Draw/Home moneylines full-width. Rendering an inline ML column
          // too would show the same pick twice, so the per-team grid collapses to
          // the team name alone and the strip owns the pick.
          const pickemThreeWay = boardIsPickem && isThreeWay;
          const awayPicks = pickemThreeWay ? []
            : boardIsPickem ? [awayML]
            : isThreeWay ? [awaySpread, over] : [awaySpread, over, awayML];
          const homePicks = pickemThreeWay ? []
            : boardIsPickem ? [homeML]
            : isThreeWay ? [homeSpread, under] : [homeSpread, under, homeML];
          const oddsCols = pickemThreeWay ? 0 : boardIsPickem ? 1 : (isThreeWay ? 2 : 3);
          const gridCols = oddsCols === 0 ? "1fr" : `1fr ${"64px ".repeat(oddsCols).trim()}`;
          const oddsHeaders = pickemThreeWay ? []
            : boardIsPickem ? ["Winner"]
            : isThreeWay ? ["Spread","Total"] : ["Spread","Total","ML"];

          return (
          // ── BLINK FIX (Aug 29, 2026) ─────────────────────────────────
          // The entry animation (fadeUp, staggered) and the tap bounce used
          // to share ONE animation property via a ternary. Clearing the
          // bounce swapped animation-name back to fadeUp — which RESTARTS
          // it, so every odds tap made the card re-run its entrance:
          // fade to invisible, slide back in, after its stagger delay.
          // That was the "blinks a few times" on pick selection.
          // Entry now lives on this outer wrapper (assigned once, never
          // reassigned); the bounce rests at "none", and pickBounce → none
          // stops cleanly without restarting anything.
          <div key={game.id} style={{
            animation:"fadeUp 0.35s ease both",
            animationDelay:`${idx*70}ms`,
          }}>
          <div className="game-card" style={{
            marginBottom:12,
            animation: pickAnimId === game.id ? "pickBounce 0.35s ease" : "none",
            opacity: game.completed ? 0.45 : 1,
            // ── Flat surface, single left-rule accent ──
            // Live games get a green left rule; everything else is flat on the
            // elevated dark surface. No gradients, no multi-layer shadow.
            background: T.card,
            border: game.isLive
              ? "1px solid rgba(74,222,128,0.18)"
              : "1px solid rgba(255,255,255,0.06)",
            borderLeft: game.isLive
              ? "3px solid #4ade80"
              : "1px solid rgba(255,255,255,0.06)",
            borderRadius: game.isLive ? "0 14px 14px 0" : 14,
            overflow:"hidden",
            position:"relative",
          }}>
            <div style={{ padding:"14px 14px 12px", position:"relative" }}>
                {/* Meta row */}
                <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:12 }}>
                  <span style={{
                    // Sport badge — was saturated colored fill (made every game card
                    // glow with a different color). Now: muted neutral background with
                    // a colored accent dot so the sport is identifiable but not loud.
                    fontSize:10, fontWeight:700, letterSpacing:"0.06em",
                    textTransform:"uppercase", color:"rgba(255,255,255,0.65)",
                    background:"rgba(255,255,255,0.05)", padding:"3px 9px 3px 7px", borderRadius:6,
                    border:"1px solid rgba(255,255,255,0.06)",
                    fontFamily:FB,
                    display:"inline-flex", alignItems:"center", gap:5,
                  }}>
                    <span style={{ width:5, height:5, borderRadius:"50%", background:sc.color }}/>
                    {game.sport}
                  </span>
                  {game.completed ? (
                    <span style={{ fontSize:10, fontWeight:700, color:"rgba(255,255,255,0.40)", letterSpacing:"0.10em", fontFamily:FM }}>FINAL</span>
                  ) : game.isLive ? (
                    <span style={{ display:"flex", alignItems:"center", gap:8, flex:1, minWidth:0 }}>
                      <span style={{ display:"flex", alignItems:"center", gap:6 }}>
                        <span style={{
                          width:5, height:5, borderRadius:"50%",
                          background:"#4ade80",
                          animation:"pulse 2s ease infinite",
                          flexShrink:0,
                        }}/>
                        <span style={{ fontSize:10, fontWeight:700, color:"#4ade80", letterSpacing:"0.12em", fontFamily:FM }}>LIVE</span>
                      </span>
                      {(game.livePeriod?.periodLabel || game.period) && (
                        <span style={{
                          fontSize:10, fontWeight:600, color:"rgba(255,255,255,0.45)",
                          letterSpacing:"0.04em", fontFamily:FM, fontVariantNumeric:"tabular-nums",
                          overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap",
                        }}>
                          · {game.livePeriod?.periodLabel || game.period}
                          {(game.livePeriod?.clockLabel || game.clock) && ` · ${game.livePeriod?.clockLabel || game.clock}`}
                        </span>
                      )}
                      {game.liveState && (
                        <span style={{ display:"inline-flex", flexShrink:0 }}>
                          <LiveSituation liveState={game.liveState} compact />
                        </span>
                      )}
                    </span>
                  ) : minsLeft > 0 && minsLeft <= 60 ? (
                    <span style={{ fontSize:10, fontWeight:700, color:"#fbbf24", display:"flex", alignItems:"center", gap:5, fontFamily:FM, letterSpacing:"0.04em" }}>
                      <span style={{
                        width:6, height:6, borderRadius:"50%", background:"#fbbf24",
                        animation:"pulse 2s ease infinite",
                      }}/><span>{minsLeft}m</span>
                    </span>
                  ) : (
                    <span style={{ fontSize:11, color:"rgba(255,255,255,0.42)", fontWeight:500, fontVariantNumeric:"tabular-nums" }}>{game.time}</span>
                  )}
                </div>

                {/* Teams + Odds. The row itself opens the detail modal — gated
                    on !boardIsPickem for the same reason as the "View all odds"
                    button below: hiding the button alone would leave the whole
                    row as a tap target into the same wager surface. */}
                <div onClick={() => !game.completed && !game.opponentTBD && !boardIsPickem && openGameDetail(game, "main")} style={{ cursor: (game.completed || game.opponentTBD || boardIsPickem) ? "default" : "pointer" }}>
                  {/* Column headers — refined: no underline, spacing alone is enough.
                      The labels stay visible at low opacity so the scan reads
                      cleanly without a hard line drawing the eye. Hidden when
                      the opponent is TBD (no odds to label). */}
                  {!game.opponentTBD && (
                  <div style={{ display:"grid", gridTemplateColumns: gridCols, gap:4, marginBottom:8, paddingRight:2 }}>
                    <div/>
                    {oddsHeaders.map(h => (
                      <div key={h} style={{ textAlign:"center", fontSize:10, color:"rgba(255,255,255,0.32)",
                        fontWeight:600, letterSpacing:"0.01em", fontFamily:FB, padding:"2px 0" }}>{h}</div>
                    ))}
                  </div>
                  )}

                  {/* Away team row */}
                  <div style={{ display:"grid", gridTemplateColumns: game.opponentTBD ? "1fr" : gridCols, gap:4, alignItems:"center", marginBottom:3 }}>
                    <div style={{ display:"flex", alignItems:"center", gap:10, minWidth:0, paddingRight:4 }}>
                      <TeamLogo name={game.away.full} sport={game.sport} size={32} logoUrl={game.awayLogo}
                        style={{ border:"1.5px solid rgba(255,255,255,0.06)", flexShrink:0 }}
                      />
                      <div style={{ minWidth:0, flex:1 }}>
                        <div style={{
                          fontSize:14, fontWeight:700, lineHeight:1.15,
                          color: awayWinning ? "#fff" : "rgba(255,255,255,0.75)",
                          // Wrap to up to 2 lines instead of truncating with ellipsis.
                          // Long team names like "Vegas Golden Knights" and "Cleveland
                          // Cavaliers" would otherwise lose half the name. wordBreak
                          // ensures it wraps cleanly at word boundaries.
                          overflow:"hidden",
                          display:"-webkit-box", WebkitBoxOrient:"vertical", WebkitLineClamp:2,
                          wordBreak:"break-word",

                        }}>{game.away.full}</div>
                        {(game.playoff || game.awayRecord) && (
                          <div style={{
                            fontSize:10, fontWeight:600,
                            color:"rgba(255,255,255,0.40)",
                            marginTop:1,
                          }}>
                            {game.playoff
                              ? game.playoff.awayStatus
                              : game.awayRecord}
                          </div>
                        )}
                        {game.awayPitcher?.name && !game.completed && (
                          <div style={{ fontSize:10, fontWeight:600, color:"rgba(255,255,255,0.42)", marginTop:1 }}>
                            {game.awayPitcher.name}{game.awayPitcher.era ? ` · ${game.awayPitcher.era}` : ""}
                          </div>
                        )}
                        {!game.awayPitcher?.name && !game.completed && (game.sport||"").toUpperCase() === "MLB" && (
                          <div style={{ fontSize:10, fontWeight:600, color:"rgba(255,255,255,0.32)", marginTop:1 }}>SP: TBD</div>
                        )}
                        {showScore && (
                          <div style={{
                            fontSize:22, fontWeight:700, fontFamily:FM, letterSpacing:"-0.02em", marginTop:4,
                            fontVariantNumeric:"tabular-nums",
                            color: awayWinning ? "#4ade80" : "rgba(255,255,255,0.30)",

                            transition:"color 0.2s ease",
                          }}>{awayScore}</div>
                        )}
                      </div>
                    </div>
                    {!game.opponentTBD && awayPicks.map(p => (
                      <PickButton
                        key={p.key}
                        active={isPicked(game.id, p.key)}
                        topVal={p.label}
                        btmVal={p.type === 'ml' ? null : fmtVig(p.vig)}
                        rowIndex={0}
                        isML={p.type === 'ml'}
                        isLiveOdds={game.isLive && !game.completed}
                        onClick={(e) => { e.stopPropagation(); !game.completed && toggle(game, p); }}
                        disabled={game.completed}
                      />
                    ))}
                  </div>

                  {/* Divider between teams.
                      • Upcoming (not live/final/TBD): DK-style "AT" convention —
                        a left-anchored hairline, the "AT" label, then the game
                        time, reading "away AT home @ time" down the card. This
                        mirrors the standard US-sports lines layout.
                      • TBD opponent: surface the waiting state.
                      • Live/final: a plain hairline (status already shows in the
                        meta row, score shows per team). */}
                  {game.opponentTBD ? (
                    <div style={{ display:"flex", alignItems:"center", gap:10, margin:"8px 0" }}>
                      <div style={{ height:"1px", flex:1, background:"rgba(255,255,255,0.04)" }}/>
                      <span style={{ fontSize:10, color:"rgba(255,255,255,0.40)", fontWeight:600, letterSpacing:"0.04em", fontFamily:FB }}>
                        Opponent TBD · odds when matchup is set
                      </span>
                      <div style={{ height:"1px", flex:1, background:"rgba(255,255,255,0.04)" }}/>
                    </div>
                  ) : (!game.completed && !game.isLive) ? (
                    <div style={{ display:"flex", alignItems:"center", gap:8, margin:"7px 0 8px", paddingLeft:42 }}>
                      <span style={{ fontSize:10, fontWeight:700, color:"rgba(255,255,255,0.38)",
                        letterSpacing:"0.10em", fontFamily:FB }}>AT</span>
                      <div style={{ height:"1px", flex:1, background:"rgba(255,255,255,0.05)" }}/>
                      <span style={{ fontSize:10, color:"rgba(255,255,255,0.36)", fontWeight:500,
                        letterSpacing:"0.02em", fontVariantNumeric:"tabular-nums", fontFamily:FM }}>{game.time}</span>
                    </div>
                  ) : (
                    <div style={{ height:"1px", background:"rgba(255,255,255,0.04)", margin:"8px 0" }}/>
                  )}

                  {/* Home team row */}
                  <div style={{ display:"grid", gridTemplateColumns: game.opponentTBD ? "1fr" : gridCols, gap:4, alignItems:"center" }}>
                    <div style={{ display:"flex", alignItems:"center", gap:10, minWidth:0, paddingRight:4 }}>
                      <TeamLogo name={game.home.full} sport={game.sport} size={32} logoUrl={game.homeLogo}
                        style={{ border:"1.5px solid rgba(255,255,255,0.06)", flexShrink:0 }}
                      />
                      <div style={{ minWidth:0, flex:1 }}>
                        <div style={{
                          fontSize:14, fontWeight:700, lineHeight:1.15,
                          color: homeWinning ? "#fff" : "rgba(255,255,255,0.75)",
                          // Wrap to up to 2 lines instead of truncating with ellipsis.
                          // Long team names like "Vegas Golden Knights" and "Cleveland
                          // Cavaliers" would otherwise lose half the name. wordBreak
                          // ensures it wraps cleanly at word boundaries.
                          overflow:"hidden",
                          display:"-webkit-box", WebkitBoxOrient:"vertical", WebkitLineClamp:2,
                          wordBreak:"break-word",

                        }}>{game.home.full}</div>
                        {(game.playoff || game.homeRecord) && (
                          <div style={{
                            fontSize:10, fontWeight:600,
                            color:"rgba(255,255,255,0.40)",
                            marginTop:1,
                          }}>
                            {game.playoff
                              ? game.playoff.homeStatus
                              : game.homeRecord}
                          </div>
                        )}
                        {game.homePitcher?.name && !game.completed && (
                          <div style={{ fontSize:10, fontWeight:600, color:"rgba(255,255,255,0.42)", marginTop:1 }}>
                            {game.homePitcher.name}{game.homePitcher.era ? ` · ${game.homePitcher.era}` : ""}
                          </div>
                        )}
                        {!game.homePitcher?.name && !game.completed && (game.sport||"").toUpperCase() === "MLB" && (
                          <div style={{ fontSize:10, fontWeight:600, color:"rgba(255,255,255,0.32)", marginTop:1 }}>SP: TBD</div>
                        )}
                        {showScore && (
                          <div style={{
                            fontSize:22, fontWeight:700, fontFamily:FM, letterSpacing:"-0.02em", marginTop:4,
                            fontVariantNumeric:"tabular-nums",
                            color: homeWinning ? "#4ade80" : "rgba(255,255,255,0.30)",

                            transition:"color 0.2s ease",
                          }}>{homeScore}</div>
                        )}
                      </div>
                    </div>
                    {!game.opponentTBD && homePicks.map(p => (
                      <PickButton
                        key={p.key}
                        active={isPicked(game.id, p.key)}
                        topVal={p.label}
                        btmVal={p.type === 'ml' ? null : fmtVig(p.vig)}
                        rowIndex={1}
                        isML={p.type === 'ml'}
                        isLiveOdds={game.isLive && !game.completed}
                        onClick={(e) => { e.stopPropagation(); !game.completed && toggle(game, p); }}
                        disabled={game.completed}
                      />
                    ))}
                  </div>

                  {/* ── 3-WAY MONEYLINE STRIP (soccer / World Cup) ──────────────
                      Books price soccer ML as Away / Draw / Home. We surface all
                      three as one full-width row beneath the teams, mirroring the
                      book's Moneyline block. Only renders when a Draw is priced.
                      Each cell stops propagation so a tap registers as a pick
                      rather than opening the detail sheet. Picking any cell
                      conflicts-out the other two (same type:'ml'), so the strip
                      behaves as a true single-choice 3-way market. */}
                  {isThreeWay && !game.opponentTBD && (
                    <div style={{ marginTop:10 }}>
                      <div style={{ fontSize:10, color:"rgba(255,255,255,0.32)", fontWeight:600,
                        letterSpacing:"0.01em", fontFamily:FB, marginBottom:6, paddingLeft:2 }}>Moneyline</div>
                      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:6 }}>
                        {[
                          { p: awayML, name: game.away.short || game.away.full },
                          { p: drawPick, name: "Draw" },
                          { p: homeML, name: game.home.short || game.home.full },
                        ].map(({ p, name }) => {
                          const picked = isPicked(game.id, p.key);
                          return (
                            <button key={p.key}
                              onClick={(e) => { e.stopPropagation(); !game.completed && toggle(game, p); }}
                              disabled={game.completed}
                              style={{
                                padding:"9px 4px 8px", borderRadius:11, cursor: game.completed ? "default" : "pointer",
                                background: picked ? "rgba(255,107,61,0.13)" : "rgba(255,255,255,0.04)",
                                border: picked ? "1px solid rgba(255,107,61,0.40)" : "1px solid rgba(255,255,255,0.07)",
                                boxShadow: picked ? "0 0 10px rgba(255,107,61,0.16)" : "none",
                                transition:"all 0.18s cubic-bezier(0.34,1.4,0.64,1)",
                                display:"flex", flexDirection:"column", alignItems:"center", gap:3, minWidth:0,
                              }}>
                              <span style={{ fontSize:11, fontWeight:600,
                                color: picked ? T.coral : "rgba(255,255,255,0.55)",
                                fontFamily:FB, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", maxWidth:"100%" }}>{name}</span>
                              <span style={{ fontSize:15, fontWeight:800, fontFamily:FM, fontVariantNumeric:"tabular-nums",
                                color: picked ? T.coral : p.odds == null ? "rgba(255,255,255,0.30)" : "#4ade80" }}>{p.label}</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>

                {/* Live predictions notice */}
                {game.isLive && !game.completed && (
                  <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginTop:8, padding:"4px 2px 0" }}>
                    <span style={{
                      fontSize:9, fontWeight:800, color:"#4ade80", fontFamily:FM,
                      display:"flex", alignItems:"center", gap:4,
                    }}>
                      {oddsRefreshing
                        ? <><span style={{ animation:"pulse 0.7s ease infinite" }}>●</span> updating</>
                        : game.isLiveOdds ? <><span style={{color:"#4ade80"}}>●</span> live odds</> : "last odds"}
                    </span>
                    {oddsAgeLabel && <span style={{ fontSize:9, color:"rgba(255,255,255,0.32)", fontFamily:FM }}>{oddsAgeLabel}</span>}
                  </div>
                )}
              </div>

              {/* More odds button — was alarm-orange (read like a warning), now
                  neutral. Hover restores brand tone so it still feels interactive.
                  Hidden when opponent is TBD — there are no odds to view.
                  Hidden in pickem: the detail modal is a WAGER surface (Main
                  Lines = spread/total/ML, plus Player Props). A pickem league
                  forces allowedBetTypes to ["ml"], so every line in there except
                  the moneyline is rejected at submit — and the moneyline is
                  already on the card as the Winner column. Offering it builds a
                  slip that dies on commit, which is the dead-end this format
                  keeps having to design out (cf. the Player Props gate directly
                  below, and the spread/total column suppression on the board). */}
              {!game.completed && !game.opponentTBD && !boardIsPickem && (
                <div style={{ borderTop:"1px solid rgba(255,255,255,0.04)", padding:"0 14px",
                  display:"flex", alignItems:"stretch" }}>
                  <button
                    onClick={() => openGameDetail(game, "main")}
                    onMouseEnter={(e) => { e.currentTarget.style.color = "#fff"; e.currentTarget.querySelector("svg").setAttribute("stroke","#fff"); }}
                    onMouseLeave={(e) => { e.currentTarget.style.color = "rgba(255,255,255,0.55)"; e.currentTarget.querySelector("svg").setAttribute("stroke","rgba(255,255,255,0.55)"); }}
                    style={{
                      flex:1, padding:"11px 0",
                      background:"none", border:"none", cursor:"pointer",
                      fontSize:12, fontWeight:600, color:"rgba(255,255,255,0.55)", fontFamily:FB,
                      display:"flex", alignItems:"center", justifyContent:"center", gap:6,
                      transition:"color 0.15s", letterSpacing:"-0.005em",
                    }}>
                    View all odds
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.55)" strokeWidth="2.5" strokeLinecap="round" style={{ transition:"stroke 0.15s" }}><polyline points="9 18 15 12 9 6"/></svg>
                  </button>
                  {/* Player Props entry — opens the detail modal straight to the
                      Player Props tab. Only shown for sports the book prices props
                      for (basketball/football/baseball/hockey/soccer/mma/afl/rugby
                      league); the modal still gracefully shows a "props not out yet"
                      state if the fetch returns none. Props load on open only, so
                      this adds no API calls until tapped — quota-safe.
                      Hidden in pickem: a player prop isn't a winner pick, and the
                      league's allowedBetTypes (["ml"]) rejects it at submit. */}
                  {!boardIsPickem && sportHasProps(game.sportKey) && (
                    <>
                      <div style={{ width:1, background:"rgba(255,255,255,0.06)", margin:"8px 0" }}/>
                      <button
                        onClick={() => openGameDetail(game, "players")}
                        onMouseEnter={(e) => { e.currentTarget.style.color = T.coral; e.currentTarget.querySelector("polyline").style.stroke = T.coral; }}
                        onMouseLeave={(e) => { e.currentTarget.style.color = "rgba(255,255,255,0.55)"; e.currentTarget.querySelector("polyline").style.stroke = "rgba(255,255,255,0.55)"; }}
                        style={{
                          flex:1, padding:"11px 0",
                          background:"none", border:"none", cursor:"pointer",
                          fontSize:12, fontWeight:600, color:"rgba(255,255,255,0.55)", fontFamily:FB,
                          display:"flex", alignItems:"center", justifyContent:"center", gap:6,
                          transition:"color 0.15s", letterSpacing:"-0.005em",
                        }}>
                        Player Props
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.55)" strokeWidth="2.5" strokeLinecap="round" style={{ transition:"stroke 0.15s" }}><polyline points="9 18 15 12 9 6"/></svg>
                      </button>
                    </>
                  )}
                </div>
              )}

              {/* Why toggle — only shown for mock data with editorial copy */}
              {game.why && (
              <div style={{ borderTop:`1px solid ${T.border}` }}>
                <button onClick={() => setExpanded(expanded===game.id ? null : game.id)} style={{
                  width:"100%", padding:"9px 16px",
                  background:"none", border:"none", cursor:"pointer",
                  fontSize:12, fontWeight:600, color: expanded===game.id ? T.ember : T.inkMuted,
                  textAlign:"left", display:"flex", alignItems:"center", gap:5, transition:"color 0.13s",
                }}>
                  <span style={{ display:"inline-block", transform: expanded===game.id?"rotate(180deg)":"none", transition:"transform 0.2s", fontSize:10 }}>▾</span>
                  Why this game?
                </button>
                {expanded===game.id && (
                  <div style={{ padding:"10px 16px 14px", borderTop:`1px solid ${T.border}`, background:T.bgWarm, animation:"fadeUp 0.15s ease" }}>
                    <p style={{ fontSize:13, color:T.inkSec, lineHeight:1.65 }}>{game.why}</p>
                  </div>
                )}
              </div>
              )}
            </div>
          </div>
          );
        })}

        {_rowsTruncated && (
          <button
            onClick={() => { haptic.light(); _setVisibleRowCount(c => c + ROW_RENDER_CAP); }}
            style={{
              width:"100%", padding:"14px 0", marginTop:8,
              borderRadius:14, cursor:"pointer",
              background:"rgba(255,255,255,0.04)",
              border:"1px solid rgba(255,255,255,0.08)",
              color:"rgba(255,255,255,0.65)", fontSize:13, fontWeight:700,
            }}
          >
            Show more games ({_allRows.length - _rows.length} more)
          </button>
        )}

        {/* "See N more games" expander removed with the 10-game cap (2026-07).
            Every game in `filtered` now renders, so this button had nothing left
            to reveal — its label computed `filtered.length - 10` against a
            threshold that no longer exists, which for a 16-game NFL slate read
            "See 6 more games" while all 16 were already on screen. */}
            {_completed.length > 0 && (
              <button onClick={() => setShowCompleted(s => !s)} style={{
                width:"100%", padding:"10px 13px", marginTop:4,
                background:"transparent", border:`1px dashed ${T.border}`,
                borderRadius:12, color:T.inkMuted, fontSize:12, fontWeight:600,
                cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", gap:6,
              }}>
                <span style={{ transform: showCompleted ? "rotate(180deg)" : "none",
                  display:"inline-block", transition:"transform 0.2s", fontSize:10 }}>▾</span>
                {showCompleted
                  ? "Hide completed games"
                  : `${_completed.length} completed game${_completed.length !== 1 ? "s" : ""}`}
              </button>
            )}
      </div>

      {/* Floating pick slip CTA — visible on both mobile and desktop when picks are queued.
          The `floating-pick-cta` class lets it auto-hide when any modal is open
          (via the body.modal-open CSS rule). */}
      {Object.keys(picks).length > 0 && (
        <button
          className="floating-pick-cta"
          onClick={() => setSlipOpen?.(true)}
          style={{
            position:'fixed', bottom:'calc(72px + env(safe-area-inset-bottom, 0px))', left:'50%', transform:'translateX(-50%)',
            zIndex:35, display:'flex', alignItems:'center', gap:10,
            padding:'14px 28px', borderRadius:20,
            background:T.coral,
            border:'none',
            color:'#fff', fontSize:15, fontWeight:700,
            boxShadow:'0 8px 32px rgba(255,107,61,0.4), 0 2px 8px rgba(0,0,0,0.2)',
            cursor:'pointer', whiteSpace:'nowrap',
            animation:'fadeUp 0.25s ease',
            letterSpacing:'-0.01em', fontFamily:FD,
            transition:'opacity 0.18s ease',
          }}
        >
          <span style={{
            background:'rgba(255,255,255,0.2)', borderRadius:12,
            padding:'2px 10px', fontSize:13, fontWeight:700,
            minWidth:22, textAlign:'center',
          }}>{Object.keys(picks).length}</span>
          Review Slip
        </button>
      )}
      {/* Game Detail Modal */}
      {selectedGameDetail && (
        <GameDetailModal
          game={selectedGameDetail}
          onClose={() => setSelectedGameDetail(null)}
          initialTab={detailInitialTab}
          picks={picks}
          toggle={toggle}
          isPicked={isPicked}
          oddsRefreshing={oddsRefreshing}
          onOpenSlip={() => { setSelectedGameDetail(null); setSlipOpen?.(true); }}
        />
      )}
    </div>
  );
}

/* ===========================================
   PASS THE TORCH — UI Components
=========================================== */

// ── Torch Loan Button — available anytime the league is active ──────────────
function TorchLoanButton({ onClick, onInfoClick }) {
  const [pressed, setPressed] = useState(false);
  return (
    <div style={{ position:"relative", marginBottom:10 }}>
      {/* Main button — full-card tap target */}
      <button
        onClick={(e) => { e.stopPropagation(); onClick?.(); }}
        onMouseDown={() => setPressed(true)}
        onMouseUp={() => setPressed(false)}
        onMouseLeave={() => setPressed(false)}
        onTouchStart={() => setPressed(true)}
        onTouchEnd={() => setPressed(false)}
        style={{
          position:"relative", overflow:"hidden",
          width:"100%", padding:"18px 18px", borderRadius:18,
          background: pressed
            ? `linear-gradient(135deg, rgba(255,107,61,0.22), rgba(255,107,61,0.10))`
            : `linear-gradient(135deg, rgba(255,107,61,0.14), rgba(255,107,61,0.05))`,
          border:`1.5px solid ${pressed ? "rgba(255,107,61,0.45)" : "rgba(255,107,61,0.22)"}`,
          cursor:"pointer", display:"flex", alignItems:"center", gap:12,
          transition:"all 0.2s cubic-bezier(0.34,1.56,0.64,1)",
          transform: pressed ? "scale(0.97)" : "scale(1)",
          boxShadow: pressed ? "0 2px 8px rgba(255,107,61,0.2)" : "0 4px 24px rgba(255,107,61,0.14)",
          animation:"borderGlow 3s ease infinite",
      }}>
        {/* Diagonal shimmer sweep (decorative only) */}
        <span aria-hidden="true" style={{
          position:"absolute", top:0, left:0, bottom:0, width:"40%", pointerEvents:"none",
          background:"linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.08) 50%, transparent 100%)",
          animation:"torchShimmer 4.5s ease-in-out infinite",
          mixBlendMode:"screen",
        }}/>
        {/* Flame icon — pulses to draw the eye */}
        <div style={{
          width:46, height:46, borderRadius:14, flexShrink:0, position:"relative", zIndex:1,
          background:T.coral,
          display:"flex", alignItems:"center", justifyContent:"center",
          animation:"torchFlamePulse 2.2s ease-in-out infinite",
        }}>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="1.8" strokeLinecap="round">
            <path d="M12 22c-4.97 0-9-2.69-9-6v-.18C4.32 12.47 7.6 10 8 6c.02-.17.17-.3.34-.28C10.09 5.96 12 8 12 8s1.91-2.04 3.66-2.28c.17-.02.32.11.34.28.4 4 3.68 6.47 5 9.82V16c0 3.31-4.03 6-9 6z"/>
          </svg>
        </div>
        {/* Title + subtitle */}
        <div style={{ flex:1, textAlign:"left", position:"relative", zIndex:1, minWidth:0 }}>
          <div style={{
            fontFamily:FD, fontSize:20, fontWeight:700, color:"#fff",
            letterSpacing:"0.02em", textTransform:"uppercase", lineHeight:1,
            marginBottom:3,
          }}>
            Pass the Torch
          </div>
          <div style={{ fontSize:12, color:"rgba(255,255,255,0.55)", letterSpacing:"0.01em", lineHeight:1.35 }}>
            Split a pick. Both put up. Both win or lose.
          </div>
        </div>
        {/* Chevron */}
        <div style={{
          width:28, height:28, borderRadius:8, flexShrink:0, position:"relative", zIndex:1,
          background:"rgba(255,107,61,0.14)", border:"1px solid rgba(255,107,61,0.25)",
          display:"flex", alignItems:"center", justifyContent:"center",
        }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={T.coral} strokeWidth="2.5" strokeLinecap="round"><path d="M9 18l6-6-6-6"/></svg>
        </div>
      </button>
      {/* Info icon — floats top-right over the card, separate tap target */}
      {onInfoClick && (
        <button
          onClick={(e) => { e.stopPropagation(); onInfoClick?.(); }}
          aria-label="How Pass the Torch works"
          style={{
            position:"absolute", top:-7, right:-7, zIndex:3,
            width:26, height:26, minWidth:26, minHeight:26, borderRadius:"50%",
            background:"rgba(16,18,26,0.96)", border:"1px solid rgba(255,255,255,0.16)",
            cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center",
            color:"rgba(255,255,255,0.75)", fontSize:13, fontWeight:700,
            fontFamily:"Georgia, serif", fontStyle:"italic",
            backdropFilter:"blur(8px)", WebkitBackdropFilter:"blur(8px)",
            boxShadow:"0 3px 10px rgba(0,0,0,0.35)",
            transition:"all 0.2s",
          }}
        >
          i
        </button>
      )}
    </div>
  );
}

// ── Torch Info Modal — "how it works" explainer ──────────────────────────────
function TorchInfoModal({ onClose, onStart }) {
  useModalOpen(true);  // Hide bottom nav while this modal is open
  const dimText = "rgba(255,255,255,0.3)";
  const secText = "rgba(255,255,255,0.55)";
  const modalBg = "#12141C";

  return (
    <div onClick={onClose} style={{
      position:"fixed", inset:0, zIndex:9999,
      background:"rgba(0,0,0,0.88)", backdropFilter:"blur(12px)",
      display:"flex", alignItems:"flex-end", justifyContent:"center",
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background:modalBg, borderRadius:"24px 24px 0 0",
        border:"1px solid rgba(255,255,255,0.06)", borderBottom:"none",
        width:"100%", maxWidth:480, maxHeight:"calc(92vh - env(safe-area-inset-top, 0px))",
        overflow:"auto", WebkitOverflowScrolling:"touch",
        paddingBottom:"calc(80px + env(safe-area-inset-bottom, 0px))",
        animation:"fadeUp 0.25s ease",
      }}>
        <div style={{ height:2, background:"linear-gradient(90deg,transparent," + T.coral + ",transparent)" }}/>
        <div style={{ padding:"22px 20px 26px" }}>

          {/* Header */}
          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:20 }}>
            <div style={{ display:"flex", alignItems:"center", gap:10 }}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={T.coral} strokeWidth="1.8">
                <path d="M12 22c-4.97 0-9-2.69-9-6v-.18C4.32 12.47 7.6 10 8 6c.02-.17.17-.3.34-.28C10.09 5.96 12 8 12 8s1.91-2.04 3.66-2.28c.17-.02.32.11.34.28.4 4 3.68 6.47 5 9.82V16c0 3.31-4.03 6-9 6z"/>
              </svg>
              <span style={{ fontFamily:FD, fontSize:18, fontWeight:700, color:"#fff" }}>
                How it Works
              </span>
            </div>
            <button onClick={onClose} style={{
              background:"rgba(255,255,255,0.06)", border:"1px solid rgba(255,255,255,0.06)",
              borderRadius:10, width:36, height:36, minWidth:44, minHeight:44, cursor:"pointer",
              color:secText, display:"flex", alignItems:"center", justifyContent:"center", fontSize:16,
            }} aria-label="Close">✕</button>
          </div>

          {/* Editorial tagline */}
          <div style={{
            fontFamily:FD, fontSize:30, fontWeight:700, color:"#fff",
            lineHeight:1.02, letterSpacing:"-0.01em", marginBottom:8, textTransform:"uppercase",
          }}>
            Split a pick<br/>with a league mate.
          </div>
          <div style={{ fontSize:13, color:secText, lineHeight:1.5, marginBottom:20 }}>
            Both sides match. One pick. Shared risk, shared payout.
          </div>

          {/* Example math */}
          <div style={{ fontSize:10, fontWeight:700, color:dimText, letterSpacing:"0.08em", marginBottom:8 }}>EXAMPLE — 50 PTS EACH</div>
          <div style={{
            padding:"14px", borderRadius:14, marginBottom:22,
            background:"rgba(255,107,61,0.04)", border:"1px solid rgba(255,107,61,0.10)",
          }}>
            <div style={{ display:"flex", gap:8, marginBottom:10 }}>
              <div style={{ flex:1, padding:"10px 8px", borderRadius:10, background:"rgba(255,255,255,0.03)", border:"1px solid rgba(255,255,255,0.04)", textAlign:"center" }}>
                <div style={{ fontSize:9, fontWeight:700, color:dimText, letterSpacing:"0.06em", marginBottom:3 }}>YOU PUT UP</div>
                <div style={{ fontFamily:FM, fontSize:18, fontWeight:700, color:"#fff" }}>50</div>
              </div>
              <div style={{ alignSelf:"center", color:T.coral, fontWeight:800, fontSize:14 }}>+</div>
              <div style={{ flex:1, padding:"10px 8px", borderRadius:10, background:"rgba(255,255,255,0.03)", border:"1px solid rgba(255,255,255,0.04)", textAlign:"center" }}>
                <div style={{ fontSize:9, fontWeight:700, color:dimText, letterSpacing:"0.06em", marginBottom:3 }}>THEY MATCH</div>
                <div style={{ fontFamily:FM, fontSize:18, fontWeight:700, color:"#fff" }}>50</div>
              </div>
              <div style={{ alignSelf:"center", color:T.coral, fontWeight:800, fontSize:14 }}>=</div>
              <div style={{ flex:1, padding:"10px 8px", borderRadius:10, background:"rgba(255,107,61,0.08)", border:"1px solid rgba(255,107,61,0.20)", textAlign:"center" }}>
                <div style={{ fontSize:9, fontWeight:700, color:T.coral, letterSpacing:"0.06em", marginBottom:3, opacity:0.8 }}>COMBINED</div>
                <div style={{ fontFamily:FM, fontSize:18, fontWeight:700, color:T.coral }}>100</div>
              </div>
            </div>
            <div style={{ fontSize:11.5, color:"rgba(255,255,255,0.5)", lineHeight:1.5, textAlign:"center" }}>
              Win it, split the payout 50/50. Lose it, both take the hit.
            </div>
          </div>

          {/* Three-beat explainer */}
          <div style={{ display:"flex", flexDirection:"column", gap:12, marginBottom:22 }}>
            {[
              { n:"1", t:"Pick a league mate + amount", b:"Minimum 10 pts per side. Max 100. Both sides always match." },
              { n:"2", t:"Build the pick together", b:"You choose the game and line. They co-stake whatever you land on." },
              { n:"3", t:"Win together, lose together", b:"Real skin in the game on both sides. Same profit as a solo bet, halved." },
            ].map((step) => (
              <div key={step.n} style={{ display:"flex", gap:12, alignItems:"flex-start" }}>
                <div style={{
                  width:28, height:28, minWidth:28, borderRadius:"50%", flexShrink:0,
                  background:"rgba(255,107,61,0.10)", border:"1px solid rgba(255,107,61,0.22)",
                  display:"flex", alignItems:"center", justifyContent:"center",
                  fontFamily:FM, fontSize:12, fontWeight:700, color:T.coral,
                }}>{step.n}</div>
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:13.5, fontWeight:700, color:"#fff", marginBottom:2 }}>{step.t}</div>
                  <div style={{ fontSize:12, color:secText, lineHeight:1.5 }}>{step.b}</div>
                </div>
              </div>
            ))}
          </div>

          {/* Buttons */}
          <div style={{ display:"flex", gap:8 }}>
            <button onClick={onClose} style={{
              flex:1, padding:"14px", borderRadius:14, cursor:"pointer",
              background:"rgba(255,255,255,0.05)", border:"1px solid rgba(255,255,255,0.08)",
              color:secText, fontSize:14, fontWeight:600,
            }}>Got it</button>
            {onStart && (
              <button onClick={() => { onClose?.(); onStart(); }} style={{
                flex:1.4, padding:"14px", borderRadius:14, cursor:"pointer", border:"none",
                background:"linear-gradient(135deg," + T.coral + "," + T.coralDeep + ")",
                color:"#fff", fontSize:14, fontWeight:700,
                boxShadow:"0 6px 24px rgba(255,107,61,0.3), 0 2px 0 rgba(255,255,255,0.1) inset",
                display:"flex", alignItems:"center", justifyContent:"center", gap:6,
              }}>
                Try It
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Torch Request Modal — simplified: pick lender + amount, then go to Games ──
function TorchRequestModal({ onClose, user, leagueId, leagueMinWager = 0, standings = [], onSubmit, onGoToGames, myBalance = 0 }) {
  const { closing, requestClose } = useSheetClose(onClose, 200);
  useModalOpen(!closing);  // Hide bottom nav while this modal is open

  // Three-step flow: mode → recipient → amount/message.
  // mode = null   → user hasn't chosen yet (step 0, the mode picker)
  // mode = "ask"  → existing flow: I'm broke, find someone to fund my pick
  // mode = "offer"→ new flow: I have points, offer to fund a broke teammate's pick
  const [mode, setMode] = useState(null);
  const [selectedTarget, setSelectedTarget] = useState(null); // lender (ask) OR recipient (offer)
  // Free-form numeric input. Stored as a STRING so the user can type, clear,
  // and re-enter without React forcing a "0" placeholder. We coerce to a
  // number (`numericAmount`) at validation/submit time.
  const [amount, setAmount] = useState("");
  const [message, setMessage] = useState("");
  const [step, setStep] = useState(0); // 0 = mode picker, 1 = pick teammate, 2 = amount/msg

  const myUid = user?.uid || "";
  // Effective minimum is the LARGER of the system floor and the league's
  // per-pick minWager. A torch loan smaller than the league's per-pick rule
  // is unusable (the borrower can't actually submit a pick at that amount —
  // PickSlipDrawer rejects sub-minWager wagers, leaving the borrower trapped
  // between a capped torch budget and a higher pick floor). Mirroring this
  // floor in the modal blocks invalid loans before they're ever drafted.
  const baseMin = typeof TORCH_MIN_LOAN === "number" ? TORCH_MIN_LOAN : 1;
  const effectiveMin = Math.max(baseMin, Number(leagueMinWager) || 0);
  const isLeagueRule = effectiveMin > baseMin;
  // Kept for backwards compatibility with the rest of this file's references.
  const minLoan = effectiveMin;
  const safeStandings = Array.isArray(standings) ? standings : [];

  // ASK mode: list of teammates wealthy enough to lend (>= effectiveMin)
  const lenders = safeStandings
    .filter(m => m && m.id !== myUid && (m.balance || 0) >= effectiveMin)
    .sort((a, b) => (b.balance || 0) - (a.balance || 0));

  // OFFER mode: ANY teammate, regardless of balance. Lower balances surface first
  // (a "spot" is more valuable to a broke user) but a wealthy teammate is also a
  // valid target — the lender can share a pick with anyone.
  const recipients = safeStandings
    .filter(m => m && m.id !== myUid)
    .sort((a, b) => (a.balance || 0) - (b.balance || 0));

  // Stake bound depends on mode:
  //   ask   → lender's balance (selectedTarget) bounds us
  //   offer → MY balance bounds us (I'm the lender now)
  const maxAffordable = mode === "offer"
    ? (myBalance || 0)
    : (selectedTarget ? (selectedTarget.balance || 0) : 0);

  // Coerce the string input to a clean integer for validation. Empty/garbage
  // input becomes 0, which fails the `>= minLoan` check below — exactly what
  // we want, since the CTA should be disabled until the user types a real
  // amount.
  const numericAmount = Math.floor(Number(amount) || 0);
  const canSubmitAmount =
    numericAmount >= minLoan &&
    numericAmount <= maxAffordable &&
    maxAffordable >= minLoan; // gate when target/me can't even cover the minimum

  // If the target switches and the typed amount no longer fits, clear the
  // input so the user has to re-enter (rather than silently capping them).
  useEffect(() => {
    if (numericAmount > maxAffordable && amount !== "") {
      setAmount("");
    }
  }, [maxAffordable, numericAmount, amount]);

  const modalBg = "#12141C";
  const cardBorder = "rgba(255,255,255,0.06)";
  const dimText = "rgba(255,255,255,0.3)";
  const secText = "rgba(255,255,255,0.5)";

  const headerTitle = step === 0
    ? "Pass the Torch"
    : step === 1
      ? (mode === "offer" ? "Choose a Teammate to Spot" : "Choose a Teammate to Ask")
      : (mode === "offer" ? "How Much to Spot Them?" : "How Much to Ask For?");

  return (
    <div onClick={() => requestClose()} style={{
      position:"fixed", inset:0, zIndex:9999,
      background:"rgba(0,0,0,0.88)", backdropFilter:"blur(8px)",
      display:"flex", alignItems:"flex-end", justifyContent:"center",
      animation: closing ? "slipFadeOut 0.18s ease both" : "fadeIn 0.2s ease both",
      pointerEvents: closing ? "none" : "auto",
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background:modalBg, borderRadius:"24px 24px 0 0",
        border:"1px solid " + cardBorder, borderBottom:"none",
        width:"100%", maxWidth:480, maxHeight:"calc(92vh - env(safe-area-inset-top, 0px))", overflow:"auto", WebkitOverflowScrolling:"touch",
        paddingBottom:"calc(80px + env(safe-area-inset-bottom, 0px))",
        animation: closing ? "fadeExitDown 0.2s cubic-bezier(0.4,0,1,1) both" : "fadeUp 0.26s cubic-bezier(0.32,0.72,0,1)",
      }}>
        <div style={{ height:2, background:"linear-gradient(90deg,transparent," + T.coral + ",transparent)" }}/>
        <div style={{ padding:"20px 20px 28px" }}>

          {/* Header */}
          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:20 }}>
            <div style={{ display:"flex", alignItems:"center", gap:10 }}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={T.coral} strokeWidth="1.8">
                <path d="M12 22c-4.97 0-9-2.69-9-6v-.18C4.32 12.47 7.6 10 8 6c.02-.17.17-.3.34-.28C10.09 5.96 12 8 12 8s1.91-2.04 3.66-2.28c.17-.02.32.11.34.28.4 4 3.68 6.47 5 9.82V16c0 3.31-4.03 6-9 6z"/>
              </svg>
              <span style={{ fontFamily:FD, fontSize:18, fontWeight:700, color:"#fff" }}>
                {headerTitle}
              </span>
            </div>
            <button onClick={() => requestClose()} style={{
              background:"rgba(255,255,255,0.06)", border:"1px solid " + cardBorder,
              borderRadius:10, width:36, height:36, minWidth:44, minHeight:44, cursor:"pointer", color:secText,
              display:"flex", alignItems:"center", justifyContent:"center", fontSize:16,
            }} aria-label="Close">✕</button>
          </div>

          {/* Step dots — 3 steps now (mode → teammate → amount) */}
          <div style={{ display:"flex", gap:4, marginBottom:20 }}>
            {[0,1,2].map(s => (
              <div key={s} style={{
                flex:1, height:3, borderRadius:2,
                background: s <= step ? T.coral : "rgba(255,255,255,0.06)",
              }}/>
            ))}
          </div>

          {/* STEP 0: Mode picker */}
          {step === 0 && (
            <div>
              <div style={{ fontSize:12, color:dimText, marginBottom:14, fontWeight:600 }}>
                What do you want to do?
              </div>

              {/* ASK CARD */}
              <button
                onClick={() => { setMode("ask"); setStep(1); }}
                style={{
                  display:"flex", alignItems:"center", gap:12, padding:"16px",
                  borderRadius:16, cursor:"pointer", width:"100%", textAlign:"left",
                  background:"rgba(255,255,255,0.03)", border:"1px solid " + cardBorder,
                  marginBottom:8,
                }}
              >
                <div style={{
                  width:42, height:42, borderRadius:12, flexShrink:0,
                  background:"rgba(255,107,61,0.10)", border:"1px solid rgba(255,107,61,0.16)",
                  display:"flex", alignItems:"center", justifyContent:"center",
                }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={T.coral} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 5v14M5 12l7 7 7-7"/>
                  </svg>
                </div>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontSize:14, fontWeight:700, color:"#fff", marginBottom:2 }}>Ask for a torch</div>
                  <div style={{ fontSize:11.5, color:secText, lineHeight:1.4 }}>
                    Low on points? A teammate funds your pick. If it hits, you win the payout and they win 1.5x for spotting you.
                  </div>
                </div>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={dimText} strokeWidth="2"><path d="M9 18l6-6-6-6"/></svg>
              </button>

              {/* OFFER CARD — disabled only if user has 0 points or no other members in the league */}
              <button
                onClick={() => {
                  if ((myBalance || 0) < minLoan) return;
                  if (recipients.length === 0) return;
                  setMode("offer"); setStep(1);
                }}
                disabled={(myBalance || 0) < minLoan || recipients.length === 0}
                style={{
                  display:"flex", alignItems:"center", gap:12, padding:"16px",
                  borderRadius:16, cursor: ((myBalance || 0) < minLoan || recipients.length === 0) ? "not-allowed" : "pointer",
                  width:"100%", textAlign:"left",
                  background: ((myBalance || 0) < minLoan || recipients.length === 0) ? "rgba(255,255,255,0.015)" : "rgba(255,255,255,0.03)",
                  border:"1px solid " + cardBorder,
                  opacity: ((myBalance || 0) < minLoan || recipients.length === 0) ? 0.55 : 1,
                }}
              >
                <div style={{
                  width:42, height:42, borderRadius:12, flexShrink:0,
                  background:"rgba(217,164,65,0.10)", border:"1px solid rgba(217,164,65,0.18)",
                  display:"flex", alignItems:"center", justifyContent:"center",
                }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={T.gold} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 19V5M5 12l7-7 7 7"/>
                  </svg>
                </div>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontSize:14, fontWeight:700, color:"#fff", marginBottom:2 }}>Offer a torch</div>
                  <div style={{ fontSize:11.5, color:secText, lineHeight:1.4 }}>
                    {(myBalance || 0) < minLoan
                      ? (isLeagueRule
                          ? `Need at least ${minLoan} pts (this league requires ${leagueMinWager} pts/pick). You have ${(myBalance || 0).toLocaleString()}.`
                          : `You need at least ${minLoan} pts to offer a torch.`)
                      : recipients.length === 0
                      ? "No teammates in this league yet."
                      : "Pick a teammate to ride along on your pick. You fund it — if it hits, you win 1.5x and they win the payout."}
                  </div>
                </div>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={dimText} strokeWidth="2"><path d="M9 18l6-6-6-6"/></svg>
              </button>
            </div>
          )}

          {/* STEP 1: Choose teammate (lender if asking, recipient if offering) */}
          {step === 1 && (
            <div>
              <button onClick={() => { setStep(0); setMode(null); setSelectedTarget(null); }} style={{
                background:"none", border:"none", cursor:"pointer", color:secText,
                fontSize:12, fontWeight:600, marginBottom:16, padding:0, display:"flex", alignItems:"center", gap:4,
              }}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>
                Back
              </button>

              <div style={{ fontSize:12, color:dimText, marginBottom:12, fontWeight:600 }}>
                {mode === "offer"
                  ? "Pick a teammate to ride along. You fund the pick — both of you win if it hits."
                  : "Pick a teammate to fund your bet. They risk the points — if it hits, you both win."}
              </div>

              {(() => {
                const list = mode === "offer" ? recipients : lenders;
                if (list.length === 0) {
                  return (
                    <div style={{ padding:"32px 16px", textAlign:"center" }}>
                      <div style={{ fontSize:14, color:secText, marginBottom:4 }}>
                        {mode === "offer"
                          ? "No teammates in this league yet"
                          : isLeagueRule
                            ? `No teammates with at least ${minLoan.toLocaleString()} pts`
                            : "No teammates with points to lend right now"}
                      </div>
                      <div style={{ fontSize:12, color:dimText, lineHeight:1.5, maxWidth:280, margin:"0 auto" }}>
                        {mode === "offer"
                          ? "Invite some friends and try again."
                          : isLeagueRule
                            ? `This league requires a min ${leagueMinWager} pt wager per pick — torches must meet that floor. Wait for next payday or check back later.`
                            : "Wait for next payday or check back later."}
                      </div>
                    </div>
                  );
                }
                return (
                  <div style={{ display:"flex", flexDirection:"column", gap:4 }}>
                    {list.map(m => (
                      <button key={m.id} onClick={() => { setSelectedTarget(m); setStep(2); }} style={{
                        display:"flex", alignItems:"center", gap:12, padding:"14px 16px",
                        borderRadius:16, cursor:"pointer", width:"100%", textAlign:"left",
                        background:"rgba(255,255,255,0.03)", border:"1px solid " + cardBorder,
                        transition:"all 0.2s",
                      }}>
                        <div style={{
                          width:40, height:40, borderRadius:14, flexShrink:0,
                          background:"linear-gradient(135deg,rgba(255,255,255,0.08),rgba(255,255,255,0.03))",
                          display:"flex", alignItems:"center", justifyContent:"center",
                          fontSize:14, fontWeight:700, color:secText,
                        }}>{(m.username || "?")[0].toUpperCase()}</div>
                        <div style={{ flex:1 }}>
                          <div style={{ fontSize:14, fontWeight:600, color:"#fff" }}>{m.username || "Unknown"}</div>
                          <div style={{ fontSize:11, color:dimText, marginTop:2 }}>
                            {(m.balance || 0).toLocaleString()} pts{mode === "ask" ? " available" : ""}
                          </div>
                        </div>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={dimText} strokeWidth="2"><path d="M9 18l6-6-6-6"/></svg>
                      </button>
                    ))}
                  </div>
                );
              })()}
            </div>
          )}

          {/* STEP 2: Amount + message, then go to Games */}
          {step === 2 && selectedTarget && (
            <div>
              <button onClick={() => setStep(1)} style={{
                background:"none", border:"none", cursor:"pointer", color:secText,
                fontSize:12, fontWeight:600, marginBottom:16, padding:0, display:"flex", alignItems:"center", gap:4,
              }}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>
                Back
              </button>

              {/* Selected target card — verb shifts on mode */}
              <div style={{
                padding:"12px 14px", borderRadius:14, marginBottom:18,
                background: mode === "offer" ? "rgba(217,164,65,0.06)" : "rgba(255,107,61,0.06)",
                border: `1px solid ${mode === "offer" ? "rgba(217,164,65,0.15)" : "rgba(255,107,61,0.15)"}`,
                display:"flex", alignItems:"center", gap:10,
              }}>
                <div style={{
                  width:36, height:36, borderRadius:12,
                  background: mode === "offer"
                    ? `linear-gradient(135deg,${T.gold},#B88A2C)`
                    : T.coral,
                  display:"flex", alignItems:"center", justifyContent:"center",
                  fontSize:14, fontWeight:700, color:"#fff",
                }}>{(selectedTarget.username || "?")[0].toUpperCase()}</div>
                <div>
                  <div style={{ fontSize:13, fontWeight:700, color:"#fff" }}>
                    {mode === "offer"
                      ? `Sharing a pick with ${selectedTarget.username || "Unknown"}`
                      : `Asking ${selectedTarget.username || "Unknown"} to fund`}
                  </div>
                  <div style={{ fontSize:11, color:dimText, marginTop:1 }}>
                    {(selectedTarget.balance || 0).toLocaleString()} pts {mode === "ask" ? "available" : "balance"}
                  </div>
                </div>
              </div>

              {/* Amount */}
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"baseline", marginBottom:8 }}>
                <div style={{ fontSize:10, fontWeight:700, color:dimText, letterSpacing:"0.08em" }}>
                  {mode === "offer" ? "WAGER (YOU FUND)" : "WAGER (THEY FUND)"}
                </div>
                <div style={{ fontSize:10, fontWeight:700, color:dimText, letterSpacing:"0.02em" }}>
                  {mode === "offer"
                    ? `YOU HAVE ${(myBalance || 0).toLocaleString()} PTS`
                    : `THEY HAVE ${(selectedTarget.balance || 0).toLocaleString()} PTS`}
                </div>
              </div>
              {maxAffordable < minLoan ? (
                <div style={{
                  padding:"14px 12px", borderRadius:12, marginBottom:18,
                  background:"rgba(248,113,113,0.05)", border:"1px solid rgba(248,113,113,0.10)",
                  textAlign:"center", fontSize:12, color:"rgba(248,113,113,0.7)", lineHeight:1.5,
                }}>
                  {mode === "offer"
                    ? (isLeagueRule
                        ? `You need at least ${minLoan} pts to torch in this league (league min wager is ${leagueMinWager} pts per pick). You have ${(myBalance || 0).toLocaleString()}.`
                        : `You don't have enough points (need at least ${minLoan}).`)
                    : (isLeagueRule
                        ? `${selectedTarget.username} only has ${(selectedTarget.balance || 0).toLocaleString()} pts — this league requires a min ${leagueMinWager} pt wager per pick. Pick a wealthier teammate.`
                        : `${selectedTarget.username} doesn't have enough points to fund a torch (need at least ${minLoan}). Pick a different teammate.`)}
                </div>
              ) : (
                <>
                  <input
                    type="number"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    min={minLoan}
                    max={maxAffordable}
                    step={1}
                    value={amount}
                    onChange={e => {
                      // Strip non-digits, allow empty string, clamp at maxAffordable.
                      // Don't clamp at minLoan during typing — the CTA gate handles
                      // that — otherwise a user typing "1000" would have it snap to
                      // a partial value as they type.
                      const raw = (e.target.value || "").replace(/[^\d]/g, "");
                      if (raw === "") { setAmount(""); return; }
                      const n = Math.floor(Number(raw));
                      if (!Number.isFinite(n) || n < 0) { setAmount(""); return; }
                      const capped = n > maxAffordable ? maxAffordable : n;
                      setAmount(String(capped));
                    }}
                    placeholder={isLeagueRule ? `${minLoan}+ pts` : "How much?"}
                    style={{
                      width:"100%", padding:"16px 14px", borderRadius:14,
                      background:"rgba(255,255,255,0.03)",
                      border:`1px solid ${numericAmount > 0 && !canSubmitAmount ? "rgba(248,113,113,0.35)" : cardBorder}`,
                      color:"#fff", fontSize:22, fontFamily:FM, fontWeight:700,
                      letterSpacing:"-0.01em", textAlign:"center",
                      outline:"none", marginBottom:10, boxSizing:"border-box",
                      WebkitAppearance:"none", MozAppearance:"textfield",
                    }}
                  />
                  <div style={{
                    display:"flex", justifyContent:"space-between",
                    fontSize:11, color:dimText, marginBottom:18, padding:"0 4px",
                  }}>
                    <span>
                      Min {minLoan.toLocaleString()} pts
                      {isLeagueRule && (
                        <span style={{ color:"#FF8B6E", marginLeft:4, fontWeight:600 }}>· league rule</span>
                      )}
                    </span>
                    <span>Max {maxAffordable.toLocaleString()} pts</span>
                  </div>
                  {isLeagueRule && (
                    <div style={{
                      fontSize:10.5, color:dimText, marginTop:-12, marginBottom:18, padding:"0 4px",
                      lineHeight:1.5,
                    }}>
                      This league requires a minimum {leagueMinWager} pt wager per pick — torches must meet that floor so the {mode === "offer" ? "recipient" : "borrower"} can actually use it.
                    </div>
                  )}
                </>
              )}

              {/* Message */}
              <div style={{ fontSize:10, fontWeight:700, color:dimText, letterSpacing:"0.08em", marginBottom:8 }}>MESSAGE (OPTIONAL)</div>
              <input
                value={message} onChange={e => setMessage(e.target.value)}
                maxLength={80}
                placeholder={mode === "offer" ? "I got you — let's eat" : "Got a live one tonight"}
                style={{
                  width:"100%", padding:"12px 14px", borderRadius:12,
                  background:"rgba(255,255,255,0.03)", border:"1px solid " + cardBorder,
                  color:"rgba(255,255,255,0.8)", fontSize:14, fontFamily:"inherit",
                  outline:"none", marginBottom:20, boxSizing:"border-box",
                }}
              />

              {/* Stake preview — shows YOU PUT UP / THEY FUND with the perspective swapped for offer mode */}
              <div style={{
                padding:"14px", borderRadius:14, marginBottom:20,
                background:"rgba(255,107,61,0.04)", border:"1px solid rgba(255,107,61,0.10)",
                opacity: numericAmount > 0 ? 1 : 0.5,
                transition:"opacity 0.2s",
              }}>
                <div style={{ display:"flex", gap:8, marginBottom:10 }}>
                  <div style={{ flex:1, padding:"10px 8px", borderRadius:10, background:"rgba(255,255,255,0.03)", border:"1px solid rgba(255,255,255,0.04)", textAlign:"center" }}>
                    <div style={{ fontSize:9, fontWeight:700, color:dimText, letterSpacing:"0.06em", marginBottom:3 }}>
                      {mode === "offer" ? "YOU FUND" : "YOU PUT UP"}
                    </div>
                    <div style={{ fontFamily:FM, fontSize:18, fontWeight:700, color:"#fff" }}>
                      {mode === "offer" ? numericAmount : 0}
                    </div>
                  </div>
                  <div style={{ alignSelf:"center", color:T.coral, fontWeight:800, fontSize:14 }}>+</div>
                  <div style={{ flex:1, padding:"10px 8px", borderRadius:10, background:"rgba(255,255,255,0.03)", border:"1px solid rgba(255,255,255,0.04)", textAlign:"center" }}>
                    <div style={{ fontSize:9, fontWeight:700, color:dimText, letterSpacing:"0.06em", marginBottom:3 }}>
                      {mode === "offer" ? "THEY PUT UP" : "THEY FUND"}
                    </div>
                    <div style={{ fontFamily:FM, fontSize:18, fontWeight:700, color:"#fff" }}>
                      {mode === "offer" ? 0 : numericAmount}
                    </div>
                  </div>
                  <div style={{ alignSelf:"center", color:T.coral, fontWeight:800, fontSize:14 }}>=</div>
                  <div style={{ flex:1, padding:"10px 8px", borderRadius:10, background:"rgba(255,107,61,0.08)", border:"1px solid rgba(255,107,61,0.20)", textAlign:"center" }}>
                    <div style={{ fontSize:9, fontWeight:700, color:T.coral, letterSpacing:"0.06em", marginBottom:3, opacity:0.8 }}>WAGER</div>
                    <div style={{ fontFamily:FM, fontSize:18, fontWeight:700, color:T.coral }}>{numericAmount}</div>
                  </div>
                </div>
                <div style={{ fontSize:11.5, color:"rgba(255,255,255,0.5)", lineHeight:1.5, textAlign:"center" }}>
                  {mode === "offer"
                    ? <>If it hits, <span style={{ color:"#4ade80", fontWeight:700 }}>you win 1.5x</span> and they win the payout. If it misses, only you take the hit.</>
                    : <>If it hits, you win the payout and <span style={{ color:"#4ade80", fontWeight:700 }}>they win 1.5x</span> for spotting you. If it misses, only they take the hit.</>}
                </div>
              </div>

              {/* CTA: Go to Games to pick — payload now includes offerMode flag and recipientUid for offer flow */}
              <button
                onClick={() => {
                  if (!canSubmitAmount) return;
                  const payload = mode === "offer"
                    ? {
                        offerMode: true,
                        recipientUid: selectedTarget.id,
                        recipientName: selectedTarget.username || "Unknown",
                        amount: numericAmount,
                        message,
                        leagueId,
                      }
                    : {
                        offerMode: false,
                        lenderUid: selectedTarget.id,
                        lenderName: selectedTarget.username || "Unknown",
                        amount: numericAmount,
                        message,
                        leagueId,
                      };
                  onSubmit?.(payload);
                  requestClose();
                  if (onGoToGames) onGoToGames();
                }}
                disabled={!canSubmitAmount}
                style={{
                  width:"100%", padding:"16px", borderRadius:16, border:"none",
                  cursor: !canSubmitAmount ? "not-allowed" : "pointer",
                  background: !canSubmitAmount
                    ? "rgba(255,255,255,0.05)"
                    : "linear-gradient(135deg," + T.coral + "," + T.coralDeep + ")",
                  color: !canSubmitAmount ? "rgba(255,255,255,0.3)" : "#fff",
                  fontSize:15, fontWeight:700,
                  boxShadow: !canSubmitAmount ? "none" : "0 6px 24px rgba(255,107,61,0.3), 0 2px 0 rgba(255,255,255,0.1) inset",
                  display:"flex", alignItems:"center", justifyContent:"center", gap:8,
                  transition:"all 0.2s",
                }}
              >
                Browse Games & Pick
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Torch Inbox Card — lender reviews a request ──────────────────────────────
function TorchInboxCard({ loan, onAccept, onDecline, accepting }) {
  // Tracks the post-accept success state so we can morph the button to a
  // checkmark before the card animates out. Mirrors the PickSlipDrawer pattern —
  // gives the "I just funded my friend's pick" moment some weight.
  const [accepted, setAccepted] = useState(false);
  return (
    <div style={{
      margin:"8px 10px", borderRadius:18, overflow:"hidden",
      background:"linear-gradient(135deg, rgba(255,107,61,0.06), rgba(255,107,61,0.02))",
      border:"1px solid rgba(255,107,61,0.15)",
      boxShadow:"0 4px 20px rgba(255,107,61,0.06)",
      animation:"fadeUp 0.25s ease both",
    }}>
      <div style={{ height:2, background:`linear-gradient(90deg,${T.coral},rgba(255,107,61,0))` }}/>
      <div style={{ padding:"16px" }}>
        {/* Header */}
        <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:14 }}>
          <div style={{
            width:36, height:36, borderRadius:12,
            background:T.coral,
            display:"flex", alignItems:"center", justifyContent:"center",
          }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="1.8">
              <path d="M12 22c-4.97 0-9-2.69-9-6v-.18C4.32 12.47 7.6 10 8 6c.02-.17.17-.3.34-.28C10.09 5.96 12 8 12 8s1.91-2.04 3.66-2.28c.17-.02.32.11.34.28.4 4 3.68 6.47 5 9.82V16c0 3.31-4.03 6-9 6z"/>
            </svg>
          </div>
          <div>
            <div style={{ fontSize:14, fontWeight:700, color:"#fff" }}>Pass the Torch</div>
            <div style={{ fontSize:12, color:"rgba(255,255,255,0.4)" }}>{loan.borrowerName} wants you to fund a pick</div>
          </div>
        </div>

        {/* Details */}
        <div style={{ background:"rgba(255,255,255,0.03)", borderRadius:12, padding:"12px", marginBottom:12, border:"1px solid rgba(255,255,255,0.04)" }}>
          <div style={{ display:"flex", justifyContent:"space-between", marginBottom:8 }}>
            <span style={{ fontSize:11, color:"rgba(255,255,255,0.35)" }}>You'd put up</span>
            <span style={{ fontSize:14, fontWeight:700, color:"#fff", fontFamily:FM }}>{loan.totalWager || loan.amount} pts</span>
          </div>
          <div style={{ display:"flex", justifyContent:"space-between", marginBottom:8 }}>
            <span style={{ fontSize:11, color:"rgba(255,255,255,0.35)" }}>Game</span>
            <span style={{ fontSize:12, fontWeight:600, color:"rgba(255,255,255,0.7)" }}>{loan.gameLabel}</span>
          </div>
          <div style={{ display:"flex", justifyContent:"space-between" }}>
            <span style={{ fontSize:11, color:"rgba(255,255,255,0.35)" }}>Their pick</span>
            <span style={{ fontSize:12, fontWeight:700, color:T.coral }}>{loan.pickLabel}</span>
          </div>
        </div>

        {/* Message */}
        {loan.message && (
          <div style={{ padding:"8px 12px", borderRadius:8, background:"rgba(255,107,61,0.05)", borderLeft:`3px solid rgba(255,107,61,0.25)`, marginBottom:12, fontSize:12, color:"rgba(255,255,255,0.5)", fontStyle:"italic" }}>
            "{loan.message}"
          </div>
        )}

        {/* Lender-funded explainer — lender's POV. Lender gets 1.5x bonus on win; borrower
            gets the full payout. Co-stake's old "match + match = combined" language is dead. */}
        <div style={{ fontSize:11.5, color:"rgba(255,255,255,0.55)", lineHeight:1.6, textAlign:"center", marginBottom:14, padding:"10px 12px", background:"rgba(255,255,255,0.02)", borderRadius:10, border:"1px solid rgba(255,255,255,0.04)" }}>
          You fund the {loan.totalWager || loan.amount} pt wager. If it hits, <span style={{ color:"#4ade80", fontWeight:700 }}>you win 1.5x</span> for spotting them. If it misses, you eat it.
        </div>

        {/* Buttons */}
        <div style={{ display:"flex", gap:8 }}>
          <button onClick={() => onDecline?.(loan.id)} disabled={accepted || accepting} style={{
            flex:1, padding:"12px", borderRadius:12,
            border:"1px solid rgba(255,255,255,0.08)", background:"rgba(255,255,255,0.04)",
            color:"rgba(255,255,255,0.5)", fontSize:13, fontWeight:600, cursor: accepted || accepting ? "default" : "pointer",
            opacity: accepted || accepting ? 0.4 : 1,
            transition:"opacity 0.2s",
          }}>Pass</button>
          <button
            onClick={() => {
              if (accepted || accepting) return;
              haptic.medium();
              setAccepted(true);  // morph immediately for instant feedback
              onAccept?.(loan.id);  // parent handles the API call + card removal
            }}
            disabled={accepting || accepted}
            className={accepted ? "no-press" : ""}
            style={{
              flex:1.2, padding:"12px", borderRadius:12, border:"none",
              background: accepted
                ? "linear-gradient(135deg, #22c55e, #16a34a)"
                : T.coral,
              color:"#fff", fontSize:13, fontWeight:700,
              cursor: accepting || accepted ? "default" : "pointer",
              boxShadow: accepted
                ? "0 4px 18px rgba(34,197,94,0.4)"
                : "0 4px 16px rgba(255,107,61,0.25)",
              opacity: accepting && !accepted ? 0.85 : 1,
              display:"flex", alignItems:"center", justifyContent:"center", gap:6,
              transition:"background 0.35s cubic-bezier(0.32,0.72,0,1.18), box-shadow 0.35s ease",
            }}>
              {accepted ? (
                <>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" style={{ animation:"checkPop 0.4s cubic-bezier(0.32,0.72,0,1.32) both" }}>
                    <polyline points="20 6 9 17 4 12" style={{ strokeDasharray:30, strokeDashoffset:30, animation:"checkDraw 0.32s 0.05s cubic-bezier(0.65,0,0.35,1) forwards" }}/>
                  </svg>
                  <span style={{ animation:"fadeUp 0.25s 0.1s both" }}>Funded</span>
                </>
              ) : accepting ? (
                <>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.85)" strokeWidth="2.5" strokeLinecap="round" style={{ animation:"spin 0.8s linear infinite" }}>
                    <path d="M21 12a9 9 0 1 1-6.219-8.56"/>
                  </svg>
                  <span>Funding…</span>
                </>
              ) : (
                <span>Fund it</span>
              )}
            </button>
        </div>
      </div>
    </div>
  );
}

// ── Torch Offer Inbox Card — broke recipient reviews an incoming offer ──────
// Shown when someone has offered to spot YOU. Lender already funded — you just
// accept (pick goes live with both names) or decline (lender refunded).
function TorchOfferInboxCard({ loan, onAccept, onDecline, accepting }) {
  const [accepted, setAccepted] = useState(false);
  return (
    <div style={{
      margin:"8px 10px", borderRadius:18, overflow:"hidden",
      // Gold tint so this is visually distinct from a request card (which is coral).
      background:"linear-gradient(135deg, rgba(217,164,65,0.07), rgba(217,164,65,0.02))",
      border:"1px solid rgba(217,164,65,0.18)",
      boxShadow:"0 4px 20px rgba(217,164,65,0.05)",
      animation:"fadeUp 0.25s ease both",
    }}>
      <div style={{ height:2, background:`linear-gradient(90deg,${T.gold},rgba(217,164,65,0))` }}/>
      <div style={{ padding:"16px" }}>
        {/* Header */}
        <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:14 }}>
          <div style={{
            width:36, height:36, borderRadius:12,
            background:`linear-gradient(135deg,${T.gold},#B88A2C)`,
            display:"flex", alignItems:"center", justifyContent:"center",
          }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="1.8">
              <path d="M12 22c-4.97 0-9-2.69-9-6v-.18C4.32 12.47 7.6 10 8 6c.02-.17.17-.3.34-.28C10.09 5.96 12 8 12 8s1.91-2.04 3.66-2.28c.17-.02.32.11.34.28.4 4 3.68 6.47 5 9.82V16c0 3.31-4.03 6-9 6z"/>
            </svg>
          </div>
          <div>
            <div style={{ fontSize:14, fontWeight:700, color:"#fff" }}>Torch Offered</div>
            <div style={{ fontSize:12, color:"rgba(255,255,255,0.4)" }}>{loan.lenderName} wants to spot you a pick</div>
          </div>
        </div>

        {/* Details */}
        <div style={{ background:"rgba(255,255,255,0.03)", borderRadius:12, padding:"12px", marginBottom:12, border:"1px solid rgba(255,255,255,0.04)" }}>
          <div style={{ display:"flex", justifyContent:"space-between", marginBottom:8 }}>
            <span style={{ fontSize:11, color:"rgba(255,255,255,0.35)" }}>They put up</span>
            <span style={{ fontSize:14, fontWeight:700, color:"#fff", fontFamily:FM }}>{loan.totalWager || loan.amount} pts</span>
          </div>
          <div style={{ display:"flex", justifyContent:"space-between", marginBottom:8 }}>
            <span style={{ fontSize:11, color:"rgba(255,255,255,0.35)" }}>Game</span>
            <span style={{ fontSize:12, fontWeight:600, color:"rgba(255,255,255,0.7)" }}>{loan.gameLabel}</span>
          </div>
          <div style={{ display:"flex", justifyContent:"space-between" }}>
            <span style={{ fontSize:11, color:"rgba(255,255,255,0.35)" }}>The pick</span>
            <span style={{ fontSize:12, fontWeight:700, color:T.gold }}>{loan.pickLabel}</span>
          </div>
        </div>

        {/* Message */}
        {loan.message && (
          <div style={{ padding:"8px 12px", borderRadius:8, background:"rgba(217,164,65,0.05)", borderLeft:`3px solid rgba(217,164,65,0.30)`, marginBottom:12, fontSize:12, color:"rgba(255,255,255,0.5)", fontStyle:"italic" }}>
            "{loan.message}"
          </div>
        )}

        {/* Explainer — recipient's POV. They get the full payout, lender gets 1.5x bonus. */}
        <div style={{ fontSize:11.5, color:"rgba(255,255,255,0.55)", lineHeight:1.6, textAlign:"center", marginBottom:14, padding:"10px 12px", background:"rgba(255,255,255,0.02)", borderRadius:10, border:"1px solid rgba(255,255,255,0.04)" }}>
          You put up nothing. If it hits, <span style={{ color:"#4ade80", fontWeight:700 }}>you win the full payout</span> and they win 1.5x for spotting you. If it misses, only they take the hit.
        </div>

        {/* Buttons */}
        <div style={{ display:"flex", gap:8 }}>
          <button onClick={() => onDecline?.(loan.id)} disabled={accepted || accepting} style={{
            flex:1, padding:"12px", borderRadius:12,
            border:"1px solid rgba(255,255,255,0.08)", background:"rgba(255,255,255,0.04)",
            color:"rgba(255,255,255,0.5)", fontSize:13, fontWeight:600, cursor: accepted || accepting ? "default" : "pointer",
            opacity: accepted || accepting ? 0.4 : 1,
            transition:"opacity 0.2s",
          }}>Pass</button>
          <button
            onClick={() => {
              if (accepted || accepting) return;
              haptic.medium();
              setAccepted(true);
              onAccept?.(loan.id);
            }}
            disabled={accepting || accepted}
            className={accepted ? "no-press" : ""}
            style={{
              flex:1.2, padding:"12px", borderRadius:12, border:"none",
              background: accepted
                ? "linear-gradient(135deg, #22c55e, #16a34a)"
                : `linear-gradient(135deg,${T.gold},#B88A2C)`,
              color:"#fff", fontSize:13, fontWeight:700,
              cursor: accepting || accepted ? "default" : "pointer",
              boxShadow: accepted
                ? "0 4px 18px rgba(34,197,94,0.4)"
                : "0 4px 16px rgba(217,164,65,0.30)",
              opacity: accepting && !accepted ? 0.85 : 1,
              display:"flex", alignItems:"center", justifyContent:"center", gap:6,
              transition:"background 0.35s cubic-bezier(0.32,0.72,0,1.18), box-shadow 0.35s ease",
            }}>
              {accepted ? (
                <>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" style={{ animation:"checkPop 0.4s cubic-bezier(0.32,0.72,0,1.32) both" }}>
                    <polyline points="20 6 9 17 4 12" style={{ strokeDasharray:30, strokeDashoffset:30, animation:"checkDraw 0.32s 0.05s cubic-bezier(0.65,0,0.35,1) forwards" }}/>
                  </svg>
                  <span style={{ animation:"fadeUp 0.25s 0.1s both" }}>Locked in</span>
                </>
              ) : accepting ? (
                <>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.85)" strokeWidth="2.5" strokeLinecap="round" style={{ animation:"spin 0.8s linear infinite" }}>
                    <path d="M21 12a9 9 0 1 1-6.219-8.56"/>
                  </svg>
                  <span>Locking in…</span>
                </>
              ) : (
                <span>Take the spot</span>
              )}
            </button>
        </div>
      </div>
    </div>
  );
}


/* ===========================================
   PAGE: LEAGUES  (league room architecture)
=========================================== */
// ── Auto League Milestones ──────────────────────────────────────
// Generates engagement cards from pick data — fills the feed even when nobody's posting
const computeLeagueMilestones = (memberPicks = [], standings = [], leagueName = "", scoringMode = "wager") => {
  // PICKEM-PTS: non-wager leagues score on `wins`; every member balance is 0 by design.
  const msNonWager = scoringMode === "pickem" || scoringMode === "survivor";
  const milestones = [];
  const allPicks = memberPicks;
  if (allPicks.length < 3) return milestones;

  // 1. Hot Streak — who has the longest active win streak?
  const byUser = {};
  allPicks.forEach(p => {
    if (!p.username) return;
    if (!byUser[p.username]) byUser[p.username] = [];
    byUser[p.username].push(p);
  });
  let bestStreak = { user: null, count: 0 };
  Object.entries(byUser).forEach(([user, picks]) => {
    const sorted = [...picks].sort((a, b) => {
      const ta = tsToMillis(a.submittedAt) ?? 0;
      const tb = tsToMillis(b.submittedAt) ?? 0;
      return tb - ta;
    });
    let streak = 0;
    for (const p of sorted) {
      if (p.status === "won") streak++;
      else break;
    }
    if (streak > bestStreak.count) bestStreak = { user, count: streak };
  });
  if (bestStreak.count >= 3) {
    milestones.push({
      type: "milestone", icon: "🔥", color: "#FF6B3D",
      title: `${bestStreak.user} is on fire`,
      sub: `${bestStreak.count}-pick win streak — who can stop them?`,
    });
  }

  // 2. Biggest Win — largest single payout this week
  const weekAgo = Date.now() - 7 * 86400000;
  const recentWins = allPicks.filter(p => {
    if (p.status !== "won" || !p.points) return false;
    const ts = tsToMillis(p.submittedAt) ?? 0;
    return ts > weekAgo;
  });
  if (recentWins.length > 0) {
    const biggest = recentWins.reduce((best, p) => (p.points || 0) > (best.points || 0) ? p : best, recentWins[0]);
    if (biggest.points >= 100) {
      milestones.push({
        type: "milestone", icon: "💰", color: "#4ade80",
        title: `Biggest win this week`,
        sub: `${biggest.username || "Someone"} won +${(biggest.points||0).toLocaleString()} pts on ${biggest.label || "a pick"}`,
      });
    }
  }

  // 3. Most Active — who's making the most picks?
  const weekPicks = allPicks.filter(p => {
    const ts = tsToMillis(p.submittedAt) ?? 0;
    return ts > weekAgo;
  });
  const pickCounts = {};
  weekPicks.forEach(p => { if (p.username) pickCounts[p.username] = (pickCounts[p.username] || 0) + 1; });
  const mostActive = Object.entries(pickCounts).sort((a, b) => b[1] - a[1])[0];
  if (mostActive && mostActive[1] >= 5) {
    milestones.push({
      type: "milestone", icon: "⚡", color: "#00D4FF",
      title: `Most active this week`,
      sub: `${mostActive[0]} made ${mostActive[1]} picks — they're locked in`,
    });
  }

  // 4. Underdog alert — someone hit big odds this week
  const longshots = recentWins.filter(p => p.odds && p.odds >= 200);
  if (longshots.length > 0) {
    const best = longshots.reduce((b, p) => (p.odds || 0) > (b.odds || 0) ? p : b, longshots[0]);
    milestones.push({
      type: "milestone", icon: "🎯", color: "#FCD34D",
      title: `Underdog alert`,
      sub: `${best.username || "Someone"} hit a +${best.odds} longshot — ${best.label || ""}`,
    });
  }

  // 5. Standings movement — leader change (only when leader has pulled ahead)
  if (standings.length >= 2) {
    const leader = standings[0];
    const second = standings[1];
    const leaderBal = msNonWager ? (leader.wins ?? 0) : (leader.balance ?? 0);
    const secondBal = msNonWager ? (second.wins ?? 0) : (second.balance ?? 0);
    // Only show leader milestone when the leader actually has a higher score
    // (not when everyone is tied at starting balance with 0 picks)
    if (leaderBal > secondBal) {
      milestones.push({
        type: "milestone", icon: "👑", color: "#FFD700",
        title: `${leader.username || leader.name || "Leader"} leads ${leagueName}`,
        sub: msNonWager
          ? `${leader.wins || 0} wins — ${leader.wins || 0}W-${leader.losses || 0}L`
          : `${(leader.balance || 0).toLocaleString()} pts — ${leader.wins || 0}W-${leader.losses || 0}L`,
      });
    }
  }

  return milestones.slice(0, 3); // max 3 milestones
};

function LeaguesPage({ user, onLeagueDeleted, setActivePage, refreshLeagues, picksRefreshKey = 0, balance = 0, onBalanceChange, userLeagues = [], onAfterSettle, games: rootGames = [], onOpenTorchModal, onOpenTorchInfo, leagueResetKey = 0, nav = null }) {
  const [leagues, setLeagues]         = useState([]);
  const [loading, setLoading]         = useState(true);
  // Restore activeLeague + roomTab from localStorage so refresh keeps you in the Den
  const [activeLeague, setActive]     = useState(() => {
    try { return localStorage.getItem("torch_active_league") || null; } catch { return null; }
  });

  // When bottom nav "Leagues" is tapped, reset to league list
  const resetKeyRef = useRef(leagueResetKey);
  useEffect(() => {
    if (leagueResetKey !== resetKeyRef.current) {
      resetKeyRef.current = leagueResetKey;
      setActive(null);
    }
  }, [leagueResetKey]);

  // Sync nav stack: any time activeLeague clears (in-app back button, leave,
  // delete, bottom nav Leagues tap), silently remove the corresponding stack
  // frame so a subsequent browser Back doesn't try to "exit" a league we're
  // already not in.
  const prevActiveLeagueRef = useRef(activeLeague);
  useEffect(() => {
    const prev = prevActiveLeagueRef.current;
    if (prev && !activeLeague) {
      nav?.popSilent?.(`league:${prev}`);
    }
    prevActiveLeagueRef.current = activeLeague;
  }, [activeLeague, nav]);
  const [standings, setStandings]     = useState({});
  const [activity, setActivity]       = useState({});
  const [memberPicks, setMemberPicks] = useState({}); // { [leagueId]: [picks] }
  // roomTab: "den" = social feed, "standings" = full leaderboard, "picks" = live picks
  const [roomTab, setRoomTab] = useState(() => {
    try {
      const saved = localStorage.getItem("torch_den_tab");
      return ["den","standings","picks"].includes(saved) ? saved : "den";
    } catch { return "den"; }
  });
  const [reactions, setReactions]     = useState({}); // { [activityId]: { fire:[uid], up:[uid], skull:[uid] } }
  const [statSheet, setStatSheet]     = useState(null); // null | "rank" | "balance" | "record" | "payday"  — bottom sheet
  const [showCreate, setShowCreate]   = useState(false);
  const [showJoin, setShowJoin]       = useState(false);
  // Active bracket-pool overlay (Phase 2A). null = closed, league object = open.
  const [bracketOpen, setBracketOpen] = useState(null);
  // Per-league bracket completion: { status: "none" | "in_progress" | "complete", groupsCount: 0..12 }.
  // Drives the Make a Pick button label inside WC2026 leagues
  // ("Make Your Picks" → "Continue Picking · X/12" → "View My Bracket").
  const [wcCompletion, setWcCompletion] = useState({});
  // Member brackets for the current league's "Brackets" tab. Populated on
  // demand (lazy fetch when the tab opens) and keyed by leagueId. Each entry
  // is an array of { userId, username, predictions, summary }. Members with
  // no doc are still included as "hasn't picked yet" placeholders.
  const [wcMemberBrackets, setWcMemberBrackets] = useState({});
  const [wcMemberBracketsLoading, setWcMemberBracketsLoading] = useState(false);
  // Selected member's bracket for the read-only details modal.
  const [wcSelectedMemberBracket, setWcSelectedMemberBracket] = useState(null);
  // Live group-stage standings — single subscription shared across the
  // standings tab, brackets tab, and any future surface that needs scoring.
  // Returns null pre-tournament; populates June 11-27 as matches play out.
  const wcLiveStandings = useWorldCupLiveStandings();
  const [showInvite, setShowInvite]   = useState(null);
  // True only when the invite sheet is opened right after creating a league,
  // so the sheet can show a celebratory "League created!" header instead of
  // the plain "Invite Friends" one. Reset whenever the sheet closes.
  const [justCreatedLeague, setJustCreatedLeague] = useState(false);
  const [copiedCode, setCopiedCode]   = useState(false);
  const [leagueTab, setLeagueTab]     = useState("mine");
  const [createName, setCreateName]   = useState("");
  const [createMax, setCreateMax]     = useState("20");
  const [createBalance, setCreateBalance] = useState("1000");
  const [createEndDate, setCreateEndDate] = useState("");
  // Optional start date — if set in the future, picks are blocked until then.
  // Lets a captain create a league, share invites, and have the season formally
  // kick off on a specific day. Defaults to today on modal open (= "starts now").
  const [createStartDate, setCreateStartDate] = useState("");
  // When the create-league modal opens, seed end date with a sensible 90-day
  // default so the required field isn't blank. Picking a season template will
  // override this with the template's own endDate.
  useEffect(() => {
    if (showCreate && !createEndDate) {
      setCreateEndDate(defaultCustomEndDateISO(90));
    }
    if (showCreate && !createStartDate) {
      // Default to today (= league starts immediately). User can move it
      // forward if they want a delayed kickoff.
      setCreateStartDate(defaultCustomEndDateISO(0));
    }
    // Always land on the fork when the modal opens. Without this, closing the
    // modal mid-flow and reopening would drop the user back into whichever path
    // they abandoned, with that path's state still seeded.
    if (showCreate) { setCreatePath(null); setCreateStep(1); }
  }, [showCreate]); // eslint-disable-line react-hooks/exhaustive-deps
  const [makePublic, setMakePublic]   = useState(false);
  const [creating, setCreating]       = useState(false);
  const [createErr, setCreateErr]     = useState("");
  const [joinCode, setJoinCode]       = useState("");
  const [joining, setJoining]         = useState(false);
  const [joinErr, setJoinErr]         = useState("");
  const [createMotto, setCreateMotto] = useState("");
  const [createPrize, setCreatePrize] = useState("");
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [createPayday, setCreatePayday] = useState("200");
  // Pick privacy default for NEW leagues: hidden until kickoff (office-pool
  // style). Commissioners can pick "Open picks" in the format step. Existing
  // leagues are untouched (absent flag = open).
  const [pickPrivacyMode, setPickPrivacyMode] = useState("until_start");
  // Resolve a league's scoring format by id, for the standings loader. Standings
  // sort differently per mode (balance vs correct picks vs survivor state), but
  // most call sites only have a leagueId in hand — this looks the mode up from
  // the league objects already loaded into userLeagues. Defaults to "wager" if
  // the league isn't found, which is the pre-existing behaviour.
  const modeFor = useCallback((leagueId) => {
    const l = (userLeagues || []).find(x => x.id === leagueId);
    return l?.scoringMode || "wager";
  }, [userLeagues]);
  // ─── SCORING MODE — league format (step 1 of NFL template + survivor) ──────
  // "wager"   → existing Vegas-odds ledger (default, unchanged). Picks debit a
  //             balance and pay out by American odds. This is the ONLY mode that
  //             touches the L-1 balance transaction in submitPickCallable.
  // "pickem"  → straight-up pick'em. Pick a winner per game; 1 pt per correct
  //             pick (tracked as the member `wins` field — no new counter). No
  //             wager UI, no balance ledger for picks.
  // "survivor"→ elimination pool. One team per week; correct advances, wrong
  //             eliminates. Also wager-free. (Elimination logic ships later; this
  //             field is written now so the mode exists at creation.)
  // Persisted on the league doc via the handleCreate post-creation merge, exactly
  // like templateId/format — leagues.js signature stays untouched.
  const [scoringMode, setScoringMode] = useState("wager");
  // Pick'em is offered ONLY while the NFL template is selected. Reset the mode
  // whenever the template moves off nfl-2026, otherwise stale state ships a
  // broken league: pick "Run your NFL season" → Pick'em → go back → switch to
  // MLB. The Pick'em radio is gone from step 3 by then, but `scoringMode` still
  // reads "pickem" and handleCreate would write an MLB pickem league whose board
  // can never render a week slate.
  //
  // Keyed on selectedTemplate?.id so it fires for BOTH setter call sites (the
  // featured NFL tile and the event sub-picker) and for the sub-picker's
  // deselect-to-null toggle, without duplicating the reset at each of them.
  // Guarded on the current value so this can't clobber an unrelated render.
  useEffect(() => {
    if (selectedTemplate?.id !== "nfl-2026" && scoringMode !== "wager") {
      setScoringMode("wager");
    }
  }, [selectedTemplate?.id, scoringMode]);
  // ─── WIZARD STATE — commissioner customization (v1) ────────────────────────
  // The create modal is a 4-step wizard. createStep tracks the current step
  // (1..4). Each step has sensible defaults so a user can next-next-next-create
  // in seconds; tweaking is for those who care.
  const [createStep, setCreateStep] = useState(1);
  // ── CREATE PATH — the step-0 fork ──────────────────────────────────────────
  // null   → the fork screen itself (nothing chosen yet)
  // "nfl"  → NFL-season path: sport is NFL by definition, so step 1 (sport
  //          picker) is SKIPPED and the format question leads.
  // "custom" → the original wizard, unchanged, steps 1→4.
  //
  // Implemented as a step-0 gate rather than a restructure of steps 1–4: those
  // steps keep their existing numbering, validation, and render blocks exactly
  // as-is. The NFL path just pre-seeds the state step 1 would have collected
  // (template, sport, name, dates) and starts at step 2. That keeps the diff
  // shallow and means the custom path literally cannot regress — it runs the
  // same code it ran before.
  const [createPath, setCreatePath] = useState(null);
  // Step 1 — Sports. Empty array = "all sports allowed". Stored as the sport
  // keys used elsewhere (e.g. "americanfootball_nfl").
  const [allowedSports, setAllowedSports] = useState([]);
  // Step 2 — Time Frame. One of: "week" | "day" | "month" | "season" | "event"
  // | "custom". Drives how createEndDate is auto-populated when the user picks
  // a tile (custom keeps whatever they typed in the date input).
  const [timeFrameType, setTimeFrameType] = useState("week");
  // Step 3 — Pick Types allowed in this league. Validated at slip submit time.
  // Settlement is unaffected (it just grades whatever picks made it through).
  const [allowedBetTypes, setAllowedBetTypes] = useState(["spread", "ml", "total", "prop"]);
  // Step 4 — Pick Requirements. Stored as a single object on the league doc.
  //   cadence: "none" | "daily" | "weekly"
  //   minPicks: required picks per period
  //   minWager / maxWager: per-pick wager bounds enforced at submit
  //   penalty: { type, value } — applied by enforcePickRequirements CF
  const [pickReqCadence, setPickReqCadence] = useState("none");
  const [pickReqMinPicks, setPickReqMinPicks] = useState("1");
  const [pickReqMinWager, setPickReqMinWager] = useState("");
  const [pickReqMaxWager, setPickReqMaxWager] = useState(""); // empty = no cap
  const [penaltyType, setPenaltyType] = useState("minWager");
  const [penaltyValue, setPenaltyValue] = useState("200");
  // Brutal mode is gated behind a disclosure to prevent accidental selection.
  const [showAdvancedPenalty, setShowAdvancedPenalty] = useState(false);
  const [publicLeagues, setPublicLeagues] = useState([]);
  const [loadingPublic, setLoadingPublic] = useState(false);
  const [joiningPublic, setJoiningPublic] = useState(null);
  const [selectedLeaguePick, setSelectedLeaguePick] = useState(null);
  const [viewingMember, setViewingMember] = useState(null); // { uid, username }
  const [trashDraft, setTrashDraft] = useState({}); // { [leagueId]: string }
  // Reaction chips (GG / RIP / called it / etc.) used to take a full row above the
  // input on every Den open — visual noise for something users tap rarely. Now
  // hidden behind a + button in the input row; expanded only when summoned.
  const [showReactions, setShowReactions] = useState(false);
  const [trashPosting, setTrashPosting] = useState(false);
  const chatScrollRef = useRef(null);
  // showStandingsSheet removed — standings now live in the den tab bar
  const [showRoomMenu, setShowRoomMenu] = useState(false);

  // ── Captain features state ──
  const [showAnnounce, setShowAnnounce] = useState(false);
  const [announceDraft, setAnnounceDraft] = useState("");
  const [announcing, setAnnouncing] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [editName, setEditName] = useState("");
  const [editMotto, setEditMotto] = useState("");
  const [editPrize, setEditPrize] = useState("");
  const [editPayday, setEditPayday] = useState("");
  const [savingSettings, setSavingSettings] = useState(false);
  const [endingLeague, setEndingLeague] = useState(false);
  const [showCaptainInsights, setShowCaptainInsights] = useState(false);

  // ── Unified modal-open tracker ──
  // When any league-page modal is open, hide the bottom nav so sheets get full
  // viewport. Each self-contained modal component (PickSlip, GameDetail, etc.)
  // calls useModalOpen() on its own, but inline modals owned by LeaguesPage need
  // this aggregate hook to participate.
  useModalOpen(
    showAnnounce || showSettings || showCaptainInsights ||
    showCreate || showJoin || !!showInvite ||
    showRoomMenu || showReactions
  );

  // ── Pass the Torch state ──
  const [showTorchRequest, setShowTorchRequest] = useState(false);
  const [torchInbox, setTorchInbox] = useState([]); // pending loans where I'm lender
  const [myTorchLoan, setMyTorchLoan] = useState(null); // my active/pending loan as borrower
  const [torchAccepting, setTorchAccepting] = useState(false);
  const [torchCancelling, setTorchCancelling] = useState(false);

  // ── UGC Moderation state (Apple Guideline 1.2) ──
  const [blockedUsers, setBlockedUsers] = useState({}); // { uid: { username, blockedAt } }
  const [showEula, setShowEula] = useState(false);
  const [eulaAccepted, setEulaAccepted] = useState(() => hasAcceptedEula(user?.uid));
  const [reportModal, setReportModal] = useState(null); // { contentId, contentText, reportedUserId, reportedUsername, leagueId }

  // Load blocked users on mount
  useEffect(() => {
    if (!user?.uid) return;
    loadBlockedUsers(user.uid).then(setBlockedUsers).catch(() => {});
  }, [user?.uid]);

  // Check EULA when entering Den
  useEffect(() => {
    if (activeLeague && roomTab === "den" && !eulaAccepted && !hasAcceptedEula(user?.uid)) {
      setShowEula(true);
    }
  }, [activeLeague, roomTab, eulaAccepted, user?.uid]);

  const handleAcceptEula = async () => {
    await acceptEula(user.uid);
    setEulaAccepted(true);
    setShowEula(false);
    window.torchToast?.("You're in the league.", "success");
  };

  const handleBlockUser = async (blockedUid, blockedUsername) => {
    const ok = await blockUser(user.uid, blockedUid, blockedUsername);
    if (ok) {
      setBlockedUsers(prev => ({ ...prev, [blockedUid]: { username: blockedUsername } }));
      window.torchToast?.(`${blockedUsername} blocked`, "info");
    }
  };

  const handleUnblockUser = async (blockedUid) => {
    const ok = await unblockUser(user.uid, blockedUid);
    if (ok) {
      setBlockedUsers(prev => { const n = { ...prev }; delete n[blockedUid]; return n; });
      window.torchToast?.("User unblocked", "info");
    }
  };

  const username = user?.displayName || user?.email?.split("@")[0] || "Anonymous";

  const fetchLeagues = () => {
    if (!user) return Promise.resolve([]);
    setLoading(true);
    return loadMyLeagues(user.uid)
      .then(ls => { setLeagues(ls); return ls; })
      .catch(() => { setLeagues([]); return []; })
      .finally(() => setLoading(false));
  };
  useEffect(() => { fetchLeagues(); }, [user]);

  // Handle intent from Home page quick action buttons (Create a League / Browse Leagues)
  useEffect(() => {
    try {
      const intent = localStorage.getItem("torch_den_intent");
      if (!intent) return;
      localStorage.removeItem("torch_den_intent");
      if (intent === "create") {
        setActive(null); setShowCreate(true);
        // Check if a template was selected from Home
        try {
          const tmplId = localStorage.getItem("torch_template_id");
          localStorage.removeItem("torch_template_id");
          if (tmplId) {
            const tmpl = SEASON_TEMPLATES.find(t => t.id === tmplId);
            if (tmpl) {
              setSelectedTemplate(tmpl);
              setCreateName(tmpl.name);
              if (tmpl.endDate) setCreateEndDate(tmpl.endDate);
              if (tmpl.startDate) setCreateStartDate(tmpl.startDate);
            }
          }
        } catch {}
      }
      if (intent === "discover") { setActive(null); setLeagueTab("discover"); }
      if (intent === "join") { setActive(null); setShowJoin(true); }
    } catch {}
  }, []);

  // After leagues load, if we have a persisted activeLeague from a refresh,
  // auto-enter that league so the user lands right back in the Den
  const restoredRef = useRef(false);
  useEffect(() => {
    // If the caller set a navigation intent (Create / Discover) right before
    // navigating here, that takes precedence over any persisted active league
    let hasPendingIntent = false;
    try { hasPendingIntent = !!localStorage.getItem("torch_den_intent"); } catch {}
    if (hasPendingIntent) return;
    if (loading || leagues.length === 0 || restoredRef.current) return;
    if (!activeLeague) return;
    const match = leagues.find(l => l.id === activeLeague);
    if (match) {
      restoredRef.current = true;
      enterLeague(match);
    } else {
      // League no longer accessible — clear the stale restore
      setActive(null);
      try { localStorage.removeItem("torch_active_league"); } catch {}
    }
  }, [loading, leagues]);

  // Refresh standings + picks + activity cache when a pick is submitted or settled
  useEffect(() => {
    if (picksRefreshKey > 0) {
      // Re-fetch live standings for the active league instead of just clearing —
      // clearing causes the header strip to go blank until the next onSnapshot fires.
      if (activeLeague) {
        loadLeagueStandings(activeLeague, modeFor(activeLeague)).then(freshSt => {
          setStandings(prev => ({ ...prev, [activeLeague]: freshSt }));
          // FIX: Re-load member picks for the active league (was wiped but never re-fetched)
          if (freshSt.length > 0) {
            loadMemberPicksForLeague(activeLeague, freshSt);
          }
        }).catch(() => {});
        // Refresh Den activity feed so settled pick statuses update in real-time
        loadLeagueActivity(activeLeague, 40).then(freshAct => {
          setActivity(prev => ({ ...prev, [activeLeague]: freshAct }));
        }).catch(() => {});
        // Refresh torch inbox — merge incoming requests (I'm lender) AND incoming offers (I'm broke recipient)
        Promise.allSettled([
          loadIncomingTorchRequests(user?.uid, activeLeague),
          loadIncomingTorchOffers(user?.uid, activeLeague),
        ]).then(([reqs, offers]) => {
          const inboxReqs   = reqs.status   === "fulfilled" ? reqs.value   : [];
          const inboxOffers = offers.status === "fulfilled" ? offers.value : [];
          setTorchInbox([...inboxReqs, ...inboxOffers]);
        }).catch(() => {});
      } else {
        setStandings({});
        setMemberPicks({});
      }
    }
  }, [picksRefreshKey]);

  // -- Poll for torch inbox + activity feed while in a league room --
  // The lender needs to see incoming torch requests in the Social feed
  // without navigating away. Poll every 20s — docs are tiny, negligible cost.
  useEffect(() => {
    if (!activeLeague || !user?.uid) return;
    const poll = setInterval(() => {
      // Refresh torch inbox — merge requests-where-I'm-lender + offers-where-I'm-recipient
      Promise.allSettled([
        loadIncomingTorchRequests(user.uid, activeLeague),
        loadIncomingTorchOffers(user.uid, activeLeague),
      ]).then(([reqs, offers]) => {
        const inboxReqs   = reqs.status   === "fulfilled" ? reqs.value   : [];
        const inboxOffers = offers.status === "fulfilled" ? offers.value : [];
        setTorchInbox([...inboxReqs, ...inboxOffers]);
      }).catch(() => {});
      // Refresh activity feed so torch_request cards appear in Social tab
      loadLeagueActivity(activeLeague, 40).then(freshAct => {
        setActivity(prev => ({ ...prev, [activeLeague]: freshAct }));
      }).catch(() => {});
    }, 20000);
    return () => clearInterval(poll);
  }, [activeLeague, user?.uid]);

  // -- Live listener: subscribe to MY member doc whenever we enter a league room --
  // This is the source of truth for the header Balance/Record stats.
  // onSnapshot fires the moment Firestore updates — no polling, no cache staleness.
  useEffect(() => {
    if (!activeLeague || !user?.uid) return;
    const unsub = subscribeToMemberDoc(activeLeague, user.uid, (liveData) => {
      setStandings(prev => {
        const current = prev[activeLeague] || [];
        // Replace or inject the current user's row with live Firestore data
        const hasEntry = current.some(p => p.id === user.uid);
        if (hasEntry) {
          return { ...prev, [activeLeague]: current.map(p =>
            p.id === user.uid ? { ...p, ...liveData } : p
          )};
        }
        // Not in standings yet — append
        return { ...prev, [activeLeague]: [...current, { id: user.uid, ...liveData }] };
      });
    });
    return unsub; // auto-unsubscribe on unmount or league change
  }, [activeLeague, user?.uid]);

  // LAZY-RANK: whenever any league's standings land (room entry, settle
  // refresh, live member-doc snapshot), mirror my rank + W-L + balance onto
  // the matching entry in the Leagues-page list. loadMyLeagues only computes
  // userRank for ended leagues (a per-league standings read at load would be
  // N×M docs per session), so active-league rank badges and the Den "Best #"
  // cell were inert until now. Standings arrive pre-sorted mode-aware by
  // loadLeagueStandings, so rank is just my index. No-ops when nothing
  // changed so this can't ping-pong with other setLeagues callers.
  useEffect(() => {
    if (!user?.uid) return;
    setLeagues(prev => {
      let changed = false;
      const next = prev.map(l => {
        const st = standings[l.id];
        if (!Array.isArray(st) || st.length === 0) return l;
        const idx = st.findIndex(m => m.id === user.uid);
        if (idx < 0) return l;
        const me = st[idx];
        const rank = idx + 1;
        const wins = me.wins ?? l.wins ?? 0;
        const losses = me.losses ?? l.losses ?? 0;
        const balance = me.balance ?? l.balance;
        if (l.userRank === rank && l.wins === wins && l.losses === losses && l.balance === balance) return l;
        changed = true;
        return { ...l, userRank: rank, wins, losses, balance };
      });
      return changed ? next : prev;
    });
  }, [standings, user?.uid]);

  // Deep link join code
  // URL first (live click on a logged-in session), then sessionStorage fallback
  // (logged-out → signup, where an OAuth redirect may have wiped the params).
  // The TorchApp-level effect captures the code before auth gating; this
  // consumes it once user.uid resolves and joinLeagueByCode can be called.
  useEffect(() => {
    if (!user?.uid) return;
    let code = null;
    const m = window.location.pathname.match(/\/join\/([A-Z0-9]{4,8})/i);
    if (m) code = m[1].toUpperCase();
    if (!code) {
      const qCode = new URLSearchParams(window.location.search).get("join");
      if (qCode) code = qCode.toUpperCase();
    }
    if (!code) {
      try { code = sessionStorage.getItem("torch_pending_join"); } catch {}
    }
    if (code) {
      setJoinCode(code);
      setShowJoin(true);
      try { sessionStorage.removeItem("torch_pending_join"); } catch {}
      window.history.replaceState({}, "", "/");
    }
  }, [user?.uid]);

  // -- Reusable: load recent picks from ALL members, filtered to a league -------
  const loadMemberPicksForLeague = useCallback(async (lid, members) => {
    const allPicks = await Promise.all(
      members.slice(0, 10).map(p =>
        // BOUNDED-READS (fix 2.2): this league's picks only, not each member's
        // whole history.
        loadLeaguePicks(p.id, lid)
          .then(raw => normalizePicks(raw)
            .filter(pk => pk.leagueId === lid)
            .slice(0, 20)
            .map(pk => ({ ...pk, username: p.username, userId: p.id }))
          )
          .catch(() => [])
      )
    );
    const sorted = allPicks.flat().sort((a, b) =>
      (b.submittedAt?.getTime?.() ?? b.submittedAt?.seconds ?? 0) -
      (a.submittedAt?.getTime?.() ?? a.submittedAt?.seconds ?? 0)
    );
    setMemberPicks(prev => ({ ...prev, [lid]: sorted }));
  }, []);

  const enterLeague = async (league) => {
    setActive(league.id);
    try {
      haptic.light(); localStorage.setItem("torch_active_league", league.id);
      localStorage.setItem(`torch_visited_${league.id}`, Date.now().toString());
    } catch {}
    // Browser-back support: pressing Back will exit the league room and return
    // to the leagues list (instead of leaving the entire app).
    nav?.push?.(`league:${league.id}`, () => {
      setActive(null);
      try { localStorage.removeItem("torch_active_league"); } catch {}
    });
    // Restore saved tab — don't reset to "den" every time user enters league
    try {
      const saved = localStorage.getItem("torch_den_tab");
      if (["den","standings","picks"].includes(saved)) setRoomTab(saved);
      else setRoomTab("den");
    } catch { setRoomTab("den"); }
    // ALWAYS fetch fresh standings when entering a league room.
    // Previously this was guarded by `if (!freshSt)` which meant standings loaded
    // once and never refreshed — so new members who joined after the initial load
    // were invisible in the standings/header until a pick was submitted.
    // The onSnapshot (line ~4733) only watches the CURRENT user's member doc,
    // so it can't detect new members joining. This fetch is a quick read of the
    // members subcollection (<50 docs) so there's no meaningful perf cost.
    let freshSt = await loadLeagueStandings(league.id, league.scoringMode || "wager").catch(() => standings[league.id] || []);
    setStandings(prev => ({ ...prev, [league.id]: freshSt }));

    // SERVER-SETTLEMENT (fix 4.1 step 2, 2026-09-28): the W/L reconcile that
    // rewrote your member doc from your pick history is gone. The server is
    // the only writer of member wins/losses now, and the rules lock will
    // refuse app writes to them.

    // Load activity (also always refresh to catch new events)
    const ev = await loadLeagueActivity(league.id, 40).catch(() => activity[league.id] || []);
    setActivity(prev => ({ ...prev, [league.id]: ev }));
    // Load torch loan data
    // IMPORTANT: expireStaleLoans and loadIncomingTorchRequests must be in
    // separate try/catch blocks. Phase 2+ passes the current user's uid so
    // expireStaleLoans only processes loans where the current user is the
    // borrower — the only loans they have permission to refund via rules.
    try {
      await expireStaleLoans(league.id, user.uid);
    } catch (e) {
      // Expected to fail on client due to security rules — server-side
      // settleAllPicks cron handles expiration via Admin SDK instead.
      console.warn("[Torch] expireStaleLoans skipped (expected on client):", e.message);
    }
    try {
      // Merge requests (I'm lender) + offers (I'm broke recipient) into one inbox
      const [reqs, offers] = await Promise.allSettled([
        loadIncomingTorchRequests(user.uid, league.id),
        loadIncomingTorchOffers(user.uid, league.id),
      ]);
      const inboxReqs   = reqs.status   === "fulfilled" ? reqs.value   : [];
      const inboxOffers = offers.status === "fulfilled" ? offers.value : [];
      setTorchInbox([...inboxReqs, ...inboxOffers]);
    } catch (e) { console.warn("[Torch] Inbox load error:", e.message); }
    try {
      const myLoan = await loadMyActiveTorchLoan(user.uid, league.id);
      // Only track loan if it's still active or pending — expired/settled/declined should not block the button
      setMyTorchLoan(myLoan && (myLoan.status === "pending" || myLoan.status === "active") ? myLoan : null);
    } catch (e) { console.warn("[Torch] My loan load error:", e.message); }
    // Always reload member picks — standings may include new members whose
    // picks we haven't fetched yet
    if (freshSt.length > 0) {
      await loadMemberPicksForLeague(league.id, freshSt);
    }
  };

  // (getRoomTab / setTab removed with tab-based room)

  // Reactions — toggle on activity item
  const handleReaction = async (leagueId, eventId, emoji) => {
    haptic.light();
    const key = `${leagueId}_${eventId}`;
    const cur = reactions[key] || {};
    const uid = user.uid;
    const already = (cur[emoji] || []).includes(uid);
    const updated = {
      ...cur,
      [emoji]: already
        ? (cur[emoji] || []).filter(id => id !== uid)
        : [...(cur[emoji] || []), uid],
    };
    setReactions(prev => ({ ...prev, [key]: updated }));
    // Persist to Firestore (non-blocking)
    try {
      const { doc, updateDoc, arrayUnion, arrayRemove, getFirestore } = await import("firebase/firestore");
      const db2 = getFirestore(app);
      const ref = doc(db2, "leagues", leagueId, "activity", eventId);
      await updateDoc(ref, {
        [`reactions.${emoji}`]: already
          ? arrayRemove(uid)
          : arrayUnion(uid),
      });
    } catch {}
  };

  // FIX #4: batch all reaction seeds into one setReactions call — previously
  // called N times in a loop, causing N sequential re-renders per league load.
  useEffect(() => {
    const merged = {};
    Object.entries(activity).forEach(([lid, events]) => {
      events.forEach(ev => {
        if (ev.reactions) merged[`${lid}_${ev.id}`] = ev.reactions;
      });
    });
    if (Object.keys(merged).length > 0)
      setReactions(prev => ({ ...prev, ...merged }));
  }, [activity]);

  const handleCreate = async () => {
    if (!createName.trim()) { setCreateErr("Enter a league name."); return; }
    if (!createEndDate) { setCreateErr("Pick an end date — every league needs a champion."); return; }
    const endMs = new Date(createEndDate).getTime();
    if (!Number.isFinite(endMs) || endMs < Date.now()) {
      setCreateErr("End date must be in the future.");
      return;
    }
    // Start date is optional but if set, must be ≤ end date.
    // Allow today (= league starts immediately, default).
    let sDate = null;
    if (createStartDate) {
      const startMs = new Date(createStartDate).getTime();
      if (!Number.isFinite(startMs)) {
        setCreateErr("Start date is invalid.");
        return;
      }
      if (startMs > endMs) {
        setCreateErr("Start date can't be after end date.");
        return;
      }
      // Treat any startDate <= today as a no-op (don't store). Past start dates
      // would be treated by the picker as "league already started" — same as not
      // setting one — but stripping them here keeps the data cleaner.
      const todayMidnight = new Date();
      todayMidnight.setHours(0, 0, 0, 0);
      if (startMs > todayMidnight.getTime()) {
        sDate = createStartDate;
      }
    }
    // Starting balance only applies to the wager (Vegas odds) format. Pickem and
    // survivor have no point ledger, so the balance input isn't shown and this
    // validation would reject the (irrelevant) default. handleCreate forces a
    // balance of 0 for those modes below.
    // ── PICK'EM IS NFL-ONLY — final enforcement before the write ─────────────
    // Third and last gate (option-level in step 3, effect-level on template
    // change, here at the write). This one is the authoritative one: it's the
    // only gate that can't be outrun by a render-ordering or state-timing edge,
    // and every value below derives from `effectiveScoringMode` rather than the
    // raw `scoringMode` state so a stale "pickem" can't leak into the doc.
    const effectiveScoringMode =
      (scoringMode === "pickem" && selectedTemplate?.id !== "nfl-2026")
        ? "wager"
        : scoringMode;
    const isWagerMode = effectiveScoringMode === "wager";
    const balNum = isWagerMode ? (parseInt(createBalance) || 0) : 0;
    if (isWagerMode && balNum < 100)    { setCreateErr("Starting balance must be at least 100 pts."); return; }
    if (isWagerMode && balNum > 100000) { setCreateErr("Starting balance can't exceed 100,000 pts."); return; }

    // ── Wizard validation ───────────────────────────────────────────────────
    // Pick types: at least one must be selected. An empty array would brick the
    // league (every pick blocked at submit). Default to all-on if user somehow
    // cleared everything — defensive, the UI prevents this anyway.
    // For non-wager scoring modes (pickem / survivor) the only pick primitive is
    // "pick the winner" — a moneyline pick with no wager. Force the bet types to
    // ["ml"] regardless of what the (hidden) pick-type toggles hold, so the
    // league doc is internally consistent and slip validation can't block the
    // one pick type these modes rely on.
    const finalAllowedBetTypes = effectiveScoringMode !== "wager"
      ? ["ml"]
      : ((Array.isArray(allowedBetTypes) && allowedBetTypes.length > 0)
          ? allowedBetTypes
          : ["spread", "ml", "total", "prop"]);
    // Wager bounds — sanity check ranges. Only meaningful in "wager" mode;
    // pickem/survivor have no wager UI, so skip these checks and neutralize the
    // values below (minWager 1 is an inert floor for a mode that never debits).
    const minWagerNum = isWagerMode ? (parseInt(pickReqMinWager) || 0) : 1;
    if (isWagerMode && minWagerNum < 1) { setCreateErr("Minimum wager must be at least 1 pt."); return; }
    const maxWagerNum = isWagerMode ? (pickReqMaxWager ? parseInt(pickReqMaxWager) : null) : null;
    if (isWagerMode && maxWagerNum != null && maxWagerNum < minWagerNum) {
      setCreateErr("Max wager can't be less than min wager."); return;
    }
    // Cadence — minPicks ≥ 1 if cadence is enabled.
    const minPicksNum = parseInt(pickReqMinPicks) || 1;
    if (pickReqCadence !== "none" && minPicksNum < 1) {
      setCreateErr("Minimum picks must be at least 1 if a cadence is set."); return;
    }
    // Penalty value — only enforced for fixed/percent. Brutal mode forces 100%.
    // Every penalty type deducts POINTS, so penalties are wager-mode only; the
    // UI hides the block and we force "none" for pickem/survivor below.
    const finalPenaltyType = isWagerMode ? penaltyType : "none";
    const penaltyValueNum = isWagerMode ? (parseInt(penaltyValue) || 0) : 0;
    if (isWagerMode && penaltyType === "fixed" && penaltyValueNum < 1) {
      setCreateErr("Fixed penalty must be at least 1 pt."); return;
    }
    if (isWagerMode && penaltyType === "percent" && (penaltyValueNum < 1 || penaltyValueNum > 100)) {
      setCreateErr("Percent penalty must be between 1 and 100."); return;
    }
    // ROUND 12: a "min wager fee" penalty with no min wager set deducts 0 on
    // the server (computePenaltyAmount: minWager || 0) while the old UI claimed
    // 200/miss. Only enforceable when a cadence exists, so only checked then.
    if (isWagerMode && pickReqCadence !== "none" && penaltyType === "minWager" && !(parseInt(pickReqMinWager) > 0)) {
      setCreateErr("The min-wager penalty needs a min wager amount — set one on the picks step, or choose a different penalty."); return;
    }

    setCreating(true); setCreateErr("");
    try {
      // Payday is a recurring POINT stipend — meaningless without a ledger, so
      // pickem/survivor get 0 (the payday CF skips leagues with no amount).
      // ROUND 10: safe parse -- `parseInt("0") || 200` was a zero-trap that
      // turned an explicit "no payday" into the 200 default. 0 is a valid,
      // meaningful choice (the payday cron skips amount<=0). Blank/garbage
      // still falls back to 200; anything else is clamped to 0..10000.
      const paydayParsed = parseInt(createPayday, 10);
      const paydayAmt = Number.isFinite(paydayParsed) ? Math.max(0, Math.min(10000, paydayParsed)) : 200;
      const paydaySettings = { paydayAmount: isWagerMode ? paydayAmt : 0 };
      const eSport = selectedTemplate?.eventSport || null;
      // Form-level startDate takes precedence over the template's hardcoded one,
      // since the user may have edited the input after picking a template.
      // Falls back to template's startDate if the form value got cleared.
      const finalStartDate = sDate || selectedTemplate?.startDate || null;
      // Non-wager modes seed members with a 0 balance rather than 1000 — there's
      // no ledger to seed. balNum is already forced to 0 for those modes above.
      const finalBalance = isWagerMode ? createBalance : 0;
      const { id } = await createLeague(user.uid, username, createName, createMax, finalBalance, createEndDate, makePublic, paydaySettings, eSport, finalStartDate);
      await addLeagueToUser(user.uid, id);
      // ── Extra league fields (post-creation update) ──────────────────────────
      // We piggy-back on the existing post-creation update so leagues.js stays
      // unchanged. Cloud Functions and the slip read these fields directly off
      // the league doc, so no other server-side changes are needed.
      try {
        const { doc: fDoc, updateDoc: fUpdate, getFirestore: fGetFS } = await import("firebase/firestore");
        const fdb = fGetFS();
        // Build pickRequirements object. "none" cadence + "none" penalty mean
        // "no enforcement" — Cloud Functions skip such leagues entirely.
        const pickRequirements = {
          cadence: pickReqCadence,                              // "none" | "daily" | "weekly"
          minPicks: pickReqCadence === "none" ? 0 : minPicksNum,
          minWager: minWagerNum,
          maxWager: maxWagerNum,                                // null = no cap
          penalty: {
            // No cadence = nothing can be missed — write "none" so league docs
            // stop carrying a misleading default penalty (rules-card bug, Aug 29).
            type:  pickReqCadence === "none" ? "none" : finalPenaltyType,   // "none"|"minWager"|"fixed"|"percent"|"brutal"
            value: (pickReqCadence !== "none" && (finalPenaltyType === "percent" || finalPenaltyType === "fixed")) ? penaltyValueNum : 0,
          },
        };
        // allowedSports: empty array on the wizard means "all sports". Persist
        // null in that case so existing single-sport `sportFilter` logic still
        // works as a fallback for legacy templates.
        // Pick'em forces NFL-only regardless of the step-1 sport toggles.
        // `allowedSports` is an INDEPENDENT wizard control chosen in step 1 —
        // before the format is even picked in step 3 — so a commissioner can
        // legitimately arrive at step 3 with "All sports" (or MLB + NFL) held in
        // state and then choose Pick'em. Without this the league doc would claim
        // to allow sports the week-slate board can't group and the mode doesn't
        // mean anything for. The NFL template's own eventSport is written to
        // `sportFilter` separately; this pins the newer multi-sport field to
        // match so the two can't disagree.
        const sportsArr = effectiveScoringMode === "pickem"
          ? ["americanfootball_nfl"]
          : (Array.isArray(allowedSports) && allowedSports.length > 0
              ? allowedSports
              : null);
        await fUpdate(fDoc(fdb, "leagues", id), {
          ...(createMotto.trim() ? { motto: createMotto.trim() } : {}),
          ...(createPrize.trim() ? { prize: createPrize.trim() } : {}),
          ...(eSport ? { sportFilter: eSport } : {}),
          ...(selectedTemplate?.id ? { templateId: selectedTemplate.id } : {}),
          ...(selectedTemplate?.format ? { format: selectedTemplate.format } : {}),
          ...(finalStartDate ? { startDate: finalStartDate } : {}),
          // ── New wizard fields ─
          scoringMode: effectiveScoringMode,                     // "wager"|"pickem"|"survivor"
          allowedSports: sportsArr,                              // string[] | null
          allowedBetTypes: finalAllowedBetTypes,                 // string[]
          pickRequirements,                                      // object
          timeFrameType,                                         // "day"|"week"|"month"|"season"|"event"|"custom"
          pickPrivacy: pickPrivacyMode,                          // "until_start"|"open"
        });
      } catch (ex) { console.warn("Extra league fields:", ex); }
      // Meta Pixel: custom league-created conversion (web funnel). Optimize campaigns toward this.
      torchFbq("LeagueCreated", { template: selectedTemplate?.id || "custom", is_public: makePublic }, { custom: true });
      logTorchEvent("league_created", {
        public: makePublic,
        template: selectedTemplate?.id || "custom",
        timeFrameType,
        cadence: pickReqCadence,
        penalty: penaltyType,
        sportCount: allowedSports.length || "all",
        betTypeCount: finalAllowedBetTypes.length,
      });
      // Reset all create-form state, including wizard state.
      setShowCreate(false);
      setCreateName(""); setCreateMax("20"); setCreateBalance("1000");
      setCreateEndDate(""); setCreateStartDate("");
      setMakePublic(false); setCreateMotto(""); setCreatePrize("");
      setSelectedTemplate(null); setCreatePayday("200"); setPickPrivacyMode("until_start");
      setScoringMode("wager");
      setCreateStep(1);
      setCreatePath(null);
      setAllowedSports([]);
      setTimeFrameType("week");
      setAllowedBetTypes(["spread", "ml", "total", "prop"]);
      setPickReqCadence("none"); setPickReqMinPicks("1");
      setPickReqMinWager(""); setPickReqMaxWager("");
      setPenaltyType("minWager"); setPenaltyValue("");
      setShowAdvancedPenalty(false);
      fetchLeagues().then(ls => {
        // ── Viral handoff ──────────────────────────────────────────────────
        // The moment a commissioner creates a league is when they're most
        // motivated to get their crew in — and an empty league dies. So we
        // don't just drop them back to the list: we immediately surface the
        // invite sheet (QR + code + share) for the league they just made.
        // This is the single highest-leverage step for the "one commissioner
        // brings a group" acquisition thesis. Find the new league by id from
        // the freshly-loaded list so we pass the full object (name + code).
        const fresh = Array.isArray(ls) ? ls.find(l => l.id === id) : null;
        if (fresh) {
          setJustCreatedLeague(true);
          setShowInvite(fresh);
        }
      }).catch(() => {});
      refreshLeagues?.(); // FIX #2: sync root userLeagues so slip/nav see new league
    } catch (e) { setCreateErr(e.message); }
    finally { setCreating(false); }
  };

  const handleJoin = async () => {
    if (!joinCode.trim()) { setJoinErr("Enter a league code."); return; }
    setJoining(true); setJoinErr("");
    try {
      const { id } = await joinLeagueByCode(user.uid, username, joinCode);
      await addLeagueToUser(user.uid, id);
      logTorchEvent("league_joined", { method: "code" });
      setShowJoin(false); setJoinCode(""); fetchLeagues();
      refreshLeagues?.(); // FIX #2: sync root userLeagues
    } catch (e) { setJoinErr(e.message); }
    finally { setJoining(false); }
  };

  const handleLeave = (leagueId) => {
    window.torchConfirm("Leave this league?", async () => {
      try { await leaveLeague(user.uid, leagueId); setActive(null); fetchLeagues(); refreshLeagues?.(); window.torchToast?.("Left the league", "info"); }
      // REJOIN-KEEPS-RECORD (fix 3.4): leaving is refused while a pick in this
      // league is pending -- say so instead of the generic retry message.
      catch (e) { window.torchToast?.(e?.code === "torch/pending-picks" ? e.message : "Couldn't leave. Try again.", "error"); }
    }, { danger:true, confirmLabel:"Leave" });
  };

  // ── Captain: Post Announcement ──
  const handlePostAnnouncement = async (leagueId) => {
    if (!announceDraft.trim()) return;
    setAnnouncing(true);
    try {
      const { doc: fDoc, updateDoc: fUpdate, getFirestore: fGetFS } = await import("firebase/firestore");
      const fdb = fGetFS();
      await fUpdate(fDoc(fdb, "leagues", leagueId), {
        announcement: announceDraft.trim(),
        announcementAt: new Date().toISOString(),
        announcementBy: username,
      });
      await writeLeagueActivity(leagueId, {
        type: "announcement",
        userId: user.uid,
        username,
        text: announceDraft.trim(),
      });
setShowAnnounce(false); window.torchToast?.("Announcement posted", "success");
      setAnnounceDraft("");
      window.torchToast?.("Announcement posted!", "success");
      // Refresh activity
      loadLeagueActivity(leagueId, 40).then(fresh => {
        setActivity(prev => ({ ...prev, [leagueId]: fresh }));
      });
    } catch (e) { window.torchToast?.(e.message || "Failed to post", "error"); }
    finally { setAnnouncing(false); }
  };

  // ── Captain: Save League Settings ──
  const handleSaveSettings = async (leagueId) => {
    setSavingSettings(true);
    try {
      const { doc: fDoc, updateDoc: fUpdate, getFirestore: fGetFS } = await import("firebase/firestore");
      const fdb = fGetFS();
      const updates = {};
      if (editName.trim()) updates.name = editName.trim();
      if (editMotto !== undefined) updates.motto = editMotto.trim();
      if (editPrize !== undefined) updates.prize = editPrize.trim();
      if (Object.keys(updates).length === 0) { setSavingSettings(false); return; }
      await fUpdate(fDoc(fdb, "leagues", leagueId), updates);
      setShowSettings(false);
      window.torchToast?.("Settings saved!", "success");
      fetchLeagues();
      refreshLeagues?.();
    } catch (e) { window.torchToast?.(e.message || "Save failed", "error"); }
    finally { setSavingSettings(false); }
  };

  // ── Captain: End the season ──
  // Wires the (already-vetted) endLeague() from leagues.js — crowns the current
  // standings leader as champion, sets status:"ended" + endedAt, and writes a
  // "Season over!" Den event. Moves the league to the PAST section. Reversible
  // via the existing Restart action. Confirmation-gated since it locks the league.
  const handleEndLeague = async (leagueId, leagueName) => {
    if (endingLeague) return;
    window.torchConfirm?.(
      `End "${leagueName}"? This locks final standings, crowns the current leader as champion, and moves the league to your Past list. You can restart it later if needed.`,
      async () => {
        setEndingLeague(true);
        try {
          const champion = await endLeague(leagueId, user.uid);
          setShowSettings(false);
          window.torchToast?.(
            champion ? `🏆 Season ended — ${champion.username} crowned champion!` : "Season ended.",
            "success"
          );
          fetchLeagues();
          refreshLeagues?.();
        } catch (e) {
          window.torchToast?.(e.message || "Couldn't end the league. Try again.", "error");
        } finally { setEndingLeague(false); }
      },
      { confirmLabel: "End Season", danger: true }
    );
  };


  // ── Captain: Compute league insights ──
  const getCaptainInsights = (leagueId) => {
    const st2 = standings[leagueId] || [];
    const acts = activity[leagueId] || [];
    // Captain is a competitor too — insights respect pick privacy.
    const _insightLg = leagues.find(l => l.id === leagueId);
    const picks = (memberPicks[leagueId] || []).filter(pk => !isPickHiddenByPrivacy(pk, _insightLg, user?.uid));

    const now = new Date();
    const weekAgo = new Date(now - 7 * 24 * 60 * 60 * 1000);

    // Picks this week
    const recentPicks = picks.filter(p => {
      const d = p.createdAt?.seconds ? new Date(p.createdAt.seconds * 1000) : new Date(p.createdAt);
      return d >= weekAgo;
    });

    // Active pickers this week
    const activePickers = new Set(recentPicks.map(p => p.userId || p.uid)).size;

    // Biggest win this week
    let biggestWin = null;
    recentPicks.forEach(p => {
      if ((p.status === "won" || p.status === "WIN") && (p.payout || 0) > (biggestWin?.payout || 0)) {
        biggestWin = p;
      }
    });

    // Members who haven't picked this week
    const pickerIds = new Set(recentPicks.map(p => p.userId || p.uid));
    const inactive = st2.filter(m => !pickerIds.has(m.id));

    // Leader — only meaningful when someone has pulled ahead
    // PICKEM-PTS: compare on wins in non-wager leagues (balances are all 0 there).
    const _ciMode = _insightLg?.scoringMode || "wager";
    const _ciKey = (_ciMode === "pickem" || _ciMode === "survivor") ? "wins" : "balance";
    const leader = st2.length >= 2 && (st2[0][_ciKey] ?? 0) > (st2[1][_ciKey] ?? 0) ? st2[0]
      : st2.length === 1 ? st2[0] : null;

    // Total members
    const totalMembers = st2.length;

    return { recentPicks: recentPicks.length, activePickers, totalMembers, biggestWin, inactive, leader };
  };

  const handleDelete = (league) => {
    window.torchConfirm(`Delete "${league.name}"? This cannot be undone.`, async () => {
      try {
        // Use cached standings if available; otherwise fetch fresh member list.
        // If standings[league.id] is empty (user never entered the Den), deleteLeague
        // receives an empty memberIds array which can cause Firestore permission errors
        // because the cleanup sub-doc writes have no target.
        let memberIds = (standings[league.id] || []).map(p => p.id);
        if (memberIds.length === 0) {
          try {
            const freshSt = await loadLeagueStandings(league.id, league.scoringMode || "wager");
            memberIds = (freshSt || []).map(p => p.id);
          } catch {}
        }
        // Always include the commissioner themselves in the member list
        if (!memberIds.includes(user.uid)) memberIds = [user.uid, ...memberIds];
        await deleteLeague(league.id, user.uid, memberIds);
        window.torchToast?.(`"${league.name}" deleted`, "info");
        setActive(null);
        try { localStorage.removeItem("torch_active_league"); } catch {}
        setLeagues(prev => prev.filter(l => l.id !== league.id));
        onLeagueDeleted?.(league.id);
      } catch (err) { window.torchToast?.(err.message || "Couldn't delete. Try again.", "error"); }
    }, { danger:true, confirmLabel:"Delete League" });
  };

  const handleInvite = async (league) => {
    const inviteUrl = `https://torchpicks.com?join=${league.code}`;
    // Use the full username (e.g. "Cold Pasta") — stripping to first name
    // mangled multi-word handles and stripped identity the user chose.
    const inviterName = user?.displayName || user?.email?.split("@")[0] || "Your friend";
    const isWc = isWorldCup2026League(league);
    // Different share copy for WC bracket pools (special event) vs regular leagues.
    // Goal: the recipient gets meaningful context BEFORE tapping the link, so
    // tap rate goes up AND post-tap they're primed for what they're signing up
    // for. The WC variant brands the event as "the World Cup Special" — the
    // marketing name we're pushing in the Instagram/TikTok push around the
    // June 11 kickoff — and answers the "is this gambling" objection inline
    // ("Free to play, bragging rights only") so the link doesn't get gated by
    // hesitation.
    //
    // Both variants include the invite code in the body text so the recipient
    // can use it manually if the URL doesn't auto-deep-link (older devices,
    // forwarded messages, etc.). The URL still carries `?join=${code}` for
    // the happy path.
    const shareText = isWc
      ? `${inviterName} wants you in the World Cup Special on Torch — predict every group, every knockout, and the final score. Free to play, bragging rights only.\n\nJoin "${league.name}" with code: ${league.code}`
      : `${inviterName} invited you to "${league.name}" on Torch — sports picks with friends, no money. Free to play, bragging rights only.\n\nJoin with code: ${league.code}`;
    const shareTitle = isWc ? "World Cup Special · Torch" : "Torch league invite";
    logTorchEvent("invite_shared", { method: navigator.share ? "native" : "clipboard", isWc });
    if (navigator.share) {
      try { await navigator.share({ title: shareTitle, text: shareText, url: inviteUrl }); return; }
      catch {}
    }
    setShowInvite(league);
  };

  // ── Bracket completion helper + fetch ────────────────────────────────
  // Reads the user's predictions doc and returns { status, groupsCount }.
  // PHASE 2B definition: "complete" requires every section locked —
  //   • all 12 groups (1st-4th picked)
  //   • all knockout rounds (R32 16, R16 8, QF 4, SF 2, FINAL 1 = 31 winners)
  //   • golden boot (non-empty player name)
  //   • tiebreaker (positive integer for total goals in final)
  // The groupsCount is exposed so the CTA can show progress along the way
  // ("Continue Picking · 4/12" while groups are still being filled in).
  const getBracketCompletionStatus = (p) => {
    if (!p) return { status: "none", groupsCount: 0 };
    const groupsObj = p.groups || {};
    const groupsCount = ["A","B","C","D","E","F","G","H","I","J","K","L"].filter(L =>
      Array.isArray(groupsObj[L]) && groupsObj[L].length === 4
    ).length;
    const koObj = p.knockouts || {};
    const koSlots = { R32: 16, R16: 8, QF: 4, SF: 2, FINAL: 1 };
    // Phase-aware knockout completion — only counts rounds that have unlocked.
    // Knockouts are OBJECTS keyed by matchSlot, so count filled slots.
    // Pre-tournament: zero rounds unlocked → koDone defaults to true (nothing to do).
    // Post-R32-unlock: must have all 16 R32 winners. Etc.
    const now = new Date();
    const unlockedRounds = ["R32","R16","QF","SF","FINAL"].filter(r => {
      const ts = ROUND_UNLOCKS[r];
      return ts ? now >= new Date(ts) : false;
    });
    const koDone = unlockedRounds.every((r) =>
      koRoundFilledCount(koObj[r]) === koSlots[r]
    );
    const hasAnyKo = koHasAnyPicks(koObj);
    const gbDone = typeof p.goldenBoot === "string" && p.goldenBoot.trim().length > 0;
    const tbDone = Number.isFinite(Number(p.totalGoals)) && Number(p.totalGoals) > 0;
    const hasAnyTb = p.totalGoals != null;

    // No activity yet at all → "none"
    if (groupsCount === 0 && !hasAnyKo && !hasAnyTb && !gbDone) {
      return { status: "none", groupsCount: 0 };
    }
    // Everything currently lockable is locked → "complete".
    // Pre-tournament: groups(12) + boot + tiebreaker. After R32 unlocks: also needs R32 picks. Etc.
    if (groupsCount === 12 && koDone && gbDone && tbDone) {
      return { status: "complete", groupsCount: 12 };
    }
    // Otherwise → in progress
    return { status: "in_progress", groupsCount };
  };

  useEffect(() => {
    // Fetch only when bracket overlay is closed and we're inside a WC league.
    if (bracketOpen) return;
    if (!activeLeague || !user?.uid) return;
    const league = userLeagues.find(l => l.id === activeLeague);
    if (!league || !isWorldCup2026League(league)) return;
    let alive = true;
    (async () => {
      try {
        const { doc: fDoc, getDoc, getFirestore } = await import("firebase/firestore");
        const fdb = getFirestore();
        const snap = await getDoc(fDoc(fdb, "leagues", activeLeague, "predictions", user.uid));
        if (!alive) return;
        const result = getBracketCompletionStatus(snap.exists() ? snap.data() : null);
        setWcCompletion(c => ({ ...c, [activeLeague]: result }));
      } catch (e) {
        // Fallback is { status: "none" } → "Make Your Picks" stays. Warn so a
        // rules/path mismatch on knockout-unlock day is debuggable, not silent.
        console.warn("[WC] bracket completion fetch failed", e);
      }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeLeague, user?.uid, bracketOpen]);

  // ── WC2026 Member-Brackets fetch ─────────────────────────────────────
  // Lazy load: only fires when the user opens the "Brackets" tab AND we're
  // inside a WC league. Pulls every member's predictions doc + cross-references
  // with standings to fill in usernames. Refetches when the bracket overlay
  // closes (so a fresh lock-in by another user surfaces immediately).
  //
  // Loads for ALL tabs of a WC league, not just Brackets/Standings. Multiple
  // surfaces now depend on this cache:
  //   • Brackets tab — per-member preview cards
  //   • Standings tab — WC score sort/display
  //   • Picks tab — user's own locked bracket summary
  //   • Captain strip on every tab — X/Y LOCKED count
  // The earlier tab-gated guard caused the captain strip to show "0/N LOCKED"
  // and the Picks tab to show "No bracket yet" until the user manually
  // navigated to Brackets or Standings to trigger the fetch.
  useEffect(() => {
    if (bracketOpen) return;
    if (!activeLeague || !user?.uid) return;
    const league = userLeagues.find(l => l.id === activeLeague);
    if (!league || !isWorldCup2026League(league)) return;
    let alive = true;
    setWcMemberBracketsLoading(true);
    (async () => {
      try {
        const { collection: fColl, getDocs, getFirestore } = await import("firebase/firestore");
        const fdb = getFirestore();
        const snap = await getDocs(fColl(fdb, "leagues", activeLeague, "predictions"));
        if (!alive) return;
        const stArr = standings[activeLeague] || [];
        const stMap = Object.fromEntries(stArr.map(m => [m.id, m]));
        const items = snap.docs.map((doc) => {
          const data = doc.data() || {};
          const member = stMap[doc.id];
          return {
            userId: doc.id,
            username: data.username || member?.username || "Unknown",
            predictions: data,
            summary: summarizeMemberBracket(data),
          };
        });
        // Include members with NO predictions doc (haven't started yet).
        const seen = new Set(items.map(i => i.userId));
        for (const m of stArr) {
          if (!seen.has(m.id)) {
            items.push({
              userId: m.id,
              username: m.username || "Unknown",
              predictions: null,
              summary: { status: "none", groupsCount: 0 },
            });
          }
        }
        // Sort: complete first (by champion alpha), then in-progress, then none.
        items.sort((a, b) => {
          const aRank = a.summary.status === "complete" ? 0 : a.summary.status === "in_progress" ? 1 : 2;
          const bRank = b.summary.status === "complete" ? 0 : b.summary.status === "in_progress" ? 1 : 2;
          if (aRank !== bRank) return aRank - bRank;
          // Within same status, "you" first
          if (a.userId === user.uid) return -1;
          if (b.userId === user.uid) return 1;
          return (a.username || "").localeCompare(b.username || "");
        });
        setWcMemberBrackets((prev) => ({ ...prev, [activeLeague]: items }));
      } catch (e) {
        console.warn("[WC2026 Brackets] member fetch failed:", e?.message || e);
      } finally {
        if (alive) setWcMemberBracketsLoading(false);
      }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeLeague, user?.uid, bracketOpen]);

  const goToGamesForLeague = async (leagueObj) => {
    // The cached `userLeagues` array may not include `format` or `templateId`
    // (depending on how loadMyLeagues projects fields). If neither is present,
    // fetch the live league doc to be sure we don't miss a bracket pool.
    let live = leagueObj;
    const hasIndicators = !!(leagueObj?.format || leagueObj?.templateId);
    if (leagueObj?.id && !hasIndicators) {
      try {
        const { doc: fDoc, getDoc, getFirestore } = await import("firebase/firestore");
        const fdb = getFirestore();
        const snap = await getDoc(fDoc(fdb, "leagues", leagueObj.id));
        if (snap.exists()) live = { ...leagueObj, ...snap.data() };
      } catch (e) {
        console.warn("[WC2026] live league fetch failed:", e?.message || e);
      }
    }
    // Bracket-pool leagues (World Cup 2026 etc.) replace the per-game pick flow
    // with their own full-screen prediction UI, so they route to the bracket
    // rather than the Games tab. (This used to also serve as a bypass around the
    // start-date block below; that block has since been removed for all leagues,
    // but the bracket routing itself is still required.)
    if (isWorldCup2026League(live)) {
      setBracketOpen(live);
      return;
    }
    // Start-date navigation block — REMOVED (2026-07-15).
    //
    // This blocked opening the Games tab from a league until league.startDate,
    // toasting "Season hasn't started yet — N days to go". Same flaw as the
    // submission gate (removed in handleSubmit): predicting ahead of kickoff is
    // the product, not an error. The WC bypass directly above already made this
    // argument for bracket pools — it applies to every season-long league.
    //
    // Per-game locks still prevent picking a game that has already started.
    // PICKEM-MAKE-PICKS (2026-09-28, user-reported): the handoff below used to be
    // written ONLY when the league had a legacy sportFilter/eventSport that
    // SPORT_FILTER_MAP knew. Pick'em leagues carry their NFL lock in
    // allowedSports (the wizard pins ["americanfootball_nfl"]) and only get a
    // sportFilter when created from the NFL template -- so for every other
    // pick'em league no league context was handed to the board, and "Make a
    // Pick" opened the unrestricted wager board: every sport, spreads, totals,
    // props. Now: allowedSports is authoritative (same precedence as the slip,
    // the board's leagueSportLock and the CF), pick'em/survivor are always NFL,
    // and the context is handed off whenever the league restricts anything or
    // isn't a wager league. The context also carries scoringMode + sport keys so
    // the board can enforce them even if userLeagues hasn't caught up yet
    // (e.g. a league joined moments ago).
    const mode = live?.scoringMode || "wager";
    const nonWager = mode === "pickem" || mode === "survivor";
    const sportKeys = nonWager
      ? ["americanfootball_nfl"]
      : (Array.isArray(live?.allowedSports) && live.allowedSports.length > 0)
        ? live.allowedSports
        : (live?.sportFilter || live?.eventSport) ? [live.sportFilter || live.eventSport] : [];
    const firstFilter = sportKeys.map(k => SPORT_FILTER_MAP[k]).find(Boolean);
    if (sportKeys.length > 0 || nonWager) {
      try {
        if (firstFilter) localStorage.setItem("torch_games_filter", firstFilter);
        localStorage.setItem("torch_games_league_ctx", JSON.stringify({
          id: live.id, name: live.name, sport: sportKeys[0] || null, sports: sportKeys, scoringMode: mode,
        }));
      } catch {}
    }
    setActivePage?.("Games");
  };

  const postTrashTalk = async (leagueId, msg, imageFile = null) => {
    if ((!msg?.trim() && !imageFile) || trashPosting) return;
    // Content filter — Apple Guideline 1.2
    if (msg?.trim() && containsObjectionableContent(msg.trim())) {
      window.torchToast?.("Your message contains content that violates our community guidelines.", "error");
      return;
    }
    setTrashPosting(true);
    try {
      let imageUrl = null;
      if (imageFile) {
        try {
          const { ref, uploadBytes, getDownloadURL } = await import("firebase/storage");
          const imgRef = ref(storage, `chat/${leagueId}/${Date.now()}_${user.uid}`);
          await uploadBytes(imgRef, imageFile);
          imageUrl = await getDownloadURL(imgRef);
        } catch (e) { console.warn("[Torch] Image upload failed:", e.message); }
      }

      const uname = user?.displayName || user?.email?.split("@")[0] || "Anonymous";
      await writeLeagueActivity(user.uid, [leagueId], {
        type: "taunt",
        username: uname,
        label: msg?.trim() || (imageUrl ? "Shared a photo" : ""),
        imageUrl: imageUrl || null,
        sport: null, points: null, wager: null,
      });
      setActivity(prev => ({
        ...prev,
        [leagueId]: [
          { id: Date.now().toString(), type:"taunt", username: uname, userId: user.uid,
            label: msg?.trim() || (imageUrl ? "Shared a photo" : ""), imageUrl,
            createdAt: { toDate: () => new Date() }, reactions: {} },
          ...(prev[leagueId] || []),
        ],
      }));
      setTrashDraft(prev => ({ ...prev, [leagueId]: "" }));
    } catch { window.torchToast?.("Couldn't post. Try again.", "error"); }
    finally { setTrashPosting(false); }
  };


  const copyCode = (code) => {
    navigator.clipboard?.writeText(`https://torchpicks.com?join=${code}`)
      .then(() => { setCopiedCode(true); setTimeout(() => setCopiedCode(false), 2000); }).catch(() => {});
  };

  useEffect(() => {
    if (leagueTab !== "discover") return;
    // FIX #9: skip reload if already fetched (cache for the session)
    if (publicLeagues.length > 0) return;
    setLoadingPublic(true);
    loadPublicLeagues(user?.uid).then(setPublicLeagues).catch(() => setPublicLeagues([])).finally(() => setLoadingPublic(false));
  }, [leagueTab]);

  // -- LEAGUE ROOM VIEW --------------------------------------------------------
  if (activeLeague) {
    const league = leagues.find(l => l.id === activeLeague);
    if (!league) { return null; }
    const st  = standings[activeLeague] || [];
    const evRaw  = activity[activeLeague]  || [];
    // Filter out blocked users' content (Apple Guideline 1.2)
    const evVisible = evRaw.filter(e => !blockedUsers[e.userId]);
    // Pick privacy: in flagged leagues, other members' pick events stay out
    // of the Den until reveal. Den events written before Aug 2026 lack
    // commenceTime, so those reveal at settle (helper's conservative path);
    // newer events carry commenceTime and reveal at kickoff.
    const privacyOn = league.pickPrivacy === "until_start";
    const isPickEvent = (e) => !!e.pickId || e.type === "pick" || e.type === "parlay" || e.type === "ml" || e.type === "spread" || e.type === "total" || e.type === "prop" || e.type === "team_total";
    const ev = !privacyOn ? evVisible
      : evVisible.filter(e => !isPickEvent(e) || !isPickHiddenByPrivacy(e, league, user?.uid));
    const mpAll = memberPicks[activeLeague] || [];
    const mp = !privacyOn ? mpAll
      : mpAll.filter(pk => !isPickHiddenByPrivacy(pk, league, user?.uid));
    const myRank  = st.findIndex(p => p.id === user?.uid) + 1;
    const myEntry = st.find(p => p.id === user?.uid);
    const leagueEnded = league.isEnded || league.status === "ended" || isLeagueEnded(league);
    const champion = league.champion || league.winner;

    // Bracket-pool CTA: label changes based on the user's completion status.
    // wcCompletion[leagueId] = { status, groupsCount } set by the effect that
    // fetches predictions when the bracket overlay closes or on entering a WC league.
    const isWcLeague = isWorldCup2026League(league);
    const wcInfo = isWcLeague ? (wcCompletion[league.id] || { status: "none", groupsCount: 0 }) : null;
    const bracketStatus = wcInfo?.status ?? null;const bracketGroupsCount = wcInfo?.groupsCount ?? 0;

    // ── Layer 5 · WC standings ──────────────────────────────────────────
    // For WC leagues, sort + display by accumulated bracket-pool score
    // (group-stage + knockout points from `wcLiveStandings`) instead of
    // cash balance. Pre-tournament + before any matches: every member shows
    // 0 pts and sort falls back to wcCorrectSlots → username. Once matches
    // start playing, members re-shuffle by their predictions' accuracy.
    //
    // wcPoints is the canonical TOTAL across rounds: group + knockout. Golden
    // boot scoring is admin-set post-tournament (no live feed for top scorer)
    // and not summed in here; we surface the user's pick on the row but never
    // award the 120 pts automatically. Tiebreaker proximity is carried as
    // wcTiebreakerGuess for future "exact tie on points" resolution; today's
    // sort doesn't use it (rare case, easier to read sort by points→slots→name).
    const wcStandingsList = (() => {
      if (!isWcLeague) return null;
      const memberBrackets = wcMemberBrackets[league.id] || [];
      const byUserId = Object.fromEntries(memberBrackets.map(m => [m.userId, m]));
      return st
        .map((member) => {
          const m = byUserId[member.id];
          const predictions = m?.predictions || null;
          const groupScore = predictions
            ? computeGroupStageScore(predictions, wcLiveStandings)
            : null;
          const koScore = predictions
            ? computeKnockoutScore(predictions, wcLiveStandings)
            : null;
          const bootScore = predictions
            ? computeGoldenBootScore(predictions, wcLiveStandings)
            : null;
          const wcGroupPoints    = groupScore?.totalPoints || 0;
          const wcKnockoutPoints = koScore?.totalPoints    || 0;
          const wcBootPoints     = bootScore?.points       || 0;
          const wcTotalPoints    = wcGroupPoints + wcKnockoutPoints + wcBootPoints;
          const goldenBootPick   = (predictions?.goldenBoot && String(predictions.goldenBoot).trim()) || null;
          const tiebreakerGuess  = Number.isFinite(predictions?.totalGoals) ? predictions.totalGoals : null;
          const matchesPlayed    = (groupScore?.matchesPlayed || 0) + (koScore?.matchesCompleted || 0);
          // Final-score tiebreaker: once the Final is decided, rank ties by who
          // came closest to the actual combined scoreline. null until decided.
          const finalMatch       = Array.isArray(wcLiveStandings?.knockouts?.FINAL) ? wcLiveStandings.knockouts.FINAL[0] : null;
          const finalActualTotal = (finalMatch && finalMatch.winnerCode != null
            && Number.isFinite(Number(finalMatch.homeScore)) && Number.isFinite(Number(finalMatch.awayScore)))
            ? Number(finalMatch.homeScore) + Number(finalMatch.awayScore)
            : null;
          const tiebreakerDelta  = (finalActualTotal != null && tiebreakerGuess != null)
            ? Math.abs(tiebreakerGuess - finalActualTotal)
            : null;
          return {
            ...member,
            wcPoints:           wcTotalPoints,
            wcGroupPoints,
            wcKnockoutPoints,
            wcBootPoints,
            wcBootAwarded:      !!bootScore?.awarded,
            wcBootCorrect:      !!bootScore?.isCorrect,
            wcGoldenBootPick:   goldenBootPick,
            wcTiebreakerGuess:  tiebreakerGuess,
            wcTiebreakerDelta:  tiebreakerDelta,
            wcCorrectSlots:     groupScore
              ? Math.round(wcGroupPoints / WC_SCORING.GROUP_STAGE_PER_SLOT)
              : 0,
            wcMatchesPlayed:    matchesPlayed,
            wcIsFinal:          !!(groupScore?.isFullyComplete && koScore?.isFullyComplete && bootScore?.awarded),
          };
        })
        // Tiebreak order: total pts → closest final-score guess → group
        // correct slots → username. tiebreakerDelta is null until the Final is
        // decided, so it only sorts once everyone's delta is known.
        .sort((a, b) => {
          if ((b.wcPoints || 0) !== (a.wcPoints || 0)) return (b.wcPoints || 0) - (a.wcPoints || 0);
          const ad = a.wcTiebreakerDelta, bd = b.wcTiebreakerDelta;
          if (ad != null && bd != null && ad !== bd) return ad - bd;
          if (ad != null && bd == null) return -1;  // a has a (closer) guess, b didn't guess
          if (ad == null && bd != null) return 1;
          if ((b.wcCorrectSlots || 0) !== (a.wcCorrectSlots || 0)) return (b.wcCorrectSlots || 0) - (a.wcCorrectSlots || 0);
          return String(a.username || "").localeCompare(String(b.username || ""));
        });
    })();
    // Standings tab uses this when in a WC league; falls back to balance-sort otherwise.
    const displayStandings = isWcLeague ? (wcStandingsList || []) : st;
    // Helper: display points value for a row (WC pts vs balance).
    // PICKEM-PTS: pickem/survivor score on the member `wins` field — balance is
    // 0 for every member by design (startingBalance 0, settlement never credits).
    // Reading balance here rendered "0 PTS" for the whole leaderboard.
    const isNonWagerRoom = (league?.scoringMode || "wager") === "pickem" || (league?.scoringMode || "wager") === "survivor";
    const ptsUnit = isNonWagerRoom ? "Wins" : "pts";
    const getDisplayPts = isWcLeague
      ? (p) => (p?.wcPoints || 0)
      : isNonWagerRoom
        ? (p) => (p?.wins ?? 0)
        : (p) => (p?.balance ?? 0);
    const pickCtaLabel  = !isWcLeague ? "Make a Pick"
                       : bracketStatus === "complete"    ? "View Locked Bracket"
                       : bracketStatus === "in_progress" ? `Continue Picking · ${bracketGroupsCount}/12`
                       : "Make Your Group Stage Picks";
    const pickCtaArrowLabel = `${pickCtaLabel}${pickCtaLabel === "Make a Pick" ? " →" : ""}`;
    const bracketLocked = bracketStatus === "complete";

    // Days-to-kickoff countdown for the WC locked-state subtitle. Computed
    // off WORLD_CUP_2026.predictionLockAt (currently 2026-06-11T20:00:00Z,
    // the canonical tournament opener). Once kickoff passes (diff <= 0), the
    // subtitle flips to "Tournament live" so the badge stays meaningful
    // through July 19.
    const daysToKickoff = isWcLeague ? (() => {
      try {
        const lockAt = new Date(WORLD_CUP_2026.predictionLockAt);
        const diffMs = lockAt.getTime() - Date.now();
        return Math.ceil(diffMs / (1000 * 60 * 60 * 24));
      } catch { return null; }
    })() : null;
    const kickoffLabel = daysToKickoff == null ? null
                       : daysToKickoff > 1   ? `${daysToKickoff} days to kickoff`
                       : daysToKickoff === 1 ? "1 day to kickoff"
                       : daysToKickoff === 0 ? "Kicks off today"
                                             : "Tournament live";

    // Override W/L with actual pick counts to prevent member doc drift
    const myLeaguePicks = mp.filter(p => p.userId === user?.uid && ["won","lost","push"].includes(p.status));
    const myDisplayWins   = myLeaguePicks.filter(p => p.status === "won").length;
    const myDisplayLosses = myLeaguePicks.filter(p => p.status === "lost").length;

    // Pending picks for "Live Picks" strip
    // Only show picks where the game started within the last 6 hours.
    // Picks stuck pending for longer than that are stale data — don't show as "live".
    const SIX_HOURS = 6 * 60 * 60 * 1000;
    const now = Date.now();
    const pendingPicks = mp.filter(p => {
      if (p.status !== "pending") return false;
      // Parse commenceTime (Firestore Timestamp, serialized {_seconds}, ISO string, or Date)
      const ct = p.commenceTime?.toDate?.()
        ?? (p.commenceTime?._seconds  ? new Date(p.commenceTime._seconds  * 1000) : null)
        ?? (p.commenceTime?.seconds   ? new Date(p.commenceTime.seconds   * 1000) : null)
        ?? (p.commenceTime ? new Date(p.commenceTime) : null);
      if (!ct || isNaN(ct.getTime())) return true; // no time data → show it (safe default)
      return (now - ct.getTime()) < SIX_HOURS;
    });

    // Consensus: how many members bet the same game/direction
    const pickConsensus = {};
    pendingPicks.forEach(p => {
      const key = p.gameId || p.label || "";
      if (!pickConsensus[key]) pickConsensus[key] = [];
      pickConsensus[key].push(p.username);
    });

    // Medals for rank strip
    const medals = ["#1","#2","#3"];

    return (
      <div style={{ maxWidth:640, margin:"0 auto", paddingBottom:"calc(90px + env(safe-area-inset-bottom, 0px))", background:T.bg, minHeight:"100vh" }}>

        {/* ── World Cup bracket overlay (full-screen, position:fixed) ────────── */}
        {bracketOpen && (
          <WorldCupBracket
            user={user}
            username={username}
            league={bracketOpen}
            onClose={() => setBracketOpen(null)}
          />
        )}

        {/* -- LEAGUE HERO HEADER — cinematic arena banner -------------------- */}
        <div style={{
          background:"rgba(8,9,13,0.92)",
          borderBottom:"1px solid rgba(255,255,255,0.06)",
          position:"sticky", top:0, zIndex:20,
          backdropFilter:"blur(16px)",
        }}>
          {/* Top accent line — flat rule, no glow */}
          <div style={{ height:2, background: leagueEnded ? "#D9A441" : T.coral, opacity:0.5 }}/>

          {/* Champion banner — shown when league has ended */}
          {leagueEnded && (
            <div style={{
              padding:"12px 16px",
              background:"rgba(217,164,65,0.07)",
              borderBottom:"1px solid rgba(217,164,65,0.15)",
              display:"flex", alignItems:"center", gap:12,
            }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#D9A441" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink:0 }}>
                <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6"/>
                <path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18"/>
                <path d="M4 22h16"/>
                <path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22"/>
                <path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22"/>
                <path d="M18 2H6v7a6 6 0 0 0 12 0V2Z"/>
              </svg>
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ fontSize:13, fontWeight:700, color:"#D9A441", letterSpacing:"-0.2px" }}>
                  Season Complete
                </div>
                <div style={{ fontSize:11, color:"rgba(255,255,255,0.5)", marginTop:1, lineHeight:1.4 }}>
                  {champion
                    ? `${champion.username || champion.name || "Champion"} won with ${getDisplayPts(champion).toLocaleString()} ${ptsUnit}`
                    : "This league's season has ended"}
                </div>
              </div>
              {league.isCommissioner && (
                <button
                  onClick={() => {
                    window.torchConfirm?.(
                      "Start a new season? All balances and records will be reset.",
                      async () => {
                        try {
                          await restartLeague(activeLeague, user.uid);
                          window.torchToast?.("New season started!", "success");
                          fetchLeagues();
                          enterLeague({ ...league, status: "active", isEnded: false });
                        } catch (e) {
                          window.torchToast?.(e.message, "error");
                        }
                      },
                      { danger: true, confirmLabel: "Start new season" }
                    );
                  }}
                  style={{
                    padding:"8px 14px", borderRadius:12, flexShrink:0,
                    background:"rgba(217,164,65,0.14)",
                    border:"1px solid rgba(217,164,65,0.3)",
                    color:"#D9A441", fontSize:11, fontWeight:700, cursor:"pointer",
                  }}
                >New Season</button>
              )}
            </div>
          )}

          {/* Title row */}
          <div style={{ display:"flex", alignItems:"center", gap:8, padding:"10px 14px 8px" }}>
            <button onClick={() => { setActive(null); try { localStorage.removeItem("torch_active_league"); } catch {} setShowRoomMenu(false); }}
              aria-label="Back to leagues"
              style={{
                background:"none", border:"none", padding:"8px 6px 8px 0", cursor:"pointer",
                color:"rgba(255,255,255,0.5)", display:"flex", alignItems:"center",
                justifyContent:"center", flexShrink:0, marginLeft:-4,
              }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6"/></svg>
            </button>
            <div style={{ flex:1, minWidth:0 }}>
              <div style={{
                fontFamily:FD, fontSize:16, fontWeight:700, color:"#fff",
                whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis",
                letterSpacing:"-0.01em", lineHeight:1.1,
              }}>{league.name}</div>
              <div style={{ fontSize:10, color:"rgba(255,255,255,0.40)", marginTop:2, letterSpacing:"0.01em" }}>
                {league.isCommissioner ? "Captain" : league.commissionerName}
                {(league.sportFilter || league.eventSport) && <span> · {SPORT_FILTER_MAP[league.sportFilter || league.eventSport] || "All Sports"}</span>}
                {(() => {
                  // WC bracket pools: hide the "Nd left" tail. It implies a
                  // deadline the user should act against, but in a bracket
                  // pool the only deadline (kickoff lock) is already shown in
                  // the rules pills (LOCKS Jun 11) and the stats sub-row
                  // (X days to kickoff). Showing a third countdown here is
                  // visual noise.
                  if (isWorldCup2026League(league)) return null;
                  if (!league.endDate) return null;
                  const end = new Date(league.endDate?.seconds ? league.endDate.seconds * 1000 : league.endDate);
                  const diff = Math.ceil((end - new Date()) / (1000*60*60*24));
                  if (diff <= 0) return null;
                  return <span style={{ color: diff < 7 ? "#f87171" : "rgba(255,255,255,0.40)" }}> · {diff}d left</span>;
                })()}
              </div>
              {league.startDate && !isWorldCup2026League(league) && (() => {
                // The yellow "Season starts in N days" / "World Cup starts in
                // N days" header line. Kept for regular leagues. Suppressed
                // for WC because the same countdown surfaces twice already:
                // in the rules pills (LOCKS Jun 11) and the stats sub-row
                // (X days to kickoff in teal). Three duplicates on one
                // screen was the symptom; this line was the loudest.
                const start = new Date(league.startDate);
                const diff = Math.ceil((start - new Date()) / (1000*60*60*24));
                if (diff <= 0) return null;
                return <div style={{ marginTop:3, fontSize:9.5, color:"#FCD34D", fontWeight:700, fontFamily:FM }}>Season starts in {diff} day{diff !== 1 ? "s" : ""}</div>;
              })()}
            </div>

            {/* ⋯ menu */}
            <div style={{ position:"relative" }}>
              <button onClick={() => setShowRoomMenu(v => !v)} aria-label="League menu" style={{
                background:"none", border:"none", padding:"8px 4px", cursor:"pointer",
                color:"rgba(255,255,255,0.5)", fontSize:18, lineHeight:1, flexShrink:0,
                fontWeight:700, letterSpacing:"0.05em",
              }}>⋯</button>
              {showRoomMenu && (
                <>
                  <div onClick={() => setShowRoomMenu(false)} style={{ position:"fixed", inset:0, zIndex:40 }}/>
                  <div style={{
                    position:"absolute", top:40, right:0, zIndex:50,
                    background:T.card, border:`1px solid ${T.border}`, borderRadius:14,
                    boxShadow:"0 8px 32px rgba(0,0,0,0.4)", overflow:"hidden", minWidth:180,
                  }}>
                    <button onClick={() => { handleInvite(league); setShowRoomMenu(false); }} style={{
                      display:"flex", alignItems:"center", gap:10, width:"100%", padding:"13px 16px",
                      border:"none", background:"transparent", cursor:"pointer",
                      fontSize:13, fontWeight:600, color:T.ink, textAlign:"left",
                      borderBottom:`1px solid ${T.border}`,
                    }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/>
                        <circle cx="9" cy="7" r="4"/>
                        <path d="M22 11h-6m3-3v6"/>
                      </svg>
                      Invite Friends
                    </button>
                    {league.isCommissioner && (<>
                      <button onClick={() => { haptic.light(); setAnnounceDraft(""); setShowAnnounce(true); setShowRoomMenu(false); }} style={{
                        display:"flex", alignItems:"center", gap:10, width:"100%", padding:"13px 16px",
                        border:"none", background:"transparent", cursor:"pointer",
                        fontSize:13, fontWeight:600, color:T.ink, textAlign:"left",
                        borderBottom:`1px solid ${T.border}`,
                      }}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M3 11l18-7v16L3 13z"/>
                          <path d="M11 11v6"/>
                        </svg>
                        Post Announcement
                      </button>
                      <button onClick={() => {
                        setEditName(league.name || "");
                        setEditMotto(league.motto || "");
                        setEditPrize(league.prize || "");
                        setEditPayday(String(league.paydayAmount ?? WEEKLY_PAYDAY));
                        setShowSettings(true);
                        setShowRoomMenu(false);
                      }} style={{
                        display:"flex", alignItems:"center", gap:10, width:"100%", padding:"13px 16px",
                        border:"none", background:"transparent", cursor:"pointer",
                        fontSize:13, fontWeight:600, color:T.ink, textAlign:"left",
                        borderBottom:`1px solid ${T.border}`,
                      }}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <circle cx="12" cy="12" r="3"/>
                          <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
                        </svg>
                        League Settings
                      </button>
                    </>)}
                    {league.isCommissioner ? (
                      <button onClick={() => { handleDelete(league); setShowRoomMenu(false); }} style={{
                        display:"flex", alignItems:"center", gap:10, width:"100%", padding:"13px 16px",
                        border:"none", background:"transparent", cursor:"pointer",
                        fontSize:13, fontWeight:600, color:T.red, textAlign:"left",
                      }}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="3 6 5 6 21 6"/>
                          <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
                          <path d="M10 11v6M14 11v6"/>
                        </svg>
                        Delete League
                      </button>
                    ) : (
                      <button onClick={() => { handleLeave(activeLeague); setShowRoomMenu(false); }} style={{
                        display:"flex", alignItems:"center", gap:10, width:"100%", padding:"13px 16px",
                        border:"none", background:"transparent", cursor:"pointer",
                        fontSize:13, fontWeight:600, color:T.red, textAlign:"left",
                      }}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
                          <polyline points="16 17 21 12 16 7"/>
                          <line x1="21" y1="12" x2="9" y2="12"/>
                        </svg>
                        Leave League
                      </button>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Member count + quick stats in header */}
          <div style={{ display:"flex", alignItems:"center", gap:8, padding:"0 14px 14px" }}>
            <div style={{ display:"flex", alignItems:"center" }}>
              {st.slice(0,5).map((p, ri) => (
                <div key={p.id} style={{
                  width:22, height:22, borderRadius:"50%", marginLeft: ri>0 ? -7 : 0,
                  background: p.id===user?.uid
                    ? T.coral
                    : `linear-gradient(135deg,rgba(255,255,255,0.18),rgba(255,255,255,0.06))`,
                  border:`2px solid #080F1A`,
                  display:"flex", alignItems:"center", justifyContent:"center",
                  fontSize:9, fontWeight:700, color:"#fff", flexShrink:0,
                }}>{(p.username?.[0]||"?").toUpperCase()}</div>
              ))}
              {st.length > 5 && (
                <div style={{
                  width:22, height:22, borderRadius:"50%", marginLeft:-7,
                  background:"rgba(255,255,255,0.08)", border:"2px solid #080F1A",
                  display:"flex", alignItems:"center", justifyContent:"center",
                  fontSize:9, fontWeight:700, color:"rgba(255,255,255,0.5)",
                }}>+{st.length-5}</div>
              )}
            </div>
            <span style={{ fontSize:10, color:"rgba(255,255,255,0.40)", fontWeight:600 }}>
              {st.length} competing
            </span>
            {pendingPicks.length > 0 && (
              <span style={{
                marginLeft:"auto", fontSize:10, fontWeight:600, color:T.coral,
                letterSpacing:"0.01em",
              }}>
                {pendingPicks.length} in play
              </span>
            )}
          </div>
        </div>


        {/* -- LEAGUE RULES STRIP — visible to all members ------------------------
            Quiet info row showing the rules everyone agreed to: starting balance,
            pick cadence, miss penalty, and any sport/bet-type restrictions. Placed
            here (not in a settings tab) so members don't have to hunt for them. */}
        {(() => {
          const startBal = league.startingBalance ?? 1000;
          const pr = league.pickRequirements || {};
          const cadence = pr.cadence;          // "none" | "daily" | "weekly"
          const minPicks = pr.minPicks ?? 0;
          const minWager = pr.minWager;
          const penalty = pr.penalty || {};
          const penaltyLabel = (() => {
            if (cadence === "none" || !cadence) return null;
            switch (penalty.type) {
              case "fixed":   return penalty.value ? `−${penalty.value} pts` : null;
              case "percent": return penalty.value ? `−${penalty.value}% bal` : null;
              case "minWager":return minWager ? `−${minWager} pts` : null;
              case "brutal":  return "Lose all";
              default:        return null;
            }
          })();
          const cadenceLabel = (() => {
            if (!cadence || cadence === "none") return null;
            const noun = cadence === "daily" ? "day" : cadence === "weekly" ? "wk" : cadence;
            return `${minPicks || 1}/${noun}`;
          })();
          const sportsLabel = Array.isArray(league.allowedSports) && league.allowedSports.length > 0
            ? (league.allowedSports.length === 1 ? (SPORT_FILTER_MAP[league.allowedSports[0]] || league.allowedSports[0]) : `${league.allowedSports.length} sports`)
            : null;
          const payAmt = league.paydayAmount ?? WEEKLY_PAYDAY;
          const Pill = ({ label, value, accent }) => (
            <div style={{
              display:"flex", flexDirection:"column", alignItems:"flex-start", gap:1,
              padding:"6px 10px", borderRadius:8,
              background:"rgba(255,255,255,0.025)",
              border:"1px solid rgba(255,255,255,0.05)",
              flex:"0 0 auto",
            }}>
              <span style={{ fontSize:9, fontWeight:700, letterSpacing:"0.10em", color:"rgba(255,255,255,0.42)", textTransform:"uppercase", fontFamily:FM }}>{label}</span>
              <span style={{ fontSize:11.5, fontWeight:700, color: accent || "rgba(255,255,255,0.85)", fontFamily:FM, letterSpacing:"-0.005em" }}>{value}</span>
            </div>
          );
          // WC2026 bracket pools don't have a starting balance / cadence /
          // payday — they have one big lock and a max bracket score. The
          // regular Start/Pick/Miss/Payday/Sport pills are meaningless here.
          // Surface the three WC-relevant facts instead with teal accents
          // matching the rest of the WC league chrome.
          if (isWcLeague) {
            const wcLockShort = (() => {
              try {
                return new Date(WORLD_CUP_2026.predictionLockAt).toLocaleDateString("en-US", {
                  month: "short", day: "numeric", timeZone: "UTC",
                });
              } catch { return "Jun 11"; }
            })();
            return (
              <div style={{
                padding:"6px 14px 10px",
                display:"flex", flexWrap:"wrap", gap:6,
                borderBottom:"1px solid rgba(255,255,255,0.04)",
              }}>
                <Pill label="Format"  value="Bracket Pool"                                accent={T.teal} />
                <Pill label="Locks"   value={wcLockShort}                                 accent={T.teal} />
                <Pill label="Perfect" value={`${WC_SCORING.TOTAL_MAX.toLocaleString()} pts`} accent={T.gold} />
              </div>
            );
          }
          return (
            <div style={{
              padding:"6px 14px 10px",
              display:"flex", flexWrap:"wrap", gap:6,
              borderBottom:"1px solid rgba(255,255,255,0.04)",
            }}>
              <Pill label="Start" value={`${startBal.toLocaleString()} pts`} />
              {cadenceLabel && <Pill label="Pick" value={cadenceLabel} />}
              {penaltyLabel && <Pill label="Miss" value={penaltyLabel} accent="#FCA5A5" />}
              {payAmt > 0 && <Pill label="Payday" value={`+${payAmt}/wk`} accent="#86EFAC" />}
              {sportsLabel && <Pill label="Sport" value={sportsLabel} />}
            </div>
          );
        })()}


        {/* -- MY STATS — quiet inline row instead of HUD panels ----------------- */}
        {myEntry && (() => {
          const now = new Date();
          const dayOfWeek = now.getDay();
          const payDay = league?.paydayDay ?? 5;
          const payAmount = league?.paydayAmount ?? WEEKLY_PAYDAY;
          const rawDaysUntil = ((payDay - dayOfWeek) + 7) % 7;
          const daysUntil = rawDaysUntil || 7;
          const nextPayday = new Date(now);
          nextPayday.setDate(now.getDate() + daysUntil);
          nextPayday.setHours(0,0,0,0);
          const msLeft = nextPayday - now;
          const hLeft  = Math.floor(msLeft / 3600000);
          const mLeft  = Math.floor((msLeft % 3600000) / 60000);
          const payLabel = hLeft < 1 ? `${mLeft}m` : hLeft < 24 ? `${hLeft}h` : `${Math.ceil(hLeft/24)}d`;
          const payColor = hLeft < 24 ? "#4ade80" : hLeft < 72 ? "#FCD34D" : "rgba(255,255,255,0.55)";

          const balColor = isNonWagerRoom ? "rgba(255,255,255,0.85)" : (myEntry.balance??0) >= 1000 ? "rgba(255,255,255,0.85)" : (myEntry.balance??0) > 0 ? "rgba(255,255,255,0.85)" : "#FCA5A5";
          // Color the podium only — gold/silver/bronze for #1/#2/#3, neutral for everyone else.
          // Coloring every rank turns the stat strip into a video game UI; podium-only feels editorial.
          const rankColor = myRank===1 ? "#FFD700" : myRank===2 ? "#C0C0C0" : myRank===3 ? "#CD7F32" : "rgba(255,255,255,0.85)";
          const rankWeight = myRank && myRank <= 3 ? 700 : 600;

          // WC2026: balance/record/payday are meaningless for bracket pools.
          // Replace the hero strip with bracket-relevant info: completion
          // status (or live score post-kickoff), rank within league, and
          // a kickoff countdown that flips to "Tournament live" once games start.
          if (isWcLeague) {
            const myWcRowIdx = (wcStandingsList || []).findIndex(m => m.id === user?.uid);
            const myWcRow = myWcRowIdx >= 0 ? wcStandingsList[myWcRowIdx] : null;
            const myWcRank = myWcRowIdx >= 0 ? myWcRowIdx + 1 : null;
            const myWcPoints = myWcRow?.wcPoints || 0;
            const tournamentLive = (wcStandingsList || []).some(m => (m.wcMatchesPlayed || 0) > 0);
            // Hero displays score during tournament, completion status before.
            // Pre-tournament with 0 score isn't a useful headline, so we lead
            // with "Locked" / "X/12" instead, mirroring the CTA semantics.
            const heroValue = tournamentLive
              ? myWcPoints
              : (bracketLocked ? "Locked" : `${bracketGroupsCount}/12`);
            const heroSuffix = tournamentLive ? "pts" : (bracketLocked ? null : "groups");
            const heroColor = (tournamentLive || bracketLocked)
              ? T.teal
              : "rgba(255,255,255,0.85)";
            const wcRankColor = myWcRank === 1 ? "#FFD700"
                              : myWcRank === 2 ? "#C0C0C0"
                              : myWcRank === 3 ? "#CD7F32"
                                               : "rgba(255,255,255,0.85)";
            return (
              <div style={{ margin:"10px 16px 4px" }}>
                <div style={{ display:"flex", alignItems:"baseline", gap:12, flexWrap:"wrap" }}>
                  <div style={{ display:"flex", alignItems:"baseline", gap:5 }}>
                    <span style={{
                      fontFamily:FD, fontSize:28, fontWeight:700, color:heroColor,
                      letterSpacing:"-0.025em", lineHeight:1,
                    }}>
                      {heroValue}
                    </span>
                    {heroSuffix && (
                      <span style={{
                        fontSize:10, fontWeight:600, color:"rgba(255,255,255,0.32)",
                        letterSpacing:"0.06em", textTransform:"uppercase",
                      }}>{heroSuffix}</span>
                    )}
                  </div>
                  {myWcRank && st.length > 1 && (
                    <span style={{
                      fontSize:11, color:"rgba(255,255,255,0.45)",
                      display:"inline-flex", alignItems:"baseline", gap:3,
                    }}>
                      <span style={{
                        color: wcRankColor,
                        fontWeight: myWcRank && myWcRank <= 3 ? 700 : 600,
                      }}>#{myWcRank}</span>
                      <span style={{ color:"rgba(255,255,255,0.42)" }}>in league</span>
                    </span>
                  )}
                </div>
                <div style={{ display:"flex", alignItems:"center", gap:8, marginTop:6, fontSize:11, color:"rgba(255,255,255,0.42)" }}>
                  <span style={{ display:"inline-flex", alignItems:"baseline", gap:4 }}>
                    <span style={{ color: bracketLocked ? T.teal : "rgba(255,255,255,0.7)", fontWeight:600 }}>
                      {bracketLocked ? "Bracket Locked" : `${bracketGroupsCount}/12 groups`}
                    </span>
                  </span>
                  {kickoffLabel && (
                    <>
                      <span style={{ color:"rgba(255,255,255,0.32)" }}>·</span>
                      <span style={{ display:"inline-flex", alignItems:"baseline", gap:4 }}>
                        <span style={{ color: tournamentLive ? T.teal : "rgba(255,255,255,0.7)", fontWeight:600 }}>
                          {kickoffLabel}
                        </span>
                      </span>
                    </>
                  )}
                </div>
              </div>
            );
          }

          return (
            // Restructured stats row: balance is the hero (largest, brightest),
            // rank/record/payday are supporting metrics in equal lighter weight.
            // Replaces the four-equal-items-separated-by-dots layout that had
            // no visual hierarchy. Tap targets preserved on each segment.
            <div style={{ margin:"10px 16px 4px" }}>
              <div style={{ display:"flex", alignItems:"baseline", gap:12, flexWrap:"wrap" }}>
                {/* Hero: balance — eyes land here first */}
                <button onClick={() => setStatSheet("balance")} style={{
                  background:"none", border:"none", padding:0, cursor:"pointer",
                  display:"flex", alignItems:"baseline", gap:5, color:"inherit",
                }}>
                  <span style={{
                    fontFamily:FD, fontSize:28, fontWeight:700, color:balColor,
                    letterSpacing:"-0.025em", lineHeight:1,
                  }}>
                    <AnimatedNumber value={getDisplayPts(myEntry)} />
                  </span>
                  <span style={{
                    fontSize:10, fontWeight:600, color:"rgba(255,255,255,0.32)",
                    letterSpacing:"0.06em", textTransform:"uppercase",
                  }}>{ptsUnit}</span>
                </button>
                {/* Rank chip — only shown when ranked, podium gets color */}
                {myRank && (
                  <button onClick={() => setStatSheet("rank")} style={{
                    background:"none", border:"none", padding:0, cursor:"pointer",
                    fontSize:11, color:"rgba(255,255,255,0.45)", display:"inline-flex", alignItems:"baseline", gap:3,
                  }}>
                    <span style={{ color:rankColor, fontWeight:rankWeight }}>#{myRank}</span>
                    <span style={{ color:"rgba(255,255,255,0.42)" }}>in league</span>
                  </button>
                )}
              </div>
              {/* Sub-row: record + next payday — same weight, dot separator stays for these */}
              <div style={{ display:"flex", alignItems:"center", gap:8, marginTop:6, fontSize:11, color:"rgba(255,255,255,0.42)" }}>
                <button onClick={() => setStatSheet("record")} style={{
                  background:"none", border:"none", padding:0, cursor:"pointer", color:"inherit",
                  fontSize:11, display:"inline-flex", alignItems:"baseline", gap:4,
                }}>
                  <span style={{ color:"rgba(255,255,255,0.7)", fontWeight:600 }}>{myDisplayWins}–{myDisplayLosses}</span>
                  <span style={{ color:"rgba(255,255,255,0.42)" }}>record</span>
                </button>
                {/* PICKEM-PTS: no chip when there's no stipend — pick'em leagues carry paydayAmount 0 and rendered "+0 in 5d" */}
                {payAmount > 0 && (<>
                <span style={{ color:"rgba(255,255,255,0.32)" }}>·</span>
                <button onClick={() => setStatSheet("payday")} style={{
                  background:"none", border:"none", padding:0, cursor:"pointer", color:"inherit",
                  fontSize:11, display:"inline-flex", alignItems:"baseline", gap:4,
                }}>
                  <span style={{ color:payColor, fontWeight:600 }}>+{payAmount}</span>
                  <span style={{ color:"rgba(255,255,255,0.42)" }}>in {payLabel}</span>
                </button>
                </>)}
              </div>
            </div>
          );
        })()}
        {/* -- PASS THE TORCH / SPOT ME — either/or CTA based on balance -----
            Broke users (< 25 pts) see Spot Me; healthier balances see PtT.
            Mutually exclusive: server-side also enforces you can't have both. */}
        {/* Pass the Torch CTA — shown when user has no active loan in this league.
            Single CTA for everyone: when balance is low, the copy nudges them to
            split a pick with a teammate.
            Excluded in pickem/survivor: Pass the Torch lends POINTS between
            members, and those formats have no ledger — every member sits at 0, so
            there's nothing to lend, borrow, or repay. Worse, the balance check
            below (bal < 25) would show every single member the "Out of points?"
            broke-user nudge. Same rationale as the existing World Cup exclusion:
            a league format without a wager economy has no use for a loan. */}
        {!leagueEnded && myEntry && !myTorchLoan && !isWorldCup2026League(league)
          && (league?.scoringMode || "wager") === "wager" && (() => {
          const bal = myEntry.balance ?? 0;
          const isBroke = bal < 25;
          return (
            <div style={{ padding:"4px 16px 0", display:"flex", alignItems:"center", gap:8 }}>
              <button onClick={() => {
                // Pass the league's per-pick minimum so the modal can pre-emptively
                // block sub-minWager loans that would be unusable downstream.
                const lmw = Number(league?.pickRequirements?.minWager) || 0;
                if (onOpenTorchModal) onOpenTorchModal(activeLeague, standings[activeLeague] || [], lmw);
                else setShowTorchRequest(true);
              }} style={{
                flex:1, padding:"6px 0", background:"none", border:"none", cursor:"pointer",
                textAlign:"left", display:"flex", alignItems:"center", gap:6,
                color:"rgba(255,255,255,0.55)", fontSize:11.5,
              }}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={T.coral} strokeWidth="1.8" strokeLinecap="round">
                  <path d="M12 22c-4.97 0-9-2.69-9-6v-.18C4.32 12.47 7.6 10 8 6c.02-.17.17-.3.34-.28C10.09 5.96 12 8 12 8s1.91-2.04 3.66-2.28c.17-.02.32.11.34.28.4 4 3.68 6.47 5 9.82V16c0 3.31-4.03 6-9 6z"/>
                </svg>
                {isBroke ? (
                  <span>Out of points? <span style={{ color:T.coral, fontWeight:600, borderBottom:`1px solid ${T.coral}40` }}>Split a pick</span> with a teammate.</span>
                ) : (
                  <span>Want to split a pick? <span style={{ color:T.coral, fontWeight:600, borderBottom:`1px solid ${T.coral}40` }}>Pass the Torch</span></span>
                )}
              </button>
              <button onClick={() => onOpenTorchInfo?.(activeLeague, standings[activeLeague] || [])}
                aria-label="How Pass the Torch works"
                style={{
                  width:18, height:18, borderRadius:"50%", background:"rgba(255,255,255,0.04)",
                  border:"1px solid rgba(255,255,255,0.08)", cursor:"pointer",
                  color:"rgba(255,255,255,0.45)", fontSize:10, fontWeight:700,
                  fontFamily:"Georgia, serif", fontStyle:"italic",
                  display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0,
                }}>i</button>
            </div>
          );
        })()}

        {/* Active torch loan status — shown when borrower has pending/active.
            Pending state shows a Cancel button so borrower can withdraw the
            request before the lender responds. Active loans are committed —
            no cancel since the lender has already paid in. */}
        {/* For OFFER-INITIATED loans, the borrower (recipient) sees the gold inbox
            card while pending, so this strip would be redundant and misleading
            ("Waiting on X to fund" doesn't apply — X already funded). Once accepted
            (status === "active"), this strip IS correct and shows alongside the active pick. */}
        {myTorchLoan && !(myTorchLoan.offerInitiated && myTorchLoan.status === "pending") && (
          <div style={{
            margin:"8px 14px 0", padding:"12px 14px", borderRadius:14,
            background:"rgba(255,107,61,0.06)", border:"1px solid rgba(255,107,61,0.12)",
            display:"flex", alignItems:"center", gap:10,
          }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={T.coral} strokeWidth="1.8">
              <path d="M12 22c-4.97 0-9-2.69-9-6v-.18C4.32 12.47 7.6 10 8 6c.02-.17.17-.3.34-.28C10.09 5.96 12 8 12 8s1.91-2.04 3.66-2.28c.17-.02.32.11.34.28.4 4 3.68 6.47 5 9.82V16c0 3.31-4.03 6-9 6z"/>
            </svg>
            <div style={{ flex:1, minWidth:0 }}>
              <div style={{ fontSize:12, fontWeight:700, color:T.coral }}>
                {myTorchLoan.status === "pending" ? "Torch request pending" : "Torch active"}
              </div>
              <div style={{ fontSize:11, color:"rgba(255,255,255,0.35)", marginTop:1 }}>
                {myTorchLoan.status === "pending"
                  ? `Waiting on ${myTorchLoan.lenderName} to fund ${myTorchLoan.amount} pts`
                  : `${myTorchLoan.amount} pt pick · funded by ${myTorchLoan.lenderName}`}
              </div>
            </div>
            {myTorchLoan.status === "pending" && (
              <button
                onClick={() => {
                  if (torchCancelling) return;
                  window.torchConfirm?.(
                    `Cancel your torch request to ${myTorchLoan.lenderName}?`,
                    async () => {
                      setTorchCancelling(true);
                      try {
                        await cancelTorchLoan(myTorchLoan.id, user.uid);
                        setMyTorchLoan(null);
                        // Refresh balance + standings if anything was refunded
                        const freshSt = await loadLeagueStandings(activeLeague, modeFor(activeLeague)).catch(() => []);
                        setStandings(prev => ({ ...prev, [activeLeague]: freshSt }));
                        window.torchToast?.("Request cancelled", "info");
                      } catch (e) {
                        window.torchToast?.(e.message || "Cancel failed", "error");
                      } finally {
                        setTorchCancelling(false);
                      }
                    },
                    { confirmLabel: "Yes, cancel", danger: true }
                  );
                }}
                disabled={torchCancelling}
                style={{
                  flexShrink:0, padding:"6px 12px", borderRadius:10,
                  background:"rgba(255,255,255,0.05)",
                  border:"1px solid rgba(255,255,255,0.08)",
                  color:"rgba(255,255,255,0.6)", fontSize:11, fontWeight:600,
                  cursor: torchCancelling ? "wait" : "pointer",
                  whiteSpace:"nowrap",
                }}
              >{torchCancelling ? "…" : "Cancel"}</button>
            )}
          </div>
        )}

        {/* Torch inbox — merged feed of REQUESTS where I'm the lender + OFFERS where I'm the recipient.
            Each item carries `offerInitiated` set by torchLoans.js's offerTorchLoan; we branch the
            CF call + copy + activity event on that flag. */}
        {torchInbox.length > 0 && (
          <div style={{ padding:"4px 0" }}>
            {torchInbox.map(loan => {
              // Pick the right card component:
              //   offer-initiated → gold TorchOfferInboxCard (current user is the recipient)
              //   request-initiated → coral TorchInboxCard      (current user is the lender)
              const CardComp = loan.offerInitiated ? TorchOfferInboxCard : TorchInboxCard;
              return (
              <CardComp
                key={loan.id}
                loan={loan}
                accepting={torchAccepting}
                onAccept={async (loanId) => {
                  setTorchAccepting(true);
                  try {
                    if (loan.offerInitiated) {
                      // OFFER FLOW — current user is the BORROWER accepting a free pick from a lender.
                      // The CF (respondToTorchOfferCallable) submits the pick atomically under both names.
                      await respondToTorchOffer(loanId, true);
                      try {
                        const myName = user.displayName || user.email?.split("@")[0] || "Someone";
                        await writeLeagueActivity(user.uid, [activeLeague], {
                          type: "torch_offer_accepted",
                          userId: user.uid, username: myName,
                          // PRIVACY: torch den events broadcast to the whole
                          // league — in until_start leagues the pick's side
                          // stays out of the label pre-kickoff (the two torch
                          // parties still see the full pick in the torch flow
                          // itself, which is consensual).
                          label: league?.pickPrivacy === "until_start"
                            ? `accepted ${loan.lenderName}'s torch — ${loan.amount} pts`
                            : `accepted ${loan.lenderName}'s torch — ${loan.amount} pts on ${loan.pickLabel}`,
                          torchLoanId: loanId,
                          torchAmount: loan.amount,
                          torchLenderUid: loan.lenderUid,
                          torchLenderName: loan.lenderName,
                          torchBorrowerUid: user.uid,
                          torchBorrowerName: myName,
                          torchPickLabel: loan.pickLabel || "",
                          torchGameLabel: loan.gameLabel || "",
                          torchStatus: "active",
                          torchOfferInitiated: true,
                        });
                      } catch {}
                      setTorchInbox(prev => prev.filter(l => l.id !== loanId));
                      window.torchToast?.(`Locked in. ${loan.lenderName}'s torch is on your card.`, "success");
                    } else {
                      // REQUEST FLOW — current user is the LENDER funding a borrower's pick (existing path)
                      await respondToTorchLoan(loanId, user.uid, true);
                      try {
                        await writeLeagueActivity(user.uid, [activeLeague], {
                          type: "torch_passed",
                          userId: user.uid, username: user.displayName || user.email?.split("@")[0],
                          // PRIVACY: same pick-detail strip as the accept event.
                          label: league?.pickPrivacy === "until_start"
                            ? `passed the torch to ${loan.borrowerName} — ${loan.amount} pts`
                            : `passed the torch to ${loan.borrowerName} — ${loan.amount} pts on ${loan.pickLabel}`,
                        });
                      } catch {}
                      setTorchInbox(prev => prev.filter(l => l.id !== loanId));
                      window.torchToast?.(`Funded. ${loan.borrowerName}'s pick is in.`, "success");
                    }
                    const freshSt = await loadLeagueStandings(activeLeague, modeFor(activeLeague)).catch(() => []);
                    setStandings(prev => ({ ...prev, [activeLeague]: freshSt }));
                  } catch (e) {
                    window.torchToast?.(e.message, "error");
                  } finally { setTorchAccepting(false); }
                }}
                onDecline={async (loanId) => {
                  try {
                    if (loan.offerInitiated) {
                      // OFFER decline — CF refunds the lender (they were debited at offer time)
                      await respondToTorchOffer(loanId, false);
                      setTorchInbox(prev => prev.filter(l => l.id !== loanId));
                      window.torchToast?.("Offer declined", "info");
                      // Refresh standings so the lender's refunded balance shows immediately
                      const freshSt = await loadLeagueStandings(activeLeague, modeFor(activeLeague)).catch(() => []);
                      setStandings(prev => ({ ...prev, [activeLeague]: freshSt }));
                    } else {
                      await respondToTorchLoan(loanId, user.uid, false);
                      setTorchInbox(prev => prev.filter(l => l.id !== loanId));
                      window.torchToast?.("Request declined", "info");
                    }
                  } catch (e) {
                    window.torchToast?.(e.message, "error");
                  }
                }}
              />
              );
            })}
          </div>
        )}

        {/* -- CAPTAIN CONTROL STRIP — visible only to commissioners -----------------------------
            Compressed from 3 stat tiles + 3 chunky buttons into a single inline scoreboard
            and a low-key action row. Same functionality, ~50% less vertical space, no gradient
            backgrounds competing with the chat below. */}
        {league.isCommissioner && !leagueEnded && (
          <div style={{ padding:"4px 16px 6px", borderBottom:"1px solid rgba(255,255,255,0.04)" }}>
            {(() => {
              const ins = getCaptainInsights(activeLeague);
              const leaderName = ins.leader ? (ins.leader.username || ins.leader.name || "—").split(" ")[0] : "—";
              const activeColor = ins.activePickers > 0 ? "rgba(255,255,255,0.85)" : "rgba(255,255,255,0.35)";
              // WC-specific: count members who have any predictions saved
              // (rough proxy for "engaged" — a strict "locked" check would
              // require completion status for every member, which we don't
              // load up-front. Saved-anything is a useful nudge metric for
              // captains pre-tournament.)
              const wcSavedCount = isWcLeague
                ? ((wcMemberBrackets[league.id] || []).filter(m => m.predictions).length)
                : 0;
              // WC lock-date short label: "Jun 11" from WORLD_CUP_2026.predictionLockAt.
              const wcLockLabel = isWcLeague ? (() => {
                try {
                  return new Date(WORLD_CUP_2026.predictionLockAt).toLocaleDateString("en-US", {
                    month: "short", day: "numeric", timeZone: "UTC",
                  });
                } catch { return "Jun 11"; }
              })() : null;
              return (
                <>
                  <div style={{
                    display:"flex", flexWrap:"wrap", alignItems:"center", gap:8, fontSize:9.5, fontFamily:FM,
                    color:"rgba(255,255,255,0.42)", letterSpacing:"0.07em", marginBottom:4,
                  }}>
                    <span><span style={{ color:"rgba(255,255,255,0.55)", fontWeight:600 }}>CAPTAIN</span></span>
                    <span style={{ color:"rgba(255,255,255,0.32)" }}>·</span>
                    {isWcLeague ? (
                      // WC variant: show how many members have started a bracket
                      // and the lock deadline. Captain nudges late lockers via
                      // the existing Announce action.
                      <>
                        <span><span style={{ color:wcSavedCount > 0 ? T.teal : "rgba(255,255,255,0.35)", fontWeight:700 }}>{wcSavedCount}/{ins.totalMembers}</span> LOCKED</span>
                        <span style={{ color:"rgba(255,255,255,0.32)" }}>·</span>
                        <span>LOCK <span style={{ color:T.teal, fontWeight:700 }}>{wcLockLabel}</span></span>
                      </>
                    ) : (
                      <>
                        <span><span style={{ color:activeColor, fontWeight:700 }}>{ins.activePickers}/{ins.totalMembers}</span> ACTIVE</span>
                        <span style={{ color:"rgba(255,255,255,0.32)" }}>·</span>
                        <span><span style={{ color:"rgba(255,255,255,0.85)", fontWeight:700 }}>{ins.recentPicks}</span> PICKS/WK</span>
                        <span style={{ color:"rgba(255,255,255,0.32)" }}>·</span>
                        <span>LEADER <span style={{ color:"rgba(255,255,255,0.85)", fontWeight:700 }}>{leaderName}</span></span>
                      </>
                    )}
                  </div>
                  <div style={{ display:"flex", gap:12, alignItems:"center" }}>
                    <button onClick={() => { haptic.light(); setAnnounceDraft(""); setShowAnnounce(true); }} style={{
                      background:"none", border:"none", padding:0, cursor:"pointer",
                      color:T.coral, fontSize:11, fontWeight:600, letterSpacing:"0.01em",
                      borderBottom:`1px solid ${T.coral}40`,
                    }}>Announce</button>
                    <button onClick={() => {
                      setEditName(league.name || ""); setEditMotto(league.motto || ""); setEditPrize(league.prize || "");
                      setEditPayday(String(league.paydayAmount ?? WEEKLY_PAYDAY)); setShowSettings(true);
                    }} style={{
                      background:"none", border:"none", padding:0, cursor:"pointer",
                      color:"rgba(255,255,255,0.45)", fontSize:11, fontWeight:500,
                    }}>Settings</button>
                    <button onClick={() => setShowCaptainInsights(true)} style={{
                      background:"none", border:"none", padding:0, cursor:"pointer",
                      color:"rgba(255,255,255,0.45)", fontSize:11, fontWeight:500,
                    }}>Stats</button>
                  </div>
                </>
              );
            })()}
          </div>
        )}

        {/* -- DEN TAB BAR ----------------------------- */}
        <div style={{
          display:"flex", gap:0, padding:"0 14px",
          borderBottom:"1px solid rgba(255,255,255,0.06)",
          background:T.bg,
        }}>
          {[
            { key:"den",       label:"Social",    icon:(on) => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={on?"#fff":"rgba(255,255,255,0.40)"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg> },
            { key:"standings", label:"Standings", icon:(on) => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={on?"#fff":"rgba(255,255,255,0.40)"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6"/><path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18"/><path d="M4 22h16"/><path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22"/><path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22"/><path d="M18 2H6v7a6 6 0 0 0 12 0V2Z"/></svg> },
            // WC2026 only — surface a "Brackets" tab so users can see who in
            // their league has predicted what (after each player's lock-in).
            // Skipped entirely for non-WC leagues to avoid clutter.
            ...(isWcLeague ? [{ key:"brackets", label:"Brackets", icon:(on) => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={on?"#fff":"rgba(255,255,255,0.40)"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M8 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h3"/><path d="M16 3h3a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-3"/><path d="M8 7h8"/><path d="M8 12h8"/><path d="M8 17h5"/></svg> }] : []),
            { key:"picks",     label:"Picks",     icon:(on) => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={on?"#fff":"rgba(255,255,255,0.40)"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="6"/><circle cx="12" cy="12" r="2"/></svg> },
          ].map(({ key, label, icon }) => {
            const active = roomTab === key;
            return (
              <button key={key} onClick={() => { haptic.light(); setRoomTab(key); try { localStorage.setItem("torch_den_tab", key); } catch {} }} className="no-press" style={{
                flex:1, padding:"10px 4px 9px",
                border: "none", background: "transparent", cursor:"pointer",
                position:"relative",
                display:"flex", alignItems:"center", justifyContent:"center", gap:6,
                transition:"all 0.18s",
              }}>
                {icon(active)}
                <span style={{
                  fontSize:12, fontWeight: active ? 700 : 500,
                  color: active ? "#fff" : "rgba(255,255,255,0.40)",
                  letterSpacing:"-0.005em",
                  transition:"color 0.18s, font-weight 0.18s",
                }}>{label}</span>
                {/* Premium underline — pill, not full-width line */}
                {active && (
                  <span style={{
                    position:"absolute", bottom:-1, left:"30%", right:"30%", height:2,
                    background:T.coral, borderRadius:1,
                    boxShadow:`0 0 6px ${T.coral}AA`,
                  }}/>
                )}
              </button>
            );
          })}
        </div>
        {/* == PICKS TAB ====================================================== */}
        {roomTab === "picks" && (
          <div style={{ paddingBottom: "calc(90px + env(safe-area-inset-bottom, 0px))" }}>

        {/* -- WC2026: USER'S BRACKET SUMMARY -----------------------------------
            Bracket-pool leagues don't have individual game picks — they have
            ONE bracket per user. Surface that bracket here at the top of the
            Picks tab. The blocks below (live picks rail, empty state, member
            picks lists) will not render because pendingPicks and memberPicks
            are both empty for WC leagues, but we still explicitly gate the
            empty state with !isWcLeague below to avoid a confusing "No picks
            yet" message under a bracket that's already saved. */}
        {isWcLeague && (() => {
          const myBracket = (wcMemberBrackets[league.id] || [])
            .find(m => m.userId === user?.uid)?.predictions;

          // Pre-bracket nudge: user hasn't started yet.
          if (!myBracket || Object.keys(myBracket?.groups || {}).length === 0) {
            return (
              <div style={{ padding:"40px 20px", textAlign:"center" }}>
                <div style={{
                  width:50, height:50, borderRadius:14, margin:"0 auto 12px",
                  background:T.tealDim, border:`1px solid ${T.teal}33`,
                  display:"flex", alignItems:"center", justifyContent:"center",
                }}>
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={T.teal} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M8 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h3"/>
                    <path d="M16 3h3a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-3"/>
                    <path d="M8 7h8"/><path d="M8 12h8"/><path d="M8 17h5"/>
                  </svg>
                </div>
                <div style={{ fontSize:14, fontWeight:700, color:"rgba(255,255,255,0.85)", marginBottom:5, fontFamily:FD, letterSpacing:"-0.01em" }}>
                  No bracket yet
                </div>
                <div style={{ fontSize:11, color:"rgba(255,255,255,0.45)", lineHeight:1.5, marginBottom:16 }}>
                  Pick your group winners, golden boot, and final tiebreaker {kickoffLabel ? `· ${kickoffLabel}` : ""}
                </div>
                <button onClick={() => goToGamesForLeague(league)} style={{
                  padding:"10px 22px",
                  background:T.teal,
                  border:"none", borderRadius:18, color:"#fff", fontSize:12, fontWeight:700, cursor:"pointer",
                  boxShadow:"0 4px 16px -2px rgba(20,184,166,0.40)",
                }}>Make Your Picks</button>
              </div>
            );
          }

          return (
            <div style={{ padding:"14px" }}>
              {/* Header — title, completion status, edit/view affordance */}
              <div style={{
                marginBottom:14,
                display:"flex", alignItems:"baseline", justifyContent:"space-between", gap:10,
              }}>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontSize:17, fontWeight:700, color:"#fff", fontFamily:FD, letterSpacing:"-0.01em" }}>
                    Your Bracket
                  </div>
                  <div style={{ fontSize:11, color:bracketLocked ? T.teal : "rgba(255,255,255,0.45)", marginTop:2, fontWeight:600 }}>
                    {bracketLocked
                      ? `Locked · ${kickoffLabel || "ready for kickoff"}`
                      : `${bracketGroupsCount}/12 groups picked`}
                  </div>
                </div>
                <button onClick={() => goToGamesForLeague(league)} style={{
                  background:"transparent", border:`1px solid ${T.teal}40`, borderRadius:10,
                  padding:"6px 12px", color:T.teal, fontSize:10, fontWeight:700, cursor:"pointer",
                  letterSpacing:"0.04em", textTransform:"uppercase", flexShrink:0,
                }}>{bracketLocked ? "View" : "Edit"}</button>
              </div>

              {/* Prominent knockout CTA — the tiny "Edit" affordance wasn't
                  readable as "go make your knockout picks," so once any KO
                  round has unlocked we surface a big, unmistakable button.
                  Shown for every member during the knockout phase (group
                  picks being locked doesn't matter — this is about KO). */}
              {(() => {
                const koOpen = ["R32","R16","QF","SF","FINAL"].some(r => {
                  const ts = ROUND_UNLOCKS[r];
                  return ts && Date.now() >= new Date(ts).getTime();
                });
                if (!koOpen) return null;
                return (
                  <button
                    onClick={() => goToGamesForLeague(league)}
                    style={{
                      width:"100%", marginBottom:14, padding:"15px 16px",
                      background:T.teal, border:"none", borderRadius:13,
                      color:"#fff", cursor:"pointer",
                      display:"flex", alignItems:"center", justifyContent:"space-between", gap:12,
                      boxShadow:"0 2px 10px rgba(0,0,0,0.25)",
                    }}
                  >
                    <div style={{ textAlign:"left" }}>
                      <div style={{ fontSize:15, fontWeight:800, fontFamily:FD, letterSpacing:"-0.01em" }}>
                        Make your knockout picks →
                      </div>
                      <div style={{ fontSize:11.5, fontWeight:600, color:"rgba(255,255,255,0.85)", marginTop:2 }}>
                        The Round of 32 is live — pick winners before each match.
                      </div>
                    </div>
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink:0 }}>
                      <polyline points="9 18 15 12 9 6"/>
                    </svg>
                  </button>
                );
              })()}

              {/* Groups — 2-column grid of group cards with 1st-4th picks */}
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8, marginBottom:10 }}>
                {WC_GROUP_LETTERS.map((L) => {
                  const picks = myBracket?.groups?.[L];
                  const isComplete = Array.isArray(picks) && picks.length === 4;
                  return (
                    <div key={L} style={{
                      background: T.card,
                      border: `1px solid ${isComplete ? T.teal + "22" : T.border}`,
                      borderRadius:10, padding:"10px 12px",
                    }}>
                      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:6 }}>
                        <div style={{ display:"flex", alignItems:"baseline", gap:6 }}>
                          <span style={{ fontSize:9, fontWeight:700, color:"rgba(255,255,255,0.40)", letterSpacing:"0.10em", textTransform:"uppercase" }}>Group</span>
                          <span style={{ fontSize:14, fontWeight:700, color:"#fff", fontFamily:FD }}>{L}</span>
                        </div>
                        {isComplete && (
                          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke={T.teal} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                        )}
                      </div>
                      {isComplete ? (
                        picks.map((code, i) => {
                          const team = WC_TEAMS[code];
                          // Top 2 finishers advance to the knockouts — visually
                          // separate them from 3rd/4th place so the user can
                          // see at a glance which teams they're predicting will
                          // move on. Teal rank number + white bold team name
                          // for advancing slots; ranks 3-4 stay dim.
                          const isAdv = i < 2;
                          return (
                            <div key={i} style={{
                              fontSize:10.5,
                              color: isAdv ? "#fff" : "rgba(255,255,255,0.45)",
                              lineHeight:1.6, display:"flex", alignItems:"baseline", gap:5,
                              fontWeight: isAdv ? 600 : 400,
                            }}>
                              <span style={{
                                color: isAdv ? T.teal : "rgba(255,255,255,0.30)",
                                width:10, fontWeight:700,
                              }}>{i + 1}.</span>
                              <span style={{ display:"inline-flex", alignItems:"center", gap:1 }}><TeamFlag team={team} size={12} />{team?.abbr || code}</span>
                            </div>
                          );
                        })
                      ) : (
                        <div style={{ fontSize:11, color:"rgba(255,255,255,0.42)", fontStyle:"italic" }}>Not picked</div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Golden Boot + Tiebreaker — side-by-side */}
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8 }}>
                <div style={{
                  background: T.card,
                  border: `1px solid ${myBracket?.goldenBoot ? T.teal + "22" : T.border}`,
                  borderRadius:10, padding:"10px 12px",
                }}>
                  <div style={{ fontSize:9, fontWeight:700, color:"rgba(255,255,255,0.40)", letterSpacing:"0.10em", textTransform:"uppercase", marginBottom:6 }}>Golden Boot</div>
                  <div style={{
                    fontSize:12, fontWeight:700,
                    color: myBracket?.goldenBoot ? "#fff" : "rgba(255,255,255,0.30)",
                    fontStyle: myBracket?.goldenBoot ? "normal" : "italic",
                  }}>
                    {myBracket?.goldenBoot || "Not picked"}
                  </div>
                </div>
                <div style={{
                  background: T.card,
                  border: `1px solid ${Number.isFinite(myBracket?.totalGoals) && myBracket.totalGoals > 0 ? T.teal + "22" : T.border}`,
                  borderRadius:10, padding:"10px 12px",
                }}>
                  <div style={{ fontSize:9, fontWeight:700, color:"rgba(255,255,255,0.40)", letterSpacing:"0.10em", textTransform:"uppercase", marginBottom:6 }}>Tiebreaker</div>
                  <div style={{
                    fontSize:12, fontWeight:700,
                    color: Number.isFinite(myBracket?.totalGoals) && myBracket.totalGoals > 0 ? "#fff" : "rgba(255,255,255,0.30)",
                    fontStyle: Number.isFinite(myBracket?.totalGoals) && myBracket.totalGoals > 0 ? "normal" : "italic",
                  }}>
                    {Number.isFinite(myBracket?.totalGoals) && myBracket.totalGoals > 0
                      ? `${myBracket.totalGoals} goals in Final`
                      : "Not set"}
                  </div>
                </div>
              </div>
            </div>
          );
        })()}

        {/* -- LIVE PICKS RAIL — compact horizontal strip above the two-column body -- */}
        {!isWcLeague && pendingPicks.length > 0 && (
          <div style={{
            borderTop:"1px solid rgba(255,255,255,0.06)",
            padding:"10px 14px 8px",
            background:"rgba(220,38,38,0.04)",
          }}>
            <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:8 }}>
              <div style={{ display:"flex", alignItems:"center", gap:5 }}>
                <span style={{ width:6, height:6, borderRadius:"50%", background:T.coral, display:"inline-block", animation:"pulse 1.2s ease infinite", boxShadow:`0 0 0 3px rgba(249,115,22,0.15)` }}/>
                <span style={{ fontSize:10, fontWeight:700, color:T.coral, letterSpacing:"0.08em", textTransform:"uppercase" }}>
                  {pendingPicks.length} In Play
                </span>
              </div>
            </div>
            <div style={{ display:"flex", gap:8, overflowX:"auto", WebkitOverflowScrolling:"touch", scrollbarWidth:"none", paddingBottom:2 }}>
              {pendingPicks.map((pk, i) => {
                const isMe = pk.userId === user?.uid;
                return (
                  <div key={pk.id||i}
                    onClick={() => setSelectedLeaguePick(pk)}
                    style={{
                      flexShrink:0, borderRadius:12, padding:"9px 12px",
                      background: isMe ? `rgba(255,107,61,0.10)` : "rgba(255,255,255,0.04)",
                      border:`1px solid ${isMe ? "rgba(255,107,61,0.25)" : "rgba(255,255,255,0.07)"}`,
                      cursor:"pointer", minWidth:120, maxWidth:150,
                    }}
                  >
                    <div
                      onClick={e => { e.stopPropagation(); setViewingMember({ uid: pk.userId, username: pk.username?.split(" ")[0] || "?" }); }}
                      style={{ fontSize:10, fontWeight:700, color: isMe ? T.coral : "rgba(255,255,255,0.45)", marginBottom:4, cursor:"pointer" }}>
                      {isMe ? "You" : pk.username?.split(" ")[0]}
                    </div>
                    <div style={{ fontSize:12, fontWeight:700, color:"#fff", lineHeight:1.3, marginBottom:4, overflow:"hidden", display:"-webkit-box", WebkitLineClamp:2, WebkitBoxOrient:"vertical" }}>
                      {pk.label || pk.gameLabel}
                    </div>
                    <div style={{ fontSize:9, fontWeight:700, color:"rgba(255,255,255,0.42)", textTransform:"uppercase", letterSpacing:"0.05em" }}>
                      {/* Pick'em picks carry wager 0 forever, so the wager-less
                          fallback text "pending" would show even after settle.
                          For non-wager leagues just show the sport. */}
                      {pk.sport}{pk.wager > 0 ? ` · ${pk.wager.toLocaleString()} pts` : ((league?.scoringMode || "wager") === "wager" ? " · pending" : "")}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

            {/* Empty state when no picks at all */}
            {!isWcLeague && pendingPicks.length === 0 && (memberPicks[activeLeague]?.length ?? 0) === 0 && (
              <div style={{ padding:"40px 20px", textAlign:"center" }}>
                <div style={{
                  width:50, height:50, borderRadius:14, margin:"0 auto 12px",
                  background:"rgba(255,255,255,0.04)", border:"1px solid rgba(255,255,255,0.06)",
                  display:"flex", alignItems:"center", justifyContent:"center",
                }}>
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.2)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="6"/><circle cx="12" cy="12" r="2"/>
                  </svg>
                </div>
                <div style={{ fontSize:14, fontWeight:700, color:"rgba(255,255,255,0.55)", marginBottom:5, fontFamily:FD, letterSpacing:"-0.01em" }}>
                  {leagueEnded ? "Season Complete" : "No picks yet"}
                </div>
                <div style={{ fontSize:11, color:"rgba(255,255,255,0.32)", lineHeight:1.5, marginBottom:16 }}>
                  {leagueEnded ? "Season's over. Final standings below." : "Pick a game and it'll show up here. Everyone in the league sees it."}
                </div>
                {!leagueEnded && <button onClick={() => goToGamesForLeague(league)} style={{
                  padding:"10px 22px",
                  background:T.coral,
                  border:"none", borderRadius:18, color:"#fff", fontSize:12, fontWeight:700, cursor:"pointer",
                }}>{pickCtaArrowLabel}</button>}
              </div>
            )}

            {/* All member picks grid */}
            {(memberPicks[activeLeague]?.length ?? 0) > 0 && (
              <div style={{ padding:"12px 14px 0" }}>
                <div style={{ fontSize:9.5, fontWeight:700, color:"rgba(255,255,255,0.42)", letterSpacing:"0.10em", textTransform:"uppercase", marginBottom:10 }}>
                  All Picks · {memberPicks[activeLeague].length}
                </div>
                <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                  {memberPicks[activeLeague].map((pk, i) => {
                    const isMe = pk.userId === user?.uid;
                    const statusColor = pk.status === "won" ? "#4ade80" : pk.status === "lost" ? "#f87171" : pk.status === "push" ? "#6B7280" : pk.status === "voided" ? "#6B7280" : pk.status === "cashed" ? "#9CA3AF" : pk.status === "pending" ? T.coral : "rgba(255,255,255,0.3)";
                    const statusLabel = pk.status === "won" ? "✓ Won" : pk.status === "lost" ? "✗ Lost" : pk.status === "push" ? "↩ Push" : pk.status === "voided" ? "⊘ Void" : pk.status === "cashed" ? "Cashed" : pk.status === "pending" ? "Live" : pk.status ?? "—";
                    return (
                      <div key={pk.id||i}
                        onClick={() => setSelectedLeaguePick(pk)}
                        style={{
                          borderRadius:16, cursor:"pointer",
                          background: isMe ? "rgba(255,107,61,0.07)" : "rgba(255,255,255,0.03)",
                          border:`1px solid ${isMe ? "rgba(255,107,61,0.2)" : "rgba(255,255,255,0.07)"}`,
                          overflow:"hidden",
                          animation:"fadeUp 0.18s ease both", animationDelay:`${Math.min(i,10)*20}ms`,
                        }}
                      >
                        {/* Top strip: user + status */}
                        <div style={{
                          display:"flex", alignItems:"center", justifyContent:"space-between",
                          padding:"10px 12px 8px",
                          borderBottom:"1px solid rgba(255,255,255,0.06)",
                        }}>
                          <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                            <div
                              onClick={e => { e.stopPropagation(); setViewingMember({ uid: pk.userId, username: pk.username }); }}
                              style={{
                                width:36, height:36, minWidth:44, minHeight:44, borderRadius:"50%", flexShrink:0, cursor:"pointer",
                                background: isMe ? T.coral : "rgba(255,255,255,0.1)",
                                display:"flex", alignItems:"center", justifyContent:"center",
                                fontSize:11, fontWeight:700, color:"#fff",
                              }}>{(pk.username?.[0]||"?").toUpperCase()}</div>
                            <span style={{ fontSize:12, fontWeight:700, color: isMe ? T.coral : "rgba(255,255,255,0.5)" }}>
                              {isMe ? "You" : pk.username}
                            </span>
                            {pk.sport && (
                              <span style={{ fontSize:10, fontWeight:700, padding:"1px 7px", borderRadius:20, background:"rgba(255,255,255,0.07)", color:"rgba(255,255,255,0.35)" }}>
                                {pk.sport}
                              </span>
                            )}
                          </div>
                          {/* Status badge */}
                          <div style={{
                            fontSize:10, fontWeight:700, padding:"3px 9px", borderRadius:20,
                            background: statusColor === "#4ade80" ? "rgba(22,163,74,0.15)"
                              : statusColor === "#f87171" ? "rgba(220,38,38,0.15)"
                              : "rgba(217,119,6,0.15)",
                            color: statusColor,
                            display:"flex", alignItems:"center", gap:4,
                          }}>
                            {pk.status === "pending" && (
                              <span style={{ width:5, height:5, borderRadius:"50%", background:T.coral, display:"inline-block", animation:"pulse 1.2s ease infinite" }}/>
                            )}
                            {statusLabel}
                          </div>
                        </div>
                        {/* Pick content */}
                        <div style={{ padding:"10px 12px 12px" }}>
                          {/* Game matchup */}
                          {pk.gameLabel && (
                            <div style={{ fontSize:11, color:"rgba(255,255,255,0.35)", marginBottom:5, fontWeight:600 }}>
                              {pk.gameLabel}
                            </div>
                          )}
                          {/* Pick label */}
                          <div style={{ fontSize:15, fontWeight:700, color:"#fff", lineHeight:1.3, marginBottom:6, letterSpacing:"-0.2px" }}>
                            {pk.label || pk.gameLabel}
                          </div>
                          {/* Bottom row: pick type + odds + wager */}
                          <div style={{ display:"flex", alignItems:"center", gap:6, flexWrap:"wrap" }}>
                            {pk.type && (
                              <span style={{ fontSize:10, fontWeight:700, padding:"2px 7px", borderRadius:20, background:"rgba(255,255,255,0.07)", color:"rgba(255,255,255,0.4)", textTransform:"uppercase" }}>
                                {pk.type}
                              </span>
                            )}
                            {pk.odds != null && (
                              <span style={{ fontSize:10, fontWeight:700, padding:"2px 7px", borderRadius:20, background:"rgba(255,107,61,0.12)", color:T.coral }}>
                                {pk.odds > 0 ? `+${pk.odds}` : pk.odds}
                              </span>
                            )}
                            {pk.wager > 0 && (
                              <span style={{ fontSize:10, color:"rgba(255,255,255,0.42)", fontWeight:600, marginLeft:"auto" }}>
                                {pk.wager.toLocaleString()} pts at risk
                              </span>
                            )}
                          </div>
                          {/* Date submitted */}
                          {(pk.submittedAt || pk.createdAt) && (() => {
                            const ts = pk.submittedAt instanceof Date ? pk.submittedAt
                              : pk.submittedAt?.toDate?.() ?? (pk.submittedAt?.seconds ? new Date(pk.submittedAt.seconds*1000) : null)
                              ?? pk.createdAt?.toDate?.() ?? (pk.createdAt?.seconds ? new Date(pk.createdAt.seconds*1000) : null);
                            if (!ts) return null;
                            return (
                              <div style={{ fontSize:10, color:"rgba(255,255,255,0.36)", fontWeight:500, marginTop:4 }}>
                                {timeAgo(ts)}
                              </div>
                            );
                          })()}
                        </div>
                      </div>
                    );
                    })}
                  </div>
              </div>
                )}

              </div>
        )} {/* end picks tab */}

        {/* == SOCIAL TAB ===================================================== */}
        {roomTab === "den" && (<>

        {/* -- NFL SEASON BANNER — only for NFL-template leagues --------------- */}
        {isNflSeasonLeague(league) && (
          <div style={{ margin:"14px 14px 0" }}>
            <NflSeasonBanner />
          </div>
        )}

        {/* -- LEAGUE PULSE — only render when there's something to show -------- */}
        {(() => {
          const mp = memberPicks[activeLeague] || [];
          const todayStr = new Date().toLocaleDateString("en-CA");
          const todayPicks = mp.filter(p => {
            const ts = p.submittedAt?.toDate?.() ?? (p.submittedAt?.seconds ? new Date(p.submittedAt.seconds * 1000) : null);
            return ts && ts.toLocaleDateString("en-CA") === todayStr;
          });
          const pendingCount = mp.filter(p => p.status === "pending").length;
          const weekAgo = Date.now() - 7 * 86400000;
          const weekWins = mp.filter(p => {
            if (p.status !== "won") return false;
            const ts = tsToMillis(p.submittedAt) ?? 0;
            return ts > weekAgo;
          }).length;

          // If nothing is happening, don't render the row at all
          if (todayPicks.length === 0 && pendingCount === 0 && weekWins === 0) return null;

          const parts = [];
          if (todayPicks.length > 0) parts.push(<span key="t"><span style={{ color:"#fff", fontWeight:600 }}>{todayPicks.length}</span> <span style={{ color:"rgba(255,255,255,0.40)" }}>today</span></span>);
          if (pendingCount > 0) parts.push(<span key="l"><span style={{ color:"#FCD34D", fontWeight:600 }}>{pendingCount}</span> <span style={{ color:"rgba(255,255,255,0.40)" }}>live</span></span>);
          if (weekWins > 0) parts.push(<span key="w"><span style={{ color:"#4ade80", fontWeight:600 }}>{weekWins}</span> <span style={{ color:"rgba(255,255,255,0.40)" }}>wins this week</span></span>);

          return (
            <div style={{ margin:"14px 18px 0", display:"flex", alignItems:"center", flexWrap:"wrap", gap:"4px 10px", fontSize:13 }}>
              {parts.map((node, i) => (
                <Fragment key={i}>
                  {i > 0 && <span style={{ color:"rgba(255,255,255,0.32)" }}>·</span>}
                  {node}
                </Fragment>
              ))}
            </div>
          );
        })()}

        {/* -- CAPTAIN ANNOUNCEMENT — pinned at top of feed -------------------- */}
        {league.announcement && (
          <div style={{
            margin:"12px 14px 0", padding:"14px 16px", borderRadius:16,
            background:"rgba(255,107,61,0.05)",
            border:"1px solid rgba(255,107,61,0.15)",
            position:"relative",
          }}>
            <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:6 }}>
              <span style={{ fontSize:12, fontWeight:600, color:T.coral }}>From the captain</span>
              {league.isCommissioner && (
                <button onClick={async () => {
                  try {
                    const { doc: fDoc, updateDoc: fUpdate, getFirestore: fGetFS } = await import("firebase/firestore");
                    await fUpdate(fDoc(fGetFS(), "leagues", activeLeague), { announcement: "", announcementAt: "", announcementBy: "" });
                    window.torchToast?.("Announcement cleared", "success");
                    fetchLeagues();
                  } catch {}
                }} style={{
                  marginLeft:"auto", background:"none", border:"none", cursor:"pointer",
                  fontSize:11, color:"rgba(255,255,255,0.35)", fontWeight:500,
                }}>Clear</button>
              )}
            </div>
            <div style={{ fontSize:14, color:"rgba(255,255,255,0.8)", lineHeight:1.5 }}>{league.announcement}</div>
            {league.announcementBy && (
              <div style={{ fontSize:10, color:"rgba(255,255,255,0.40)", marginTop:6 }}>— {league.announcementBy}</div>
            )}
          </div>
        )}

        {/* -- League motto + prize display -------------------- */}
        {(league.motto || league.prize) && !league.announcement && (
          <div style={{
            margin:"10px 14px 0", padding:"10px 14px", borderRadius:12,
            background:"rgba(255,255,255,0.02)", border:"1px solid rgba(255,255,255,0.04)",
            display:"flex", alignItems:"center", gap:10,
          }}>
            {league.motto && (
              <div style={{ flex:1, fontSize:12, color:"rgba(255,255,255,0.35)", fontStyle:"italic" }}>"{league.motto}"</div>
            )}
            {league.prize && (
              <div style={{ fontSize:10, padding:"3px 8px 3px 6px", borderRadius:8, background:"rgba(255,215,0,0.08)", border:"1px solid rgba(255,215,0,0.12)", color:"#FFD700", fontWeight:700, fontFamily:FM, flexShrink:0, display:"inline-flex", alignItems:"center", gap:4 }}>
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6"/>
                  <path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18"/>
                  <path d="M4 22h16"/>
                  <path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22"/>
                  <path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22"/>
                  <path d="M18 2H6v7a6 6 0 0 0 12 0V2Z"/>
                </svg>
                {league.prize}
              </div>
            )}
          </div>
        )}

        {/* -- LEAGUE RULES — pinned info card visible to ALL members ----------
            Surfaces the key rules so members understand the structure without
            digging into Settings. Built from league.startingBalance,
            league.paydayAmount, and league.pickRequirements. Each cell is
            conditional — only renders if that rule actually applies. */}
        {(() => {
          const startBal = league.startingBalance ?? 1000;
          const payday   = league.paydayAmount ?? 0;
          const pr       = league.pickRequirements || {};
          const cadence  = pr.cadence || "none";
          const minPicks = pr.minPicks ?? 0;
          const minWager = pr.minWager ?? 0;
          const penalty  = pr.penalty || {};
          const penaltyType  = penalty.type || "none";
          const penaltyValue = penalty.value ?? 0;

          const cells = [];
          cells.push({ label: "Starting", value: Number(startBal).toLocaleString() });
          if (payday > 0) {
            cells.push({ label: "Payday", value: `+${Number(payday).toLocaleString()}/wk`, accent: "#4ade80" });
          }
          if (cadence !== "none" && minPicks > 0) {
            const cadenceLabel = cadence === "daily" ? "day" : cadence === "weekly" ? "wk" : cadence;
            cells.push({ label: "Required", value: `${minPicks}/${cadenceLabel}` });
          }

          // ── Penalty cell ── render whenever picks are required OR a penalty
          // type is explicitly set. For "minWager" type, surface the actual
          // minWager amount so the user sees the real deduction (e.g., -100)
          // instead of the vague text "Min wager". Falls back to minWager for
          // legacy leagues that have a cadence but no explicit penalty object.
          // A penalty only exists relative to a requirement: the enforcement
          // CF skips cadence:"none" leagues before its penalty logic runs
          // (functions/index.js ~L315), so a no-requirement league can never
          // be charged. Rendering the cell off penaltyType alone showed
          // "-1 If missed" on every default-created league (the wizard stamps
          // penalty {type:"minWager"} + minWager:1 even with cadence "none") —
          // user-reported display bug, Aug 29.
          const hasCadence = cadence !== "none";
          if (hasCadence) {
            let penaltyDisplay = null;
            if (penaltyType === "fixed" && penaltyValue > 0) {
              penaltyDisplay = `-${Number(penaltyValue).toLocaleString()}`;
            } else if (penaltyType === "percent" && penaltyValue > 0) {
              penaltyDisplay = `-${penaltyValue}%`;
            } else if (penaltyType === "minWager" && minWager > 0) {
              penaltyDisplay = `-${Number(minWager).toLocaleString()}`;
            } else if (penaltyType === "brutal") {
              penaltyDisplay = "Forfeit all";
            } else if (hasCadence && minWager > 0) {
              // Legacy fallback: cadence set but no penalty object — most CFs
              // default to deducting minWager per miss in this case.
              penaltyDisplay = `-${Number(minWager).toLocaleString()}`;
            }
            // Only push the cell if we resolved a value — avoids "—" placeholders.
            if (penaltyDisplay) {
              const cadenceNoun = cadence === "daily" ? "/day" : cadence === "weekly" ? "/wk" : "";
              const labelText = cadenceNoun ? `Miss${cadenceNoun}` : "If missed";
              cells.push({ label: labelText, value: penaltyDisplay, accent: "#FF6B6B" });
            }
          }

          // Only render if there's more than just "Starting" — otherwise the card
          // is just one cell of trivia and adds noise.
          if (cells.length < 2) return null;

          return (
            <div style={{
              margin:"10px 14px 0", borderRadius:12,
              background:"rgba(255,255,255,0.02)", border:"1px solid rgba(255,255,255,0.05)",
              overflow:"hidden",
            }}>
              <div style={{
                padding:"8px 14px 6px",
                fontSize:9.5, fontWeight:700, letterSpacing:"0.12em",
                color:"rgba(255,255,255,0.42)", fontFamily:FM,
                textTransform:"uppercase",
                borderBottom:"1px solid rgba(255,255,255,0.04)",
              }}>
                League Rules
              </div>
              <div style={{ display:"flex", alignItems:"stretch" }}>
                {cells.map((c, i) => (
                  <Fragment key={c.label}>
                    {i > 0 && <div style={{ width:1, background:"rgba(255,255,255,0.05)" }}/>}
                    <div style={{ flex:1, padding:"10px 6px", textAlign:"center" }}>
                      <div style={{
                        fontFamily:FM, fontSize:14, fontWeight:700,
                        color: c.accent || "rgba(255,255,255,0.92)",
                        letterSpacing:"-0.01em", fontVariantNumeric:"tabular-nums",
                        lineHeight:1.1,
                      }}>{c.value}</div>
                      <div style={{
                        fontSize:9, fontWeight:700, letterSpacing:"0.10em",
                        textTransform:"uppercase", color:"rgba(255,255,255,0.42)",
                        marginTop:3, fontFamily:FM,
                      }}>{c.label}</div>
                    </div>
                  </Fragment>
                ))}
              </div>
            </div>
          );
        })()}

        {/* -- DARK GLASS COMPOSER — sticky above bottom nav (snaps to bottom:0 when keyboard open via .keyboard-open CSS) -------------------- */}
        {!leagueEnded && <div className="den-composer" style={{
          position:"fixed", bottom:"calc(58px + env(safe-area-inset-bottom, 0px))",
          left:"50%", transform:"translateX(-50%)",
          zIndex:30, width:"100%", maxWidth:640,
          background:"rgba(8,10,16,0.95)", backdropFilter:"blur(16px)",
          borderTop:"1px solid rgba(255,255,255,0.06)",
          padding:"6px 10px 6px",
          transition:"bottom 0.18s ease, padding-bottom 0.18s ease",
        }}>

          {/* Quick reactions — collapsed by default. Tap the + in the input row to expand. */}
          {showReactions && (
            <div style={{ display:"flex", gap:6, marginBottom:6, overflowX:"auto", WebkitOverflowScrolling:"touch", scrollbarWidth:"none", paddingBottom:1 }}>
              {["GG","RIP","called it","trash","let's go","respect","lock it in","smh"].map(text => (
                <button key={text} onClick={() => {
                  setTrashDraft(prev => ({ ...prev, [activeLeague]: text }));
                  setShowReactions(false);
                }} style={{
                  flexShrink:0, padding:"5px 12px", borderRadius:14,
                  border:"1px solid rgba(255,255,255,0.06)", background:"rgba(255,255,255,0.03)",
                  color:"rgba(255,255,255,0.55)", fontSize:13, fontWeight:500, cursor:"pointer",
                  transition:"all 0.15s", whiteSpace:"nowrap",
                }}>
                  {text}
                </button>
              ))}
            </div>
          )}

          {/* Input row */}
          <div style={{ display:"flex", gap:6, alignItems:"flex-end" }}>
            <button onClick={() => setShowReactions(v => !v)}
              aria-label={showReactions ? "Hide quick reactions" : "Show quick reactions"}
              style={{
                width:34, height:34, borderRadius:"50%", flexShrink:0,
                border:"1px solid rgba(255,255,255,0.08)",
                background: showReactions ? "rgba(255,107,61,0.10)" : "rgba(255,255,255,0.04)",
                color: showReactions ? T.coral : "rgba(255,255,255,0.55)",
                cursor:"pointer", fontSize:18, fontWeight:300, lineHeight:1,
                display:"flex", alignItems:"center", justifyContent:"center",
                transition:"all 0.15s", transform: showReactions ? "rotate(45deg)" : "rotate(0)",
              }}>+</button>
            <div style={{
              flex:1, display:"flex", gap:4, alignItems:"flex-end",
              background:"rgba(255,255,255,0.05)", border:"1px solid rgba(255,255,255,0.08)",
              borderRadius:22, padding:"6px 10px 6px 16px",
            }}>
              <textarea
                value={trashDraft[activeLeague] || ""}
                onChange={e => {
                  setTrashDraft(prev => ({ ...prev, [activeLeague]: e.target.value }));
                  // Auto-grow up to ~4 lines (96px). Reset to 'auto' first so it can shrink too.
                  const ta = e.target;
                  ta.style.height = "auto";
                  ta.style.height = Math.min(ta.scrollHeight, 96) + "px";
                }}
                onKeyDown={e => {
                  // Enter sends; Shift+Enter inserts newline (iMessage parity)
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    postTrashTalk(activeLeague, trashDraft[activeLeague]);
                    // Reset textarea height after send
                    e.target.style.height = "auto";
                  }
                }}
                onFocus={() => {
                  // Scroll page to bottom so newest message stays visible above the input.
                  // Slight delay lets the keyboard finish animating in.
                  setTimeout(() => {
                    window.scrollTo({ top: document.documentElement.scrollHeight, behavior: "smooth" });
                  }, 250);
                }}
                placeholder="Talk smack…"
                maxLength={280}
                rows={1}
                inputMode="text"
                enterKeyHint="send"
                autoCapitalize="sentences"
                autoCorrect="on"
                spellCheck="true"
                style={{
                  flex:1, background:"none", border:"none", outline:"none",
                  fontSize:15, color:"rgba(255,255,255,0.85)", fontFamily:"inherit",
                  resize:"none", lineHeight:1.4, paddingTop:6, paddingBottom:6,
                  minHeight:22, maxHeight:96, overflowY:"auto",
                }}
              />
              {(trashDraft[activeLeague]?.length || 0) > 200 && (
                <span style={{ fontSize:10, color: (trashDraft[activeLeague]?.length || 0) > 260 ? "#FF3B30" : "rgba(255,255,255,0.20)", fontWeight:600, flexShrink:0, marginRight:4, alignSelf:"center" }}>{280 - (trashDraft[activeLeague]?.length || 0)}</span>
              )}
              <button
                onClick={() => {
                  postTrashTalk(activeLeague, trashDraft[activeLeague]);
                  // Reset textarea height after send
                  const ta = document.activeElement?.tagName === "TEXTAREA" ? document.activeElement : null;
                  if (ta) ta.style.height = "auto";
                }}
                disabled={!trashDraft[activeLeague]?.trim() || trashPosting}
                style={{
                  width:36, height:36, minWidth:44, minHeight:44, borderRadius:"50%", border:"none", cursor:"pointer",
                  background: trashDraft[activeLeague]?.trim() ? T.coral : "rgba(255,255,255,0.06)",
                  color:"#fff", flexShrink:0, transition:"all 0.2s",
                  display:"flex", alignItems:"center", justifyContent:"center",
                  opacity: trashPosting ? 0.5 : 1,
                  boxShadow: trashDraft[activeLeague]?.trim() ? "0 3px 12px rgba(255,107,61,0.3)" : "none",
                  alignSelf:"flex-end",
                }}
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
              </button>
            </div>
          </div>
        </div>}

        {/* -- SOCIAL FEED — dark hi-tech ---------------------------------------- */}
        <div ref={chatScrollRef} className="den-feed" style={{
          background:T.bg,
          paddingTop:12, paddingBottom: leagueEnded ? 100 : 220,
          minHeight:400,
          position:"relative",
        }}>
          {/* Grid pattern bg */}
          <div style={{
            position:"absolute", inset:0, opacity:0.02, pointerEvents:"none",
            backgroundImage:"linear-gradient(rgba(255,255,255,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.06) 1px, transparent 1px)",
            backgroundSize:"28px 28px",
          }}/>

          {/* Make a Pick CTA */}
          {!leagueEnded && <div className="den-make-pick" style={{ position:"relative", zIndex:2, padding:"10px 16px 6px", display:"flex", flexDirection:"column", alignItems:"center", gap:6 }}>
            {bracketLocked && (
              <div style={{
                fontSize:10.5, fontWeight:700,
                letterSpacing:"0.04em",
                display:"flex", alignItems:"center", gap:6,
                color:T.teal,
                textAlign:"center",
              }}>
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                <span style={{ textTransform:"uppercase", letterSpacing:"0.08em" }}>Picks Locked In</span>
                {kickoffLabel && (
                  <span style={{ color:"rgba(244,244,246,0.45)", fontWeight:600, letterSpacing:"0.02em", textTransform:"none" }}>· {kickoffLabel}</span>
                )}
              </div>
            )}
            <button onClick={() => goToGamesForLeague(league)} className="no-press" style={{
              display:"flex", alignItems:"center", gap:7,
              padding:"11px 22px", borderRadius:999,
              background: bracketLocked
                ? T.teal
                : T.coral,
              border:"none", color:"#fff", fontSize:13, fontWeight:700, cursor:"pointer",
              boxShadow: bracketLocked
                ? "0 1px 0 rgba(255,255,255,0.20) inset, 0 4px 16px -2px rgba(20,184,166,0.40)"
                : "0 1px 0 rgba(255,255,255,0.20) inset, 0 4px 16px -2px rgba(255,107,61,0.40)",
              letterSpacing:"-0.005em",
              transition:"transform 0.12s",
            }}
              onMouseDown={(e)=>(e.currentTarget.style.transform="scale(0.97)")}
              onMouseUp={(e)=>(e.currentTarget.style.transform="scale(1)")}
              onMouseLeave={(e)=>(e.currentTarget.style.transform="scale(1)")}
              onTouchStart={(e)=>(e.currentTarget.style.transform="scale(0.97)")}
              onTouchEnd={(e)=>(e.currentTarget.style.transform="scale(1)")}
            >
              {bracketLocked
                ? <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z"/><circle cx="12" cy="12" r="3"/></svg>
                : <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="6"/><circle cx="12" cy="12" r="2"/></svg>
              }
              {pickCtaLabel}
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.4" strokeLinecap="round"><polyline points="9 18 15 12 9 6"/></svg>
            </button>
          </div>}

          {/* ── Auto League Milestones ── */}
          {(() => {
            const ms = computeLeagueMilestones(memberPicks[activeLeague] || [], st, league?.name || "", league?.scoringMode || "wager");
            if (ms.length === 0) return null;
            return (
              <div style={{ padding:"0 14px", marginBottom:8 }}>
                {ms.map((m, mi) => (
                  <div key={mi} style={{
                    marginBottom:8, padding:"12px 14px", borderRadius:14,
                    background:"rgba(255,255,255,0.02)", border:`1px solid ${m.color}15`,
                    display:"flex", alignItems:"center", gap:12,
                    animation:"fadeUp 0.3s ease both", animationDelay:`${mi * 100}ms`,
                  }}>
                    <div style={{
                      width:36, height:36, borderRadius:10, flexShrink:0,
                      background:`${m.color}12`, border:`1px solid ${m.color}20`,
                      display:"flex", alignItems:"center", justifyContent:"center", fontSize:18,
                    }}>{m.icon}</div>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ fontSize:12, fontWeight:700, color:"#fff" }}>{m.title}</div>
                      <div style={{ fontSize:11, color:"rgba(255,255,255,0.35)", marginTop:1, lineHeight:1.4 }}>{m.sub}</div>
                    </div>
                  </div>
                ))}
              </div>
            );
          })()}

          {/* Empty state */}
          {ev.length === 0 && (
            <div style={{ padding:"40px 20px", textAlign:"center" }}>
              <div style={{
                position:"relative", width:54, height:54, borderRadius:16, margin:"0 auto 12px",
                background:"rgba(255,107,61,0.04)",
                border:"1px solid rgba(255,255,255,0.06)",
                display:"flex", alignItems:"center", justifyContent:"center",
                overflow:"hidden",
              }}>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.55)" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" style={{ position:"relative" }}>
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                </svg>
              </div>
              <div style={{ fontSize:14, fontWeight:700, color:"#fff", marginBottom:5, fontFamily:FD, letterSpacing:"-0.01em" }}>
                {isWcLeague ? "Trash talk before kickoff" : "Start the conversation"}
              </div>
              <div style={{ fontSize:11, color:"rgba(255,255,255,0.45)", lineHeight:1.5, marginBottom:0, maxWidth:240, margin:"0 auto" }}>
                {isWcLeague
                  ? "Argue your picks. Roast theirs."
                  : "Talk trash, share picks, make fun of your friends."}
              </div>
              {/* Empty-state secondary CTA — split by league type:
                    WC leagues  → "Invite Friends" (primary CTA already exists at top of feed; redundant pick CTA was confusing users — see image 3 audit)
                    Regular     → "Make a Pick" (kept as-is, this is the historical behavior)
                  This eliminates the duplicate "Continue Picking" buttons that
                  appeared back-to-back in bracket-pool league rooms. */}
              {isWcLeague ? (
                <button onClick={() => { try { window.navigator.vibrate?.(8); } catch {} setShowInvite(league); }} className="no-press" style={{
                  marginTop:16, padding:"10px 22px",
                  background:"transparent",
                  border:`1px solid ${T.borderHi}`,
                  borderRadius:12, color:T.ink, fontSize:12, fontWeight:700, cursor:"pointer",
                  letterSpacing:"-0.005em",
                  display:"inline-flex", alignItems:"center", gap:7,
                  transition:"transform 0.12s, background 0.15s",
                }}
                  onMouseDown={(e)=>(e.currentTarget.style.transform="scale(0.97)")}
                  onMouseUp={(e)=>(e.currentTarget.style.transform="scale(1)")}
                  onMouseLeave={(e)=>(e.currentTarget.style.transform="scale(1)")}
                  onTouchStart={(e)=>(e.currentTarget.style.transform="scale(0.97)")}
                  onTouchEnd={(e)=>(e.currentTarget.style.transform="scale(1)")}
                >
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/>
                    <circle cx="9" cy="7" r="4"/>
                    <line x1="19" y1="8" x2="19" y2="14"/>
                    <line x1="22" y1="11" x2="16" y2="11"/>
                  </svg>
                  Invite Friends
                </button>
              ) : (
              <button onClick={() => goToGamesForLeague(league)} className="no-press" style={{
                marginTop:16, padding:"10px 22px",
                background: bracketLocked
                  ? T.teal
                  : T.coral,
                border:"none", borderRadius:12, color:"#fff", fontSize:12, fontWeight:700, cursor:"pointer",
                boxShadow: bracketLocked
                  ? "0 1px 0 rgba(255,255,255,0.20) inset, 0 4px 12px -2px rgba(20,184,166,0.40)"
                  : "0 1px 0 rgba(255,255,255,0.20) inset, 0 4px 12px -2px rgba(255,107,61,0.40)",
                letterSpacing:"-0.005em",
                transition:"transform 0.12s",
              }}
                onMouseDown={(e)=>(e.currentTarget.style.transform="scale(0.97)")}
                onMouseUp={(e)=>(e.currentTarget.style.transform="scale(1)")}
                onMouseLeave={(e)=>(e.currentTarget.style.transform="scale(1)")}
                onTouchStart={(e)=>(e.currentTarget.style.transform="scale(0.97)")}
                onTouchEnd={(e)=>(e.currentTarget.style.transform="scale(1)")}
              >{pickCtaLabel}</button>
              )}
            </div>
          )}

          {/* Feed items */}
          {ev.map((e, i) => {
            const isTaunt = e.type === "taunt";
            const isWin   = e.type === "won";
            const isLoss  = e.type === "lost";
            const isMe    = e.userId === user?.uid || e.username === (user?.displayName || user?.email?.split("@")[0]);
            const rx      = reactions[`${activeLeague}_${e.id}`] || {};
            const ago = timeAgo(e.createdAt);
            const ts = e.createdAt?.toDate ? e.createdAt.toDate() : e.createdAt?.seconds ? new Date(e.createdAt.seconds*1000) : new Date();
            const timeStr = ts.toLocaleTimeString("en-US",{hour:"numeric",minute:"2-digit"});
            const prevE = ev[i-1];
            const prevTs = prevE?.createdAt?.toDate ? prevE.createdAt.toDate() : prevE?.createdAt?.seconds ? new Date(prevE.createdAt.seconds*1000) : null;
            const showDateSep = i === 0 || (prevTs && ts.toDateString() !== prevTs.toDateString());
            const dateSepLabel = (() => {
              const now2 = new Date();
              if (ts.toDateString() === now2.toDateString()) return "Today";
              const y = new Date(now2); y.setDate(y.getDate()-1);
              if (ts.toDateString() === y.toDateString()) return "Yesterday";
              return ts.toLocaleDateString("en-US",{weekday:"short", month:"short", day:"numeric"});
            })();
            const avatarColors = ["#FF6B6B","#4ECDC4","#45B7D1","#96CEB4","#FFEAA7","#DDA0DD","#98D8C8","#F7DC6F","#BB8FCE","#85C1E9"];
            const nameHash = (e.username || "?").split("").reduce((a, c) => a + c.charCodeAt(0), 0);
            const avatarColor = avatarColors[nameHash % avatarColors.length];

            // Shared date separator
            const DateSep = showDateSep ? (
              <div style={{ display:"flex", alignItems:"center", gap:10, margin:"18px 16px 12px" }}>
                <div style={{ flex:1, height:"1px", background:"linear-gradient(90deg, transparent, rgba(255,255,255,0.08), rgba(255,255,255,0.03))" }}/>
                <span style={{ fontSize:9, fontWeight:700, color:"rgba(255,255,255,0.32)", letterSpacing:"0.14em", padding:"3px 12px", background:"rgba(255,255,255,0.03)", borderRadius:10, border:"1px solid rgba(255,255,255,0.04)", fontFamily:FM }}>{dateSepLabel}</span>
                <div style={{ flex:1, height:"1px", background:"linear-gradient(90deg, rgba(255,255,255,0.03), rgba(255,255,255,0.08), transparent)" }}/>
              </div>
            ) : null;

            // Shared reaction row
            const ReactionRow = e.id ? (
              <div style={{ padding:"5px 12px 7px", display:"flex", gap:3, alignItems:"center" }}>
                {[["🔥","fire"],["💀","skull"],["😂","lol"],["💸","money"],["🫡","salute"]].map(([em, key]) => {
                  const c = (rx[key]||[]).length; const m = (rx[key]||[]).includes(user?.uid);
                  return (<button key={key} onClick={() => handleReaction(activeLeague, e.id, key)} style={{
                    padding:"3px 8px", borderRadius:20, border: m ? "1px solid rgba(255,107,61,0.20)" : "1px solid rgba(255,255,255,0.04)",
                    cursor:"pointer", background: m ? "rgba(255,107,61,0.10)" : "rgba(255,255,255,0.03)", fontSize:13,
                    display:"flex", alignItems:"center", gap:3, transition:"all 0.15s",
                  }}>{em}{c>0 && <span style={{ fontSize:10, fontWeight:700, color: m ? T.coral : "rgba(255,255,255,0.25)", fontFamily:FM }}>{c}</span>}</button>);
                })}
                {/* Flag / Report button — Apple Guideline 1.2 */}
                {!isMe && <button onClick={() => setReportModal({ contentId: e.id, contentText: e.label || e.text || "", reportedUserId: e.userId, reportedUsername: e.username, leagueId: activeLeague })} style={{
                  padding:"4px 10px", borderRadius:20, border:"1px solid rgba(251,191,36,0.35)",
                  cursor:"pointer", background:"rgba(251,191,36,0.12)", fontSize:11,
                  display:"flex", alignItems:"center", gap:4, marginLeft:"auto",
                  color:"#E5B459", fontWeight:700, letterSpacing:0.2,
                }} title="Report content">
                  ⚠ Report
                </button>}
              </div>
            ) : null;

            // ── TORCH REQUEST / PASSED CARD ──
            if (e.type === "torch_request" || e.type === "torch_passed") {
              const isTorchRequest = e.type === "torch_request";
              const isLender = e.torchLenderUid === user?.uid;
              const isPending = e.torchStatus === "pending";
              return (
                <Fragment key={e.id||i}>
                  {DateSep}
                  <div style={{
                    margin:"6px 12px 8px", borderRadius:18, overflow:"hidden",
                    background:"rgba(255,255,255,0.03)", border:"1px solid rgba(255,107,61,0.12)",
                    boxShadow:"0 2px 12px rgba(0,0,0,0.3), 0 0 8px rgba(255,107,61,0.04)",
                    animation:"fadeUp 0.2s ease both",
                  }}>
                    <div style={{ height:2, background:`linear-gradient(90deg, transparent, ${T.coral}, transparent)` }}/>
                    <div style={{ padding:"14px 16px" }}>
                      <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:10 }}>
                        <div style={{
                          width:34, height:34, borderRadius:12,
                          background:T.coral,
                          display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0,
                          boxShadow:"0 3px 12px rgba(255,107,61,0.2)",
                        }}>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="1.8"><path d="M12 22c-4.97 0-9-2.69-9-6v-.18C4.32 12.47 7.6 10 8 6c.02-.17.17-.3.34-.28C10.09 5.96 12 8 12 8s1.91-2.04 3.66-2.28c.17-.02.32.11.34.28.4 4 3.68 6.47 5 9.82V16c0 3.31-4.03 6-9 6z"/></svg>
                        </div>
                        <div style={{ flex:1 }}>
                          <div style={{ fontSize:13, fontWeight:700, color:"#fff" }}>{isTorchRequest ? "Pass the Torch" : "Torch Passed!"}</div>
                          <div style={{ fontSize:11, color:"rgba(255,255,255,0.40)" }}>{timeStr}</div>
                        </div>
                        {isPending && <span style={{ fontSize:9, fontWeight:700, padding:"3px 8px", borderRadius:6, background:"rgba(255,149,0,0.12)", border:"1px solid rgba(255,149,0,0.20)", color:"#FF9500", animation:"pulse 2s ease infinite", fontFamily:FM }}>PENDING</span>}
                        {!isPending && <span style={{ fontSize:9, fontWeight:700, padding:"3px 8px", borderRadius:6, background:"rgba(52,199,89,0.12)", border:"1px solid rgba(52,199,89,0.20)", color:"#34C759", fontFamily:FM }}>ACCEPTED</span>}
                      </div>
                      <div style={{ padding:"10px 12px", borderRadius:12, background:"rgba(255,255,255,0.03)", border:"1px solid rgba(255,255,255,0.06)", marginBottom:8 }}>
                        <div style={{ fontSize:13, fontWeight:600, color:"rgba(255,255,255,0.80)", lineHeight:1.5 }}>
                          {e.torchBorrowerName || e.username} wants {e.torchLenderName} to fund a <span style={{ fontWeight:800, color:T.coral, fontFamily:FM }}>{e.torchAmount || "?"} pt</span> pick
                        </div>
                        {e.torchPickLabel && <div style={{ fontSize:12, color:"rgba(255,255,255,0.42)", marginTop:3 }}>Pick: {e.torchPickLabel}</div>}
                        {e.torchGameLabel && <div style={{ fontSize:11, color:"rgba(255,255,255,0.36)", marginTop:2 }}>{e.torchGameLabel}</div>}
                      </div>
                      {isLender && isPending && e.torchLoanId && (
                        <div style={{ display:"flex", gap:8, marginBottom:6 }}>
                          <button disabled={torchAccepting} onClick={async () => {
                            setTorchAccepting(true);
                            try {
                              await respondToTorchLoan(e.torchLoanId, user.uid, true);
                              try { await writeLeagueActivity(user.uid, [activeLeague], { type:"torch_passed", userId:user.uid, username:user.displayName||user.email?.split("@")[0], label:`accepted the torch for ${e.torchBorrowerName} — ${e.torchAmount} pts`, torchLoanId:e.torchLoanId, torchAmount:e.torchAmount, torchLenderName:e.torchLenderName, torchBorrowerName:e.torchBorrowerName, torchStatus:"active" }); } catch{}
                              setTorchInbox(prev => prev.filter(l => l.id !== e.torchLoanId));
                              window.torchToast?.("Funded. Pick is in.", "success");
                              const fs = await loadLeagueStandings(activeLeague, modeFor(activeLeague)).catch(()=>[]);
                              setStandings(prev => ({ ...prev, [activeLeague]: fs }));
                            } catch(err) { window.torchToast?.(err.message, "error"); }
                            finally { setTorchAccepting(false); }
                          }} style={{
                            flex:1, padding:"11px", borderRadius:12, border:"none", cursor:"pointer",
                            background:T.coral,
                            color:"#fff", fontSize:13, fontWeight:700,
                            boxShadow:"0 4px 16px rgba(255,107,61,0.2)",
                            opacity:torchAccepting?0.6:1,
                          }}>{torchAccepting ? "Funding…" : "Fund it"}</button>
                          <button onClick={async () => { try { await respondToTorchLoan(e.torchLoanId, user.uid, false); setTorchInbox(prev => prev.filter(l => l.id !== e.torchLoanId)); window.torchToast?.("Declined", "info"); } catch(e2) { window.torchToast?.(e2.message, "error"); } }} style={{
                            padding:"11px 16px", borderRadius:12, cursor:"pointer",
                            background:"rgba(255,255,255,0.04)", border:"1px solid rgba(255,255,255,0.08)",
                            color:"rgba(255,255,255,0.50)", fontSize:13, fontWeight:600,
                          }}>Decline</button>
                        </div>
                      )}
                      <div style={{ fontSize:11, color:"rgba(255,255,255,0.36)", padding:"6px 8px", borderRadius:8, background:"rgba(255,255,255,0.02)", lineHeight:1.5 }}>
                        Win → both get the full payout. Lose → {e.torchLenderName} eats {e.torchAmount||"?"} pts.
                      </div>
                    </div>
                    {ReactionRow}
                  </div>
                </Fragment>
              );
            }

            // ── PAYDAY CARD ──
            if (e.type === "announcement") {
              return (
                <div key={e.id||i} style={{
                  padding:"14px 16px", borderRadius:16, marginBottom:6,
                  background:"rgba(255,107,61,0.05)",
                  border:"1px solid rgba(255,107,61,0.12)",
                  animation:"fadeUp 0.3s ease",
                }}>
                  <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:6 }}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={T.coral} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M3 11l18-5v12L3 13"/>
                      <path d="M11.6 16.8a3 3 0 1 1-5.8-1.6"/>
                    </svg>
                    <span style={{ fontSize:10, fontWeight:800, color:T.coral, fontFamily:FM, letterSpacing:"0.06em" }}>ANNOUNCEMENT</span>
                    <span style={{ fontSize:10, color:"rgba(255,255,255,0.36)", marginLeft:"auto" }}>{timeAgo(e.createdAt)}</span>
                  </div>
                  <div style={{ fontSize:13, color:"rgba(255,255,255,0.75)", lineHeight:1.5 }}>{e.text}</div>
                  <div style={{ fontSize:10, color:"rgba(255,255,255,0.40)", marginTop:4 }}>— {e.username}</div>
                </div>
              );
            }
            if (e.type === "payday") {
              return (
                <Fragment key={e.id||i}>
                  {DateSep}
                  <div style={{
                    margin:"6px 12px 8px", padding:"14px 16px", borderRadius:16,
                    background:"rgba(251,191,36,0.05)",
                    border:"1px solid rgba(251,191,36,0.12)",
                    display:"flex", alignItems:"center", gap:12,
                    boxShadow:"0 0 16px rgba(251,191,36,0.04)",
                  }}>
                    <div style={{
                      width:38, height:38, borderRadius:12, flexShrink:0,
                      background:"rgba(252,211,77,0.12)",
                      border:"1px solid rgba(251,191,36,0.15)",
                      display:"flex", alignItems:"center", justifyContent:"center", fontSize:18,
                    }}>💰</div>
                    <div style={{ flex:1 }}>
                      <div style={{ fontSize:14, fontWeight:800, color:"#E5B459", fontFamily:FM }}>
                        +{e.points || e.wager || 200} pts
                      </div>
                      <div style={{ fontSize:11, color:"rgba(255,255,255,0.42)", marginTop:2 }}>
                        {e.username || "Everyone"} received payday · {ago}
                      </div>
                    </div>
                  </div>
                </Fragment>
              );
            }

            // ── SEASON END CARD ──
            if (e.type === "season_end") {
              return (
                <Fragment key={e.id||i}>
                  {DateSep}
                  <div style={{
                    margin:"6px 12px 8px", padding:"16px", borderRadius:16,
                    background:"rgba(255,215,0,0.06)",
                    border:"1px solid rgba(255,215,0,0.18)",
                    textAlign:"center",
                    boxShadow:"0 0 20px rgba(255,215,0,0.06)",
                  }}>
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#FFD700" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom:6, display:"inline-block" }}>
                      <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6"/>
                      <path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18"/>
                      <path d="M4 22h16"/>
                      <path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22"/>
                      <path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22"/>
                      <path d="M18 2H6v7a6 6 0 0 0 12 0V2Z"/>
                    </svg>
                    <div style={{ fontSize:14, fontWeight:800, color:"#FFD700", fontFamily:FD, letterSpacing:"-0.2px" }}>
                      Season Complete
                    </div>
                    <div style={{ fontSize:12, color:"rgba(255,255,255,0.50)", marginTop:4, lineHeight:1.5 }}>
                      {e.label || "The season has ended!"}
                    </div>
                    <div style={{ fontSize:10, color:"rgba(255,255,255,0.32)", marginTop:6 }}>{ago}</div>
                  </div>
                </Fragment>
              );
            }

            // ── SEASON START CARD ──
            if (e.type === "season_start") {
              return (
                <Fragment key={e.id||i}>
                  {DateSep}
                  <div style={{
                    margin:"6px 12px 8px", padding:"16px", borderRadius:16,
                    background:"rgba(255,107,61,0.06)",
                    border:"1px solid rgba(255,107,61,0.18)",
                    textAlign:"center",
                    boxShadow:"0 0 20px rgba(255,107,61,0.06)",
                  }}>
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={T.coral} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom:6, display:"inline-block" }}>
                      <path d="M12 2C12 2 5 9 5 14a7 7 0 0014 0c0-5-7-12-7-12z"/>
                    </svg>
                    <div style={{ fontSize:14, fontWeight:800, color:T.coral, fontFamily:FD, letterSpacing:"-0.2px" }}>
                      New Season
                    </div>
                    <div style={{ fontSize:12, color:"rgba(255,255,255,0.50)", marginTop:4, lineHeight:1.5 }}>
                      {e.label || "A new season has started!"}
                    </div>
                    <div style={{ fontSize:10, color:"rgba(255,255,255,0.32)", marginTop:6 }}>{ago}</div>
                  </div>
                </Fragment>
              );
            }

            // ── CHAT BUBBLE ──
            if (isTaunt) {
              return (
                <Fragment key={e.id||i}>
                  {DateSep}
                  <div style={{
                    margin:"2px 14px 2px",
                    display:"flex", alignItems:"flex-end", gap:6,
                    flexDirection: isMe ? "row-reverse" : "row",
                    animation:"fadeUp 0.15s ease both",
                  }}>
                    {!isMe && (
                      <div onClick={() => setViewingMember({ uid: e.userId, username: e.username })} style={{
                        width:32, height:32, borderRadius:"50%", flexShrink:0, marginBottom:12, cursor:"pointer",
                        background: avatarColor,
                        display:"flex", alignItems:"center", justifyContent:"center",
                        fontSize:11, fontWeight:700, color:"#fff",
                      }}>{(e.username?.[0]||"?").toUpperCase()}</div>
                    )}
                    <div style={{ maxWidth:"78%", minWidth:40 }}>
                      {!isMe && (
                        <div onClick={() => setViewingMember({ uid: e.userId, username: e.username })} style={{
                          fontSize:10.5, fontWeight:600, color: avatarColor, marginBottom:2, marginLeft:10, cursor:"pointer",
                        }}>{e.username}</div>
                      )}
                      <div style={{
                        padding: e.imageUrl ? "4px" : "8px 12px",
                        borderRadius: isMe ? "16px 4px 16px 16px" : "4px 16px 16px 16px",
                        background: isMe ? T.coral : "rgba(255,255,255,0.06)",
                        color: isMe ? "#fff" : "rgba(255,255,255,0.85)",
                        fontSize:13, lineHeight:1.45,
                        border: isMe ? "1px solid rgba(255,107,61,0.25)" : "1px solid rgba(255,255,255,0.06)",
                        boxShadow: isMe ? "0 2px 10px rgba(255,107,61,0.12)" : "0 2px 6px rgba(0,0,0,0.18)",
                        overflow:"hidden",
                      }}>
                        {e.imageUrl && (<img src={e.imageUrl} alt="" style={{ width:"100%", maxWidth:280, borderRadius: e.label && e.label !== "Shared a photo" ? "14px 14px 0 0" : 14, display:"block" }}
                          onError={(ev) => { ev.currentTarget.style.display = "none"; }}/>)}
                        {e.label && e.label !== "Shared a photo" && (<div style={{ padding: e.imageUrl ? "8px 10px 6px" : 0 }}>{e.label}</div>)}
                      </div>
                      <div style={{ display:"flex", alignItems:"center", gap:4, marginTop:2, justifyContent: isMe ? "flex-end" : "flex-start", paddingLeft:8, paddingRight:8 }}>
                        <span style={{ fontSize:10, color:"rgba(255,255,255,0.32)" }}>{timeStr}</span>
                        {isMe && (<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.15)" strokeWidth="2.5" strokeLinecap="round"><polyline points="20 6 9 17 4 12"/></svg>)}
                        {/* Flag / Report — Apple Guideline 1.2 */}
                        {!isMe && e.id && <button onClick={() => setReportModal({ contentId: e.id, contentText: e.label || e.text || "", reportedUserId: e.userId, reportedUsername: e.username, leagueId: activeLeague })} style={{
                          padding:"3px 8px", borderRadius:12, border:"1px solid rgba(251,191,36,0.35)",
                          cursor:"pointer", background:"rgba(251,191,36,0.12)", fontSize:11,
                          display:"flex", alignItems:"center", gap:3, marginLeft:6,
                          color:"#E5B459", fontWeight:700, letterSpacing:0.2,
                        }} title="Report">
                          ⚠ Report
                        </button>}
                      </div>
                      {e.id && Object.values(rx).some(arr => arr.length > 0) && (
                        <div style={{ display:"flex", gap:2, marginTop:1, flexWrap:"wrap", justifyContent: isMe ? "flex-end" : "flex-start", paddingLeft:4, paddingRight:4 }}>
                          {[["🔥","fire"],["💀","skull"],["😂","lol"],["💸","money"],["🫡","salute"]].map(([em, key]) => {
                            const c = (rx[key]||[]).length; if (c === 0) return null;
                            const m = (rx[key]||[]).includes(user?.uid);
                            return (<button key={key} onClick={() => handleReaction(activeLeague, e.id, key)} style={{
                              padding:"1px 6px", borderRadius:12, border: m ? "1px solid rgba(255,107,61,0.15)" : "1px solid rgba(255,255,255,0.04)",
                              cursor:"pointer", background: m ? "rgba(255,107,61,0.10)" : "rgba(255,255,255,0.04)", fontSize:12,
                              display:"flex", alignItems:"center", gap:2,
                            }}>{em}<span style={{ fontSize:9, fontWeight:700, color: m ? T.coral : "rgba(255,255,255,0.20)" }}>{c}</span></button>);
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                </Fragment>
              );
            }

            // ── HI-TECH PICK RESULT CARD ──
            // Try to find the actual pick doc to get real-time status
            let freshPick = e.pickId ? mp.find(pk => pk.id === e.pickId) : null;
            // Fallback: match by userId + label if pickId wasn't stored (older events).
            // CRITICAL: Also require gameLabel (or gameId) match — otherwise picks
            // with the same label across different games (e.g., two "+1.5" spread
            // picks on different NHL games) cross-pollinate, and clicking the
            // social card opens a different game's pick detail. Game data was
            // bleeding from one pick into another's detail view before this guard.
            if (!freshPick && e.userId && e.label) {
              freshPick = mp.find(pk =>
                pk.userId === e.userId
                && pk.label === e.label
                && (
                  // Tier 1: gameId match if both sides have it (most reliable)
                  (e.gameId && pk.gameId && pk.gameId === e.gameId)
                  // Tier 2: gameLabel match (e.g., "MONC @ TBL" === "MONC @ TBL")
                  || (e.gameLabel && pk.gameLabel && pk.gameLabel === e.gameLabel)
                  // Tier 3: home+away team full names match
                  || (e.homeTeamFull && pk.homeTeamFull && e.awayTeamFull && pk.awayTeamFull
                      && pk.homeTeamFull === e.homeTeamFull && pk.awayTeamFull === e.awayTeamFull)
                )
              ) || null;
            }

            // Settlement now writes to e.status (new), but old events had e.type overwritten.
            // Priority: freshPick → e.status (new field) → e.type if status value (backward compat) → "pending"
            // "cashed" is included so picks the user cashed-out early stop showing as
            // pending in the league feed. (cashOutPick writes status="cashed" to the
            // pick doc; freshPick will surface that on the next memberPicks load.)
            const STATUS_TYPES = new Set(["won","lost","voided","push","cashed"]);
            const resolvedStatus = freshPick?.status
              || (STATUS_TYPES.has(e.status) ? e.status : null)
              || (STATUS_TYPES.has(e.type) ? e.type : null)
              || e.status
              || "pending";

            const effectiveWin  = resolvedStatus === "won"  || isWin;
            const effectiveLoss = resolvedStatus === "lost" || isLoss;
            const effectiveVoid = resolvedStatus === "voided";
            const effectivePush = resolvedStatus === "push";
            const effectiveCashed = resolvedStatus === "cashed";
            const EIGHT_HOURS = 8 * 60 * 60 * 1000;
            const TWELVE_HOURS = 12 * 60 * 60 * 1000;
            const eTs = e.createdAt?.toDate ? e.createdAt.toDate() : e.createdAt?.seconds ? new Date(e.createdAt.seconds*1000) : null;
            const eCommence = e.commenceTime?.toDate ? e.commenceTime.toDate() : e.commenceTime?._seconds ? new Date(e.commenceTime._seconds*1000) : e.commenceTime?.seconds ? new Date(e.commenceTime.seconds*1000) : e.commenceTime ? new Date(e.commenceTime) : null;
            const ageFromCommence = eCommence ? (Date.now() - eCommence.getTime()) : null;
            const ageFromSubmit   = eTs ? (Date.now() - eTs.getTime()) : null;
            // FIX: gate stale check on game actually starting. Previously a pick placed 12h+ ago
            // for a future game was flagged "Settling…" even though the game hadn't started.
            const gameStarted = eCommence != null && Date.now() >= eCommence.getTime();
            const isPending = !effectiveWin && !effectiveLoss && !effectiveVoid && !effectivePush && !effectiveCashed;
            const isStale = isPending && (
              // Primary: game started 8h+ ago and still pending → settlement is overdue
              (gameStarted && ageFromCommence != null && ageFromCommence > EIGHT_HOURS) ||
              // Fallback for legacy picks missing commenceTime: use submit age
              (eCommence == null && ageFromSubmit != null && ageFromSubmit > TWELVE_HOURS)
            );
            // ── Penalty / system events (missed-pick deductions, etc.) ──
            // These aren't real picks — they're activity-feed events written by the
            // missed-picks Cloud Function. They have no pickId, no game, and the
            // deduction is final the moment it's written. Detect by event type or
            // by the label content ("deducted" / "missed required picks") and treat
            // as "Settled". Match label content WITHOUT requiring isPending so
            // deductions written with a non-pending status still render correctly.
            const labelText = typeof e?.label === "string" ? e.label : "";
            const isPenaltyEvent = (
              e?.type === "missed_picks_penalty" ||
              e?.type === "penalty" ||
              e?.type === "deduction" ||
              /\bdeducted\b/i.test(labelText) ||
              /missed\s+(?:yesterday'?s?|this\s+week'?s?|today'?s?)?\s*required\s+picks?/i.test(labelText)
            );
            // Try to extract the deduction amount from the event for the points pill.
            // Prefer e.points (signed) → e.wager → parse from label "— 100 pts deducted".
            const penaltyPts = isPenaltyEvent
              ? (typeof e?.points === "number" && e.points !== 0 ? Math.abs(e.points)
                : typeof e?.wager === "number" && e.wager > 0 ? e.wager
                : (e?.label?.match(/(\d+)\s*pts?\s*deducted/i)?.[1]) || 0)
              : 0;
            const pendingLabel = isPenaltyEvent ? "Settled" : isStale ? "Settling…" : (gameStarted ? "Live" : "Pending");
            // Cashed-out picks: net delta against the wager. cashOutPick stores the
            // signed profit in `points` (cashoutValue - wager), so we can use the
            // same +/- formatting as a won/lost pick.
            const cashedNet = effectiveCashed ? Number(freshPick?.points ?? e.points ?? 0) : 0;
            const cashedSign = cashedNet >= 0 ? "+" : "−";
            const cashedColor = cashedNet >= 0 ? "#34C759" : "#FF3B30";
            const accentColor  = isPenaltyEvent ? "#FF3B30" : effectiveCashed ? cashedColor : effectiveWin ? "#34C759" : effectiveLoss ? "#FF3B30" : effectiveVoid ? "#6B7280" : effectivePush ? "#6B7280" : T.coral;
            const accentBorder = isPenaltyEvent ? "rgba(255,59,48,0.15)" : effectiveCashed ? "rgba(107,114,128,0.18)" : effectiveWin ? "rgba(52,199,89,0.20)" : effectiveLoss ? "rgba(255,59,48,0.15)" : effectiveVoid ? "rgba(107,114,128,0.12)" : effectivePush ? "rgba(107,114,128,0.12)" : "rgba(255,107,61,0.12)";
            const accentBg     = isPenaltyEvent ? "rgba(255,59,48,0.03)" : effectiveCashed ? "rgba(107,114,128,0.04)" : effectiveWin ? "rgba(52,199,89,0.04)" : effectiveLoss ? "rgba(255,59,48,0.03)" : "rgba(255,255,255,0.02)";
            // Pick'em/survivor: no wager, no payout — the wager-shaped pill
            // renders "+0" on wins and "-0" on losses. Swap in the standings
            // point instead. Pending pick'em events get no pill (nothing is
            // "at risk" when nothing is staked).
            const denNonWager = (league?.scoringMode || "wager") !== "wager";
            const ptsDelta     = isPenaltyEvent ? (penaltyPts > 0 ? `-${Number(penaltyPts).toLocaleString()}` : "Settled") : effectiveCashed ? `${cashedSign}${Math.abs(cashedNet).toLocaleString()} · Cashed` : effectiveWin ? (denNonWager ? "+1" : `+${((freshPick?.points ?? e.points) || 0).toLocaleString()}`) : effectiveLoss ? (denNonWager ? "Missed" : `-${(e.wager||0).toLocaleString()}`) : effectiveVoid ? "Voided" : effectivePush ? "Push" : (!denNonWager && e.wager) ? `${(e.wager||0).toLocaleString()} at risk` : null;
            const winAnim      = effectiveWin ? "winPop 0.4s cubic-bezier(0.34,1.56,0.64,1) both" : "fadeUp 0.2s ease both";

            // Team data for mini scoreboard.
            // We'd love to show team logos but only if we have FULL team names.
            // Legacy events sometimes only have a `gameLabel` like "BUF @ BOS"
            // (short codes). Splitting that gives us "BUF" / "BOS" which match
            // no team in the logo lookup tables -- and worse, fall through to
            // wrong-sport defaults (Arsenal crests showing on hockey games).
            // So: only set awayTeam/homeTeam when we have proper full names.
            //
            // Parlays span multiple games -- there's no single matchup to logo.
            // Detect them via isParlay/type/gameLabel-contains-" · " and skip
            // the logo block entirely; the joined gameLabel renders as text below.
            //
            // Outright/futures picks (Kentucky Derby, golf tournaments, season
            // winners) don't have two opposing teams -- they have ONE contestant
            // entered in an event. Splitting the gameLabel ("Run for the Roses
            // @ 2026 Kentucky Derby") gives us non-team strings that get
            // Arsenal'd by the fuzzy fallback. Detect and skip.
            const isParlayEvent = e.isParlay === true || e.type === "parlay" || (typeof e.gameLabel === "string" && e.gameLabel.includes(" · "));
            const isOutrightEvent =
              e.type === "outright" || e.pickType === "outright" || e.marketType === "outright"
              || (typeof e.sportKey === "string" && e.sportKey.includes("_winner"))
              || NON_TEAM_SPORTS.has(e.sport)
              || (typeof e.label === "string" && /\bto\s+(?:Win|Place|Show|Finish|Top\s+\d)\b/i.test(e.label));
            const looksLikeAbbrev = (s) => !!s && s.length <= 4 && s === s.toUpperCase();
            const awayCandidate = (isParlayEvent || isOutrightEvent) ? null : (e.awayTeamFull || e.gameLabel?.split(" @ ")?.[0] || null);
            const homeCandidate = (isParlayEvent || isOutrightEvent) ? null : (e.homeTeamFull || e.gameLabel?.split(" @ ")?.[1] || null);
            const awayTeam = looksLikeAbbrev(awayCandidate) ? null : awayCandidate;
            const homeTeam = looksLikeAbbrev(homeCandidate) ? null : homeCandidate;

            return (
              <Fragment key={e.id||i}>
                {DateSep}
                <div style={{
                  margin:"5px 12px 7px", overflow:"hidden", borderRadius:18,
                  background:accentBg,
                  border:`1px solid ${accentBorder}`,
                  borderLeft:`3px solid ${accentColor}`,
                  animation: effectiveWin ? "fadeUp 0.2s ease both" : effectiveLoss ? "loseFlash 0.5s ease both" : "fadeUp 0.2s ease both",
                  boxShadow: effectiveWin ? `0 2px 16px rgba(52,199,89,0.15), 0 0 24px rgba(52,199,89,0.08)` : effectiveLoss ? `0 2px 12px rgba(255,59,48,0.06)` : `0 2px 10px rgba(0,0,0,0.20)`,
                  backdropFilter:"blur(20px)",
                  WebkitBackdropFilter:"blur(20px)",
                }}>
                  {/* Top accent line */}
                  {effectiveWin && <div style={{ height:2, background:"linear-gradient(90deg, #34C759, rgba(52,199,89,0))" }}/>}
                  {isPending && !isStale && <div style={{ height:2, background:`linear-gradient(90deg, ${T.coral}, rgba(255,107,61,0))` }}/>}

                  {/* User row — compact, chat-message style */}
                  <div style={{ padding:"8px 14px 4px", display:"flex", alignItems:"center", gap:8 }}>
                    <div onClick={() => setViewingMember({ uid: e.userId, username: e.username })} style={{
                      width:28, height:28, borderRadius:"50%", flexShrink:0, cursor:"pointer",
                      background: isMe ? T.coral : avatarColor,
                      display:"flex", alignItems:"center", justifyContent:"center",
                      fontSize:10, fontWeight:700, color:"#fff",
                      border:"1px solid rgba(255,255,255,0.08)",
                    }}>{(e.username?.[0]||"?").toUpperCase()}</div>
                    <span style={{ fontSize:11.5, fontWeight:600, color: isMe ? T.coral : "rgba(255,255,255,0.55)", cursor:"pointer" }}
                      onClick={() => setViewingMember({ uid: e.userId, username: e.username })}
                    >{isMe ? "You" : e.username}</span>
                    <span style={{ fontSize:10, color:"rgba(255,255,255,0.32)" }}>{timeStr}</span>
                    {e.sport && (() => { const esc = getSportColor(e.sport); return <span style={{ fontSize:9, fontWeight:800, padding:"2px 6px", borderRadius:4, background:esc.dim, border:`1px solid ${esc.border}`, color:esc.accent, marginLeft:"auto", fontFamily:FM, letterSpacing:"0.04em" }}>{e.sport}</span>; })()}
                  </div>

                  {/* Tappable pick content */}
                  <div
                    onClick={async () => {
                      const glParts = (e.gameLabel || "").split(" @ ");
                      const parsedAway = e.awayTeamFull || (glParts.length === 2 ? glParts[0].trim() : null);
                      const parsedHome = e.homeTeamFull || (glParts.length === 2 ? glParts[1].trim() : null);
                      const numOdds = typeof e.odds === "number" ? e.odds : parseInt(e.odds, 10);
                      const wager = e.wager || 0;
                      const calcExpPayout = (w, o) => { if (!w || !o || isNaN(o)) return w; return Math.round(w * (o > 0 ? (o/100)+1 : (100/Math.abs(o))+1)); };
                      const computedExpPay = calcExpPayout(wager, numOdds);
                      const STATUSES = new Set(["won","lost","pending","voided","push","parlay"]);
                      const marketType = e.pickType || e.marketType || (STATUSES.has(e.type) ? null : e.type) || null;

                      // Try cached freshPick first, then live Firestore read for real-time status + full team data
                      let realPick = freshPick;
                      if (!realPick || realPick.status === "pending") {
                        try {
                          const { collection, query, where, orderBy, limit, getDocs, getFirestore, doc, getDoc } = await import("firebase/firestore");
                          const db2 = getFirestore(app);
                          if (e.pickId) {
                            const pickSnap = await getDoc(doc(db2, "picks", e.userId, "userPicks", e.pickId));
                            if (pickSnap.exists()) realPick = { id: pickSnap.id, ...pickSnap.data(), userId: e.userId, username: e.username };
                          }
                          if (!realPick || realPick.status === "pending") {
                            // Label-based fallback. Same cross-pollination risk as the
                            // in-memory mp.find fallback above: two picks with the same
                            // label but different games would mismatch. Filter the query
                            // results to only accept matches that ALSO match the
                            // activity event's gameId / gameLabel / team names.
                            const q = query(collection(db2, "picks", e.userId, "userPicks"), where("label", "==", e.label), orderBy("submittedAt", "desc"), limit(5));
                            const snap = await getDocs(q);
                            if (!snap.empty) {
                              // Find the first result that matches the activity event's
                              // game (not just label). Without this, a more recent pick
                              // on a different game can clobber the correct one.
                              const candidates = snap.docs.map(d => ({ id: d.id, ...d.data(), userId: e.userId, username: e.username }));
                              const matchesGame = (pk) =>
                                (e.gameId && pk.gameId && pk.gameId === e.gameId)
                                || (e.gameLabel && pk.gameLabel && pk.gameLabel === e.gameLabel)
                                || (e.homeTeamFull && pk.homeTeamFull && e.awayTeamFull && pk.awayTeamFull
                                    && pk.homeTeamFull === e.homeTeamFull && pk.awayTeamFull === e.awayTeamFull);
                              // Prefer a settled match first (real-time status), then any pending match
                              const found = candidates.find(c => matchesGame(c) && c.status !== "pending")
                                         || candidates.find(c => matchesGame(c))
                                         || null;
                              if (found && (found.status !== "pending" || !realPick)) realPick = found;
                            }
                          }
                        } catch {}
                      }

                      if (realPick) {
                        setSelectedLeaguePick({
                          ...realPick,
                          status: realPick.status || resolvedStatus,
                          homeTeamFull: realPick.homeTeamFull || parsedHome,
                          awayTeamFull: realPick.awayTeamFull || parsedAway,
                          expectedPayout: realPick.expectedPayout || computedExpPay,
                          odds: realPick.odds ?? (!isNaN(numOdds) ? numOdds : null),
                          combinedOdds: realPick.combinedOdds || realPick.odds || (!isNaN(numOdds) ? numOdds : null),
                          sport: realPick.sport || e.sport,
                          gameLabel: realPick.gameLabel || e.gameLabel,
                          leagueId: realPick.leagueId || activeLeague,
                        });
                      } else {
                        const settledProfit = (resolvedStatus === "won") ? (e.points || 0) : 0;
                        setSelectedLeaguePick({
                          id: e.pickId || e.id, status: resolvedStatus, wager, odds: !isNaN(numOdds) ? numOdds : null,
                          combinedOdds: e.combinedOdds || (!isNaN(numOdds) ? numOdds : null), expectedPayout: computedExpPay,
                          points: settledProfit, label: e.label, gameLabel: e.gameLabel, sport: e.sport, sportKey: e.sportKey || "",
                          type: marketType, side: e.side, line: e.line, commenceTime: e.commenceTime,
                          homeTeamFull: parsedHome, awayTeamFull: parsedAway,
                          isParlay: e.isParlay || e.type === "parlay", legs: e.legs, leagueId: activeLeague,
                          userId: e.userId, submittedAt: e.createdAt, settledAt: e.settledAt, gameId: e.gameId, finalScore: e.finalScore,
                        });
                      }
                    }}
                    style={{ padding:"2px 14px 12px", cursor:"pointer" }}
                  >
                    {/* Mini team matchup with logos */}
                    {(awayTeam || homeTeam) && (
                      <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:6 }}>
                        {awayTeam && <TeamLogo name={awayTeam} sport={e.sport} size={18} />}
                        <span style={{ fontSize:11, fontWeight:600, color:"rgba(255,255,255,0.40)" }}>{e.gameLabel || `${awayTeam} @ ${homeTeam}`}</span>
                        {homeTeam && <TeamLogo name={homeTeam} sport={e.sport} size={18} />}
                      </div>
                    )}
                    {!awayTeam && !homeTeam && e.gameLabel && (
                      <div style={{ fontSize:11, color:"rgba(255,255,255,0.40)", marginBottom:4, fontWeight:600 }}>{e.gameLabel}</div>
                    )}

                    {/* Pick label + odds + status */}
                    <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", gap:8 }}>
                      <div style={{ minWidth:0, flex:1 }}>
                        <div style={{ fontSize:15, fontWeight:700, color:"rgba(255,255,255,0.90)", lineHeight:1.3, marginBottom:5 }}>{denPickLabel(e.label, denNonWager)}</div>
                        <div style={{ display:"flex", alignItems:"center", gap:5, flexWrap:"wrap" }}>
                          <div style={{ display:"flex", alignItems:"center", gap:4 }}>
                            <span style={{
                              width:7, height:7, borderRadius:"50%",
                              background: isStale ? "rgba(255,255,255,0.10)" : accentColor,
                              display:"inline-block",
                              boxShadow: isPenaltyEvent ? "none" : (isPending && !isStale) ? `0 0 6px ${accentColor}` : effectiveWin ? `0 0 6px ${accentColor}` : "none",
                              animation: (isPending && !isStale && !isPenaltyEvent) ? "pulse 1.5s ease infinite" : "none",
                            }}/>
                            <span style={{ fontSize:11, fontWeight:700, color: isStale ? "rgba(255,255,255,0.20)" : accentColor }}>{isPenaltyEvent ? "Settled" : effectiveWin ? "Won" : effectiveLoss ? "Lost" : pendingLabel}</span>
                          </div>
                          {/* Odds are meaningless in pick'em/survivor: nothing is
                              staked and a correct pick is worth exactly 1 point
                              regardless of price. Showing "-162" next to a pick'em
                              pick implies a payout that will never happen. */}
                          {!denNonWager && e.odds && (
                            <span style={{
                              fontSize:11, fontWeight:800, fontFamily:FM,
                              padding:"2px 8px", borderRadius:6,
                              background:`${accentColor}15`, border:`1px solid ${accentColor}25`,
                              color:accentColor,
                            }}>{e.odds > 0 ? `+${e.odds}` : e.odds}</span>
                          )}
                        </div>
                      </div>

                      {/* Points + tap chevron */}
                      <div style={{ display:"flex", flexDirection:"column", alignItems:"flex-end", gap:4, flexShrink:0 }}>
                        {ptsDelta && (
                          <div style={{
                            fontSize:15, fontWeight:800, fontFamily:FM, color:accentColor,
                            padding:"6px 12px", borderRadius:10,
                            background: effectiveWin ? "rgba(52,199,89,0.10)" : effectiveLoss ? "rgba(255,59,48,0.08)" : "rgba(255,255,255,0.04)",
                            border:`1px solid ${accentBorder}`,
                            letterSpacing:"-0.3px",
                          }}>{ptsDelta}<span style={{ fontSize:9, fontWeight:600, marginLeft:2, opacity:0.6 }}>pts</span></div>
                        )}
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.12)" strokeWidth="2" strokeLinecap="round"><polyline points="9 18 15 12 9 6"/></svg>
                      </div>
                    </div>
                  </div>

                  {/* Reactions */}
                  {ReactionRow}
                </div>
              </Fragment>
            );
          })}
        </div>

        </>) } {/* end social tab */}


        {/* == STANDINGS TAB ================================================== */}
        {/* ── STAT SHEET BOTTOM MODAL ─────────────────────────────────────── */}
        {statSheet && (() => {
          const st2 = standings[activeLeague] || [];
          const myEntry2 = st2.find(p => p.id === user?.uid);
          const myRank2  = st2.findIndex(p => p.id === user?.uid) + 1;
          const mp2 = memberPicks[activeLeague] || [];
          const myPicks2 = mp2.filter(p => p.userId === user?.uid || p.id === user?.uid);

          // Payday countdown
          const now2 = new Date();
          const payDay2 = league?.paydayDay ?? 5;
          const daysUntil2 = ((payDay2 - now2.getDay()) + 7) % 7 || 7;
          const nextPayday2 = new Date(now2);
          nextPayday2.setDate(now2.getDate() + daysUntil2);
          nextPayday2.setHours(0,0,0,0);
          const msLeft2 = nextPayday2 - now2;
          const hLeft2  = Math.floor(msLeft2 / 3600000);

          const sheets = {
            rank: {
              title: "🏅 Your Rank",
              content: () => (
                <div>
                  <div style={{ textAlign:"center", padding:"16px 0 20px" }}>
                    <div style={{ fontSize:52, fontWeight:700, color: myRank2===1?"#FFD700":myRank2===2?"#C0C0C0":myRank2===3?"#CD7F32":"#fff", lineHeight:1, letterSpacing:"-2px" }}>
                      #{myRank2 || "—"}
                    </div>
                    <div style={{ fontSize:13, color:"rgba(255,255,255,0.4)", marginTop:6 }}>
                      out of {st2.length} member{st2.length!==1?"s":""}
                    </div>
                  </div>
                  {st2.length > 0 && (
                    <div style={{ borderTop:"1px solid rgba(255,255,255,0.07)", paddingTop:14 }}>
                      <div style={{ fontSize:11, fontWeight:700, color:"rgba(255,255,255,0.42)", textTransform:"uppercase", letterSpacing:"0.08em", marginBottom:10 }}>Standings</div>
                      {st2.slice(0,5).map((p, i) => {
                        const isMe = p.id === user?.uid;
                        return (
                          <div key={p.id} style={{ display:"flex", alignItems:"center", gap:10, padding:"8px 0", borderBottom:"1px solid rgba(255,255,255,0.04)" }}>
                            <div style={{ width:22, fontSize:13, fontWeight:700, color: i===0?"#FFD700":i===1?"#C0C0C0":i===2?"#CD7F32":"rgba(255,255,255,0.3)", textAlign:"center" }}>#{i+1}</div>
                            <div style={{ width:28, height:28, borderRadius:"50%", background: isMe?T.coral:"rgba(255,255,255,0.1)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:11, fontWeight:700, color:"#fff", flexShrink:0 }}>
                              {(p.username?.[0]||"?").toUpperCase()}
                            </div>
                            <div style={{ flex:1, fontSize:13, fontWeight: isMe?700:500, color: isMe?"#FF6B3D":"rgba(255,255,255,0.8)" }}>{isMe ? "You" : p.username}</div>
                            {(() => {
                              // Pickem/survivor show correct picks, not a balance:
                              // there is no ledger, so every member's balance is 0
                              // and rendering "0 pts" in red would read as a bug.
                              const mode = league?.scoringMode || "wager";
                              if (mode === "pickem" || mode === "survivor") {
                                const elim = p.survivorStatus === "eliminated";
                                return (
                                  <div style={{ fontSize:13, fontWeight:700, color: elim ? "rgba(255,255,255,0.35)" : "#4ade80" }}>
                                    {elim ? `Out · Wk ${p.eliminatedWeek ?? "?"}` : `${p.wins ?? 0} correct`}
                                  </div>
                                );
                              }
                              return <div style={{ fontSize:13, fontWeight:700, color:(p.balance??0)>0?"#4ade80":"#f87171" }}>{(p.balance??0).toLocaleString()} pts</div>;
                            })()}
                          </div>
                        );
                      })}
                      {st2.length > 5 && <div style={{ fontSize:11, color:"rgba(255,255,255,0.40)", textAlign:"center", paddingTop:8 }}>+{st2.length-5} more members</div>}
                    </div>
                  )}
                  {myRank2 > 1 && st2[myRank2-2] && (() => {
                    const mode = league?.scoringMode || "wager";
                    const nonWager = mode === "pickem" || mode === "survivor";
                    const gap = nonWager
                      ? (st2[myRank2-2].wins ?? 0) - (myEntry2?.wins ?? 0)
                      : st2[myRank2-2].balance - (myEntry2?.balance??0);
                    const unit = nonWager ? `pick${gap === 1 ? "" : "s"}` : "pts";
                    return (
                      <div style={{ marginTop:14, padding:"10px 14px", background:"rgba(255,107,61,0.06)", border:"1px solid rgba(255,107,61,0.15)", borderRadius:10, fontSize:12, color:"rgba(255,255,255,0.5)" }}>
                        <span style={{ color:T.coral, fontWeight:700 }}>{gap} {unit}</span> behind #{myRank2-1} · {st2[myRank2-2].username}
                      </div>
                    );
                  })()}
                  {myRank2 === 1 && st2[1] && (() => {
                    const mode = league?.scoringMode || "wager";
                    const nonWager = mode === "pickem" || mode === "survivor";
                    const lead = nonWager
                      ? (myEntry2?.wins ?? 0) - (st2[1]?.wins ?? 0)
                      : (myEntry2?.balance??0) - (st2[1]?.balance??0);
                    const unit = nonWager ? `pick${lead === 1 ? "" : "s"}` : "pts";
                    return (
                      <div style={{ marginTop:14, padding:"10px 14px", background:"rgba(255,215,0,0.06)", border:"1px solid rgba(255,215,0,0.15)", borderRadius:10, fontSize:12, color:"rgba(255,255,255,0.5)" }}>
                        Leading by <span style={{ color:"#FFD700", fontWeight:700 }}>{lead} {unit}</span> · keep it up
                      </div>
                    );
                  })()}
                </div>
              ),
            },
            balance: {
              title: "Balance",
              content: () => {
                const wins2  = myPicks2.filter(p => p.status==="won");
                const losses2= myPicks2.filter(p => p.status==="lost");
                const pushes2= myPicks2.filter(p => p.status==="push");
                const totalWon  = wins2.reduce((s,p)  => s + (p.points||0), 0);
                const totalLost = losses2.reduce((s,p) => s + (p.wager||0),  0);

                // Build transaction history from picks + paydays
                const transactions = [];

                // Add settled picks
                myPicks2.filter(p => ["won","lost","push"].includes(p.status)).forEach(p => {
                  const ts = p.settledAt?.toDate ? p.settledAt.toDate() : p.settledAt?.seconds ? new Date(p.settledAt.seconds*1000) : p.createdAt?.toDate ? p.createdAt.toDate() : p.createdAt?.seconds ? new Date(p.createdAt.seconds*1000) : null;
                  if (!ts) return;
                  const delta = p.status === "won" ? (p.points||0) : p.status === "lost" ? -(p.wager||0) : 0;
                  const label = p.status === "won" ? `Won: ${p.label || p.type || "Pick"}` : p.status === "lost" ? `Lost: ${p.label || p.type || "Pick"}` : `Push: ${p.label || "Pick"}`;
                  const gameLabel = p.gameLabel || (p.awayTeamFull && p.homeTeamFull ? `${p.awayTeamFull} @ ${p.homeTeamFull}` : "");
                  transactions.push({ ts, delta, label, gameLabel, type: p.status, sport: p.sport });
                });

                // Add paydays from activity feed
                const ev = activity[activeLeague] || [];
                ev.filter(e => e.type === "payday" && (e.userId === user?.uid || e.userId === "system")).forEach(e => {
                  const ts = e.createdAt?.toDate ? e.createdAt.toDate() : e.createdAt?.seconds ? new Date(e.createdAt.seconds*1000) : null;
                  if (!ts) return;
                  transactions.push({ ts, delta: e.points || e.wager || 200, label: "League Payday", gameLabel: "", type: "payday", sport: "" });
                });

                // Fallback: if member has lastPayday but no payday event was found, add one
                if (myEntry2?.lastPayday) {
                  const lpTs = myEntry2.lastPayday.toDate ? myEntry2.lastPayday.toDate() : myEntry2.lastPayday.seconds ? new Date(myEntry2.lastPayday.seconds*1000) : myEntry2.lastPayday instanceof Date ? myEntry2.lastPayday : null;
                  // ?? not || — a league that zeroed its payday must not show
                  // phantom +200 rows; skip the fallback row entirely at 0.
                  if (lpTs && (league?.paydayAmount ?? 200) > 0 && !transactions.some(t => t.type === "payday" && Math.abs(t.ts - lpTs) < 60000)) {
                    transactions.push({ ts: lpTs, delta: league?.paydayAmount ?? 200, label: "League Payday", gameLabel: "", type: "payday", sport: "" });
                  }
                }

                // Sort newest first
                transactions.sort((a, b) => b.ts - a.ts);

                const fmtDate = (d) => d.toLocaleDateString("en-US", { month:"short", day:"numeric" });
                const fmtTime = (d) => d.toLocaleTimeString("en-US", { hour:"numeric", minute:"2-digit" });

                return (
                  <div>
                    <div style={{ textAlign:"center", padding:"16px 0 20px" }}>
                      <div style={{ fontSize:36, fontWeight:700, color:(getDisplayPts(myEntry2)??0)>=0?"#4ade80":"#f87171", lineHeight:1, letterSpacing:"-1px", fontFamily:FM }}>
                        <AnimatedNumber value={getDisplayPts(myEntry2)} />
                      </div>
                      <div style={{ fontSize:13, color:"rgba(255,255,255,0.4)", marginTop:6 }}>{isNonWagerRoom ? "league wins" : "league pts"}</div>
                    </div>
                    <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8, marginBottom:14 }}>
                      {[
                        { label:"Total won", val:`+${totalWon.toLocaleString()}`, color:"#4ade80" },
                        { label:"Total lost", val:`-${totalLost.toLocaleString()}`, color:"#f87171" },
                        { label:"Win rate", val: myPicks2.filter(p=>["won","lost"].includes(p.status)).length > 0 ? `${Math.round(wins2.length/(wins2.length+losses2.length)*100)}%` : "—", color:"#FCD34D" },
                        { label:"Pushes", val:`${pushes2.length}`, color:"rgba(255,255,255,0.5)" },
                      ].map(({ label, val, color }) => (
                        <div key={label} style={{ background:"rgba(255,255,255,0.04)", border:"1px solid rgba(255,255,255,0.07)", borderRadius:10, padding:"10px 12px" }}>
                          <div style={{ fontSize:10, color:"rgba(255,255,255,0.42)", fontWeight:700, textTransform:"uppercase", letterSpacing:"0.08em", marginBottom:4 }}>{label}</div>
                          <div style={{ fontSize:18, fontWeight:700, color }}>{val}</div>
                        </div>
                      ))}
                    </div>

                    {/* Transaction History */}
                    {transactions.length > 0 && (
                      <div style={{ marginTop:6 }}>
                        <div style={{ fontSize:11, fontWeight:700, color:"rgba(255,255,255,0.42)", textTransform:"uppercase", letterSpacing:"0.08em", marginBottom:10, paddingLeft:2 }}>Transaction History</div>
                        <div style={{ display:"flex", flexDirection:"column", gap:1 }}>
                          {transactions.slice(0, 30).map((tx, i) => {
                            const isWin = tx.type === "won";
                            const isLoss = tx.type === "lost";
                            const isPay = tx.type === "payday";
                            const accent = isWin ? "#4ade80" : isLoss ? "#f87171" : isPay ? "#FCD34D" : "rgba(255,255,255,0.4)";
                            const bg = isWin ? "rgba(74,222,128,0.04)" : isLoss ? "rgba(248,113,113,0.04)" : isPay ? "rgba(252,211,77,0.04)" : "rgba(255,255,255,0.02)";
                            const icon = isWin ? "✓" : isLoss ? "✗" : isPay ? "💰" : "—";
                            const prevTx = transactions[i-1];
                            const showDate = i === 0 || (prevTx && fmtDate(tx.ts) !== fmtDate(prevTx.ts));
                            return (
                              <Fragment key={`tx-${i}`}>
                                {showDate && (
                                  <div style={{ fontSize:9, fontWeight:800, color:"rgba(255,255,255,0.32)", letterSpacing:"0.12em", padding:"10px 4px 4px", fontFamily:FM }}>{fmtDate(tx.ts)}</div>
                                )}
                                <div style={{
                                  display:"flex", alignItems:"center", gap:10, padding:"8px 10px",
                                  background:bg, borderRadius:10,
                                  borderLeft:`3px solid ${accent}`,
                                }}>
                                  <span style={{ fontSize:12, width:20, textAlign:"center", flexShrink:0 }}>{icon}</span>
                                  <div style={{ flex:1, minWidth:0 }}>
                                    <div style={{ fontSize:12, fontWeight:600, color:"rgba(255,255,255,0.75)", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{tx.label}</div>
                                    {tx.gameLabel && <div style={{ fontSize:10, color:"rgba(255,255,255,0.40)", marginTop:1, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{tx.gameLabel}</div>}
                                  </div>
                                  <div style={{ textAlign:"right", flexShrink:0 }}>
                                    <div style={{ fontSize:14, fontWeight:800, fontFamily:FM, color:accent }}>
                                      {tx.delta > 0 ? "+" : ""}{tx.delta.toLocaleString()}
                                    </div>
                                    <div style={{ fontSize:9, color:"rgba(255,255,255,0.36)", marginTop:1 }}>{fmtTime(tx.ts)}</div>
                                  </div>
                                </div>
                              </Fragment>
                            );
                          })}
                        </div>
                        {transactions.length > 30 && (
                          <div style={{ textAlign:"center", fontSize:10, color:"rgba(255,255,255,0.36)", marginTop:8 }}>Showing latest 30 of {transactions.length}</div>
                        )}
                      </div>
                    )}

                    <div style={{ fontSize:11, color:"rgba(255,255,255,0.40)", textAlign:"center", marginTop:14 }}>
                      Starting balance: {(league?.startingBalance ?? 1000).toLocaleString()} pts · Rank #{myRank2} of {st2.length}
                    </div>
                  </div>
                );
              },
            },
            record: {
              title: "Your Record",
              content: () => {
                const wins2   = myPicks2.filter(p => p.status==="won");
                const losses2 = myPicks2.filter(p => p.status==="lost");
                const pushes2 = myPicks2.filter(p => p.status==="push");
                const pending2= myPicks2.filter(p => p.status==="pending");
                const bySport = {};
                myPicks2.filter(p=>["won","lost"].includes(p.status)).forEach(p => {
                  const s = p.sport || "Other";
                  if (!bySport[s]) bySport[s] = { w:0, l:0 };
                  if (p.status==="won") bySport[s].w++; else bySport[s].l++;
                });
                const streak = myEntry2?.streak ?? 0;
                return (
                  <div>
                    <div style={{ textAlign:"center", padding:"16px 0 20px" }}>
                      <div style={{ display:"flex", alignItems:"baseline", gap:6, justifyContent:"center" }}>
                        <span style={{ fontSize:42, fontWeight:700, color:"#4ade80", lineHeight:1 }}>{wins2.length}W</span>
                        <span style={{ fontSize:28, fontWeight:700, color:"rgba(255,255,255,0.40)" }}>–</span>
                        <span style={{ fontSize:42, fontWeight:700, color:"#f87171", lineHeight:1 }}>{losses2.length}L</span>
                        {pushes2.length > 0 && <span style={{ fontSize:28, fontWeight:700, color:"rgba(255,255,255,0.4)", lineHeight:1 }}>{pushes2.length}P</span>}
                      </div>
                      {streak > 0 && <div style={{ fontSize:12, color:"#4ade80", marginTop:8, fontWeight:700 }}>{streak}-game win streak</div>}
                      {pending2.length > 0 && <div style={{ fontSize:12, color:"rgba(252,211,77,0.8)", marginTop:4 }}>{pending2.length} pick{pending2.length!==1?"s":""} in play</div>}
                    </div>
                    {Object.keys(bySport).length > 0 && (
                      <div style={{ borderTop:"1px solid rgba(255,255,255,0.07)", paddingTop:14 }}>
                        <div style={{ fontSize:11, fontWeight:700, color:"rgba(255,255,255,0.42)", textTransform:"uppercase", letterSpacing:"0.08em", marginBottom:10 }}>By Sport</div>
                        {Object.entries(bySport).sort((a,b)=>b[1].w+b[1].l - a[1].w-a[1].l).map(([sport, rec]) => (
                          <div key={sport} style={{ display:"flex", alignItems:"center", gap:10, padding:"7px 0", borderBottom:"1px solid rgba(255,255,255,0.04)" }}>
                            <div style={{ flex:1, fontSize:13, color:"rgba(255,255,255,0.7)", fontWeight:600 }}>{sport}</div>
                            <div style={{ fontSize:13, fontWeight:700, color:"#4ade80" }}>{rec.w}W</div>
                            <div style={{ fontSize:13, fontWeight:700, color:"#f87171" }}>{rec.l}L</div>
                            <div style={{ fontSize:11, color:"rgba(255,255,255,0.42)", minWidth:36, textAlign:"right" }}>
                              {rec.w+rec.l > 0 ? `${Math.round(rec.w/(rec.w+rec.l)*100)}%` : "—"}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              },
            },
            payday: {
              title: "League Payday",
              content: () => (
                <div>
                  <div style={{ textAlign:"center", padding:"16px 0 20px" }}>
                    <div style={{ fontSize:48, fontWeight:700, color:"#FCD34D", lineHeight:1, letterSpacing:"-2px" }}>+{league?.paydayAmount ?? 200}</div>
                    <div style={{ fontSize:13, color:"rgba(255,255,255,0.4)", marginTop:6 }}>pts on payday</div>
                  </div>
                  <div style={{ background:"rgba(217,119,6,0.08)", border:"1px solid rgba(217,119,6,0.2)", borderRadius:12, padding:"14px 16px", marginBottom:14 }}>
                    <div style={{ fontSize:13, fontWeight:700, color:"#FCD34D", marginBottom:6 }}>
                      Payday keeps the game going
                    </div>
                    <div style={{ fontSize:12, color:"rgba(255,255,255,0.4)", lineHeight:1.6 }}>
                      Your league captain sets the payday schedule and amount. When payday hits, everyone in the league gets points added to their balance automatically.
                    </div>
                  </div>
                  <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8 }}>
                    {[
                      { label:"Weekly top-up", val:`+${league?.paydayAmount ?? 200} pts`, color:"#FCD34D" },
                      { label:"Balance floor", val:"0 pts", color:"#f87171" },
                    ].map(({ label, val, color }) => (
                      <div key={label} style={{ background:"rgba(255,255,255,0.04)", border:"1px solid rgba(255,255,255,0.07)", borderRadius:10, padding:"10px 12px" }}>
                        <div style={{ fontSize:10, color:"rgba(255,255,255,0.42)", fontWeight:700, textTransform:"uppercase", letterSpacing:"0.08em", marginBottom:4 }}>{label}</div>
                        <div style={{ fontSize:18, fontWeight:700, color }}>{val}</div>
                      </div>
                    ))}
                  </div>
                </div>
              ),
            },
          };

          const sheet = sheets[statSheet];
          if (!sheet) return null;

          return (
            <>
              {/* Backdrop */}
              <div onClick={() => setStatSheet(null)} style={{
                position:"fixed", inset:0, zIndex:80,
                background:"rgba(0,0,0,0.65)", backdropFilter:"blur(4px)",
              }}/>
              {/* Sheet */}
              <div style={{
                position:"fixed", bottom:0, left:"50%", transform:"translateX(-50%)",
                width:"100%", maxWidth:640, zIndex:81,
                background:T.card,
                border:"1px solid rgba(255,255,255,0.1)",
                borderBottom:"none",
                borderRadius:"20px 20px 0 0",
                padding:"0 18px calc(32px + env(safe-area-inset-bottom, 0px))",
                maxHeight:"calc(92vh - env(safe-area-inset-top, 0px))", overflowY:"auto", minHeight:0, WebkitOverflowScrolling:"touch", overscrollBehavior:"contain",
              }}>
                {/* Handle */}
                <div style={{ display:"flex", justifyContent:"center", padding:"12px 0 4px" }}>
                  <div style={{ width:36, height:4, borderRadius:2, background:"rgba(255,255,255,0.15)" }}/>
                </div>
                {/* Header */}
                <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:4, paddingBottom:12, borderBottom:"1px solid rgba(255,255,255,0.07)" }}>
                  <div style={{ fontSize:16, fontWeight:700, color:"#fff" }}>{sheet.title}</div>
                  <button onClick={() => setStatSheet(null)} style={{
                    width:28, height:28, borderRadius:"50%", border:"1px solid rgba(255,255,255,0.12)",
                    background:"rgba(255,255,255,0.06)", cursor:"pointer", color:"rgba(255,255,255,0.6)",
                    fontSize:16, display:"flex", alignItems:"center", justifyContent:"center",
                  }} aria-label="Close">×</button>
                </div>
                {sheet.content()}
              </div>
            </>
          );
        })()}

        {roomTab === "standings" && (
          <div style={{ padding:"12px 14px 100px" }}>

            {leagueEnded && champion && (
              <div style={{
                marginBottom:14, padding:"20px", textAlign:"center",
                background:"rgba(255,215,0,0.08)",
                border:"1px solid rgba(255,215,0,0.2)", borderRadius:18,
              }}>
                <div style={{ fontSize:10, fontWeight:700, color:"rgba(255,255,255,0.5)", textTransform:"uppercase", letterSpacing:"0.1em", marginBottom:4 }}>Season Winner</div>
                <div style={{ fontSize:22, fontWeight:700, color:"#FFD700" }}>{champion.username || champion.name || "Champion"}</div>
                <div style={{ fontSize:13, color:"rgba(255,255,255,0.55)", marginTop:4 }}>{getDisplayPts(champion).toLocaleString()} {ptsUnit}</div>
              </div>
            )}

            {/* ── TOP 3 PODIUM ────────────────────────────────────────────── */}
            {displayStandings.length >= 3 && !leagueEnded && (() => {
              const podium = [displayStandings[1], displayStandings[0], displayStandings[2]]; // 2nd, 1st, 3rd (visual order)
              const podiumMeta = [
                { rank:2, height:54, color:"#C0C0C0", glow:"rgba(192,192,192,0.08)", border:"rgba(192,192,192,0.20)", label:"2ND" },
                { rank:1, height:70, color:"#FFD700", glow:"rgba(255,215,0,0.10)", border:"rgba(255,215,0,0.28)", label:"1ST" },
                { rank:3, height:42, color:"#CD7F32", glow:"rgba(205,127,50,0.08)", border:"rgba(205,127,50,0.18)", label:"3RD" },
              ];
              return (
                <div style={{ display:"flex", alignItems:"flex-end", justifyContent:"center", gap:5, marginBottom:12, padding:"4px 0 0" }}>
                  {podium.map((p, pi) => {
                    const m = podiumMeta[pi];
                    const isMe = p.id === user?.uid;
                    return (
                      <div key={p.id} onClick={() => setViewingMember({ uid: p.id, username: p.username })}
                        style={{ flex:1, cursor:"pointer", display:"flex", flexDirection:"column", alignItems:"center", maxWidth:110 }}>
                        {/* Avatar */}
                        <div style={{
                          width: pi===1?38:32, height: pi===1?38:32, borderRadius:11, marginBottom:6,
                          background: isMe ? T.coral : `linear-gradient(135deg,${m.color}30,${m.color}15)`,
                          border:`1.5px solid ${m.color}50`,
                          display:"flex", alignItems:"center", justifyContent:"center",
                          fontSize: pi===1?14:12, fontWeight:700, color: isMe?"#fff":m.color,
                        }}>{(p.username?.[0]||"?").toUpperCase()}</div>
                        {/* Name */}
                        <div style={{ fontSize:11, fontWeight:700, color: isMe?T.coral:"#fff", textAlign:"center", marginBottom:3, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis", maxWidth:"100%" }}>
                          {isMe ? "You" : p.username}
                        </div>
                        {/* Podium bar */}
                        <div style={{
                          width:"100%", height:m.height, borderRadius:"8px 8px 0 0",
                          background:`linear-gradient(180deg, ${m.color}18, ${m.color}08)`,
                          border:`1px solid ${m.border}`, borderBottom:"none",
                          display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center",
                          gap:1, position:"relative", overflow:"hidden",
                        }}>
                          <div style={{ fontSize:9, fontWeight:800, color:m.color, letterSpacing:"0.14em", fontFamily:FM, opacity:0.8 }}>{m.label}</div>
                          <div style={{ fontSize:13, fontWeight:800, color:m.color, fontFamily:FM, letterSpacing:"-0.02em" }}>{getDisplayPts(p).toLocaleString()}</div>
                          <div style={{ fontSize:9, color:"rgba(255,255,255,0.42)", fontWeight:600 }}>
                            {isWcLeague
                              ? `${p.wcCorrectSlots ?? 0}/48 correct`
                              : `${p.wins??0}W–${p.losses??0}L`}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })()}

            {/* ── FULL LEADERBOARD ────────────────────────────────────────── */}
            <div style={{
              fontSize:9.5, fontWeight:800, color:T.inkMuted, letterSpacing:"0.10em",
              textTransform:"uppercase", marginBottom:8, paddingLeft:4, fontFamily:FB,
            }}>Leaderboard{isWcLeague ? " · World Cup Score" : ""}</div>
            <div style={{ background:T.card, border:`1px solid ${T.border}`, borderRadius:14, overflow:"hidden" }}>
              {displayStandings.map((p, ri) => {
                const isMe = p.id === user?.uid;
                const settled = (p.wins??0)+(p.losses??0);
                const wr = settled > 0 ? Math.round((p.wins??0)/settled*100) : null;
                const isPodium = ri < 3;
                const rankColor = ri===0 ? "#FFD700" : ri===1 ? "#E5E7EB" : ri===2 ? "#D97706" : T.inkMuted;
                const rankBg = ri===0 ? "rgba(255,215,0,0.08)" : ri===1 ? "rgba(229,231,235,0.05)" : ri===2 ? "rgba(217,119,6,0.08)" : "rgba(255,255,255,0.025)";
                const leaderBal = getDisplayPts(displayStandings[0]) ?? 1;
                const myPts = getDisplayPts(p);
                const barPct = leaderBal > 0 ? Math.max(2, Math.round((myPts / leaderBal) * 100)) : 0;
                // WC scores are always >= 0; only the "negative balance" red applies to non-WC leagues.
                const balanceColor = isMe ? T.coral : (isWcLeague ? T.ink : (myPts >= 0 ? T.ink : T.red));
                return (
                  <div key={p.id}
                    onClick={() => setViewingMember({ uid: p.id, username: p.username })}
                    style={{
                      display:"flex", alignItems:"center", gap:10, padding:"10px 12px",
                      background: isMe ? "rgba(255,107,61,0.05)" : "transparent",
                      borderTop: ri > 0 ? `1px solid ${T.border}` : "none",
                      cursor:"pointer", position:"relative", overflow:"hidden",
                      animation:"fadeUp 0.22s ease both",
                      animationDelay:`${Math.min(ri, 12) * 22}ms`,
                      transition:"background 0.15s",
                    }}
                  >
                    {/* Subtle progress bar background — leader-relative */}
                    <div style={{
                      position:"absolute", left:0, top:0, bottom:0, width:`${barPct}%`,
                      background: isMe ? "rgba(255,107,61,0.03)" : isPodium ? `${rankColor}05` : "rgba(255,255,255,0.008)",
                      transition:"width 0.6s ease",
                      pointerEvents:"none",
                    }}/>
                    {/* ── Rank ── */}
                    <div style={{
                      width:24, height:24, borderRadius:8, flexShrink:0, position:"relative",
                      background: isPodium ? rankBg : "transparent",
                      border: isPodium ? `1px solid ${rankColor}25` : "none",
                      display:"flex", alignItems:"center", justifyContent:"center",
                      fontFamily:FD, fontSize: isPodium ? 12 : 11, fontWeight: 800,
                      color: isPodium ? rankColor : T.inkMuted,
                      letterSpacing:"-0.02em", fontVariantNumeric:"tabular-nums",
                    }}>
                      {ri+1}
                    </div>
                    {/* ── Avatar ── */}
                    <div style={{
                      width:28, height:28, borderRadius:9, flexShrink:0, position:"relative", overflow:"hidden",
                      background: isMe
                        ? T.coral
                        : isPodium
                          ? `${rankColor}22`
                          : "rgba(255,255,255,0.04)",
                      border: (p.streak??0) >= 3
                        ? `1.5px solid rgba(255,107,61,0.40)`
                        : isMe
                          ? "1px solid rgba(255,255,255,0.10)"
                          : isPodium
                            ? `1px solid ${rankColor}28`
                            : `1px solid ${T.border}`,
                      display:"flex", alignItems:"center", justifyContent:"center",
                      fontFamily:FD, fontSize:11, fontWeight:700,
                      color: isMe ? "#fff" : isPodium ? rankColor : T.inkSec,
                      animation: (p.streak??0) >= 3 ? "flameBorder 2s ease infinite" : "none",
                      letterSpacing:"-0.02em",
                    }}>
                      <span style={{ position:"relative" }}>{(p.username?.[0]||"?").toUpperCase()}</span>
                    </div>
                    <div style={{ flex:1, minWidth:0, position:"relative" }}>
                      <div style={{ display:"flex", alignItems:"center", gap:5 }}>
                        <span style={{
                          fontSize:12, fontWeight:isMe?700:600,
                          color: T.ink,
                          overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap",
                          letterSpacing:"-0.005em", maxWidth:"100%",
                        }}>{p.username}</span>
                        {isMe && <span style={{ fontSize:9, color:T.coral, fontWeight:700, letterSpacing:"-0.005em", flexShrink:0 }}>· you</span>}
                        {(p.streak??0) >= 3 && (
                          <span style={{
                            fontSize:9, fontWeight:800, padding:"1px 5px", borderRadius:5,
                            background:"rgba(255,107,61,0.08)", color:T.coral, fontFamily:FB,
                            display:"inline-flex", alignItems:"center", gap:3, flexShrink:0,
                            fontVariantNumeric:"tabular-nums",
                          }}>
                            <svg width="9" height="9" viewBox="0 0 24 24" fill="currentColor" stroke="none" style={{ flexShrink:0 }}>
                              <path d="M12 2C12 2 5 9 5 14a7 7 0 0014 0c0-5-7-12-7-12z"/>
                            </svg>
                            W{p.streak}
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize:10, color:T.inkMuted, marginTop:2, display:"flex", alignItems:"center", gap:5 }}>
                        {isWcLeague ? (
                          <span style={{ fontFamily:FM, fontVariantNumeric:"tabular-nums" }}>
                            {(p.wcKnockoutPoints || 0) > 0 ? (
                              <>G {p.wcGroupPoints || 0} · K {p.wcKnockoutPoints || 0}</>
                            ) : (
                              <>
                                {p.wcCorrectSlots ?? 0}/48 correct
                                {p.wcMatchesPlayed > 0 && (
                                  <> · {p.wcMatchesPlayed} {p.wcMatchesPlayed === 1 ? "match" : "matches"}</>
                                )}
                              </>
                            )}
                          </span>
                        ) : (
                          <>
                            <span style={{ fontFamily:FM, fontVariantNumeric:"tabular-nums" }}>{p.wins??0}–{p.losses??0}</span>
                            {wr!=null && (
                              <>
                                <span style={{ width:2, height:2, borderRadius:"50%", background:T.inkFaint }}/>
                                <span style={{
                                  fontWeight:700, fontFamily:FM, fontVariantNumeric:"tabular-nums",
                                  color: wr>=60?T.green:wr>=50?T.inkSec:T.red,
                                }}>{wr}%</span>
                              </>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                    <div style={{ textAlign:"right", flexShrink:0, position:"relative" }}>
                      <div style={{
                        fontFamily:FD, fontSize:14, fontWeight:800, color: balanceColor,
                        letterSpacing:"-0.02em", fontVariantNumeric:"tabular-nums", lineHeight:1,
                      }}>{getDisplayPts(p).toLocaleString()}</div>
                      <div style={{ fontSize:9, color:T.inkMuted, marginTop:2, fontWeight:700, letterSpacing:"0.10em", textTransform:"uppercase" }}>{ptsUnit}</div>
                    </div>
                    {league.isCommissioner && !isMe && (
                      <button onClick={e => { e.stopPropagation(); window.torchConfirm(`Remove ${p.username}?`, async () => {
                        try { await leaveLeague(p.id, activeLeague); setStandings(prev => ({ ...prev, [activeLeague]: prev[activeLeague].filter(x => x.id !== p.id) })); }
                        catch { window.torchToast?.("Couldn't remove.", "error"); }
                      }, { danger:true, confirmLabel:"Remove" }); }} style={{
                        width:22, height:22, borderRadius:"50%", border:`1px solid ${T.border}`,
                        background:"rgba(255,255,255,0.025)", cursor:"pointer", color:T.inkMuted,
                        display:"flex", alignItems:"center", justifyContent:"center", position:"relative",
                        flexShrink:0, transition:"all 0.15s",
                      }} aria-label="Remove member"
                        onMouseEnter={(e)=>{e.currentTarget.style.background="rgba(199,101,97,0.10)"; e.currentTarget.style.borderColor="rgba(199,101,97,0.30)"; e.currentTarget.style.color=T.red;}}
                        onMouseLeave={(e)=>{e.currentTarget.style.background="rgba(255,255,255,0.025)"; e.currentTarget.style.borderColor=T.border; e.currentTarget.style.color=T.inkMuted;}}
                      >
                        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M18 6L6 18M6 6l12 12"/></svg>
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )} {/* end standings tab */}

        {/* == WC2026 BRACKETS TAB ============================================
             Roster view of every league member's predictions. Locked brackets
             show champion + headline picks (tappable for full details). In-
             progress brackets show as locked placeholders so late deciders
             can't copy the leaders. Renders only for WC bracket-pool leagues
             (the tab itself is conditionally added to the tab bar above). */}
        {roomTab === "brackets" && isWcLeague && (
          <div style={{ padding:"14px 14px calc(100px + env(safe-area-inset-bottom, 0px))" }}>
            {/* Header / explainer */}
            <div style={{
              padding:"4px 4px 14px",
            }}>
              <div style={{
                fontFamily:FD, fontSize:20, fontWeight:700,
                color:"#fff", letterSpacing:"-0.025em",
                marginBottom:6, lineHeight:1.1,
              }}>League Brackets</div>
              <div style={{
                fontSize:12, color:"rgba(255,255,255,0.55)", lineHeight:1.5,
              }}>
                See what your league mates predicted. Brackets reveal once each player fully locks in — no peeking allowed.
              </div>
            </div>

            {/* Loading state */}
            {wcMemberBracketsLoading && !wcMemberBrackets[activeLeague] && (
              <div style={{ padding:"40px 16px", textAlign:"center" }}>
                <div style={{
                  width:24, height:24, margin:"0 auto 12px",
                  border:`2px solid ${T.border}`,
                  borderTopColor:T.teal,
                  borderRadius:"50%",
                  animation:"spin 0.7s linear infinite",
                }}/>
                <div style={{ color:"rgba(255,255,255,0.45)", fontSize:12 }}>Loading brackets…</div>
              </div>
            )}

            {/* Empty league state — shouldn't normally happen since the
                viewing user is themselves a member */}
            {!wcMemberBracketsLoading && (wcMemberBrackets[activeLeague] || []).length === 0 && (
              <div style={{ padding:"40px 16px", textAlign:"center" }}>
                <div style={{ fontSize:14, color:"rgba(255,255,255,0.55)", marginBottom:4 }}>
                  No members yet
                </div>
                <div style={{ fontSize:12, color:"rgba(255,255,255,0.36)" }}>
                  Invite friends to start filling brackets.
                </div>
              </div>
            )}

            {/* Cards */}
            {(wcMemberBrackets[activeLeague] || []).map((m) => (
              <MemberBracketCard
                key={m.userId}
                member={m}
                summary={m.summary}
                isMe={m.userId === user?.uid}
                onTap={() => setWcSelectedMemberBracket(m)}
              />
            ))}

            {/* Reveal-summary footer when at least one bracket is locked */}
            {(wcMemberBrackets[activeLeague] || []).some(m => m.summary.status === "complete") && (
              <div style={{
                marginTop:14, padding:"12px 14px",
                background:"rgba(20,184,166,0.04)",
                border:`1px solid rgba(20,184,166,0.18)`,
                borderRadius:11,
                fontSize:11, color:"rgba(255,255,255,0.55)", lineHeight:1.5,
                textAlign:"center",
              }}>
                {(() => {
                  const list = wcMemberBrackets[activeLeague] || [];
                  const locked = list.filter(m => m.summary.status === "complete").length;
                  const total = list.length;
                  return `${locked} of ${total} ${total === 1 ? "bracket" : "brackets"} locked in. Once everyone locks, the full picks reveal.`;
                })()}
              </div>
            )}

            {/* Member bracket details modal — opens when a locked card is
                tapped. Read-only view of that member's full predictions
                (groups + knockout path + boot + goals). */}
            <MemberBracketDetailsModal
              open={!!wcSelectedMemberBracket}
              onClose={() => setWcSelectedMemberBracket(null)}
              member={wcSelectedMemberBracket || {}}
              summary={wcSelectedMemberBracket?.summary || { status: "none", groupsCount: 0 }}
              predictions={wcSelectedMemberBracket?.predictions || null}
            />
          </div>
        )}

        {/* -- INVITE SHEET --------------------------------------------------- */}

      {/* ═══ CAPTAIN: ANNOUNCEMENT MODAL ═══ */}
      {showAnnounce && (
        <div onClick={() => setShowAnnounce(false)} style={{ position:"fixed", inset:0, zIndex:100, background:"rgba(0,0,0,0.55)", display:"flex", alignItems:"flex-end" }}>
          <div onClick={e=>e.stopPropagation()} style={{ width:"100%", maxWidth:640, margin:"0 auto", background:T.card, borderRadius:"20px 20px 0 0", padding:"24px 20px calc(80px + env(safe-area-inset-bottom, 0px))", maxHeight:"70vh", overflowY:"auto", minHeight:0, WebkitOverflowScrolling:"touch", overscrollBehavior:"contain" }}>
            <div style={{ width:36, height:4, background:T.border, borderRadius:2, margin:"0 auto 20px" }}/>
            <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", gap:10, marginBottom:16 }}>
              <div style={{ display:"flex", alignItems:"center", gap:10, flex:1, minWidth:0 }}>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={T.coral} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink:0 }}>
                  <path d="M3 11l18-5v12L3 13"/>
                  <path d="M11.6 16.8a3 3 0 1 1-5.8-1.6"/>
                </svg>
                <div style={{ minWidth:0 }}>
                  <div style={{ fontSize:17, fontWeight:700, color:"#fff" }}>Post Announcement</div>
                  <div style={{ fontSize:12, color:"rgba(255,255,255,0.35)", marginTop:2 }}>Pinned at the top of your league's Social feed</div>
                </div>
              </div>
              <button onClick={() => setShowAnnounce(false)} aria-label="Close" style={{
                flexShrink:0, width:32, height:32, borderRadius:10,
                background:"rgba(255,255,255,0.05)", border:"1px solid rgba(255,255,255,0.08)",
                color:"rgba(255,255,255,0.5)", cursor:"pointer",
                display:"flex", alignItems:"center", justifyContent:"center",
              }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
            <textarea value={announceDraft} onChange={e => setAnnounceDraft(e.target.value)} placeholder="e.g. NFL Sunday slate — everyone get your picks in by kickoff!" maxLength={280}
              style={{ width:"100%", height:100, padding:"14px", border:`1.5px solid ${T.border}`, borderRadius:14, fontSize:15, background:T.bgWarm, color:T.ink, outline:"none", fontFamily:FB, boxSizing:"border-box", resize:"none" }}/>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginTop:8 }}>
              <span style={{ fontSize:11, color:"rgba(255,255,255,0.36)" }}>{announceDraft.length}/280</span>
            </div>
            <button onClick={() => handlePostAnnouncement(activeLeague)} disabled={announcing || !announceDraft.trim()} style={{
              width:"100%", padding:"14px", background:T.coral, border:"none", borderRadius:12,
              color:"#fff", fontSize:15, fontWeight:700, cursor:"pointer", opacity: announcing || !announceDraft.trim() ? 0.5 : 1, marginTop:12,
            }}>{announcing ? "Posting…" : "Post Announcement"}</button>
          </div>
        </div>
      )}

      {/* ═══ CAPTAIN: LEAGUE SETTINGS MODAL ═══ */}
      {showSettings && (
        <div onClick={() => setShowSettings(false)} style={{ position:"fixed", inset:0, zIndex:100, background:"rgba(0,0,0,0.55)", display:"flex", alignItems:"flex-end" }}>
          <div onClick={e=>e.stopPropagation()} style={{ width:"100%", maxWidth:640, margin:"0 auto", background:T.card, borderRadius:"20px 20px 0 0", padding:"24px 20px calc(80px + env(safe-area-inset-bottom, 0px))", maxHeight:"85vh", overflowY:"auto", minHeight:0, WebkitOverflowScrolling:"touch", overscrollBehavior:"contain" }}>
            <div style={{ width:36, height:4, background:T.border, borderRadius:2, margin:"0 auto 20px" }}/>
            <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", gap:10, marginBottom:20 }}>
              <div style={{ display:"flex", alignItems:"center", gap:10, flex:1, minWidth:0 }}>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink:0, opacity:0.85 }}>
                  <circle cx="12" cy="12" r="3"/>
                  <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
                </svg>
                <div style={{ fontSize:17, fontWeight:700, color:"#fff" }}>League Settings</div>
              </div>
              <button onClick={() => setShowSettings(false)} aria-label="Close" style={{
                flexShrink:0, width:32, height:32, borderRadius:10,
                background:"rgba(255,255,255,0.05)", border:"1px solid rgba(255,255,255,0.08)",
                color:"rgba(255,255,255,0.5)", cursor:"pointer",
                display:"flex", alignItems:"center", justifyContent:"center",
              }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>

            {[
              { label:"League Name", val:editName, set:setEditName, placeholder:"League name", max:60 },
              { label:"League Motto", val:editMotto, set:setEditMotto, placeholder:"Optional", max:80 },
              { label:"What's on the Line?", val:editPrize, set:setEditPrize, placeholder:"e.g. Loser buys dinner", max:100 },
            ].map(({ label, val, set, placeholder, max }) => (
              <div key={label} style={{ marginBottom:14 }}>
                <div style={{ fontSize:11, fontWeight:700, color:T.inkSec, marginBottom:5, textTransform:"uppercase", letterSpacing:"0.07em" }}>{label}</div>
                <input value={val} onChange={e=>set(e.target.value)} placeholder={placeholder} maxLength={max}
                  style={{ width:"100%", padding:"12px 14px", border:`1.5px solid ${T.border}`, borderRadius:10, fontSize:15, background:T.bgWarm, color:T.ink, outline:"none", fontFamily:FB, boxSizing:"border-box" }}/>
              </div>
            ))}

            <button onClick={() => handleSaveSettings(activeLeague)} disabled={savingSettings} style={{
              width:"100%", padding:"14px", background:T.coral, border:"none", borderRadius:12,
              color:"#fff", fontSize:15, fontWeight:700, cursor:"pointer", opacity: savingSettings ? 0.5 : 1, marginTop:8,
            }}>{savingSettings ? "Saving…" : "Save Settings"}</button>

            {/* Danger zone: End Season. Hidden for World Cup / tournament leagues
                (they end via the tournament flow) and for leagues already ended. */}
            {!isWorldCup2026League?.(league) && !(league.isEnded || league.status === "ended") && (
              <div style={{ marginTop:22, paddingTop:18, borderTop:"1px solid rgba(255,255,255,0.07)" }}>
                <div style={{ fontSize:11, fontWeight:700, color:"rgba(248,113,113,0.75)", marginBottom:6, textTransform:"uppercase", letterSpacing:"0.07em" }}>End of Season</div>
                <div style={{ fontSize:12.5, color:"rgba(255,255,255,0.40)", lineHeight:1.4, marginBottom:12 }}>
                  Lock final standings and crown the current leader as champion. The league moves to your Past list. You can restart it later.
                </div>
                <button onClick={() => handleEndLeague(activeLeague, league.name)} disabled={endingLeague} style={{
                  width:"100%", padding:"13px", background:"transparent", border:"1.5px solid rgba(248,113,113,0.45)", borderRadius:12,
                  color:"#f87171", fontSize:14, fontWeight:700, cursor:"pointer", opacity: endingLeague ? 0.5 : 1,
                }}>{endingLeague ? "Ending…" : "End Season"}</button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ═══ CAPTAIN: INSIGHTS MODAL ═══ */}
      {showCaptainInsights && (
        <div onClick={() => setShowCaptainInsights(false)} style={{ position:"fixed", inset:0, zIndex:100, background:"rgba(0,0,0,0.55)", display:"flex", alignItems:"flex-end" }}>
          <div onClick={e=>e.stopPropagation()} style={{ width:"100%", maxWidth:640, margin:"0 auto", background:T.card, borderRadius:"20px 20px 0 0", padding:"24px 20px calc(80px + env(safe-area-inset-bottom, 0px))", maxHeight:"85vh", overflowY:"auto", minHeight:0, WebkitOverflowScrolling:"touch", overscrollBehavior:"contain" }}>
            <div style={{ width:36, height:4, background:T.border, borderRadius:2, margin:"0 auto 20px" }}/>
            <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", gap:10, marginBottom:20 }}>
              <div style={{ display:"flex", alignItems:"center", gap:10, flex:1, minWidth:0 }}>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink:0, opacity:0.85 }}>
                  <line x1="18" y1="20" x2="18" y2="10"/>
                  <line x1="12" y1="20" x2="12" y2="4"/>
                  <line x1="6"  y1="20" x2="6"  y2="14"/>
                </svg>
                <div style={{ minWidth:0 }}>
                  <div style={{ fontSize:17, fontWeight:700, color:"#fff" }}>League Insights</div>
                  <div style={{ fontSize:12, color:"rgba(255,255,255,0.35)", marginTop:2 }}>This week's activity</div>
                </div>
              </div>
              <button onClick={() => setShowCaptainInsights(false)} aria-label="Close" style={{
                flexShrink:0, width:32, height:32, borderRadius:10,
                background:"rgba(255,255,255,0.05)", border:"1px solid rgba(255,255,255,0.08)",
                color:"rgba(255,255,255,0.5)", cursor:"pointer",
                display:"flex", alignItems:"center", justifyContent:"center",
              }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
            {(() => {
              const ins = getCaptainInsights(activeLeague);
              return (<>
                {/* Stats grid */}
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:18 }}>
                  {[
                    { label:"Active Pickers", val:`${ins.activePickers} / ${ins.totalMembers}`, color: ins.activePickers >= ins.totalMembers * 0.5 ? "#4ade80" : "#f87171", sub: ins.activePickers >= ins.totalMembers * 0.5 ? "Healthy engagement" : "Low engagement" },
                    { label:"Picks This Week", val:String(ins.recentPicks), color:"#fff", sub:`${ins.totalMembers > 0 ? (ins.recentPicks / ins.totalMembers).toFixed(1) : 0} per member` },
                    { label:"Current Leader", val: ins.leader ? (ins.leader.username || ins.leader.name || "—") : "—", color:"#FFD700", sub: ins.leader ? (isNonWagerRoom ? `${(ins.leader.wins||0).toLocaleString()} wins` : `${(ins.leader.balance||0).toLocaleString()} pts`) : "" },
                    { label:"Biggest Win", val: ins.biggestWin ? `+${(ins.biggestWin.payout||0).toLocaleString()}` : "—", color: ins.biggestWin ? "#4ade80" : "rgba(255,255,255,0.25)", sub: ins.biggestWin ? (ins.biggestWin.username || "Unknown") : "No wins yet" },
                  ].map(({ label, val, color, sub }) => (
                    <div key={label} style={{
                      padding:"16px 14px", borderRadius:14, background:"rgba(255,255,255,0.03)", border:"1px solid rgba(255,255,255,0.05)",
                    }}>
                      <div style={{ fontSize:9, fontWeight:700, color:"rgba(255,255,255,0.40)", letterSpacing:"0.1em", fontFamily:FM, marginBottom:6 }}>{label.toUpperCase()}</div>
                      <div style={{ fontFamily:FM, fontSize:20, fontWeight:800, color, letterSpacing:"-0.3px" }}>{val}</div>
                      {sub && <div style={{ fontSize:10, color:"rgba(255,255,255,0.40)", marginTop:4 }}>{sub}</div>}
                    </div>
                  ))}
                </div>

                {/* Inactive members */}
                {ins.inactive.length > 0 && (
                  <div style={{ marginBottom:16 }}>
                    <div style={{ fontSize:11, fontWeight:700, color:"rgba(255,255,255,0.40)", letterSpacing:"0.08em", marginBottom:8, fontFamily:FM }}>HASN'T PICKED THIS WEEK</div>
                    <div style={{ display:"flex", flexWrap:"wrap", gap:6 }}>
                      {ins.inactive.map(m => (
                        <div key={m.id} style={{
                          padding:"6px 12px", borderRadius:10, background:"rgba(239,68,68,0.06)", border:"1px solid rgba(239,68,68,0.10)",
                          fontSize:12, fontWeight:600, color:"rgba(255,255,255,0.45)",
                        }}>{m.username || m.name || "Unknown"}</div>
                      ))}
                    </div>
                    <button onClick={() => {
                      const names = ins.inactive.map(m => m.username || m.name || "?").join(", ");
                      setAnnounceDraft(`Hey ${names} — get your picks in before lock.`);
                      setShowCaptainInsights(false);
                      setShowAnnounce(true);
                    }} style={{
                      marginTop:10, padding:"9px 16px", borderRadius:10, border:"none", cursor:"pointer",
                      background:"rgba(255,107,61,0.10)", color:T.coral, fontSize:12, fontWeight:700,
                    }}>Nudge Inactive Members</button>
                  </div>
                )}
                {ins.inactive.length === 0 && ins.totalMembers > 0 && (
                  <div style={{
                    padding:"14px", borderRadius:12, background:"rgba(74,222,128,0.06)", border:"1px solid rgba(74,222,128,0.10)",
                    textAlign:"center", fontSize:13, color:"#4ade80", fontWeight:600,
                  }}>All members are active this week</div>
                )}
              </>);
            })()}
          </div>
        </div>
      )}

        {showInvite && (
          <div onClick={() => { setShowInvite(null); setJustCreatedLeague(false); }} style={{
            position:"fixed", inset:0, zIndex:100, background:"rgba(0,0,0,0.45)",
            display:"flex", alignItems:"flex-end",
          }}>
            <div onClick={e=>e.stopPropagation()} style={{
              width:"100%", maxWidth:640, margin:"0 auto",
              background:T.card, borderRadius:"20px 20px 0 0", paddingTop:24, paddingLeft:20, paddingRight:20, paddingBottom:"calc(80px + env(safe-area-inset-bottom, 0px))", maxHeight:"calc(92vh - env(safe-area-inset-top, 0px))", overflowY:"auto", minHeight:0, WebkitOverflowScrolling:"touch", overscrollBehavior:"contain",
            }}>
              <div style={{ width:36, height:4, background:T.border, borderRadius:2, margin:"0 auto 20px" }}/>
              {justCreatedLeague ? (
                <>
                  <div style={{ fontSize:18, fontWeight:800, marginBottom:4, fontFamily:FD, letterSpacing:"-0.01em" }}>
                    🎉 League created
                  </div>
                  <div style={{ fontSize:13, color:T.inkSec, marginBottom:16 }}>
                    Now get your crew in — a league's no fun solo. Share the code, link, or QR to invite friends to <strong>{league.name}</strong>.
                  </div>
                </>
              ) : (
                <>
                  <div style={{ fontSize:17, fontWeight:700, marginBottom:4 }}>Invite Friends</div>
                  <div style={{ fontSize:13, color:T.inkSec, marginBottom:16 }}>Share the code, link, or QR to invite friends to <strong>{league.name}</strong></div>
                </>
              )}

              {/* QR Code */}
              <div style={{
                padding:16, background:"#fff", borderRadius:14, marginBottom:14,
                display:"flex", flexDirection:"column", alignItems:"center", gap:10,
              }}>
                <img
                  src={`https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(`https://torchpicks.com?join=${league.code}`)}&color=0D0D0F&bgcolor=FFFFFF&margin=0`}
                  alt="QR Code"
                  width={180} height={180}
                  style={{ borderRadius:8 }}
                />
                <div style={{ fontSize:10, fontWeight:700, color:"#666", letterSpacing:"0.1em" }}>SCAN TO JOIN</div>
              </div>

              {/* League Code */}
              <div style={{ padding:"14px", background:T.cardAlt, borderRadius:12, textAlign:"center", marginBottom:14 }}>
                <div style={{ fontSize:11, color:T.inkMuted, marginBottom:4 }}>League Code</div>
                <div style={{ fontSize:28, fontWeight:700, letterSpacing:"0.15em", color:T.coral }}>{league.code}</div>
              </div>
              <button onClick={() => handleInvite(league)} style={{
                width:"100%", padding:"13px", marginBottom:10,
                background: T.coral,
                border:"none", borderRadius:12, color:"#fff", fontSize:14, fontWeight:700, cursor:"pointer",
                boxShadow:"0 2px 8px rgba(0,0,0,0.3)",
                display:"flex", alignItems:"center", justifyContent:"center", gap:8,
              }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/>
                  <line x1="8.6" y1="13.5" x2="15.4" y2="17.5"/><line x1="15.4" y1="6.5" x2="8.6" y2="10.5"/>
                </svg>
                Share invite
              </button>
              <button onClick={() => copyCode(league.code)} style={{
                width:"100%", padding:"13px",
                background: copiedCode ? T.green : "rgba(255,255,255,0.05)",
                border:`1px solid ${copiedCode ? "transparent" : T.border}`, borderRadius:12,
                color: copiedCode ? "#fff" : T.ink, fontSize:14, fontWeight:700, cursor:"pointer",
                transition:"background 0.2s",
              }}>{copiedCode ? "✓ Copied!" : "Copy invite link"}</button>
            </div>
          </div>
        )}

        {/* Modals */}
        {viewingMember && (
          <UserProfileModal
            profileUid={viewingMember.uid}
            profileUsername={viewingMember.username}
            currentUser={user}
            leagueId={activeLeague}
            leagueName={league?.name || "League"}
            viewerLeagues={userLeagues}
            onClose={() => setViewingMember(null)}
            isBlocked={!!blockedUsers[viewingMember.uid]}
            onBlock={() => handleBlockUser(viewingMember.uid, viewingMember.username)}
            onUnblock={() => handleUnblockUser(viewingMember.uid)}
          />
        )}
        {selectedLeaguePick && (
          <PickDetailModal
            pick={selectedLeaguePick}
            user={user}
            userLeagues={leagues}
            onClose={() => setSelectedLeaguePick(null)}
          />
        )}
        {/* EULA Modal — Apple Guideline 1.2 */}
        {showEula && <EulaModal onAccept={handleAcceptEula} />}
        {/* Report Content Modal — Apple Guideline 1.2 */}
        {reportModal && (
          <ReportContentModal
            {...reportModal}
            currentUser={user}
            onClose={() => setReportModal(null)}
            onReported={() => window.torchToast?.("Report submitted — we'll review within 24 hours.", "info")}
          />
        )}
      </div>
    );
  }
  // -- LEAGUE LIST VIEW --------------------------------------------------------
  return (
    <div style={{ maxWidth:640, margin:"0 auto", background:T.bg, minHeight:"100vh", paddingBottom:"calc(90px + env(safe-area-inset-bottom, 0px))" }}>

      {/* ── World Cup bracket overlay (full-screen, position:fixed) ────────── */}
      {bracketOpen && (
        <WorldCupBracket
          user={user}
          username={username}
          league={bracketOpen}
          onClose={() => setBracketOpen(null)}
        />
      )}

      {/* ── HERO ──────────────────────────────────────────────────────────── */}
      <div style={{
        position:"relative",
        background:T.bg,
        borderBottom:"1px solid rgba(255,255,255,0.06)",
      }}>
        <div style={{ position:"relative", padding:"18px 16px 14px", zIndex:1 }}>
          {/* Masthead */}
          <div style={{ fontFamily:FD, fontSize:28, fontWeight:700, color:"#fff", letterSpacing:"-0.03em", lineHeight:1, marginBottom:16 }}>
            The Den
          </div>

          {/* Stats — divider-separated, size-driven (broadcast-graphics style) */}
          {!loading && leagues.length > 0 && (() => {
            const bestRank = leagues.reduce((best, l) => l.userRank && l.userRank < best ? l.userRank : best, Infinity);
            const totalWins = leagues.reduce((sum, l) => sum + (l.wins ?? 0), 0);
            const totalMembers = leagues.reduce((sum, l) => sum + (l.memberCount ?? 0), 0);
            const rankColor = bestRank === 1 ? "#FFD700" : bestRank <= 3 ? "#f59e0b" : "#fff";
            const cells = [
              { v: leagues.length, label: "Leagues", color: "#fff" },
              ...(bestRank !== Infinity ? [{ v: `#${bestRank}`, label: "Best", color: rankColor }] : []),
              { v: totalWins, label: "Wins", color: "#fff" },
              { v: totalMembers, label: "Rivals", color: "#fff" },
            ];
            return (
              <div style={{ display:"flex", gap:14, alignItems:"stretch" }}>
                {cells.map((c, i) => (
                  <Fragment key={c.label}>
                    {i > 0 && <div style={statDivider} />}
                    <div>
                      <div style={statNum(c.color)}>{c.v}</div>
                      <div style={statLabel}>{c.label}</div>
                    </div>
                  </Fragment>
                ))}
              </div>
            );
          })()}
        </div>
      </div>

      <div style={{ padding:"14px 14px 0" }}>

        {/* Tab bar */}
        <div style={{ display:"flex", marginBottom:14, gap:0, borderBottom:"1px solid rgba(255,255,255,0.05)" }}>
          {[["mine","My Leagues"],["discover","Discover"]].map(([val, label]) => (
            <button key={val} onClick={() => setLeagueTab(val)} style={{
              padding:"8px 14px", border:"none", cursor:"pointer", background:"transparent",
              color: leagueTab===val ? "#fff" : "rgba(255,255,255,0.3)",
              fontSize:12, fontWeight: leagueTab===val ? 700 : 500,
              borderBottom: leagueTab===val ? `2px solid ${T.coral}` : "2px solid transparent",
              marginBottom:-1, transition:"all 0.2s", letterSpacing:"0.01em",
            }}>{label}</button>
          ))}
        </div>

        {leagueTab === "mine" && (<>

          {/* Create + Join — slim, less dominant */}
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8, marginBottom:14 }}>
            <button onClick={() => { haptic.light(); setShowCreate(true); setShowJoin(false); }} style={{
              padding:"12px 12px", borderRadius:12, cursor:"pointer", textAlign:"left",
              background:"linear-gradient(135deg, rgba(255,107,61,0.18) 0%, rgba(216,67,21,0.10) 100%)",
              border:"1px solid rgba(255,107,61,0.30)",
              position:"relative", overflow:"hidden",
              display:"flex", alignItems:"center", gap:10,
            }}>
              <div style={{ width:28, height:28, borderRadius:8, background:"rgba(255,107,61,0.22)", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                <span style={{ fontSize:18, color:"#fff", lineHeight:1 }}>+</span>
              </div>
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ fontSize:12, fontWeight:700, color:"#fff", letterSpacing:"-0.01em" }}>Create</div>
                <div style={{ fontSize:9.5, color:"rgba(255,255,255,0.50)", marginTop:1 }}>You're Commissioner</div>
              </div>
            </button>
            <button onClick={() => { haptic.light(); setShowJoin(true); setShowCreate(false); }} style={{
              padding:"12px 12px", borderRadius:12, cursor:"pointer", textAlign:"left",
              background:"rgba(255,255,255,0.03)",
              border:"1px solid rgba(255,255,255,0.08)",
              position:"relative", overflow:"hidden",
              display:"flex", alignItems:"center", gap:10,
            }}>
              <div style={{ width:28, height:28, borderRadius:8, background:"rgba(255,255,255,0.04)", border:"1px solid rgba(255,255,255,0.06)", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                <span style={{ fontSize:14, color:T.coral, lineHeight:1 }}>→</span>
              </div>
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ fontSize:12, fontWeight:700, color:"#fff", letterSpacing:"-0.01em" }}>Join</div>
                <div style={{ fontSize:9.5, color:"rgba(255,255,255,0.40)", marginTop:1 }}>Enter a code</div>
              </div>
            </button>
          </div>

          {!loading && leagues.length > 0 && (
            <div style={{ marginBottom:12 }}>
              <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between" }}>
                <div style={{ fontSize:9.5, fontWeight:700, color:"rgba(255,255,255,0.32)", letterSpacing:"0.12em", textTransform:"uppercase", fontFamily:FM }}>
                  Active · {leagues.filter(l => !l.isEnded && l.status !== "ended").length}
                </div>
                <button onClick={() => setShowCreate(true)} style={{ background:"none", border:"none", cursor:"pointer", fontSize:9.5, fontWeight:700, color:T.coral, padding:"2px 0", letterSpacing:"0.08em", textTransform:"uppercase" }}>+ New</button>
              </div>
            </div>
          )}

          {loading && (
            <div style={{ padding:"4px 0" }}>
              {[0,1].map(i => <LeagueCardSkeleton key={i} />)}
            </div>
          )}

          {!loading && leagues.length === 0 && (
            <div style={{ padding:"32px 4px 28px" }}>
              <div style={{ fontFamily:FD, fontSize:24, fontWeight:800, color:"#fff", letterSpacing:"-0.02em", lineHeight:1.1, marginBottom:8 }}>
                You're not in a league yet
              </div>
              <div style={{ fontSize:13, color:"rgba(255,255,255,0.50)", lineHeight:1.5, marginBottom:22, maxWidth:320 }}>
                Create your own to be commissioner, or join one with a code from a friend.
              </div>
              <div style={{ display:"flex", flexDirection:"column", gap:8, maxWidth:340 }}>
                <button onClick={() => setShowCreate(true)} style={{
                  padding:"14px 16px", borderRadius:12, border:"none", cursor:"pointer",
                  background:T.coral,
                  color:"#fff", fontSize:14, fontWeight:700, letterSpacing:"-0.005em",
                  boxShadow:"0 4px 14px -2px rgba(255,107,61,0.30)",
                  display:"flex", alignItems:"center", justifyContent:"space-between",
                }}>
                  <span>Create a league</span>
                  <span style={{ fontSize:16 }}>→</span>
                </button>
                <button onClick={() => setShowJoin(true)} style={{
                  padding:"13px 16px", borderRadius:12, cursor:"pointer",
                  border:`1px solid ${T.border}`,
                  background:"rgba(255,255,255,0.03)",
                  color:T.ink, fontSize:13, fontWeight:600, letterSpacing:"-0.005em",
                  display:"flex", alignItems:"center", justifyContent:"space-between",
                }}>
                  <span>Join with a code</span>
                  <span style={{ fontSize:16, color:T.inkSec }}>→</span>
                </button>
              </div>
            </div>
          )}

          {/* League cards — split into Active and Past Seasons */}
          {!loading && (() => {
            const activeLeagues = leagues.filter(l => !l.isEnded && l.status !== "ended");
            const endedLeagues = leagues.filter(l => l.isEnded || l.status === "ended");
            return (<>
              {activeLeagues.map((league, i) => {
            const daysLeft = league.endDate ? (() => {
              const d = league.endDate.toDate ? league.endDate.toDate() : new Date(league.endDate);
              return Math.ceil((d - new Date()) / 86400000);
            })() : null;
            const myRankDisplay = league.userRank;
            const myW = league.wins ?? 0;
            const myL = league.losses ?? 0;
            const hasRecord = (myW + myL) > 0;
            const winRate = hasRecord ? Math.round(myW / (myW + myL) * 100) : null;
            const isFirst = myRankDisplay === 1;
            const isPodium = myRankDisplay && myRankDisplay <= 3;
            const rankColor = isFirst ? "#FFD700" : myRankDisplay===2 ? "#C0C0C0" : myRankDisplay===3 ? "#CD7F32" : "rgba(255,255,255,0.6)";

            const hasUnread = (() => {
              try {
                const lastVisited = parseInt(localStorage.getItem(`torch_visited_${league.id}`) || "0");
                if (!lastVisited) return false;
                const leagueActivity = activity[league.id] || [];
                return leagueActivity.some(ev => {
                  const evTs = ev.createdAt?.seconds ? ev.createdAt.seconds * 1000 : ev.createdAt?.toDate?.().getTime?.() ?? 0;
                  return evTs > lastVisited;
                });
              } catch { return false; }
            })();

            const leagueInitial = (league.name || "T").trim().charAt(0).toUpperCase();
            // PICKEM-PTS (Leagues page): non-wager leagues score on wins, balance is 0 by design.
            const cardMode = league.scoringMode || "wager";
            const cardNonWager = cardMode === "pickem" || cardMode === "survivor";
            const cardBig = cardNonWager ? (league.wins ?? 0) : (league.balance ?? 0);
            const cardUnit = cardNonWager ? "Wins" : "pts";
            const balColor = cardNonWager ? "#fff" : (league.balance??0) >= 0 ? "#fff" : "#FCA5A5";
            const isWc = isWorldCup2026League(league);

            // Single left-rule accent encodes state (flat, data-forward):
            //   gold = leading · teal = WC pool · coral = unread/captain · faint = default
            const accent = isFirst
              ? "#FFD700"
              : isWc
              ? "#14B8A6"
              : hasUnread
              ? "#FF6B3D"
              : league.isCommissioner
              ? "rgba(255,107,61,0.55)"
              : "rgba(255,255,255,0.10)";

            return (
              <button key={league.id}
                onClick={() => enterLeague(league)}
                className="glass-hover"
                style={{
                  display:"flex", alignItems:"center", gap:10, padding:"10px 12px",
                  cursor:"pointer", width:"100%", textAlign:"left",
                  marginBottom:6, animation:"fadeUp 0.3s ease both", animationDelay:`${i*50}ms`,
                  background: T.card,
                  border:"1px solid rgba(255,255,255,0.06)",
                  borderLeft:`3px solid ${accent}`,
                  borderRadius:"0 12px 12px 0",
                }}
              >
                {/* Compact league avatar — 32px */}
                <div style={{
                  position:"relative", width:32, height:32, borderRadius:9, flexShrink:0,
                  background:"rgba(255,255,255,0.04)",
                  border:"1px solid rgba(255,255,255,0.08)",
                  display:"flex", alignItems:"center", justifyContent:"center",
                  overflow:"hidden",
                }}>
                  <span style={{
                    position:"relative", fontFamily:FD, fontSize:13, fontWeight:700,
                    color: isWc ? "#5eead4" : isPodium ? "#E5B459" : "rgba(255,255,255,0.85)",
                    letterSpacing:"-0.02em",
                  }}>{leagueInitial}</span>
                  {myRankDisplay && (
                    <span style={{
                      position:"absolute", bottom:-2, right:-2,
                      minWidth:13, height:13, padding:"0 3px", borderRadius:7,
                      background:T.bg, border:`1px solid ${rankColor}`,
                      fontFamily:FM, fontSize:9, fontWeight:700, color: rankColor,
                      display:"flex", alignItems:"center", justifyContent:"center",
                      letterSpacing:"-0.02em", lineHeight:1, ...tnum,
                    }}>#{myRankDisplay}</span>
                  )}
                </div>

                {/* Name + meta */}
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ display:"flex", alignItems:"center", gap:5 }}>
                    <span style={{ fontSize:13, fontWeight:600, color:"#fff", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", letterSpacing:"-0.005em" }}>{league.name}</span>
                    {league.isCommissioner && <span style={{ fontSize:9, padding:"1px 4px", borderRadius:3, background:"rgba(255,107,61,0.10)", color:T.coral, fontWeight:700, letterSpacing:"0.04em" }}>CAPT</span>}
                    {hasUnread && <span style={{ width:6, height:6, borderRadius:"50%", background:T.coral, flexShrink:0, display:"inline-block", animation:"pulse 1.4s ease infinite" }}/>}
                  </div>
                  <div style={{ fontSize:10.5, color:"rgba(255,255,255,0.40)", marginTop:2, display:"flex", alignItems:"center", gap:6 }}>
                    {!isWc && hasRecord && <span style={{ color: winRate >= 50 ? "#34d399" : "rgba(255,255,255,0.5)", fontWeight:600, ...tnum }}>{myW}–{myL}</span>}
                    <span>{league.memberCount} {league.memberCount === 1 ? "member" : "members"}</span>
                    {daysLeft !== null && daysLeft > 0 && daysLeft <= 14 && (
                      <>
                        <span style={{ width:2, height:2, borderRadius:"50%", background:"rgba(255,255,255,0.32)" }}/>
                        <span style={{ color: daysLeft<=7 ? "#FF8A65" : "rgba(255,255,255,0.40)", fontWeight:600 }}>{daysLeft}d left</span>
                      </>
                    )}
                  </div>
                </div>

                {/* Right value — WC countdown or balance */}
                {isWc ? (() => {
                  let daysToKick = null;
                  try {
                    const ms = new Date(WORLD_CUP_2026.predictionLockAt).getTime() - Date.now();
                    daysToKick = Math.ceil(ms / (1000 * 60 * 60 * 24));
                  } catch {}
                  const isLive = daysToKick != null && daysToKick < 0;
                  const big = isLive ? "LIVE"
                            : daysToKick == null   ? "—"
                            : daysToKick === 0     ? "TODAY"
                            : daysToKick === 1     ? "1d"
                                                   : `${daysToKick}d`;
                  const small = isLive ? "Tournament" : "to lock";
                  return (
                    <div style={{ textAlign:"right", flexShrink:0 }}>
                      <div style={{
                        fontFamily:FD, fontSize:14, fontWeight:700, color:"#14B8A6",
                        letterSpacing:"-0.02em", lineHeight:1, ...tnum,
                      }}>{big}</div>
                      <div style={{ fontSize:9, color:"rgba(255,255,255,0.32)", fontWeight:700, letterSpacing:"0.10em", textTransform:"uppercase", marginTop:2 }}>{small}</div>
                    </div>
                  );
                })() : (
                  <div style={{ textAlign:"right", flexShrink:0 }}>
                    <div style={{ fontFamily:FD, fontSize:14, fontWeight:700, color:balColor, letterSpacing:"-0.02em", lineHeight:1, ...tnum }}>
                      {cardBig.toLocaleString()}
                    </div>
                    <div style={{ fontSize:9, color:"rgba(255,255,255,0.32)", fontWeight:700, letterSpacing:"0.10em", textTransform:"uppercase", marginTop:2 }}>{cardUnit}</div>
                  </div>
                )}
              </button>
            );
          })}

              {/* ── PAST SEASONS SECTION ── */}
              {endedLeagues.length > 0 && (
                <div style={{ marginTop:32 }}>
                  <div style={{ marginBottom:14, padding:"0 2px" }}>
                    <div style={{ fontSize:11, fontWeight:700, color:"rgba(255,255,255,0.42)", letterSpacing:"0.14em", textTransform:"uppercase", fontFamily:FM }}>
                      Past · {endedLeagues.length}
                    </div>
                    <div style={{ width:22, height:2, background:"rgba(255,107,61,0.55)", marginTop:7, borderRadius:1 }}/>
                  </div>
                  {endedLeagues.map((league, i) => {
                    const champ = league.champion || league.winner;
                    return (
                      <div key={league.id}
                        className="glass-hover"
                        onClick={() => enterLeague(league)}
                        style={{
                          marginBottom:8, cursor:"pointer",
                          animation:"fadeUp 0.3s ease both", animationDelay:`${i*50}ms`,
                          borderRadius:16, overflow:"hidden",
                          background:"linear-gradient(155deg, rgba(217,164,65,0.04), rgba(14,14,18,1) 100%)",
                          border:"1px solid rgba(217,164,65,0.10)",
                          position:"relative",
                        }}
                      >
                        <div style={{ height:2, background:"linear-gradient(90deg, rgba(217,164,65,0.3), transparent)" }}/>
                        <div style={{ padding:"12px 16px", display:"flex", alignItems:"center", gap:12 }}>
                          {/* Trophy icon */}
                          <div style={{
                            flexShrink:0, width:44, height:44, borderRadius:14,
                            background:"linear-gradient(135deg, rgba(217,164,65,0.12), rgba(217,164,65,0.04))",
                            border:"1px solid rgba(217,164,65,0.15)",
                            display:"flex", alignItems:"center", justifyContent:"center",
                          }}>
                            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#D9A441" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6"/>
                              <path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18"/>
                              <path d="M4 22h16"/>
                              <path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22"/>
                              <path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22"/>
                              <path d="M18 2H6v7a6 6 0 0 0 12 0V2Z"/>
                            </svg>
                          </div>

                          <div style={{ flex:1, minWidth:0 }}>
                            <div style={{ fontSize:14, fontWeight:700, color:"rgba(255,255,255,0.7)", letterSpacing:"-0.2px", marginBottom:3, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                              {league.name}
                            </div>
                            <div style={{ fontSize:11, color:"rgba(255,255,255,0.42)" }}>
                              {champ
                                ? `Won by ${champ.username || champ.name || "?"} · ${((league.scoringMode === "pickem" || league.scoringMode === "survivor") ? (champ.wins || 0) : (champ.balance || 0)).toLocaleString()} ${(league.scoringMode === "pickem" || league.scoringMode === "survivor") ? "wins" : "pts"}`
                                : `${league.memberCount} members · Season ended`}
                            </div>
                          </div>

                          <div style={{
                            padding:"6px 10px", borderRadius:16,
                            background:"rgba(255,255,255,0.04)",
                            border:"1px solid rgba(255,255,255,0.08)",
                          }}>
                            <span style={{ fontSize:11, fontWeight:600, color:"rgba(255,255,255,0.4)" }}>View</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </>);
          })()}
        </>)}

        {/* Discover tab */}
        {leagueTab === "discover" && (
          <div>
            <div style={{ marginBottom:16 }}>
              <div style={{ fontSize:13, color:"rgba(255,255,255,0.50)", lineHeight:1.5 }}>
                Public leagues across sports. Join any with one tap.
              </div>
            </div>
            {loadingPublic && (
              <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                {[1,2,3].map(i => <div key={i} style={{ height:90, borderRadius:14, background:"rgba(255,255,255,0.04)", animation:"pulse 1.4s ease infinite" }}/>)}
              </div>
            )}
            {!loadingPublic && publicLeagues.length === 0 && (
              <div style={{ padding:"40px 0", textAlign:"center" }}>
                <div style={{ fontFamily:FD, fontSize:16, fontWeight:700, color:"rgba(255,255,255,0.55)", letterSpacing:"-0.005em", marginBottom:6 }}>No public leagues</div>
                <div style={{ fontSize:12, color:"rgba(255,255,255,0.32)", maxWidth:260, margin:"0 auto", lineHeight:1.5 }}>Create one and make it public to appear here.</div>
              </div>
            )}
            {publicLeagues.map((l, i) => {
              const fillPct = l.maxMembers ? Math.round((l.memberCount / l.maxMembers) * 100) : 0;
              const almostFull = fillPct >= 80;
              const isFull = l.memberCount >= l.maxMembers;
              // Featured tier: urgency (near-capacity) — promotes action
              const featured = almostFull && !isFull;
              return (
                <div key={l.id} style={{
                  marginBottom: featured ? 14 : 12,
                  background: featured
                    ? "linear-gradient(155deg,rgba(255,107,61,0.10),rgba(255,107,61,0.02) 60%,rgba(14,14,18,1) 100%)"
                    : T.card,
                  border: `${featured ? 2 : 1}px solid ${featured ? "rgba(255,107,61,0.48)" : T.border}`,
                  borderRadius:16, overflow:"hidden",
                  animation:"fadeUp 0.2s ease both", animationDelay:`${i*40}ms`,
                  boxShadow: featured ? "0 6px 24px rgba(255,107,61,0.12)" : "none",
                  position:"relative",
                }}>
                  {featured && (
                    <div style={{ height:2, background:`linear-gradient(90deg,${T.coral},${T.coralDeep},transparent)` }}/>
                  )}
                  <div style={{ padding:"16px 16px 14px" }}>
                    <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", gap:12, marginBottom:12 }}>
                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={{ fontFamily:FD, fontSize:20, fontWeight:700, color:"#fff", letterSpacing:"-0.3px", marginBottom:5, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", textTransform:"uppercase" }}>{l.name}</div>
                        <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                          <span style={{ fontSize:11, color:"rgba(255,255,255,0.4)" }}>by {l.commissionerName}</span>
                          {l.commissionerWinRate != null && (
                            <span style={{ fontSize:11, fontWeight:700, fontFamily:FM, color: l.commissionerWinRate >= 55 ? T.green : "rgba(255,255,255,0.4)" }}>
                              {l.commissionerWinRate}%
                            </span>
                          )}
                        </div>
                      </div>
                      <button onClick={async () => {
                        if (isFull) return;
                        setJoiningPublic(l.id);
                        try {
                          const { id } = await joinLeagueByCode(user.uid, username, l.code);
                          await addLeagueToUser(user.uid, id);
                          logTorchEvent("league_joined", { method: "public" });
                          fetchLeagues(); setLeagueTab("mine");
                          window.torchToast?.(`Joined ${l.name}!`, "success");
                        } catch (e) { window.torchToast?.(e.message, "error"); }
                        finally { setJoiningPublic(null); }
                      }} disabled={joiningPublic===l.id || isFull} style={{
                        padding:"9px 18px",
                        background: isFull ? "rgba(255,255,255,0.06)" : T.coral,
                        border: isFull ? `1px solid ${T.border}` : "none",
                        borderRadius:20,
                        color: isFull ? T.inkMuted : "#fff",
                        fontSize:12, fontWeight:700, cursor: isFull ? "default" : "pointer",
                        opacity: joiningPublic===l.id ? 0.6 : 1, flexShrink:0,
                        boxShadow: isFull ? "none" : "0 2px 10px rgba(255,107,61,0.25)",
                        letterSpacing:"0.04em", textTransform:"uppercase",
                      }}>{joiningPublic===l.id ? "Joining…" : isFull ? "Full" : "Join →"}</button>
                    </div>
                    {/* Member fill bar */}
                    <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                      <div style={{ flex:1, height:4, borderRadius:2, background:"rgba(255,255,255,0.07)", overflow:"hidden" }}>
                        <div style={{
                          width:`${fillPct}%`, height:"100%", borderRadius:2,
                          background: featured
                            ? `linear-gradient(90deg,${T.coral},${T.coralDeep})`
                            : `linear-gradient(90deg,rgba(255,107,61,0.55),rgba(216,67,21,0.55))`,
                          transition:"width 0.4s ease",
                        }}/>
                      </div>
                      <span style={{ fontSize:10, fontWeight:700, fontFamily:FM, color: featured ? T.coral : "rgba(255,255,255,0.35)", flexShrink:0, letterSpacing:"0.04em" }}>
                        {l.memberCount}/{l.maxMembers}
                        {featured && " · ALMOST FULL"}
                        {isFull && " · FULL"}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>


      {/* Create modal */}
      {showCreate && (
        <div onClick={() => setShowCreate(false)} style={{ position:"fixed", inset:0, zIndex:100, background:"rgba(0,0,0,0.45)", display:"flex", alignItems:"flex-end" }}>
          <div onClick={e=>e.stopPropagation()} style={{ width:"100%", maxWidth:640, margin:"0 auto", background:T.card, borderRadius:"20px 20px 0 0", paddingTop:24, paddingLeft:20, paddingRight:20, paddingBottom:"calc(80px + env(safe-area-inset-bottom, 0px))", maxHeight:"calc(92vh - env(safe-area-inset-top, 0px))", overflowY:"auto", minHeight:0, WebkitOverflowScrolling:"touch", overscrollBehavior:"contain" }}>
            <div style={{ width:36, height:4, background:T.border, borderRadius:2, margin:"0 auto 16px" }}/>

            {/* ─── STEP 0 — PATH FORK ───────────────────────────────────────
                The campaign sells one thing: "run your NFL league." Making that
                the first choice — ahead of an 11-tile sport grid where NFL sits
                as a peer of MMA — is the whole point of this screen. The NFL
                path then skips the sport picker entirely (an NFL league's sport
                is not a question) and leads with the format instead. */}
            {createPath === null && (() => {
              const s = getNflSeasonState();
              const kickoffNote = s.phase === "preseason"
                ? `Kickoff in ${s.daysToKickoff} day${s.daysToKickoff === 1 ? "" : "s"}`
                : s.phase === "week" ? `Week ${s.week} of ${NFL_2026_WEEKS}` : null;
              return (
                <div>
                  <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:14 }}>
                    <button onClick={() => setShowCreate(false)} style={{
                      width:30, height:30, borderRadius:8, border:"1px solid rgba(255,255,255,0.08)",
                      background:"rgba(255,255,255,0.03)", color:T.inkSec, cursor:"pointer",
                      display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0,
                    }} aria-label="Close">
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                    </button>
                  </div>

                  <div style={{ fontFamily:FD, fontSize:22, fontWeight:800, letterSpacing:"-0.01em", color:T.ink, lineHeight:1.1 }}>
                    Start a league
                  </div>
                  <div style={{ fontSize:12, color:T.inkMuted, marginTop:4, marginBottom:18, lineHeight:1.4 }}>
                    Run the NFL season with your crew, or build something custom.
                  </div>

                  {/* NFL path — the hero. One tap configures template, sport,
                      dates and name; the wizard resumes at the schedule step. */}
                  <button
                    onClick={() => {
                      haptic.light();
                      setCreateErr("");
                      setCreatePath("nfl");
                      // Seed everything step 1 would have collected.
                      if (NFL_TEMPLATE) {
                        setSelectedTemplate(NFL_TEMPLATE);
                        if (NFL_TEMPLATE.endDate)   setCreateEndDate(NFL_TEMPLATE.endDate);
                        if (NFL_TEMPLATE.startDate) setCreateStartDate(NFL_TEMPLATE.startDate);
                        if (!createName.trim())     setCreateName(NFL_TEMPLATE.name);
                      }
                      setTimeFrameType("event");
                      setAllowedSports(["americanfootball_nfl"]);
                      // Pick'em is the format this path exists to sell — and it's
                      // NFL-only, so this is the one path where it's valid.
                      setScoringMode("pickem");
                      setCreateStep(2);
                    }}
                    className="no-press"
                    style={{
                      width:"100%", textAlign:"left", cursor:"pointer",
                      padding:"18px 16px", marginBottom:10, borderRadius:14,
                      border:`1.5px solid ${T.coral}`,
                      background:"rgba(255,107,61,0.10)",
                      display:"flex", alignItems:"center", gap:14,
                    }}
                  >
                    <div style={{
                      width:46, height:46, borderRadius:12, flexShrink:0,
                      background:"rgba(255,107,61,0.15)",
                      display:"flex", alignItems:"center", justifyContent:"center", fontSize:24,
                    }}>🏈</div>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:3 }}>
                        <span style={{ fontFamily:FD, fontSize:17, fontWeight:800, color:"#fff", letterSpacing:"-0.01em" }}>
                          NFL Season
                        </span>
                        {kickoffNote && (
                          <span style={{
                            fontSize:9, fontWeight:800, letterSpacing:"0.06em", textTransform:"uppercase",
                            color:T.coral, background:"rgba(255,107,61,0.15)", padding:"2px 7px", borderRadius:5,
                            whiteSpace:"nowrap",
                          }}>{kickoffNote}</span>
                        )}
                      </div>
                      <div style={{ fontSize:12, color:"rgba(255,255,255,0.6)", lineHeight:1.4 }}>
                        Pick'em with your crew — every week, all season. Straight up, no wagering.
                      </div>
                    </div>
                    <span style={{ fontSize:18, color:T.coral, flexShrink:0 }}>›</span>
                  </button>

                  {/* Custom path — the original wizard, untouched. */}
                  <button
                    onClick={() => {
                      haptic.light();
                      setCreateErr("");
                      setCreatePath("custom");
                      setCreateStep(1);
                    }}
                    className="no-press"
                    style={{
                      width:"100%", textAlign:"left", cursor:"pointer",
                      padding:"18px 16px", borderRadius:14,
                      border:"1px solid rgba(255,255,255,0.07)",
                      background:"rgba(255,255,255,0.025)",
                      display:"flex", alignItems:"center", gap:14,
                    }}
                  >
                    <div style={{
                      width:46, height:46, borderRadius:12, flexShrink:0,
                      background:"rgba(255,255,255,0.04)",
                      display:"flex", alignItems:"center", justifyContent:"center", fontSize:24,
                    }}>🔥</div>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ fontFamily:FD, fontSize:17, fontWeight:800, color:T.ink, letterSpacing:"-0.01em", marginBottom:3 }}>
                        Custom league
                      </div>
                      <div style={{ fontSize:12, color:T.inkMuted, lineHeight:1.4 }}>
                        Any sport, any format, your rules. Vegas odds or straight picks.
                      </div>
                    </div>
                    <span style={{ fontSize:18, color:T.inkMuted, flexShrink:0 }}>›</span>
                  </button>
                </div>
              );
            })()}

            {/* ─── WIZARD HEADER — progress + step title + back/close ──────── */}
            {createPath !== null && (() => {
              // NFL path skips step 1 (sport picker) — an NFL league's sport
              // isn't a question. The progress bar and "N / N" counter must
              // reflect the path actually being walked, or the NFL path would
              // open on "2 / 4" with the first pip already filled and no way to
              // reach step 1.
              const isNflPath = createPath === "nfl";
              const STEPS = isNflPath
                ? [{ n:2, label:"Schedule" }, { n:3, label:"Picks" }, { n:4, label:"Stakes" }]
                : [{ n:1, label:"Sports" }, { n:2, label:"Schedule" }, { n:3, label:"Picks" }, { n:4, label:"Stakes" }];
              const firstStep = isNflPath ? 2 : 1;
              const stepIdx = STEPS.findIndex(s => s.n === createStep) + 1;
              return (
                <div style={{ marginBottom:18 }}>
                  {/* Progress dots */}
                  <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:14 }}>
                    <button onClick={() => {
                      // Back from the first step of either path returns to the
                      // fork, not straight out of the modal — the fork is a real
                      // screen now and skipping it would strand the user.
                      if (createStep > firstStep) setCreateStep(s => s - 1);
                      else { setCreatePath(null); setCreateErr(""); }
                    }} style={{
                      width:30, height:30, borderRadius:8, border:"1px solid rgba(255,255,255,0.08)",
                      background:"rgba(255,255,255,0.03)", color:T.inkSec, cursor:"pointer",
                      display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0,
                    }} aria-label="Back">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
                    </button>
                    <div style={{ flex:1, display:"flex", alignItems:"center", gap:6, margin:"0 12px" }}>
                      {STEPS.map(s => (
                        <div key={s.n} style={{
                          flex:1, height:3, borderRadius:2,
                          background: s.n <= createStep ? T.coral : "rgba(255,255,255,0.08)",
                          transition:"background 0.2s",
                        }}/>
                      ))}
                    </div>
                    <div style={{ fontSize:10.5, fontWeight:700, color:T.inkMuted, fontFamily:FM, letterSpacing:"0.05em", flexShrink:0 }}>
                      {stepIdx} / {STEPS.length}
                    </div>
                  </div>
                  {/* Step title */}
                  <div style={{ fontFamily:FD, fontSize:22, fontWeight:800, letterSpacing:"-0.01em", color:T.ink, lineHeight:1.1 }}>
                    {createStep === 1 && "Choose your sports"}
                    {createStep === 2 && (isNflPath ? "Name your league" : "Set the schedule")}
                    {createStep === 3 && "How will picks work?"}
                    {createStep === 4 && "Stakes & rules"}
                  </div>
                  <div style={{ fontSize:12, color:T.inkMuted, marginTop:4, lineHeight:1.4 }}>
                    {createStep === 1 && "Pick the sports your league will cover. Tap All Sports for total flexibility."}
                    {createStep === 2 && (isNflPath
                      ? "Name it, and confirm the season dates. Format comes next."
                      : "How long does this league run, and when does it kick off?")}
                    {createStep === 3 && (scoringMode === "wager"
                      ? "Pick types, cadence, and wager bounds — the rules of engagement."
                      : "Choose the format. Pick'em is straight up; Vegas Odds adds wagering.")}
                    {createStep === 4 && (scoringMode === "wager"
                      ? "Starting balance, missed-pick penalty, and the prize on the line."
                      : "Name your stakes — motto and the prize on the line.")}
                  </div>
                </div>
              );
            })()}

            {/* ─── STEP 1 — Sports + League Name ──────────────────────────── */}
            {createPath !== null && createStep === 1 && (() => {
              const WIZARD_SPORTS = [
                { key:"americanfootball_nfl",   label:"NFL",    emoji:"🏈" },
                { key:"basketball_nba",         label:"NBA",    emoji:"🏀" },
                { key:"baseball_mlb",           label:"MLB",    emoji:"⚾" },
                { key:"icehockey_nhl",          label:"NHL",    emoji:"🏒" },
                { key:"americanfootball_ncaaf", label:"NCAAF",  emoji:"🏈" },
                { key:"basketball_ncaab",       label:"NCAAB",  emoji:"🏀" },
                { key:"basketball_wnba",        label:"WNBA",   emoji:"🏀" },
                { key:"soccer",                 label:"Soccer", emoji:"⚽" },
                { key:"golf",                   label:"Golf",   emoji:"⛳" },
                { key:"tennis",                 label:"Tennis", emoji:"🎾" },
                { key:"mma_mixed_martial_arts", label:"MMA",    emoji:"🥊" },
              ];
              const allOn = !allowedSports || allowedSports.length === 0;
              return (
                <div>
                  {/* League name */}
                  <div style={{ marginBottom:18 }}>
                    <div style={{ fontSize:10.5, fontWeight:700, color:"rgba(255,255,255,0.40)", marginBottom:7, textTransform:"uppercase", letterSpacing:"0.10em" }}>
                      League Name <span style={{ color:T.coral }}>*</span>
                    </div>
                    <input value={createName} onChange={e=>setCreateName(e.target.value)} placeholder="e.g. The Pick Six" maxLength={40}
                      onFocus={(e)=>{e.currentTarget.style.borderColor="rgba(255,107,61,0.50)"; e.currentTarget.style.boxShadow="0 0 0 3px rgba(255,107,61,0.10)";}}
                      onBlur={(e)=>{e.currentTarget.style.borderColor="rgba(255,255,255,0.06)"; e.currentTarget.style.boxShadow="none";}}
                      style={{ width:"100%", padding:"13px 14px", border:"1px solid rgba(255,255,255,0.06)", borderRadius:12, fontSize:15, background:"rgba(255,255,255,0.03)", color:T.ink, outline:"none", fontFamily:FB, boxSizing:"border-box", transition:"border-color 0.15s, box-shadow 0.15s" }}/>
                  </div>

                  {/* All Sports toggle */}
                  <button onClick={() => { haptic.light(); setAllowedSports([]); }} style={{
                    width:"100%", padding:"14px 16px", marginBottom:10, borderRadius:14,
                    border: allOn ? `2px solid ${T.coral}` : "1.5px solid rgba(255,255,255,0.06)",
                    background: allOn ? "rgba(255,107,61,0.10)" : "rgba(255,255,255,0.025)",
                    cursor:"pointer", display:"flex", alignItems:"center", gap:12, textAlign:"left",
                  }}>
                    <div style={{ display:"flex", alignItems:"center", justifyContent:"center", width:28 }}>
                      <SportGlyph sport="all" color={allOn ? T.coral : "rgba(255,255,255,0.75)"} size={24} />
                    </div>
                    <div style={{ flex:1 }}>
                      <div style={{ fontSize:14, fontWeight:700, color:T.ink, marginBottom:2 }}>All Sports</div>
                      <div style={{ fontSize:11, color:T.inkMuted, lineHeight:1.3 }}>Members can pick any sport on Torch</div>
                    </div>
                    {allOn && (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={T.coral} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                    )}
                  </button>

                  <div style={{ fontSize:10.5, fontWeight:700, color:"rgba(255,255,255,0.40)", marginTop:14, marginBottom:8, textTransform:"uppercase", letterSpacing:"0.10em" }}>
                    Or pick specific sports
                  </div>
                  <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:7 }}>
                    {WIZARD_SPORTS.map(s => {
                      const on = allowedSports.includes(s.key);
                      return (
                        <button key={s.key} onClick={() => {
                          haptic.light();
                          setAllowedSports(prev => prev.includes(s.key) ? prev.filter(k => k !== s.key) : [...prev, s.key]);
                        }} style={{
                          padding:"11px 6px", borderRadius:11,
                          border: on ? `1.5px solid ${T.coral}` : "1px solid rgba(255,255,255,0.06)",
                          background: on ? "rgba(255,107,61,0.12)" : "rgba(255,255,255,0.025)",
                          cursor:"pointer", textAlign:"center",
                          transition:"all 0.15s",
                        }}>
                          <div style={{ marginBottom:5, display:"flex", justifyContent:"center" }}>
                            <SportGlyph sport={s.key} color={on ? T.coral : "rgba(255,255,255,0.75)"} size={20} />
                          </div>
                          <div style={{ fontSize:11, fontWeight:700, color: on ? T.coral : T.ink, letterSpacing:"-0.01em" }}>{s.label}</div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })()}

            {/* ─── STEP 2 — Time Frame + Public/Private ───────────────────── */}
            {createPath !== null && createStep === 2 && (() => {
              const TIMEFRAMES = [
                { id:"day",     label:"Day",     desc:"24 hours",    days:1   },
                { id:"week",    label:"Week",    desc:"7 days",      days:7   },
                { id:"month",   label:"Month",   desc:"30 days",     days:30  },
                // ROUND 12: relabeled from "Season" -- it sat one tile away from
                // "Season/Event" and the two different meanings of the same word
                // made the grid read as broken. id stays "season": it's persisted
                // as timeFrameType on existing league docs.
                { id:"season",  label:"3 Months", desc:"90 days",     days:90  },
                { id:"event",   label:"Season/Event", desc:"League or event", days:null },
                { id:"custom",  label:"Custom",  desc:"Pick dates",  days:null },
              ];
              const setFrame = (tf) => {
                haptic.light();
                setTimeFrameType(tf.id);
                if (tf.days) {
                  // Auto-set end date based on tile selection. Today + N days.
                  const d = new Date(); d.setDate(d.getDate() + tf.days);
                  setCreateEndDate(d.toISOString().slice(0,10));
                  setSelectedTemplate(null);
                }
              };
              return (
                <div>
                  {/* NFL path collects the league name here, since it skips the
                      step-1 sport screen where the field normally lives. */}
                  {createPath === "nfl" && (
                    <div style={{ marginBottom:16 }}>
                      <div style={{ fontSize:10.5, fontWeight:700, color:"rgba(255,255,255,0.50)", marginBottom:8, textTransform:"uppercase", letterSpacing:"0.10em" }}>
                        League name <span style={{ color:T.coral }}>*</span>
                      </div>
                      <input
                        value={createName}
                        onChange={e => { setCreateName(e.target.value); if (createErr) setCreateErr(""); }}
                        placeholder="e.g. The Pick Six"
                        style={{
                          width:"100%", padding:"13px 14px", borderRadius:12,
                          border:"1px solid rgba(255,255,255,0.07)", background:"rgba(255,255,255,0.03)",
                          color:T.ink, fontSize:15, fontFamily:FB, outline:"none", boxSizing:"border-box",
                        }}
                      />
                    </div>
                  )}

                  {/* ROUND 10: the "Run your NFL season" FEATURED tile that used to
                      sit here was removed. It looked identical to the step-0 NFL
                      Season path but produced a DIFFERENT league: it applied the
                      NFL template's dates/name only -- no pick'em, no sport lock --
                      so two near-identical buttons created two different products.
                      The step-0 fork is now the one and only NFL entry point;
                      custom-path users who want NFL wagering pick the NFL Season
                      event from the season/event list below like any other event. */}

                  {/* Timeframe grid — custom path only. On the NFL path the
                      timeframe is already "event" (the 2026 season), so this
                      grid asks a question the fork answered. Worse, setFrame()
                      calls setSelectedTemplate(null): tapping any tile here
                      would clear the NFL template, which trips the scoringMode
                      guard and silently drops the commissioner from Pick'em
                      back to Vegas Odds — with no visible cause. */}
                  {createPath !== "nfl" && (
                  <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8, marginBottom:14 }}>
                    {TIMEFRAMES.map(tf => {
                      const on = timeFrameType === tf.id;
                      return (
                        <button key={tf.id} onClick={() => setFrame(tf)} style={{
                          padding:"14px 12px", borderRadius:13,
                          border: on ? `2px solid ${T.coral}` : "1.5px solid rgba(255,255,255,0.06)",
                          background: on ? "rgba(255,107,61,0.12)" : "rgba(255,255,255,0.025)",
                          cursor:"pointer", textAlign:"left",
                        }}>
                          <div style={{ fontFamily:FD, fontSize:17, fontWeight:800, color: on ? "#fff" : T.ink, letterSpacing:"-0.01em", marginBottom:3 }}>{tf.label}</div>
                          <div style={{ fontSize:11, color: on ? "rgba(255,255,255,0.65)" : T.inkMuted }}>{tf.desc}</div>
                        </button>
                      );
                    })}
                  </div>
                  )}

                  {/* Event sub-picker — custom path only. On the NFL path this
                      offered MLB Season / NBA Playoffs / World Cup / Premier
                      League to someone who just tapped "NFL Season" at the fork:
                      it re-asks the answered question AND lists wrong answers.
                      Tapping one would swap the template out from under a
                      Pick'em league and silently reset the format. */}
                  {createPath !== "nfl" && timeFrameType === "event" && (
                    <div style={{ marginBottom:14, padding:12, borderRadius:12, background:"rgba(255,255,255,0.025)", border:"1px solid rgba(255,255,255,0.05)" }}>
                      <div style={{ fontSize:10.5, fontWeight:700, color:"rgba(255,255,255,0.50)", marginBottom:8, textTransform:"uppercase", letterSpacing:"0.10em" }}>
                        Pick a season or event
                      </div>
                      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:6 }}>
                        {SEASON_TEMPLATES.filter(t => t.id !== "custom" && isTemplateLive(t)).map(tmpl => {
                          const on = selectedTemplate?.id === tmpl.id;
                          return (
                            <button key={tmpl.id} onClick={() => {
                              haptic.light();
                              setSelectedTemplate(on ? null : tmpl);
                              if (!on) {
                                if (tmpl.endDate) setCreateEndDate(tmpl.endDate);
                                if (tmpl.startDate) setCreateStartDate(tmpl.startDate);
                                if (!createName.trim()) setCreateName(tmpl.name);
                              }
                            }} style={{
                              padding:"9px 8px", borderRadius:10,
                              border: on ? `1.5px solid ${T.coral}` : "1px solid rgba(255,255,255,0.06)",
                              background: on ? "rgba(255,107,61,0.12)" : "rgba(255,255,255,0.03)",
                              cursor:"pointer", textAlign:"center", fontSize:11.5, fontWeight:600, color: on ? T.coral : T.ink,
                            }}>{tmpl.emoji} {tmpl.name}</button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* NFL path — confirm what the fork already set, instead of
                      re-asking it. Replaces the timeframe grid + season picker
                      with a statement of fact; the date inputs below stay
                      editable for commissioners running a shorter league. */}
                  {createPath === "nfl" && (
                    <div style={{
                      display:"flex", alignItems:"center", gap:10, marginBottom:14,
                      padding:"11px 13px", borderRadius:11,
                      background:"rgba(255,107,61,0.06)", border:"1px solid rgba(255,107,61,0.14)",
                    }}>
                      <span style={{ fontSize:16, flexShrink:0 }}>🏈</span>
                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={{ fontSize:12.5, fontWeight:700, color:T.ink }}>2026 NFL Season</div>
                        <div style={{ fontSize:11, color:T.inkMuted, marginTop:1 }}>
                          Week 1 through Week {NFL_2026_WEEKS} · runs to February
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Date inputs — always visible, auto-populated by tile selection */}
                  <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:14 }}>
                    <div>
                      <div style={{ fontSize:10, fontWeight:700, color:"rgba(255,255,255,0.40)", marginBottom:6, textTransform:"uppercase", letterSpacing:"0.10em" }}>Start <span style={{ color:"rgba(255,255,255,0.42)", fontWeight:500 }}>· optional</span></div>
                      <input type="date" value={createStartDate} onChange={e=>{ setCreateStartDate(e.target.value); if (createPath !== "nfl") setTimeFrameType("custom"); }}
                        style={{ width:"100%", padding:"11px 12px", border:"1px solid rgba(255,255,255,0.06)", borderRadius:11, fontSize:14, background:"rgba(255,255,255,0.03)", color:T.ink, outline:"none", fontFamily:FB, boxSizing:"border-box" }}/>
                    </div>
                    <div>
                      <div style={{ fontSize:10, fontWeight:700, color:"rgba(255,255,255,0.40)", marginBottom:6, textTransform:"uppercase", letterSpacing:"0.10em" }}>End <span style={{ color:T.coral }}>*</span></div>
                      <input type="date" value={createEndDate} onChange={e=>{ setCreateEndDate(e.target.value); if (createPath !== "nfl") setTimeFrameType("custom"); }} required
                        style={{ width:"100%", padding:"11px 12px", border:"1px solid rgba(255,255,255,0.06)", borderRadius:11, fontSize:14, background:"rgba(255,255,255,0.03)", color:T.ink, outline:"none", fontFamily:FB, boxSizing:"border-box" }}/>
                    </div>
                  </div>

                  {/* Public / private */}
                  <label style={{ display:"flex", alignItems:"center", gap:10, cursor:"pointer", padding:"12px 14px", borderRadius:12, background:"rgba(255,255,255,0.025)", border:"1px solid rgba(255,255,255,0.05)" }}>
                    <input type="checkbox" checked={makePublic} onChange={e=>setMakePublic(e.target.checked)} style={{ width:18, height:18, accentColor:T.coral }}/>
                    <div style={{ flex:1 }}>
                      <div style={{ fontSize:13, fontWeight:600, color:T.ink }}>Make this league public</div>
                      <div style={{ fontSize:11, color:T.inkMuted, marginTop:1 }}>Anyone on Torch can discover and join</div>
                    </div>
                  </label>
                </div>
              );
            })()}

            {/* ─── STEP 3 — Pick Types + Cadence + Wager Bounds ──────────── */}
            {createPath !== null && createStep === 3 && (() => {
              const BET_TYPES = [
                { key:"spread", label:"Spreads",     desc:"Point spread picks" },
                { key:"ml",     label:"Moneyline",   desc:"Pick the winner" },
                { key:"total",  label:"Totals",      desc:"Over / under combined score" },
                { key:"prop",   label:"Player Props", desc:"Individual player stats" },
              ];
              const CADENCES = [
                { key:"none",   label:"No requirement",         desc:"Members pick whenever" },
                { key:"daily",  label:"Daily",                  desc:"Pick every day" },
                { key:"weekly", label:"Weekly",                 desc:"Pick at least once per week" },
              ];
              const toggleType = (k) => {
                haptic.light();
                setAllowedBetTypes(prev => {
                  if (prev.includes(k)) {
                    // Don't allow removing the last one — at least one must remain
                    if (prev.length === 1) return prev;
                    return prev.filter(x => x !== k);
                  }
                  return [...prev, k];
                });
              };
              // Pick'em is NFL-only at launch. It is week-structured (the board
              // groups games under "Week N" via NFL_2026_WEEK_STARTS, which is a
              // hardcoded 2026 NFL table), so offering it for MLB/EPL/custom
              // would produce a league whose slate can never be grouped and
              // whose "week" has no meaning. Gate the OPTION here; the effect on
              // selectedTemplate resets stale state if the template changes
              // after the fact; handleCreate enforces it once more at the write.
              const nflTemplateSelected = selectedTemplate?.id === "nfl-2026";
              const SCORING_MODES = [
                { key:"wager",    label:"Vegas Odds",   desc:"Wager virtual points at real odds. Classic Torch." },
                ...(nflTemplateSelected ? [
                { key:"pickem",   label:"Pick'em",      desc:"Straight up — pick winners, 1 point each. No wagering." },
                ] : []),
                // ── SURVIVOR — deliberately not offered (decided 2026-07-15) ──
                // Cut from the NFL launch to protect the preseason window. Survivor
                // needs, before it could ship safely:
                //   1. A server-side week model. NFL_2026_WEEK_STARTS lives only in
                //      this file; functions/index.js has no concept of a week, and
                //      survivor is week-structured throughout (one pick per week,
                //      eliminatedWeek, no team reuse across weeks). Duplicating the
                //      table would recreate the client/server divergence class that
                //      caused the F3 payday and F4 odds-truncation bugs.
                //   2. Team identity on picks. `pick.side` is "home"/"away", which
                //      can't express "already used the Chiefs in week 3".
                //   3. A scheduled sweep to eliminate members who never picked —
                //      settlement only runs on picks that EXIST, so a missed week
                //      eliminates nobody. Code that eliminates people based on the
                //      absence of data is the highest-risk thing in the build.
                //   4. A commissioner-reversible undo (required by spec), correctly
                //      ordered against later eliminations.
                //
                // The downstream `scoringMode === "survivor"` branches in this file,
                // leagues.js, and index.js are intentionally LEFT IN PLACE. They're
                // unreachable while this option is absent (nothing can write the
                // value), they're already covered by the 325-test gate, and they
                // treat survivor exactly like pickem — no ledger, no wager, no Pass
                // the Torch. Ripping them out would mean a large diff through
                // submitPickCallable and settleAllPicks to delete branches that
                // cannot execute. To ship survivor later: restore this line, then
                // build 1–4 above on top of the existing scaffolding.
              ];
              return (
                <div>
                  {/* Scoring mode — the format of the whole league. Drives whether
                      the wager controls below are relevant. */}
                  <div style={{ fontSize:10.5, fontWeight:700, color:"rgba(255,255,255,0.40)", marginBottom:8, textTransform:"uppercase", letterSpacing:"0.10em" }}>League format</div>
                  {/* ROUND 12: when only one format is available (custom path
                      without the NFL template), a radio group with a single
                      option is noise -- render a plain info card instead, plus
                      a hint that tells the captain where Pick'em actually
                      lives, so the option is discoverable without pretending
                      there's a choice on this screen. */}
                  {SCORING_MODES.length === 1 ? (
                    <div style={{ marginBottom:18 }}>
                      <div style={{
                        padding:"11px 14px", borderRadius:11,
                        border:`1.5px solid ${T.coral}`, background:"rgba(255,107,61,0.12)",
                      }}>
                        <div style={{ fontSize:13, fontWeight:700, color:T.ink }}>Vegas Odds</div>
                        <div style={{ fontSize:11, color:T.inkMuted, marginTop:1, lineHeight:1.3 }}>Wager virtual points at real odds. Classic Torch.</div>
                      </div>
                      <div style={{ fontSize:11, color:T.inkMuted, marginTop:6, lineHeight:1.4 }}>
                        Want a Pick'em league instead? Choose <strong style={{ color:T.inkSec }}>NFL Season</strong> on the schedule step — Pick'em is NFL-only.
                      </div>
                    </div>
                  ) : (
                  <div style={{ display:"flex", flexDirection:"column", gap:6, marginBottom:18 }}>
                    {SCORING_MODES.map(m => {
                      const on = scoringMode === m.key;
                      return (
                        <button key={m.key} onClick={() => { haptic.light(); setScoringMode(m.key); }} style={{
                          padding:"11px 14px", borderRadius:11,
                          border: on ? `1.5px solid ${T.coral}` : "1px solid rgba(255,255,255,0.06)",
                          background: on ? "rgba(255,107,61,0.12)" : "rgba(255,255,255,0.025)",
                          cursor:"pointer", textAlign:"left", display:"flex", alignItems:"center", gap:10,
                        }}>
                          <div style={{
                            width:18, height:18, borderRadius:"50%", flexShrink:0,
                            border: on ? `5px solid ${T.coral}` : "1.5px solid rgba(255,255,255,0.20)",
                            background: on ? T.bg : "transparent", transition:"all 0.15s",
                          }}/>
                          <div style={{ flex:1 }}>
                            <div style={{ fontSize:13, fontWeight:700, color: on ? T.ink : T.inkSec }}>{m.label}</div>
                            <div style={{ fontSize:11, color:T.inkMuted, marginTop:1, lineHeight:1.3 }}>{m.desc}</div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                  )}

                  {/* Pick-type toggles and wager bounds only apply to the Vegas-odds
                      format. Pick'em and Survivor are moneyline-only and wager-free,
                      so we hide those controls to keep the league doc consistent with
                      what handleCreate forces for non-wager modes. */}
                  {scoringMode !== "wager" ? (
                    <div style={{
                      padding:"14px 16px", borderRadius:12,
                      border:"1px solid rgba(255,255,255,0.06)", background:"rgba(255,255,255,0.025)",
                    }}>
                      <div style={{ fontSize:12.5, fontWeight:700, color:T.ink, marginBottom:4 }}>
                        {scoringMode === "survivor" ? "Survivor pool" : "Straight-up pick'em"}
                      </div>
                      <div style={{ fontSize:11.5, color:T.inkMuted, lineHeight:1.4 }}>
                        {scoringMode === "survivor"
                          ? "Members pick one team to win each week. No points, no wagers — survive or go out. You'll set the schedule; elimination is handled automatically."
                          : "Members pick winners straight up. One point per correct pick, no wagering. Standings rank by total correct picks."}
                      </div>
                      {/* SILENT-DISCARD FIX: handleCreate pins allowedSports to
                          NFL for pickem/survivor, quietly dropping any other
                          sports toggled in step 1. A commissioner who selected
                          NFL + NCAAF walked out believing they had a two-sport
                          league and hit "NFL-only" at first pick (real user
                          report, Aug 2026). Surface the discard HERE, at the
                          moment of choice, naming exactly what won't apply. */}
                      {(() => {
                        const dropped = (Array.isArray(allowedSports) ? allowedSports : [])
                          .filter(k => k !== "americanfootball_nfl")
                          .map(k => SPORT_FILTER_MAP[k] || k);
                        if (dropped.length === 0) return null;
                        return (
                          <div style={{
                            marginTop:10, padding:"9px 11px", borderRadius:9,
                            border:"1px solid rgba(255,107,61,0.35)", background:"rgba(255,107,61,0.08)",
                            fontSize:11.5, color:T.ink, lineHeight:1.45,
                          }}>
                            <strong style={{ color:T.coral }}>Heads up:</strong>{" "}
                            {scoringMode === "survivor" ? "Survivor" : "Pick'em"} is NFL-only.
                            Your {dropped.join(", ")} selection won't apply to this league.
                            For a multi-sport league, choose Vegas Odds instead.
                          </div>
                        );
                      })()}
                    </div>
                  ) : (<>
                  {/* Pick types */}
                  {/* Pick visibility (pick privacy) */}
                  <div style={{ fontSize:10.5, fontWeight:700, color:"rgba(255,255,255,0.40)", marginBottom:8, textTransform:"uppercase", letterSpacing:"0.10em" }}>Pick visibility</div>
                  <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:7, marginBottom:18 }}>
                    {[
                      { key:"until_start", label:"Hidden until kickoff", desc:"Picks reveal when each game locks — office-pool style. Protects strategy." },
                      { key:"open",        label:"Open picks",           desc:"Everyone sees picks the moment they're made. Max trash talk." },
                    ].map(o => {
                      const on = pickPrivacyMode === o.key;
                      return (
                        <button key={o.key} onClick={() => { haptic.light(); setPickPrivacyMode(o.key); }} style={{
                          padding:"11px 10px", borderRadius:11,
                          border: on ? `1.5px solid ${T.coral}` : "1px solid rgba(255,255,255,0.06)",
                          background: on ? "rgba(255,107,61,0.12)" : "rgba(255,255,255,0.025)",
                          cursor:"pointer", textAlign:"left",
                        }}>
                          <div style={{ fontSize:13, fontWeight:700, color: on ? T.coral : T.ink, marginBottom:2 }}>{o.label}</div>
                          <div style={{ fontSize:10.5, color:T.inkMuted, lineHeight:1.3 }}>{o.desc}</div>
                        </button>
                      );
                    })}
                  </div>

                  <div style={{ fontSize:10.5, fontWeight:700, color:"rgba(255,255,255,0.40)", marginBottom:8, textTransform:"uppercase", letterSpacing:"0.10em" }}>Pick types allowed</div>
                  <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:7, marginBottom:18 }}>
                    {BET_TYPES.map(t => {
                      const on = allowedBetTypes.includes(t.key);
                      return (
                        <button key={t.key} onClick={() => toggleType(t.key)} style={{
                          padding:"11px 10px", borderRadius:11,
                          border: on ? `1.5px solid ${T.coral}` : "1px solid rgba(255,255,255,0.06)",
                          background: on ? "rgba(255,107,61,0.12)" : "rgba(255,255,255,0.025)",
                          cursor:"pointer", textAlign:"left",
                        }}>
                          <div style={{ fontSize:13, fontWeight:700, color: on ? T.coral : T.ink, marginBottom:2 }}>{t.label}</div>
                          <div style={{ fontSize:10.5, color:T.inkMuted, lineHeight:1.3 }}>{t.desc}</div>
                        </button>
                      );
                    })}
                  </div>

                  {/* Cadence */}
                  <div style={{ fontSize:10.5, fontWeight:700, color:"rgba(255,255,255,0.40)", marginBottom:8, textTransform:"uppercase", letterSpacing:"0.10em" }}>Pick cadence</div>
                  <div style={{ display:"flex", flexDirection:"column", gap:6, marginBottom:14 }}>
                    {CADENCES.map(c => {
                      const on = pickReqCadence === c.key;
                      return (
                        <button key={c.key} onClick={() => { haptic.light(); setPickReqCadence(c.key); }} style={{
                          padding:"11px 14px", borderRadius:11,
                          border: on ? `1.5px solid ${T.coral}` : "1px solid rgba(255,255,255,0.06)",
                          background: on ? "rgba(255,107,61,0.12)" : "rgba(255,255,255,0.025)",
                          cursor:"pointer", textAlign:"left", display:"flex", alignItems:"center", gap:10,
                        }}>
                          <div style={{
                            width:18, height:18, borderRadius:"50%", flexShrink:0,
                            border: on ? `5px solid ${T.coral}` : "1.5px solid rgba(255,255,255,0.20)",
                            background: on ? T.bg : "transparent", transition:"all 0.15s",
                          }}/>
                          <div style={{ flex:1 }}>
                            <div style={{ fontSize:13, fontWeight:700, color: on ? T.ink : T.inkSec }}>{c.label}</div>
                            <div style={{ fontSize:11, color:T.inkMuted, marginTop:1 }}>{c.desc}</div>
                          </div>
                        </button>
                      );
                    })}
                  </div>

                  {/* Min picks per period — only when cadence is enabled */}
                  {pickReqCadence !== "none" && (
                    <div style={{ marginBottom:14 }}>
                      <div style={{ fontSize:10.5, fontWeight:700, color:"rgba(255,255,255,0.40)", marginBottom:7, textTransform:"uppercase", letterSpacing:"0.10em" }}>Picks required per {pickReqCadence === "daily" ? "day" : "week"}</div>
                      <input type="number" inputMode="numeric" pattern="[0-9]*" value={pickReqMinPicks} onChange={e=>setPickReqMinPicks(e.target.value)} min="1" max="50"
                        style={{ width:"100%", padding:"12px 14px", border:"1px solid rgba(255,255,255,0.06)", borderRadius:11, fontSize:15, background:"rgba(255,255,255,0.03)", color:T.ink, outline:"none", fontFamily:FM, boxSizing:"border-box" }}/>
                    </div>
                  )}

                  {/* Wager bounds */}
                  <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
                    <div>
                      <div style={{ fontSize:10.5, fontWeight:700, color:"rgba(255,255,255,0.40)", marginBottom:7, textTransform:"uppercase", letterSpacing:"0.10em" }}>Min wager</div>
                      <input type="number" inputMode="numeric" pattern="[0-9]*" value={pickReqMinWager} onChange={e=>setPickReqMinWager(e.target.value)} min="1" placeholder="No minimum"
                        style={{ width:"100%", padding:"12px 14px", border:"1px solid rgba(255,255,255,0.06)", borderRadius:11, fontSize:15, background:"rgba(255,255,255,0.03)", color:T.ink, outline:"none", fontFamily:FM, boxSizing:"border-box" }}/>
                    </div>
                    <div>
                      <div style={{ fontSize:10.5, fontWeight:700, color:"rgba(255,255,255,0.40)", marginBottom:7, textTransform:"uppercase", letterSpacing:"0.10em" }}>Max wager <span style={{ fontWeight:500, color:"rgba(255,255,255,0.42)", textTransform:"none", letterSpacing:0 }}>· optional</span></div>
                      <input type="number" inputMode="numeric" pattern="[0-9]*" value={pickReqMaxWager} onChange={e=>setPickReqMaxWager(e.target.value)} min="1" placeholder="No limit"
                        style={{ width:"100%", padding:"12px 14px", border:"1px solid rgba(255,255,255,0.06)", borderRadius:11, fontSize:15, background:"rgba(255,255,255,0.03)", color:T.ink, outline:"none", fontFamily:FM, boxSizing:"border-box" }}/>
                    </div>
                  </div>
                  </>)}
                </div>
              );
            })()}

            {/* ─── STEP 4 — Stakes + Penalty + Review ─────────────────────── */}
            {createPath !== null && createStep === 4 && (() => {
              const PENALTIES = [
                { key:"none",     label:"None",        desc:"Track misses but don't penalize" },
                // ROUND 12: honest copy -- the server's computePenaltyAmount uses
                // minWager || 0 (NO 200 fallback), so with a blank min wager this
                // penalty deducts nothing. The old desc promised "200 pts per
                // miss" the server would never take; submit-time validation now
                // rejects that combination outright.
                { key:"minWager", label:"Min wager fee", desc: pickReqMinWager ? `Auto-deduct ${pickReqMinWager} pts per miss` : "Set a min wager (previous step) to use this" },
                { key:"fixed",    label:"Fixed fee",   desc:"Custom point amount" },
                { key:"percent",  label:"% of balance", desc:"Lose a slice of your stack" },
              ];
              return (
                <div>
                  {/* Starting balance — quick chips. Wager mode only: pickem and
                      survivor have no point ledger to seed. */}
                  {scoringMode === "wager" && (<>
                  <div style={{ fontSize:10.5, fontWeight:700, color:"rgba(255,255,255,0.40)", marginBottom:8, textTransform:"uppercase", letterSpacing:"0.10em" }}>Starting balance</div>
                  <div style={{ display:"flex", gap:6, marginBottom:14, flexWrap:"wrap" }}>
                    {[["500","Light"],["1000","Standard"],["2500","Big"],["5000","Whale"]].map(([n,lbl]) => {
                      const on = String(createBalance) === n;
                      return (
                        <button key={n} onClick={() => setCreateBalance(n)} style={{
                          padding:"7px 13px", borderRadius:999,
                          border: on ? "1px solid rgba(255,107,61,0.50)" : "1px solid rgba(255,255,255,0.06)",
                          background: on ? "rgba(255,107,61,0.10)" : "rgba(255,255,255,0.03)",
                          color: on ? T.coral : "rgba(255,255,255,0.55)",
                          fontSize:12, fontWeight:600, cursor:"pointer",
                        }}>
                          <span style={{ fontFamily:FM }}>{Number(n).toLocaleString()}</span>
                          <span style={{ color: on ? "rgba(255,107,61,0.50)" : "rgba(255,255,255,0.30)", margin:"0 5px" }}>·</span>
                          <span>{lbl}</span>
                        </button>
                      );
                    })}
                  </div>

                  {/* ROUND 10: weekly payday control restored. The wizard was
                      silently stamping every wager league with the 200/wk default
                      because the input for createPayday had no UI -- captains
                      could only discover and change it after creation, in league
                      settings. Amount-only by design (captain's call 2026-08):
                      0 = no payday; the payday cron skips leagues with amount 0. */}
                  <div style={{ fontSize:10.5, fontWeight:700, color:"rgba(255,255,255,0.40)", marginBottom:8, textTransform:"uppercase", letterSpacing:"0.10em" }}>
                    Weekly payday <span style={{ fontWeight:500, color:"rgba(255,255,255,0.42)", textTransform:"none", letterSpacing:0 }}>· 0 = off</span>
                  </div>
                  <input
                    value={createPayday}
                    onChange={e => setCreatePayday(e.target.value.replace(/[^0-9]/g, "").slice(0, 5))}
                    inputMode="numeric"
                    placeholder="200"
                    style={{ width:"100%", padding:"12px 14px", border:"1px solid rgba(255,255,255,0.06)", borderRadius:11, fontSize:14, background:"rgba(255,255,255,0.03)", color:T.ink, outline:"none", fontFamily:FM, boxSizing:"border-box", marginBottom:6 }}
                  />
                  <div style={{ fontSize:11, color:T.inkMuted, marginBottom:14, lineHeight:1.4 }}>
                    Every member gets this many points each week to keep the competition going. Set 0 for no payday.
                  </div>
                  </>)}

                  {/* Penalty — only show if cadence is enabled, otherwise it's irrelevant.
                      Also wager-only: every penalty type deducts points. */}
                  {scoringMode === "wager" && pickReqCadence !== "none" && (
                    <div style={{ marginBottom:14 }}>
                      <div style={{ fontSize:10.5, fontWeight:700, color:"rgba(255,255,255,0.40)", marginBottom:8, textTransform:"uppercase", letterSpacing:"0.10em" }}>Missed-pick penalty</div>
                      <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
                        {PENALTIES.map(p => {
                          const on = penaltyType === p.key;
                          return (
                            <button key={p.key} onClick={() => {
                              haptic.light(); setPenaltyType(p.key);
                              if (p.key === "minWager") setPenaltyValue(pickReqMinWager || "200");
                              if (p.key === "fixed" && (!penaltyValue || penaltyValue === "0")) setPenaltyValue("100");
                              if (p.key === "percent") setPenaltyValue("10");
                            }} style={{
                              padding:"10px 14px", borderRadius:11,
                              border: on ? `1.5px solid ${T.coral}` : "1px solid rgba(255,255,255,0.06)",
                              background: on ? "rgba(255,107,61,0.12)" : "rgba(255,255,255,0.025)",
                              cursor:"pointer", textAlign:"left",
                            }}>
                              <div style={{ fontSize:13, fontWeight:700, color: on ? T.coral : T.ink, marginBottom:2 }}>{p.label}</div>
                              <div style={{ fontSize:11, color:T.inkMuted, lineHeight:1.3 }}>{p.desc}</div>
                            </button>
                          );
                        })}
                      </div>

                      {/* Custom value field for fixed/percent */}
                      {(penaltyType === "fixed" || penaltyType === "percent") && (
                        <div style={{ marginTop:10 }}>
                          <input type="number" inputMode="numeric" pattern="[0-9]*" value={penaltyValue} onChange={e=>setPenaltyValue(e.target.value)}
                            min="1" max={penaltyType === "percent" ? "100" : undefined}
                            placeholder={penaltyType === "percent" ? "10" : "100"}
                            style={{ width:"100%", padding:"11px 14px", border:"1px solid rgba(255,255,255,0.06)", borderRadius:11, fontSize:15, background:"rgba(255,255,255,0.03)", color:T.ink, outline:"none", fontFamily:FM, boxSizing:"border-box" }}/>
                          <div style={{ fontSize:10.5, color:T.inkMuted, marginTop:5 }}>
                            {penaltyType === "fixed" ? "Points deducted per missed period" : "% of current balance forfeited per miss"}
                          </div>
                        </div>
                      )}

                      {/* Brutal mode — gated behind disclosure */}
                      <button onClick={() => setShowAdvancedPenalty(s => !s)} style={{
                        marginTop:10, padding:"6px 0", background:"none", border:"none",
                        color:T.inkMuted, fontSize:11, cursor:"pointer",
                        display:"flex", alignItems:"center", gap:5,
                      }}>
                        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ transform: showAdvancedPenalty ? "rotate(90deg)" : "none", transition:"transform 0.15s" }}><polyline points="9 18 15 12 9 6"/></svg>
                        Advanced (high-stakes leagues)
                      </button>
                      {showAdvancedPenalty && (
                        <button onClick={() => {
                          window.torchConfirm?.(
                            "Brutal mode: ONE missed period = forfeit entire balance. Members can't recover. Use only with friends who agree.",
                            () => { setPenaltyType("brutal"); setPenaltyValue("100"); },
                            { danger: true, confirmLabel: "Enable brutal mode" }
                          );
                        }} style={{
                          marginTop:8, width:"100%", padding:"10px 14px", borderRadius:11,
                          border: penaltyType === "brutal" ? "1.5px solid #C76561" : "1px dashed rgba(199,101,97,0.30)",
                          background: penaltyType === "brutal" ? "rgba(199,101,97,0.10)" : "rgba(199,101,97,0.04)",
                          color: penaltyType === "brutal" ? "#E08682" : T.inkSec,
                          fontSize:12, fontWeight:600, cursor:"pointer", textAlign:"left",
                        }}>
                          ☠️ Brutal Mode {penaltyType === "brutal" ? "— ACTIVE" : "— miss = lose everything"}
                        </button>
                      )}
                    </div>
                  )}

                  {/* Motto + Prize */}
                  <div style={{ marginBottom:12 }}>
                    <div style={{ fontSize:10.5, fontWeight:700, color:"rgba(255,255,255,0.40)", marginBottom:7, textTransform:"uppercase", letterSpacing:"0.10em" }}>Motto <span style={{ fontWeight:500, color:"rgba(255,255,255,0.42)", textTransform:"none", letterSpacing:0 }}>· optional</span></div>
                    <input value={createMotto} onChange={e=>setCreateMotto(e.target.value)} placeholder="e.g. Talk trash, back it up" maxLength={80}
                      style={{ width:"100%", padding:"12px 14px", border:"1px solid rgba(255,255,255,0.06)", borderRadius:11, fontSize:14, background:"rgba(255,255,255,0.03)", color:T.ink, outline:"none", fontFamily:FB, boxSizing:"border-box" }}/>
                  </div>
                  <div style={{ marginBottom:14 }}>
                    <div style={{ fontSize:10.5, fontWeight:700, color:"rgba(255,255,255,0.40)", marginBottom:7, textTransform:"uppercase", letterSpacing:"0.10em" }}>Prize <span style={{ fontWeight:500, color:"rgba(255,255,255,0.42)", textTransform:"none", letterSpacing:0 }}>· optional</span></div>
                    <input value={createPrize} onChange={e=>setCreatePrize(e.target.value)} placeholder="e.g. Loser buys dinner" maxLength={100}
                      style={{ width:"100%", padding:"12px 14px", border:"1px solid rgba(255,255,255,0.06)", borderRadius:11, fontSize:14, background:"rgba(255,255,255,0.03)", color:T.ink, outline:"none", fontFamily:FB, boxSizing:"border-box" }}/>
                  </div>

                  {/* Review summary */}
                  <div style={{ padding:"12px 14px", borderRadius:12, background:"rgba(255,107,61,0.05)", border:"1px solid rgba(255,107,61,0.14)", marginBottom:14 }}>
                    <div style={{ fontSize:10.5, fontWeight:700, color:T.coral, marginBottom:7, textTransform:"uppercase", letterSpacing:"0.10em" }}>Your league at a glance</div>
                    <div style={{ fontSize:12, color:T.inkSec, lineHeight:1.6 }}>
                      <strong style={{ color:T.ink }}>{createName.trim() || "(Unnamed)"}</strong> · {(allowedSports.length === 0 ? "All sports" : allowedSports.length === 1 ? (SPORT_FILTER_MAP[allowedSports[0]] || allowedSports[0]) : `${allowedSports.length} sports`)} · {timeFrameType} · {scoringMode === "wager" ? (allowedBetTypes.length === 4 ? "all bet types" : `${allowedBetTypes.length} bet type${allowedBetTypes.length===1?"":"s"}`) : "pick the winner"}<br/>
                      {scoringMode === "wager" ? (<>
                        {pickReqCadence === "none" ? "No pick requirement" : `${pickReqMinPicks || 1} pick${(pickReqMinPicks||"1")==="1"?"":"s"}/${pickReqCadence === "daily" ? "day" : "week"}`} · {pickReqMinWager ? `Min wager ${pickReqMinWager} pts` : "No min wager"}{pickReqMaxWager ? ` · Max ${pickReqMaxWager}` : ""}<br/>
                        {/* ROUND 10: payday surfaced; penalty only shown when a
                            cadence exists -- penalties only fire on missed
                            required picks, so "No pick requirement · Penalty:
                            200 pts/miss" was a contradiction on one card. */}
                        {(() => { const pp = parseInt(createPayday, 10); const pa = Number.isFinite(pp) ? Math.max(0, Math.min(10000, pp)) : 200; return pa > 0 ? `Payday: +${pa.toLocaleString()} pts/wk` : "No payday"; })()}
                        {pickReqCadence !== "none" && (<> · Penalty: {penaltyType === "none" ? "none" : penaltyType === "minWager" ? `${pickReqMinWager || 0} pts/miss` : penaltyType === "fixed" ? `${penaltyValue} pts/miss` : penaltyType === "percent" ? `${penaltyValue}% balance/miss` : penaltyType === "brutal" ? "BRUTAL — full balance" : penaltyType}</>)}
                      </>) : scoringMode === "survivor" ? (<>
                        Survivor pool · one team per week · no points, no wagers<br/>
                        Wrong pick = eliminated. Last member standing wins.
                      </>) : (<>
                        Straight-up pick'em · 1 point per correct pick<br/>
                        No wagers. Standings rank by total correct picks.
                      </>)}
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* ─── ERROR + LEGAL ─────────────────────────────────────────── */}
            {createErr && <div style={{ color:T.red, fontSize:13, marginBottom:10, padding:"10px 14px", background:"rgba(199,101,97,0.08)", border:"1px solid rgba(199,101,97,0.20)", borderRadius:10 }}>{createErr}</div>}

            {/* ─── FOOTER — Next or Create ───────────────────────────────── */}
            {createPath !== null && (createStep < 4 ? (
              <button onClick={() => {
                // Soft validation per step before advancing.
                // The name lives on step 1 for the custom path, but the NFL path
                // skips step 1 and collects the name on step 2 — so the name
                // check has to follow the field, not the step number. Without
                // this, the NFL path could reach step 4 unnamed and only fail at
                // handleCreate.
                if (createPath === "custom" && createStep === 1 && !createName.trim()) { setCreateErr("Give your league a name first."); return; }
                if (createPath === "nfl" && createStep === 2 && !createName.trim()) { setCreateErr("Give your league a name first."); return; }
                if (createStep === 2 && !createEndDate) { setCreateErr("Pick an end date."); return; }
                setCreateErr("");
                setCreateStep(s => s + 1);
              }} className="no-press" style={{
                width:"100%", padding:"14px",
                background:T.coral,
                border:"none", borderRadius:14,
                color:"#fff", fontSize:15, fontWeight:700, cursor:"pointer",
                boxShadow:"0 1px 0 rgba(255,255,255,0.2) inset, 0 4px 16px -4px rgba(255,107,61,0.4), 0 1px 2px rgba(0,0,0,0.3)",
                letterSpacing:"-0.01em",
                display:"flex", alignItems:"center", justifyContent:"center", gap:8,
              }}>
                Next <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
              </button>
            ) : (
              <>
                <div style={{ fontSize:11, color:"rgba(255,255,255,0.36)", textAlign:"center", marginBottom:10, lineHeight:1.5 }}>
                  Torch is for entertainment only. No real money is wagered or awarded.
                </div>
                <button onClick={handleCreate} disabled={creating} className="no-press" style={{
                  width:"100%", padding:"15px",
                  background: creating ? "rgba(255,107,61,0.5)" : `linear-gradient(180deg, #FF6A2F 0%, #FF5A1F 50%, #E84E15 100%)`,
                  border:"none", borderRadius:14,
                  color:"#fff", fontSize:15, fontWeight:700, cursor:creating?"default":"pointer", opacity:creating?0.7:1,
                  boxShadow: creating ? "none" : "0 1px 0 rgba(255,255,255,0.2) inset, 0 4px 16px -4px rgba(255,107,61,0.4), 0 1px 2px rgba(0,0,0,0.3)",
                  letterSpacing:"-0.01em",
                }}>{creating ? "Creating…" : "Create League"}</button>
              </>
            ))}

          </div>
        </div>
      )}

      {/* Join modal */}
      {showJoin && (
        <div onClick={() => setShowJoin(false)} style={{ position:"fixed", inset:0, zIndex:100, background:"rgba(0,0,0,0.45)", display:"flex", alignItems:"flex-end" }}>
          <div onClick={e=>e.stopPropagation()} style={{ width:"100%", maxWidth:640, margin:"0 auto", background:T.card, borderRadius:"20px 20px 0 0", paddingTop:24, paddingLeft:20, paddingRight:20, paddingBottom:"calc(80px + env(safe-area-inset-bottom, 0px))", maxHeight:"calc(92vh - env(safe-area-inset-top, 0px))", overflowY:"auto", minHeight:0, WebkitOverflowScrolling:"touch", overscrollBehavior:"contain" }}>
            <div style={{ width:36, height:4, background:T.border, borderRadius:2, margin:"0 auto 20px" }}/>
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:16 }}>
              <div style={{ fontSize:17, fontWeight:700 }}>Join a League</div>
              <button onClick={() => setShowJoin(false)} aria-label="Close" style={{
                flexShrink:0, width:32, height:32, borderRadius:10,
                background:"rgba(255,255,255,0.05)", border:"1px solid rgba(255,255,255,0.08)",
                color:"rgba(255,255,255,0.5)", cursor:"pointer",
                display:"flex", alignItems:"center", justifyContent:"center",
              }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
            <div style={{ fontSize:11, fontWeight:700, color:T.inkSec, marginBottom:5, textTransform:"uppercase", letterSpacing:"0.07em" }}>Enter Code</div>
            <input value={joinCode} onChange={e=>setJoinCode(e.target.value.toUpperCase())} placeholder="e.g. ABC123"
              style={{ width:"100%", padding:"12px 14px", border:`1.5px solid ${T.border}`, borderRadius:10, fontSize:18, letterSpacing:"0.15em", fontWeight:700, background:T.bgWarm, color:T.ink, outline:"none", fontFamily:FB, marginBottom:12, boxSizing:"border-box" }}/>
            {joinErr && <div style={{ color:T.red, fontSize:13, marginBottom:10 }}>{joinErr}</div>}
            <button onClick={handleJoin} disabled={joining} style={{
              width:"100%", padding:"14px",
              background: joining ? "rgba(255,107,61,0.5)" : `linear-gradient(180deg, ${T.coral}, ${T.coralDeep})`,
              border:"none", borderRadius:12,
              color:"#fff", fontSize:15, fontWeight:700, cursor: joining?"wait":"pointer",
              boxShadow: joining ? "none" : "0 4px 16px rgba(255,107,61,0.25)",
            }}>{joining ? "Joining…" : "Join League"}</button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ===========================================
   PAGE: MY PICKS
=========================================== */
// --- CASH-OUT PICK ------------------------------------------------------------
// Marks pick as "cashed", records the payout, and credits user balance.
// Mirrors the pattern in picks.js / settlement.js so no extra file needed.
// SUBMIT-NO-FALLBACK-ON-REJECTION (fix 2.4 client half, 2026-09-28): the slip
// fell back to a direct Firestore write (submitPicks) on EVERY callable error
// except resource-exhausted and failed-precondition. invalid-argument is how
// submitPickCallable says "this league doesn't allow that sport / bet type",
// "invalid odds", "missing game time" -- so the app's own fallback wrote the
// pick the server had just refused. Only a failure where the server may never
// have seen or finished the request should fall back.
function isTransientCallableError(err) {
  const code = err?.code || "";
  if (!code) return true; // not a callable error at all (e.g. network fetch threw)
  return code === "functions/unavailable" || code === "functions/deadline-exceeded"
      || code === "functions/internal"    || code === "functions/unknown";
}

// SERVER-CASHOUT (fix 1.6, 2026-09-28): cash-out now runs in
// cashOutPickCallable. The old client version wrote status:"cashed" with no
// check that the pick was still pending and credited a client-computed value,
// so a pick the cron had just settled could be paid twice (payout + cash-out)
// or a lost pick cashed out. The server re-checks pending, re-prices off a
// live line it fetches itself, and credits global + league balance in one
// transaction. It pays `quotedValue` (what the user confirmed) only if that is
// <= its own price; otherwise it throws "aborted" with details.newValue.
async function cashOutPick(pickId, quotedValue) {
  const { getFunctions, httpsCallable } = await import("firebase/functions");
  const fn = httpsCallable(getFunctions(app), "cashOutPickCallable");
  const res = await fn({ pickId, quotedValue });
  return res.data; // { success, cashoutValue, points }
}

// --- CASH-OUT VALUE CALCULATOR -----------------------------------------------
// cashout ≈ wager × (currentImplied / originalImplied) × 0.90 (house margin)
// Returns integer points floored to 1.

// -- Parlay helpers ------------------------------------------------------------
// Multiply each leg's decimal odds together, return combined American odds + payout
function calcParlayDecimal(legs) {
  return legs.reduce((prod, leg) => {
    const american = leg.odds || -110;
    const dec = american > 0 ? 1 + american / 100 : 1 + 100 / Math.abs(american);
    return prod * dec;
  }, 1);
}

function calcParlayAmericanOdds(legs) {
  const dec = calcParlayDecimal(legs);
  if (dec <= 1) return -110;
  if (dec >= 2) return Math.round((dec - 1) * 100);
  return Math.round(-100 / (dec - 1));
}

function calcParlayPayout(wager, legs) {
  return Math.round(wager * calcParlayDecimal(legs));
}

function calcCashoutValue(wager, originalOdds, currentOdds) {
  if (!wager || wager <= 0) return 0;
  const toDecimal = (o) => {
    if (!o || o === 0) return 1.909; // default -110
    if (o > 0) return o / 100 + 1;
    return 100 / Math.abs(o) + 1;
  };
  const toImplied = (o) => {
    const d = toDecimal(o);
    return d > 1 ? 1 / d : 0.5;
  };
  const origImpl = toImplied(originalOdds);
  const currImpl = toImplied(currentOdds);
  if (origImpl <= 0) return Math.floor(wager * 0.5);
  const raw = wager * (currImpl / origImpl) * 0.90;
  return Math.max(1, Math.floor(raw));
}


/* ===========================================
   TOURNAMENT SETTLE TOOL — manually settle outright/futures picks
   Shows pending tournament picks grouped by tournament.
   Captain/admin enters the winner name, and all matching picks settle.
=========================================== */
function TournamentSettleTool({ picks, userId, onSettled }) {
  const [open, setOpen] = useState(false);
  const [winner, setWinner] = useState("");
  const [settling, setSettling] = useState(false);

  // Group by tournament (gameId or homeTeamFull which is the tournament title)
  const tournaments = {};
  picks.forEach(p => {
    const key = p.homeTeamFull || p.gameLabel || p.gameId || "Unknown";
    if (!tournaments[key]) tournaments[key] = [];
    tournaments[key].push(p);
  });

  const handleSettle = async (tournamentName, tournamentPicks, winnerName) => {
    if (!winnerName.trim() || settling) return;

    // ── Pre-validate odds before settling ──
    // Same defense as settleTournament below: a `pick.odds || 0` fallback
    // silently turns winners into zero-payout settlements. Better to abort
    // with a clear error than half-settle.
    const badOddsPicks = tournamentPicks.filter(p => {
      const o = p.odds;
      return o == null || o === 0 || !Number.isFinite(Number(o));
    });
    if (badOddsPicks.length > 0) {
      console.error("[Tournament] handleSettle aborted — bad odds:", badOddsPicks.map(p => ({ id: p.id, label: p.label, odds: p.odds })));
      window.torchToast?.(`Aborting — ${badOddsPicks.length} pick${badOddsPicks.length > 1 ? "s" : ""} ${badOddsPicks.length > 1 ? "have" : "has"} missing odds. Fix data and retry.`, "error");
      return;
    }

    setSettling(true);
    try {
      const { getFirestore, doc, updateDoc } = await import("firebase/firestore");
      const { calcPayout } = await import("./settlement");
      const dbInst = getFirestore(app);
      const settled = [];

      for (const pick of tournamentPicks) {
        const isWinner = pick.side?.toLowerCase() === winnerName.trim().toLowerCase()
          || pick.label?.toLowerCase().includes(winnerName.trim().toLowerCase());
        const status = isWinner ? "won" : "lost";
        const wager = pick.wager || 0;
        // odds is now guaranteed to be a valid non-zero number by pre-validation
        const odds = Number(pick.odds);
        const profit = isWinner ? calcPayout(wager, odds) - wager : 0;
        const balDelta = isWinner ? calcPayout(wager, odds) : 0;

        await updateDoc(doc(dbInst, "picks", userId, "userPicks", pick.id), {
          status, points: profit, settledAt: new Date(),
          tournamentWinner: winnerName.trim(),
        });

        // Credit balance for wins and pushes
        // PRACTICE-ONLY-GLOBAL (fix 2.6): users.balance is practice-only now.
        if (balDelta > 0 && !pick.leagueId) {
          const { applyBalanceDelta } = await import("./settlement");
          await applyBalanceDelta(userId, balDelta, `tournament_win_${pick.id}`);
        }

        settled.push({ ...pick, status, points: profit });
      }

      onSettled?.(settled);
      const wins = settled.filter(s => s.status === "won").length;
      window.torchToast?.(`Settled ${settled.length} picks — ${wins} won.`, "success");
      setWinner("");
    } catch (e) {
      window.torchToast?.(e.message || "Settlement failed", "error");
    } finally { setSettling(false); }
  };

  if (!open) {
    return (
      <button onClick={() => setOpen(true)} style={{
        width:"100%", padding:"12px 14px", borderRadius:12, marginTop:8,
        border:"1px solid rgba(255,215,0,0.20)", background:"rgba(255,215,0,0.06)",
        color:"#FCD34D", fontSize:12, fontWeight:700, cursor:"pointer",
        display:"flex", alignItems:"center", justifyContent:"center", gap:8,
      }}>
        Settle Tournament Picks ({picks.length} pending)
      </button>
    );
  }

  return (
    <div style={{
      marginTop:10, padding:"16px", borderRadius:16,
      background:"rgba(255,215,0,0.04)", border:"1px solid rgba(255,215,0,0.15)",
    }}>
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:14 }}>
        <div style={{ fontSize:14, fontWeight:700, color:"#FCD34D" }}>Settle Tournament Picks</div>
        <button onClick={() => setOpen(false)} style={{
          background:"none", border:"none", color:"rgba(255,255,255,0.42)", fontSize:16, cursor:"pointer",
        }}>×</button>
      </div>
      <div style={{ fontSize:11, color:"rgba(255,255,255,0.35)", marginBottom:16, lineHeight:1.5 }}>
        Enter the tournament winner's name below. All picks for that golfer will be settled as WON. All others as LOST.
      </div>
      {Object.entries(tournaments).map(([tournName, tPicks]) => (
        <div key={tournName} style={{ marginBottom:16 }}>
          <div style={{ fontSize:12, fontWeight:700, color:"#fff", marginBottom:6 }}>{tournName}</div>
          <div style={{ fontSize:10, color:"rgba(255,255,255,0.42)", marginBottom:8 }}>
            {tPicks.length} pick{tPicks.length !== 1 ? "s" : ""} — {[...new Set(tPicks.map(p => p.side))].join(", ")}
          </div>
          <div style={{ display:"flex", gap:8 }}>
            <input
              value={winner} onChange={e => setWinner(e.target.value)}
              placeholder="Winner name (e.g. Scottie Scheffler)"
              style={{
                flex:1, padding:"10px 14px", borderRadius:10, border:"1px solid rgba(255,255,255,0.10)",
                background:"rgba(255,255,255,0.04)", color:"#fff", fontSize:13, outline:"none",
              }}
            />
            <button
              onClick={() => {
                if (!winner.trim()) return;
                window.torchConfirm?.(
                  `Settle ${tPicks.length} picks with winner "${winner.trim()}"? This cannot be undone.`,
                  () => handleSettle(tournName, tPicks, winner),
                  { confirmLabel: "Settle" }
                );
              }}
              disabled={settling || !winner.trim()}
              style={{
                padding:"10px 18px", borderRadius:10, border:"none", cursor:"pointer",
                background: settling ? "rgba(255,215,0,0.1)" : "rgba(255,215,0,0.15)",
                color:"#FCD34D", fontSize:12, fontWeight:700, flexShrink:0,
                opacity: settling || !winner.trim() ? 0.4 : 1,
              }}>
              {settling ? "Settling..." : "Settle"}
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

// SERVER-SETTLEMENT (fix 4.1 step 2, 2026-09-28): picks are settled ONLY by the
// server cron (settleAllPicks, every 5 min), which also credits league
// balances, W/L and the practice balance. The app no longer grades or pays
// anything; it just notices when picks it last saw as pending have been
// settled, and refreshes the UI for those. One pending-picks query, plus one
// doc read per pick that changed.
async function fetchServerSettledPicks(uid, candidatePicks) {
  if (!uid || !candidatePicks?.length) return [];
  const stillPending = new Set((await loadPendingPicks(uid).catch(() => null) || candidatePicks).map(p => p.id));
  const gone = candidatePicks.map(p => p.id).filter(id => id && !stillPending.has(id));
  if (gone.length === 0) return [];
  const { getFirestore, doc, getDoc } = await import("firebase/firestore");
  const dbS = getFirestore(app);
  const snaps = await Promise.all(gone.map(id => getDoc(doc(dbS, "picks", uid, "userPicks", id)).catch(() => null)));
  const rows = snaps.filter(sn => sn && sn.exists()).map(sn => {
    const d = sn.data();
    return { id: sn.id, ...d, submittedAt: d.submittedAt?.toDate?.() ?? new Date() };
  });
  return normalizePicks(rows).filter(p => p.status && p.status !== "pending");
}

function MyPicksPage({ user, userStats, onBalanceChange, refreshLeagues, userLeagues = [], onAfterSettle, onPendingCountChange }) {
  // FIX #8: use userLeagues from root — no separate loadMyLeagues call needed
  const [filter, setFilter]       = useState("All");
  const [leagueFilter, setLeagueFilter] = useState("all"); // "all" | leagueId
  const [allPicks, setAllPicks]   = useState([]);
  // Bubble pending pick count to root so TopNav badge stays current
  useEffect(() => {
    const count = allPicks.filter(p => p.status === 'pending').length;
    onPendingCountChange?.(count);
  }, [allPicks, onPendingCountChange]);
  const myLeagues = userLeagues.filter(l => !l.isEnded && l.status !== "ended");  // active leagues only
  const [loadingPicks, setLoadingPicks] = useState(true);
  const [settling, setSettling]   = useState(false);
  const settlingRef               = useRef(false); // FIX: readable synchronously in closures — useState reads stale value
  const [manualSettling, setManualSettling] = useState(false);
  const [showRetryBtn, setShowRetryBtn] = useState(false);
  useEffect(() => { const t = setTimeout(() => setShowRetryBtn(true), 10000); return () => clearTimeout(t); }, []);
  const [cleaning, setCleaning]         = useState(false);
  const [expandedParlays, setExpandedParlays] = useState({}); // { [pickId]: bool }
  const [liveScores, setLiveScores] = useState({});
  const [cashoutState, setCashoutState] = useState({});
  const [scoresLoading, setScoresLoading] = useState(false);
  const [sharePick, setSharePick]   = useState(null);
  const [selectedPick, setSelectedPick] = useState(null); // for detail modal
  const [showNotifPrompt, setShowNotifPrompt] = useState(false);

  // -- Shared settle helper — centralises settle → update → activity write ------
  const runSettlement = useCallback(async (pendingPicks, opts = {}) => {
    if (!pendingPicks.length || settlingRef.current) return [];
    settlingRef.current = true;
    if (opts.manual) setManualSettling(true); else if (!opts.silent) setSettling(true);
    try {
      // SERVER-SETTLEMENT (fix 4.1 step 2): pick up results the server wrote.
      const nowSettled = await fetchServerSettledPicks(user.uid, pendingPicks);
      if (nowSettled.length > 0) {
        setAllPicks(prev => prev.map(p => {
          const updated = nowSettled.find(s => s.id === p.id);
          return updated ? { ...p, ...updated } : p;
        }));
        // FIX: Wrap all non-critical calls in try/catch. If any of these throw
        // (updateUserStats, notifySettlement, etc.), the league balance update
        // block below would be skipped entirely — leaving balances uncredited.
        try { await updateUserStats(user.uid, user.displayName || user.email); } catch {}
        try {
          const freshBalance = await getUserBalance(user.uid);
          if (freshBalance !== null) onBalanceChange?.(freshBalance);
        } catch {}
        try { notifySettlement(nowSettled); } catch {}
        try { incrementSettlementCount(); } catch {}
        // ── Pulse mirror status sync ──────────────────────────────────────
        // For each pick that just settled in this client run, update its
        // /publicPicks/{pickId} mirror so the social feed reflects the new
        // status. Fire-and-forget; the helper swallows missing-doc errors
        // (mirror may not exist for picks predating Phase 3a). Server-side
        // CF settlement (cron) gets covered by the Phase 3b CF trigger.
        for (const s of nowSettled) {
          updatePulseMirrorStatus(s.id, s.status, s.points ?? 0).catch(() => {});
        }
        setTimeout(() => {
          if (shouldPromptForNotifications()) setShowNotifPrompt(true);
        }, 2500);
        try {
          const uname = user.displayName || user.email?.split("@")[0] || "Anonymous";
          const { getFirestore, collection, query, where, getDocs, updateDoc } = await import("firebase/firestore");
          const dbS = getFirestore(app);
          for (const s of nowSettled) {
            if (s.leagueId) {
              // Try to UPDATE the existing pending activity event for this pick.
              // This keeps the Den feed clean — one entry per pick, status updated in place.
              try {
                const evCol = collection(dbS, "leagues", s.leagueId, "activity");
                // Try by pickId first (new events have it), then fallback to userId+label (old events)
                let evSnap = null;
                const evQ = query(evCol, where("pickId", "==", s.id));
                evSnap = await getDocs(evQ);
                if (evSnap.empty && s.label) {
                  // Fallback: match by userId + label for old events that lack pickId
                  const fallbackQ = query(evCol, where("userId", "==", user.uid), where("label", "==", s.label));
                  evSnap = await getDocs(fallbackQ);
                }
                if (!evSnap.empty) {
                  await updateDoc(evSnap.docs[0].ref, {
                    status: s.status, points: s.points ?? 0,
                    settledAt: new Date(),
                    pickId: s.id, // backfill pickId for future lookups
                  });
                } else {
                  await writeLeagueActivity(user.uid, [s.leagueId], {
                    type: s.type || 'pick', status: s.status, username: uname,
                    label: s.label, sport: s.sport,
                    gameLabel: s.gameLabel || "",
                    odds: s.odds || null,
                    points: s.points, wager: s.wager,
                    pickId: s.id,
                  });
                }
              } catch (actErr) { console.warn("[Torch] Activity update failed:", actErr.message); }
            }
          }
        } catch {}
        if (opts.manual) window.torchToast?.(`Settled ${nowSettled.length} pick${nowSettled.length > 1 ? "s" : ""}.`, "success");

        // Win celebration
        const wins = nowSettled.filter(s => s.status === "won");
        if (wins.length > 0) {
          const totalWon = wins.reduce((sum, s) => sum + (s.points ?? 0) + (s.wager ?? 0), 0);
          haptic.success();
          recordDailyPick();
          // Pick'em wins carry no points payout (wager 0, points 0), so the
          // "+0 pts" suffix reads like a broken wager. Only append the points
          // when there are actual points to report.
          const winMsg = totalWon > 0
            ? `${wins.length} pick${wins.length > 1 ? "s" : ""} hit. +${totalWon.toLocaleString()} pts`
            : `${wins.length} pick${wins.length > 1 ? "s" : ""} hit.`;
          setTimeout(() => window.torchToast?.(winMsg, "success"), opts.manual ? 1500 : 300);
        }

        // SERVER-SETTLEMENT (fix 4.1 step 2): the server already credited the
        // league balance / W-L / streak in the same run that settled these
        // picks, so the app-side credit block (updateLeagueMemberBalance +
        // leagueCredited writes) is gone.

        // Refresh nav league list so top-bar balances reflect the settlement
        refreshLeagues?.();
        // Bust LeaguesPage standings + activity cache so Den updates immediately
        onAfterSettle?.();
      } else if (opts.manual) {
        window.torchToast?.("No new results yet — picks settle automatically within a few minutes of the final.", "info");
      }
      return nowSettled;
    } catch (e) {
      console.error('[Torch] Settlement error:', e);
      return [];
    } finally {
      settlingRef.current = false;
      setSettling(false);
      setManualSettling(false);
    }
  }, [user?.uid, user?.displayName, user?.email, onBalanceChange, refreshLeagues, onAfterSettle]);

  // SERVER-SETTLEMENT (fix 4.1 step 2): while My Picks is open, check every
  // 90s (tab visible) for picks the server has settled since they loaded.
  // (allPicksRef is declared below with the live-score polling refs.)
  useEffect(() => {
    if (!user?.uid) return;
    const iv = setInterval(() => {
      if (document.visibilityState !== "visible") return;
      const pending = allPicksRef.current.filter(p => p.status === "pending");
      if (pending.length) runSettlement(pending, { silent: true }).catch(() => {});
    }, 90 * 1000);
    return () => clearInterval(iv);
  }, [user?.uid, runSettlement]);

  // -- Reconcile league balances for already-settled picks ---------------------
  // Runs once on mount. Finds settled picks where leagueCredited is NOT true,
  // meaning neither the Cloud Function nor the client settlement path managed to
  // apply the league balance credit. This is a narrow safety net — not a full
  // balance recomputation (the old approach couldn't account for paydays/cashouts).
  // ── RECONCILER REMOVED ──────────────────────────────────────────────────
  // The reconcileLeagueBalances function has been removed. It was a band-aid
  // for missing deductLeagueWager calls (now fixed at the source). It re-credited
  // settled picks that were already properly handled, causing double-credits.

  // -- STALE PICK WATCHDOG -----------------------------------------------------
  // Any pick still pending 4+ hours after commenceTime is definitively stuck.
  // Even the longest games (baseball extra innings, soccer + ET + PKs) finish in ~5h.
  // This runs automatically on load (silent) AND is available as a manual action.
  //
  // 4h covers the longest possible game (baseball extra innings, hockey OT, etc.)
  // Any pick still pending 4h after game time is definitively done — settle or void it.
  const STALE_THRESHOLD_MS = 4 * 60 * 60 * 1000; // 4 hours

  const findStalePicks = useCallback((picks) => {
    const now = Date.now();
    return picks.filter(p => {
      if (p.status !== "pending") return false;
      // Skip outright/tournament picks — these are multi-day events (e.g. Masters)
      // where commenceTime is the tournament start, not a single game
      // Skip all outright/tournament picks — multi-day events
      if (p.type === "outright") return false;
      if (p.type === "ml" && (p.label?.includes("to Win") || p.label?.includes("Top 5") || p.label?.includes("Top 10") || p.label?.includes("Top 20") || p.label?.includes("Make Cut"))) return false;
      if (p.sportKey?.includes("_winner") || p.sportKey?.includes("_top_") || p.sportKey?.includes("_make_cut") || p.sportKey?.includes("golf_")) return false;
      // Use commenceTime first; fall back to submittedAt for picks placed mid-game
      // where commenceTime was never stored (game already started when pick was made)
      const resolveTime = (ts) => {
        if (!ts) return null;
        if (ts?.toDate) return ts.toDate().getTime();
        if (ts?.seconds) return ts.seconds * 1000;
        if (ts?._seconds) return ts._seconds * 1000;
        const ms = new Date(ts).getTime();
        return isNaN(ms) ? null : ms;
      };
      // LIVE BETS: a live bet's commenceTime is the game's START, which is already
      // in the past when the bet is placed — so anchoring staleness on commenceTime
      // would void a freshly-placed live bet on next load (this silently voided
      // real live bets and refunded them). Judge live bets from submittedAt instead:
      // a live bet is stale only if it's been PENDING (unsettled) for 4h+ since it
      // was placed, regardless of when the game started.
      if (p.isLiveBet) {
        const placedAt = resolveTime(p.submittedAt) ?? resolveTime(p.createdAt);
        if (!placedAt) return false; // no submit time → don't void (safer)
        return (now - placedAt) > STALE_THRESHOLD_MS;
      }
      const gameTime = resolveTime(p.commenceTime) ?? resolveTime(p.submittedAt) ?? resolveTime(p.createdAt);
      if (!gameTime) return false;
      return (now - gameTime) > STALE_THRESHOLD_MS;
    });
  }, [STALE_THRESHOLD_MS]);

  // ROUND 8: autoVoidStalePicks removed -- see the load-chain comment below.

  // Manual cleanup button (Profile page) -- ROUND 8: settle-only, never voids.
  // Previously this voided + refunded client-side at the 4h threshold, which
  // raced the server's grace window (and mishandled torch/spot-me stake
  // splits). Now it force-runs a settlement attempt on stale-looking picks
  // and reports honestly; anything truly stuck is left for settleAllPicks'
  // grace-window auto-void, which refunds with the correct split semantics.
  const cleanStalePicks = useCallback(async () => {
    if (cleaning) return;
    const stale = findStalePicks(allPicks);
    if (stale.length === 0) {
      window.torchToast?.("No stuck picks found.", "info");
      return;
    }
    setCleaning(true);
    try {
      // SERVER-SETTLEMENT (fix 4.1 step 2): the server settles every pick
      // (and auto-voids with a refund after the grace window); this only
      // checks whether it already has.
      let settledIds = new Set();
      try {
        const nowSettled = await fetchServerSettledPicks(user.uid, stale);
        nowSettled.forEach(s => settledIds.add(s.id));
        if (nowSettled.length > 0) {
          setAllPicks(prev => prev.map(p => {
            const s = nowSettled.find(x => x.id === p.id);
            return s ? { ...p, ...s } : p;
          }));
          refreshLeagues?.();
        }
      } catch (e) { console.warn("[Torch] cleanStalePicks check failed:", e.message); }
      const stuck = stale.length - settledIds.size;
      if (settledIds.size > 0 && stuck === 0) {
        window.torchToast?.(`Settled ${settledIds.size} stuck pick${settledIds.size > 1 ? "s" : ""}.`, "success");
      } else if (settledIds.size > 0) {
        window.torchToast?.(`Settled ${settledIds.size}. ${stuck} still waiting on results -- they'll void with a refund automatically if no result arrives within the grace window.`, "info");
      } else {
        window.torchToast?.(`${stuck} pick${stuck > 1 ? "s are" : " is"} still waiting on results. If no result arrives within the grace window (7 days; 2 for MMA/Boxing), they'll void with a full refund automatically.`, "info");
      }
    } catch (e) {
      console.error("[Torch] cleanStalePicks failed:", e);
      window.torchToast?.("Cleanup failed. Try again.", "error");
    } finally { setCleaning(false); }
  }, [allPicks, user?.uid, cleaning, findStalePicks, refreshLeagues]);


  // Load picks then auto-settle any pending ones
  //
  // PERF FIX (render): the spinner used to stay up through the ENTIRE chain —
  // loadPicks → setAllPicks → await runSettlement (and formerly a per-pick
  // auto-void write loop); everything blocked the spinner via .finally().
  // Result: users with stuck/pending picks stared at a spinner for seconds while
  // network round-trips ran, even though the pick data was already in hand.
  //
  // Now: clear the spinner the moment picks are in state. Void + settle run in
  // the background and patch the list in via their own setAllPicks(prev => …)
  // updates. The page renders instantly; settlement results stream in after.
  useEffect(() => {
    if (!user) return;
    setLoadingPicks(true);
    loadPicks(user.uid)
      .then((rawPicks) => {
        const picks = normalizePicks(rawPicks);
        setAllPicks(picks);
        // Picks are in state — render NOW. Don't gate display on settlement.
        setLoadingPicks(false);

        // Background: void stale, then settle remaining pending. Fire-and-forget.
        // Each updates the list via its own setAllPicks(prev => …) patch, so the
        // user sees results appear without the initial render waiting on them.
        (async () => {
          // ROUND 8: client auto-void REMOVED. The 4h client void raced the
          // server's 7-day (2-day MMA/Boxing) grace window and always won,
          // permanently voiding picks the cron would still have settled --
          // including MMA main events (card commence + 4h lands mid-card),
          // co-staked torch/spot-me picks (whose stake split only the server
          // handles), and pickem picks (voided with no possible refund).
          // Voiding is now exclusively settleAllPicks' autoVoidPick, which
          // waits out the grace window and splits refunds correctly.

          // SERVER-SETTLEMENT (fix 4.1 step 2): nothing to settle here -- the
          // picks were just read fresh. The 90s poll below picks up results.
        })();
      })
      .catch(e => {
        console.error('[Torch] loadPicks chain FAILED:', e);
        setAllPicks([]);
        setLoadingPicks(false);
      });
    // INTENT: load picks exactly once per user session, not on every callback
    // recreation. `runSettlement` is stable enough for
    // our purposes (they recreate only when their own props/state change, all
    // of which also depend on user.uid). Adding them to deps would cause picks
    // to re-fetch unnecessarily on minor parent re-renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.uid]);

  // -- Foreground FCM message listener -----------------------------------------
  useEffect(() => {
    let unsub = () => {};
    listenForForegroundMessages((payload) => {
      const { title, body } = payload.notification || {};
      if (title) window.torchToast?.(`${title}${body ? ` — ${body}` : ""}`, "success");
    }).then(fn => { unsub = fn; }).catch(() => {});
    return () => unsub();
  }, []);

  // Re-settle when user returns to the tab (app comes back into focus)
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState !== "visible") return;
      const pending = allPicksRef.current.filter(p => p.status === "pending");
      if (pending.length > 0 && !settlingRef.current) {
        runSettlementRef.current(pending);
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // FIX #2: stable ref for runSettlement so the polling effect below can call
  // the latest version without listing it as a dependency (which would restart
  // the interval on every settlement, causing interval thrash).
  const runSettlementRef = useRef(runSettlement);
  useEffect(() => { runSettlementRef.current = runSettlement; }, [runSettlement]);

  // -- Live score polling + re-settle on completion -----------------------------
  // Stable refs so poll closures always see latest state without restarting interval
  const allPicksRef = useRef(allPicks);
  useEffect(() => { allPicksRef.current = allPicks; }, [allPicks]);

  // Whether the user currently has any live-bet pending picks (placed during a live game)
  const hasLivePending = allPicks.some(p => p.status === 'pending' && p.isLiveBet && p.gameId);
  // Total pending pick count — restart poll when picks are added or fully settled
  const pendingCount = allPicks.filter(p => p.status === 'pending' && p.gameId).length;

  useEffect(() => {
    if (pendingCount === 0) return;

    // Live bets need faster polling — game could end any minute
    const INTERVAL_MS = hasLivePending ? 30 * 1000 : 60 * 1000;

    const poll = async () => {
      setScoresLoading(true);
      try {
        const scorablePicks = allPicksRef.current.filter(p => p.status === 'pending' && p.gameId);
        if (!scorablePicks.length) return;
        const scores = await fetchLiveScoresForPicks(scorablePicks);
        if (Object.keys(scores).length > 0) setLiveScores(prev => ({ ...prev, ...scores }));

        // Settle any picks whose game just completed
        const currentPending = allPicksRef.current.filter(p => p.status === 'pending' && p.gameId);
        const newlyComplete = currentPending.filter(p => {
          if (scores[p.gameId]?.completed !== true) return false;
          // SAFETY: Don't settle if game started less than 2 hours ago —
          // the API sometimes returns completed:true for in-progress games
          const ct = p.commenceTime;
          if (ct) {
            const ms = ct?.toDate ? ct.toDate().getTime() : ct?.seconds ? ct.seconds*1000 : new Date(ct).getTime();
            if (!isNaN(ms) && (Date.now() - ms) < 2 * 60 * 60 * 1000) {
              __DEV__ && console.info(`[Torch] Skipping premature settlement for ${p.label} — game < 2h old`);
              return false;
            }
          }
          return true;
        });
        if (newlyComplete.length > 0) await runSettlementRef.current(newlyComplete);

        // For live bets: also force a settlement attempt if still pending 3.5h after
        // submission — handles cases where the scores API never flips completed=true
        const resolveTs = (ts) => {
          if (!ts) return null;
          if (ts?.toDate) return ts.toDate().getTime();
          if (ts?.seconds) return ts.seconds * 1000;
          if (ts?._seconds) return ts._seconds * 1000;
          const ms = new Date(ts).getTime();
          return isNaN(ms) ? null : ms;
        };
        const liveBetsPastThreshold = currentPending.filter(p => {
          if (!p.isLiveBet || newlyComplete.find(c => c.id === p.id)) return false;
          const submitted = resolveTs(p.submittedAt) ?? resolveTs(p.createdAt);
          return submitted && (Date.now() - submitted) > 3.5 * 60 * 60 * 1000;
        });
        if (liveBetsPastThreshold.length > 0) {
          await runSettlementRef.current(liveBetsPastThreshold);
        }
      } catch { /* silent */ }
      finally { setScoresLoading(false); }
    };

    poll();
    const interval = setInterval(poll, INTERVAL_MS);
    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingCount, hasLivePending]);

  // -- Pick projection helper — is the user currently winning this bet? --------
  const getProjection = (pick, score) => {
    if (!score || score.homeScore === undefined) return null;
    const { homeScore, awayScore, completed } = score;
    const diff = homeScore - awayScore; // positive = home winning

    // Figure out which side the user picked
    const pickedHome = pick.side === 'home' || pick.side === 'over';

    if (pick.type === 'spread') {
      const spread = pick.line ?? 0; // home spread e.g. -6.5
      const covering = pickedHome
        ? diff > -spread          // picked home: they need diff > -spread (home covering)
        : diff < -spread;         // picked away: away covering
      return { winning: covering, diff, completed, type: 'spread' };
    }
    if (pick.type === 'total') {
      const combined = homeScore + awayScore;
      const target = pick.line ?? 0;
      const winning = pick.side === 'over' ? combined > target : combined < target;
      return { winning, diff: combined, target, completed, type: 'total', side: pick.side };
    }
    if (pick.type === 'ml') {
      const winning = pickedHome ? homeScore > awayScore : awayScore > homeScore;
      return { winning, diff, completed, type: 'ml' };
    }
    return null;
  };

  const statusFilters = ["All","Pending","Won","Lost","Voided"];
  const leagueFiltered = leagueFilter === "all"
    ? allPicks
    : leagueFilter === "__fun__"
    ? allPicks.filter(p => !p.leagueId || p.leagueId === "__fun__")
    : allPicks.filter(p => p.leagueId === leagueFilter);
  const filtered = filter === "All"
    ? leagueFiltered.filter(p => p.status !== "voided")
    : leagueFiltered.filter(p => p.status === filter.toLowerCase());
  // Voided picks — separated for cleanup display, not shown in main list by default

  // Group by date
  const sortedFiltered = [...filtered].sort((a, b) => {
    const getTs = p => tsToMillis(p.submittedAt) ?? tsToMillis(p.createdAt) ?? 0;
    return getTs(b) - getTs(a);
  });
  const grouped = sortedFiltered.reduce((acc, pick) => {
    const ts = pick.submittedAt?.toDate ? pick.submittedAt.toDate() : pick.submittedAt ? new Date(pick.submittedAt) : null;
    const date = ts ? ts.toLocaleDateString("en-US", { month:"short", day:"numeric" }) : "Recent";
    if (!acc[date]) acc[date] = [];
    acc[date].push(pick);
    return acc;
  }, {});

  const settledPicks  = leagueFiltered.filter(p => p.status === "won" || p.status === "lost");
  const totalWagered  = settledPicks.reduce((s, p) => s + (p.wager || 0), 0);
  const totalReturned = leagueFiltered.filter(p => p.status === "won")
                          .reduce((s, p) => s + (p.expectedPayout || p.wager || 0), 0);
  const totalProfit   = leagueFiltered.filter(p => p.points != null)
                          .reduce((s, p) => s + p.points, 0);
  const won  = leagueFiltered.filter(p => p.status === "won").length;
  const lost = leagueFiltered.filter(p => p.status === "lost").length;
  const pending = leagueFiltered.filter(p => p.status === "pending").length;
  const totalPoints = totalProfit; // keep name for existing refs
  const _STALE_MS = 4 * 60 * 60 * 1000; // 4h — matches auto-void threshold
  const _now = Date.now();
  const staleCount = allPicks.filter(p => {
    if (p.status !== "pending") return false;
    const _rts = (ts) => {
      if (!ts) return null;
      if (ts?.toDate) return ts.toDate().getTime();
      if (ts?.seconds) return ts.seconds * 1000;
      if (ts?._seconds) return ts._seconds * 1000;
      const ms = new Date(ts).getTime();
      return isNaN(ms) ? null : ms;
    };
    const t = _rts(p.commenceTime) ?? _rts(p.submittedAt) ?? _rts(p.createdAt);
    return t && (_now - t) > _STALE_MS;
  }).length;


  return (
    <>
    <div style={{ padding:"0 0 16px" }}>
      <div style={{ padding:"16px 16px 0" }}>

        {/* ── Page hero ──
            Phase 1 cleanup: removed the net-points headline ("+28,551 pts")
            because it conflated points-won-vs-lost with the user's record in
            a way that read as confusing — a user can be 66-96 (under .500)
            and still show a +28k headline due to underdog odds. Now leads
            with Win Rate as the primary identity number; W-L sits underneath
            as a slim record row. Heat line and 5-live pill unchanged. */}
        {(() => {
          const totalSettled = won + lost;
          const wr = totalSettled > 0 ? Math.round(won / totalSettled * 100) : null;
          // Hero number:
          //   - No picks yet → em-dash placeholder
          //   - All pending, none settled → "{n} in play"
          //   - At least one settled → win rate %
          const heroNumber = (() => {
            if (totalSettled === 0 && pending === 0) return "—";
            if (totalSettled === 0 && pending > 0) return `${pending} in play`;
            return `${wr}%`;
          })();
          const heroIsPercent = totalSettled > 0;
          return (
            <div style={{
              position:"relative", overflow:"hidden",
              borderRadius:24, padding:"22px 22px 20px", marginBottom:18,
              background:"linear-gradient(155deg, rgba(255,107,61,0.10) 0%, rgba(216,67,21,0.04) 38%, rgba(20,22,30,0.6) 100%), #0E0E12",
              border:"1px solid rgba(255,255,255,0.06)",
              boxShadow:"0 1px 0 rgba(255,255,255,0.04) inset, 0 12px 40px -12px rgba(0,0,0,0.5)",
            }}>
              <div style={{ position:"absolute", top:-40, right:-40, width:180, height:180,
                background:"radial-gradient(circle, rgba(255,107,61,0.18), transparent 60%)",
                pointerEvents:"none", filter:"blur(8px)",
              }}/>

              <div style={{ position:"relative", zIndex:1, display:"flex", alignItems:"flex-start", justifyContent:"space-between", gap:12 }}>
                <div style={{ minWidth:0 }}>
                  <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:6 }}>
                    {/* Rank pill — pulls from global userStats, not the filtered
                        league/status view. The rank is who you ARE, not who
                        you look like in this filter. The "MY PICKS" eyebrow
                        that used to live here was redundant with the tab label. */}
                    <UserRank stats={userStats} size="sm" />
                  </div>
                  <div style={{
                    fontFamily:FD, fontSize:34, fontWeight:800,
                    color: heroIsPercent
                      ? (wr >= 60 ? "#4ade80" : wr >= 45 ? "#fff" : "#f87171")
                      : "#fff",
                    letterSpacing:"-0.8px", lineHeight:1,
                    fontVariantNumeric:"tabular-nums",
                  }}>
                    {heroNumber}
                  </div>
                  {heroIsPercent && (
                    <div style={{ fontSize:10.5, fontWeight:700, color:"rgba(255,255,255,0.40)", letterSpacing:"0.10em", textTransform:"uppercase", marginTop:6 }}>
                      Win rate
                    </div>
                  )}
                </div>
                {pending > 0 && (
                  <span style={{
                    flexShrink:0,
                    display:"inline-flex", alignItems:"center", gap:5,
                    padding:"5px 11px 5px 9px", borderRadius:999,
                    background:"rgba(74,222,128,0.10)", border:"1px solid rgba(74,222,128,0.25)",
                    fontSize:11, fontWeight:700, color:"#4ade80",
                    fontVariantNumeric:"tabular-nums",
                  }}>
                    <span style={{ position:"relative", width:6, height:6, borderRadius:"50%", background:"#4ade80" }}>
                      <span style={{ position:"absolute", inset:-3, borderRadius:"50%", background:"rgba(74,222,128,0.4)", animation:"sonarPing 1.6s ease-out infinite" }}/>
                    </span>
                    {pending} live
                  </span>
                )}
              </div>

              {/* W-L record row — only when there's something settled.
                  Slimmer than the old 3-column stat row since Win rate is
                  already the hero number above. */}
              {totalSettled > 0 && (
                <div style={{ position:"relative", zIndex:1, marginTop:16, display:"flex", gap:20, alignItems:"baseline" }}>
                  <div>
                    <div style={{ fontFamily:FD, fontSize:18, fontWeight:800, color:"#4ade80", lineHeight:1, fontVariantNumeric:"tabular-nums", letterSpacing:"-0.2px" }}>
                      {won}
                    </div>
                    <div style={{ fontSize:9.5, fontWeight:700, color:"rgba(255,255,255,0.40)", letterSpacing:"0.10em", textTransform:"uppercase", marginTop:4 }}>
                      Won
                    </div>
                  </div>
                  <div style={{ width:1, height:26, background:"rgba(255,255,255,0.06)", alignSelf:"center" }}/>
                  <div>
                    <div style={{ fontFamily:FD, fontSize:18, fontWeight:800, color:"#f87171", lineHeight:1, fontVariantNumeric:"tabular-nums", letterSpacing:"-0.2px" }}>
                      {lost}
                    </div>
                    <div style={{ fontSize:9.5, fontWeight:700, color:"rgba(255,255,255,0.40)", letterSpacing:"0.10em", textTransform:"uppercase", marginTop:4 }}>
                      Lost
                    </div>
                  </div>
                </div>
              )}

            </div>
          );
        })()}

        {/* League filter */}
        <div className="hide-scrollbar" style={{ display:"flex", gap:6, marginBottom:10, overflowX:"auto", WebkitOverflowScrolling:"touch", scrollbarWidth:"none", paddingBottom:2 }}>
            {[{ id:"all", name:"All Leagues" }, ...myLeagues, { id:"__fun__", name:"Practice" }].map(l => {
              const ct = l.id === "all" ? allPicks.length
                : l.id === "__fun__" ? allPicks.filter(p => !p.leagueId || p.leagueId === "__fun__").length
                : allPicks.filter(p => p.leagueId === l.id).length;
              const isActive = leagueFilter === l.id;
              return (
                <button key={l.id} onClick={() => { haptic.light(); setLeagueFilter(l.id); setFilter("All"); }} className="no-press" style={{
                  padding:"7px 13px", borderRadius:999, cursor:"pointer",
                  // White-on-dark when active — matches the Games tab pill system
                  border: isActive ? "1px solid transparent" : "1px solid transparent",
                  background: isActive ? "#fff" : "rgba(255,255,255,0.04)",
                  color: isActive ? "#08080A" : "rgba(255,255,255,0.55)",
                  fontSize:12, fontWeight: isActive ? 700 : 600, whiteSpace:"nowrap", flexShrink:0,
                  transition:"all 0.15s", display:"flex", alignItems:"center", gap:6,
                  boxShadow: isActive ? "0 4px 16px rgba(0,0,0,0.3), 0 1px 0 rgba(255,255,255,0.4) inset" : "none",
                  letterSpacing:"-0.005em",
                }}>
                  <span>{l.name}</span>
                  {ct > 0 && (
                    <span style={{
                      fontSize:10, fontWeight:700, padding:"1.5px 6px", borderRadius:8, fontFamily:FM,
                      fontVariantNumeric:"tabular-nums",
                      background: isActive ? "rgba(14,14,18,0.10)" : "rgba(255,255,255,0.08)",
                      color: isActive ? "#08080A" : "rgba(255,255,255,0.45)",
                    }}>{ct}</span>
                  )}
                </button>
              );
            })}
          </div>

        {/* Status filter */}
        <div className="hide-scrollbar" style={{ display:"flex", gap:6, marginBottom:16, overflowX:"auto", WebkitOverflowScrolling:"touch", scrollbarWidth:"none", paddingBottom:2 }}>
          {statusFilters.map(f => {
            // Status filters keep semantic color (won=green, lost=red, etc.)
            // because they ARE state — but only when active. Inactive: neutral.
            const pillColor = { All:"#fff", Pending:"#fbbf24", Won:"#4ade80", Lost:"#f87171", Voided:"rgba(255,255,255,0.45)" }[f] || "#fff";
            const isActive = filter === f;
            const count = f === "All"
              ? leagueFiltered.filter(p => p.status !== "voided").length
              : leagueFiltered.filter(p => p.status === f.toLowerCase()).length;
            return (
              <button key={f} onClick={() => { haptic.light(); setFilter(f); }} className="no-press" style={{
                padding:"7px 14px", borderRadius:999, cursor:"pointer",
                border: isActive ? `1px solid ${pillColor}40` : "1px solid transparent",
                background: isActive
                  ? (f === "All" ? "rgba(255,255,255,0.10)" : `${pillColor}14`)
                  : "rgba(255,255,255,0.04)",
                color: isActive ? pillColor : "rgba(255,255,255,0.50)",
                fontSize:12, fontWeight: isActive ? 700 : 600, whiteSpace:"nowrap",
                transition:"all 0.15s", display:"flex", alignItems:"center", gap:6,
                letterSpacing:"-0.005em",
              }}>
                <span>{f}</span>
                {count > 0 && (
                  <span style={{
                    fontSize:10, fontWeight:700, padding:"1.5px 6px", borderRadius:8, fontFamily:FM,
                    fontVariantNumeric:"tabular-nums",
                    background: isActive ? `${pillColor}22` : "rgba(255,255,255,0.08)",
                    color: isActive ? pillColor : "rgba(255,255,255,0.45)",
                  }}>{count}</span>
                )}
              </button>
            );
          })}
        </div>

        {(settling || manualSettling) && (
          <div style={{
            display:"flex", alignItems:"center", gap:8,
            padding:"10px 14px", marginBottom:12,
            background:"rgba(251,191,36,0.08)", border:"1px solid rgba(251,191,36,0.22)",
            borderRadius:12, fontSize:13, color:"#fbbf24", fontWeight:600,
          }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" style={{ animation:"spin 1.4s linear infinite" }}>
              <path d="M21 12a9 9 0 1 1-6.219-8.56"/>
            </svg>
            {manualSettling ? "Checking for results…" : "Checking results…"}
          </div>
        )}

        {/* Manual settle — subtle, only appears after 10s */}
        {!settling && !manualSettling && !loadingPicks && showRetryBtn && allPicks.some(p => p.status === "pending") && (
          <div style={{ marginBottom:12, display:"flex", flexDirection:"column", gap:6 }}>
            <button
              onClick={() => {
                const pending = allPicks.filter(p => p.status === "pending");
                runSettlement(pending, { manual: true });
              }}
              style={{
                width:"100%", padding:"8px 14px", borderRadius:10,
                border:"1px solid rgba(255,255,255,0.06)", background:"rgba(255,255,255,0.02)",
                color:"rgba(255,255,255,0.42)", fontSize:12, fontWeight:500, cursor:"pointer",
                display:"flex", alignItems:"center", justifyContent:"center", gap:6,
                transition:"all 0.15s",
              }}
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="23 4 23 10 17 10"/>
                <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
              </svg> Check for results
            </button>
            {/* Stale pick cleanup — for picks >3 days old still pending */}
            {staleCount > 0 && (
                <button
                  onClick={cleanStalePicks}
                  disabled={cleaning}
                  style={{
                    width:"100%", padding:"10px 14px", borderRadius:10,
                    border:`1px dashed ${T.inkMuted}`, background:"transparent",
                    color:T.inkSec, fontSize:13, fontWeight:600, cursor:"pointer",
                    display:"flex", alignItems:"center", justifyContent:"center", gap:6,
                    opacity: cleaning ? 0.5 : 1,
                  }}
                >
                  {cleaning
                    ? <span style={{ display:"flex", alignItems:"center", gap:6 }}>
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ animation:"spin 1.2s linear infinite" }}>
                          <path d="M21 12a9 9 0 1 1-6.22-8.56"/>
                        </svg>
                        Cleaning up…
                      </span>
                    : <span style={{ display:"flex", alignItems:"center", gap:6 }}>
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M3 6h18"/>
                          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6"/>
                          <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                        </svg>
                        Clean up {staleCount} stale pick{staleCount>1?"s":""} (void + refund)
                      </span>
                  }
                </button>
            )}

            {/* SERVER-OWNED-LOCK (fix 4.1 step 3): the "restore wrongly voided
                picks" button is gone. It wrote pick status from the app (now
                refused by the rules), and putting a refunded void back to
                pending would have paid the stake out a second time. Admin
                corrections go through resetReportedPickCallable. */}

          </div>
        )}

        {loadingPicks ? (
          <div style={{ padding:"8px 0" }}>
            {[0,1,2].map(i => <PickCardSkeleton key={i} />)}
          </div>
        ) : (
          <>
            {/* Empty state for filters */}
            {Object.keys(grouped).length === 0 && (
              <div style={{ textAlign:"center", padding:"40px 20px" }}>
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.25)" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom:12, display:"inline-block" }}>
                  <rect x="4" y="3" width="16" height="18" rx="2"/>
                  <path d="M8 8h8M8 12h8M8 16h5"/>
                </svg>
                <div style={{ fontSize:15, fontWeight:700, color:"rgba(255,255,255,0.40)", marginBottom:6, fontFamily:FD }}>
                  {leagueFilter === "__fun__" ? "No fun picks yet" : `No ${filter.toLowerCase()} picks`}
                </div>
                <div style={{ fontSize:12, color:"rgba(255,255,255,0.40)", lineHeight:1.5 }}>
                  {leagueFilter === "__fun__" ? "Make a pick with \"Practice\" selected to track without joining a league." : "Try a different filter or make some picks on the Games tab."}
                </div>
              </div>
            )}
            {/* Grouped picks */}
            {Object.entries(grouped).map(([date, datePicks], gi) => (
              <div key={date} style={{ marginBottom:20, animation:"fadeUp 0.2s ease both", animationDelay:`${gi*50}ms` }}>
                <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:10, marginTop: gi > 0 ? 8 : 0 }}>
                  <div style={{ flex:1, height:"1px", background:"linear-gradient(90deg, transparent, rgba(255,255,255,0.08), rgba(255,255,255,0.03))" }}/>
                  <span style={{ fontSize:9, fontWeight:800, color:"rgba(255,255,255,0.36)", letterSpacing:"0.12em", padding:"3px 12px", background:"rgba(255,255,255,0.03)", borderRadius:10, border:"1px solid rgba(255,255,255,0.04)", fontFamily:FM }}>{date.toUpperCase()}</span>
                  <div style={{ flex:1, height:"1px", background:"linear-gradient(90deg, rgba(255,255,255,0.03), rgba(255,255,255,0.08), transparent)" }}/>
                </div>
                {datePicks.map((pick) => {
                  const fmtOdds = o => !o ? "" : o > 0 ? `+${o}` : `${o}`;
                  const isPending = pick.status === "pending";
                  const isWon     = pick.status === "won";
                  const isLost    = pick.status === "lost";
                  // Cashed-out picks are terminal: they have a final `points` delta
                  // (signed: positive if user cashed for a profit, negative if they
                  // cashed at a loss to avoid a worse one) and should render like a
                  // settled pick rather than sitting in the pending bucket.
                  const isCashed  = pick.status === "cashed";
                  const resultAnim = isWon ? "winGlow 1.5s ease" : isLost ? "loseShake 0.4s ease" : "none";
                  const score     = isPending ? liveScores[pick.gameId] : null;
                  const proj      = score ? getProjection(pick, score) : null;
                  const hasScore  = score && (score.homeScore > 0 || score.awayScore > 0 || score.period);
                  // Normalize period — may be {periodLabel, clockLabel} object from ESPN merge
                  const safePeriod = score?.period && typeof score.period === 'object' ? score.period.periodLabel : score?.period;
                  const safeClock = score?.period && typeof score.period === 'object' ? score.period.clockLabel : score?.clock;
                  const pickTR    = (isPending && safePeriod)
                    ? calcTimeRemaining(pick.sport, safePeriod, safeClock)
                    : { minsLeft: null, display: null, urgent: false };
                  const pickPeriod = (isPending && safePeriod)
                    ? formatLivePeriod(pick.sport, safePeriod, safeClock)
                    : { periodLabel: null, clockLabel: null };
                  // ── Cash-out eligibility gate ──────────────────────────────────────
                  // Real sportsbooks freeze cash-out late in games because near the end
                  // the outcome is effectively known, making cash-out a free-money
                  // extraction (they hedge this; we don't). Three guards, ANY can block:
                  //   1. Sport-specific time freeze — quarter-based sports freeze at
                  //      end of Q3, half-based sports freeze at halftime, period sports
                  //      (NHL) freeze at end of penultimate period.
                  //   2. Null-time → closed by default. If we couldn't parse clock (ESPN
                  //      lag, unsupported sport, etc.), don't assume the user can cash
                  //      out safely — err on the side of blocking.
                  //   3. Decided-game guard — for spreads and totals, if the current
                  //      margin would require a mathematically implausible swing in the
                  //      remaining time to flip, block cash-out.
                  const CASHOUT_FREEZE_MINS = {
                    // Quarter sports — freeze at end of Q3 (i.e., when entering final quarter)
                    NBA:    12,    // 4 × 12-min quarters → 12 min left = end of Q3
                    WNBA:   10,    // 4 × 10-min quarters → 10 min left = end of Q3
                    NFL:    15,    // 4 × 15-min quarters → 15 min left = end of Q3
                    NCAAF:  15,    // 4 × 15-min quarters → 15 min left = end of Q3
                    // Half sports — freeze at halftime
                    NCAAB:  20,    // 2 × 20-min halves → 20 min left = halftime
                    Soccer: 45,    // 90-min match → 45 min left = halftime
                    MLS:    45,    // halftime
                    EPL:    45,    // halftime
                    // Period sports — freeze at end of penultimate period (analogous)
                    NHL:    20,    // 3 × 20-min periods → 20 min left = end of 2nd period
                    // Inning-based — handled separately below
                    MLB:    null,
                  };
                  const sportKey = pick.sport || "";
                  const freezeMins = CASHOUT_FREEZE_MINS[sportKey] ?? 20; // unknown sport → conservative 20 min
                  const timeBlocked = pickTR.minsLeft === null
                    ? true // unknown time → block (was previously allowed — source of the Knicks -1.5 bug)
                    : pickTR.minsLeft <= freezeMins;
                  // MLB-specific: block from top of 8th onward regardless of parsed time
                  const mlbLate = sportKey === "MLB" && safePeriod && /(^|\s)(8|9|10|11|12)(st|nd|rd|th)?/i.test(String(safePeriod));
                  // Decided-game guard (spreads + totals only; MLs don't have a line).
                  // If we have a score AND a line AND the current margin clearly covers
                  // or misses by more than could plausibly swing in remaining time, block.
                  // Being conservative: only block when the game is already > halfway and
                  // the margin vs. line is > 2× what a single possession could add back.
                  let decidedBlock = false;
                  if (hasScore && score && pick.line != null && (pick.type === "spread" || pick.type === "total")) {
                    const hs = Number(score.homeScore) || 0;
                    const as = Number(score.awayScore) || 0;
                    const minsLeft = pickTR.minsLeft;
                    // Only apply this guard in the final third of the game where we have reliable clock
                    const sportGameLen = { NBA:48, NCAAB:40, WNBA:40, NFL:60, NCAAF:60, NHL:60, Soccer:90, MLS:90, EPL:90 }[sportKey];
                    if (sportGameLen && typeof minsLeft === "number" && minsLeft < sportGameLen * 0.33) {
                      if (pick.type === "spread") {
                        // Knicks -1.5: pick.side="away" or "home"; need margin covering the spread
                        const diff = (pick.side === "home" ? hs - as : as - hs); // + = pick winning
                        const needed = -pick.line; // -1.5 line → need margin > 1.5
                        const margin = diff - needed;
                        // If pick is LOSING the spread by more than 8 with < 1/3 of game left, decided
                        if (margin < -8) decidedBlock = true;
                      } else if (pick.type === "total") {
                        const total = hs + as;
                        if (pick.side === "over"  && total > pick.line + 8) decidedBlock = true;
                        if (pick.side === "under" && total > pick.line - 2 && minsLeft < sportGameLen * 0.15) decidedBlock = true;
                      }
                    }
                  }
                  const isCashable = isPending && hasScore && !score?.completed
                    && !timeBlocked
                    && !mlbLate
                    && !decidedBlock
                    && pick.wager > 0;
                  const cs = cashoutState[pick.id] || {};
                  // -- PARLAY CARD ----------------------------------------------------------
                  if (pick.isParlay) {
                    const legs = pick.legs || [];
                    const parlayExpanded = !!expandedParlays[pick.id];
                    const setParlayExpanded = (fn) => setExpandedParlays(prev => ({
                      ...prev, [pick.id]: typeof fn === "function" ? fn(!!prev[pick.id]) : fn,
                    }));
                    const fmtO = o => !o ? "" : o > 0 ? `+${o}` : `${o}`;
                     // Cashed parlays render in muted gray with the signed net delta;
                     // pick.points stores the cashout profit (cashoutValue - wager) so
                     // the formatting mirrors won/lost rather than a "potential" stub.
                     const parlayAccent = isWon?T.green:isLost?T.red:isCashed?T.inkSec:T.gold;
                     return (
                       <div key={pick.id}
                         onClick={() => setSelectedPick(pick)}
                         style={{
                           marginBottom:8, borderRadius:16, overflow:"hidden",
                           background:"rgba(14,14,18,0.55)",
                           backdropFilter:"blur(24px)", WebkitBackdropFilter:"blur(24px)",
                           border:`1px solid rgba(255,255,255,0.05)`,
                           cursor:"pointer", transition:"all 0.15s",
                           boxShadow:"0 2px 12px rgba(0,0,0,0.2), 0 1px 0 rgba(255,255,255,0.02) inset",
                           borderLeft:`3px solid ${parlayAccent}`,
                         }}
                         onMouseOver={e => e.currentTarget.style.boxShadow="0 6px 20px rgba(0,0,0,0.30)"}
                         onMouseOut={e => e.currentTarget.style.boxShadow="0 2px 12px rgba(0,0,0,0.2)"}
                       >
                         <div style={{ padding:"12px 14px" }}>
                           <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:8 }}>
                             <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                               <span style={{
                                 fontSize:10, fontWeight:700, letterSpacing:"0.08em",
                                 textTransform:"uppercase", color:"#fff",
                                 background: parlayAccent,
                                 padding:"2px 8px", borderRadius:4,
                               }}>{legs.length}-LEG PARLAY</span>
                               {(pick.leagueId && pick.leagueId !== "__fun__") ? (
                                 <span style={{ fontSize:10, color:T.inkSec }}>
                                   {(userLeagues.find(l=>l.id===pick.leagueId)?.name) || "League"}
                                 </span>
                               ) : (
                                 <span style={{ fontSize:9, fontWeight:700, color:"#FCD34D", background:"rgba(252,211,77,0.10)", padding:"1px 6px", borderRadius:4 }}>Practice</span>
                               )}
                             </div>
                             <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={T.inkMuted} strokeWidth="2.5" strokeLinecap="round"><path d="M9 18l6-6-6-6"/></svg>
                           </div>
                           <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start" }}>
                             <div style={{ flex:1, minWidth:0 }}>
                               <div style={{ fontSize:14, fontWeight:700, color:T.ink, marginBottom:3 }}>
                                 {legs.map(l=>l.label).join(" + ")}
                               </div>
                               <div style={{ fontSize:11, color:T.inkSec }}>
                                 Combined odds: {fmtO(pick.combinedOdds)}
                               </div>
                             </div>
                             <div style={{ textAlign:"right", flexShrink:0, marginLeft:12 }}>
                               <div style={{ fontSize:16, fontWeight:700, color: parlayAccent }}>
                                 {isWon ? `+${(pick.points ?? 0).toLocaleString()}`
                                   : isLost ? `–${(pick.wager ?? 0).toLocaleString()}`
                                   : isCashed ? `${(pick.points ?? 0) >= 0 ? "+" : "−"}${Math.abs(pick.points ?? 0).toLocaleString()}`
                                   : `+${((pick.expectedPayout ?? pick.wager ?? 0) - (pick.wager ?? 0)).toLocaleString()}`}
                               </div>
                               <div style={{ fontSize:10, color:T.inkMuted }}>
                                 {isWon || isLost ? "pts" : isCashed ? "Cashed Out" : "potential"}
                               </div>
                             </div>
                           </div>
                           <div style={{ display:"flex", gap:12, marginTop:8 }}>
                             <span style={{ fontSize:11, color:T.inkSec }}>
                               Wagered: <strong>{pick.wager}</strong>
                             </span>
                             {!isLost && !isCashed && (
                               <span style={{ fontSize:11, color:T.inkSec }}>
                                 To win: <strong style={{ color:T.green }}>{(pick.expectedPayout||0) - (pick.wager||0)}</strong> pts
                               </span>
                             )}
                           </div>
                           {legs.length > 0 && (
                             <button onClick={(e) => { e.stopPropagation(); setParlayExpanded(v=>!v); }} style={{
                               marginTop:10, width:"100%", padding:"7px 0",
                               background:"transparent", border:`1px dashed ${T.border}`,
                               borderRadius:8, fontSize:11, fontWeight:700,
                               color:T.inkMuted, cursor:"pointer",
                             }}>
                               {parlayExpanded ? "▴ Hide legs" : `▾ Show ${legs.length} legs`}
                             </button>
                           )}
                           {parlayExpanded && (
                             <div style={{ marginTop:8 }}>
                               {legs.map((leg, li) => (
                                 <div key={li} style={{
                                   padding:"8px 10px", borderRadius:8,
                                   background:T.cardAlt, border:`1px solid ${T.border}`,
                                   marginBottom:5,
                                 }}>
                                   <div style={{ fontSize:12, fontWeight:700, color:T.ink }}>{leg.label}</div>
                                   <div style={{ fontSize:11, color:T.inkSec, marginTop:2 }}>
                                     {leg.sport} · {leg.type?.toUpperCase()} · {fmtO(leg.odds)}
                                   </div>
                                 </div>
                               ))}
                             </div>
                           )}
                         </div>
                       </div>
                     );
                   }

                  // -- STANDARD PICK CARD ---------------------------------------------------
                  // Format game date/time for display on the card
                  const gameDateTime = (() => {
                    const ct = pick.commenceTime;
                    if (!ct) return null;
                    try {
                      const d = ct?.toDate ? ct.toDate() : new Date(ct);
                      const now = new Date();
                      const isToday = d.toDateString() === now.toDateString();
                      const isTomorrow = d.toDateString() === new Date(now.getTime()+86400000).toDateString();
                      const dateStr = isToday ? "Today"
                        : isTomorrow ? "Tomorrow"
                        : d.toLocaleDateString("en-US",{month:"short",day:"numeric"});
                      const timeStr = d.toLocaleTimeString("en-US",{hour:"numeric",minute:"2-digit"});
                      return `${dateStr} · ${timeStr}`;
                    } catch { return null; }
                  })();
                  const statusColors = { won:T.green, lost:T.red, pending:T.gold, voided:T.inkMuted, push:T.inkSec };
                  const accentColor = statusColors[pick.status] || T.inkMuted;

                  return (
                  <div
                    key={pick.id}
                    onClick={() => setSelectedPick(pick)}
                    style={{
                      marginBottom:8, borderRadius:16, overflow:"hidden",
                      background:"rgba(14,14,18,0.55)",
                      backdropFilter:"blur(24px)", WebkitBackdropFilter:"blur(24px)",
                      border:`1px solid rgba(255,255,255,0.05)`,
                      cursor:"pointer", transition:"all 0.15s",
                      boxShadow:"0 2px 12px rgba(0,0,0,0.2), 0 1px 0 rgba(255,255,255,0.02) inset",
                      borderLeft:`3px solid ${accentColor}`,
                      animation: resultAnim,
                    }}
                    onMouseOver={e => e.currentTarget.style.boxShadow="0 6px 20px rgba(0,0,0,0.30)"}
                    onMouseOut={e => e.currentTarget.style.boxShadow="0 2px 12px rgba(0,0,0,0.2)"}
                  >
                    {/* Live score ticker — shown on pending picks with active scores */}
                    {isPending && hasScore && (
                      <div style={{
                        padding:"7px 14px",
                        background: proj?.completed
                          ? (proj?.winning ? "rgba(22,163,74,0.08)" : T.redDim)
                          : `linear-gradient(90deg, ${T.navyDim}, rgba(29,78,216,0.04))`,
                        borderBottom:`1px solid ${T.border}`,
                        display:"flex", alignItems:"center", justifyContent:"space-between",
                      }}>
                        <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                          {!score.completed && (
                            <div style={{ display:"flex", alignItems:"center", gap:5 }}>
                              {pickPeriod.periodLabel ? (
                                <span style={{
                                  fontSize:10, fontWeight:700, padding:"2px 7px", borderRadius:4,
                                  background:"rgba(255,107,61,0.10)", color:T.red,
                                  letterSpacing:"0.04em", animation:"pulse 2s ease infinite",
                                  display:"flex", alignItems:"center", gap:4,
                                }}>
                                  <span style={{ width:5,height:5,borderRadius:"50%",background:T.red,display:"inline-block" }}/>
                                  {pickPeriod.periodLabel}{pickPeriod.clockLabel && ` · ${pickPeriod.clockLabel}`}
                                </span>
                              ) : (
                                <span style={{
                                  fontSize:9, fontWeight:700, padding:"2px 6px", borderRadius:4,
                                  background:T.red, color:"#fff", letterSpacing:"0.06em",
                                  animation:"pulse 2s ease infinite",
                                }}>LIVE</span>
                              )}
                            </div>
                          )}
                          {score.completed && <span style={{ fontSize:9, fontWeight:700, padding:"2px 6px", borderRadius:4, background:T.inkMuted, color:"#fff", letterSpacing:"0.06em" }}>FINAL</span>}
                          <span style={{ fontSize:13, fontWeight:700, color:T.ink, fontFamily:FD }}>
                            {pick.awayTeamFull?.split(' ').pop()||'Away'} {score.awayScore}
                            <span style={{ color:T.inkMuted, margin:"0 4px" }}>–</span>
                            {score.homeScore} {pick.homeTeamFull?.split(' ').pop()||'Home'}
                          </span>
                        </div>
                        {proj && (
                          <div style={{ display:"flex", alignItems:"center", gap:4, padding:"2px 8px", borderRadius:10,
                            background: proj.winning ? "rgba(22,163,74,0.12)" : T.redDim,
                            border:`1px solid ${proj.winning ? T.green : T.red}33` }}>
                            <span style={{ fontSize:11 }}>{proj.winning ? "✓" : "✗"}</span>
                            <span style={{ fontSize:11, fontWeight:700, color: proj.winning ? T.green : T.red }}>
                              {proj.type==="ml" ? (proj.winning?"Winning":"Losing")
                                : proj.type==="total" ? (proj.winning?(proj.side==="over"?"Going Over":"Going Under"):(proj.side==="over"?"Under pace":"Over pace"))
                                : (proj.winning?"Covering":"Trailing")}
                            </span>
                          </div>
                        )}
                      </div>
                    )}
                    {isPending && scoresLoading && !hasScore && pick.sportKey && (
                      <div style={{ padding:"6px 14px", background:T.cardAlt, borderBottom:`1px solid ${T.border}`, display:"flex", alignItems:"center", gap:6 }}>
                        <div style={{ width:32, height:10, borderRadius:4, background:T.border, animation:"pulse 1.2s ease infinite" }}/>
                        <div style={{ width:80, height:10, borderRadius:4, background:T.border, animation:"pulse 1.2s ease infinite", animationDelay:"0.2s" }}/>
                      </div>
                    )}

                    <div style={{ padding:"13px 14px" }}>
                      {/* Sport dot + label row */}
                      <div style={{ display:"flex", alignItems:"flex-start", gap:10, marginBottom:8 }}>
                        <div style={{
                          width:8, height:8, borderRadius:"50%", flexShrink:0, marginTop:5,
                          background:{NBA:T.gold,MLB:T.ember,NHL:T.blue,NCAAB:T.coral}[pick.sport] || T.inkMuted,
                        }}/>
                        <div style={{ flex:1, minWidth:0 }}>
                          {/* Game label + game time */}
                          <div style={{ fontSize:11, color:T.inkMuted, fontWeight:600, marginBottom:3, display:"flex", alignItems:"center", gap:6 }}>
                            <span style={{ overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{pick.gameLabel}</span>
                            {gameDateTime && (
                              <span style={{ flexShrink:0, fontSize:10, fontWeight:700, color:T.inkSec,
                                padding:"1px 6px", borderRadius:6, background:T.cardAlt }}>
                                {gameDateTime}
                              </span>
                            )}
                          </div>
                          {/* Pick label — the main prediction */}
                          <div style={{ fontSize:15, fontWeight:700, color:T.navy, lineHeight:1.25, marginBottom:3 }}>
                            {pickHeadline(pick)}
                            {pick.isLiveBet && <span style={{ fontSize:9, fontWeight:700, color:"#fff", background:T.green, padding:"1px 5px", borderRadius:4, letterSpacing:"0.06em", marginLeft:6 }}>LIVE</span>}
                          </div>
                          {/* Type + odds row */}
                          <div style={{ display:"flex", alignItems:"center", gap:6, flexWrap:"wrap" }}>
                            <span style={{ fontSize:10, fontWeight:700, padding:"2px 7px", borderRadius:6, background:T.cardAlt, color:T.inkSec }}>
                              {/* Pick'em/survivor: "ML" is sportsbook jargon for what is just
                                  "pick the winner" — render WINNER instead (scoringMode is
                                  stamped on every pick doc by submitPickCallable). */}
                              {(pick.scoringMode === "pickem" || pick.scoringMode === "survivor") && pick.type === "ml" ? "WINNER" : pick.type?.toUpperCase()}
                            </span>
                            {pick.odds && (
                              <span style={{ fontSize:11, fontWeight:700, color:T.coral }}>{fmtOdds(pick.odds)}</span>
                            )}
                            {(pick.leagueId && pick.leagueId !== "__fun__") ? (() => {
                              const lg = myLeagues.find(l => l.id === pick.leagueId);
                              return lg ? <span style={{ fontSize:10, fontWeight:700, padding:"2px 7px", borderRadius:6, background:T.navyDim, color:T.navy }}>{lg.name}</span> : null;
                            })() : (
                              <span style={{ fontSize:9, fontWeight:700, color:"#FCD34D", background:"rgba(252,211,77,0.10)", padding:"2px 6px", borderRadius:4 }}>Practice</span>
                            )}
                          </div>
                        </div>
                        {/* Right: status + pts + chevron */}
                        <div style={{ flexShrink:0, display:"flex", flexDirection:"column", alignItems:"flex-end", gap:5 }}>
                          <StatusBadge status={pick.status} />
                          {pick.wager > 0 && (
                            <div style={{ textAlign:"right" }}>
                              <div style={{ fontSize:14, fontWeight:700,
                                color: isWon ? T.green : isLost ? T.red : isCashed ? ((pick.points ?? 0) >= 0 ? T.green : T.red) : T.inkSec }}>
                                {isWon ? `+${(pick.points??0).toLocaleString()}`
                                  : isLost ? `–${(pick.wager??0).toLocaleString()}`
                                  : isCashed ? `${(pick.points ?? 0) >= 0 ? "+" : "−"}${Math.abs(pick.points ?? 0).toLocaleString()}`
                                  : `${((pick.expectedPayout||pick.wager||0)-(pick.wager||0)>0?"+":"")}${Math.max(0,(pick.expectedPayout||pick.wager||0)-(pick.wager||0)).toLocaleString()}`}
                              </div>
                              <div style={{ fontSize:9, color:T.inkMuted, fontWeight:600 }}>
                                {isWon?"won":isLost?"lost":isCashed?"cashed":"potential"} pts
                              </div>
                            </div>
                          )}
                          {/* Tap-to-view chevron */}
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={T.inkMuted} strokeWidth="2.5" strokeLinecap="round"><path d="M9 18l6-6-6-6"/></svg>
                        </div>
                      </div>

                      {/* Bottom row: wager strip + action buttons (don't bubble to modal) */}
                      {pick.wager > 0 && (
                        <div style={{
                          display:"flex", alignItems:"center", gap:8,
                          padding:"7px 10px", borderRadius:10, marginLeft:18,
                          background: isWon ? "rgba(22,163,74,0.07)" : isLost ? T.redDim : T.cardAlt,
                        }}>
                          <span style={{ fontSize:11, color:T.inkSec }}>
                            Wagered: <strong>{(pick.wager||0).toLocaleString()}</strong> pts
                          </span>
                          {isWon && <span style={{ fontSize:11, color:T.green, fontWeight:700, marginLeft:"auto" }}>+{(pick.points||0).toLocaleString()} pts returned</span>}
                          {!isWon && !isLost && pick.expectedPayout > 0 && (
                            <span style={{ fontSize:11, color:T.inkSec, marginLeft:"auto" }}>
                              To win: <strong style={{ color:T.green }}>{((pick.expectedPayout||0)-(pick.wager||0)).toLocaleString()}</strong>
                            </span>
                          )}
                        </div>
                      )}

                      {/* Action buttons row — stops propagation to avoid opening modal */}
                      <div onClick={e => e.stopPropagation()} style={{ display:"flex", gap:6, marginTop:8, marginLeft:18 }}>
                        <button
                          onClick={() => setSharePick(pick)}
                          style={{
                            background:"none", border:`1px solid ${T.border}`,
                            borderRadius:8, padding:"4px 10px", cursor:"pointer",
                            display:"flex", alignItems:"center", gap:4, color:T.inkMuted,
                          }}>
                          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/>
                            <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
                          </svg>
                          <span style={{ fontSize:10, fontWeight:600 }}>Share</span>
                        </button>

                        {/* Report Issue — settled picks only */}
                        {pick.status && pick.status !== "pending" && (
                          <button
                            onClick={async (e) => {
                              e.stopPropagation();
                              try {
                                const { getFirestore, collection, addDoc, serverTimestamp } = await import("firebase/firestore");
                                const db = getFirestore(app);
                                await addDoc(collection(db, "reports"), {
                                  pickId: pick.id,
                                  userId: user?.uid || "",
                                  username: user?.displayName || "",
                                  email: user?.email || "",
                                  label: pick.label || "",
                                  status: pick.status,
                                  sport: pick.sport || pick.sportKey || "",
                                  type: pick.type || "",
                                  gameLabel: pick.gameLabel || "",
                                  wager: pick.wager || 0,
                                  odds: pick.odds || 0,
                                  points: pick.points || 0,
                                  actualStat: pick.actualStat ?? null,
                                  finalScore: pick.finalScore || null,
                                  createdAt: serverTimestamp(),
                                });
                                window.torchToast?.("Report submitted!", "success");
                              } catch (err) {
                                window.torchToast?.("Couldn't submit report", "error");
                              }
                            }}
                            style={{
                              background:"none", border:`1px solid ${T.border}`,
                              borderRadius:8, padding:"4px 10px", cursor:"pointer",
                              display:"flex", alignItems:"center", gap:4, color:T.inkMuted,
                              fontSize:10, fontWeight:600,
                            }}>
                            <span style={{ fontSize:12 }}>⚠</span>
                            <span>Report</span>
                          </button>
                        )}

                          {/* Cash Out button — live pending picks only */}
                          {isCashable && (() => {
                            const coVal = cs.value ?? calcCashoutValue(pick.wager, pick.odds ?? pick.vig, cs.currentOdds ?? pick.odds ?? pick.vig);
                            const netDelta = coVal - pick.wager; // negative = loss, positive = partial win
                            return (
                              <button
                                disabled={cs.loading}
                                onClick={async () => {
                                  // First click: fetch live odds to price the cashout
                                  if (cs.value == null && !cs.loading) {
                                    setCashoutState(p => ({ ...p, [pick.id]: { loading: true } }));
                                    try {
                                      const oddsMap = await fetchLiveOdds([{ id: pick.gameId, sportKey: pick.sportKey }]);
                                      const lo = oddsMap[pick.gameId] || {};
                                      // Find current odds for the pick's type+side
                                      const currOdds =
                                        pick.type === 'ml'
                                          ? (pick.side === 'home' ? lo.homeML : lo.awayML)
                                          : (pick.type === 'spread'
                                              ? (pick.side === 'home' ? lo.homeSpreadOdds : lo.awaySpreadOdds)
                                              : (pick.side === 'over' ? lo.overOdds : lo.underOdds));
                                      // SERVER-CASHOUT (fix 1.6): no live line = no quote. The old
                                      // fallback to the pick's ORIGINAL odds priced every such cash-out
                                      // at 90% of the stake whatever the score; the server refuses it.
                                      if (currOdds == null) {
                                        setCashoutState(p => ({ ...p, [pick.id]: { loading: false } }));
                                        window.torchToast?.('No live line right now — try again in a minute.', 'info');
                                        return;
                                      }
                                      const val = calcCashoutValue(pick.wager, pick.odds ?? pick.vig, currOdds);
                                      setCashoutState(p => ({ ...p, [pick.id]: { loading: false, value: val, currentOdds: currOdds } }));
                                    } catch {
                                      setCashoutState(p => ({ ...p, [pick.id]: { loading: false } }));
                                      window.torchToast?.('Could not fetch live odds.', 'error');
                                    }
                                    return; // second click will confirm
                                  }
                                  // Second click: confirm + execute
                                  const confirmMsg = `Cash out now for ${coVal} pts?\n\n` +
                                    `Wagered: ${pick.wager} pts  ·  Potential: ${pick.expectedPayout ?? '—'} pts\n` +
                                    `Net: ${netDelta >= 0 ? '+' : ''}${netDelta} pts vs. wager`;
                                  window.torchConfirm(confirmMsg, async () => {
                                    setCashoutState(p => ({ ...p, [pick.id]: { ...p[pick.id], loading: true } }));
                                    let keepQuote = null; // set when the server re-prices
                                    try {
                                      // SERVER-CASHOUT (fix 1.6): the callable settles the pick AND credits
                                      // global + league balance atomically; nothing to credit here.
                                      const res = await cashOutPick(pick.id, coVal);
                                      const paid = res?.cashoutValue ?? coVal;
                                      window.torchToast?.(`Cashed out ${paid} pts ✓`, 'success');
                                      // PRACTICE-ONLY-GLOBAL (fix 2.6): a league cash-out credits the league only.
                                      if (!pick.leagueId) onBalanceChange?.(prev => Math.max(0, prev + paid));
                                      // Refresh picks list
                                      const freshRaw = await loadPicks(user.uid);
                                      const fresh = normalizePicks(freshRaw);
                                      setAllPicks(fresh);
                                      refreshLeagues?.();
                                    } catch (e) {
                                      const code = e?.code || "";
                                      if (code === "functions/aborted" && e?.details?.newValue) {
                                        // Line moved against the user: show the server's new price and
                                        // let them confirm again (second tap re-submits it).
                                        keepQuote = { loading: false, value: e.details.newValue, currentOdds: e.details.livePrice };
                                        window.torchToast?.(`Line moved — cash-out is now ${e.details.newValue} pts. Tap again to confirm.`, 'info');
                                      } else if (code === "functions/failed-precondition") {
                                        // Most often: the pick settled while the button was up.
                                        window.torchToast?.(e.message || 'This pick has already settled.', 'info');
                                        try { setAllPicks(normalizePicks(await loadPicks(user.uid))); } catch {}
                                      } else {
                                        window.torchToast?.(e?.message || 'Cash-out failed. Try again.', 'error');
                                      }
                                    } finally {
                                      setCashoutState(p => { const n={...p}; if (keepQuote) n[pick.id] = keepQuote; else delete n[pick.id]; return n; });
                                    }
                                  });
                                }}
                                style={{
                                  border:`1px solid ${cs.value != null ? T.coral : T.gold}`,
                                  background: cs.value != null
                                    ? `linear-gradient(135deg, ${T.coral}22, ${T.coral}11)`
                                    : T.goldDim,
                                  borderRadius:8, padding:"3px 8px", cursor: cs.loading ? "wait" : "pointer",
                                  display:"flex", alignItems:"center", gap:3,
                                  color: cs.value != null ? T.coral : T.gold,
                                  opacity: cs.loading ? 0.6 : 1,
                                  transition:"all 0.2s",
                                }}>
                                {cs.loading
                                  ? <span style={{ fontSize:10, fontWeight:700 }}>…</span>
                                  : cs.value != null
                                    ? <span style={{ display:"flex", alignItems:"center", gap:3 }}>
                                        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>
                                        <span style={{ fontSize:10, fontWeight:700 }}>{cs.value} pts</span>
                                      </span>
                                    : <span style={{ display:"flex", alignItems:"center", gap:3 }}>
                                        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>
                                        <span style={{ fontSize:10, fontWeight:700 }}>Cash Out</span>
                                      </span>
                                }
                              </button>
                            );
                          })()}
                        </div>
                      </div>

                  </div>
                  );
                })}
              </div>
            ))}

            {filtered.length === 0 && (
              <EmptyState icon="→" title="Nothing on the board" sub="Pick a game on the Games tab. It'll show up here." />
            )}
          </>
        )}
      </div>
    </div>
    {sharePick && (
      <SharePickModal pick={sharePick} user={user} onClose={() => setSharePick(null)} />
    )}
    {selectedPick && (
      <PickDetailModal
        pick={selectedPick}
        user={user}
        userLeagues={userLeagues}
        liveScore={liveScores[selectedPick.gameId]}
        onClose={() => setSelectedPick(null)}
      />
    )}
    {showNotifPrompt && (
      <NotificationPromptBanner userId={user?.uid} onDismiss={() => setShowNotifPrompt(false)} />
    )}
    </>
  );
}

/* ===========================================
   PAGE: RANKINGS
=========================================== */

/* ===========================================
   USER PROFILE MODAL
   Opens when a leaderboard username is tapped.
   Logged-in users only — shows public stats + recent picks.
=========================================== */

// --- PICK DETAIL MODAL ----------------------------------------------------
// Was: a "hi-tech receipt" with particle canvas, spinning conic-gradient
// border, CRT scan lines, grid pattern overlays, and shimmer animations on
// top of all that. Multiple effects competed with the actual data.
// Now: a clean elevated dark sheet with directional lighting, real type
// hierarchy, and a single accent color on the status. Reads like a polished
// product receipt — premium without trying to look like a spaceship cockpit.
function PickDetailModal({ pick, user, userLeagues = [], liveScore, onClose }) {
  const { closing, requestClose } = useSheetClose(onClose, 200);
  useModalOpen(!closing);  // Hide bottom nav while this modal is open

  if (!pick) return null;

  const isWon     = pick.status === "won";
  const isLost    = pick.status === "lost";
  const isPending = pick.status === "pending";
  const isVoided  = pick.status === "voided";
  const isPush    = pick.status === "push";
  const isCashed  = pick.status === "cashed";
  const isParlay  = !!pick.isParlay;
  const isPlayerProp = pick.type === "player_prop" || pick.type === "PLAYER_PROP";

  const fmtOdds = o => !o ? "—" : o > 0 ? `+${o}` : `${o}`;

  const getGameTime = () => {
    const ct = pick.commenceTime || (pick.legs?.[0]?.commenceTime);
    if (!ct) return null;
    try { const d = ct?.toDate ? ct.toDate() : new Date(ct); return { date: d.toLocaleDateString("en-US", { weekday:"short", month:"short", day:"numeric" }), time: d.toLocaleTimeString("en-US", { hour:"numeric", minute:"2-digit", hour12:true }), full: d }; } catch { return null; }
  };
  const gameTime = getGameTime();
  const league = userLeagues.find(l => l.id === pick.leagueId);

  const statusConfig = {
    won:     { label:"Won",        accent:"#4ade80", glow:"rgba(74,222,128,0.18)",  tint:"rgba(74,222,128,0.06)" },
    lost:    { label:"Lost",       accent:"#f87171", glow:"rgba(248,113,113,0.16)", tint:"rgba(248,113,113,0.05)" },
    pending: { label:"Pending",    accent:"#fbbf24", glow:"rgba(251,191,36,0.14)",  tint:"rgba(251,191,36,0.04)" },
    push:    { label:"Push",       accent:"#9CA3AF", glow:"rgba(156,163,175,0.10)", tint:"rgba(156,163,175,0.03)" },
    voided:  { label:"Void",       accent:"#9CA3AF", glow:"rgba(156,163,175,0.10)", tint:"rgba(156,163,175,0.03)" },
    cashed:  { label:"Cashed Out", accent:"#9CA3AF", glow:"rgba(156,163,175,0.10)", tint:"rgba(156,163,175,0.03)" },
  };
  const sc = statusConfig[pick.status] || statusConfig.pending;

  const potentialProfit = Math.max(0, (pick.expectedPayout || pick.wager || 0) - (pick.wager || 0));
  const cashedNet = isCashed ? Number(pick.points ?? 0) : 0;
  // Pick'em/survivor picks have no wager and no payout, so every wager-shaped
  // label below renders as "+0" / "–0" — reads like a broken bet. The pick doc
  // carries scoringMode (stamped by submitPickCallable); league lookup is the
  // fallback for robustness. In these modes the "profit" is the standings
  // point: +1 for a correct pick, nothing otherwise.
  const isNonWagerPick_ = (pick.scoringMode || league?.scoringMode || "wager") !== "wager";
  const profitLabel = isNonWagerPick_
    ? (isWon ? "+1" : isLost ? "0" : isPush ? "Push" : isVoided ? "Void" : "+1")
    : (isWon ? `+${(pick.points ?? 0).toLocaleString()}` : isLost ? `–${(pick.wager ?? 0).toLocaleString()}` : isPush ? "Push" : isVoided ? "Void" : isCashed ? `${cashedNet >= 0 ? "+" : "−"}${Math.abs(cashedNet).toLocaleString()}` : `+${potentialProfit.toLocaleString()}`);
  const profitSub = isNonWagerPick_
    ? (isWon ? "correct pick" : isLost ? "missed" : isPush ? "no point" : isVoided ? "no point" : "if correct")
    : (isWon ? "points won" : isLost ? "points lost" : isPush ? "refunded" : isVoided ? "refunded" : isCashed ? (cashedNet >= 0 ? "cashed out" : "cashed at loss") : "potential win");

  const submittedAt = (() => { const ts = pick.submittedAt || pick.createdAt; if (!ts) return null; try { const d = ts?.toDate ? ts.toDate() : new Date(ts); return d.toLocaleDateString("en-US", { month:"short", day:"numeric", year:"numeric", hour:"numeric", minute:"2-digit" }); } catch { return null; } })();
  const settledAt = pick.settledAt ? (() => { try { const d = pick.settledAt?.toDate ? pick.settledAt.toDate() : new Date(pick.settledAt); return d.toLocaleDateString("en-US", { month:"short", day:"numeric", hour:"numeric", minute:"2-digit" }); } catch { return null; } })() : null;

  const hasScore = liveScore && (liveScore.homeScore !== undefined);
  // Normalize period — ESPN merge may store it as {periodLabel, clockLabel} object instead of raw string
  const safeLivePeriod = liveScore?.period && typeof liveScore.period === 'object'
    ? liveScore.period.periodLabel || null
    : liveScore?.period || null;
  const safeLiveClock = liveScore?.period && typeof liveScore.period === 'object'
    ? liveScore.period.clockLabel || null
    : liveScore?.clock || null;
  const finalScore = pick.finalScore || (hasScore && liveScore.completed ? { home: liveScore.homeScore, away: liveScore.awayScore } : null);
  const awayTeam = pick.awayTeamFull || pick.gameLabel?.split(" @ ")?.[0] || "Away";
  const homeTeam = pick.homeTeamFull || pick.gameLabel?.split(" @ ")?.[1] || "Home";
  const awayScoreVal = finalScore ? finalScore.away : hasScore ? liveScore?.awayScore : null;
  const homeScoreVal = finalScore ? finalScore.home : hasScore ? liveScore?.homeScore : null;
  const showScores = awayScoreVal != null || homeScoreVal != null;
  const awayWinning = showScores && awayScoreVal > homeScoreVal;
  const homeWinning = showScores && homeScoreVal > awayScoreVal;
  const typeLabel = pick.type === "total" ? "Over/Under" : pick.type === "spread" ? "Spread" : pick.type === "ml" ? (isNonWagerPick_ ? "Winner" : "Moneyline") : pick.type === "outright" ? "Tournament Pick" : isPlayerProp ? "Player Prop" : pick.type?.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase()) || "Pick";

  // Gauge math
  const gaugeRatio = pick.wager > 0 ? Math.min(2.5, (pick.expectedPayout || pick.wager) / pick.wager) : 1;
  const gaugeOffset = Math.max(10, 157 - (gaugeRatio / 2.5) * 157);
  const gaugeLabel = isWon ? "WON" : isLost ? "LOST" : `${gaugeRatio.toFixed(1)}x`;

  const BG = "#06080E"; const CARD = "rgba(255,255,255,0.025)"; const BORD = "rgba(255,255,255,0.06)";
  const INK = "#fff"; const INK2 = "rgba(255,255,255,0.50)"; const INK3 = "rgba(255,255,255,0.20)";

  // ── Enhanced receipt helpers ──
  const impliedProb = (() => {
    const o = pick.combinedOdds || pick.odds;
    if (!o) return null;
    const p = o > 0 ? 100 / (o + 100) : Math.abs(o) / (Math.abs(o) + 100);
    return Math.round(p * 100);
  })();

  const placedAgo = (() => {
    const ts = pick.submittedAt || pick.createdAt;
    if (!ts) return null;
    try {
      const d = ts?.toDate ? ts.toDate() : ts?.seconds ? new Date(ts.seconds * 1000) : new Date(ts);
      const sec = Math.floor((Date.now() - d.getTime()) / 1000);
      if (sec < 60) return "just now";
      if (sec < 3600) return `${Math.floor(sec/60)}m ago`;
      if (sec < 86400) return `${Math.floor(sec/3600)}h ago`;
      return `${Math.floor(sec/86400)}d ago`;
    } catch { return null; }
  })();

  const gameCountdown = (() => {
    if (showScores || !gameTime?.full) return null;
    const ms = gameTime.full.getTime() - Date.now();
    if (ms <= 0) return null;
    const h = Math.floor(ms / 3600000);
    const m = Math.floor((ms % 3600000) / 60000);
    return h > 0 ? `${h}h ${m}m` : `${m}m`;
  })();

  const expandedLabel = (() => {
    const l = pick.label || "";
    const sport = (pick.sport || "").toUpperCase();
    const unit = sport === "NHL" ? "Goals" : sport === "MLB" ? "Runs" : "Points";
    if (pick.type === "total" || pick.type === "alt_total") {
      if (l.startsWith("O ")) return `Over ${l.slice(2)} Total ${unit}`;
      if (l.startsWith("U ")) return `Under ${l.slice(2)} Total ${unit}`;
    }
    // Spread/ML: prepend the team name so it's clear who was picked.
    //
    // ML is conditional. New picks store a self-describing label ("Kansas City
    // Chiefs ML") — prefixing those yields "Chiefs Kansas City Chiefs ML". But
    // picks submitted BEFORE that fix have a bare price ("-625") stored in
    // Firestore forever, and those still need the prefix or the card names no
    // team at all. So: prefix only when the label doesn't already contain the
    // team. Covers both eras without a data migration.
    const pickedTeamName = pick.side === "home" ? (homeTeam.split(" ").pop() || homeTeam)
      : pick.side === "away" ? (awayTeam.split(" ").pop() || awayTeam) : null;
    const labelNamesTeam = !!pickedTeamName && l.toLowerCase().includes(pickedTeamName.toLowerCase());
    if (pickedTeamName && !labelNamesTeam
        && (pick.type === "ml" || pick.type === "spread" || pick.type === "alt_spread" || pick.type === "h2h")) {
      return `${pickedTeamName} ${l}`;
    }
    return l;
  })();
  const pickedSide = pick.side; // "home" | "away" | "over" | "under"

  const isLiveGame = hasScore && !finalScore && !liveScore?.completed;
  const combinedScore = (awayScoreVal || 0) + (homeScoreVal || 0);
  const totalLine = (pick.type === "total" || pick.type === "alt_total") ? pick.line : null;

  return createPortal(
    <div onClick={() => requestClose()} style={{
      position:"fixed", inset:0, zIndex:300,
      background:"rgba(4,5,10,0.78)",
      backdropFilter:"blur(8px)",
      display:"flex", alignItems:"flex-end", justifyContent:"center",
      padding:"0 0 calc(80px + env(safe-area-inset-bottom, 0px))",
      animation: closing ? "slipFadeOut 0.18s ease both" : "fadeIn 0.18s ease",
      pointerEvents: closing ? "none" : "auto",
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        position:"relative", width:"100%", maxWidth:440,
        background:"#131520",
        borderRadius:"24px 24px 0 0",
        border:"1px solid rgba(255,255,255,0.06)",
        borderBottom:"none",
        maxHeight:"calc(92vh - env(safe-area-inset-top, 0px))",
        display:"flex", flexDirection:"column",
        animation: closing ? "sheetExitDown 0.2s cubic-bezier(0.4,0,1,1) both" : "sheetEnterUp 0.26s cubic-bezier(0.32,0.72,0,1)",
        pointerEvents: closing ? "none" : "auto",
        boxShadow:"0 1px 0 rgba(255,255,255,0.06) inset, 0 -16px 60px -8px rgba(0,0,0,0.6)",
        overflow:"hidden",
      }}>

          {/* ── STATUS HERO ──
              Single ambient color wash at the top tinted by status (won=green,
              lost=red, pending=amber). No more particle canvas, scan lines,
              spinning conic border, grid pattern, or shimmer. Just a clean
              radial glow + the actual data. */}
          <div style={{ position:"relative", padding:"22px 20px 26px", flexShrink:0, overflow:"hidden" }}>
            {/* Top ambient glow */}
            <div style={{
              position:"absolute", top:-100, left:"50%", transform:"translateX(-50%)",
              width:300, height:200,
              background:`radial-gradient(ellipse, ${sc.glow} 0%, transparent 65%)`,
              filter:"blur(20px)",
              pointerEvents:"none",
            }}/>
            {/* Subtle status tint underlay */}
            <div style={{
              position:"absolute", inset:0,
              background:`linear-gradient(180deg, ${sc.tint} 0%, transparent 60%)`,
              pointerEvents:"none",
            }}/>

            {/* Close button — matches slip drawer's close */}
            <button onClick={() => requestClose()} aria-label="Close" style={{
              position:"absolute", top:14, right:14, zIndex:2,
              width:36, height:36, borderRadius:"50%",
              background:"rgba(255,255,255,0.04)", border:"1px solid rgba(255,255,255,0.06)",
              color:"rgba(255,255,255,0.55)", cursor:"pointer",
              display:"flex", alignItems:"center", justifyContent:"center",
              transition:"background 0.15s",
            }}
              onMouseEnter={(e)=>(e.currentTarget.style.background="rgba(255,255,255,0.08)")}
              onMouseLeave={(e)=>(e.currentTarget.style.background="rgba(255,255,255,0.04)")}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
                <path d="M18 6L6 18M6 6l12 12"/>
              </svg>
            </button>

            <div style={{ position:"relative", textAlign:"center" }}>
              {/* Status pill — clean, no neon flicker animations */}
              <div style={{
                display:"inline-flex", alignItems:"center", gap:6,
                padding:"4px 12px 4px 9px", borderRadius:999,
                marginBottom:14,
                background:`${sc.accent}14`,
                border:`1px solid ${sc.accent}30`,
              }}>
                <span style={{ position:"relative", width:6, height:6, borderRadius:"50%", background:sc.accent }}>
                  {isPending && (
                    <span style={{ position:"absolute", inset:-3, borderRadius:"50%", background:`${sc.accent}55`, animation:"sonarPing 1.6s ease-out infinite" }}/>
                  )}
                </span>
                <span style={{ fontSize:11, fontWeight:700, color:sc.accent, letterSpacing:"0.06em", textTransform:"uppercase", fontFamily:FB }}>
                  {isParlay ? `${parlayLegCountOf(pick) > 0 ? `${parlayLegCountOf(pick)}-leg ` : ""}parlay · ` : ""}{sc.label}
                </span>
              </div>

              {/* The big number — display font, tight tracking, restrained glow */}
              <div style={{
                fontFamily:FD, fontSize:54, fontWeight:800,
                letterSpacing:"-2px", lineHeight:0.95,
                fontVariantNumeric:"tabular-nums",
                color: isWon ? "#4ade80" : isLost ? "#f87171" : isPending ? "#fbbf24" : "#fff",
                /* status-color glow removed */
                animation:"countUp 0.45s cubic-bezier(0.34,1.56,0.64,1) both",
              }}>{profitLabel}</div>
              <div style={{ fontSize:10.5, fontWeight:700, marginTop:8, letterSpacing:"0.10em", textTransform:"uppercase", color:`${sc.accent}80`, fontFamily:FB }}>pts</div>
              <div style={{ fontSize:12.5, fontWeight:500, color:"rgba(255,255,255,0.50)", marginTop:6 }}>{profitSub}</div>
            </div>
          </div>

          {/* ── SCROLLABLE BODY ── */}
          <div style={{ overflowY:"auto", flex:1, minHeight:0, WebkitOverflowScrolling:"touch", overscrollBehavior:"contain", padding:"0 14px 32px", position:"relative" }}>

            {/* ── MATCHUP SCOREBOARD ── */}
            {!isParlay && (
              <div style={{
                background:"#0E0E12", borderRadius:16, overflow:"hidden",
                border:"1px solid rgba(255,255,255,0.05)",
                boxShadow:"0 1px 0 rgba(255,255,255,0.03) inset",
              }}>
                {/* Sport + type + time */}
                <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"10px 14px" }}>
                  <div style={{ display:"flex", alignItems:"center", gap:7 }}>
                    <span style={{
                      display:"inline-flex", alignItems:"center", gap:5,
                      fontSize:10, fontWeight:700, padding:"3px 9px 3px 7px", borderRadius:6,
                      background:"rgba(255,255,255,0.05)", color:"rgba(255,255,255,0.70)",
                      border:"1px solid rgba(255,255,255,0.06)",
                      letterSpacing:"0.06em", textTransform:"uppercase", fontFamily:FB,
                    }}>
                      <span style={{ width:5, height:5, borderRadius:"50%", background:getSportColor(pick.sport).accent, boxShadow:`0 0 6px ${getSportColor(pick.sport).accent}88` }}/>
                      {pick.sport}
                    </span>
                    <span style={{ fontSize:11, fontWeight:500, color:"rgba(255,255,255,0.50)" }}>{typeLabel}</span>
                  </div>
                  {isLiveGame && safeLivePeriod && (
                    <div style={{ display:"flex", alignItems:"center", gap:5 }}>
                      <span style={{ position:"relative", width:6, height:6, borderRadius:"50%", background:"#4ade80" }}>
                        <span style={{ position:"absolute", inset:-3, borderRadius:"50%", background:"rgba(74,222,128,0.4)", animation:"sonarPing 1.6s ease-out infinite" }}/>
                      </span>
                      <span style={{ fontSize:10.5, fontWeight:700, color:"#4ade80", fontFamily:FB, letterSpacing:"-0.005em" }}>{(() => { const lp = formatLivePeriod(pick.sport, safeLivePeriod, safeLiveClock); return lp?.periodLabel ? `${lp.periodLabel}${lp.clockLabel ? " · " + lp.clockLabel : ""}` : "LIVE"; })()}</span>
                    </div>
                  )}
                  {!isLiveGame && gameCountdown && (
                    <span style={{ fontSize:10.5, fontWeight:700, color:sc.accent, fontFamily:FB, letterSpacing:"-0.005em" }}>Starts in {gameCountdown}</span>
                  )}
                  {!isLiveGame && !gameCountdown && gameTime && (
                    <span style={{ fontSize:10.5, color:"rgba(255,255,255,0.50)", fontWeight:500 }}>{gameTime.date} · {gameTime.time}</span>
                  )}
                </div>

                {/* Teams + Score */}
                <div style={{ padding:"12px 12px 14px" }}>
                  {/* Solo / Outright display (golf, tournament winners) */}
                  {(!awayTeam || awayTeam === "Away" || pick.label?.includes("to Win")) ? (
                    <div style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:8, padding:"8px 0" }}>
                      <div style={{ position:"relative" }}>
                        <TeamLogo name={pick.side || pick.label?.split(" to Win")?.[0] || homeTeam} sport={pick.sport} size={56} style={{ border:`2px solid ${sc.accent}` }} />
                        <div style={{ position:"absolute", bottom:-2, left:"50%", transform:"translateX(-50%)", fontSize:9, fontWeight:700, color:sc.accent, letterSpacing:"0.08em", whiteSpace:"nowrap" }}>YOUR PICK</div>
                      </div>
                      <div style={{ fontSize:15, fontWeight:700, color:"#fff", textAlign:"center" }}>{pick.side || pick.label?.split(" to Win")?.[0] || "Pick"}</div>
                      {!showScores && gameCountdown && <div style={{ fontSize:9, fontWeight:600, color:"rgba(255,255,255,0.36)" }}>Pre-game</div>}
                    </div>
                  ) : (
                  <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between" }}>
                    {/* Away team */}
                    <div style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:4, flex:1, minWidth:0 }}>
                      <div style={{ position:"relative" }}>
                        <TeamLogo name={awayTeam} sport={pick.sport} size={48} style={{ border: pickedSide === "away" ? `2px solid ${sc.accent}` : "1px solid rgba(255,255,255,0.08)",  }} />
                        {pickedSide === "away" && <div style={{ position:"absolute", bottom:-2, left:"50%", transform:"translateX(-50%)", fontSize:9, fontWeight:700, color:sc.accent, letterSpacing:"0.08em", whiteSpace:"nowrap" }}>YOUR PICK</div>}
                      </div>
                      <div style={{ fontSize:11, fontWeight:700, color: awayWinning ? "#4ade80" : "rgba(255,255,255,0.8)", textAlign:"center", lineHeight:1.2 }}>
                        {awayTeam.includes(" ") ? awayTeam.split(" ").slice(-2).join(" ") : awayTeam}
                      </div>
                    </div>

                    {/* Score center */}
                    <div style={{ flexShrink:0, textAlign:"center", padding:"0 4px", minWidth:110 }}>
                      {showScores ? (<>
                        <div style={{ display:"flex", alignItems:"baseline", justifyContent:"center", gap:6 }}>
                          <span style={{ fontSize:34, fontWeight:700, fontFamily:FM, color: awayWinning ? "#4ade80" : "rgba(255,255,255,0.7)" }}>{awayScoreVal ?? "–"}</span>
                          <span style={{ fontSize:16, color:"rgba(255,255,255,0.32)", fontWeight:200 }}>:</span>
                          <span style={{ fontSize:34, fontWeight:700, fontFamily:FM, color: homeWinning ? "#4ade80" : "rgba(255,255,255,0.7)" }}>{homeScoreVal ?? "–"}</span>
                        </div>
                        <div style={{ display:"inline-flex", alignItems:"center", gap:4, padding:"2px 10px", borderRadius:20, marginTop:4, background: (finalScore || liveScore?.completed) ? "rgba(255,255,255,0.04)" : "rgba(239,68,68,0.1)", border:`1px solid ${(finalScore || liveScore?.completed) ? BORD : "rgba(239,68,68,0.2)"}` }}>
                          {isLiveGame && <div style={{ width:4, height:4, borderRadius:"50%", background:"#EF4444", animation:"pulse 1.5s ease infinite" }}/>}
                          <span style={{ fontSize:9, fontWeight:700, letterSpacing:"0.1em", color: (finalScore || liveScore?.completed) ? INK3 : "#f87171" }}>{(finalScore || liveScore?.completed) ? "FINAL" : "LIVE"}</span>
                        </div>
                      </>) : (
                        <div style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:4 }}>
                          <div style={{ fontSize:14, color:INK3, fontWeight:300, width:36, height:36, borderRadius:"50%", border:`1px solid ${BORD}`, display:"flex", alignItems:"center", justifyContent:"center" }}>@</div>
                          {gameCountdown && <div style={{ fontSize:9, fontWeight:600, color:"rgba(255,255,255,0.36)" }}>Pre-game</div>}
                        </div>
                      )}
                    </div>

                    {/* Home team */}
                    <div style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:4, flex:1, minWidth:0 }}>
                      <div style={{ position:"relative" }}>
                        <TeamLogo name={homeTeam} sport={pick.sport} size={48} style={{ border: pickedSide === "home" ? `2px solid ${sc.accent}` : "1px solid rgba(255,255,255,0.08)",  }} />
                        {pickedSide === "home" && <div style={{ position:"absolute", bottom:-2, left:"50%", transform:"translateX(-50%)", fontSize:9, fontWeight:700, color:sc.accent, letterSpacing:"0.08em", whiteSpace:"nowrap" }}>YOUR PICK</div>}
                      </div>
                      <div style={{ fontSize:11, fontWeight:700, color: homeWinning ? "#4ade80" : "rgba(255,255,255,0.8)", textAlign:"center", lineHeight:1.2 }}>
                        {homeTeam.includes(" ") ? homeTeam.split(" ").slice(-2).join(" ") : homeTeam}
                      </div>
                    </div>
                  </div>
                  )}
                </div>
                {isLiveGame && totalLine != null && (
                  <div style={{ padding:"8px 14px", borderTop:`1px solid ${BORD}`, background:"rgba(255,255,255,0.015)" }}>
                    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:6 }}>
                      <span style={{ fontSize:9, fontWeight:700, color:INK3, letterSpacing:"0.1em" }}>TOTAL PROGRESS</span>
                      <span style={{ fontSize:11, fontWeight:800, fontFamily:FM, color: combinedScore >= totalLine ? "#4ade80" : INK2 }}>
                        {combinedScore} / {totalLine}
                      </span>
                    </div>
                    <div style={{ height:6, borderRadius:3, background:"rgba(255,255,255,0.06)", overflow:"hidden", position:"relative" }}>
                      <div style={{
                        height:"100%", borderRadius:3, transition:"width 0.5s ease",
                        width:`${Math.min(100, (combinedScore / totalLine) * 100)}%`,
                        background: combinedScore >= totalLine ? "#22C55E" : T.coral,
                      }}/>
                      {/* Line marker */}
                      <div style={{ position:"absolute", top:-2, bottom:-2, left:"100%", width:2, background:INK2, borderRadius:1 }}/>
                    </div>
                    <div style={{ fontSize:9, color: pick.side === "over" ? (combinedScore >= totalLine ? "#4ade80" : "rgba(255,255,255,0.25)") : (combinedScore < totalLine ? "#4ade80" : "rgba(255,255,255,0.25)"), marginTop:4, fontWeight:600 }}>
                      {pick.side === "over"
                        ? (combinedScore >= totalLine ? "✓ Over hit!" : `Need ${Math.ceil(totalLine - combinedScore)} more`)
                        : (combinedScore < totalLine ? "✓ Tracking under" : "Over hit — needs to come back")}
                    </div>
                  </div>
                )}

                {/* Your prediction line */}
                <div style={{ padding:"10px 14px", borderTop:`1px solid ${BORD}`, background:`${sc.accent}06`, display:"flex", alignItems:"center", justifyContent:"space-between" }}>
                  <div style={{ display:"flex", alignItems:"center", gap:8, minWidth:0, flex:1 }}>
                    <div style={{ width:8, height:8, borderRadius:"50%", flexShrink:0, background:sc.accent, boxShadow:`0 0 8px ${sc.glow}`, animation: isPending ? "pulse 2s ease infinite" : "none" }}/>
                    <span style={{ fontSize:14, fontWeight:800, color:INK, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{expandedLabel}</span>
                  </div>
                  {pick.odds && <span style={{ fontSize:13, fontWeight:700, fontFamily:FM, padding:"4px 10px", borderRadius:8, background:`${sc.accent}18`, border:`1px solid ${sc.accent}30`, color:sc.accent, flexShrink:0 }}>{fmtOdds(pick.odds)}</span>}
                </div>

                {/* Result banner */}
                {(isWon || isLost) && (
                  <div style={{ padding:"10px 14px", background: isWon ? "rgba(34,197,94,0.06)" : "rgba(239,68,68,0.05)", borderTop:`1px solid ${isWon ? "rgba(34,197,94,0.10)" : "rgba(239,68,68,0.08)"}` }}>
                    <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom: finalScore ? 6 : 0 }}>
                      <span style={{ fontSize:14, fontWeight:800, color: isWon ? "#4ade80" : "#f87171" }}>{isWon ? "✓" : "✗"}</span>
                      <span style={{ fontSize:11, fontWeight:700, color: isWon ? "#4ade80" : "#f87171" }}>
                        {isWon ? "Prediction correct" : "Prediction missed"}
                      </span>
                    </div>
                    {finalScore && (
                      <div style={{ padding:"6px 10px", borderRadius:8, background:"rgba(255,255,255,0.03)", border:"1px solid rgba(255,255,255,0.04)" }}>
                        <div style={{ fontSize:10, fontWeight:600, color:INK2 }}>
                          Final: {awayTeam.split(" ").pop()} {finalScore.away} – {finalScore.home} {homeTeam.split(" ").pop()}
                        </div>
                        {(pick.type === "total" || pick.type === "alt_total") && (() => {
                          const combined = finalScore.home + finalScore.away;
                          const line = Number(pick.line) || 0;
                          const maxVal = Math.max(combined, line) * 1.2 || 10;
                          const fillPct = Math.min((combined / maxVal) * 100, 100);
                          const linePct = Math.min((line / maxVal) * 100, 100);
                          const hit = pick.side === "over" ? combined > line : combined < line;
                          const barColor = hit ? "#4ade80" : "#f87171";
                          const barGlow = hit ? "rgba(74,222,128,0.3)" : "rgba(248,113,113,0.3)";
                          return (
                            <div style={{ marginTop:6 }}>
                              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:4 }}>
                                <span style={{ fontSize:9, fontWeight:700, color:INK3 }}>{pick.side?.toUpperCase()} {line}</span>
                                <span style={{ fontSize:9, fontWeight:700, color:barColor }}>Total: {combined}</span>
                              </div>
                              <div style={{ position:"relative", height:12, borderRadius:6, background:"rgba(255,255,255,0.06)", overflow:"visible" }}>
                                <div style={{ position:"absolute", left:0, top:0, bottom:0, width:`${fillPct}%`, borderRadius:6, background:`linear-gradient(90deg, ${barColor}60, ${barColor})`, boxShadow:`0 0 10px ${barGlow}` }}/>
                                <div style={{ position:"absolute", left:`${linePct}%`, top:-3, bottom:-3, width:2, background:"rgba(255,255,255,0.5)", borderRadius:1, zIndex:1 }}/>
                                <div style={{ position:"absolute", left:`${fillPct}%`, top:"50%", transform:"translate(-50%,-50%)", background:barColor, borderRadius:8, padding:"0px 6px", fontSize:9, fontWeight:800, color:"#000", zIndex:2 }}>{combined}</div>
                              </div>
                              <div style={{ position:"relative", height:12, marginTop:1 }}>
                                <div style={{ position:"absolute", left:`${linePct}%`, transform:"translateX(-50%)", fontSize:9, color:INK3, fontWeight:600 }}>{line}</div>
                              </div>
                            </div>
                          );
                        })()}
                        {(pick.type === "spread" || pick.type === "alt_spread") && (
                          <div style={{ fontSize:10, fontWeight:600, color:INK3, marginTop:2 }}>
                            Margin: {finalScore.home > finalScore.away ? homeTeam.split(" ").pop() : awayTeam.split(" ").pop()} by {Math.abs(finalScore.home - finalScore.away)} · Line: {pick.line > 0 ? "+" : ""}{pick.line}
                          </div>
                        )}
                      </div>
                    )}
                    {/* ── Player Prop Progress Meter ── */}
                    {isPlayerProp && pick.actualStat != null && pick.line != null && (() => {
                      const actual = Number(pick.actualStat) || 0;
                      const line = Number(pick.line) || 0;
                      const maxVal = Math.max(actual, line) * 1.3 || 10;
                      const fillPct = Math.min((actual / maxVal) * 100, 100);
                      const linePct = Math.min((line / maxVal) * 100, 100);
                      const hit = pick.side?.toLowerCase() === "over" ? actual > line : actual < line;
                      const barColor = hit ? "#4ade80" : "#f87171";
                      const barGlow = hit ? "rgba(74,222,128,0.3)" : "rgba(248,113,113,0.3)";
                      return (
                        <div style={{ marginTop:8, padding:"10px 14px", borderRadius:12, background:"rgba(255,255,255,0.02)", border:"1px solid rgba(255,255,255,0.04)" }}>
                          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:6 }}>
                            <span style={{ fontSize:10, fontWeight:700, color:INK2 }}>{pick.side?.toUpperCase()} {line}+</span>
                            <span style={{ fontSize:10, fontWeight:700, color:barColor }}>Actual: {actual}</span>
                          </div>
                          {/* Bar */}
                          <div style={{ position:"relative", height:14, borderRadius:7, background:"rgba(255,255,255,0.06)", overflow:"visible" }}>
                            <div style={{ position:"absolute", left:0, top:0, bottom:0, width:`${fillPct}%`, borderRadius:7, background:`linear-gradient(90deg, ${barColor}60, ${barColor})`, boxShadow:`0 0 12px ${barGlow}`, transition:"width 0.5s ease" }}/>
                            {/* Line marker */}
                            <div style={{ position:"absolute", left:`${linePct}%`, top:-4, bottom:-4, width:2, background:"rgba(255,255,255,0.5)", borderRadius:1, zIndex:1 }}/>
                            {/* Actual value badge */}
                            <div style={{
                              position:"absolute", left:`${fillPct}%`, top:"50%", transform:"translate(-50%, -50%)",
                              background:barColor, borderRadius:10, padding:"1px 8px",
                              fontSize:10, fontWeight:800, color:"#000", zIndex:2, whiteSpace:"nowrap",
                              boxShadow:`0 0 8px ${barGlow}`,
                            }}>{actual}</div>
                          </div>
                          {/* Line label below */}
                          <div style={{ position:"relative", height:14, marginTop:2 }}>
                            <div style={{ position:"absolute", left:`${linePct}%`, transform:"translateX(-50%)", fontSize:9, color:INK3, fontWeight:600 }}>{line}</div>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                )}
              </div>
            )}

            {/* ── PARLAY CARD ── */}
            {isParlay && (
              <div style={{ background:CARD, borderRadius:18, border:"1px solid rgba(255,107,61,0.08)", overflow:"hidden" }}>
                <div style={{ padding:"10px 14px", borderBottom:`1px solid ${BORD}` }}>
                  <span style={{ fontSize:9, fontWeight:800, color:T.coral, letterSpacing:"0.12em" }}>{pick.legs?.length || "?"}-LEG PARLAY</span>
                </div>
                {(pick.legs || []).map((leg, li) => {
                  const legTime = (() => { const ct = leg.commenceTime; if (!ct) return null; try { const d = ct?.toDate ? ct.toDate() : ct?.seconds ? new Date(ct.seconds*1000) : new Date(ct); return d.toLocaleDateString("en-US",{weekday:"short",month:"short",day:"numeric"}) + " · " + d.toLocaleTimeString("en-US",{hour:"numeric",minute:"2-digit"}); } catch { return null; } })();
                  const legSportColor = getSportColor(leg.sport);
                  const legPickedTeam = leg.side === "home" ? (leg.homeTeamFull?.split(" ").pop() || "") : leg.side === "away" ? (leg.awayTeamFull?.split(" ").pop() || "") : "";
                  const legExpandedLabel = (leg.type === "total" || leg.type === "alt_total") ? leg.label : legPickedTeam ? `${legPickedTeam} ${leg.label}` : leg.label;
                  // Per-leg score from settlement (legResults stored by CF)
                  const lr = (pick.legResults || []).find(r => r.gameId === leg.gameId);
                  const legStatus = lr?.status || null;
                  const legStatusColor = legStatus === "won" ? "#4ade80" : legStatus === "lost" ? "#f87171" : legStatus === "push" ? "#9CA3AF" : null;
                  return (
                    <div key={li} style={{ padding:"12px 14px", borderBottom: li < (pick.legs?.length||1)-1 ? `1px solid ${BORD}` : "none", borderLeft: legStatusColor ? `3px solid ${legStatusColor}` : "none" }}>
                      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:8 }}>
                        <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                          <span style={{ fontSize:9, fontWeight:800, padding:"2px 6px", borderRadius:4, background:legSportColor.dim, color:legSportColor.accent, letterSpacing:"0.06em" }}>{leg.sport}</span>
                          <span style={{ fontSize:9, fontWeight:600, color:INK2 }}>{(leg.type || "").replace(/_/g," ").toUpperCase()}</span>
                        </div>
                        <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                          {legTime && <span style={{ fontSize:9, color:INK2, fontWeight:500 }}>{legTime}</span>}
                          {legStatus && <span style={{ fontSize:9, fontWeight:800, color:legStatusColor, letterSpacing:"0.06em" }}>{legStatus === "won" ? "✓ HIT" : legStatus === "lost" ? "✗ MISS" : "PUSH"}</span>}
                        </div>
                      </div>
                      <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                        <div style={{ display:"flex", alignItems:"center", gap:4, flexShrink:0 }}>
                          <TeamLogo name={leg.awayTeamFull} sport={leg.sport} size={24} style={{ border: leg.side === "away" ? `1.5px solid ${sc.accent}` : "1px solid rgba(255,255,255,0.06)" }} />
                          <span style={{ fontSize:9, color:INK3, fontWeight:600 }}>@</span>
                          <TeamLogo name={leg.homeTeamFull} sport={leg.sport} size={24} style={{ border: leg.side === "home" ? `1.5px solid ${sc.accent}` : "1px solid rgba(255,255,255,0.06)" }} />
                        </div>
                        <div style={{ flex:1, minWidth:0 }}>
                          <div style={{ fontSize:12, fontWeight:700, color:INK, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{legExpandedLabel}</div>
                          <div style={{ fontSize:10, color:INK3, marginTop:1 }}>{leg.gameLabel || ""} · {fmtOdds(leg.odds)}</div>
                        </div>
                      </div>
                      {/* Per-leg score details */}
                      {lr && (
                        <div style={{ marginTop:8, padding:"6px 10px", borderRadius:8, background:"rgba(255,255,255,0.03)", border:"1px solid rgba(255,255,255,0.04)" }}>
                          <div style={{ fontSize:10, color:INK2, fontWeight:600 }}>
                            Final: {leg.awayTeamFull?.split(" ").pop()} {lr.awayScore} – {lr.homeScore} {leg.homeTeamFull?.split(" ").pop()}
                            {(leg.type === "total" || leg.type === "alt_total") && <span style={{ color: (lr.homeScore+lr.awayScore) > (leg.line||0) ? "#4ade80" : "#f87171", marginLeft:6 }}>Combined: {lr.homeScore+lr.awayScore} / {leg.line}</span>}
                            {(leg.type === "spread" || leg.type === "alt_spread") && <span style={{ color:INK3, marginLeft:6 }}>Margin: {lr.homeScore > lr.awayScore ? "+" : ""}{lr.homeScore - lr.awayScore}</span>}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* ── COMPACT STATS ── */}
            {/* Pick'em/survivor: no wager exists, so the WAGERED box is noise
                and the third box's payout math renders "+0"/"–0". Swap in the
                standings-point framing instead; the ODDS box stays (the line
                is real information either way). */}
            <div style={{ display:"flex", gap:8, marginTop:10 }}>
              {!isNonWagerPick_ && (
              <div style={{ flex:1, background:T.card, borderRadius:12, border:`1px solid ${T.border}`, padding:"12px 14px", textAlign:"center" }}>
                <div style={{ fontSize:9, fontWeight:800, color:"rgba(0,212,255,0.5)", letterSpacing:"0.12em", marginBottom:4 }}>WAGERED</div>
                <div style={{ fontSize:18, fontWeight:700, fontFamily:FM, color:INK, fontVariantNumeric:"tabular-nums" }}>{(pick.wager||0).toLocaleString()}</div>
              </div>
              )}
              <div style={{ flex:1, background:T.card, borderRadius:12, border:`1px solid ${T.border}`, padding:"12px 14px", textAlign:"center" }}>
                <div style={{ fontSize:9, fontWeight:700, color:T.inkMuted, letterSpacing:"0.12em", marginBottom:4 }}>ODDS</div>
                <div style={{ fontSize:18, fontWeight:700, fontFamily:FM, color:INK, fontVariantNumeric:"tabular-nums" }}>{fmtOdds(pick.combinedOdds || pick.odds)}</div>
                {impliedProb && <div style={{ fontSize:9, color:INK3, marginTop:2 }}>{impliedProb}% implied</div>}
              </div>
              <div style={{ flex:1, background: isWon ? "rgba(34,197,94,0.06)" : isLost ? "rgba(239,68,68,0.06)" : T.card, borderRadius:12, border:`1px solid ${isWon ? "rgba(34,197,94,0.20)" : isLost ? "rgba(239,68,68,0.20)" : T.border}`, padding:"12px 14px", textAlign:"center" }}>
                <div style={{ fontSize:9, fontWeight:700, color: isWon ? "rgba(74,222,128,0.6)" : isLost ? "rgba(248,113,113,0.6)" : "rgba(255,107,61,0.5)", letterSpacing:"0.12em", marginBottom:4 }}>
                  {isNonWagerPick_ ? (isWon ? "CORRECT" : isLost ? "MISSED" : "IF CORRECT") : (isWon ? "WON" : isLost ? "LOST" : "PROFIT")}
                </div>
                <div style={{ fontSize:18, fontWeight:700, fontFamily:FM, color: isWon ? "#4ade80" : isLost ? "#f87171" : T.coral, fontVariantNumeric:"tabular-nums" }}>
                  {isNonWagerPick_
                    ? (isWon ? "+1" : isLost ? "0" : isPush || isVoided ? "—" : "+1")
                    : (isWon ? `+${(pick.points ?? 0).toLocaleString()}` : isLost ? `–${(pick.wager ?? 0).toLocaleString()}` : isPush || isVoided ? "—" : `+${potentialProfit.toLocaleString()}`)}
                </div>
              </div>
            </div>

            {/* ── TOTAL RETURN + META ── */}
            {pick.expectedPayout > 0 && !isLost && !isPush && !isVoided && (
              <div style={{ marginTop:8, background: isWon ? "rgba(34,197,94,0.06)" : "rgba(255,107,61,0.05)", borderRadius:12, border:`1px solid ${isWon ? "rgba(34,197,94,0.20)" : "rgba(255,107,61,0.15)"}`, padding:"12px 16px", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                <div>
                  <span style={{ fontSize:12, fontWeight:700, color:INK }}>Total Return</span>
                  <div style={{ fontSize:9, color:INK3, marginTop:1 }}>includes {(pick.wager||0).toLocaleString()} pt wager</div>
                </div>
                <span style={{ fontSize:20, fontWeight:700, fontFamily:FM, color: isWon ? "#4ade80" : INK, fontVariantNumeric:"tabular-nums" }}>{(pick.expectedPayout||0).toLocaleString()}<span style={{ fontSize:10, opacity:0.4, marginLeft:3 }}>pts</span></span>
              </div>
            )}

            {/* Placed timestamp */}
            {placedAgo && (
              <div style={{ textAlign:"center", marginTop:10, fontSize:10, color:INK3 }}>
                Placed {placedAgo}{league ? ` · ${league.name}` : ""}
              </div>
            )}

            {/* Report Issue — settled picks only */}
            {pick.status && pick.status !== "pending" && (
              <div style={{ textAlign:"center", marginTop:10 }}>
                <button
                  onClick={async () => {
                    try {
                      const { getFirestore, collection, addDoc, serverTimestamp } = await import("firebase/firestore");
                      const db = getFirestore(app);
                      await addDoc(collection(db, "reports"), {
                        pickId: pick.id,
                        // FIX: capture the pick OWNER's uid (may differ from reporter).
                        // pick.userId is set when viewing another user's pick via Den/league feed;
                        // falls back to the reporter's own uid when they're reporting their own pick.
                        pickOwnerUserId: pick.userId || pick._userId || user?.uid || "",
                        userId: user?.uid || "",   // reporter's uid (kept for backwards compat)
                        reporterUid: user?.uid || "",
                        username: user?.displayName || "",
                        email: user?.email || "",
                        label: pick.label || "",
                        status: pick.status,
                        sport: pick.sport || pick.sportKey || "",
                        type: pick.type || "",
                        gameLabel: pick.gameLabel || "",
                        wager: pick.wager || 0,
                        odds: pick.odds || 0,
                        points: pick.points || 0,
                        actualStat: pick.actualStat ?? null,
                        finalScore: pick.finalScore || null,
                        createdAt: serverTimestamp(),
                      });
                      window.torchToast?.("Report submitted — we'll review within 24 hours.", "success");
                    } catch (e) {
                      console.warn("[Report] Failed:", e.message);
                      window.torchToast?.("Couldn't submit report. Try again.", "error");
                    }
                  }}
                  style={{
                    fontSize:10, fontWeight:600, color:"rgba(255,255,255,0.40)",
                    background:"none", border:"none", cursor:"pointer",
                    display:"inline-flex", alignItems:"center", gap:4,
                  }}>
                  ⚠ Report an issue with this pick
                </button>
              </div>
            )}

          </div>
        </div>
      </div>,
    document.body
  );
}


function UserProfileModal({ profileUid, profileUsername, currentUser, leagueId = null, leagueName = "", onClose, isBlocked = false, onBlock, onUnblock, viewerLeagues = [] }) {
  const { closing, requestClose } = useSheetClose(onClose, 200);
  const [stats, setStats]         = useState(null);
  const [rawPicks, setRawPicks]   = useState([]); // all picks, unfiltered
  const [picksLoaded, setPicksLoaded] = useState(false); // true once loadPicks resolves successfully (even if empty)
  const [loading, setLoading]     = useState(true);
  const [tab, setTab]             = useState("picks"); // "picks" | "badges"
  const [pickFilter, setPickFilter] = useState("all"); // "all" | "active" | "won" | "lost"
  const [pickScope, setPickScope] = useState(leagueId ? "league" : "all"); // "league" | "all"
  const [selectedProfilePick, setSelectedProfilePick] = useState(null);

  const isMe = profileUid === currentUser?.uid;

  useEffect(() => {
    if (!profileUid) return;
    setLoading(true);
    setPicksLoaded(false);
    // Track each promise independently so a stats failure doesn't prevent us
    // from trusting picks data and vice versa.
    let picksOk = false;
    Promise.all([
      loadUserStats(profileUid).catch(() => null),
      loadPicks(profileUid).then(r => { picksOk = true; return r; }).catch(() => []),
    ]).then(([s, picksRaw]) => {
      setStats(s);
      const picks = normalizePicks(picksRaw)
        .filter(p => p.status !== "voided")
        // Pick privacy: hide this user's unrevealed picks from privacy-flagged
        // leagues the viewer belongs to (viewer's own profile unaffected —
        // helper exempts viewerUid). Leagues outside viewerLeagues can't be
        // resolved client-side; that residual is noted in the privacy block.
        .filter(p => {
          if (!p.leagueId) return true;
          const lg = (viewerLeagues || []).find(l => l.id === p.leagueId);
          return !lg || !isPickHiddenByPrivacy(p, lg, currentUser?.uid);
        })
        .sort((a, b) => {
          const tb = b.submittedAt?.seconds ?? b.createdAt?.seconds ?? 0;
          const ta = a.submittedAt?.seconds ?? a.createdAt?.seconds ?? 0;
          return tb - ta;
        });
      setRawPicks(picks);
      setPicksLoaded(picksOk);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [profileUid]);

  // Apply scope filter (league vs all)
  const allPicks = pickScope === "league" && leagueId
    ? rawPicks.filter(p => p.leagueId === leagueId)
    : rawPicks;

  const activePicks  = allPicks.filter(p => p.status === "pending");
  const wonPicks     = allPicks.filter(p => p.status === "won");
  const lostPicks    = allPicks.filter(p => p.status === "lost");

  // W/L resolution rules:
  //   - If picks loaded successfully → ALWAYS trust the computed count (zero is a valid count).
  //   - If picks failed to load AND we're viewing all-leagues scope → fall back to stored stats.
  //   - If picks failed to load AND we're viewing a specific league → can't show a number we trust;
  //     show 0 and accept the brief inaccuracy. Stored userStats is GLOBAL, so it would lie at
  //     league scope (e.g. show "12W–3L" for someone with no record in this league).
  const inLeagueScope = pickScope === "league" && !!leagueId;
  const wins   = picksLoaded
    ? wonPicks.length
    : (inLeagueScope ? 0 : (stats?.wins   ?? 0));
  const losses = picksLoaded
    ? lostPicks.length
    : (inLeagueScope ? 0 : (stats?.losses ?? 0));
  const total   = wins + losses;
  const winRate = total > 0 ? Math.round((wins / total) * 100) : 0;
  const streak  = stats?.streak ?? 0;
  const balance = stats?.balance ?? stats?.totalPoints ?? 0;
  const totalWagered = allPicks.reduce((s, p) => s + (Number(p.wager)||0), 0);
  const totalReturned = wonPicks.reduce((s, p) => s + (Number(p.payout)||Number(p.wager)||0) + (Number(p.profit)||Number(p.points)||0), 0);

  const filteredPicks = pickFilter === "active" ? activePicks
    : pickFilter === "won"  ? wonPicks
    : pickFilter === "lost" ? lostPicks
    : allPicks;

  // ── League attribution for cross-league pick views ──
  // When the user flips to "All Picks" scope, picks may come from leagues the
  // VIEWER isn't a member of. To respect private-league privacy (and avoid
  // Firestore permission errors trying to read leagues we can't read), we only
  // name leagues the viewer is already in; anything else gets a muted
  // "Private league" label. Practice picks (no leagueId) get the Practice label.
  const viewerLeagueMap = (Array.isArray(viewerLeagues) ? viewerLeagues : []).reduce((acc, l) => {
    if (l?.id) acc[l.id] = l.name || "League";
    return acc;
  }, {});
  const resolveLeagueLabel = (pick) => {
    if (!pick?.leagueId) return "Practice";
    return viewerLeagueMap[pick.leagueId] || "Private league";
  };

  const fmtOdds = (o) => !o ? "" : o > 0 ? `+${o}` : `${o}`;
  const fmtDate = (p) => {
    const ts = p.submittedAt?.toDate?.() ?? (p.submittedAt?.seconds ? new Date(p.submittedAt.seconds*1000) : null)
             ?? p.createdAt?.toDate?.() ?? (p.createdAt?.seconds ? new Date(p.createdAt.seconds*1000) : null);
    if (!ts) return "";
    const now = Date.now();
    return timeAgo(ts);
  };

  return (
    <>
    <div style={{
      position:"fixed", inset:0, zIndex:200,
      background:"rgba(0,0,0,0.65)", backdropFilter:"blur(8px)",
      animation: closing ? "slipFadeOut 0.18s ease both" : "fadeIn 0.2s ease both",
      pointerEvents: closing ? "none" : "auto",
    }} onClick={() => requestClose()}/>
    <div style={{
      position:"fixed", bottom:0, left:0, right:0,
      margin:"0 auto",
      width:"100%", maxWidth:560, zIndex:201,
      background:"#0D0E12", borderRadius:"22px 22px 0 0",
      border:"1px solid rgba(255,255,255,0.09)",
      height:"85vh",
      maxHeight:"85vh",
      overflow:"hidden",
      display:"flex", flexDirection:"column",
      animation: closing ? "sheetExitDown 0.2s cubic-bezier(0.4,0,1,1) both" : "sheetEnterUp 0.26s cubic-bezier(0.32,0.72,0,1) both",
      pointerEvents: closing ? "none" : "auto",
      boxShadow:"0 -8px 48px rgba(0,0,0,0.5)",
    }}>
      {/* Drag handle */}
      <div style={{ width:36, height:4, borderRadius:2, background:"rgba(255,255,255,0.15)", margin:"12px auto 0" }}/>
      {/* Close X lives inline in the avatar header row below — keeping a single
          close button per sheet (was duplicating with an absolute one here). */}

      {/* Hero header */}
      <div style={{
        padding:"12px 20px 0",
        background:"linear-gradient(160deg,#0E1420 0%,#0D0E12 100%)",
        borderBottom:"1px solid rgba(255,255,255,0.07)",
        flexShrink:0,
      }}>
        <div style={{ display:"flex", alignItems:"center", gap:14, marginBottom:10 }}>
          {/* Avatar */}
          <div style={{
            width:54, height:54, borderRadius:"50%", flexShrink:0,
            background: isMe
              ? T.coral
              : `linear-gradient(135deg,rgba(255,255,255,0.2),rgba(255,255,255,0.06))`,
            display:"flex", alignItems:"center", justifyContent:"center",
            fontSize:22, fontWeight:700, color:"#fff",
            boxShadow: isMe ? `0 0 20px rgba(255,107,61,0.35)` : "none",
          }}>{(profileUsername||"?")[0].toUpperCase()}</div>

          <div style={{ flex:1, minWidth:0 }}>
            <div style={{ display:"flex", alignItems:"center", gap:8 }}>
              <span style={{ fontSize:18, fontWeight:700, color:"#fff", letterSpacing:"-0.3px" }}>
                {profileUsername}
              </span>
              {isMe && (
                <span style={{ fontSize:10, fontWeight:700, padding:"2px 8px", borderRadius:20,
                  background:"rgba(255,107,61,0.15)", color:T.coral, border:"1px solid rgba(255,107,61,0.25)" }}>
                  You
                </span>
              )}
              {streak >= 3 && (
                <span style={{ fontSize:11, fontWeight:700, color:T.coral }}>{streak}W</span>
              )}
            </div>
            {leagueName && (
              <div style={{ fontSize:10, color:"rgba(255,255,255,0.42)", marginTop:3, fontWeight:600 }}>
                {leagueName} picks
              </div>
            )}
            {!loading && (
              <div style={{ display:"flex", gap:16, marginTop:6 }}>
                {[
                  { val:`${wins}W–${losses}L`, label:"record" },
                  { val:`${winRate}%`, label:"win rate",
                    color: winRate>=60?"#4ade80":winRate>=45?"rgba(255,255,255,0.7)":"#f87171" },
                  { val: activePicks.length > 0 ? `${activePicks.length} live` : `${total} picks`,
                    label: activePicks.length > 0 ? "in play" : "total",
                    color: activePicks.length > 0 ? T.coral : undefined },
                ].map(({ val, label, color }) => (
                  <div key={label}>
                    <div style={{ fontSize:14, fontWeight:700, color: color||"#fff", lineHeight:1 }}>{val}</div>
                    <div style={{ fontSize:9, color:"rgba(255,255,255,0.35)", marginTop:2, textTransform:"uppercase", letterSpacing:"0.07em" }}>{label}</div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Block / Unblock — Apple Guideline 1.2 */}
          {!isMe && onBlock && !isBlocked && (
            <button onClick={() => {
              window.torchConfirm?.(
                `Block ${profileUsername}? Their posts will be hidden from your feed and a report will be sent to the developer.`,
                () => { onBlock?.(); requestClose(); },
                { danger: true, confirmLabel: "Block" }
              );
            }} style={{
              padding:"6px 10px", borderRadius:8, fontSize:10, fontWeight:700, cursor:"pointer",
              background:"rgba(239,68,68,0.10)", border:"1px solid rgba(239,68,68,0.20)",
              color:"#EF4444", display:"flex", alignItems:"center", gap:4, flexShrink:0,
            }}>
              🚫 Block
            </button>
          )}
          {!isMe && onBlock && isBlocked && (
            <button onClick={() => { onUnblock?.(); requestClose(); }} style={{
              padding:"6px 10px", borderRadius:8, fontSize:10, fontWeight:700, cursor:"pointer",
              background:"rgba(255,255,255,0.06)", border:"1px solid rgba(255,255,255,0.10)",
              color:"rgba(255,255,255,0.45)", display:"flex", alignItems:"center", gap:4, flexShrink:0,
            }}>
              Unblock
            </button>
          )}
          <button onClick={() => requestClose()} style={{
            width:32, height:32, borderRadius:"50%", border:"1px solid rgba(255,255,255,0.12)",
            background:"rgba(255,255,255,0.07)", cursor:"pointer", color:"rgba(255,255,255,0.6)",
            fontSize:16, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0,
          }} aria-label="Close">✕</button>
        </div>

        {/* Tabs */}
        <div style={{ display:"flex", gap:0, marginBottom:0 }}>
          {[["picks","Picks"],["badges","Badges"]].map(([key, label]) => (
            <button key={key} onClick={() => setTab(key)} style={{
              padding:"10px 16px", border:"none", background:"transparent", cursor:"pointer",
              fontSize:13, fontWeight: tab===key ? 700 : 500,
              color: tab===key ? "#fff" : "rgba(255,255,255,0.35)",
              borderBottom: tab===key ? `2px solid ${T.coral}` : "2px solid transparent",
              marginBottom:-1, transition:"all 0.15s", letterSpacing:"0.01em",
            }}>{label}</button>
          ))}
        </div>
      </div>

      {/* Body — scrollable */}
      <div style={{ overflowY:"auto", flex:1, minHeight:0, WebkitOverflowScrolling:"touch", overscrollBehavior:"contain", paddingBottom:"calc(env(safe-area-inset-bottom,0px) + 24px)" }}>
        {loading ? (
          <div style={{ padding:"48px 20px", textAlign:"center" }}>
            <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
              {[1,2,3].map(i => (
                <div key={i} style={{ height:72, borderRadius:12, background:"rgba(255,255,255,0.05)", animation:"pulse 1.4s ease infinite" }}/>
              ))}
            </div>
          </div>
        ) : tab === "picks" ? (
          <div style={{ padding:"14px 16px" }}>
            {/* Scope toggle — league vs all picks */}
            {leagueId && (
              <div style={{
                display:"flex", gap:0, marginBottom:12, borderRadius:12, overflow:"hidden",
                border:"1px solid rgba(255,255,255,0.08)", background:"rgba(255,255,255,0.02)",
              }}>
                {[
                  { key:"league", label: leagueName || "This League" },
                  { key:"all",    label:"All Picks" },
                ].map(({ key, label }) => (
                  <button key={key} onClick={() => { setPickScope(key); setPickFilter("all"); }} style={{
                    flex:1, padding:"8px 12px", border:"none", cursor:"pointer",
                    background: pickScope===key ? "rgba(255,107,61,0.12)" : "transparent",
                    color: pickScope===key ? T.coral : "rgba(255,255,255,0.40)",
                    fontSize:12, fontWeight:700, transition:"all 0.15s",
                    borderBottom: pickScope===key ? `2px solid ${T.coral}` : "2px solid transparent",
                  }}>{label}</button>
                ))}
              </div>
            )}
            {/* Filter chips */}
            <div style={{ display:"flex", gap:6, marginBottom:14, overflowX:"auto", WebkitOverflowScrolling:"touch", scrollbarWidth:"none" }}>
              {[
                { key:"all",    label:`All (${allPicks.length})` },
                { key:"active", label:`🔴 Live (${activePicks.length})`, show: activePicks.length > 0 },
                { key:"won",    label:`✅ Won (${wonPicks.length})` },
                { key:"lost",   label:`❌ Lost (${lostPicks.length})` },
              ].filter(f => f.show !== false).map(({ key, label }) => (
                <button key={key} onClick={() => setPickFilter(key)} style={{
                  flexShrink:0, padding:"5px 13px", borderRadius:20, cursor:"pointer",
                  border: pickFilter===key ? "none" : "1px solid rgba(255,255,255,0.10)",
                  background: pickFilter===key ? T.coral : "rgba(255,255,255,0.04)",
                  color: pickFilter===key ? "#fff" : "rgba(255,255,255,0.5)",
                  fontSize:11, fontWeight:700, transition:"all 0.15s",
                  boxShadow: pickFilter===key ? "0 2px 10px rgba(255,107,61,0.25)" : "none",
                }}>{label}</button>
              ))}
            </div>

            {!picksLoaded ? (
              // Skeleton ghosts — show the SHAPE of picks while they load instead
              // of a blank screen or a generic spinner. Tells users "your content is
              // coming, here's what it'll look like." Robinhood/Linear style.
              <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                {[0, 1, 2].map(i => (
                  <div key={i} style={{
                    borderRadius:14, padding:"14px 14px",
                    background:"rgba(255,255,255,0.025)",
                    border:"1px solid rgba(255,255,255,0.04)",
                    display:"flex", alignItems:"center", gap:12,
                    animation:`skeletonPulse 1.6s ease-in-out ${i * 0.15}s infinite`,
                  }}>
                    <div style={{ width:36, height:36, borderRadius:10, background:"rgba(255,255,255,0.04)", flexShrink:0 }}/>
                    <div style={{ flex:1, display:"flex", flexDirection:"column", gap:6 }}>
                      <div style={{ height:13, width:"55%", borderRadius:4, background:"rgba(255,255,255,0.05)" }}/>
                      <div style={{ height:10, width:"35%", borderRadius:4, background:"rgba(255,255,255,0.035)" }}/>
                    </div>
                    <div style={{ width:48, height:24, borderRadius:6, background:"rgba(255,255,255,0.04)" }}/>
                  </div>
                ))}
              </div>
            ) : filteredPicks.length === 0 ? (
              <div style={{ padding:"56px 0", textAlign:"center" }}>
                <div style={{
                  width:56, height:56, borderRadius:16, margin:"0 auto 14px",
                  background:"rgba(255,255,255,0.04)", border:"1px solid rgba(255,255,255,0.06)",
                  display:"flex", alignItems:"center", justifyContent:"center",
                }}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.25)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    {pickFilter==="won" ? <><path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6"/><path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18"/><path d="M4 22h16"/><path d="M18 2H6v7a6 6 0 0 0 12 0V2Z"/></>
                     : pickFilter==="lost" ? <><circle cx="12" cy="12" r="10"/><path d="M8 15h8M9 9h.01M15 9h.01"/></>
                     : <><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="6"/><circle cx="12" cy="12" r="2"/></>}
                  </svg>
                </div>
                <div style={{ fontSize:14, fontWeight:700, color:"rgba(255,255,255,0.5)" }}>
                  {pickFilter==="active" ? "Nothing live right now" : pickFilter==="won" ? "No Ws on the board yet" : pickFilter==="lost" ? "Clean slate. No losses." : "Your picks land here once you make some"}
                </div>
                <div style={{ fontSize:12, color:"rgba(255,255,255,0.32)", marginTop:6, lineHeight:1.5 }}>
                  {pickFilter==="active" ? "Live picks show up here as games tip off." : pickFilter==="won" ? "Hit one and it'll be sitting here." : pickFilter==="lost" ? "Long may it last." : "Browse Games to throw one down."}
                </div>
              </div>
            ) : (
              <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                {filteredPicks.map((p, i) => {
                  const isWon     = p.status === "won";
                  const isLost    = p.status === "lost";
                  const isPending = p.status === "pending";
                  const accentColor  = isWon ? "#4ade80" : isLost ? "#f87171" : "#FCD34D";
                  const accentBg    = isWon ? "rgba(74,222,128,0.06)" : isLost ? "rgba(248,113,113,0.06)" : "rgba(252,211,77,0.04)";
                  const accentBorder = isWon ? "rgba(74,222,128,0.18)" : isLost ? "rgba(248,113,113,0.18)" : "rgba(252,211,77,0.15)";
                  const profit = isWon
                    ? (Number(p.points)||0)
                    : isLost ? -(Number(p.wager)||0)
                    : null;
                  const isParlay = p.isParlay || (p.legs?.length > 1);

                  // "Fresh settled" — was settled in the last 5 min. Triggers a
                  // reveal animation: status pill draws in, accent color sweeps,
                  // points number counts up. Lets the user actually FEEL the win
                  // when they open the app to a settled pick instead of arriving
                  // at a static result. After 5 min, no animation — the moment passed.
                  const settledMs = p.settledAt?.toMillis?.()
                    || (p.settledAt?.seconds ? p.settledAt.seconds * 1000 : null)
                    || (p.settledAt instanceof Date ? p.settledAt.getTime() : null);
                  const wasJustSettled = !isPending && settledMs && (Date.now() - settledMs < 5 * 60 * 1000);

                  return (
                    <div key={p.id||i}
                      // RESET-OWNER-ONLY (fix 3.2): tag the owner so a report filed from this
                      // profile records whose pick it is, not the reporter's uid.
                      onClick={() => setSelectedProfilePick({ ...p, userId: p.userId || profileUid })}
                      style={{
                        borderRadius:14, overflow:"hidden",
                        background: accentBg,
                        border:`1px solid ${accentBorder}`,
                        cursor:"pointer", transition:"transform 0.12s",
                        // Fresh-settled gets a one-shot reveal animation. Otherwise
                        // standard staggered fade-up like all other cards.
                        animation: wasJustSettled
                          ? `settledReveal 0.7s cubic-bezier(0.32,0.72,0,1.18) both`
                          : `fadeUp 0.18s ease both`,
                        animationDelay: wasJustSettled ? "0ms" : `${Math.min(i,8)*25}ms`,
                        // Glow ring on fresh wins/losses — drops away after the animation completes.
                        boxShadow: wasJustSettled && isWon
                          ? `0 0 0 1px ${accentColor}40, 0 0 24px ${accentColor}28, 0 0 48px ${accentColor}10`
                          : "none",
                        position:"relative",
                      }}
                      onMouseEnter={e => e.currentTarget.style.transform="translateY(-1px)"}
                      onMouseLeave={e => e.currentTarget.style.transform="translateY(0)"}
                    >
                      {/* Pending pulse bar */}
                      {isPending && (
                        <div style={{ height:2, background:`linear-gradient(90deg,${T.coral},rgba(255,107,61,0))`, animation:"pulse 1.8s ease infinite" }}/>
                      )}

                      <div style={{ padding:"11px 14px" }}>
                        <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", gap:8 }}>
                          <div style={{ flex:1, minWidth:0 }}>
                            {/* Status + sport row */}
                            <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:5 }}>
                              <span style={{
                                width:6, height:6, borderRadius:"50%", flexShrink:0,
                                background: accentColor, display:"inline-block",
                                boxShadow: isPending ? `0 0 0 3px rgba(252,211,77,0.15)` : "none",
                              }}/>
                              <span style={{ fontSize:10, fontWeight:700, color:accentColor, letterSpacing:"0.04em" }}>
                                {isWon ? "WON" : isLost ? "LOST" : "LIVE"}
                              </span>
                              {p.sport && (
                                <span style={{ fontSize:9, fontWeight:700, padding:"1px 6px", borderRadius:4,
                                  background:"rgba(255,255,255,0.06)", color:"rgba(255,255,255,0.35)",
                                  letterSpacing:"0.05em" }}>{p.sport}</span>
                              )}
                              {isParlay && (
                                <span style={{ fontSize:9, fontWeight:700, padding:"1px 6px", borderRadius:4,
                                  background:"rgba(255,107,61,0.12)", color:T.coral, border:"1px solid rgba(255,107,61,0.2)" }}>
                                  PARLAY
                                </span>
                              )}
                              <span style={{ fontSize:9, color:"rgba(255,255,255,0.40)", marginLeft:"auto" }}>{fmtDate(p)}</span>
                            </div>

                            {/* Pick label */}
                            <div style={{ fontSize:13, fontWeight:700, color:"rgba(255,255,255,0.9)", lineHeight:1.35, marginBottom:4 }}>
                              {p.label || p.gameLabel || "Pick"}
                            </div>

                            {/* Meta: wager · odds · type · league (all-scope only) */}
                            <div style={{ display:"flex", alignItems:"center", gap:8, flexWrap:"wrap" }}>
                              {p.wager > 0 && (
                                <span style={{ fontSize:11, color:"rgba(255,255,255,0.45)" }}>
                                  {p.wager.toLocaleString()} pts wagered
                                </span>
                              )}
                              {p.odds && (
                                <span style={{ fontSize:11, fontWeight:700,
                                  color: p.odds > 0 ? "#4ade80" : "rgba(255,255,255,0.5)" }}>
                                  {fmtOdds(p.odds)}
                                </span>
                              )}
                              {p.type && !isParlay && (
                                <span style={{ fontSize:10, padding:"1px 6px", borderRadius:4,
                                  background:"rgba(255,255,255,0.06)", color:"rgba(255,255,255,0.4)",
                                  fontWeight:600, textTransform:"uppercase", letterSpacing:"0.04em" }}>
                                  {p.type}
                                </span>
                              )}
                              {/* League attribution — only in "All Picks" scope (in-league view already
                                  has the league name in the tab header above, so this would be redundant).
                                  Deliberately NOT styled as a tappable pill — it's a read-only label, and
                                  the viewer may not be a member of the named league anyway. */}
                              {!inLeagueScope && (
                                <span style={{
                                  fontSize:10, fontStyle:"italic",
                                  color:"rgba(255,255,255,0.42)", fontWeight:400,
                                }}>
                                  · {resolveLeagueLabel(p)}
                                </span>
                              )}
                            </div>
                          </div>

                          {/* Pts delta */}
                          {profit !== null && (
                            <div style={{
                              fontSize:18, fontWeight:700, letterSpacing:"-0.5px",
                              color: profit >= 0 ? "#4ade80" : "#f87171", flexShrink:0, paddingTop:2,
                              // Fresh wins get a delayed "ka-ching" pop — number scales in
                              // briefly after the card reveal, so the eye lands here last.
                              animation: wasJustSettled && isWon ? "pointsPop 0.5s 0.25s cubic-bezier(0.32,0.72,0,1.32) both" : "none",
                            }}>
                              {profit >= 0 ? "+" : ""}
                              {wasJustSettled && isWon
                                ? <AnimatedFromZero value={Math.abs(profit)} duration={650} />
                                : profit.toLocaleString()}
                              <span style={{ fontSize:9, fontWeight:600, opacity:0.6, marginLeft:2 }}>pts</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        ) : (
          /* Badges tab */
          <div style={{ padding:"16px 20px" }}>
            {(() => {
              const achs = getAchievements({ wins, losses, streak, bestStreak: stats?.bestStreak ?? 0, totalPicks: stats?.totalPicks ?? total, balance });
              return achs.length === 0 ? (
                <div style={{ textAlign:"center", padding:"48px 0" }}>
                  <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.25)" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" style={{ display:"inline-block", marginBottom:10 }}>
                    <circle cx="12" cy="9" r="6"/>
                    <path d="M8.5 13.5 7 22l5-3 5 3-1.5-8.5"/>
                  </svg>
                  <div style={{ fontSize:14, fontWeight:700, color:"rgba(255,255,255,0.4)" }}>No badges yet</div>
                  <div style={{ fontSize:12, color:"rgba(255,255,255,0.40)", marginTop:6 }}>Win some picks to earn badges</div>
                </div>
              ) : (
                <div style={{ display:"flex", flexWrap:"wrap", gap:8 }}>
                  {achs.map((a, i) => (
                    <div key={i} style={{
                      padding:"10px 14px", background:"rgba(255,255,255,0.04)",
                      border:"1px solid rgba(255,255,255,0.08)", borderRadius:12,
                      animation:"fadeUp 0.2s ease both", animationDelay:`${i*40}ms`,
                    }}>
                      <div style={{ fontSize:13, fontWeight:700, color:"rgba(255,255,255,0.9)" }}>{a.label}</div>
                      <div style={{ fontSize:11, color:"rgba(255,255,255,0.4)", marginTop:1 }}>{a.sub}</div>
                    </div>
                  ))}
                </div>
              );
            })()}
          </div>
        )}
      </div>
    </div>
    {selectedProfilePick && (
      <PickDetailModal
        pick={selectedProfilePick}
        user={currentUser}
        userLeagues={[]}
        onClose={() => setSelectedProfilePick(null)}
      />
    )}
    </>
  );
}

function getAchievements({ wins, losses, streak, bestStreak, totalPicks, balance }) {
  const winRate = wins + losses > 0 ? wins / (wins + losses) : 0;
  const badges = [];
  if (wins >= 1)                           badges.push({ label:"First Blood",    sub:"First win"          });
  if (wins >= 10)                          badges.push({ label:"Double Digits",  sub:"10 wins"            });
  if (wins >= 50)                          badges.push({ label:"Veteran",        sub:"50 wins"            });
  if (bestStreak >= 3)                     badges.push({ label:"On Fire",        sub:`${bestStreak} streak`});
  if (bestStreak >= 7)                     badges.push({ label:"Unstoppable",    sub:"7-win streak"       });
  if (winRate >= 0.6 && totalPicks >= 10)  badges.push({ label:"Sharp",          sub:"60%+ win rate"      });
  if (winRate >= 0.75 && totalPicks >= 20) badges.push({ label:"Genius",         sub:"75%+ win rate"      });
  if (balance >= 1500)                     badges.push({ label:"Whale",          sub:"1500+ pts"          });
  if (balance >= 5000)                     badges.push({ label:"Diamond",        sub:"5000+ pts"          });
  if (totalPicks >= 50)                    badges.push({ label:"Grinder",        sub:"50 picks made"      });
  return badges;
}

/* ===========================================
   PAGE: PROFILE (Home Hub)
=========================================== */
/* ===========================================
   HOW TORCH WORKS — FAQ / ABOUT MODAL
=========================================== */
function HowTorchWorksModal({ onClose }) {
  const sections = [
    { icon:"●", title:"What is Torch?",
      body:"Torch is a free sports prediction app. Make picks on real games, earn virtual points, and compete with friends. No real money — ever. Just bragging rights." },
    { icon:"◆", title:"How do points work?",
      body:"You start each league with a set balance. Wager points on your picks — if you're right, you win points based on the odds. If you're wrong, you lose what you wagered." },
    { icon:"▲", title:"Spreads, totals & moneylines",
      body:"Spread: pick a team to win by enough points. Total: pick if the combined score goes over or under a number. Moneyline: just pick who wins." },
    { icon:"★", title:"Leagues",
      body:"Create a private league, share the join code with friends, and see who's the best picker. Each league has its own balance and leaderboard." },
    { icon:"◉", title:"Practice mode",
      body:"Not in a league? You start with 100 practice pts to learn the picks. Create or join a league when you're ready to compete with friends." },
    { icon:"▸", title:"League Payday",
      body:"Your league captain sets a recurring payday — when it hits, everyone gets points added to their balance automatically. This keeps the game competitive even if you had a rough week." },
    { icon:"○", title:"How picks settle",
      body:"When a game finishes, Torch automatically grades your picks against the final score. Wins add points to your balance, losses are already deducted from when you placed the pick." },
    { icon:"◈", title:"Is this gambling?",
      body:"No. Torch uses virtual points with no cash value. You can't deposit, withdraw, or win real money. Real games, real odds, fake stakes, bragging rights." },
    { icon:"▶", title:"Pass the Torch",
      body:"Split a pick with a league mate. You put up points, they match, and you're both in on the same bet. Win together, lose together. Built for the group chat." },
  ];

  return (
    <div onClick={onClose} style={{
      position:"fixed", inset:0, zIndex:250,
      background:"rgba(10,16,26,0.88)", backdropFilter:"blur(8px)",
      display:"flex", alignItems:"flex-end", justifyContent:"center",
      padding:"calc(20px + env(safe-area-inset-top, 0px)) 16px calc(20px + env(safe-area-inset-bottom, 0px))",
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        width:"100%", maxWidth:480,
        maxHeight:"100%",
        background:"linear-gradient(160deg,#181A24,#11131A)",
        border:"1px solid rgba(255,255,255,0.09)",
        borderRadius:20,
        boxShadow:"0 20px 60px rgba(0,0,0,0.5)",
        display:"flex", flexDirection:"column",
        minHeight:0,
      }}>
        {/* Header — sticky inside the inner card; never scrolls away */}
        <div style={{
          display:"flex", alignItems:"center", justifyContent:"space-between",
          padding:"22px 22px 18px",
          borderBottom:"1px solid rgba(255,255,255,0.06)",
          flexShrink:0,
        }}>
          <div style={{ display:"flex", alignItems:"center", gap:10, minWidth:0 }}>
            <TorchLogo size={32} />
            <div style={{ minWidth:0 }}>
              <div style={{ fontSize:18, fontWeight:700, color:"#fff", letterSpacing:"-0.3px" }}>How Torch Works</div>
              <div style={{ fontSize:11, color:"rgba(255,255,255,0.4)" }}>Everything you need to know</div>
            </div>
          </div>
          <button onClick={onClose} style={{
            flexShrink:0,
            background:"rgba(255,255,255,0.08)", border:"none", borderRadius:10,
            width:36, height:36, cursor:"pointer", color:"rgba(255,255,255,0.7)",
            fontSize:18, display:"flex", alignItems:"center", justifyContent:"center",
          }} aria-label="Close">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>

        {/* Scrollable body */}
        <div style={{
          flex:1, overflowY:"auto", minHeight:0,
          WebkitOverflowScrolling:"touch", overscrollBehavior:"contain",
          padding:"6px 22px 22px",
        }}>

        {/* FAQ items */}
        {sections.map((s, i) => (
          <div key={i} style={{
            padding:"14px 0",
            borderTop: i === 0 ? "none" : "1px solid rgba(255,255,255,0.06)",
          }}>
            <div style={{ display:"flex", alignItems:"flex-start", gap:12 }}>
              <span style={{ fontSize:20, flexShrink:0, lineHeight:1 }}>{s.icon}</span>
              <div>
                <div style={{ fontSize:14, fontWeight:700, color:"#fff", marginBottom:5 }}>{s.title}</div>
                <div style={{ fontSize:13, color:"rgba(255,255,255,0.55)", lineHeight:1.6 }}>{s.body}</div>
              </div>
            </div>
          </div>
        ))}
        </div>
      </div>
    </div>
  );
}

// ── ADMIN PANEL ─────────────────────────────────────────────────────────────
function AdminPanel({ user, onClose }) {
  const [search, setSearch] = useState("");
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [picks, setPicks] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [loading, setLoading] = useState(false);
  const [pickLoading, setPickLoading] = useState(false);
  const [resettingId, setResettingId] = useState(null);
  const [adminTab, setAdminTab] = useState("users"); // "users" | "reports" | "tournament"
  const [reports, setReports] = useState([]);
  const [reportsLoading, setReportsLoading] = useState(false);
  const [tournamentPicks, setTournamentPicks] = useState([]);
  const [tournamentLoading, setTournamentLoading] = useState(false);
  const [tournamentWinner, setTournamentWinner] = useState("");
  const [tournamentSecond, setTournamentSecond] = useState(""); // 2nd place — used for Place/Show settlement
  const [tournamentThird,  setTournamentThird]  = useState(""); // 3rd place — used for Show settlement
  const [tournamentSettling, setTournamentSettling] = useState(false);
  // ── World Cup golden-boot admin state ──
  // The boot has no live feed; an admin enters the tournament top scorer's
  // name post-tournament. Writes goldenBootCode to /worldCup2026Live/standings,
  // which the scorer reads to award SCORING.GOLDEN_BOOT (120) to matching picks.
  const [wcBootName, setWcBootName]         = useState("");
  const [wcBootSaving, setWcBootSaving]     = useState(false);
  const [wcBootSaved, setWcBootSaved]       = useState(false);
  const [wcBootCurrent, setWcBootCurrent]   = useState(null);  // currently-stored value
  const [wcBootConfirming, setWcBootConfirming] = useState(false);
  // ── Push notification admin state ──
  // Drives the send-push card. leagueId blank => target ALL users. dryRun
  // counts tokens without sending. Confirm step prevents a fat-finger blast.
  const [pushTitle, setPushTitle]         = useState("Lock in your World Cup knockout picks!");
  const [pushBody, setPushBody]           = useState("Go to your league page and tap Picks now to pick your Round of 32 winners.");
  const [pushLeagueId, setPushLeagueId]   = useState("");
  const [pushUrl, setPushUrl]             = useState("https://torchpicks.com");
  const [pushSending, setPushSending]     = useState(false);
  const [pushConfirming, setPushConfirming] = useState(false);
  const [pushResult, setPushResult]       = useState(null);
  // Fun-balance backfill — separate state so the two backfill cards don't share buttons.
  const [funConfirming, setFunConfirming] = useState(false);
  const [funRunning, setFunRunning]       = useState(false);
  const [funResult, setFunResult]         = useState(null); // { credited, alreadySeeded, errors, totalScanned }
  // Public-picks (Pulse mirror) backfill — walks every pick in /picks/{uid}/userPicks
  // and creates the mirror doc in /publicPicks if missing. Idempotent. Fills the
  // Pulse feed with historical data so it isn't empty for users who submitted
  // picks before the mirror infrastructure was healthy.
  const [pulseConfirming, setPulseConfirming] = useState(false);
  const [pulseRunning, setPulseRunning]       = useState(false);
  const [pulseResult, setPulseResult]         = useState(null); // { scanned, alreadyMirrored, created, errors }
  const [purgeConfirming, setPurgeConfirming] = useState(false);
  const [purgeRunning, setPurgeRunning]       = useState(false);
  const [purgeResult, setPurgeResult]         = useState(null); // { leaguesMatched, mirrorsDeleted, commentsDeleted, errors }
  const [bfPurgeConfirming, setBfPurgeConfirming] = useState(false);
  const [bfPurgeRunning, setBfPurgeRunning]       = useState(false);
  const [bfPurgeResult, setBfPurgeResult]         = useState(null); // { membersDeleted, activityDeleted, leagueDocDeleted, errors }


  // Load reports
  const loadReports = async () => {
    setReportsLoading(true);
    try {
      const { getFirestore, collection, getDocs, query, orderBy } = await import("firebase/firestore");
      const db = getFirestore(app);
      const snap = await getDocs(query(collection(db, "reports"), orderBy("createdAt", "desc")));
      const results = [];
      snap.forEach(d => results.push({ id: d.id, ...d.data() }));
      setReports(results);
    } catch (e) {
      console.warn("[Admin] Load reports failed:", e.message);
      // Fallback without orderBy (index might not exist)
      try {
        const { getFirestore, collection, getDocs } = await import("firebase/firestore");
        const db = getFirestore(app);
        const snap = await getDocs(collection(db, "reports"));
        const results = [];
        snap.forEach(d => results.push({ id: d.id, ...d.data() }));
        results.sort((a, b) => (tsToMillis(b.createdAt) ?? 0) - (tsToMillis(a.createdAt) ?? 0));
        setReports(results);
      } catch {}
    }
    setReportsLoading(false);
  };

  // Delete a report
  const deleteReport = async (reportId) => {
    try {
      const { getFirestore, doc, deleteDoc } = await import("firebase/firestore");
      const db = getFirestore(app);
      await deleteDoc(doc(db, "reports", reportId));
      setReports(prev => prev.filter(r => r.id !== reportId));
      window.torchToast?.("Report dismissed", "info");
    } catch (e) { window.torchToast?.("Delete failed", "error"); }
  };

  // ── Fun-balance backfill — double-tap to confirm ──
  // Calls backfillFunBalanceCallable. Idempotent: server skips users that
  // already have a `seededAt` flag, so re-runs are safe. For each user
  // without the flag, sets balance to max(currentBalance, 1000) and stamps
  // `seededAt`. Used to retroactively credit existing users with the new
  // 1,000-pt practice floor introduced after the seeding rule went live.
  const onBackfillFunBalanceClick = async () => {
    if (!funConfirming) {
      setFunConfirming(true);
      setTimeout(() => setFunConfirming(false), 4000);
      return;
    }
    setFunConfirming(false);
    setFunRunning(true);
    try {
      const { getFunctions, httpsCallable } = await import("firebase/functions");
      const fns = getFunctions(app);
      const fn = httpsCallable(fns, "backfillFunBalanceCallable");
      const res = await fn({});
      const data = res?.data || {};
      setFunResult(data);
      const summary = `Credited ${data.credited ?? 0} · Already seeded: ${data.alreadySeeded ?? 0}`
        + ((data.errors ?? 0) > 0 ? ` · Errors: ${data.errors}` : "");
      window.torchToast?.(`Fun backfill done — ${summary}`, "success");
      console.info("[Admin/FunBalance] Backfill result:", data);
    } catch (e) {
      console.error("[Admin/FunBalance] Backfill failed:", e);
      window.torchToast?.(`Fun backfill failed: ${e.message || "unknown"}`, "error");
    } finally {
      setFunRunning(false);
    }
  };


  // ── Pulse mirror backfill — double-tap to confirm ──
  // Calls backfillPublicPicksCallable. Idempotent: server skips picks
  // that already have a /publicPicks/{pickId} mirror, so re-runs only
  // create missing ones. Use after any window when client-side mirror
  // writes were failing (e.g., rules deploy mismatch) to fill the feed.
  const onBackfillPulseClick = async () => {
    if (!pulseConfirming) {
      setPulseConfirming(true);
      setTimeout(() => setPulseConfirming(false), 4000);
      return;
    }
    setPulseConfirming(false);
    setPulseRunning(true);
    try {
      const { getFunctions, httpsCallable } = await import("firebase/functions");
      const fns = getFunctions(app);
      const fn = httpsCallable(fns, "backfillPublicPicksCallable");
      const res = await fn({});
      const data = res?.data || {};
      setPulseResult(data);
      const summary = `Created ${data.created ?? 0} · Already mirrored: ${data.alreadyMirrored ?? 0} · Scanned ${data.scanned ?? 0}`
        + ((data.errors ?? 0) > 0 ? ` · Errors: ${data.errors}` : "");
      window.torchToast?.(`Pulse backfill done — ${summary}`, "success");
      console.info("[Admin/Pulse] Backfill result:", data);
    } catch (e) {
      console.error("[Admin/Pulse] Backfill failed:", e);
      window.torchToast?.(`Pulse backfill failed: ${e.message || "unknown"}`, "error");
    } finally {
      setPulseRunning(false);
    }
  };

  // ── Purge pick'em/survivor mirrors — double-tap to confirm ──
  // Calls purgeNonWagerMirrorsCallable. One-time cleanup for mirrors created
  // by onPickCreatedMirror before it learned to skip non-wager picks (the
  // trigger saw the client's deliberate no-mirror as a MISSING mirror and
  // "repaired" it with admin privileges). Deletes every /publicPicks doc
  // whose leagueId belongs to a pickem/survivor league, comments first.
  // Idempotent — a second run finds nothing.
  const onPurgeMirrorsClick = async () => {
    if (!purgeConfirming) {
      setPurgeConfirming(true);
      setTimeout(() => setPurgeConfirming(false), 4000);
      return;
    }
    setPurgeConfirming(false);
    setPurgeRunning(true);
    try {
      const { getFunctions, httpsCallable } = await import("firebase/functions");
      const fns = getFunctions(app);
      const fn = httpsCallable(fns, "purgeNonWagerMirrorsCallable");
      const res = await fn({});
      const data = res?.data || {};
      setPurgeResult(data);
      const summary = `Deleted ${data.mirrorsDeleted ?? 0} mirror${(data.mirrorsDeleted ?? 0) === 1 ? "" : "s"} across ${data.leaguesMatched ?? 0} league${(data.leaguesMatched ?? 0) === 1 ? "" : "s"}`
        + ((data.errors ?? 0) > 0 ? ` · Errors: ${data.errors}` : "");
      window.torchToast?.(`Mirror purge done — ${summary}`, "success");
      console.info("[Admin/PurgeMirrors] Result:", data);
    } catch (e) {
      console.error("[Admin/PurgeMirrors] Failed:", e);
      window.torchToast?.(`Mirror purge failed: ${e.message || "unknown"}`, "error");
    } finally {
      setPurgeRunning(false);
    }
  };

  // ── Purge Bonfire data — double-tap to confirm ──
  // Calls purgeBonfireDataCallable. Final step of the Bonfire removal
  // (2026-07-23): all Bonfire code is gone from the client and functions;
  // this deletes the DATA it left behind — every doc under
  // leagues/bonfire/members and /activity, then the league doc itself.
  // Idempotent — once the subtree is gone, a re-run finds nothing.
  const onPurgeBonfireClick = async () => {
    if (!bfPurgeConfirming) {
      setBfPurgeConfirming(true);
      setTimeout(() => setBfPurgeConfirming(false), 4000);
      return;
    }
    setBfPurgeConfirming(false);
    setBfPurgeRunning(true);
    try {
      const { getFunctions, httpsCallable } = await import("firebase/functions");
      const fns = getFunctions(app);
      const fn = httpsCallable(fns, "purgeBonfireDataCallable");
      const res = await fn({});
      const data = res?.data || {};
      setBfPurgeResult(data);
      const summary = `Members: ${data.membersDeleted ?? 0} · Activity: ${data.activityDeleted ?? 0} · League doc: ${data.leagueDocDeleted ? "deleted" : "already gone"}`
        + ((data.errors ?? 0) > 0 ? ` · Errors: ${data.errors}` : "");
      window.torchToast?.(`Bonfire purge done — ${summary}`, "success");
      console.info("[Admin/PurgeBonfire] Result:", data);
    } catch (e) {
      console.error("[Admin/PurgeBonfire] Failed:", e);
      window.torchToast?.(`Bonfire purge failed: ${e.message || "unknown"}`, "error");
    } finally {
      setBfPurgeRunning(false);
    }
  };


  // Reset the reported pick to pending AND auto-dismiss the report.
  // Delegates to the resetReportedPickCallable Cloud Function, which uses admin SDK
  // to bypass Firestore rules (client can't read userPicks across other users).
  // Cron will re-grade once the game is truly FINAL.
  const resetReportedPick = async (report) => {
    // RESET-OWNER-ONLY (fix 3.2): the server now resets by exact pick id only.
    if (!report?.pickId) {
      window.torchToast?.("Report has no pick id — fix this one by hand in the console", "error");
      return;
    }
    if (!window.confirm(`Reset "${report.label || 'this pick'}" to pending? Cron will re-grade within 5 min.`)) return;
    setResettingId(report.id);
    try {
      const { getFunctions, httpsCallable } = await import("firebase/functions");
      const fns = getFunctions(app);
      const fn = httpsCallable(fns, "resetReportedPickCallable");
      const res = await fn({
        reportId: report.id,
        pickId: report.pickId || null,
        label: report.label || null,
        pickOwnerUserId: report.pickOwnerUserId || report.userId || null,
      });
      if (res?.data?.success) {
        console.info("[Admin] Reset result:", res.data);
        setReports(prev => prev.filter(r => r.id !== report.id));
        window.torchToast?.(`Reset "${report.label}" → cron will re-grade within 5 min`, "success");
      } else {
        throw new Error("Function returned no success");
      }
    } catch (e) {
      console.warn("[Admin] Reset reported pick failed:", e);
      // Callable errors wrap the HttpsError message under e.message or e.details
      const msg = e?.message || e?.details || String(e);
      window.torchToast?.("Reset failed: " + msg, "error");
    }
    setResettingId(null);
  };

  // Load ALL pending tournament picks across ALL users (admin only)
  const loadTournamentPicks = async () => {
    setTournamentLoading(true);
    try {
      const { getFirestore, collectionGroup, getDocs, query, where } = await import("firebase/firestore");
      const db = getFirestore(app);
      const snap = await getDocs(query(
        collectionGroup(db, "userPicks"),
        where("status", "==", "pending")
      ));
      const outright = [];
      snap.forEach(d => {
        const data = d.data();
        const pathParts = d.ref.path.split("/");
        const userId = pathParts[1];
        if (data.type === "outright" || data.label?.includes("to Win") || data.sportKey?.includes("_winner")) {
          outright.push({ id: d.id, ref: d.ref, userId, ...data });
        }
      });
      setTournamentPicks(outright);
    } catch (e) {
      console.warn("[Admin] Load tournament picks failed:", e.message);
      window.torchToast?.("Couldn't pull tournament picks right now. Try again in a sec.", "error");
    }
    setTournamentLoading(false);
  };

  // Settle all tournament picks with up to 3 finishers.
  //   winnerName  — REQUIRED, used for: golf majors (Masters), Derby Win picks
  //   secondName  — OPTIONAL, used for: Derby Place picks (top 2), Derby Show picks (top 3)
  //   thirdName   — OPTIONAL, used for: Derby Show picks (top 3)
  // Picks without a `market` field (legacy / golf) settle as "match the winner only".
  // Picks with market='win'|'place'|'show' settle against the appropriate finisher set.
  const settleTournament = async (winnerName, secondName = "", thirdName = "") => {
    const w = (winnerName || "").trim();
    if (!w || tournamentSettling || tournamentPicks.length === 0) return;
    const s = (secondName || "").trim();
    const t = (thirdName || "").trim();
    // Detect whether any picks need 2nd/3rd to be graded fairly.
    const hasPlace = tournamentPicks.some(p => p.market === "place");
    const hasShow  = tournamentPicks.some(p => p.market === "show");
    if (hasPlace && !s) {
      if (!window.confirm("There are Place picks pending and no 2nd-place finisher entered. Continue and grade them all as LOST?")) return;
    }
    if (hasShow && (!s || !t)) {
      if (!window.confirm("There are Show picks pending and the top 3 isn't fully entered. Continue and grade missing slots as LOST?")) return;
    }
    const summary = [w, s && `2nd: ${s}`, t && `3rd: ${t}`].filter(Boolean).join(" • ");
    if (!window.confirm(`Settle ${tournamentPicks.length} tournament picks with — ${summary}? This cannot be undone.`)) return;
    setTournamentSettling(true);
    try {
      // ── Pre-validate odds before settling anything ──
      // A missing/zero odds value would settle a winning longshot at the -110
      // fallback. Abort instead and let the admin fix the data.
      const badOddsPicks = tournamentPicks.filter(p => {
        const o = p.odds;
        return o == null || o === 0 || !Number.isFinite(Number(o));
      });
      if (badOddsPicks.length > 0) {
        const userList = badOddsPicks.slice(0, 5).map(p => `${p.userId?.slice(0, 8) || "?"} (${p.label || "no label"})`).join(", ");
        const msg = `Aborting — ${badOddsPicks.length} pick${badOddsPicks.length > 1 ? "s" : ""} ${badOddsPicks.length > 1 ? "have" : "has"} missing/invalid odds. Examples: ${userList}. Fix the pick data before settling, or those wins will underpay.`;
        console.error("[Admin/Tournament] Settlement aborted:", { count: badOddsPicks.length, picks: badOddsPicks.map(p => ({ id: p.id, userId: p.userId, label: p.label, odds: p.odds })) });
        window.torchToast?.(msg, "error");
        setTournamentSettling(false);
        return;
      }
      // SERVER-SETTLEMENT (fix 4.1 step 2, 2026-09-28): the panel records the
      // result; the next settlement run (within 5 minutes) grades every pending
      // pick on these races/tournaments -- Win / Place / Show, same rules as
      // before -- and credits league balance, W/L and practice balance with the
      // server's accounting. The panel used to write results and credit the
      // practice balance directly, which paid league winners to the wrong
      // balance and never credited their league.
      const { getFunctions, httpsCallable } = await import("firebase/functions");
      const fn = httpsCallable(getFunctions(app), "adminSetOutrightWinnerCallable");
      const res = await fn({
        winner: w, second: s, third: t,
        picks: tournamentPicks.map(p => ({ userId: p.userId, pickId: p.id })),
      });
      const n = res?.data?.gameIds?.length || 0;
      window.torchToast?.(`Result saved for ${n} event${n === 1 ? "" : "s"} — ${tournamentPicks.length} picks settle within 5 minutes.`, "success");
      setTournamentPicks([]);
      setTournamentWinner("");
      setTournamentSecond("");
      setTournamentThird("");
    } catch (e) {
      window.torchToast?.(e.message || "Settlement failed", "error");
    }
    setTournamentSettling(false);
  };

  // ── World Cup golden-boot: load current + save ──
  const loadWcBoot = async () => {
    try {
      const { getFirestore, doc, getDoc } = await import("firebase/firestore");
      const db = getFirestore(app);
      const snap = await getDoc(doc(db, "worldCup2026Live", "standings"));
      const code = snap.exists() ? snap.data()?.goldenBootCode : null;
      const val = typeof code === "string" && code.trim() ? code.trim() : null;
      setWcBootCurrent(val);
      if (val && !wcBootName) setWcBootName(val);
    } catch (e) {
      console.warn("[Admin/WC] load goldenBootCode failed:", e.message);
    }
  };

  // ── Send push notification (admin) ──
  // Two-step: first tap arms confirm (or runs immediately if dryRun), second
  // tap actually sends. Calls the sendPushToLeague callable. Blank leagueId
  // targets every user; a leagueId scopes to that league's members.
  const sendPush = async (dryRun) => {
    const title = pushTitle.trim();
    const body = pushBody.trim();
    if (!title || !body) {
      window.torchToast?.("Title and message are both required", "error");
      return;
    }
    // Real sends require a confirm tap; dry runs fire immediately.
    if (!dryRun && !pushConfirming) {
      setPushConfirming(true);
      setTimeout(() => setPushConfirming(false), 4000);
      return;
    }
    setPushConfirming(false);
    setPushSending(true);
    setPushResult(null);
    try {
      const { getFunctions, httpsCallable } = await import("firebase/functions");
      const fns = getFunctions(app);
      const fn = httpsCallable(fns, "sendPushToLeague");
      const res = await fn({
        leagueId: pushLeagueId.trim() || undefined,
        title,
        body,
        url: pushUrl.trim() || undefined,
        dryRun: !!dryRun,
      });
      const d = res?.data || {};
      setPushResult(d);
      if (dryRun) {
        window.torchToast?.(`Dry run — ${d.tokens ?? 0} device tokens across ${d.targeted ?? 0} users`, "info");
      } else {
        window.torchToast?.(`Sent to ${d.sent ?? 0} devices · ${d.failed ?? 0} failed · ${d.pruned ?? 0} pruned`, "success");
      }
    } catch (e) {
      window.torchToast?.(e.message || "Push send failed", "error");
      setPushResult({ error: e.message });
    } finally {
      setPushSending(false);
    }
  };

  const saveWcBoot = async () => {
    const name = wcBootName.trim();
    if (!name) return;
    setWcBootSaving(true);
    try {
      const { getFirestore, doc, setDoc } = await import("firebase/firestore");
      const db = getFirestore(app);
      // merge:true so we never clobber the cron-written groups/knockouts.
      await setDoc(doc(db, "worldCup2026Live", "standings"), { goldenBootCode: name }, { merge: true });
      setWcBootCurrent(name);
      setWcBootSaved(true);
      setWcBootConfirming(false);
      window.torchToast?.(`Golden Boot set to "${name}" — 120 pts now scoring.`, "success");
      setTimeout(() => setWcBootSaved(false), 2500);
    } catch (e) {
      window.torchToast?.(e.message || "Failed to set Golden Boot", "error");
    }
    setWcBootSaving(false);
  };

  // Auto-load all users on mount
  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { getFirestore, collection, getDocs, limit, query } = await import("firebase/firestore");
        const db = getFirestore(app);
        const snap = await getDocs(query(collection(db, "users"), limit(50)));
        const results = [];
        snap.forEach(d => results.push({ id: d.id, ...d.data() }));
        setUsers(results);
      } catch (e) { console.warn("[Admin] Auto-load users failed:", e.message); }
      setLoading(false);
    })();
  }, []);

  // Search users by email or username
  const searchUsers = async () => {
    if (!search.trim()) {
      // Empty search — reload all users
      setLoading(true);
      try {
        const { getFirestore, collection, getDocs, limit, query } = await import("firebase/firestore");
        const db = getFirestore(app);
        const snap = await getDocs(query(collection(db, "users"), limit(50)));
        const results = [];
        snap.forEach(d => results.push({ id: d.id, ...d.data() }));
        setUsers(results);
      } catch {}
      setLoading(false);
      return;
    }
    // Filter locally first
    const q = search.trim().toLowerCase();
    const local = users.filter(u => {
      const name = (u.username || u.displayName || "").toLowerCase();
      const email = (u.email || "").toLowerCase();
      return name.includes(q) || email.includes(q) || u.id.includes(q);
    });
    if (local.length > 0) { setUsers(local); return; }
    // Fallback: Firestore query
    setLoading(true);
    try {
      const { getFirestore, collection, query: fsQuery, where, getDocs, limit } = await import("firebase/firestore");
      const db = getFirestore(app);
      const results = [];
      const emailQ = fsQuery(collection(db, "users"), where("email", "==", q), limit(10));
      const emailSnap = await getDocs(emailQ);
      emailSnap.forEach(d => results.push({ id: d.id, ...d.data() }));
      if (results.length === 0) {
        const allSnap = await getDocs(fsQuery(collection(db, "users"), limit(100)));
        allSnap.forEach(d => {
          const data = d.data();
          const name = (data.username || data.displayName || data.email || "").toLowerCase();
          if (name.includes(q)) results.push({ id: d.id, ...data });
        });
      }
      setUsers(results);
    } catch (e) { console.warn("[Admin] Search failed:", e.message); }
    setLoading(false);
  };

  // Load picks for a user — direct Firestore read
  const loadUserPicks = async (uid) => {
    setPickLoading(true);
    setSelectedUser(uid);
    setPicks([]);
    try {
      const { getFirestore, collection, getDocs } = await import("firebase/firestore");
      const db = getFirestore(app);
      const colRef = collection(db, "picks", uid, "userPicks");
      const snap = await getDocs(colRef);
      const results = [];
      snap.forEach(d => results.push({ id: d.id, ...d.data(), _userId: uid }));
      // Sort client-side to avoid needing Firestore index
      results.sort((a, b) => {
        const ts = p => tsToMillis(p.submittedAt) ?? 0;
        return ts(b) - ts(a);
      });
      setPicks(results);
      if (results.length === 0) window.torchToast?.(`No picks found for user ${uid.slice(0,8)}...`, "info");
    } catch (e) {
      console.error("[Admin] Load picks failed:", e);
      window.torchToast?.("That didn't work — " + e.message, "error");
    }
    setPickLoading(false);
  };

  // Search picks by label across known users
  const searchPicksByLabel = async () => {
    if (!search.trim()) return;
    setLoading(true);
    setPicks([]);
    setSelectedUser("search");
    try {
      const { getFirestore, collectionGroup, query, where, getDocs, limit } = await import("firebase/firestore");
      const db = getFirestore(app);
      // collectionGroup query on "userPicks" with label filter
      const q = query(collectionGroup(db, "userPicks"), where("label", "==", search.trim()), limit(20));
      const snap = await getDocs(q);
      const results = [];
      snap.forEach(d => results.push({ id: d.id, ...d.data(), _userId: d.ref.parent.parent.id }));
      setPicks(results);
    } catch (e) {
      console.warn("[Admin] Pick search failed:", e.message);
      window.torchToast?.("Search isn't ready yet. We're still indexing this league.", "error");
    }
    setLoading(false);
  };

  // Reset a pick to pending
  const resetPick = async (pick) => {
    const uid = pick._userId || pick.userId || selectedUser;
    if (!uid || uid === "search") {
      window.torchToast?.("Can't determine user ID for this pick", "error");
      return;
    }
    setResettingId(pick.id);
    try {
      // SERVER-OWNED-LOCK (fix 4.1 step 3): reset through the callable, which
      // also REVERSES the original payout. The old direct write set the pick
      // back to pending but left the credit in place, so the re-settle paid
      // the user twice.
      const { getFunctions, httpsCallable } = await import("firebase/functions");
      await httpsCallable(getFunctions(app), "resetReportedPickCallable")({ pickId: pick.id, pickOwnerUserId: uid, label: pick.label || null });
      setPicks(prev => prev.map(p => p.id === pick.id ? { ...p, status: "pending", points: 0 } : p));
      window.torchToast?.(`Reset "${pick.label}" to pending — CF will re-settle within 5 min`, "success");
    } catch (e) {
      console.warn("[Admin] Reset failed:", e.message);
      window.torchToast?.("Reset failed: " + e.message, "error");
    }
    setResettingId(null);
  };

  const filtered = statusFilter === "all" ? picks : picks.filter(p => p.status === statusFilter);
  const statusColors = { won:"#4ade80", lost:"#f87171", pending:"#E5B459", voided:"#6B7280", push:"#9CA3AF" };

  return createPortal(
    <div style={{ position:"fixed", inset:0, zIndex:400, background:"rgba(0,0,0,0.92)", backdropFilter:"blur(20px)", display:"flex", flexDirection:"column" }}>
      {/* Header */}
      <div style={{ padding:"env(safe-area-inset-top, 12px) 16px 12px", borderBottom:"1px solid rgba(239,68,68,0.15)", background:"rgba(239,68,68,0.04)", flexShrink:0 }}>
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:12 }}>
          <div style={{ display:"flex", alignItems:"center", gap:8 }}>
            <span style={{ fontSize:18 }}>⚙️</span>
            <span style={{ fontFamily:FD, fontSize:20, fontWeight:700, color:"#f87171" }}>Admin Panel</span>
          </div>
          <button onClick={onClose} style={{ width:36, height:36, borderRadius:10, background:"rgba(255,255,255,0.06)", border:"1px solid rgba(255,255,255,0.08)", color:"rgba(255,255,255,0.5)", fontSize:14, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center" }}>✕</button>
        </div>

        {/* Search bar */}
        <div style={{ display:"flex", gap:8 }}>
          <input
            value={search} onChange={e => setSearch(e.target.value)}
            onKeyDown={e => { if (e.key === "Enter") searchUsers(); }}
            placeholder="Search by email, username, or pick label..."
            style={{ flex:1, padding:"10px 12px", borderRadius:10, border:"1px solid rgba(255,255,255,0.08)", background:"rgba(255,255,255,0.04)", color:"#fff", fontSize:14, outline:"none", fontFamily:FB, boxSizing:"border-box" }}
          />
          <button onClick={() => searchUsers()} style={{ padding:"10px 16px", borderRadius:10, border:"none", background:"rgba(239,68,68,0.15)", color:"#f87171", fontSize:13, fontWeight:700, cursor:"pointer", flexShrink:0 }}>
            {loading ? "..." : "Search"}
          </button>
        </div>

        {/* Tabs */}
        <div style={{ display:"flex", gap:0, borderTop:"1px solid rgba(255,255,255,0.04)" }}>
          <button onClick={() => setAdminTab("users")} style={{ flex:1, padding:"10px 0", background:"none", border:"none", borderBottom: adminTab === "users" ? "2px solid #f87171" : "2px solid transparent", color: adminTab === "users" ? "#f87171" : "rgba(255,255,255,0.3)", fontSize:13, fontWeight:700, cursor:"pointer" }}>Users</button>
          <button onClick={() => { setAdminTab("reports"); if (reports.length === 0) loadReports(); }} style={{ flex:1, padding:"10px 0", background:"none", border:"none", borderBottom: adminTab === "reports" ? "2px solid #f87171" : "2px solid transparent", color: adminTab === "reports" ? "#f87171" : "rgba(255,255,255,0.3)", fontSize:13, fontWeight:700, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", gap:6 }}>
            Reports
            {reports.length > 0 && <span style={{ background:"#EF4444", color:"#fff", fontSize:10, fontWeight:800, padding:"1px 6px", borderRadius:8, minWidth:16, textAlign:"center" }}>{reports.length}</span>}
          </button>
          <button onClick={() => { setAdminTab("tournament"); if (tournamentPicks.length === 0) loadTournamentPicks(); loadWcBoot(); }} style={{ flex:1, padding:"10px 0", background:"none", border:"none", borderBottom: adminTab === "tournament" ? "2px solid #FCD34D" : "2px solid transparent", color: adminTab === "tournament" ? "#FCD34D" : "rgba(255,255,255,0.3)", fontSize:13, fontWeight:700, cursor:"pointer" }}>Settle</button>
          <button onClick={() => setAdminTab("tools")} style={{ flex:1, padding:"10px 0", background:"none", border:"none", borderBottom: adminTab === "tools" ? `2px solid ${T.coral}` : "2px solid transparent", color: adminTab === "tools" ? T.coral : "rgba(255,255,255,0.3)", fontSize:13, fontWeight:700, cursor:"pointer" }}>💰 Practice</button>
        </div>
      </div>

      {/* Body */}
      <div style={{ flex:1, overflow:"auto", WebkitOverflowScrolling:"touch", overscrollBehavior:"contain", padding:16, minHeight:0 }}>

        {/* ── PRACTICE TAB ── */}
        {adminTab === "tools" && (
          <div>
            <div style={{ fontSize:11, fontWeight:800, color:"rgba(255,255,255,0.36)", letterSpacing:"0.1em", fontFamily:FM, marginBottom:14 }}>
              PRACTICE ADMIN
            </div>

            {/* ── Fun-balance backfill ── */}
            {/* Credits 1,000 pts to every existing user that doesn't yet have
                the `seededAt` flag on their /users/{uid} doc — i.e. anyone who
                signed in before the seed code went live. Idempotent. */}
            <div style={{
              padding:"18px 20px", marginTop:12, borderRadius:14,
              background:"linear-gradient(180deg,rgba(255,140,0,0.06) 0%,rgba(255,107,61,0.02) 100%)",
              border:"1px solid rgba(255,140,0,0.20)",
            }}>
              <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:8 }}>
                <span style={{ fontSize:18 }}>💰</span>
                <div style={{ fontSize:15, fontWeight:800, color:"#fff", fontFamily:FD }}>
                  Backfill Practice Balance
                </div>
              </div>
              <div style={{ fontSize:12.5, color:"rgba(255,255,255,0.55)", lineHeight:1.55, marginBottom:14 }}>
                Iterates the <code style={{ background:"rgba(255,255,255,0.06)", padding:"1px 5px", borderRadius:4, fontSize:11.5 }}>users</code> collection and credits 1,000 pts to every user without a <code style={{ background:"rgba(255,255,255,0.06)", padding:"1px 5px", borderRadius:4, fontSize:11.5 }}>seededAt</code> flag. Uses <code style={{ background:"rgba(255,255,255,0.06)", padding:"1px 5px", borderRadius:4, fontSize:11.5 }}>max(currentBalance, 1000)</code> so existing winners aren't demoted.
                <br/><br/>
                Idempotent — running it twice is safe. Run this once after deploying the seeding code; new signups are auto-seeded on first login thereafter.
              </div>

              <button
                onClick={onBackfillFunBalanceClick}
                disabled={funRunning}
                style={{
                  width:"100%", padding:"13px", borderRadius:10, border:"none",
                  cursor: funRunning ? "wait" : "pointer",
                  background: funRunning
                    ? "rgba(255,255,255,0.08)"
                    : funConfirming
                      ? "linear-gradient(135deg,#EF4444,#DC2626)"
                      : T.coral,
                  color:"#fff", fontSize:13, fontWeight:800,
                  letterSpacing:"0.04em", textTransform:"uppercase",
                  transition:"background 0.18s",
                  boxShadow: funConfirming
                    ? "0 4px 16px rgba(239,68,68,0.35)"
                    : "0 4px 16px rgba(255,107,61,0.25)",
                }}
              >
                {funRunning
                  ? "Running…"
                  : funConfirming
                    ? "Tap again to confirm"
                    : "Run Practice Balance Backfill"}
              </button>

              {funConfirming && !funRunning && (
                <div style={{ marginTop:8, fontSize:11, color:"rgba(255,255,255,0.5)", textAlign:"center", fontFamily:FM }}>
                  Auto-cancels in 4s
                </div>
              )}
            </div>

            {/* Fun backfill result card */}
            {funResult && (
              <div style={{
                padding:"14px 16px", marginTop:8, borderRadius:12,
                background:"rgba(74,222,128,0.05)",
                border:"1px solid rgba(74,222,128,0.20)",
              }}>
                <div style={{ fontSize:12, fontWeight:800, color:"#4ade80", letterSpacing:"0.10em", fontFamily:FM, marginBottom:8 }}>
                  ✓ LAST RUN
                </div>
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:8 }}>
                  {[
                    { label:"Credited",      val: funResult.credited      ?? 0, color:"#4ade80" },
                    { label:"Already seeded",val: funResult.alreadySeeded  ?? 0, color:"rgba(255,255,255,0.55)" },
                    { label:"Errors",        val: funResult.errors        ?? 0, color: (funResult.errors ?? 0) > 0 ? "#f87171" : "rgba(255,255,255,0.35)" },
                  ].map(({ label, val, color }) => (
                    <div key={label} style={{
                      padding:"8px 10px", borderRadius:8,
                      background:"rgba(255,255,255,0.02)",
                      border:"1px solid rgba(255,255,255,0.04)",
                      textAlign:"center",
                    }}>
                      <div style={{ fontFamily:FD, fontSize:18, fontWeight:800, color, lineHeight:1 }}>{val}</div>
                      <div style={{ fontSize:9, color:"rgba(255,255,255,0.35)", marginTop:4, letterSpacing:"0.08em", textTransform:"uppercase", fontWeight:700, fontFamily:FM }}>{label}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ── Pulse mirror backfill ── */}
            {/* Walks every pick in /picks/{uid}/userPicks via collectionGroup
                and writes a mirror to /publicPicks for any that's missing.
                Idempotent — checks existence before writing. Use after any
                period when client-side mirror writes were failing silently
                (rules deploy mismatch, permission errors, etc). */}
            <div style={{
              padding:"18px 20px", marginTop:12, borderRadius:14,
              background:"linear-gradient(180deg,rgba(91,160,229,0.06) 0%,rgba(91,160,229,0.02) 100%)",
              border:"1px solid rgba(91,160,229,0.20)",
            }}>
              <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:8 }}>
                <span style={{ fontSize:18 }}>📡</span>
                <div style={{ fontSize:15, fontWeight:800, color:"#fff", fontFamily:FD }}>
                  Backfill Pulse Feed
                </div>
              </div>
              <div style={{ fontSize:12.5, color:"rgba(255,255,255,0.55)", lineHeight:1.55, marginBottom:14 }}>
                Walks every pick in <code style={{ background:"rgba(255,255,255,0.06)", padding:"1px 5px", borderRadius:4, fontSize:11.5 }}>/picks/&#123;uid&#125;/userPicks</code> and creates a <code style={{ background:"rgba(255,255,255,0.06)", padding:"1px 5px", borderRadius:4, fontSize:11.5 }}>/publicPicks</code> mirror doc for any that doesn't already have one.
                <br/><br/>
                Idempotent — picks that already mirror are skipped. Run this once after the rules infrastructure was fixed to fill the Pulse feed with historical picks.
              </div>

              <button
                onClick={onBackfillPulseClick}
                disabled={pulseRunning}
                style={{
                  width:"100%", padding:"13px", borderRadius:10, border:"none",
                  cursor: pulseRunning ? "wait" : "pointer",
                  background: pulseRunning
                    ? "rgba(255,255,255,0.08)"
                    : pulseConfirming
                      ? "linear-gradient(135deg,#EF4444,#DC2626)"
                      : "linear-gradient(135deg,#5BA0E5,#3B7FBF)",
                  color:"#fff", fontSize:13, fontWeight:800,
                  letterSpacing:"0.04em", textTransform:"uppercase",
                  transition:"background 0.18s",
                  boxShadow: pulseConfirming
                    ? "0 4px 16px rgba(239,68,68,0.35)"
                    : "0 4px 16px rgba(91,160,229,0.25)",
                }}
              >
                {pulseRunning
                  ? "Running… (this may take a minute)"
                  : pulseConfirming
                    ? "Tap again to confirm"
                    : "Run Pulse Feed Backfill"}
              </button>

              {pulseConfirming && !pulseRunning && (
                <div style={{ marginTop:8, fontSize:11, color:"rgba(255,255,255,0.5)", textAlign:"center", fontFamily:FM }}>
                  Auto-cancels in 4s
                </div>
              )}
            </div>

            {/* Pulse backfill result card */}
            {pulseResult && (
              <div style={{
                padding:"14px 16px", marginTop:8, borderRadius:12,
                background:"rgba(74,222,128,0.05)",
                border:"1px solid rgba(74,222,128,0.20)",
              }}>
                <div style={{ fontSize:12, fontWeight:800, color:"#4ade80", letterSpacing:"0.10em", fontFamily:FM, marginBottom:8 }}>
                  ✓ LAST RUN
                </div>
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr 1fr", gap:6 }}>
                  {[
                    { label:"Created",         val: pulseResult.created         ?? 0, color:"#4ade80" },
                    { label:"Already mirrored",val: pulseResult.alreadyMirrored ?? 0, color:"rgba(255,255,255,0.55)" },
                    { label:"Scanned",         val: pulseResult.scanned         ?? 0, color:"rgba(255,255,255,0.55)" },
                    { label:"Errors",          val: pulseResult.errors          ?? 0, color: (pulseResult.errors ?? 0) > 0 ? "#f87171" : "rgba(255,255,255,0.35)" },
                  ].map(({ label, val, color }) => (
                    <div key={label} style={{
                      padding:"8px 6px", borderRadius:8,
                      background:"rgba(255,255,255,0.02)",
                      border:"1px solid rgba(255,255,255,0.04)",
                      textAlign:"center",
                    }}>
                      <div style={{ fontFamily:FD, fontSize:17, fontWeight:800, color, lineHeight:1 }}>{val}</div>
                      <div style={{ fontSize:9, color:"rgba(255,255,255,0.35)", marginTop:4, letterSpacing:"0.06em", textTransform:"uppercase", fontWeight:700, fontFamily:FM }}>{label}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ── Purge Pick'em Mirrors -- one-time cleanup ── */}
            {/* Calls purgeNonWagerMirrorsCallable. Removes /publicPicks docs
                belonging to pickem/survivor leagues (created by the mirror
                trigger before it was mode-aware). Idempotent. */}
            <div style={{
              padding:"18px 20px", marginTop:12, borderRadius:14,
              background:"linear-gradient(180deg,rgba(248,113,113,0.06) 0%,rgba(248,113,113,0.02) 100%)",
              border:"1px solid rgba(248,113,113,0.20)",
            }}>
              <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:8 }}>
                <span style={{ fontSize:18 }}>🧹</span>
                <div style={{ fontSize:15, fontWeight:800, color:"#fff", fontFamily:FD }}>
                  Purge Pick'em Mirrors
                </div>
              </div>
              <div style={{ fontSize:12.5, color:"rgba(255,255,255,0.55)", lineHeight:1.55, marginBottom:14 }}>
                Deletes every <code style={{ background:"rgba(255,255,255,0.06)", padding:"1px 5px", borderRadius:4, fontSize:11.5 }}>/publicPicks</code> mirror doc that belongs to a pick'em or survivor league — these leaked into the Pulse feed via the server-side mirror trigger before it was mode-aware. Comments are deleted with them.
                <br/><br/>
                Idempotent — a second run finds nothing to delete. This permanently removes the mirror docs (source picks in <code style={{ background:"rgba(255,255,255,0.06)", padding:"1px 5px", borderRadius:4, fontSize:11.5 }}>userPicks</code> are untouched).
              </div>

              <button
                onClick={onPurgeMirrorsClick}
                disabled={purgeRunning}
                style={{
                  width:"100%", padding:"13px", borderRadius:10, border:"none",
                  cursor: purgeRunning ? "wait" : "pointer",
                  background: purgeRunning
                    ? "rgba(255,255,255,0.08)"
                    : purgeConfirming
                      ? "linear-gradient(135deg,#EF4444,#DC2626)"
                      : "linear-gradient(135deg,#F87171,#DC2626)",
                  color:"#fff", fontSize:13, fontWeight:800,
                  letterSpacing:"0.04em", textTransform:"uppercase",
                  transition:"background 0.18s",
                  boxShadow: purgeConfirming
                    ? "0 4px 16px rgba(239,68,68,0.35)"
                    : "0 4px 16px rgba(248,113,113,0.25)",
                }}
              >
                {purgeRunning
                  ? "Purging…"
                  : purgeConfirming
                    ? "Tap again to confirm delete"
                    : "Purge Pick'em Mirrors"}
              </button>

              {purgeConfirming && !purgeRunning && (
                <div style={{ marginTop:8, fontSize:11, color:"rgba(255,255,255,0.5)", textAlign:"center", fontFamily:FM }}>
                  Auto-cancels in 4s
                </div>
              )}
            </div>

            {/* Purge result card */}
            {purgeResult && (
              <div style={{
                padding:"14px 16px", marginTop:8, borderRadius:12,
                background:"rgba(74,222,128,0.05)",
                border:"1px solid rgba(74,222,128,0.20)",
              }}>
                <div style={{ fontSize:12, fontWeight:800, color:"#4ade80", letterSpacing:"0.10em", fontFamily:FM, marginBottom:8 }}>
                  ✓ LAST RUN
                </div>
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr 1fr", gap:6 }}>
                  {[
                    { label:"Mirrors deleted",  val: purgeResult.mirrorsDeleted  ?? 0, color:"#4ade80" },
                    { label:"Comments deleted", val: purgeResult.commentsDeleted ?? 0, color:"rgba(255,255,255,0.55)" },
                    { label:"Leagues matched",  val: purgeResult.leaguesMatched  ?? 0, color:"rgba(255,255,255,0.55)" },
                    { label:"Errors",           val: purgeResult.errors          ?? 0, color: (purgeResult.errors ?? 0) > 0 ? "#f87171" : "rgba(255,255,255,0.35)" },
                  ].map(({ label, val, color }) => (
                    <div key={label} style={{
                      padding:"8px 6px", borderRadius:8,
                      background:"rgba(255,255,255,0.02)",
                      border:"1px solid rgba(255,255,255,0.04)",
                      textAlign:"center",
                    }}>
                      <div style={{ fontFamily:FD, fontSize:17, fontWeight:800, color, lineHeight:1 }}>{val}</div>
                      <div style={{ fontSize:9, color:"rgba(255,255,255,0.35)", marginTop:4, letterSpacing:"0.06em", textTransform:"uppercase", fontWeight:700, fontFamily:FM }}>{label}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ── Purge Bonfire Data -- one-time removal cleanup ── */}
            {/* Calls purgeBonfireDataCallable. Deletes the leagues/bonfire
                subtree (members, activity, league doc). All Bonfire code was
                removed 2026-07-23; this clears the data it left behind. */}
            <div style={{
              padding:"18px 20px", marginTop:12, borderRadius:14,
              background:"linear-gradient(180deg,rgba(251,191,36,0.06) 0%,rgba(251,191,36,0.02) 100%)",
              border:"1px solid rgba(251,191,36,0.20)",
            }}>
              <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:8 }}>
                <span style={{ fontSize:18 }}>🔥</span>
                <div style={{ fontSize:15, fontWeight:800, color:"#fff", fontFamily:FD }}>
                  Purge Bonfire Data
                </div>
              </div>
              <div style={{ fontSize:12.5, color:"rgba(255,255,255,0.55)", lineHeight:1.55, marginBottom:14 }}>
                Deletes everything under <code style={{ background:"rgba(255,255,255,0.06)", padding:"1px 5px", borderRadius:4, fontSize:11.5 }}>leagues/bonfire</code> — all member docs, all activity events, then the league doc itself. The Bonfire code was fully removed on 2026-07-23; this clears the orphaned data.
                <br/><br/>
                Permanent. Idempotent — a second run finds nothing to delete. User picks, balances, and private leagues are untouched.
              </div>

              <button
                onClick={onPurgeBonfireClick}
                disabled={bfPurgeRunning}
                style={{
                  width:"100%", padding:"13px", borderRadius:10, border:"none",
                  cursor: bfPurgeRunning ? "wait" : "pointer",
                  background: bfPurgeRunning
                    ? "rgba(255,255,255,0.08)"
                    : bfPurgeConfirming
                      ? "linear-gradient(135deg,#EF4444,#DC2626)"
                      : "linear-gradient(135deg,#FBBF24,#D97706)",
                  color:"#fff", fontSize:13, fontWeight:800,
                  letterSpacing:"0.04em", textTransform:"uppercase",
                  transition:"background 0.18s",
                  boxShadow: bfPurgeConfirming
                    ? "0 4px 16px rgba(239,68,68,0.35)"
                    : "0 4px 16px rgba(251,191,36,0.25)",
                }}
              >
                {bfPurgeRunning
                  ? "Purging…"
                  : bfPurgeConfirming
                    ? "Tap again to confirm delete"
                    : "Purge Bonfire Data"}
              </button>

              {bfPurgeConfirming && !bfPurgeRunning && (
                <div style={{ marginTop:8, fontSize:11, color:"rgba(255,255,255,0.5)", textAlign:"center", fontFamily:FM }}>
                  Auto-cancels in 4s
                </div>
              )}

              {bfPurgeResult && (
                <div style={{ marginTop:10, padding:"10px 12px", borderRadius:10, background:"rgba(74,222,128,0.05)", border:"1px solid rgba(74,222,128,0.20)", fontSize:11.5, color:"rgba(255,255,255,0.6)", fontFamily:FM }}>
                  ✓ Members: {bfPurgeResult.membersDeleted ?? 0} · Activity: {bfPurgeResult.activityDeleted ?? 0} · League doc: {bfPurgeResult.leagueDocDeleted ? "deleted" : "already gone"}{(bfPurgeResult.errors ?? 0) > 0 ? ` · Errors: ${bfPurgeResult.errors}` : ""}
                </div>
              )}
            </div>

            {/* Repair Mirror Drift card removed 2026-07-23: it called
                repairPublicPicksCallable, which does not exist in deployed
                functions (audit round 1) — the button silently errored on
                every press. Backfill + purge + orphan sweep cover the mirror
                toolkit. */}
          </div>
        )}

        {/* ── REPORTS TAB ── */}
        {adminTab === "reports" && (
          <div>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:12 }}>
              <div style={{ fontSize:11, fontWeight:800, color:"rgba(255,255,255,0.36)", letterSpacing:"0.1em", fontFamily:FM }}>
                {reportsLoading ? "LOADING..." : `REPORTS (${reports.length})`}
              </div>
              <button onClick={loadReports} style={{ background:"none", border:"none", color:"#f87171", fontSize:11, fontWeight:600, cursor:"pointer" }}>Refresh</button>
            </div>
            {reports.length === 0 && !reportsLoading && (
              <div style={{ textAlign:"center", padding:40, color:"rgba(255,255,255,0.32)", fontSize:13 }}>No reports — all clear!</div>
            )}
            {reports.map(r => {
              const isUGC = r.contentType === "den_activity" || r.contentType === "block_user";
              const reportTime = r.createdAt?.toDate?.().toLocaleString() || (r.createdAt?.seconds ? new Date(r.createdAt.seconds * 1000).toLocaleString() : "—");
              return (
                <div key={r.id} style={{
                  padding:"14px 16px", marginBottom:8, borderRadius:12,
                  background: r.contentType === "block_user" ? "rgba(239,68,68,0.04)" : "rgba(255,255,255,0.02)",
                  border: r.contentType === "block_user" ? "1px solid rgba(239,68,68,0.20)" : "1px solid rgba(239,68,68,0.12)",
                  borderLeft: r.contentType === "block_user" ? "3px solid #EF4444" : "3px solid #FF9500",
                }}>
                  <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:8 }}>
                    <span style={{
                      fontSize:9, fontWeight:800, padding:"2px 8px", borderRadius:6, fontFamily:FM, letterSpacing:"0.06em",
                      background: r.contentType === "block_user" ? "rgba(239,68,68,0.12)" : isUGC ? "rgba(255,149,0,0.12)" : "rgba(251,191,36,0.12)",
                      color: r.contentType === "block_user" ? "#EF4444" : isUGC ? "#FF9500" : "#E5B459",
                      border: `1px solid ${r.contentType === "block_user" ? "rgba(239,68,68,0.20)" : isUGC ? "rgba(255,149,0,0.20)" : "rgba(251,191,36,0.20)"}`,
                    }}>
                      {r.contentType === "block_user" ? "🚫 USER BLOCKED" : isUGC ? "⚠ CONTENT FLAGGED" : "📋 PICK REPORT"}
                    </span>
                    <span style={{ fontSize:9, color:"rgba(255,255,255,0.36)", marginLeft:"auto" }}>{reportTime}</span>
                  </div>
                  {isUGC ? (
                    <>
                      <div style={{ fontSize:13, fontWeight:700, color:"#fff", marginBottom:4 }}>Reason: {r.reason || "Not specified"}</div>
                      <div style={{ fontSize:12, color:"rgba(255,255,255,0.5)", marginBottom:6 }}>
                        Reported user: <span style={{ color:"#FF9500", fontWeight:700 }}>{r.reportedUsername || "Unknown"}</span>
                      </div>
                      {r.contentText && (
                        <div style={{ padding:"8px 12px", borderRadius:8, marginBottom:8, background:"rgba(255,255,255,0.03)", border:"1px solid rgba(255,255,255,0.06)", fontSize:12, color:"rgba(255,255,255,0.6)", lineHeight:1.5, fontStyle:"italic" }}>
                          "{r.contentText}"
                        </div>
                      )}
                      <div style={{ fontSize:10, color:"rgba(255,255,255,0.40)", lineHeight:1.5 }}>
                        <div>Reporter: {r.reporterName || "Anonymous"} ({r.reporterUid?.slice(0,8)}...)</div>
                        <div>Reported UID: {r.reportedUserId?.slice(0,12)}...</div>
                        {r.leagueId && <div>League: {r.leagueId.slice(0,12)}...</div>}
                        <div>Status: <span style={{ color: r.status === "pending" ? "#FF9500" : "#4ADE80", fontWeight:700 }}>{r.status || "pending"}</span></div>
                      </div>
                    </>
                  ) : (
                    <>
                      <div style={{ fontSize:13, fontWeight:700, color:"#fff", lineHeight:1.3 }}>{r.label || "—"}</div>
                      <div style={{ fontSize:11, color:"rgba(255,255,255,0.4)", marginTop:3 }}>{r.username || "Unknown"} · {r.email || "—"}</div>
                      <div style={{ fontSize:10, color:"rgba(255,255,255,0.40)", marginTop:3, fontFamily:FM }}>
                        {r.sport} · {r.type} · Status: {r.status} · Wager: {r.wager} · Odds: {r.odds} · Pts: {r.points}
                        {r.actualStat != null && ` · Actual: ${r.actualStat}`}
                        {r.finalScore && ` · Score: ${r.finalScore.away}-${r.finalScore.home}`}
                      </div>
                      <div style={{ fontSize:9, color:"rgba(255,255,255,0.32)", marginTop:2, fontFamily:FM }}>Pick: {r.pickId} · User: {r.userId}</div>
                    </>
                  )}
                  <div style={{ display:"flex", gap:6, marginTop:10 }}>
                    {isUGC && r.reportedUserId && (
                      <button onClick={() => { setAdminTab("users"); setSearch(r.reportedUsername || ""); searchUsers(); }} style={{
                        padding:"6px 12px", borderRadius:6, border:"1px solid rgba(255,149,0,0.2)",
                        background:"rgba(255,149,0,0.08)", color:"#FF9500", fontSize:10, fontWeight:700, cursor:"pointer",
                      }}>Find User</button>
                    )}
                    {!isUGC && r.userId && (
                      <button onClick={() => { setAdminTab("users"); loadUserPicks(r.userId); }} style={{
                        padding:"6px 12px", borderRadius:6, border:"1px solid rgba(251,191,36,0.2)",
                        background:"rgba(251,191,36,0.08)", color:"#E5B459", fontSize:10, fontWeight:700, cursor:"pointer",
                      }}>View Picks</button>
                    )}
                    {!isUGC && r.pickId && r.userId && (
                      <button
                        disabled={resettingId === r.id}
                        onClick={() => resetReportedPick(r)}
                        style={{
                          padding:"6px 12px", borderRadius:6, border:"1px solid rgba(74,222,128,0.25)",
                          background:"rgba(74,222,128,0.10)", color:"#4ADE80",
                          fontSize:10, fontWeight:700, cursor:"pointer",
                          opacity: resettingId === r.id ? 0.5 : 1,
                        }}
                      >{resettingId === r.id ? "..." : "Reset Pick"}</button>
                    )}
                    <button onClick={() => deleteReport(r.id)} style={{
                      padding:"6px 12px", borderRadius:6, border:"1px solid rgba(255,255,255,0.06)",
                      background:"rgba(255,255,255,0.03)", color:"rgba(255,255,255,0.42)", fontSize:10, fontWeight:600, cursor:"pointer",
                      marginLeft:"auto",
                    }}>Dismiss</button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* ── TOURNAMENT TAB ── */}
        {adminTab === "tournament" && (
          <div>
            {/* ── Send push notification (admin broadcast) ── */}
            <div style={{
              marginBottom:18, padding:"14px 16px", borderRadius:14,
              background:"rgba(255,107,61,0.05)", border:"1px solid rgba(255,107,61,0.20)",
            }}>
              <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:4 }}>
                <div style={{ fontSize:13, fontWeight:800, color:"#fff" }}>Send Push Notification</div>
                <div style={{ fontSize:10, fontWeight:700, color:"#FF6B3D", fontFamily:FM, letterSpacing:"0.04em" }}>iOS + WEB</div>
              </div>
              <div style={{ fontSize:11.5, color:"rgba(255,255,255,0.5)", lineHeight:1.5, marginBottom:10 }}>
                Reaches every device that granted notification permission. Leave League ID blank to target all users, or paste a League ID to scope it. Run a Dry Run first to see the token count before sending for real.
              </div>

              <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                <input
                  value={pushTitle}
                  onChange={e => { setPushTitle(e.target.value); setPushConfirming(false); }}
                  placeholder="Notification title"
                  style={{ padding:"10px 12px", borderRadius:10, background:"rgba(0,0,0,0.3)", border:"1px solid rgba(255,255,255,0.1)", color:"#fff", fontSize:13, outline:"none" }}
                />
                <textarea
                  value={pushBody}
                  onChange={e => { setPushBody(e.target.value); setPushConfirming(false); }}
                  placeholder="Notification message"
                  rows={2}
                  style={{ padding:"10px 12px", borderRadius:10, background:"rgba(0,0,0,0.3)", border:"1px solid rgba(255,255,255,0.1)", color:"#fff", fontSize:13, outline:"none", resize:"vertical", fontFamily:"inherit" }}
                />
                <input
                  value={pushLeagueId}
                  onChange={e => { setPushLeagueId(e.target.value); setPushConfirming(false); }}
                  placeholder="League ID (blank = all users)"
                  style={{ padding:"10px 12px", borderRadius:10, background:"rgba(0,0,0,0.3)", border:"1px solid rgba(255,255,255,0.1)", color:"#fff", fontSize:13, outline:"none" }}
                />
                <input
                  value={pushUrl}
                  onChange={e => setPushUrl(e.target.value)}
                  placeholder="Tap-through URL (optional)"
                  style={{ padding:"10px 12px", borderRadius:10, background:"rgba(0,0,0,0.3)", border:"1px solid rgba(255,255,255,0.1)", color:"#fff", fontSize:13, outline:"none" }}
                />
              </div>

              <div style={{ display:"flex", gap:8, marginTop:10 }}>
                <button
                  onClick={() => sendPush(true)}
                  disabled={pushSending}
                  style={{ flex:1, padding:"10px 16px", borderRadius:10, border:"1px solid rgba(255,255,255,0.14)", cursor:"pointer", background:"rgba(255,255,255,0.06)", color:"#fff", fontSize:13, fontWeight:700, opacity:pushSending?0.5:1 }}
                >{pushSending ? "…" : "Dry Run"}</button>
                <button
                  onClick={() => sendPush(false)}
                  disabled={pushSending || !pushTitle.trim() || !pushBody.trim()}
                  style={{
                    flex:1, padding:"10px 16px", borderRadius:10, border:"none", whiteSpace:"nowrap",
                    cursor:(pushTitle.trim()&&pushBody.trim())?"pointer":"default",
                    background: pushConfirming ? "#FF6B3D" : (pushTitle.trim()&&pushBody.trim()) ? "rgba(255,107,61,0.18)" : "rgba(255,255,255,0.06)",
                    color:"#fff", fontSize:13, fontWeight:700, opacity:pushSending?0.5:1,
                  }}
                >{pushSending ? "Sending…" : pushConfirming ? "Confirm Send" : "Send for Real"}</button>
              </div>

              {pushResult && !pushResult.error && (
                <div style={{ fontSize:11.5, color:"rgba(255,255,255,0.7)", marginTop:10, lineHeight:1.5 }}>
                  {pushResult.dryRun ? "Dry run: " : "Sent: "}
                  <span style={{ color:"#FF8A5E", fontWeight:700 }}>{pushResult.tokens ?? 0}</span> tokens across{" "}
                  <span style={{ color:"#FF8A5E", fontWeight:700 }}>{pushResult.targeted ?? 0}</span> users.
                  {!pushResult.dryRun && <> Delivered <span style={{ color:"#4ade80", fontWeight:700 }}>{pushResult.sent ?? 0}</span>, failed {pushResult.failed ?? 0}, pruned {pushResult.pruned ?? 0}.</>}
                </div>
              )}
            </div>

            {/* ── World Cup: set Golden Boot winner (post-tournament) ── */}
            <div style={{
              marginBottom:18, padding:"14px 16px", borderRadius:14,
              background:"rgba(20,184,166,0.05)", border:"1px solid rgba(20,184,166,0.18)",
            }}>
              <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:4 }}>
                <div style={{ fontSize:13, fontWeight:800, color:"#fff" }}>World Cup · Golden Boot</div>
                <div style={{ fontSize:10, fontWeight:700, color:"#5eead4", fontFamily:FM, letterSpacing:"0.04em" }}>120 PTS</div>
              </div>
              <div style={{ fontSize:11.5, color:"rgba(255,255,255,0.5)", lineHeight:1.5, marginBottom:10 }}>
                Set the tournament top scorer after the Final. Matching picks score 120. Match is name-based and case-insensitive — enter the name as it appears in the pick list.
              </div>
              {wcBootCurrent && (
                <div style={{ fontSize:11.5, color:"rgba(255,255,255,0.65)", marginBottom:10 }}>
                  Currently set: <span style={{ color:"#5eead4", fontWeight:700 }}>{wcBootCurrent}</span>
                </div>
              )}
              <div style={{ display:"flex", gap:8 }}>
                <input
                  value={wcBootName}
                  onChange={e => { setWcBootName(e.target.value); setWcBootConfirming(false); }}
                  placeholder="e.g. Kylian Mbappé"
                  style={{
                    flex:1, padding:"10px 12px", borderRadius:10,
                    background:"rgba(0,0,0,0.3)", border:"1px solid rgba(255,255,255,0.1)",
                    color:"#fff", fontSize:13, outline:"none",
                  }}
                />
                <button
                  onClick={() => { if (wcBootConfirming) saveWcBoot(); else setWcBootConfirming(true); }}
                  disabled={!wcBootName.trim() || wcBootSaving}
                  style={{
                    padding:"10px 16px", borderRadius:10, border:"none", whiteSpace:"nowrap",
                    cursor: wcBootName.trim() ? "pointer" : "default",
                    background: wcBootSaved ? "rgba(34,197,94,0.2)"
                      : wcBootConfirming ? "linear-gradient(135deg,#14b8a6,#0d9488)"
                      : wcBootName.trim() ? "rgba(20,184,166,0.18)" : "rgba(255,255,255,0.06)",
                    color: wcBootSaved ? "#4ade80" : "#fff", fontSize:13, fontWeight:700,
                    opacity: wcBootSaving ? 0.5 : 1,
                  }}
                >{wcBootSaved ? "Saved ✓" : wcBootSaving ? "Saving…" : wcBootConfirming ? "Confirm" : "Set Boot"}</button>
              </div>
            </div>

            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:12 }}>
              <div style={{ fontSize:11, fontWeight:800, color:"rgba(255,255,255,0.36)", letterSpacing:"0.1em", fontFamily:FM }}>
                {tournamentLoading ? "LOADING..." : `PENDING TOURNAMENT PICKS (${tournamentPicks.length})`}
              </div>
              <button onClick={loadTournamentPicks} style={{ background:"none", border:"none", color:"#FCD34D", fontSize:11, fontWeight:600, cursor:"pointer" }}>Refresh</button>
            </div>

            {tournamentPicks.length === 0 && !tournamentLoading && (
              <div style={{ textAlign:"center", padding:40, color:"rgba(255,255,255,0.32)", fontSize:13 }}>No pending tournament picks</div>
            )}

            {tournamentPicks.length > 0 && (
              <>
                {/* Group by tournament */}
                {(() => {
                  const groups = {};
                  tournamentPicks.forEach(p => {
                    const key = p.homeTeamFull || p.gameLabel || p.sportKey || "Unknown Tournament";
                    if (!groups[key]) groups[key] = [];
                    groups[key].push(p);
                  });
                  return Object.entries(groups).map(([tournName, picks]) => {
                    // Sub-group by market (Win/Place/Show); legacy picks without `market` show under "Win"
                    const byMarket = { win: [], place: [], show: [] };
                    picks.forEach(p => {
                      const m = (p.market || "win").toLowerCase();
                      if (byMarket[m]) byMarket[m].push(p); else byMarket.win.push(p);
                    });
                    const stripPost = (n) => (n || "").replace(/^#\d+\s+/, "");
                    const userCt = [...new Set(picks.map(p => p.userId))].length;
                    return (
                      <div key={tournName} style={{
                        marginBottom:16, padding:"14px 16px", borderRadius:12,
                        background:"rgba(255,215,0,0.04)", border:"1px solid rgba(255,215,0,0.15)",
                      }}>
                        <div style={{ fontSize:14, fontWeight:700, color:"#FCD34D", marginBottom:4 }}>🏆 {tournName}</div>
                        <div style={{ fontSize:11, color:"rgba(255,255,255,0.35)", marginBottom:10 }}>
                          {picks.length} pick{picks.length !== 1 ? "s" : ""} from {userCt} user{userCt !== 1 ? "s" : ""}
                        </div>
                        {["win","place","show"].map(mk => {
                          const mkPicks = byMarket[mk];
                          if (!mkPicks?.length) return null;
                          const horseCounts = {};
                          mkPicks.forEach(p => {
                            const name = stripPost(p.side || p.label?.replace(/\s+to\s+(Win|Place|Show)$/i, "") || "—");
                            horseCounts[name] = (horseCounts[name] || 0) + 1;
                          });
                          const sorted = Object.entries(horseCounts).sort((a,b) => b[1] - a[1]);
                          const mkLabel = mk === "win" ? "WIN" : mk === "place" ? "PLACE" : "SHOW";
                          return (
                            <div key={mk} style={{ marginBottom:8 }}>
                              <div style={{ fontSize:10, fontWeight:800, color:"#FCD34D", letterSpacing:"0.08em", fontFamily:FM, marginBottom:4 }}>
                                {mkLabel} ({mkPicks.length})
                              </div>
                              <div style={{ fontSize:10, color:"rgba(255,255,255,0.42)", lineHeight:1.6 }}>
                                {sorted.map(([n,c]) => `${n} (${c})`).join(", ")}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    );
                  });
                })()}

                {/* ── Settlement input ── */}
                {(() => {
                  // Show 2nd/3rd inputs only if there are picks that need them
                  const showSecond = tournamentPicks.some(p => p.market === "place" || p.market === "show");
                  const showThird  = tournamentPicks.some(p => p.market === "show");
                  const inputStyle = {
                    flex:1, padding:"10px 14px", borderRadius:10,
                    border:"1px solid rgba(255,255,255,0.10)",
                    background:"rgba(255,255,255,0.04)", color:"#fff",
                    fontSize:13, outline:"none", minWidth: 0,
                  };
                  return (
                    <div style={{
                      padding:"16px", borderRadius:14,
                      background:"rgba(255,215,0,0.06)", border:"1px solid rgba(255,215,0,0.20)",
                    }}>
                      <div style={{ fontSize:13, fontWeight:700, color:"#FCD34D", marginBottom:8 }}>
                        {showSecond ? "Enter Top 3 Finishers" : "Enter Tournament Winner"}
                      </div>
                      <div style={{ fontSize:11, color:"rgba(255,255,255,0.35)", marginBottom:12, lineHeight:1.5 }}>
                        {showSecond
                          ? "Win picks settle on 1st only. Place picks settle on 1st OR 2nd. Show picks settle on 1st, 2nd, OR 3rd. Settles across ALL users. Cannot be undone."
                          : "All picks matching this name will be settled as WON. All others as LOST. This settles across ALL users. Cannot be undone."}
                      </div>
                      <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                        <div style={{ display:"flex", gap:8, alignItems:"center" }}>
                          <span style={{ width:38, fontSize:11, fontWeight:800, color:"#FCD34D", fontFamily:FM }}>1st</span>
                          <input
                            value={tournamentWinner}
                            onChange={e => setTournamentWinner(e.target.value)}
                            placeholder={showSecond ? "e.g. Renegade" : "e.g. Rory McIlroy"}
                            style={inputStyle}
                          />
                        </div>
                        {showSecond && (
                          <div style={{ display:"flex", gap:8, alignItems:"center" }}>
                            <span style={{ width:38, fontSize:11, fontWeight:800, color:"rgba(252,211,77,0.65)", fontFamily:FM }}>2nd</span>
                            <input
                              value={tournamentSecond}
                              onChange={e => setTournamentSecond(e.target.value)}
                              placeholder="e.g. Commandment"
                              style={inputStyle}
                            />
                          </div>
                        )}
                        {showThird && (
                          <div style={{ display:"flex", gap:8, alignItems:"center" }}>
                            <span style={{ width:38, fontSize:11, fontWeight:800, color:"rgba(252,211,77,0.45)", fontFamily:FM }}>3rd</span>
                            <input
                              value={tournamentThird}
                              onChange={e => setTournamentThird(e.target.value)}
                              placeholder="e.g. The Puma"
                              style={inputStyle}
                            />
                          </div>
                        )}
                        <button
                          onClick={() => settleTournament(tournamentWinner, tournamentSecond, tournamentThird)}
                          disabled={!tournamentWinner.trim() || tournamentSettling}
                          style={{
                            marginTop:4, padding:"12px 20px", borderRadius:10, border:"none", cursor:"pointer",
                            background: tournamentWinner.trim() ? "linear-gradient(135deg, #D9A441, #D97706)" : "rgba(255,255,255,0.06)",
                            color:"#fff", fontSize:13, fontWeight:700,
                            opacity: tournamentSettling ? 0.5 : 1,
                          }}
                        >{tournamentSettling ? "Settling…" : "Settle All"}</button>
                      </div>
                    </div>
                  );
                })()}
              </>
            )}
          </div>
        )}

        {/* ── USERS TAB ── */}
        {adminTab === "users" && !selectedUser && (
          <div>
            <div style={{ fontSize:11, fontWeight:800, color:"rgba(255,255,255,0.36)", letterSpacing:"0.1em", marginBottom:8, fontFamily:FM }}>
              {loading ? "LOADING..." : `USERS (${users.length})`}
            </div>
            {users.map(u => (
              <button key={u.id} onClick={() => loadUserPicks(u.id)} style={{
                width:"100%", padding:"12px 14px", marginBottom:6, borderRadius:12,
                background:"rgba(255,255,255,0.03)", border:"1px solid rgba(255,255,255,0.06)",
                cursor:"pointer", textAlign:"left", display:"flex", alignItems:"center", justifyContent:"space-between",
              }}>
                <div>
                  <div style={{ fontSize:14, fontWeight:700, color:"#fff" }}>{u.username || u.displayName || "No name"}</div>
                  <div style={{ fontSize:11, color:"rgba(255,255,255,0.42)", marginTop:2 }}>{u.email || "no email"}</div>
                  <div style={{ fontSize:9, color:"rgba(255,255,255,0.32)", marginTop:1, fontFamily:FM }}>{u.id}</div>
                </div>
                <div style={{ display:"flex", flexDirection:"column", alignItems:"flex-end", gap:2 }}>
                  <div style={{ fontSize:11, color:"rgba(255,255,255,0.36)", fontFamily:FM }}>{u.balance ?? "—"} pts</div>
                  <div style={{ fontSize:9, color:"rgba(255,255,255,0.32)" }}>Tap to load picks →</div>
                </div>
              </button>
            ))}
            {!loading && users.length === 0 && (
              <div style={{ textAlign:"center", padding:40, color:"rgba(255,255,255,0.32)", fontSize:13 }}>No users found</div>
            )}
          </div>
        )}

        {/* Picks list */}
        {adminTab === "users" && selectedUser && (
          <div>
            {selectedUser && selectedUser !== "search" && (
              <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:12 }}>
                <button onClick={() => { setSelectedUser(null); setPicks([]); }} style={{ background:"none", border:"none", color:"#f87171", fontSize:12, fontWeight:600, cursor:"pointer", padding:0 }}>← Back to users</button>
                <span style={{ fontSize:10, color:"rgba(255,255,255,0.32)", fontFamily:FM }}>{picks.length} picks · {selectedUser.slice(0,12)}...</span>
              </div>
            )}

            {/* Status filters */}
            <div style={{ display:"flex", gap:6, marginBottom:12, flexWrap:"wrap" }}>
              {["all","pending","won","lost","voided","push"].map(s => (
                <button key={s} onClick={() => setStatusFilter(s)} style={{
                  padding:"5px 12px", borderRadius:16, border:"none", cursor:"pointer", fontSize:11, fontWeight:600,
                  background: statusFilter === s ? "rgba(239,68,68,0.15)" : "rgba(255,255,255,0.04)",
                  color: statusFilter === s ? "#f87171" : "rgba(255,255,255,0.35)",
                }}>{s === "all" ? `All (${picks.length})` : `${s} (${picks.filter(p => p.status === s).length})`}</button>
              ))}
            </div>

            {pickLoading && <div style={{ textAlign:"center", padding:20, color:"rgba(255,255,255,0.42)" }}>Pulling picks…</div>}

            {/* Pick cards */}
            {filtered.map(pick => (
              <div key={pick.id} style={{
                padding:"12px 14px", marginBottom:6, borderRadius:12,
                background:"rgba(255,255,255,0.02)", border:`1px solid rgba(255,255,255,0.06)`,
                borderLeft:`3px solid ${statusColors[pick.status] || "#666"}`,
              }}>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", gap:8 }}>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontSize:13, fontWeight:700, color:"#fff", lineHeight:1.3 }}>{pick.label || "—"}</div>
                    <div style={{ fontSize:10, color:"rgba(255,255,255,0.40)", marginTop:3, fontFamily:FM }}>
                      {pick.sport} · {pick.type} · {pick.gameLabel || "—"}
                    </div>
                    <div style={{ fontSize:10, color:"rgba(255,255,255,0.36)", marginTop:2 }}>
                      Wager: {pick.wager} · Odds: {pick.odds} · Pts: {pick.points ?? "—"}
                      {pick.actualStat != null && ` · Actual: ${pick.actualStat}`}
                      {pick.finalScore && ` · Final: ${pick.finalScore.away}-${pick.finalScore.home}`}
                    </div>
                    {pick._userId && <div style={{ fontSize:9, color:"rgba(255,255,255,0.32)", marginTop:2, fontFamily:FM }}>uid: {pick._userId}</div>}
                    <div style={{ fontSize:9, color:"rgba(255,255,255,0.32)", marginTop:1, fontFamily:FM }}>id: {pick.id}</div>
                  </div>
                  <div style={{ display:"flex", flexDirection:"column", alignItems:"flex-end", gap:6, flexShrink:0 }}>
                    <span style={{ fontSize:10, fontWeight:800, color:statusColors[pick.status], padding:"2px 8px", borderRadius:6, background:`${statusColors[pick.status]}18`, letterSpacing:"0.06em" }}>
                      {(pick.status || "?").toUpperCase()}
                    </span>
                    {pick.status !== "pending" && (
                      <button
                        disabled={resettingId === pick.id}
                        onClick={() => resetPick(pick)}
                        style={{
                          padding:"4px 10px", borderRadius:6, border:"1px solid rgba(251,191,36,0.2)",
                          background:"rgba(251,191,36,0.08)", color:"#E5B459",
                          fontSize:10, fontWeight:700, cursor:"pointer",
                          opacity: resettingId === pick.id ? 0.5 : 1,
                        }}
                      >{resettingId === pick.id ? "..." : "Reset → Pending"}</button>
                    )}
                  </div>
                </div>
              </div>
            ))}

            {!pickLoading && filtered.length === 0 && picks.length > 0 && (
              <div style={{ textAlign:"center", padding:20, color:"rgba(255,255,255,0.36)", fontSize:12 }}>No picks match "{statusFilter}" filter</div>
            )}
            {!pickLoading && picks.length === 0 && selectedUser && (
              <div style={{ textAlign:"center", padding:20, color:"rgba(255,255,255,0.36)", fontSize:12 }}>No picks found</div>
            )}
          </div>
        )}

      </div>
    </div>,
    document.body
  );
}

function ProfilePage({ onSignOut, onDeleteAccount, setActivePage, user, userStats, refreshStats, balance, userLeagues = [] }) {
  // FIX #8: accept userLeagues from root instead of fetching independently
  const [editMode, setEditMode]     = useState(false);
  const [username, setUsername]     = useState("");
  const [saving, setSaving]         = useState(false);
  const [avatarUrl, setAvatarUrl]   = useState(user?.photoURL || null);
  const [showFaq, setShowFaq]       = useState(false);
  const [showAdmin, setShowAdmin]   = useState(false);
  const myLeagues = userLeagues.filter(l => !l.isEnded && l.status !== "ended"); // filter ended



  const displayName = userStats?.username || user?.displayName || user?.email?.split("@")[0] || "You";
  useEffect(() => { if (!editMode) setUsername(displayName); }, [displayName, editMode]);
  useEffect(() => { setAvatarUrl(user?.photoURL || null); }, [user?.photoURL]);

  const handleSave = async () => {
    if (!username.trim()) return;
    setSaving(true);
    try {
      await updateProfile(user, { displayName: username.trim() });
      await updateUserStats(user.uid, username.trim());
      await refreshStats();
      setEditMode(false);
      window.torchToast?.("Profile saved", "success");
    } catch { window.torchToast?.("Save failed — try again", "error"); }
    finally { setSaving(false); }
  };

  const wins       = userStats?.wins       ?? 0;
  const losses     = userStats?.losses     ?? 0;
  const streak     = userStats?.streak     ?? 0;
  const totalPicks = userStats?.totalPicks ?? 0;
  const winRate    = wins + losses > 0 ? Math.round(wins / (wins + losses) * 100) : 0;
  const achievements = getAchievements({ wins, losses, streak, bestStreak: userStats?.bestStreak??0, totalPicks, balance: balance ?? 0 });

  // Canonical rank — single source of truth (Rookie/Pro/Veteran/All-Star/Legend).
  // Previously this page had its own getTrophyTier helper with Guru/Sharp/Steady/
  // Climbing labels, which drifted out of sync with the rest of the app.
  const rank = getUserRank(userStats);

  return (
    <div style={{ maxWidth:640, margin:"0 auto", paddingBottom:"calc(90px + env(safe-area-inset-bottom, 0px))", background:T.bg, minHeight:"100vh" }}>

      {/* -- HERO -- compact, calm, no ambient effects -- */}
      <div style={{
        position:"relative",
        padding:"22px 16px 18px",
        background: T.bg,
        borderBottom:`1px solid ${T.border}`,
      }}>
        <div style={{ display:"flex", alignItems:"flex-start", gap:14, position:"relative", zIndex:1 }}>
          {/* Avatar — 64px (was 84) */}
          <div style={{ position:"relative", flexShrink:0 }}>
            <div style={{
              width:64, height:64, borderRadius:18, overflow:"hidden",
              background: avatarUrl ? "transparent" : "rgba(255,107,61,0.12)",
              display:"flex", alignItems:"center", justifyContent:"center",
              fontSize:24, fontWeight:700, color: avatarUrl ? "#fff" : "#FF8A65", fontFamily:FD,
              border: avatarUrl ? `1px solid ${T.borderMid}` : "1px solid rgba(255,107,61,0.22)",
              letterSpacing:"-0.02em",
            }}>
              {avatarUrl ? <img src={avatarUrl} alt="avatar" style={{ width:"100%", height:"100%", objectFit:"cover" }}
                onError={(e) => { e.currentTarget.style.display = "none"; }}/> : (displayName?.[0]?.toUpperCase()||"?")}
            </div>
          </div>

          {/* Name + balance */}
          <div style={{ flex:1, minWidth:0, paddingTop:2 }}>
            {editMode ? (
              <div style={{ display:"flex", gap:6, alignItems:"center" }}>
                <input value={username} onChange={e=>setUsername(e.target.value)}
                  style={{ flex:1, padding:"6px 10px", background:"rgba(255,255,255,0.06)", border:`1px solid ${T.borderMid}`,
                    borderRadius:8, color:T.ink, fontSize:14, fontWeight:700, outline:"none", fontFamily:FB }}/>
                <button onClick={handleSave} disabled={saving} style={{ padding:"6px 12px", background:T.coral, border:"none", borderRadius:8, color:"#fff", fontSize:12, fontWeight:700, cursor:"pointer" }}>
                  {saving ? "…" : "Save"}
                </button>
                <button onClick={() => setEditMode(false)} style={{ padding:"6px", background:"transparent", border:"none", color:T.inkMuted, fontSize:12, cursor:"pointer" }} aria-label="Close">✕</button>
              </div>
            ) : (
              <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:2 }}>
                <span style={{ fontSize:16, fontWeight:700, color:T.ink, letterSpacing:"-0.01em" }}>{displayName}</span>
                <button onClick={() => setEditMode(true)} style={{ background:"rgba(255,255,255,0.05)", border:"none", borderRadius:5, padding:"2px 6px", color:T.inkMuted, fontSize:10, cursor:"pointer" }}>Edit</button>
              </div>
            )}
            <div style={{ fontSize:11, color:T.inkMuted, marginBottom:10 }}>{user?.email}</div>

            {/* ── Stat strip — divider-separated, matches the Den hero ── */}
            <div style={{ display:"flex", gap:14, alignItems:"stretch", marginTop:4 }}>
              <div>
                <div style={{ fontSize:18, fontWeight:700, color:rank.color, letterSpacing:"-0.02em", lineHeight:1.05, display:"flex", alignItems:"center", gap:4 }}>
                  <RankInsignia rankId={rank.id} color={rank.color} size={13} />
                  <span>{rank.name}</span>
                </div>
                <div style={statLabel}>Rank</div>
              </div>
              <div style={statDivider} />
              <div>
                <div style={{ ...statNum(winRate>=55?T.green:winRate>=45?"#fff":T.coral), fontFamily:FM }}>{winRate}%</div>
                <div style={statLabel}>Win rate</div>
              </div>
              <div style={statDivider} />
              <div>
                <div style={{ ...statNum(streak>0?T.gold:"#fff"), fontFamily:FM }}>{streak > 0 ? `${streak}W` : `${wins}–${losses}`}</div>
                <div style={statLabel}>{streak>0?"Streak":"Record"}</div>
              </div>
              <div style={statDivider} />
              <div>
                <div style={{ ...statNum((balance ?? 0) >= 0 ? "#fff" : T.red), fontFamily:FM }}>{(balance ?? 0).toLocaleString()}</div>
                <div style={statLabel}>Balance</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div style={{ padding:"16px 16px 0" }}>

        {/* -- HERO for new users (0 picks) -- */}
        {(userStats?.totalPicks ?? 0) === 0 && (
          <div style={{
            marginBottom:16, padding:"18px 16px",
            background: T.card,
            border:`1px solid ${T.border}`,
            borderRadius:14, textAlign:"center",
          }}>
            <div>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={T.coral} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom:8 }}>
                <path d="M12 2C12 2 5 9 5 14a7 7 0 0014 0c0-5-7-12-7-12z"/>
              </svg>
              <div style={{ fontFamily:FD, fontSize:15, fontWeight:700, color:T.ink, marginBottom:4, letterSpacing:"-0.01em" }}>
                Welcome to Torch
              </div>
              <div style={{ fontSize:11.5, color:T.inkSec, lineHeight:1.5, marginBottom:14 }}>
                Pick real games for virtual points. Compete in leagues. No real money.
              </div>
              <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
                <button onClick={() => { haptic.light(); setActivePage("Games"); }} style={{
                  padding:"10px", borderRadius:10,
                  background:"rgba(255,255,255,0.05)", border:`1px solid ${T.border}`,
                  color:T.ink, fontSize:12, fontWeight:700, cursor:"pointer", width:"100%",
                }}>Make Your First Pick</button>
                <button onClick={() => { haptic.light(); setActivePage("Den"); }} style={{
                  padding:"10px", borderRadius:10, border:"none", cursor:"pointer", width:"100%",
                  background: T.coral,
                  color:"#fff", fontSize:12, fontWeight:700,
                }}>Join or Create a League</button>
              </div>
            </div>
          </div>
        )}

        {/* Quick-action redirects (Make a Pick / My Picks / Leagues) were
            removed in cut #6 — the bottom nav handles every one of those
            destinations in one tap. Profile's job is identity, stats,
            achievements, and settings; not a redirector. */}

        {/* -- PRACTICE MODE NUDGE (only for 0-league users) -- */}
        {myLeagues.length === 0 && (
          <div style={{
            marginBottom:14, borderRadius:12, overflow:"hidden",
            background: T.card,
            border:`1px solid ${T.border}`,
            padding:"14px 14px",
          }}>
            <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:10 }}>
              <div style={{ width:30, height:30, borderRadius:9, display:"flex", alignItems:"center", justifyContent:"center", background:"rgba(255,107,61,0.10)", flexShrink:0 }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={T.coral} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 2C12 2 5 9 5 14a7 7 0 0014 0c0-5-7-12-7-12z"/>
                </svg>
              </div>
              <div>
                <div style={{ fontSize:13, fontWeight:700, color:T.ink, marginBottom:2 }}>
                  You're in Practice mode
                </div>
                <div style={{ fontSize:11, color:T.inkSec, lineHeight:1.4 }}>
                  Join a league to play with friends.
                </div>
              </div>
            </div>
            <div style={{ display:"flex", gap:6 }}>
              <button onClick={() => { haptic.light(); setActivePage("Den"); }} style={{
                flex:1, padding:"8px", background:T.coral, border:"none",
                borderRadius:8, color:"#fff", fontSize:11.5, fontWeight:700, cursor:"pointer",
              }}>Join a League</button>
              <button onClick={() => { haptic.light(); setActivePage("Den"); }} style={{
                flex:1, padding:"8px", background:"rgba(255,255,255,0.04)", border:`1px solid ${T.border}`,
                borderRadius:8, color:T.ink, fontSize:11.5, fontWeight:700, cursor:"pointer",
              }}>+ Create One</button>
            </div>
          </div>
        )}

        {/* My-leagues preview list was removed in cut #6. The Leagues tab
            (leftmost in the bottom nav, the app's landing page) shows the
            full list with the same rank/balance/member-count data. */}

        {/* -- ACHIEVEMENTS -- */}
        {achievements.length > 0 && (
          <>
            <div style={{ fontSize:9, fontWeight:700, color:"rgba(255,255,255,0.36)", letterSpacing:"0.16em", fontFamily:FM, marginTop:16, marginBottom:10 }}>ACHIEVEMENTS</div>
            <div style={{ display:"flex", flexWrap:"wrap", gap:8, marginBottom:16 }}>
              {achievements.map((a, i) => (
                <div key={i} style={{ padding:"8px 12px", background:"rgba(255,255,255,0.03)", border:"1px solid rgba(255,255,255,0.06)", borderRadius:10 }}>
                  <div style={{ fontSize:12, fontWeight:700, color:"#fff" }}>{a.label}</div>
                  <div style={{ fontSize:10, color:"rgba(255,255,255,0.4)", marginTop:1 }}>{a.sub}</div>
                </div>
              ))}
            </div>
          </>
        )}

        {/* -- HOW TORCH WORKS & HELP -- */}
        <div style={{ display:"flex", gap:8, marginTop:12, marginBottom:8 }}>
          <button onClick={() => setShowFaq(true)} style={{
            flex:1, padding:"12px", background:"rgba(255,255,255,0.05)",
            border:`1px solid ${T.border}`, borderRadius:12,
            color:T.ink, fontSize:13, fontWeight:600, cursor:"pointer",
            display:"flex", alignItems:"center", justifyContent:"center", gap:8,
          }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/>
              <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/>
            </svg>
            How Torch Works
          </button>
        </div>

        {/* -- ADMIN PANEL (only for admin accounts) -- */}
        {isAdmin(user) && (
          <button onClick={() => setShowAdmin(true)} style={{
            width:"100%", padding:"12px", marginBottom:8,
            background:"rgba(239,68,68,0.06)", border:"1px solid rgba(239,68,68,0.15)",
            borderRadius:12, color:"#f87171", fontSize:13, fontWeight:600,
            cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", gap:8,
          }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="3"/>
              <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
            </svg>
            Admin Panel
          </button>
        )}

        {/* -- SIGN OUT -- */}
        <button onClick={onSignOut} style={{
          marginTop:0, width:"100%", padding:"12px", background:"transparent",
          border:`1px solid ${T.border}`, borderRadius:12,
          color:T.inkMuted, fontSize:13, fontWeight:600, cursor:"pointer",
        }}>Sign Out</button>

        {/* -- DELETE ACCOUNT -- visually demoted (smaller, lower contrast, more spacing
            from Sign Out) to prevent fat-finger taps. The only destructive action that
            matters here, so it shouldn't sit flush with Sign Out. */}
        {onDeleteAccount && (
          <button onClick={onDeleteAccount} style={{
            marginTop:24, width:"100%", padding:"10px", background:"transparent",
            border:"none", color:"rgba(255,255,255,0.36)", fontSize:11, fontWeight:500,
            cursor:"pointer", letterSpacing:"0.01em",
          }}>Delete Account</button>
        )}

        {/* -- LEGAL & SUPPORT -- */}
        <div style={{ display:"flex", justifyContent:"center", gap:16, marginTop:16, flexWrap:"wrap" }}>
          <a href="/privacy" target="_blank" rel="noopener noreferrer" style={{ fontSize:11, color:"rgba(255,255,255,0.40)", textDecoration:"none", fontWeight:600 }}>Privacy Policy</a>
          <a href="/terms" target="_blank" rel="noopener noreferrer" style={{ fontSize:11, color:"rgba(255,255,255,0.40)", textDecoration:"none", fontWeight:600 }}>Terms of Service</a>
          <a href="mailto:support@torchpicks.com" style={{ fontSize:11, color:"rgba(255,255,255,0.40)", textDecoration:"none", fontWeight:600 }}>Support</a>
        </div>
        <div style={{ textAlign:"center", marginTop:8, fontSize:10, color:"rgba(255,255,255,0.32)" }}>
          Torch v1.0 · For entertainment only · No real money is wagered or awarded
        </div>

        {/* FAQ Modal */}
        {showFaq && <HowTorchWorksModal onClose={() => setShowFaq(false)} />}
        {showAdmin && isAdmin(user) && <AdminPanel user={user} onClose={() => setShowAdmin(false)} />}
      </div>
    </div>
  );
}

/* ===========================================
   SHARED AUTH / FORM STYLES
=========================================== */
const inputStyle = {
  width:"100%", padding:"13px 14px",
  background:T.bgWarm, border:`1px solid ${T.border}`,
  borderRadius:10, color:T.ink, fontSize:16,
  outline:"none", fontFamily:FB,
  WebkitAppearance:"none",
};

const primaryBtn = {
  width:"100%", padding:"15px",
  background: T.coral,
  border:"none", borderRadius:14,
  color:"#fff", fontSize:15, fontWeight:700,
  cursor:"pointer", marginTop:14,
  fontFamily:FB,
  transition:"transform 0.12s, opacity 0.15s, background 0.15s",
  letterSpacing:"-0.005em",
};

/* ===========================================
   PICK SLIP DRAWER
=========================================== */
function PickSlipDrawer({ picks, setPicks, onClose, user, games, balance, onBalanceChange, userLeagues: rawUserLeagues = [], onLeagueBalanceChange, refreshLeagues, onAfterSubmit, userStats, torchDraft = null, onTorchComplete }) {
  // ── EXIT ANIMATION ──────────────────────────────────────────────────────
  // The sheet used to close by instant unmount (parent setSlipOpen(false)):
  // entry was animated, exit was a single-frame teardown of a full-viewport
  // blur(14px) backdrop — reads as a flash, worst in the iOS WKWebView.
  // requestClose() plays a short CSS exit (transform/opacity only, GPU-safe
  // on WKWebView) and only then calls the real onClose. All close triggers
  // (backdrop tap, X, escape event, empty-state CTA, post-submit timeout)
  // route through it. closingRef is a synchronous double-invoke guard.
  //
  // pendingClearRef: post-submit paths must NOT setPicks({}) while the sheet
  // is still visible — items.length===0 instantly swaps the body to the
  // "Slip's empty" state under the success checkmark (the wrong-content
  // flash) and zeroes the done screen's numbers. Instead they set this flag
  // and the picks are cleared in the same commit that unmounts the sheet.
  const [closing, setClosing] = useState(false);
  const closingRef = useRef(false);
  const pendingClearRef = useRef(false);
  const requestClose = React.useCallback((after) => {
    if (closingRef.current) return;
    closingRef.current = true;
    setClosing(true);
    setTimeout(() => {
      if (pendingClearRef.current) { pendingClearRef.current = false; setPicks({}); }
      onClose?.();
      after?.();
    }, 220);
  }, [onClose, setPicks]);
  // Pass !closing so the bottom nav starts sliding back in DURING the exit
  // (its own 0.22s transition), matching native sheet-dismiss timing instead
  // of popping in after unmount.
  useModalOpen(!closing);  // Hide bottom nav while this modal is open
  // Store games in a ref — the slip only uses them during submission (gamesMap), not for rendering.
  // This prevents live score poll cycles from re-rendering the entire slip every 30s.
  const gamesRef = useRef(games);
  useEffect(() => { gamesRef.current = games; }, [games]);
  // Filter out ended leagues — can't submit picks to them
  const userLeagues = rawUserLeagues.filter(l => !l.isEnded && l.status !== "ended");

  // Sport-lock: filter leagues by sport compatibility
  const slipSportKeys = useMemo(() => {
    const sports = new Set();
    Object.values(picks).forEach(p => { if (p.sportKey) sports.add(p.sportKey); });
    return sports;
  }, [picks]);
  const compatibleLeagues = useMemo(() => {
    if (slipSportKeys.size === 0) return userLeagues;
    return userLeagues.filter(l => {
      const mode = l.scoringMode || "wager";
      const isNonWagerLeague = mode === "pickem" || mode === "survivor";
      if (isNonWagerLeague) {
        for (const sk of slipSportKeys) {
          if (!String(sk).startsWith("americanfootball_nfl")) return false;
        }
        // PICKEM-MAKE-PICKS: pick'em takes full-game moneylines only (the CF
        // rejects anything else). A spread/total/prop/1H pick on the slip --
        // e.g. added from a game detail page reached outside the pick'em board
        // -- must not offer the pick'em league, or the user only learns at submit.
        if (Object.values(picks).some(p => (p.type || "") !== "ml")) return false;
      }
      const keys = (Array.isArray(l.allowedSports) && l.allowedSports.length > 0)
        ? l.allowedSports
        : (l.sportFilter || l.eventSport)
          ? [l.sportFilter || l.eventSport]
          : [];
      if (keys.length === 0) return true;
      for (const sk of slipSportKeys) {
        const ok = keys.some(k => sk === k || sk.includes(k) || k.includes(sk));
        if (!ok) return false;
      }
      return true;
    });
  }, [userLeagues, slipSportKeys, picks]);
  const items = Object.entries(picks);
  // Spread/ML labels are stored as bare numbers ("+8.5", "+150") — prepend the
  // picked team so the slip doesn't read as the home team's pick by default.
  // Mirrors the parlay-history pattern at ~line 11712.
  const slipDisplay = (p) => {
    const pickedTeamFull = p.side === "away" ? p.awayTeamFull
                         : p.side === "home" ? p.homeTeamFull
                         : null;
    const teamShort = pickedTeamFull ? pickedTeamFull.split(" ").pop() : "";
    // Skip prefix for picks where label is already self-explanatory or sides aren't team-based
    const skipTeamPrefix = p.type === "total" || p.type === "alt_total"
                        || p.type === "player_prop" || p.type === "team_total"
                        || p.type === "outright";
    // ML labels are stored self-describing ("Kansas City Chiefs ML") as of the
    // pick-label fix, so prefixing them would render "Chiefs Kansas City Chiefs
    // ML". Detect rather than hardcode the type: picks submitted before that fix
    // carry a bare price ("-625") and still need the prefix. Same rule as the
    // detail modal's expandedLabel.
    const labelNamesTeam = !!teamShort && (p.label || "").toLowerCase().includes(teamShort.toLowerCase());
    const displayLabel = skipTeamPrefix || labelNamesTeam || !teamShort
                       ? p.label
                       : `${teamShort} ${p.label}`;
    return { pickedTeamFull, displayLabel };
  };
  // FIX #10: seed wagers from the league balance if a league is pre-selected,
  // not always from global balance (which can be much larger, confusing the user
  // with immediately over-budget wagers when they have a small league balance).
  const [wagers, setWagers]       = useState(() => {
    // Torch flow: seed from the lender's intended stake. lender_funded mode means
    // the lender funds the entire wager solo, so divide their stake across items.
    if (torchDraft) {
      const combined = (torchDraft.amount || 0);
      const torchWager = items.length > 0 ? Math.floor(combined / items.length) : combined;
      return Object.fromEntries(items.map(([k]) => [k, torchWager]));
    }
    // Wager intentionally starts EMPTY — we don't pre-fill a suggested amount
    // (pre-seeding the minimum or the full balance reads as a recommendation
    // and confused users). The input shows a "How much?" placeholder and the
    // submit path validates against minWager. Torch-funded flows still seed
    // from the lender's stake above; everything else starts blank.
    return Object.fromEntries(items.map(([k]) => [k, 0]));
  });
  const [step, setStep]           = useState("slip");

  // Escape key closes slip
  useEffect(() => {
    const onEsc = () => { requestClose(); };
    window.addEventListener("torch-escape", onEsc);
    return () => window.removeEventListener("torch-escape", onEsc);
  }, [requestClose]);   // slip | confirm | done
  const [submitting, setSubmitting] = useState(false);
  const [succeeded, setSucceeded] = useState(false);  // brief 700ms checkmark state before modal dismisses
  const submittingRef = useRef(false); // synchronous guard — useState has async gap where two rapid taps both pass
  const [error, setError]          = useState("");
  const [selectedLeague, setSelectedLeague] = useState(() => {
    if (torchDraft?.leagueId) return torchDraft.leagueId;
    // Default to the league the user was just viewing in the Den
    try {
      const denLeague = localStorage.getItem("torch_active_league");
      if (denLeague && userLeagues.some(l => l.id === denLeague)) return denLeague;
    } catch {}
    // New users with no leagues yet default to Just for Fun mode so they can
    // make picks immediately without hitting an "exceeds balance" wall.
    if (userLeagues.length === 0) return "__fun__";
    return userLeagues[0]?.id || null;
  });
  const [showLeagueDropdown, setShowLeagueDropdown] = useState(false);

  // Lock league when torch draft is active
  useEffect(() => {
    if (torchDraft?.leagueId) setSelectedLeague(torchDraft.leagueId);
  }, [torchDraft]);
  const [slipMode, setSlipMode]    = useState("straight"); // "straight" | "parlay"
  const [parlayWager, setParlayWager] = useState(0);

  // Auto-reset parlay mode when picks drop below 2 legs
  useEffect(() => {
    if (items.length < 2 && slipMode === "parlay") {
      setSlipMode("straight");
      setParlayWager(0);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items.length]);

  const totalWagered = useMemo(() => slipMode === "parlay"
    ? parlayWager
    : Object.values(wagers).reduce((s, w) => s + (Number(w)||0), 0),
    [slipMode, parlayWager, wagers]);
  const noLeagueSelected = compatibleLeagues.length > 0 && !selectedLeague && selectedLeague !== "__fun__";
  useEffect(() => {
    if (selectedLeague && selectedLeague !== "__fun__" && compatibleLeagues.length > 0 && !compatibleLeagues.find(l => l.id === selectedLeague)) {
      setSelectedLeague(compatibleLeagues[0].id);
    }
  }, [compatibleLeagues, selectedLeague]);
  // Parlay computed values — memoized to avoid recalculating on every render
  const parlayLegs      = useMemo(() => items.map(([, p]) => p), [items]);
  const parlayDecimal   = useMemo(() => calcParlayDecimal(parlayLegs), [parlayLegs]);
  const parlayAmerican  = useMemo(() => calcParlayAmericanOdds(parlayLegs), [parlayLegs]);
  const parlayPayout    = useMemo(() => calcParlayPayout(parlayWager, parlayLegs), [parlayWager, parlayLegs]);
  const parlayProfit    = parlayPayout - parlayWager;
  const isFunMode = selectedLeague === "__fun__";
  const activeLeagueObj = isFunMode ? { id:"__fun__", name:"Practice", memberCount:"∞", balance:0, startingBalance:0, isCommissioner:false } : (compatibleLeagues.find(l => l.id === selectedLeague) || userLeagues.find(l => l.id === selectedLeague));

  // ── SCORING MODE ──────────────────────────────────────────────────────────
  // Pickem/survivor leagues have no ledger: picks cost nothing and pay nothing.
  // Every wager-shaped gate below (budget, min/max bounds, "needs a wager to
  // submit") is meaningless there and, left alone, actively blocks submission —
  // slipReady requires at least one wagered pick, so the button would never
  // light up and handleSubmit would silently early-return on totalWagered === 0.
  //
  // Practice ("__fun__") is NOT a league and has no scoringMode; it keeps its
  // own existing wager-with-practice-points behaviour, so fun mode always
  // resolves to "wager".
  const slipScoringMode = isFunMode ? "wager" : (activeLeagueObj?.scoringMode || "wager");
  const isNonWagerMode  = slipScoringMode === "pickem" || slipScoringMode === "survivor";

  // Safety net for one real path: the user is sitting IN parlay mode on a wager
  // league, then switches the slip's league to a pickem one — the slip would
  // otherwise keep rendering parlay UI for a format that has no parlays. Force
  // it back to straight.
  //
  // This is NOT what keeps parlay out of pickem — the toggle and the teaser are
  // both gated on !isNonWagerMode so the mode can't be entered. (An earlier
  // version of this comment claimed the entry points were all hidden; the
  // toggle wasn't, which is exactly how the bouncing "Parlay" button shipped.)
  useEffect(() => {
    if (isNonWagerMode && slipMode === "parlay") setSlipMode("straight");
  }, [isNonWagerMode, slipMode]);
  // In fun mode we deliberately fall through to the global `balance` rather than
  // using a forced 0 — this lets the user wager their practice points without
  // the validator firing "Wagers exceed your balance" when they try to.
  const leagueBalance = isFunMode ? null : (activeLeagueObj?.balance ?? null);

  // Active league's per-pick minimum wager. Used by the auto-seed effect
  // below + downstream validators that already exist further down the file.
  const leagueMinWager = isFunMode
    ? 0
    : Math.max(0, activeLeagueObj?.pickRequirements?.minWager || 0);

  // When a pick is added to the slip while the slip is already open, leave its
  // wager EMPTY (0). We don't pre-seed the minimum or balance — the user enters
  // the amount via the "How much?" input, and the submit path validates against
  // minWager. Only initializes keys not already present; never overrides a
  // value the user has typed. Torch flow keeps its own seeding via torchDraft.
  useEffect(() => {
    if (torchDraft) return;
    setWagers(prev => {
      let changed = false;
      const next = { ...prev };
      for (const k of Object.keys(picks)) {
        if (!(k in next) || next[k] == null) {
          next[k] = 0;
          changed = true;
        }
      }
      return changed ? next : prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [picks, torchDraft]);

  // Torch co-stake: combined pot is 2 × per-side stake. That's the budget
  // available to spread across picks in this slip.
  const spendable = torchDraft ? (torchDraft.amount || 0) : Math.max(0, leagueBalance !== null ? leagueBalance : balance);
  const pickingForLabel = activeLeagueObj ? activeLeagueObj.name : (userLeagues.length === 0 ? "Practice" : "No league selected");
  // Non-wager modes can't be over budget: nothing is wagered and the member's
  // balance is 0 by design, so 0 > 0 is false anyway — but stating it explicitly
  // keeps the intent clear and survives any future change to `spendable`.
  const overBudget   = !isNonWagerMode && totalWagered > spendable;

  // ── Slip readiness ────────────────────────────────────────────────────────
  // Drives the submit button's appearance: it stays visually inert (dim) until
  // the slip is actually submittable, then lights up to full ember. This mirrors
  // the submit-path validation so the button's color is an honest signal of
  // "ready to lock in" rather than always-on (which made parlays confusing —
  // the button looked active even with no wager / under the minimum).
  const leagueMaxWager = isFunMode ? 0 : Math.max(0, activeLeagueObj?.pickRequirements?.maxWager || 0);
  const slipReady = useMemo(() => {
    if (items.length === 0) return false;
    if (overBudget) return false;
    // Pickem/survivor: the slip is ready as soon as there's a pick. There are no
    // wagers to validate. Parlays are a wager construct (combined odds on a
    // shared stake) and don't exist in these formats — the mode forces
    // allowedBetTypes to ["ml"], and parlay mode is hidden in the UI below.
    if (isNonWagerMode) return true;
    if (slipMode === "parlay") {
      if (items.length < 2) return false;            // needs ≥2 legs
      if (!isFunMode) {
        if ((Number(parlayWager) || 0) <= 0) return false;
        // Allow all-in below the floor (mirrors submit path).
        const allIn = (Number(parlayWager) || 0) === spendable && spendable < leagueMinWager;
        if (leagueMinWager > 0 && (Number(parlayWager) || 0) < leagueMinWager && !allIn) return false;
        if (leagueMaxWager > 0 && (Number(parlayWager) || 0) > leagueMaxWager) return false;
      }
      return true;
    }
    // straight: at least one wagered pick, and every wagered pick clears bounds
    const wagered = items.filter(([k]) => (Number(wagers?.[k]) || 0) > 0);
    if (wagered.length === 0) return false;
    if (!isFunMode && (leagueMinWager > 0 || leagueMaxWager > 0)) {
      for (const [k] of wagered) {
        const w = Number(wagers?.[k]) || 0;
        const allIn = wagered.length === 1 && w === spendable && spendable < leagueMinWager;
        if (leagueMinWager > 0 && w < leagueMinWager && !allIn) return false;
        if (leagueMaxWager > 0 && w > leagueMaxWager) return false;
      }
    }
    return true;
  }, [items, wagers, slipMode, parlayWager, isFunMode, isNonWagerMode, leagueMinWager, leagueMaxWager, spendable, overBudget]);

  const fmtOdds = (o) => o == null ? "" : o > 0 ? `+${o}` : `${o}`;

  const getPayout = (pickKey) => {
    const p = picks[pickKey];
    const w = Number(wagers[pickKey]) || 0;
    const odds = p?.odds || 0;
    return w > 0 ? calcPayout(w, odds) : 0;
  };
  const totalPotential = useMemo(() => items.reduce((s, [k]) => s + getPayout(k), 0), [items, wagers, picks]);

  const setWager = (key, val) => {
    const n = Math.max(0, Math.floor(Number(val) || 0));
    setWagers(prev => ({ ...prev, [key]: n }));
  };

  // Distribute balance evenly across all picks
  const splitEvenly = () => {
    if (items.length === 0) return;
    const each = Math.floor(spendable / items.length);
    setWagers(Object.fromEntries(items.map(([k]) => [k, each])));
  };

  // FIX #5: All-in zeros other picks' wagers — warn if >1 pick in slip
  const goAllIn = (key) => {
    if (items.length > 1) {
      const otherCount = items.length - 1;
      window.torchConfirm?.(
        `This will set your other ${otherCount} pick${otherCount > 1 ? "s" : ""} to 0 pts. They'll still submit but win nothing.`,
        () => setWagers(Object.fromEntries(items.map(([k]) => [k, k === key ? spendable : 0]))),
        { confirmLabel: "Go All In" }
      );
    } else {
      setWagers(Object.fromEntries(items.map(([k]) => [k, k === key ? spendable : 0])));
    }
  };

  const handleSubmit = async () => {
    if (!user || items.length === 0 || submitting || submittingRef.current) return; // guard duplicate taps
    // Re-validate budget at submit time (not just at confirm time)
    // This catches the race where balance changed between confirm screen open and submit
    const currentSpendable = torchDraft ? (torchDraft.amount || 0) : Math.max(0, leagueBalance !== null ? leagueBalance : balance);
    if (!isFunMode && !isNonWagerMode && totalWagered > currentSpendable) {
      setError(`Insufficient balance — you have ${currentSpendable} pts but wagered ${totalWagered} pts.`);
      setStep("slip");
      return;
    }
    // A wager-free slip is legitimate in pickem/survivor. Without the
    // isNonWagerMode exemption this early-returns silently and the user taps
    // submit forever with no feedback.
    if (totalWagered === 0 && !isFunMode && !isNonWagerMode) return;

    // Sport-lock enforcement
    // PRECEDENCE FIX (user-reported, Aug 2026): allowedSports is AUTHORITATIVE
    // when present and non-empty — the same rule leagueSportLock (GamesPage)
    // already states and follows. The legacy single-sport check below used to
    // run FIRST and unconditionally, so a template league (sportFilter stamped
    // from the template's eventSport) with extra sports in allowedSports —
    // e.g. NFL template + NCAAF — showed NCAAF games on the board and then
    // rejected the slip here with "This league is NFL only". Legacy now
    // applies only when no allowedSports array exists (older league docs).
    // (Re-applied: this guard shipped in the CF on Aug 28 but the matching
    // client drop-in was never deployed — client and CF must stay mirrored.)
    const hasAllowedSportsArr = Array.isArray(activeLeagueObj?.allowedSports) && activeLeagueObj.allowedSports.length > 0;
    const leagueSportKey = activeLeagueObj?.sportFilter || activeLeagueObj?.eventSport;
    if (!hasAllowedSportsArr && leagueSportKey && slipSportKeys.size > 0) {
      const mismatch = [...slipSportKeys].some(sk => sk !== leagueSportKey && !sk.includes(leagueSportKey) && !leagueSportKey.includes(sk));
      if (mismatch) {
        const sportName = SPORT_FILTER_MAP[leagueSportKey] || leagueSportKey;
        setError(`This league is ${sportName} only. Remove non-${sportName} picks.`);
        return;
      }
    }

    // ── Multi-sport restriction (wizard) ───────────────────────────────────
    // allowedSports replaces the legacy single sportFilter when commissioners
    // want to allow N specific sports (e.g. NFL + NBA). Empty / null = all.
    const allowedSportsArr = Array.isArray(activeLeagueObj?.allowedSports) ? activeLeagueObj.allowedSports : null;
    if (allowedSportsArr && allowedSportsArr.length > 0 && slipSportKeys.size > 0) {
      const offending = [...slipSportKeys].filter(sk => {
        return !allowedSportsArr.some(allowed => sk === allowed || sk.startsWith(allowed));
      });
      if (offending.length > 0) {
        const allowedNames = allowedSportsArr.map(s => SPORT_FILTER_MAP[s] || s).join(", ");
        setError(`This league only allows ${allowedNames}. Remove non-allowed picks.`);
        return;
      }
    }

    // ── Bet-type restriction (wizard) ──────────────────────────────────────
    // Maps each pick's `type` field to a category and checks against the
    // league's allowedBetTypes. Categories: "spread" | "ml" | "total" | "prop".
    // Parlays are checked leg-by-leg. Anything not categorized (e.g. outright
    // tournament winner picks) is allowed through — those legacy types
    // pre-date the wizard and shouldn't be silently blocked by it.
    const allowedTypesArr = Array.isArray(activeLeagueObj?.allowedBetTypes) ? activeLeagueObj.allowedBetTypes : null;
    if (allowedTypesArr && allowedTypesArr.length > 0) {
      const categorize = (t) => {
        const s = String(t || "").toLowerCase();
        if (s === "ml" || s === "ml_1h") return "ml";
        if (s === "spread" || s === "spread_1h" || s === "alt_spread") return "spread";
        if (s === "total" || s === "total_1h" || s === "alt_total" || s === "team_total") return "total";
        if (s === "player_prop") return "prop";
        return null; // unknown / outright / tournament — let through
      };
      const offending = [];
      for (const [, p] of items) {
        // Parlays carry legs; check each leg.
        const legs = (p?.isParlay && Array.isArray(p?.legs)) ? p.legs : [p];
        for (const leg of legs) {
          const cat = categorize(leg?.type);
          if (cat && !allowedTypesArr.includes(cat)) offending.push(cat);
        }
      }
      if (offending.length > 0) {
        const labelMap = { spread: "spreads", ml: "moneylines", total: "totals", prop: "player props" };
        const allowedLabels = allowedTypesArr.map(t => labelMap[t] || t).join(" / ");
        setError(`This league allows ${allowedLabels} only. Remove the other picks.`);
        return;
      }
    }

    // ── Wager bound enforcement (wizard) ────────────────────────────────────
    // pickRequirements.minWager / maxWager apply to every individual pick's
    // wager (not the slip total — a 10-pick slip with 50 pts each is fine
    // even if minWager is 200, the per-pick minimum is what the commissioner
    // controls). Skip in fun mode (no wagers there).
    //
    // All-in exception: if the user's remaining league balance is below the
    // league's minWager, allow a single all-in wager (one wagered pick whose
    // wager equals the full remaining balance). Without this, a drawn-down
    // user is locked out of picks entirely — they can never reach the floor.
    if (!isFunMode) {
      const reqs = activeLeagueObj?.pickRequirements;
      const minW = reqs?.minWager;
      const maxW = reqs?.maxWager;
      if ((minW != null && minW > 0) || (maxW != null && maxW > 0)) {
        const remainingBudget = Math.max(0, leagueBalance !== null ? leagueBalance : (balance || 0));
        // ── PARLAY: the wager is a single combined stake (parlayWager), not a
        // per-leg amount. The per-pick loop below reads `wagers[k]` (straight
        // mode's per-game wagers), which for a parlay are 0/stale — so it would
        // wrongly fire the minimum error on a parlay whose combined wager (e.g.
        // 355) clears the floor. Validate the parlay's single stake once here.
        if (slipMode === "parlay") {
          const pw = Number(parlayWager) || 0;
          if (minW != null && minW > 0 && pw < minW) {
            const allInOK = pw === remainingBudget && remainingBudget < minW;
            if (!allInOK) {
              const hint = remainingBudget > 0 && remainingBudget < minW
                ? ` Go all-in with your ${remainingBudget.toLocaleString()} pts to submit.`
                : "";
              setError(`This league requires a minimum ${minW} pt wager.${hint}`);
              return;
            }
          }
          if (maxW != null && maxW > 0 && pw > maxW) {
            setError(`This league caps wagers at ${maxW} pts.`);
            return;
          }
        } else {
          const wageredCount = items.filter(([k2]) => Number(wagers?.[k2] ?? 0) > 0).length;
          for (const [k, p] of items) {
            const w = Number(wagers?.[k] ?? p?.wager ?? 0);
            if (w <= 0) continue; // unwagered legs of a parlay handled elsewhere
            if (minW != null && minW > 0 && w < minW) {
              const allInOK = wageredCount === 1 && w === remainingBudget && remainingBudget < minW;
              if (!allInOK) {
                const hint = remainingBudget > 0 && remainingBudget < minW
                  ? ` Go all-in with your ${remainingBudget.toLocaleString()} pts to submit.`
                  : "";
                setError(`This league requires a minimum ${minW} pt wager per pick.${hint}`);
                return;
              }
            }
            if (maxW != null && maxW > 0 && w > maxW) {
              setError(`This league caps wagers at ${maxW} pts per pick.`);
              return;
            }
          }
        }
      }
    }

    // Start date enforcement — REMOVED (2026-07-15).
    //
    // This used to block ALL submission until league.startDate, which is wrong
    // for season-long templates: a pick'em pool exists to pick games BEFORE they
    // happen. Gating on startDate meant the NFL template ("2026-09-10") rejected
    // every pick until the season had already begun, by which point Week 1's
    // Thursday opener was hours away.
    //
    // The guard that actually matters is per-game: a pick on a game that has
    // already kicked off is blocked/locked elsewhere (commenceTime checks +
    // the live-bet path). startDate is a scheduling label, not a pick lock.
    //
    // Note the empty-slate case is upstream and NOT solved here: The Odds API
    // only returns games inside its own rolling window, so an NFL league created
    // in July shows no games until the provider posts Week 1 lines. The season
    // banner communicates that state; this gate only added a second, wronger
    // explanation on top of it.

    // Block submission to ended leagues (skip in fun mode)
    if (selectedLeague && selectedLeague !== "__fun__") {
      const targetLeague = userLeagues.find(l => l.id === selectedLeague);
      if (!targetLeague) {
        setError("Selected league not found. Please pick another.");
        setStep("slip");
        return;
      }
    }

    // Spread sanity guard removed (was rejecting +49.5 NBA spreads as "bad data").
    // Real blowouts and prop-style alt lines can legitimately exceed any sport-specific
    // cap, so we now trust the Odds API feed as the source of truth. If genuinely
    // corrupt data (NaN, 999, etc.) ever shows up, the server-side settlement guards
    // and the Odds API rate-of-change checks elsewhere in this file will catch it.

    // Set ref AFTER all validation guards so early returns don't permanently block the button
    submittingRef.current = true;
    setSubmitting(true); setError("");
    try {
      const gamesMap = (gamesRef.current || []).reduce((a, g) => { a[g.id] = g; return a; }, {});

      // ── TORCH INTERCEPT: if torch draft is active, create loan request only ──
      // The actual pick will be submitted when the lender accepts
      if (torchDraft) {
        const firstItem = items[0];
        if (!firstItem) { setError("No pick selected."); return; }
        const [firstKey, pickData] = firstItem;
        const pickGame = gamesMap[pickData.gameId];
        const gameLabel = pickGame
          ? (pickGame.away?.full || "Away") + " @ " + (pickGame.home?.full || "Home")
          : pickData.label || "";

        // Store ALL pick details in the loan so the accept handler can submit them
        const torchPickData = items.map(([key, p]) => {
          const g = gamesMap[p.gameId];
          return {
            key, gameId: p.gameId, type: p.type, side: p.side,
            line: p.line ?? null, label: p.label, odds: p.odds || 0,
            sport: g?.sport || p.sport || "",
            sportKey: g?.sportKey || p.sportKey || "",
            commenceTime: g?.commenceTime || p.commenceTime || null, // SLIP-GAME-TIME (fix 2026-10-04): keep the time captured at tap if the game left the list
            gameLabel: g ? (g.away?.short || g.away?.full || "") + " @ " + (g.home?.short || g.home?.full || "") : "",
            homeTeamFull: g?.homeTeamFull || g?.home?.full || "",
            awayTeamFull: g?.awayTeamFull || g?.away?.full || "",
            wager: wagers[key] || (torchDraft.amount || 0),
            // PICK-IDENTITY (fix 1.1, 2026-09-28)
            ...(p.playerName ? { playerName: p.playerName } : {}),
            ...(p.statType   ? { statType:   p.statType }   : {}),
            ...(p.teamSide   ? { teamSide:   p.teamSide }   : {}),
          };
        });

        // Branch on draft mode: ASK (existing) vs OFFER (new). The Cloud Function paths
        // differ — requestTorchLoan creates a loan that the LENDER reviews; offerTorchLoan
        // creates a loan with the lender's stake already debited that the BORROWER reviews.
        let loan;
        if (torchDraft.offerMode) {
          // OFFER FLOW — current user is the LENDER offering to fund a broke teammate
          const myName = userStats?.username || user.displayName || user.email?.split("@")[0] || "You";
          loan = await offerTorchLoan({
            lenderUid: user.uid,
            lenderName: myName,
            borrowerUid: torchDraft.recipientUid,
            borrowerName: torchDraft.recipientName,
            leagueId: torchDraft.leagueId,
            amount: torchDraft.amount,
            gameId: pickData.gameId || "",
            gameLabel,
            commenceTime: pickGame?.commenceTime || null,
            sportKey: pickGame?.sportKey || pickData.sportKey || "",
            pickKey: firstKey || "",
            pickLabel: pickData.label || "",
            pickOdds: pickData.odds || -110,
            pickType: pickData.type || "",
            pickSide: pickData.side || "",
            pickLine: pickData.line ?? null,
            message: torchDraft.message || "",
            pickDetails: torchPickData,
          });

          window.torchToast?.(`Torch offered to ${torchDraft.recipientName} — waiting for them to accept`, "success");

          // Post to Den social feed
          try {
            await writeLeagueActivity(user.uid, [torchDraft.leagueId], {
              type: "torch_offer",
              userId: user.uid, username: myName,
              // PRIVACY: strip the pick from the den broadcast in
              // until_start leagues (recipient sees the full pick in the
              // torch flow itself).
              label: (userLeagues.find(l => l.id === torchDraft.leagueId)?.pickPrivacy === "until_start")
                ? `offered ${torchDraft.recipientName} a ${torchDraft.amount} pt pick`
                : `offered ${torchDraft.recipientName} a ${torchDraft.amount} pt pick on ${pickData.label || gameLabel}`,
              torchLoanId: loan.id,
              torchAmount: torchDraft.amount,
              torchLenderUid: user.uid,
              torchLenderName: myName,
              torchBorrowerUid: torchDraft.recipientUid,
              torchBorrowerName: torchDraft.recipientName,
              torchPickLabel: pickData.label || "",
              torchGameLabel: gameLabel,
              torchStatus: "pending",
              torchOfferInitiated: true,
            });
          } catch (e) { console.warn("[Torch/Offer] Activity post failed:", e.message); }
        } else {
          // ASK FLOW — current user is the BORROWER asking a wealthy teammate to fund
          loan = await requestTorchLoan({
            borrowerUid: user.uid,
            borrowerName: userStats?.username || user.displayName || user.email?.split("@")[0] || "You",
            lenderUid: torchDraft.lenderUid,
            lenderName: torchDraft.lenderName,
            leagueId: torchDraft.leagueId,
            amount: torchDraft.amount,
            mode: "lender_funded", // borrower puts up nothing; lender funds the full wager
            gameId: pickData.gameId || "",
            gameLabel,
            commenceTime: pickGame?.commenceTime || null,
            sportKey: pickGame?.sportKey || pickData.sportKey || "",
            pickKey: firstKey || "",
            pickLabel: pickData.label || "",
            pickOdds: pickData.odds || -110,
            pickType: pickData.type || "",
            pickSide: pickData.side || "",
            pickLine: pickData.line ?? null,
            message: torchDraft.message || "",
            // Store full pick details so lender accept can submit them
            pickDetails: torchPickData,
          });

          window.torchToast?.("Torch request sent to " + torchDraft.lenderName + " — waiting for approval", "success");

          // Post to Den social feed so everyone can see it
          try {
            const borrowerName = userStats?.username || user.displayName || user.email?.split("@")[0] || "Someone";
            await writeLeagueActivity(user.uid, [torchDraft.leagueId], {
              type: "torch_request",
              userId: user.uid, username: borrowerName,
              label: `asked ${torchDraft.lenderName} to fund a ${torchDraft.amount} pt pick`,
              torchLoanId: loan.id,
              torchAmount: torchDraft.amount,
              torchLenderUid: torchDraft.lenderUid,
              torchLenderName: torchDraft.lenderName,
              torchBorrowerUid: user.uid,
              torchBorrowerName: borrowerName,
              torchPickLabel: pickData.label || "",
              torchGameLabel: gameLabel,
              torchStatus: "pending",
            });
          } catch (e) { console.warn("[Torch] Activity post failed:", e.message); }
        }

        onTorchComplete?.();
        // Defer the picks clear to the actual unmount (requestClose handles
        // it) — clearing now would flash the "Slip's empty" state mid-exit.
        pendingClearRef.current = true;
        requestClose(() => onAfterSubmit?.(torchDraft.leagueId));
        return; // EXIT — do NOT submit picks normally
      }

      // ── NORMAL FLOW (no torch) ──────────────────────────────────────────
      let submittedPicks = [];
      let submittedWager = 0;

      // ── FUN MODE: direct write, no CF, no balance ──
      if (isFunMode) {
        if (slipMode === "parlay") {
          // Fun parlay — direct write, no balance needed
          if (items.length < 2) { setError("A parlay needs at least 2 legs."); return; }
          const legs = items.map(([, p]) => {
            const game = (games || []).find(g => g.id === p.gameId);
            return {
              gameId: p.gameId || "", type: p.type || "", side: p.side || "",
              line: p.line ?? null, label: p.label || "",
              gameLabel: game ? `${game.away?.full||game.away?.short||""} @ ${game.home?.full||game.home?.short||""}` : p.label || "",
              sport: game?.sport || p.sport || "", sportKey: game?.sportKey || p.sportKey || "",
              commenceTime: game?.commenceTime || p.commenceTime || null, // SLIP-GAME-TIME (fix 2026-10-04): keep the time captured at tap if the game left the list
              homeTeamFull: game?.homeTeamFull || game?.home?.full || "",
              awayTeamFull: game?.awayTeamFull || game?.away?.full || "",
              odds: p.odds || 0,
              // PICK-IDENTITY (fix 1.1, 2026-09-28)
              ...(p.playerName ? { playerName: p.playerName } : {}),
              ...(p.statType   ? { statType:   p.statType }   : {}),
              ...(p.teamSide   ? { teamSide:   p.teamSide }   : {}),
            };
          });
          const parlayDoc = {
            isParlay: true, legs, legCount: legs.length,
            gameId: legs[0]?.gameId || "",
            gameLabel: legs.map(l => l.gameLabel || l.label).join(" · "),
            type: "parlay", side: "", line: null,
            wager: parlayWager, odds: parlayAmerican, combinedOdds: parlayAmerican,
            expectedPayout: parlayWager > 0 ? calcParlayPayout(parlayWager, parlayLegs) : 0,
            leagueId: null,
            sport: legs[0]?.sport || "", sportKey: legs[0]?.sportKey || "",
            commenceTime: legs[0]?.commenceTime || null,
            homeTeamFull: legs[0]?.homeTeamFull || "",
            awayTeamFull: legs[0]?.awayTeamFull || "",
            label: `${legs.length}-leg Parlay`,
            ...(legs.some(l => l?.isLiveBet) ? { isLiveBet: true } : {}),
          };
          try {
            // ── Deduct BEFORE submit (atomic-ish ordering) ──
            // The previous order (submit then deduct) created an exploit:
            // if applyBalanceDelta failed after submitPicks succeeded, the
            // user had a wagered pick in Firestore but never paid for it.
            // Deducting first means: deduct fails → no pick gets created.
            // Pick submit fails after deduct → we attempt refund. Worst case
            // (both fail) is balance lost but no pick — UX bug, not exploit.
            // STAKED-PICKS-SERVER-ONLY (fix 2.5, 2026-09-28): a practice parlay
            // with a stake goes through submitPickCallable (leagueId null), which
            // debits the global balance in the same transaction that writes the
            // pick and applies every submit guard (odds, finished games, limits).
            // The old debit-then-direct-write dance ran none of them, and
            // firestore.rules now only lets the client create ZERO-stake
            // practice picks. Errors fall to the catch below and are shown.
            if (parlayWager > 0) {
              await submitPicksSecure(user.uid, [parlayDoc], parlayWager, null);
              onBalanceChange(prev => Math.max(0, prev - parlayWager));
            } else {
              await submitPicks(user.uid, [parlayDoc]);
            }
            haptic.success();
            window.torchToast?.(`${legs.length}-leg parlay locked in.`, "success");
            // Clear at unmount, not now: the body render checks items.length
            // before step==="done", so clearing here made the done screen
            // unreachable (empty state shadowed it) and would zero its numbers
            // (parlayPayout derives from items). requestClose clears via
            // pendingClearRef when the user dismisses the sheet.
            pendingClearRef.current = true;
            recordDailyPick();
            setStep("done");
            onAfterSubmit?.(null);
          } catch (e) {
            setError(e.message || "Submission failed.");
            setStep("slip");
          } finally { setSubmitting(false); submittingRef.current = false; }
          return;
        }
        // Fun straight picks
        const picksArray = items.map(([k, p]) => {
          const game = (games || []).find(g => g.id === p.gameId);
          const w = Number(wagers[k]) || 0;
          return {
            gameId: p.gameId || "", type: p.type || "", side: p.side || "",
            line: p.line ?? null, label: p.label || "",
            gameLabel: game ? `${game.away?.full||game.away?.short||""} @ ${game.home?.full||game.home?.short||""}` : p.label || "",
            sport: game?.sport || p.sport || "", sportKey: game?.sportKey || p.sportKey || "",
            commenceTime: game?.commenceTime || p.commenceTime || null, // SLIP-GAME-TIME (fix 2026-10-04): keep the time captured at tap if the game left the list
            homeTeamFull: game?.homeTeamFull || game?.home?.full || "",
            awayTeamFull: game?.awayTeamFull || game?.away?.full || "",
            leagueId: null, wager: w, odds: p.odds || 0,
            expectedPayout: w > 0 ? calcPayout(w, p.odds || 0) : 0,
            ...(p.isLiveBet ? { isLiveBet: true } : {}),
            // PICK-IDENTITY (fix 1.1, 2026-09-28)
            ...(p.playerName ? { playerName: p.playerName } : {}),
            ...(p.statType   ? { statType:   p.statType }   : {}),
            ...(p.teamSide   ? { teamSide:   p.teamSide }   : {}),
          };
        });
        try {
          // ── Deduct BEFORE submit (same atomic-ish ordering as parlay above) ──
          // Prevents the "submit succeeded, deduct failed = free pick" exploit
          // by reversing the order. If deduction fails, nothing happens. If
          // submit fails after deduction, we attempt refund.
          // STAKED-PICKS-SERVER-ONLY (fix 2.5): staked practice picks go through
          // submitPickCallable (atomic global debit + submit guards); zero-stake
          // ones are the only picks the rules let the client write directly.
          const stakedPicks   = picksArray.filter(p => (Number(p.wager) || 0) > 0);
          const unstakedPicks = picksArray.filter(p => !((Number(p.wager) || 0) > 0));
          if (stakedPicks.length > 0) {
            const stake = stakedPicks.reduce((sum, p) => sum + (Number(p.wager) || 0), 0);
            await submitPicksSecure(user.uid, stakedPicks, stake, null);
            onBalanceChange(prev => Math.max(0, prev - stake));
          }
          if (unstakedPicks.length > 0) {
            await submitPicks(user.uid, unstakedPicks);
          }
          haptic.success();
          window.torchToast?.(`${picksArray.length} pick${picksArray.length > 1 ? "s" : ""} locked in.`, "success");
          // Same deferral as the fun-parlay path above: clearing picks here
          // shadowed the done screen behind the empty state. Cleared at close.
          pendingClearRef.current = true;
          recordDailyPick();
          setStep("done");
          onAfterSubmit?.(null);
        } catch (e) {
          setError(e.message || "Submission failed.");
          setStep("slip");
        } finally { setSubmitting(false); submittingRef.current = false; }
        return;
      }

      if (slipMode === "parlay") {
        // -- PARLAY: one Firestore doc, all legs inside --
        if (items.length < 2) { setError("A parlay needs at least 2 legs."); return; }
        if (parlayWager <= 0 && !isFunMode) { setError("Enter a wager for your parlay."); return; }
        const legs = items.map(([, p]) => {
          const game = gamesMap[p.gameId];
          return {
            gameId:       p.gameId || "",
            gameLabel:    game ? `${game.away?.full||game.away?.short||""} @ ${game.home?.full||game.home?.short||""}` : p.label || "",
            type:         p.type || "",
            side:         p.side || "",
            line:         p.line ?? null,
            label:        p.label || "",
            sport:        game?.sport    || p.sport    || "",
            sportKey:     game?.sportKey || p.sportKey || "",
            commenceTime: game?.commenceTime || p.commenceTime || null, // SLIP-GAME-TIME (fix 2026-10-04): keep the time captured at tap if the game left the list
            homeTeamFull: game?.homeTeamFull || game?.home?.full || "",
            awayTeamFull: game?.awayTeamFull || game?.away?.full || "",
            odds:         p.odds || 0,
            // PICK-IDENTITY (fix 1.1, 2026-09-28): legs are stored whole by the CF and graded
            // leg-by-leg; a team-total leg with no teamSide grades against
            // the away score, a prop leg with no playerName/statType falls
            // back to parsing its label.
            ...(p.playerName ? { playerName: p.playerName } : {}),
            ...(p.statType   ? { statType:   p.statType }   : {}),
            ...(p.teamSide   ? { teamSide:   p.teamSide }   : {}),
          };
        });
        const parlayDoc = {
          isParlay:      true,
          legs,
          legCount:      legs.length,
          gameId:        legs[0]?.gameId || "",
          gameLabel:     legs.map(l => l.gameLabel || l.label).join(" · "),
          type:          "parlay",
          side:          "",
          line:          null,
          wager:         parlayWager,
          odds:          parlayAmerican,
          combinedOdds:  parlayAmerican,
          expectedPayout: parlayPayout,
          leagueId:      selectedLeague === "__fun__" ? null : (selectedLeague || null),
          sport:         legs[0]?.sport || "",
          sportKey:      legs[0]?.sportKey || "",
          commenceTime:  legs[0]?.commenceTime || null,
          homeTeamFull:  legs[0]?.homeTeamFull || "",
          awayTeamFull:  legs[0]?.awayTeamFull || "",
          label:         `${legs.length}-leg Parlay`,
          ...(legs.some(l => l?.isLiveBet) ? { isLiveBet: true } : {}),
        };
        // Use secure Cloud Function path — validates balance + rate limits server-side.
        // The function also deducts balance atomically, so we skip applyBalanceDelta here.
        let submitResult = null;
        try {
          submitResult = await submitPicksSecure(user.uid, [parlayDoc], parlayWager, selectedLeague === "__fun__" ? null : (selectedLeague || null));
        } catch (fnErr) {
          // Surface rate-limit and balance errors cleanly; fall back for transient failures
          const code = fnErr?.code || "";
          if (code === "functions/resource-exhausted") {
            setError(fnErr.message || "Too many picks submitted. Slow down and try again.");
            return;
          }
          if (code === "functions/failed-precondition") {
            // L-2 FIX: failed-precondition is an authoritative "insufficient
            // balance / league ended" signal from the CF, which checks the
            // LEAGUE member balance for league picks (source of truth). The old
            // code assumed the CF might be wrongly checking global balance and
            // bypassed to a direct client write when league balance "looked"
            // sufficient -- but that direct-write path is exactly the unfloored,
            // non-atomic debit we're hardening (L-1), and the bypass could also
            // double-path a submit the CF had actually created. Since the CF's
            // check is correct, treat this as a hard stop: surface the error and
            // return. Do NOT fall through to the direct-write fallback.
            setError(fnErr.message || "Insufficient balance.");
            setStep("slip");
            return;
          }
          // STAKED-PICKS-SERVER-ONLY (fix 2.5, 2026-09-28): the direct-write
          // fallback that used to follow here is retired. firestore.rules now
          // only lets the client create zero-stake practice picks, so it could
          // no longer succeed -- and it was the one path that skipped every
          // server guard. A deliberate rejection shows the server's reason; a
          // transport failure (the server may or may not have written the pick)
          // says so, so the user checks My Picks instead of double-submitting.
          setError(isTransientCallableError(fnErr)
            ? "Couldn't confirm your submission with the server. Check My Picks before trying again."
            : (fnErr.message || "This pick can't be submitted."));
          setStep("slip");
          return;
        }
        // Update local balance state (function already deducted server-side)
        // PRACTICE-ONLY-GLOBAL (fix 2.6): the server debits the practice balance
        // only for no-league picks; a league pick debits the league only.
        if (selectedLeague && selectedLeague !== "__fun__") onLeagueBalanceChange?.(selectedLeague, -parlayWager);
        else onBalanceChange(prev => Math.max(0, prev - parlayWager));
        // NOTE: CF submitPicksSecure already deducts league balance (line 309 of index.js).
        // Do NOT call deductLeagueWager here — that would double-deduct.
        submittedPicks = [parlayDoc];
        submittedWager = parlayWager;
        const isLiveParlay = legs.some(l => l.isLive);
        window.torchToast?.(isLiveParlay
          ? `Live parlay locked in. Settles when the game ends.`
          : `${legs.length}-leg parlay locked in. Win it for ${parlayPayout} pts.`, "success");
        logTorchEvent("pick_submitted", { count: legs.length, league: !!selectedLeague, parlay: true });
        // PRIVACY: in until_start leagues, don't WRITE the submit-time den
        // event at all — render-filtering only protects members on the new
        // bundle, and a league is however many stale WebViews its members
        // run. Settlement creates the den event when the pick grades, so the
        // Den shows picks at settle regardless of client version; the Picks
        // tab still reveals at kickoff.
        if (selectedLeague && activeLeagueObj?.pickPrivacy !== "until_start") {
          const uname = userStats?.username || user.displayName || user.email?.split("@")[0] || "Someone";
          // submitResult.pickIds[0] is the parlay doc id — store it so settlement can
          // update this activity event in place instead of creating a duplicate.
          writeLeagueActivity(user.uid, [selectedLeague], {
            type: "parlay", username: uname,
            // Earliest leg kickoff — lets pick-privacy leagues reveal this
            // den event at first kickoff instead of waiting for settle.
            commenceTime: legs.map(l => l.commenceTime).filter(Boolean).sort((a, b) => new Date(a) - new Date(b))[0] || null,
            label: `${legs.length}-leg Parlay`, sport: legs[0]?.sport || "",
            gameLabel: legs.map(l => l.gameLabel || l.label).join(" · "),
            odds: parlayAmerican || null,
            points: 0, wager: parlayWager || 0,
            expectedPayout: parlayPayout || 0,
            status: "pending",
            pickId: submitResult?.pickIds?.[0] || null,
          }).catch(() => {});
        }

        // ── Pulse mirror — public feed write ─────────────────────────────
        // Same pattern as league activity write: fire-and-forget, never
        // blocks submission UX. Parlay rolls up to a single mirror doc
        // since the feed displays parlays as one card. parlayLegs holds
        // the per-leg detail for the card to expand.
        {
          const uname = userStats?.username || user.displayName || user.email?.split("@")[0] || "Someone";
          const parlayPickId = submitResult?.pickIds?.[0];
          if (parlayPickId) {
            const leagueData = selectedLeague ? userLeagues.find(l => l.id === selectedLeague) : null;
            const leagueInfo = leagueData ? { id: leagueData.id, name: leagueData.name, isPrivate: !(leagueData.isPublic ?? leagueData.public), /* LEAGUE-ISPUBLIC: field is isPublic; .public never existed */ scoringMode: leagueData.scoringMode || "wager", pickPrivacy: leagueData.pickPrivacy || null } : null;
            mirrorPickToPulse(
              { ...parlayDoc, pickId: parlayPickId, isParlay: true, parlayLegs: legs },
              user, uname, leagueInfo, userStats,
            ).catch(() => {});
          }
        }
      } else {
        // -- STRAIGHT PICKS: one doc per leg --
        const picksArray = items.map(([k, p], idx) => {
          const game = gamesMap[p.gameId];
          // Non-wager modes carry no stake. Force 0 rather than trusting the
          // wagers map — the server rejects any pickem pick with a non-zero
          // wager (see submitPickCallable's non-wager guard), so sending a
          // stale value here would hard-fail the whole submission.
          const wager = isNonWagerMode ? 0 : (Number(wagers[k]) || 0);
          const odds  = p.odds || 0;
          return {
            gameId:         p.gameId || "",
            gameLabel:      game ? `${game.away?.full||game.away?.short||""} @ ${game.home?.full||game.home?.short||""}` : p.label || "",
            type:           p.type || "",
            side:           p.side || "",
            line:           p.line ?? null,
            label:          p.label || "",
            sport:          game?.sport    || p.sport    || "",
            sportKey:       game?.sportKey || p.sportKey || "",
            commenceTime:   game?.commenceTime || p.commenceTime || null, // SLIP-GAME-TIME (fix 2026-10-04): keep the time captured at tap if the game left the list
            homeTeamFull:   game?.homeTeamFull || game?.home?.full || "",
            awayTeamFull:   game?.awayTeamFull || game?.away?.full || "",
            leagueId:       selectedLeague === "__fun__" ? null : (selectedLeague || null),
            wager,
            odds,
            // No stake means no payout. Odds are still stored (harmless, and
            // useful if a league is ever migrated) but the payout is 0.
            expectedPayout: isNonWagerMode ? 0 : calcPayout(wager, odds),
            ...(p.isLiveBet ? { isLiveBet: true } : {}),
            // PICK-IDENTITY (fix 1.1, 2026-09-28): the slip item carries these (toggle(), ~line
            // 8884) but this payload dropped them. With no teamSide both
            // settlement engines fell back to label.includes(homeTeam) on a
            // label like "O 27.5" -- always false -- so every HOME team total
            // was graded against the AWAY score. submitPickCallable and the
            // submitPicks fallback both already persist these when present.
            ...(p.playerName ? { playerName: p.playerName } : {}),
            ...(p.statType   ? { statType:   p.statType }   : {}),
            ...(p.teamSide   ? { teamSide:   p.teamSide }   : {}),
          };
        });
        // Use secure Cloud Function path — validates balance + rate limits server-side.
        // The function also deducts balance atomically, so we skip applyBalanceDelta here.
        // For pickem/survivor the server skips the debit entirely and stamps
        // scoringMode onto each pick doc from the league it reads.
        let submitResult = null;
        try {
          submitResult = await submitPicksSecure(user.uid, picksArray, isNonWagerMode ? 0 : totalWagered, selectedLeague === "__fun__" ? null : (selectedLeague || null));
        } catch (fnErr) {
          const code = fnErr?.code || "";
          if (code === "functions/resource-exhausted") {
            setError(fnErr.message || "Too many picks submitted. Slow down and try again.");
            return;
          }
          if (code === "functions/failed-precondition") {
            // L-2 FIX: see the parlay path above for the full rationale. The CF
            // checks the authoritative league member balance, so a
            // failed-precondition here is a real insufficient-balance/ended-league
            // signal. Surface it and stop -- do NOT bypass to the direct-write
            // fallback (that path is the unfloored, non-atomic debit L-1 hardens,
            // and bypassing could double-path a submit the CF already created).
            setError(fnErr.message || "Insufficient balance.");
            setStep("slip");
            return;
          }
          // STAKED-PICKS-SERVER-ONLY (fix 2.5, 2026-09-28): the direct-write
          // fallback that used to follow here is retired. firestore.rules now
          // only lets the client create zero-stake practice picks, so it could
          // no longer succeed -- and it was the one path that skipped every
          // server guard. A deliberate rejection shows the server's reason; a
          // transport failure (the server may or may not have written the pick)
          // says so, so the user checks My Picks instead of double-submitting.
          setError(isTransientCallableError(fnErr)
            ? "Couldn't confirm your submission with the server. Check My Picks before trying again."
            : (fnErr.message || "This pick can't be submitted."));
          setStep("slip");
          return;
        }
        // Update local balance state (function already deducted server-side).
        // Nothing was debited in pickem/survivor, so leave the local state alone —
        // calling these with totalWagered 0 is a no-op today, but guarding makes
        // the intent explicit and avoids a spurious re-render of the balance UI.
        if (!isNonWagerMode) {
          // PRACTICE-ONLY-GLOBAL (fix 2.6): league picks debit the league only.
          if (selectedLeague && selectedLeague !== "__fun__") onLeagueBalanceChange?.(selectedLeague, -totalWagered);
          else onBalanceChange(prev => Math.max(0, prev - totalWagered));
        }
        // NOTE: CF submitPicksSecure already deducts league balance (line 309 of index.js).
        // Do NOT call deductLeagueWager here — that would double-deduct.
        submittedPicks = picksArray;
        submittedWager = totalWagered;
        const hasLivePick = picksArray.some(p => p.isLiveBet);
        window.torchToast?.(hasLivePick
          ? `Live pick${picksArray.length > 1 ? "s" : ""} locked in. Settles when the game ends.`
          : `${items.length} pick${items.length>1?"s":""} locked in.`, "success");
        logTorchEvent("pick_submitted", { count: picksArray.length, league: !!selectedLeague, parlay: false });
        // Write to league Feed so other members see the pick.
        // PRIVACY: suppressed at WRITE time for until_start leagues (see the
        // parlay path's comment above — stale clients can't filter).
        if (selectedLeague && activeLeagueObj?.pickPrivacy !== "until_start") {
          const uname = userStats?.username || user.displayName || user.email?.split("@")[0] || "Someone";
          picksArray.forEach((p, pIdx) => {
            writeLeagueActivity(user.uid, [selectedLeague], {
              type: p.type || "pick", username: uname,
              // Kickoff time — lets pick-privacy leagues reveal this den
              // event at kickoff instead of waiting for settle.
              commenceTime: p.commenceTime || null,
              label: p.label, sport: p.sport,
              gameLabel: p.gameLabel || "",
              odds: p.odds || null,
              points: 0, wager: p.wager || 0,
              expectedPayout: p.expectedPayout || 0,
              status: "pending",
              pickId: submitResult?.pickIds?.[pIdx] || null,
            }).catch(() => {});
          });
        }

        // ── Pulse mirror — public feed write ─────────────────────────────
        // One mirror doc per pick. Mirrors ALL submissions including
        // practice-mode (no leagueId) — Tom said firehose, that means
        // practice picks too. Captain opt-out (privacy toggle) is read
        // server-side in Phase 3b's CF; client still writes.
        {
          const uname = userStats?.username || user.displayName || user.email?.split("@")[0] || "Someone";
          const leagueData = selectedLeague ? userLeagues.find(l => l.id === selectedLeague) : null;
          const leagueInfo = leagueData ? { id: leagueData.id, name: leagueData.name, isPrivate: !(leagueData.isPublic ?? leagueData.public), /* LEAGUE-ISPUBLIC: field is isPublic; .public never existed */ scoringMode: leagueData.scoringMode || "wager", pickPrivacy: leagueData.pickPrivacy || null } : null;
          picksArray.forEach((p, pIdx) => {
            const pid = submitResult?.pickIds?.[pIdx];
            if (!pid) return; // skip — mirror needs a stable ID
            mirrorPickToPulse({ ...p, pickId: pid }, user, uname, leagueInfo, userStats).catch(() => {});
          });
        }
      }

      // Close immediately — toast is the confirmation, no need to show done screen
      // This eliminates the 1800ms window where submitting=false but slip is still
      // open, which was allowing accidental double-submits

      // Duplicate to other checked leagues (fire-and-forget, don't block close)
      // ── DUPLICATE-LEAGUES BLOCK REMOVED (2026-07-23) ─────────────────────
      // A ~60-line "duplicate this slip to other checked leagues" block lived
      // here, but no UI ever populated duplicateLeagues -- the state was only
      // ever initialized/reset to []. Dead code, and dangerous dead code: its
      // CF-failure fallback did a direct submitPicks write with no
      // isNonWagerMode block and no balance-already-deducted check (both of
      // which the main path has), so re-enabling the feature via a checkbox
      // would have silently reopened the pickem-corruption path. If league
      // duplication ever ships, rebuild it from the main submit path's guards.

      // Do NOT setPicks({}) here — items.length===0 would instantly swap the
      // confirm screen to the "Slip's empty" state under the green checkmark
      // (the wrong-content flash). The clear happens inside requestClose, in
      // the same commit that unmounts the sheet.
      pendingClearRef.current = true;
      // Show the checkmark morph briefly before dismissing — gives the submit
      // moment weight. Without this, modal closes faster than the human eye
      // registers "it worked," and the action feels transactional/abrupt.
      setSucceeded(true);
      setTimeout(() => { requestClose(() => onAfterSubmit?.(selectedLeague)); }, 700);
    } catch (e) {
      console.error("[Torch] Submit failed:", e);
      const msg = e?.message || "Something went wrong. Try again.";
      setError(torchDraft ? `Torch request failed: ${msg}` : msg);
    } finally { setSubmitting(false); submittingRef.current = false; }
  };

  const removeItem = (k) => {
    setPicks(prev => { const n={...prev}; delete n[k]; return n; });
    setWagers(prev => { const n={...prev}; delete n[k]; return n; });
  };

  return (
    <>
      <div onClick={() => requestClose()} style={{
        position:"fixed", inset:0, background:"rgba(4,5,10,0.72)",
        backdropFilter:"blur(14px)", zIndex:70,
        animation: closing ? "slipFadeOut 0.18s ease both" : "fadeIn 0.2s ease both",
        pointerEvents: closing ? "none" : "auto",
      }}/>
      <div style={{
        position:"fixed", bottom:0, left:"50%", transform:"translateX(-50%)",
        width:"100%", maxWidth:500,
        // ── Surface ──
        // Was: heavy cyan border (`#00d4ff`), white top stripe, and a 3-stop
        // aqua glow ring that made the slip look like a 2018 sportsbook.
        // Now: elevated dark surface with a single subtle top edge highlight.
        background:"#131520",
        borderRadius:"24px 24px 0 0",
        border:"1px solid rgba(255,255,255,0.06)",
        borderBottom:"none",
        zIndex:80,
        paddingBottom:"env(safe-area-inset-bottom,24px)",
        // slipEnterUp / slipExitDown are uniquely named on purpose: the global
        // style block defines @keyframes slideUp TWICE, and the later
        // definition (translateY(20px), no translateX) wins document-wide —
        // so this sheet's entry was animating WITHOUT translateX(-50%),
        // rendering off-center for 300ms then snapping to center. Unique
        // names dodge the collision; both keep translateX(-50%) throughout.
        animation: closing
          ? "slipExitDown 0.22s cubic-bezier(0.4,0,1,1) both"
          : "slipEnterUp 0.3s cubic-bezier(0.32,0.72,0,1)",
        pointerEvents: closing ? "none" : "auto",
        maxHeight:"calc(92vh - env(safe-area-inset-top, 0px))", display:"flex", flexDirection:"column",
        boxShadow:"0 1px 0 rgba(255,255,255,0.06) inset, 0 -16px 60px -8px rgba(0,0,0,0.6)",
      }}>
        {/* Handle row — drag-handle pill (close X lives in the title row below) */}
        <div style={{ padding:"10px 20px 0", flexShrink:0, position:"relative" }}>
          <div style={{ width:40, height:4, borderRadius:2, background:"rgba(255,255,255,0.10)", margin:"0 auto 16px" }}/>

          {/* Torch draft banner */}
          {torchDraft && (
            <div style={{
              padding:"12px 14px", borderRadius:14, marginBottom:14,
              background:"rgba(255,107,61,0.06)",
              border:"1px solid rgba(255,107,61,0.2)",
              display:"flex", alignItems:"center", gap:10,
              animation:"borderGlow 3s ease infinite",
            }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={T.coral} strokeWidth="1.8">
                <path d="M12 22c-4.97 0-9-2.69-9-6v-.18C4.32 12.47 7.6 10 8 6c.02-.17.17-.3.34-.28C10.09 5.96 12 8 12 8s1.91-2.04 3.66-2.28c.17-.02.32.11.34.28.4 4 3.68 6.47 5 9.82V16c0 3.31-4.03 6-9 6z"/>
              </svg>
              <div style={{ flex:1 }}>
                <div style={{ fontSize:13, fontWeight:700, color:T.coral }}>
                  {torchDraft.offerMode ? "Offering a Torch" : "Pass the Torch"}
                </div>
                <div style={{ fontSize:11, color:"rgba(255,255,255,0.4)", marginTop:1 }}>
                  {torchDraft.offerMode
                    ? `${torchDraft.amount} pt pick · spotting ${torchDraft.recipientName}`
                    : `${torchDraft.amount} pt pick · funded by ${torchDraft.lenderName}`}
                </div>
              </div>
            </div>
          )}
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:14 }}>
            <div style={{ display:"flex", alignItems:"center", gap:10 }}>
              {/* Flat title — was using a gradient text fill that fought the
                  rest of the redesign's flat-typography direction. */}
              <span style={{ fontFamily:FD, fontSize:22, fontWeight:700, color:"#fff", letterSpacing:"-0.4px" }}>
                Pick Slip
              </span>
              {step === "slip" && items.length > 0 && (
                <>
                  <span style={{
                    fontSize:11, fontWeight:700, fontFamily:FM, fontVariantNumeric:"tabular-nums",
                    color:"rgba(255,255,255,0.55)", padding:"3px 9px", borderRadius:999,
                    background:"rgba(255,255,255,0.06)", border:"1px solid rgba(255,255,255,0.06)",
                  }}>
                    {items.length}
                  </span>
                  <button
                    onClick={() => { haptic.light(); setPicks({}); setWagers({}); }}
                    title="Clear all picks"
                    style={{
                      fontSize:11, fontWeight:600, color:"rgba(255,255,255,0.40)",
                      background:"none", border:"none",
                      borderRadius:8, padding:"3px 6px", cursor:"pointer",
                      transition:"color 0.15s",
                    }}
                    onMouseEnter={(e)=>(e.currentTarget.style.color="rgba(255,255,255,0.85)")}
                    onMouseLeave={(e)=>(e.currentTarget.style.color="rgba(255,255,255,0.40)")}
                  >
                    Clear
                  </button>
                </>
              )}
            </div>
            <button onClick={() => requestClose()} aria-label="Close" style={{
              width:36, height:36, minWidth:36, minHeight:36, borderRadius:"50%",
              background:"rgba(255,255,255,0.04)", border:"1px solid rgba(255,255,255,0.06)",
              cursor:"pointer", color:"rgba(255,255,255,0.55)",
              display:"flex", alignItems:"center", justifyContent:"center",
              transition:"background 0.15s",
            }}
              onMouseEnter={(e)=>(e.currentTarget.style.background="rgba(255,255,255,0.08)")}
              onMouseLeave={(e)=>(e.currentTarget.style.background="rgba(255,255,255,0.04)")}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
                <path d="M18 6L6 18M6 6l12 12"/>
              </svg>
            </button>
          </div>

          {/* Straight / Parlay mode toggle — wager leagues with 2+ picks.
              MUST carry !isNonWagerMode. Pickem/survivor have no parlays: the
              toggle was previously gated on item count ALONE, so a pickem slip
              with 2+ picks rendered a live "Parlay" button. Tapping it called
              setSlipMode("parlay"), and the guard effect immediately forced it
              back to "straight" — the button visibly bounced back and did
              nothing. The guard is a safety net for a mid-slip league switch,
              not a substitute for not rendering the control. */}
          {step === "slip" && !isNonWagerMode && items.length >= 2 && (
            <div style={{
              display:"flex", marginBottom:14, padding:3,
              background:"rgba(255,255,255,0.04)", border:"1px solid rgba(255,255,255,0.06)",
              borderRadius:12,
            }}>
              {[["straight","Straight"], ["parlay","Parlay"]].map(([mode, label]) => (
                <button key={mode} onClick={() => setSlipMode(mode)} className="no-press" style={{
                  flex:1, padding:"8px 0", border:"none", cursor:"pointer",
                  borderRadius:9,
                  background: slipMode===mode ? "#fff" : "transparent",
                  color: slipMode===mode ? "#08080A" : "rgba(255,255,255,0.55)",
                  fontSize:13, fontWeight:slipMode===mode ? 700 : 600, transition:"all 0.18s",
                  boxShadow: slipMode===mode ? "0 2px 8px rgba(0,0,0,0.25), 0 1px 0 rgba(255,255,255,0.5) inset" : "none",
                  letterSpacing:"-0.005em",
                }}>{label}</button>
              ))}
            </div>
          )}

          {/* League selector + Balance bar */}
          {step === "slip" && (
            <>
              {/* Practice mode indicator — shown when user has no leagues */}
              {userLeagues.length === 0 && (
                <div style={{
                  marginBottom:10, padding:"10px 14px", borderRadius:10,
                  background:"rgba(99,102,241,0.08)", border:"1px solid rgba(99,102,241,0.15)",
                  display:"flex", alignItems:"center", gap:10,
                }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#818CF8" strokeWidth="1.8" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="6"/><circle cx="12" cy="12" r="2"/></svg>
                  <div style={{ flex:1 }}>
                    <div style={{ fontSize:12, fontWeight:700, color:"rgba(255,255,255,0.8)" }}>Practice mode</div>
                    <div style={{ fontSize:10, color:"rgba(255,255,255,0.45)", lineHeight:1.4 }}>100 pts to learn the picks · join a league when ready</div>
                  </div>
                  {/* Points pill — shows the user their global practice balance so they
                      know what they have to wager with even before opening the league selector. */}
                  <div style={{
                    flexShrink:0, textAlign:"right",
                    padding:"4px 10px", borderRadius:8,
                    background:"rgba(255,255,255,0.05)",
                    border:"1px solid rgba(255,255,255,0.06)",
                  }}>
                    <div style={{
                      fontFamily:FD, fontSize:14, fontWeight:700, color:"#fff",
                      fontVariantNumeric:"tabular-nums", letterSpacing:"-0.3px", lineHeight:1,
                    }}>
                      {(balance ?? 0).toLocaleString()}
                    </div>
                    <div style={{ fontSize:9, fontWeight:700, color:"rgba(255,255,255,0.40)", letterSpacing:"0.10em", marginTop:3 }}>PTS</div>
                  </div>
                </div>
              )}

              {/* ── League selector ── */}
              {/* Always shown so new users (with zero leagues) can still choose "Just for Fun". */}
              {(
                <div style={{ position:"relative", marginBottom:14 }}>
                  {/* Selected league button */}
                  <button onClick={() => !torchDraft && setShowLeagueDropdown(v => !v)} className="no-press" style={{
                    width:"100%", padding:"13px 14px",
                    borderRadius:14, cursor: torchDraft ? "default" : "pointer",
                    background: overBudget ? "rgba(255,107,61,0.08)" : "rgba(255,255,255,0.03)",
                    border: `1px solid ${overBudget ? "rgba(255,107,61,0.30)" : showLeagueDropdown ? "rgba(255,107,61,0.40)" : "rgba(255,255,255,0.06)"}`,
                    display:"flex", alignItems:"center", gap:12,
                    transition:"all 0.15s", position:"relative",
                    boxShadow: showLeagueDropdown ? "0 0 0 3px rgba(255,107,61,0.10)" : "none",
                  }}>
                    {/* League icon */}
                    <div style={{
                      width:38, height:38, borderRadius:11, flexShrink:0, position:"relative", overflow:"hidden",
                      background: activeLeagueObj ? "rgba(255,107,61,0.12)" : "rgba(255,255,255,0.05)",
                      border: `1px solid ${activeLeagueObj ? "rgba(255,107,61,0.25)" : "rgba(255,255,255,0.06)"}`,
                      display:"flex", alignItems:"center", justifyContent:"center",
                    }}>
                      <div style={{ position:"absolute", inset:0, background:"radial-gradient(circle at 30% 25%, rgba(255,255,255,0.10), transparent 55%)" }}/>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={activeLeagueObj ? "#FF8A65" : "rgba(255,255,255,0.5)"} strokeWidth="2" strokeLinecap="round" style={{ position:"relative" }}>
                        <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6"/><path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18"/><path d="M4 22h16"/><path d="M18 2H6v7a6 6 0 0 0 12 0V2Z"/>
                      </svg>
                    </div>
                    {/* League name + members */}
                    <div style={{ flex:1, minWidth:0, textAlign:"left" }}>
                      <div style={{ fontSize:14, fontWeight:600, color:"#fff", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", letterSpacing:"-0.005em" }}>
                        {activeLeagueObj?.name || "Select league"}
                      </div>
                      <div style={{ fontSize:11, color:"rgba(255,255,255,0.40)", marginTop:2 }}>
                        {activeLeagueObj ? `${activeLeagueObj.memberCount || "—"} ${activeLeagueObj.memberCount === 1 ? "member" : "members"}` : "Tap to choose"}
                      </div>
                    </div>
                    {/* Balance — or, in pickem, the pick count. A pickem league
                        has no ledger, so this rendered a green "0 AVAILABLE"
                        next to a full slip: it reads as "you have no points,
                        that's why you can't submit", which is exactly the wrong
                        story. The number that matters in pickem is how many
                        games you've picked. */}
                    <div style={{ textAlign:"right", flexShrink:0 }}>
                      <div style={{
                        fontFamily:FD, fontSize:20, fontWeight:700,
                        color: isNonWagerMode ? T.ink : overBudget ? T.coral : (leagueBalance??0) >= 0 ? "#4ade80" : "#f87171",
                        letterSpacing:"-0.5px", fontVariantNumeric:"tabular-nums", lineHeight:1,
                      }}>
                        {/* In fun mode, show the user's global practice balance — not spendable (which is 0 in fun mode by design). */}
                        {isNonWagerMode ? items.length : (isFunMode ? balance : spendable).toLocaleString()}
                      </div>
                      <div style={{ fontSize:9.5, fontWeight:700, color: isNonWagerMode ? "rgba(255,255,255,0.30)" : overBudget ? T.coral : "rgba(255,255,255,0.30)", letterSpacing:"0.10em", textTransform:"uppercase", marginTop:3 }}>
                        {isNonWagerMode ? (items.length === 1 ? "pick" : "picks") : isFunMode ? "your pts" : "available"}
                      </div>
                    </div>
                    {/* Chevron */}
                    {!torchDraft && (
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.40)" strokeWidth="2.5" style={{ flexShrink:0, transform: showLeagueDropdown ? "rotate(180deg)" : "rotate(0)", transition:"transform 0.2s" }}>
                        <polyline points="6 9 12 15 18 9"/>
                      </svg>
                    )}
                  </button>

                  {/* Dropdown list */}
                  {showLeagueDropdown && !torchDraft && (
                    <>
                      <div onClick={() => setShowLeagueDropdown(false)} style={{ position:"fixed", inset:0, zIndex:81 }}/>
                      <div style={{
                        position:"absolute", top:"calc(100% + 6px)", left:0, right:0, zIndex:82,
                        background:"#1A1C26", border:"1px solid rgba(255,255,255,0.08)",
                        borderRadius:14, overflow:"hidden",
                        boxShadow:"0 16px 48px rgba(0,0,0,0.5), 0 1px 0 rgba(255,255,255,0.04) inset",
                        animation:"fadeUp 0.15s ease both",
                        maxHeight:240, overflowY:"auto", minHeight:0, WebkitOverflowScrolling:"touch", overscrollBehavior:"contain",
                      }}>
                        {/* Just for Fun option — shows the user's global practice balance.
                            Was previously hardcoded "FREE"; now reflects the actual pts
                            (1,000 starting balance + win/loss adjustments). */}
                        <button onClick={() => {
                          setSelectedLeague("__fun__");
                          setShowLeagueDropdown(false);
                          setWagers(Object.fromEntries(items.map(([k]) => [k, 0])));
                        }} style={{
                          width:"100%", padding:"12px 14px",
                          display:"flex", alignItems:"center", gap:11,
                          background: selectedLeague === "__fun__" ? "rgba(255,140,0,0.06)" : "transparent",
                          border:"none", borderBottom:"1px solid rgba(255,255,255,0.04)",
                          cursor:"pointer", textAlign:"left",
                        }}>
                          <div style={{
                            width:18, height:18, borderRadius:"50%", flexShrink:0,
                            background: selectedLeague === "__fun__" ? "rgba(255,215,0,0.20)" : "transparent",
                            border: selectedLeague === "__fun__" ? "5px solid #FFD700" : "1.5px solid rgba(255,255,255,0.20)",
                            transition:"all 0.15s",
                          }}/>
                          <div style={{ flex:1, minWidth:0 }}>
                            <div style={{ fontSize:13, fontWeight:600, color: selectedLeague === "__fun__" ? "#FFD700" : "rgba(255,255,255,0.85)" }}>Practice</div>
                            <div style={{ fontSize:11, color:"rgba(255,255,255,0.35)", marginTop:1 }}>Use your practice pts · no league required</div>
                          </div>
                          <div style={{ fontSize:14, fontWeight:700, fontFamily:FM, color: selectedLeague === "__fun__" ? "#FFD700" : "rgba(255,255,255,0.65)", fontVariantNumeric:"tabular-nums" }}>
                            {(balance ?? 0).toLocaleString()}
                          </div>
                        </button>
                        {compatibleLeagues.length === 0 && (
                          <div style={{ padding:"16px", textAlign:"center", fontSize:12, color:"rgba(255,255,255,0.35)" }}>No leagues match this sport.</div>
                        )}
                        {compatibleLeagues.map((l, li) => {
                          const isSelected = l.id === selectedLeague;
                          const lBal = l.balance ?? l.startingBalance ?? 0;
                          return (
                            <button key={l.id} onClick={() => {
                              setSelectedLeague(l.id);
                              setShowLeagueDropdown(false);
                              const newBal = l.balance ?? 0;
                              const even = items.length > 0 ? Math.floor(Math.max(0, newBal) / items.length) : 0;
                              setWagers(Object.fromEntries(items.map(([k]) => [k, even])));
                            }} style={{
                              width:"100%", padding:"12px 14px",
                              display:"flex", alignItems:"center", gap:11,
                              background: isSelected ? "rgba(255,107,61,0.06)" : "transparent",
                              border:"none", borderBottom: li < compatibleLeagues.length-1 ? "1px solid rgba(255,255,255,0.04)" : "none",
                              cursor:"pointer", transition:"background 0.1s",
                              textAlign:"left",
                            }}>
                              <div style={{
                                width:18, height:18, borderRadius:"50%", flexShrink:0,
                                background: isSelected ? "rgba(255,107,61,0.20)" : "transparent",
                                border: isSelected ? `5px solid ${T.coral}` : "1.5px solid rgba(255,255,255,0.20)",
                                transition:"all 0.15s",
                              }}/>
                              <div style={{ flex:1, minWidth:0 }}>
                                <div style={{ fontSize:13, fontWeight: isSelected ? 700 : 600, color: isSelected ? "#fff" : "rgba(255,255,255,0.85)", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", letterSpacing:"-0.005em" }}>
                                  {l.name}
                                </div>
                              </div>
                              {(l.scoringMode === "pickem" || l.scoringMode === "survivor") ? (
                                <span style={{ fontSize:9, fontWeight:700, letterSpacing:"0.08em", textTransform:"uppercase", flexShrink:0, color: isSelected ? "#4ade80" : "rgba(255,255,255,0.45)" }}>
                                  {l.scoringMode === "pickem" ? "Pick'em" : "Survivor"}
                                </span>
                              ) : (
                              <span style={{
                                fontFamily:FM, fontSize:13, fontWeight:700, flexShrink:0, fontVariantNumeric:"tabular-nums",
                                color: isSelected ? "#4ade80" : lBal >= 0 ? "rgba(255,255,255,0.45)" : "#f87171",
                              }}>{lBal.toLocaleString()}</span>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {/* Body */}
        <div style={{ overflowY:"auto", flex:1, minHeight:0, WebkitOverflowScrolling:"touch", overscrollBehavior:"contain", padding:"0 20px" }}>
          {/* step==="done" / succeeded guards: post-submit paths defer the
              picks clear, but if items ever empty out early again this branch
              must NOT shadow the done screen or the checkmark's confirm view
              (that shadowing was the original wrong-content flash). */}
          {(items.length === 0 && step !== "done" && !succeeded) ? (
            <div style={{ padding:"40px 0", textAlign:"center" }}>
              {/* Was: 🎯 emoji. Replaced with line-art target so it matches the rest of the icon system. */}
              <div style={{
                width:54, height:54, borderRadius:16, margin:"0 auto 14px",
                background:"rgba(255,255,255,0.03)", border:"1px solid rgba(255,255,255,0.06)",
                display:"flex", alignItems:"center", justifyContent:"center",
              }}>
                <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.3)" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"/>
                  <circle cx="12" cy="12" r="6"/>
                  <circle cx="12" cy="12" r="2"/>
                </svg>
              </div>
              <div style={{ fontSize:16, fontWeight:700, color:"#fff", letterSpacing:"-0.01em" }}>Slip's empty</div>
              <div style={{ fontSize:13, color:"rgba(255,255,255,0.45)", marginTop:6, lineHeight:1.5, maxWidth:260, margin:"6px auto 0" }}>Tap any odds on the Games tab and they'll show up here.</div>
              <button onClick={() => { requestClose(); }} className="no-press" style={{
                marginTop:20, padding:"11px 22px",
                background:T.coral,
                border:"none", borderRadius:12,
                color:"#fff", fontSize:13, fontWeight:700, cursor:"pointer",
                boxShadow:"0 1px 0 rgba(255,255,255,0.2) inset, 0 4px 14px -2px rgba(255,107,61,0.40)",
                letterSpacing:"-0.005em",
                transition:"transform 0.12s",
              }}
                onMouseDown={(e)=>(e.currentTarget.style.transform="scale(0.97)")}
                onMouseUp={(e)=>(e.currentTarget.style.transform="scale(1)")}
                onMouseLeave={(e)=>(e.currentTarget.style.transform="scale(1)")}
                onTouchStart={(e)=>(e.currentTarget.style.transform="scale(0.97)")}
                onTouchEnd={(e)=>(e.currentTarget.style.transform="scale(1)")}
              >Browse games</button>
            </div>
          ) : step === "done" ? (
            <div style={{ padding:"40px 0", textAlign:"center" }}>
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke={T.coral} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom:12, display:"inline-block" }}>
                <path d="M12 2C12 2 5 9 5 14a7 7 0 0014 0c0-5-7-12-7-12z"/>
              </svg>
              <div style={{ fontFamily:FD, fontSize:20, fontWeight:700, marginBottom:6 }}>
                {slipMode === "parlay" ? "Parlay locked in" : "Picks locked in"}
              </div>
              <div style={{ fontSize:13, color:T.inkSec }}>
                {slipMode === "parlay"
                  ? `${parlayWager} pts wagered · ${parlayPayout} pts if all ${items.length} legs hit`
                  : `${totalWagered} pts wagered · ${totalPotential} pts potential return`}
              </div>
            </div>
          ) : step === "confirm" ? (
            /* -- CONFIRM SCREEN -- */
            <div style={{ paddingTop:4 }}>
              {/* Confirm header */}
              <div style={{ marginBottom:18 }}>
                <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:12 }}>
                  <span style={{
                    fontSize:10, fontWeight:700, letterSpacing:"0.16em",
                    color:"#fff", textTransform:"uppercase",
                    background:T.coral, padding:"3px 10px", borderRadius:20,
                  }}>{slipMode === "parlay" ? "Parlay" : "Review"}</span>
                  <div style={{ flex:1, height:"1px", background:T.border }} />
                </div>
                <div style={{ fontFamily:FD, fontSize:24, fontWeight:700, letterSpacing:"-0.5px", color:"#fff", marginBottom:5 }}>
                  {slipMode === "parlay" ? `${items.length}-Leg Parlay` : "Confirm your picks"}
                </div>
                <div style={{ fontSize:12, fontWeight:600, color:"rgba(255,255,255,0.45)", lineHeight:1.5 }}>
                  {slipMode === "parlay"
                    ? `All ${items.length} legs need to hit. No take-backs once you submit.`
                    : "No take-backs once you submit. Make sure it's the one."}
                </div>
              </div>
              {slipMode === "parlay" ? (
                // Parlay confirm summary
                <div>
                  {items.map(([k, p]) => {
                    const { displayLabel } = slipDisplay(p);
                    return (
                      <div key={k} style={{
                        display:"flex", alignItems:"center", gap:10,
                        padding:"10px 12px", marginBottom:6,
                        background:"rgba(255,255,255,0.04)", borderRadius:10,
                        border:"1px solid rgba(255,255,255,0.08)",
                      }}>
                        <div style={{ flex:1, minWidth:0 }}>
                          <div style={{ fontSize:13, fontWeight:700, color:"rgba(255,255,255,0.9)" }}>{displayLabel}</div>
                          <div style={{ fontSize:11, color:"rgba(255,255,255,0.45)" }}>{p.sport} · {p.odds > 0 ? `+${p.odds}` : p.odds}</div>
                        </div>
                      </div>
                    );
                  })}
                  <div style={{
                    padding:"14px 16px", borderRadius:12, marginTop:8, marginBottom:16,
                    background:"rgba(255,107,61,0.07)",
                    border:"1px solid rgba(255,107,61,0.25)",
                    display:"flex", justifyContent:"space-between", alignItems:"center",
                  }}>
                    <div>
                      <div style={{ fontSize:10, fontWeight:700, color:"rgba(255,255,255,0.4)",
                        textTransform:"uppercase", letterSpacing:"0.1em", marginBottom:4 }}>Wager</div>
                      <div style={{ fontFamily:FD, fontSize:26, fontWeight:700, color:"#fff", lineHeight:1 }}>
                        {parlayWager}<span style={{ fontSize:13, color:"rgba(255,255,255,0.4)", marginLeft:4 }}>pts</span>
                      </div>
                    </div>
                    <div style={{ textAlign:"right" }}>
                      <div style={{ fontSize:10, fontWeight:700, color:"rgba(255,255,255,0.4)",
                        textTransform:"uppercase", letterSpacing:"0.1em", marginBottom:4 }}>To win</div>
                      <div style={{ fontFamily:FD, fontSize:26, fontWeight:700, color:"#4ade80", lineHeight:1 }}>
                        +{parlayProfit}<span style={{ fontSize:13, color:"rgba(255,255,255,0.4)", marginLeft:4 }}>pts</span>
                      </div>
                    </div>
                  </div>
                </div>
              ) : null}
              {slipMode === "straight" && items.map(([k, p]) => {
                const w = Number(wagers[k]) || 0;
                const payout = getPayout(k);
                const profit = payout - w;
                const { pickedTeamFull, displayLabel } = slipDisplay(p);
                return (
                  <div key={k} style={{
                    display:"flex", alignItems:"center", gap:12,
                    padding:"13px 14px", marginBottom:8,
                    background:"rgba(255,255,255,0.04)",
                    borderRadius:14,
                    border:"1px solid rgba(255,255,255,0.08)",
                    borderLeft:`4px solid ${T.coral}`,
                  }}>
                    {p.type === "player_prop" && p.awayTeamFull && p.homeTeamFull ? (
                      <div style={{ display:"flex", alignItems:"center", gap:0, flexShrink:0 }}>
                        <TeamLogo name={p.awayTeamFull} sport={p.sport} size={28} />
                        <span style={{ fontSize:9, color:"rgba(255,255,255,0.36)", fontWeight:700, margin:"0 2px" }}>@</span>
                        <TeamLogo name={p.homeTeamFull} sport={p.sport} size={28} />
                      </div>
                    ) : (
                      <TeamLogo name={pickedTeamFull || p.homeTeamFull || p.awayTeamFull || p.label} sport={p.sport} size={34} />
                    )}
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ fontSize:14, fontWeight:700, color:"#fff", letterSpacing:"-0.2px", lineHeight:1.3 }}>{displayLabel}</div>
                      <div style={{ fontSize:11, color:"rgba(255,255,255,0.35)", marginTop:3, display:"flex", alignItems:"center", gap:6 }}>
                        {isNonWagerMode ? (
                          // No odds and no stake — the pick itself is the whole
                          // statement in pickem/survivor.
                          <span style={{ background:"rgba(255,255,255,0.06)", padding:"1px 7px", borderRadius:5, fontSize:9, fontWeight:700, fontFamily:FM, color:"rgba(255,255,255,0.50)" }}>To win</span>
                        ) : (<>
                          <span style={{ background:"rgba(255,255,255,0.06)", padding:"1px 7px", borderRadius:5, fontSize:9, fontWeight:700, fontFamily:FM, color:"rgba(255,255,255,0.50)" }}>{p.type}</span>
                          <span style={{ fontWeight:700, fontFamily:FM, color: (p.odds > 0) ? "#4ade80" : "rgba(255,255,255,0.55)" }}>{fmtOdds(p.odds)}</span>
                        </>)}
                      </div>
                    </div>
                    {!isNonWagerMode && (
                    <div style={{ textAlign:"right", flexShrink:0 }}>
                      <div style={{ fontSize:20, fontWeight:700, fontFamily:FM, color:"#fff", letterSpacing:"-0.5px", lineHeight:1 }}>{w.toLocaleString()}</div>
                      <div style={{ fontSize:9, fontWeight:700, fontFamily:FM, color:"rgba(255,255,255,0.40)", lineHeight:1, marginBottom:3 }}>pts</div>
                      {profit > 0 && <div style={{ fontSize:12, fontWeight:700, fontFamily:FM, color:"#4ade80" }}>+{profit.toLocaleString()}</div>}
                    </div>
                    )}
                  </div>
                );
              })}
              {/* Statement totals row — straight mode only.
                  In pickem/survivor there's nothing wagered and nothing to
                  return, so the whole row is replaced with a plain count. */}
              {slipMode === "straight" && isNonWagerMode && <div style={{
                display:"flex", justifyContent:"space-between", alignItems:"center",
                padding:"16px", borderRadius:16, marginTop:4, marginBottom:16,
                background:T.card,
                border:"1px solid rgba(255,255,255,0.06)",
              }}>
                <div>
                  <div style={{
                    fontSize:9, fontWeight:700, letterSpacing:"0.16em", fontFamily:FM,
                    textTransform:"uppercase", color:"rgba(255,255,255,0.40)", marginBottom:6,
                  }}>Picks</div>
                  <div style={{ fontFamily:FM, fontSize:30, fontWeight:700, letterSpacing:"-0.5px", color:"#fff", lineHeight:1 }}>
                    {items.length}
                  </div>
                </div>
                <div style={{ textAlign:"right", fontSize:11, color:"rgba(255,255,255,0.40)", maxWidth:180, lineHeight:1.4 }}>
                  {slipScoringMode === "survivor"
                    ? "Survive the week or you're out"
                    : "1 point for each one you get right"}
                </div>
              </div>}
              {slipMode === "straight" && !isNonWagerMode && <div style={{
                display:"flex", justifyContent:"space-between", alignItems:"center",
                padding:"16px", borderRadius:16, marginTop:4, marginBottom:16,
                background:T.card,
                border:"1px solid rgba(255,255,255,0.06)",
                position:"relative", overflow:"hidden",
              }}>
                <div style={{ position:"relative" }}>
                  <div style={{
                    fontSize:9, fontWeight:700, letterSpacing:"0.16em", fontFamily:FM,
                    textTransform:"uppercase", color:"rgba(255,255,255,0.40)", marginBottom:6,
                  }}>Total wagered</div>
                  <div style={{ fontFamily:FM, fontSize:30, fontWeight:700, letterSpacing:"-0.5px", color:"#fff", lineHeight:1 }}>
                    {totalWagered.toLocaleString()}
                    <span style={{ fontSize:12, fontWeight:600, color:"rgba(255,255,255,0.35)", marginLeft:4 }}>pts</span>
                  </div>
                </div>
                <div style={{ width:1, height:40, background:"rgba(255,255,255,0.12)" }} />
                <div style={{ textAlign:"right" }}>
                  <div style={{
                    fontSize:9, fontWeight:700, letterSpacing:"0.16em", fontFamily:FM,
                    textTransform:"uppercase", color:"rgba(255,255,255,0.40)", marginBottom:6,
                  }}>Max return</div>
                  <div style={{ fontFamily:FM, fontSize:30, fontWeight:700, letterSpacing:"-0.5px", color:"#4ade80", lineHeight:1 }}>
                    {totalPotential.toLocaleString()}
                    <span style={{ fontSize:12, fontWeight:600, color:"rgba(255,255,255,0.35)", marginLeft:4 }}>pts</span>
                  </div>
                </div>
              </div>}
            </div>
          ) : (
            /* -- SLIP (wager entry) -- */
            <div style={{ paddingTop:4 }}>

              {/* -- PARLAY PANEL -- */}
              {slipMode === "parlay" && items.length >= 2 && (() => {
                const fmtO = (o) => o == null ? "" : o > 0 ? `+${o}` : `${o}`;
                const toDecimal = (o) => o > 0 ? 1 + o / 100 : 1 + 100 / Math.abs(o);
                // Build running multiplier chain
                const chain = items.reduce((acc, [k, p], i) => {
                  const dec = toDecimal(p.odds || -110);
                  const running = i === 0 ? dec : acc[i-1].running * dec;
                  return [...acc, { k, p, dec, running }];
                }, []);
                const finalDecimal = chain[chain.length-1]?.running ?? 1;
                const finalAmerican = parlayAmerican;
                return (
                  <div>
                    {/* Live odds banner */}
                    <div style={{
                      padding:"14px 16px", borderRadius:14, marginBottom:12,
                      background:"rgba(255,107,61,0.07)",
                      border:"1px solid rgba(255,107,61,0.25)",
                    }}>
                      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start" }}>
                        <div>
                          <div style={{ fontSize:10, fontWeight:700, color:"rgba(255,255,255,0.4)",
                            textTransform:"uppercase", letterSpacing:"0.10em", marginBottom:4 }}>
                            {items.length}-Leg Parlay Odds
                          </div>
                          <div style={{ fontSize:32, fontWeight:700, color:T.coral, letterSpacing:"-1px", lineHeight:1 }}>
                            {fmtO(finalAmerican)}
                          </div>
                          <div style={{ fontSize:11, color:"rgba(255,255,255,0.35)", marginTop:4 }}>
                            {finalDecimal.toFixed(2)}× multiplier
                          </div>
                        </div>
                        {parlayWager > 0 && (
                          <div style={{ textAlign:"right" }}>
                            <div style={{ fontSize:10, fontWeight:700, color:"rgba(255,255,255,0.4)",
                              textTransform:"uppercase", letterSpacing:"0.08em", marginBottom:4 }}>
                              If all hit
                            </div>
                            <div style={{ fontSize:26, fontWeight:700, color:"#4ade80", letterSpacing:"-0.5px", lineHeight:1 }}>
                              +{parlayProfit.toLocaleString()}
                            </div>
                            <div style={{ fontSize:10, color:"rgba(255,255,255,0.42)", marginTop:3 }}>
                              {parlayPayout.toLocaleString()} total pts
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Leg-by-leg multiplier chain */}
                    <div style={{ marginBottom:12 }}>
                      {chain.map(({ k, p, dec, running }, i) => (
                        <div key={k} style={{
                          display:"flex", alignItems:"center", gap:8,
                          padding:"9px 12px", borderRadius:10, marginBottom:4,
                          background:"rgba(255,255,255,0.04)",
                          border:"1px solid rgba(255,255,255,0.07)",
                        }}>
                          {/* Connector dot */}
                          <div style={{
                            width:20, height:20, borderRadius:"50%", flexShrink:0,
                            background:"rgba(255,107,61,0.15)", border:"1px solid rgba(255,107,61,0.25)",
                            display:"flex", alignItems:"center", justifyContent:"center",
                            fontSize:9, fontWeight:700, color:T.coral,
                          }}>{i+1}</div>

                          <div style={{ flex:1, minWidth:0 }}>
                            <div style={{ fontSize:12, fontWeight:700, color:"rgba(255,255,255,0.85)",
                              whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>
                              {p.label}
                            </div>
                            <div style={{ fontSize:10, color:"rgba(255,255,255,0.35)", marginTop:1 }}>
                              {p.sport} · <span style={{ color:"rgba(255,255,255,0.55)" }}>{fmtO(p.odds || -110)}</span>
                              {" "}= <span style={{ color:"rgba(255,255,255,0.55)" }}>{dec.toFixed(2)}×</span>
                            </div>
                          </div>

                          {/* Running multiplier */}
                          <div style={{ textAlign:"right", flexShrink:0 }}>
                            <div style={{
                              fontSize:11, fontWeight:700,
                              color: i === chain.length-1 ? T.coral : "rgba(255,255,255,0.4)",
                            }}>
                              {running.toFixed(2)}×
                            </div>
                            {i < chain.length-1 && (
                              <div style={{ fontSize:9, color:"rgba(255,255,255,0.36)" }}>running</div>
                            )}
                          </div>

                          <button onClick={() => { removeItem(k); }} style={{
                            background:"none", border:"none", color:"rgba(255,255,255,0.36)",
                            fontSize:16, cursor:"pointer", padding:"4px 6px", flexShrink:0,
                          }} aria-label="Remove leg">×</button>
                        </div>
                      ))}
                    </div>

                    {/* Wager input */}
                    <div style={{
                      padding:"14px", borderRadius:12,
                      border:`1px solid rgba(255,255,255,0.10)`,
                      background:"rgba(255,255,255,0.04)", marginBottom:12,
                    }}>
                      <div style={{ fontSize:10, fontWeight:700, color:"rgba(255,255,255,0.4)",
                        textTransform:"uppercase", letterSpacing:"0.08em", marginBottom:8 }}>
                        Parlay Wager (pts)
                      </div>
                      <div style={{ position:"relative" }}>
                        <input
                          type="number" inputMode="numeric"
                          value={parlayWager || ""}
                          onChange={e => setParlayWager(Math.max(0, Math.floor(Number(e.target.value)||0)))}
                          placeholder="How much?"
                          style={{
                            width:"100%", padding:"12px 44px 12px 14px", borderRadius:10,
                            border:`1.5px solid ${parlayWager > spendable ? T.coral : "rgba(255,255,255,0.15)"}`,
                            fontSize:22, fontWeight:700, color:"#fff", background:"rgba(255,255,255,0.06)",
                            outline:"none", boxSizing:"border-box", fontFamily:"inherit",
                          }}
                        />
                        <span style={{
                          position:"absolute", right:14, top:"50%", transform:"translateY(-50%)",
                          fontSize:12, fontWeight:600, color:"rgba(255,255,255,0.42)", pointerEvents:"none",
                        }}>pts</span>
                      </div>
                      <div style={{ display:"flex", gap:6, marginTop:8 }}>
                        {[50, 100, 250, 500].filter(v => v <= spendable && v >= (leagueMinWager || 0)).map(preset => (
                          <button key={preset} onClick={() => setParlayWager(preset)} style={{
                            padding:"5px 12px", borderRadius:20,
                            border:"1px solid rgba(255,255,255,0.1)", background:"rgba(255,255,255,0.05)",
                            fontSize:11, fontWeight:700, color:"rgba(255,255,255,0.55)", cursor:"pointer",
                          }}>{preset}</button>
                        ))}
                        {spendable > 0 && (
                          <button onClick={() => setParlayWager(spendable)} style={{
                            marginLeft:"auto", padding:"5px 12px", borderRadius:20,
                            border:`1px solid ${T.coral}`, background:"rgba(255,107,61,0.1)",
                            fontSize:11, fontWeight:700, color:T.coral, cursor:"pointer",
                          }}>Max</button>
                        )}
                      </div>
                      {parlayWager > spendable && (
                        <div style={{ marginTop:8, padding:"8px 12px", borderRadius:8,
                          background:T.redDim, border:`1px solid ${T.coral}`,
                          fontSize:12, color:T.coral, fontWeight:600 }}>
                          Insufficient balance — you have {spendable.toLocaleString()} pts
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}

              {/* -- PARLAY TEASER: show when 2+ picks in straight mode --
                  Hidden in pickem/survivor: a parlay is a wager construct
                  (combined odds on a single shared stake), which has no meaning
                  without a stake. This is the only entry point into parlay mode,
                  so hiding it keeps those leagues on the straight path. */}
          {!isNonWagerMode && slipMode === "straight" && items.length >= 2 && (() => {
            const teaserAmerican = calcParlayAmericanOdds(items.map(([, p]) => p));
            const teaserDecimal = calcParlayDecimal(items.map(([, p]) => p));
            const fmtO = (o) => o == null ? "" : o > 0 ? `+${o}` : `${o}`;
            return (
              <div
                onClick={() => setSlipMode("parlay")}
                style={{
                  margin:"0 0 12px", padding:"10px 14px", borderRadius:10, cursor:"pointer",
                  background:"rgba(255,107,61,0.06)",
                  border:"1px solid rgba(255,107,61,0.18)",
                  display:"flex", alignItems:"center", justifyContent:"space-between",
                  transition:"background 0.15s",
                }}
              >
                <div>
                  <div style={{ fontSize:10, fontWeight:700, color:"rgba(255,107,61,0.7)", letterSpacing:"0.08em", textTransform:"uppercase" }}>
                    Parlay available
                  </div>
                  <div style={{ fontSize:11, color:"rgba(255,255,255,0.4)", marginTop:2 }}>
                    {items.length} legs combined = <span style={{ color:T.coral, fontWeight:700 }}>{fmtO(teaserAmerican)}</span> ({teaserDecimal.toFixed(2)}×)
                  </div>
                </div>
                <span style={{ fontSize:11, fontWeight:700, color:T.coral }}>Switch →</span>
              </div>
            );
          })()}

          {/* -- STRAIGHT PICKS -- */}
              {/* Split evenly shortcut */}
              {slipMode === "straight" && items.length > 1 && spendable > 0 && (
                <button onClick={splitEvenly} style={{
                  width:"100%", padding:"9px", marginBottom:12,
                  background:"transparent", border:`1.5px dashed ${T.border}`,
                  borderRadius:10, fontSize:12, fontWeight:700,
                  color:T.inkSec, cursor:"pointer",
                }}>
                  ↔ Split {spendable} pts evenly across {items.length} picks
                </button>
              )}

              {slipMode === "straight" && items.map(([k, p]) => {
                const w = Number(wagers[k]) || 0;
                const payout = getPayout(k);
                const profit = payout - w;
                const avail = spendable - (totalWagered - (Number(wagers[k])||0));
                const { pickedTeamFull, displayLabel } = slipDisplay(p);
                return (
                  <div key={k} style={{
                    marginBottom:10, borderRadius:16,
                    background:"rgba(255,255,255,0.03)",
                    border:"1px solid rgba(255,255,255,0.08)",
                    overflow:"hidden", backdropFilter:"blur(8px)",
                  }}>
                    {/* Coral accent bar + pick info */}
                    <div style={{
                      borderLeft:`3px solid ${T.coral}`,
                      padding:"12px 14px 10px",
                    }}>
                      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start" }}>
                        <div style={{ display:"flex", gap:10, flex:1, minWidth:0 }}>
                          {p.type === "player_prop" && p.awayTeamFull && p.homeTeamFull ? (
                            <div style={{ display:"flex", alignItems:"center", gap:0, flexShrink:0 }}>
                              <TeamLogo name={p.awayTeamFull} sport={p.sport} size={26} />
                              <span style={{ fontSize:9, color:"rgba(255,255,255,0.36)", fontWeight:700, margin:"0 1px" }}>@</span>
                              <TeamLogo name={p.homeTeamFull} sport={p.sport} size={26} />
                            </div>
                          ) : (
                            <TeamLogo name={pickedTeamFull || p.homeTeamFull || p.awayTeamFull || p.label} sport={p.sport} size={32} />
                          )}
                          <div style={{ flex:1, minWidth:0 }}>
                            <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:3 }}>
                              <div style={{ fontSize:14, fontWeight:700, color:"#fff", lineHeight:1.3 }}>{displayLabel}</div>
                              {p.isLiveBet && (
                                <span style={{
                                  fontSize:9, fontWeight:800, color:"#fff", fontFamily:FM,
                                  background:T.green, padding:"2px 6px", borderRadius:4,
                                  letterSpacing:"0.08em", flexShrink:0,
                                }}>LIVE</span>
                              )}
                            </div>
                            <div style={{ fontSize:11, color:"rgba(255,255,255,0.35)", display:"flex", alignItems:"center", gap:5, flexWrap:"wrap" }}>
                              {p.gameLabel && <span>{p.gameLabel}</span>}
                              <span style={{ background:"rgba(255,255,255,0.06)", padding:"1px 7px", borderRadius:5, fontSize:9, fontWeight:800, color:"rgba(255,255,255,0.50)", fontFamily:FM }}>{isNonWagerMode && p.type === "ml" ? "WINNER" : p.type?.toUpperCase()}</span>
                              {p.odds && <span style={{ fontFamily:FM, fontWeight:800, fontSize:11, color: (p.odds > 0) ? "#4ade80" : "rgba(255,255,255,0.60)" }}>{fmtOdds(p.odds)}</span>}
                            </div>
                          </div>
                        </div>
                        <button onClick={() => { haptic.light(); removeItem(k); }} style={{
                          width:36, height:36, minWidth:44, minHeight:44, borderRadius:"50%",
                          border:"1px solid rgba(255,255,255,0.08)", background:"rgba(255,255,255,0.04)",
                          cursor:"pointer", color:"rgba(255,255,255,0.42)", fontSize:14, flexShrink:0,
                          display:"flex", alignItems:"center", justifyContent:"center", marginLeft:8,
                        }} aria-label="Remove pick">×</button>
                      </div>
                    </div>

                    {/* Wager input row — visible in all modes including fun mode
                        so users can enter a stake to track practice P&L.
                        Hidden entirely in pickem/survivor: there is no stake, and
                        the server rejects any pick carrying a non-zero wager. */}
                    {!isNonWagerMode && (<>
                    <div style={{ padding:"0 14px 10px", display:"flex", alignItems:"center", gap:8 }}>
                      <div style={{ flex:1, position:"relative" }}>
                        <input
                          type="number" inputMode="numeric" pattern="[0-9]*" min="0" max={Math.max(0, avail)}
                          value={wagers[k] || ""}
                          onChange={e => setWager(k, e.target.value)}
                          placeholder="How much?"
                          style={{
                            width:"100%", padding:"10px 42px 10px 14px",
                            background:"rgba(255,255,255,0.05)",
                            border:"1.5px solid rgba(255,255,255,0.10)",
                            borderRadius:12, color:"#fff", fontSize:18, fontWeight:700,
                            fontFamily:FM, outline:"none", boxSizing:"border-box",
                            WebkitAppearance:"none",
                          }}
                        />
                        <span style={{
                          position:"absolute", right:12, top:"50%", transform:"translateY(-50%)",
                          fontSize:10, fontWeight:700, fontFamily:FM, color:"rgba(255,255,255,0.40)", pointerEvents:"none",
                        }}>pts</span>
                      </div>
                      <button onClick={() => { haptic.light(); goAllIn(k); }} style={{
                        padding:"0 14px", height:46, borderRadius:12, flexShrink:0,
                        background:"rgba(255,255,255,0.06)", border:"1px solid rgba(255,255,255,0.10)",
                        fontSize:11, fontWeight:800, fontFamily:FM, color:"rgba(255,255,255,0.55)", cursor:"pointer",
                      }}>ALL IN</button>
                    </div>

                    {/* Quick presets — only show values at or above the league
                        minimum (no "+50" in a 200-min league) and within balance. */}
                    <div style={{ display:"flex", gap:5, padding:"0 14px 10px" }}>
                      {[50, 100, 250, 500].filter(preset => preset <= avail && preset >= (leagueMinWager || 0)).slice(0, 3).map(preset => (
                        <button key={preset} onClick={() => { haptic.light(); setWager(k, String(preset)); }} style={{
                          padding:"5px 14px", borderRadius:20,
                          border:"1px solid rgba(255,255,255,0.08)", background:"rgba(255,255,255,0.04)",
                          fontSize:11, fontWeight:700, fontFamily:FM, color:"rgba(255,255,255,0.45)", cursor:"pointer",
                        }}>{preset}</button>
                      ))}
                      {avail > 0 && (
                        <button onClick={() => { haptic.light(); goAllIn(k); }} style={{
                          marginLeft:"auto", padding:"5px 14px", borderRadius:20,
                          border:"1px solid rgba(255,107,61,0.15)", background:"rgba(255,107,61,0.06)",
                          fontSize:11, fontWeight:700, fontFamily:FM, color:T.coral, cursor:"pointer",
                        }}>Max</button>
                      )}
                    </div>
                    </>)}

                    {/* Payout preview — wager mode only (no stake, no return). */}
                    {!isNonWagerMode && w > 0 && (
                      <div style={{
                        display:"flex", justifyContent:"space-between", alignItems:"center",
                        padding:"10px 14px",
                        background: profit > 0 ? "rgba(74,222,128,0.05)" : "rgba(255,255,255,0.02)",
                        borderTop:"1px solid rgba(255,255,255,0.04)",
                      }}>
                        <span style={{ fontSize:11, fontFamily:FM, fontWeight:600, color:"rgba(255,255,255,0.42)" }}>RETURN</span>
                        <span style={{ fontSize:14, fontWeight:800, fontFamily:FM, color:"#4ade80" }}>
                          {payout.toLocaleString()} <span style={{ fontWeight:600, fontSize:10, color:"rgba(255,255,255,0.40)" }}>({profit > 0 ? "+" : ""}{profit.toLocaleString()})</span>
                        </span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer actions */}
        {step === "slip" && items.length > 0 && (
          <div style={{ padding:"12px 20px 4px", flexShrink:0 }}>
            {error && (
              <div style={{ fontSize:13, color:T.red, background:T.redDim,
                padding:"8px 12px", borderRadius:8, marginBottom:8 }}>{error}</div>
            )}
            {overBudget && (
              <div style={{ fontSize:12, color:T.coral, textAlign:"center", marginBottom:8, fontWeight:600 }}>
                {slipMode === "parlay"
                  ? "⚠ Parlay wager exceeds your balance"
                  : "⚠ Wagers exceed your balance — reduce to continue"}
              </div>
            )}
            <button
              onClick={() => {
                if (slipMode === "parlay") {
                  if ((parlayWager > 0 || isFunMode) && (isFunMode || parlayWager <= spendable) && items.length >= 2) setStep("confirm");
                } else {
                  // isNonWagerMode is the fix for a hard blocker: in a pickem
                  // league totalWagered is ALWAYS 0 (picks cost nothing) and
                  // isFunMode is false (it's a real league), so the old
                  // `totalWagered > 0 || isFunMode` condition was never true and
                  // this button did nothing — 16 valid picks, no way to submit.
                  // The submit handler itself already exempts pickem
                  // (see the `!isNonWagerMode` guard in the submit path); only
                  // the control that reaches it was missed.
                  if (!overBudget && (totalWagered > 0 || isFunMode || isNonWagerMode)) setStep("confirm");
                }
              }}
              disabled={slipMode === "parlay"
                ? ((parlayWager <= 0 && !isFunMode) || (!isFunMode && parlayWager > spendable) || items.length < 2)
                : (overBudget || (totalWagered === 0 && !isFunMode && !isNonWagerMode))}
              style={{
                ...primaryBtn, marginTop:0,
                opacity: (slipMode === "parlay"
                  ? ((parlayWager <= 0 && !isFunMode) || (!isFunMode && parlayWager > spendable) || items.length < 2)
                  : (overBudget || (totalWagered === 0 && !isFunMode && !isNonWagerMode))) ? 0.45 : 1,
                background: slipMode === "parlay"
                  ? `linear-gradient(135deg, #1A1C26, #13151E)`
                  : `linear-gradient(135deg, ${T.coral}, ${T.coralDeep})`
              ,boxShadow: slipMode !== "parlay" ? "0 8px 32px rgba(255,107,61,0.35), 0 0 20px rgba(255,107,61,0.12), 0 2px 0 rgba(255,255,255,0.12) inset" : "none",
              }}>
              {slipMode === "parlay"
                ? `Review ${items.length}-Leg Parlay · ${parlayWager} pts →`
                : torchDraft
                ? `Review Pick · ${torchDraft.amount} pts →`
                : (isNonWagerMode || (isFunMode && totalWagered === 0))
                ? `Review ${items.length} Pick${items.length > 1 ? "s" : ""} · Free →`
                : `Review ${items.length} Pick${items.length > 1 ? "s" : ""} · ${totalWagered} pts →`}
            </button>
            <p style={{ fontSize:11, color:T.inkMuted, textAlign:"center", marginTop:8 }}>No money. Just bragging rights.</p>
          </div>
        )}

        {step === "confirm" && (
          <div style={{ padding:"12px 20px 4px", flexShrink:0 }}>
            {error && (
              <div style={{ fontSize:13, color:T.red, background:"rgba(199,101,97,0.10)", border:"1px solid rgba(199,101,97,0.20)",
                padding:"10px 14px", borderRadius:12, marginBottom:10, lineHeight:1.5 }}>{error}</div>
            )}
            <div style={{ display:"flex", gap:10 }}>
              <button onClick={() => { setStep("slip"); setError(""); }} className="no-press" style={{
                flex:1, padding:"14px",
                background:"rgba(255,255,255,0.04)",
                border:"1px solid rgba(255,255,255,0.08)", borderRadius:14,
                fontSize:14, fontWeight:600, color:"rgba(255,255,255,0.70)", cursor:"pointer",
                letterSpacing:"-0.005em",
                display:"flex", alignItems:"center", justifyContent:"center", gap:6,
                transition:"background 0.15s, transform 0.12s",
              }}
                onMouseDown={(e)=>(e.currentTarget.style.transform="scale(0.98)")}
                onMouseUp={(e)=>(e.currentTarget.style.transform="scale(1)")}
                onMouseLeave={(e)=>(e.currentTarget.style.transform="scale(1)")}
                onTouchStart={(e)=>(e.currentTarget.style.transform="scale(0.98)")}
                onTouchEnd={(e)=>(e.currentTarget.style.transform="scale(1)")}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><polyline points="15 18 9 12 15 6"/></svg>
                Edit
              </button>
              <button onClick={() => { haptic.medium(); handleSubmit(); }} disabled={submitting || succeeded} className={succeeded ? "no-press" : "no-press"} style={{
                flex:2.5, padding:"14px",
                background: succeeded
                  ? "linear-gradient(180deg, #4ade80 0%, #22c55e 50%, #16a34a 100%)"
                  : slipReady
                    ? "linear-gradient(180deg, #FF6A2F 0%, #FF5A1F 50%, #E84E15 100%)"
                    : "linear-gradient(180deg, #3A2A22 0%, #34251E 100%)",
                border:"none", borderRadius:14,
                color: succeeded || slipReady ? "#fff" : "rgba(255,255,255,0.45)",
                fontSize:15, fontWeight:700,
                cursor: submitting || succeeded ? "default" : "pointer",
                opacity: submitting && !succeeded ? 0.92 : 1,
                boxShadow: succeeded
                  ? "0 1px 0 rgba(255,255,255,0.25) inset, 0 4px 20px -2px rgba(34,197,94,0.45)"
                  : slipReady
                    ? "0 1px 0 rgba(255,255,255,0.25) inset, 0 4px 18px -2px rgba(255,107,61,0.45)"
                    : "0 1px 0 rgba(255,255,255,0.05) inset",
                letterSpacing:"-0.005em",
                display:"flex", alignItems:"center", justifyContent:"center", gap:8,
                transition:"background 0.35s cubic-bezier(0.32,0.72,0,1.18), box-shadow 0.35s ease, color 0.25s ease, transform 0.12s",
              }}
                onMouseDown={(e)=>(!submitting && !succeeded && slipReady) && (e.currentTarget.style.transform="scale(0.98) translateY(1px)")}
                onMouseUp={(e)=>(e.currentTarget.style.transform="scale(1)")}
                onMouseLeave={(e)=>(e.currentTarget.style.transform="scale(1)")}
                onTouchStart={(e)=>(!submitting && !succeeded && slipReady) && (e.currentTarget.style.transform="scale(0.98) translateY(1px)")}
                onTouchEnd={(e)=>(e.currentTarget.style.transform="scale(1)")}
              >
                {succeeded ? (
                  <>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" style={{ animation:"checkPop 0.4s cubic-bezier(0.32,0.72,0,1.32) both" }}>
                      <polyline points="20 6 9 17 4 12" style={{ strokeDasharray:30, strokeDashoffset:30, animation:"checkDraw 0.32s 0.05s cubic-bezier(0.65,0,0.35,1) forwards" }}/>
                    </svg>
                    <span style={{ animation:"fadeUp 0.25s 0.1s both" }}>Locked in</span>
                  </>
                ) : submitting ? (
                  <>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.85)" strokeWidth="2.5" strokeLinecap="round" style={{ animation:"spin 0.8s linear infinite" }}>
                      <path d="M21 12a9 9 0 1 1-6.219-8.56"/>
                    </svg>
                    <span>{torchDraft
                      ? (torchDraft.offerMode ? "Sending offer…" : "Sending request…")
                      : "Locking in…"}</span>
                  </>
                ) : (
                  <span>{torchDraft
                    ? (torchDraft.offerMode
                        ? `Offer to ${torchDraft.recipientName}`
                        : `Send to ${torchDraft.lenderName}`)
                    : "Lock it in"}</span>
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

/* ===========================================
   AUTH HOOK
=========================================== */

/* ===========================================
   PAGE: FAQ
=========================================== */
function FAQPage({ setActivePage }) {
  const [openIdx, setOpenIdx] = useState(null);

  const faqs = [
    { q: "What is Torch?", a: "Torch is where fantasy sports meets a sportsbook — without the real money. Browse real-time odds from professional feeds, pick against the spread, over/unders, moneylines, and even player props — just like a real sportsbook. But instead of cash, you play with virtual points in private leagues with friends. Think of it as a sportsbook simulator for bragging rights." },
    { q: "How is Torch different from fantasy sports?", a: "In fantasy sports, you draft players and manage a roster over a season. Torch is pick-based — you pick games and player props against real sportsbook-style odds, and results settle automatically after each game. No drafts, no waivers, no season-long commitments. Jump in for one game or pick all day." },
    { q: "How is Torch different from a sportsbook?", a: "The experience feels like a sportsbook — you browse odds, place picks, and track results in real time. But there's zero financial risk. All points are virtual. You can't deposit, withdraw, or win real money. Torch gives you the thrill of making smart picks without the downside." },
    { q: "How do I get started?", a: "Sign up and you'll get virtual points to start picking. Head to the Games tab to browse today's odds across every major sport. Tap any game for spreads, totals, moneylines, alt lines, and player props. Add picks to your slip, set your wager, and submit to a league." },
    { q: "What can I pick?", a: "Everything you'd find at a sportsbook: spreads, totals (over/under), moneylines, alternate lines, first half lines, and player props (points, rebounds, assists, strikeouts, and more). Torch pulls odds from multiple professional bookmakers so you're always picking against real lines." },
    { q: "How do leagues work?", a: "Create a private league and share the invite code with friends. Each league has its own point balance and leaderboard. Every pick you submit deducts from your league balance, and wins pay out based on American odds — just like a real book. The captain (league creator) controls settings like starting balance and payday schedule." },
    { q: "What is the Den?", a: "The Den is your league's hub. Tap any league from the Leagues page to enter it. Inside you'll find three tabs: Social (a live activity feed showing picks, results, and league events as they happen), Standings (full leaderboard with everyone's record, balance, and rank), and Picks (every pick made by league members, with live status and results). Not in a league yet? Create or join one — every Den belongs to a league of friends." },
    { q: "What's in the Social feed?", a: "The Social tab in each league's Den shows a real-time feed of activity — when someone submits a pick, wins big, goes on a streak, or takes a loss. It's like a group chat powered by your picks. You can see what everyone's playing and react to results as they happen." },
    { q: "Can I see my friends' picks?", a: "Yes — the Picks tab in your league's Den shows every pick from every member. You can see who's picking what, who's on a hot streak, and who's fading. Tap any pick to see the full details: odds, wager, game info, and result." },
    { q: "Is this real money or gambling?", a: "No. Torch uses virtual points only. Points have no monetary value and cannot be redeemed, exchanged, or converted into real money, prizes, or anything of value. This is a prediction game, not a gambling platform." },
    { q: "How do points and payouts work?", a: "You wager virtual points on each pick. Payouts follow real American odds — a -110 favorite pays less than a +250 underdog, just like at a sportsbook. Your league captain sets the starting balance and weekly payday amount to keep competition going." },
    { q: "When do picks settle?", a: "Picks settle automatically once the game ends and final scores are confirmed. Wins appear in green, losses in red. Your league balance updates in real time." },
    { q: "What is the Captain role?", a: "Each league has a Captain (the creator) who acts as commissioner. Captains can manage league settings — starting balance, payday amount, payday schedule — plus remove members or delete the league. It's your league, your rules." },
    { q: "What is Practice mode?", a: "Practice mode is your sandbox to learn the picks. Every new user starts with 100 pts to wager on any sport or market, no league required. When your practice balance hits 0, you'll be prompted to create or join a league — that's where the real game happens. (Pass the Torch is league-only — it doesn't apply to practice picks.)" },
    { q: "What is Pass the Torch?", a: "Share a pick with a league mate. The funder (lender) puts up the points; the riding partner (borrower) puts up nothing. Two ways to start: ASK if you're low and want a teammate to spot you, or OFFER if you've got points and want a teammate riding along. Win it, the lender gets 1.5x payout for fronting the risk and the borrower gets the full payout. Lose it, only the lender takes the hit. Push, the lender's stake is refunded." },
    { q: "What sports and markets are available?", a: "NBA, NFL, MLB, NHL, NCAAB, NCAAF, soccer (EPL, La Liga, Serie A, MLS, Champions League, and more), MMA, boxing, tennis, golf, AFL, rugby, cricket, F1, NASCAR, and more. Game odds update in real time from professional sportsbook data feeds. Player props are available for most major sports." },
    { q: "Is Torch free?", a: "Yes. Free to sign up, free to play, free forever. No credit card, no in-app purchases, no ads, no hidden fees." },
  ];

  return (
    <div style={{ maxWidth:640, margin:"0 auto", padding:"0 20px calc(90px + env(safe-area-inset-bottom, 0px))", background:T.bg, minHeight:"100vh" }}>
      <div style={{ padding:"32px 0 28px", position:"relative" }}>
        <div style={{
          position:"absolute", top:-40, right:-60, width:320, height:320, borderRadius:"50%",
          background:"radial-gradient(circle, rgba(255,107,61,0.06) 0%, transparent 70%)",
          pointerEvents:"none", animation:"orbFloat1 15s ease-in-out infinite",
        }}/>
        <div style={{ position:"relative", zIndex:1 }}>
          <div style={{ fontFamily:FD, fontSize:28, fontWeight:700, color:"#fff", letterSpacing:"-0.6px", marginBottom:8 }}>
            Frequently Asked Questions
          </div>
          <div style={{ fontSize:14, color:"rgba(255,255,255,0.35)", lineHeight:1.6 }}>
            Everything you need to know about Torch.
          </div>
        </div>
      </div>

      <div style={{ display:"flex", flexDirection:"column", gap:4 }}>
        {faqs.map((faq, i) => {
          const isOpen = openIdx === i;
          return (
            <div key={i} style={{
              borderRadius:16,
              background: isOpen ? "rgba(255,255,255,0.03)" : "transparent",
              border: isOpen ? "1px solid rgba(255,255,255,0.05)" : "1px solid transparent",
              transition:"all 0.2s",
            }}>
              <button onClick={() => setOpenIdx(isOpen ? null : i)} style={{
                width:"100%", display:"flex", alignItems:"center", justifyContent:"space-between",
                padding:"16px 18px", background:"none", border:"none",
                cursor:"pointer", textAlign:"left", gap:14,
              }}>
                <span style={{
                  fontSize:15, fontWeight:600,
                  color: isOpen ? "#fff" : "rgba(255,255,255,0.6)",
                  letterSpacing:"-0.01em", lineHeight:1.4, flex:1,
                }}>{faq.q}</span>
                <span style={{
                  fontSize:12, color:"rgba(255,255,255,0.36)", flexShrink:0,
                  transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
                  transition:"transform 0.25s cubic-bezier(0.34,1.56,0.64,1)",
                }}>▼</span>
              </button>
              {isOpen && (
                <div style={{
                  padding:"0 18px 18px",
                  fontSize:14, lineHeight:1.75, color:"rgba(255,255,255,0.4)",
                  animation:"fadeUp 0.2s ease",
                }}>{faq.a}</div>
              )}
            </div>
          );
        })}
      </div>

      <div style={{ marginTop:32, textAlign:"center" }}>
        <div className="glow-line" style={{ marginBottom:28 }}/>
        <div style={{ fontSize:14, color:"rgba(255,255,255,0.42)", marginBottom:16 }}>
          Still have questions? Jump in and try it.
        </div>
        <button onClick={() => { haptic.light(); setActivePage("Games"); }} className="btn-primary" style={{
          padding:"14px 32px", borderRadius:14, border:"none",
          background:T.coral,
          color:"#fff", fontSize:15, fontWeight:700, cursor:"pointer",
        }}>Browse Games</button>
      </div>
    </div>
  );
}
// CaptainPage deleted in cut #10 — function was defined but never routed to anywhere.


/* ===========================================
   PUBLIC LANDING PAGE  (/picks)
   Shown to logged-out visitors. Hero + today's games (read-only).
   Login form toggled inline via showLogin state.
=========================================== */
function PublicLandingPage({ onLoginSuccess }) {
  const [showLogin, setShowLogin] = useState(false);
  const [faqOpen, setFaqOpen] = useState(null);

  // WC bracket-pool FAQ entries auto-hide after July 19, 2026. Placed right
  // after "Is this gambling?" — marketing-driven traffic from the WC TikTok/
  // Instagram push needs answers to lock time, scoring, and what happens if
  // their bracket busts. The 4 entries here cover the top objections: when
  // it locks (deadline urgency), how scoring works (game mechanics), whether
  // they have to keep picking (time commitment objection), and what happens
  // if their champion crashes early (bust anxiety). Numbers below mirror
  // SCORING constants in worldCup2026Config.js — keep them in sync.
  const showWcFaqs = new Date() <= new Date("2026-07-19T23:59:59Z");
  const wcFaqs = showWcFaqs ? [
    { q: "When does the World Cup Special lock?", a: "All bracket picks lock at opening kickoff: Thursday, June 11, 2026 at 1:00 PM CST when Mexico plays South Africa. Until then, you can edit your picks as many times as you want. After kickoff, your bracket is final for the rest of the tournament — no edits, no swaps, no exceptions." },
    { q: "How are bracket points scored?", a: "Up to 1,000 points total. Group stage: 5 pts per correctly-placed team (1st through 4th) across all 12 groups, 240 max. Knockouts: 5/15/30/60 pts for picking the right winner in Round of 32 / Round of 16 / Quarterfinals / Semifinals. Champion: 200 pts. Golden boot: 120 pts. The final score serves as a tiebreaker — closest to the actual scoreline wins." },
    { q: "Do I have to keep picking during the tournament?", a: "No. The whole bracket is one-shot — predict everything before kickoff on June 11 and you're done. Sit back, watch the games, and check your score after each match. No daily pick logging, no group chats blowing up at 6am with lineup questions." },
    { q: "What if my predicted champion gets eliminated early?", a: "You're still in the pool. Group-stage and knockout points stack independently, so you can outscore everyone on group placements alone. The golden boot pick and final-score tiebreaker also reward attention to detail even if your trophy lift crashes out in R32. Plenty of brackets win on the strength of the groups." },
  ] : [];

  const faqs = [
    { q: "Is this gambling?", a: "No. Torch uses virtual points only. No real money is ever wagered, deposited, or withdrawn. Think of it as fantasy sports meets a pick'em game — all the fun of making predictions, zero financial risk." },
    ...wcFaqs,
    { q: "What are virtual points?", a: "You start with free points. Wager them on real game lines — spreads, totals, moneylines. Win points if you're right, lose them if you're wrong. Your league captain sets a recurring payday to keep competition going." },
    { q: "Is Torch really free?", a: "Yes. Free to sign up, free to play, free forever. No credit card, no in-app purchases, no hidden fees. We built this for sports fans, not to make money off them." },
    { q: "How do leagues work?", a: "Create a private league and share the code with friends. Everyone gets their own league balance and leaderboard. Talk trash in the Den, watch standings update live after every game." },
    { q: "What sports are available?", a: "NBA, NFL, MLB, NHL, college basketball, college football, soccer (EPL, La Liga, Serie A, MLS, Champions League), MMA, and more. Odds update in real time from professional sportsbook feeds." },
    { q: "Can I play without joining a league?", a: "Yes — every new user starts with 100 practice pts. Make picks on any sport to learn the loop. When you've spent your practice pts, you'll need to create or join a league to keep playing." },
    { q: "What is Pass the Torch?", a: "Share a pick with a teammate without making them put up points. Either ASK a wealthy teammate to fund a pick for you, or OFFER to fund a pick for a teammate to ride along on. The lender (funder) puts up the full stake. On a win, the lender gets 1.5x the payout for fronting the risk and the borrower (rider) gets the full payout. On a loss, only the lender takes the hit. Push, lender refunded. Real social co-op, real upside for the funder." },
  ];

  return (
    <div style={{ background:"#0D0E12", minHeight:"100vh", color:"#fff", fontFamily:FB }}>
      <ToastContainer />

      {/* ── NAV ────────────────────────────────────────────────────────────── */}
      <div style={{
        position:"sticky", top:0, zIndex:50,
        background:"rgba(13,14,18,0.94)", backdropFilter:"blur(32px) saturate(180%)",
        borderBottom:"1px solid rgba(255,255,255,0.04)",
        display:"flex", alignItems:"center", justifyContent:"space-between",
        padding:"calc(env(safe-area-inset-top, 0px) + 12px) 20px 12px 20px",
      }}>
        <div style={{ display:"flex", alignItems:"center", gap:8 }}>
          <TorchLogo size={24} />
          <span style={{ fontFamily:FD, fontSize:18, fontWeight:700, color:"#fff", letterSpacing:"-0.04em" }}>torch</span>
        </div>
        <div style={{ display:"flex", gap:8 }}>
          <button onClick={() => setShowLogin(true)} style={{
            padding:"8px 16px", borderRadius:10, border:"1px solid rgba(255,255,255,0.08)",
            background:"transparent", color:"rgba(255,255,255,0.6)", fontSize:13, fontWeight:500, cursor:"pointer",
          }}>Log in</button>
          <button onClick={() => setShowLogin(true)} style={{
            padding:"8px 18px", borderRadius:10, border:"none",
            background:T.coral,
            color:"#fff", fontSize:13, fontWeight:700, cursor:"pointer",
            boxShadow:"0 2px 16px rgba(255,107,61,0.3)",
          }}>Sign up free</button>
        </div>
      </div>

      {/* ── HERO ───────────────────────────────────────────────────────────── */}
      <div style={{
        padding:"80px 24px 64px", textAlign:"center",
        position:"relative", overflow:"hidden",
      }}>
        {/* Cinematic ambient orbs */}
        <div style={{
          position:"absolute", top:-120, left:"50%", transform:"translateX(-50%)",
          width:700, height:400, borderRadius:"50%",
          background:"radial-gradient(ellipse, rgba(255,107,61,0.12) 0%, rgba(255,60,20,0.04) 40%, transparent 70%)",
          pointerEvents:"none", animation:"orbFloat1 15s ease-in-out infinite",
        }}/>
        <div style={{
          position:"absolute", bottom:-80, left:"20%", width:400, height:400, borderRadius:"50%",
          background:"radial-gradient(circle, rgba(96,165,250,0.06) 0%, transparent 60%)",
          pointerEvents:"none", animation:"orbFloat2 18s ease-in-out infinite",
        }}/>
        <div style={{
          position:"absolute", top:"40%", right:"-10%", width:420, height:420, borderRadius:"50%",
          background:"radial-gradient(circle, rgba(167,139,250,0.05) 0%, transparent 60%)",
          pointerEvents:"none", animation:"orbFloat1 20s ease-in-out infinite 3s",
        }}/>
        {/* Grid */}
        <div className="grid-bg"/>
        {/* Noise */}
        <div className="noise-overlay"/>
        {/* Scanline effect */}
        <div style={{
          position:"absolute", left:0, right:0, height:"1px",
          background:"linear-gradient(90deg, transparent 20%, rgba(255,107,61,0.15) 50%, transparent 80%)",
          animation:"scanline 8s linear infinite", pointerEvents:"none", opacity:0.4,
        }}/>

        <div style={{ maxWidth:560, margin:"0 auto", position:"relative", zIndex:1 }}>
          <h1 style={{
            fontFamily:FD, fontSize:"clamp(38px,9vw,56px)", fontWeight:700,
            color:"#fff", lineHeight:1.06, letterSpacing:"-0.04em", marginBottom:20,
          }}>
            Pick games.<br/>Talk trash.<br/>
            <span className="torch-gradient-text" style={{ fontFamily:FD }}>Win bragging rights.</span>
          </h1>
          <p style={{
            fontSize:17, color:"rgba(255,255,255,0.38)", lineHeight:1.7,
            marginBottom:40, maxWidth:420, margin:"0 auto 40px",
            fontWeight:400,
          }}>
            Free sports predictions with friends. No real money, no gambling. Just prove you know ball.
          </p>
          <button onClick={() => setShowLogin(true)} style={{
            padding:"18px 52px", borderRadius:18, border:"none",
            background:T.coral,
            color:"#fff", fontSize:18, fontWeight:700, cursor:"pointer",
            boxShadow:"0 8px 36px rgba(255,107,61,0.35), 0 2px 0 rgba(255,255,255,0.15) inset",
            letterSpacing:"-0.01em", position:"relative", overflow:"hidden",
            animation:"glowPulse 2s ease infinite",
          }}>
            <div style={{
              position:"absolute", inset:0,
              background:"linear-gradient(105deg, transparent 40%, rgba(255,255,255,0.12) 50%, transparent 60%)",
              backgroundSize:"200% 100%", animation:"shimmer 3s ease infinite", pointerEvents:"none",
            }}/>
            <span style={{ position:"relative" }}>Get Started Free</span>
          </button>
          <div style={{ fontSize:12, color:"rgba(255,255,255,0.36)", marginTop:14 }}>
            No credit card. No catch. Free forever.
          </div>

          {/* ── App Store badge (web only — hidden inside the iOS app) ───── */}
          {!IS_NATIVE_APP && (
            <div style={{ marginTop:36, display:"flex", flexDirection:"column", alignItems:"center", gap:14 }}>
              <div style={{ fontSize:11, color:"rgba(255,255,255,0.42)", fontWeight:700, letterSpacing:"0.14em", textTransform:"uppercase" }}>
                Now on iOS
              </div>
              <a
                href="https://apps.apple.com/us/app/torch-picks/id6761868223"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display:"inline-flex", alignItems:"center", gap:12,
                  padding:"11px 22px", borderRadius:13,
                  background:"#000", border:"1px solid rgba(255,255,255,0.18)",
                  textDecoration:"none", transition:"transform 0.15s, border-color 0.15s, box-shadow 0.15s",
                  boxShadow:"0 4px 18px rgba(0,0,0,0.4)",
                }}
                onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-1px)"; e.currentTarget.style.borderColor = "rgba(255,255,255,0.35)"; }}
                onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.borderColor = "rgba(255,255,255,0.18)"; }}
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="#fff" aria-hidden="true">
                  <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
                </svg>
                <div style={{ display:"flex", flexDirection:"column", alignItems:"flex-start", lineHeight:1 }}>
                  <div style={{ fontSize:10, color:"rgba(255,255,255,0.78)", fontWeight:400, letterSpacing:"0.02em", marginBottom:3 }}>Download on the</div>
                  <div style={{ fontSize:18, color:"#fff", fontWeight:600, letterSpacing:"-0.01em", fontFamily:"-apple-system, BlinkMacSystemFont, 'SF Pro Display', sans-serif" }}>App Store</div>
                </div>
              </a>
            </div>
          )}
        </div>
      </div>

      {/* ── HOW IT WORKS ───────────────────────────────────────────────────── */}
      <div style={{ maxWidth:520, margin:"0 auto", padding:"0 20px 48px" }}>
        <div style={{
          fontSize:11, fontWeight:700, color:"rgba(255,255,255,0.36)",
          letterSpacing:"0.1em", textAlign:"center", marginBottom:20,
        }}>HOW IT WORKS</div>

        <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
          {[
            { num:"1", title:"Sign up and get 100 pts", sub:"Free virtual points to start picking immediately" },
            { num:"2", title:"Pick spreads, totals, and moneylines", sub:"Real odds on real games, updated live" },
            { num:"3", title:"Create a league with friends", sub:"Compete on a shared leaderboard" },
          ].map(({ num, title, sub }) => (
            <div key={num} style={{
              display:"flex", gap:14, alignItems:"center", padding:"16px 18px",
              borderRadius:16, background:"rgba(255,255,255,0.03)",
              border:"1px solid rgba(255,255,255,0.05)",
            }}>
              <div style={{
                width:40, height:40, borderRadius:12, flexShrink:0,
                background:"rgba(255,107,61,0.08)", border:"1px solid rgba(255,107,61,0.12)",
                display:"flex", alignItems:"center", justifyContent:"center",
                fontFamily:FD, fontSize:16, fontWeight:700, color:T.coral,
              }}>{num}</div>
              <div>
                <div style={{ fontSize:14, fontWeight:700, color:"#fff", marginBottom:2 }}>{title}</div>
                <div style={{ fontSize:12, color:"rgba(255,255,255,0.35)" }}>{sub}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── STATS STRIP ────────────────────────────────────────────────────── */}
      <div style={{
        borderTop:"1px solid rgba(255,255,255,0.04)",
        borderBottom:"1px solid rgba(255,255,255,0.04)",
        padding:"20px",
      }}>
        <div style={{ maxWidth:520, margin:"0 auto", display:"flex", justifyContent:"space-around" }}>
          {[
            { val:"100%", label:"Free", color:T.coral },
            { val:"$0", label:"Real money", color:"#4ade80" },
            { val:"+200", label:"Weekly pts", color:"#FCD34D" },
            { val:"10+", label:"Sports", color:"#60A5FA" },
          ].map(({ val, label, color }) => (
            <div key={label} style={{ textAlign:"center" }}>
              <div style={{ fontFamily:FD, fontSize:20, fontWeight:700, color }}>{val}</div>
              <div style={{ fontSize:10, color:"rgba(255,255,255,0.40)", marginTop:3, fontWeight:500 }}>{label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ── WHAT YOU GET ───────────────────────────────────────────────────── */}
      <div style={{ maxWidth:520, margin:"0 auto", padding:"48px 20px" }}>
        <div style={{
          fontSize:11, fontWeight:700, color:"rgba(255,255,255,0.36)",
          letterSpacing:"0.1em", textAlign:"center", marginBottom:20,
        }}>WHAT YOU GET</div>

        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8 }}>
          {[
            { icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={T.coral} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="6"/><circle cx="12" cy="12" r="2"/></svg>,
              title:"Live odds", sub:"Real lines from sportsbook feeds" },
            { icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#4ade80" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M22 12h-4l-3 9L9 3l-3 9H2"/></svg>,
              title:"Auto-settlement", sub:"Picks grade when games end" },
            { icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#FCD34D" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6"/><path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18"/><path d="M4 22h16"/><path d="M18 2H6v7a6 6 0 0 0 12 0V2Z"/></svg>,
              title:"Private leagues", sub:"Invite-only, friends only" },
            { icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#60A5FA" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>,
              title:"Trash talk", sub:"Social feed inside every league" },
          ].map(({ icon, title, sub }) => (
            <div key={title} style={{
              padding:"18px 16px", borderRadius:16,
              background:"rgba(255,255,255,0.03)", border:"1px solid rgba(255,255,255,0.05)",
            }}>
              <div style={{ marginBottom:10 }}>{icon}</div>
              <div style={{ fontSize:13, fontWeight:700, color:"#fff", marginBottom:3 }}>{title}</div>
              <div style={{ fontSize:11, color:"rgba(255,255,255,0.42)", lineHeight:1.5 }}>{sub}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ── FAQ ────────────────────────────────────────────────────────────── */}
      <div style={{
        borderTop:"1px solid rgba(255,255,255,0.04)",
        padding:"48px 20px",
      }}>
        <div style={{ maxWidth:520, margin:"0 auto" }}>
          <div style={{
            fontSize:11, fontWeight:700, color:"rgba(255,255,255,0.36)",
            letterSpacing:"0.1em", textAlign:"center", marginBottom:20,
          }}>FREQUENTLY ASKED QUESTIONS</div>

          <div style={{ display:"flex", flexDirection:"column", gap:2 }}>
            {faqs.map((faq, i) => {
              const isOpen = faqOpen === i;
              return (
                <div key={i} style={{
                  borderRadius:12,
                  background: isOpen ? "rgba(255,255,255,0.03)" : "transparent",
                  transition:"background 0.15s",
                }}>
                  <button onClick={() => setFaqOpen(isOpen ? null : i)} style={{
                    width:"100%", display:"flex", alignItems:"center", justifyContent:"space-between",
                    padding:"14px 16px", background:"none", border:"none",
                    cursor:"pointer", textAlign:"left", gap:12,
                  }}>
                    <span style={{
                      fontSize:14, fontWeight:600, color: isOpen ? "#fff" : "rgba(255,255,255,0.65)",
                      letterSpacing:"-0.01em", lineHeight:1.4, flex:1,
                    }}>{faq.q}</span>
                    <span style={{
                      fontSize:11, color:"rgba(255,255,255,0.36)", flexShrink:0,
                      transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
                      transition:"transform 0.2s ease",
                    }}>▼</span>
                  </button>
                  {isOpen && (
                    <div style={{
                      padding:"0 16px 16px",
                      fontSize:13, lineHeight:1.7, color:"rgba(255,255,255,0.4)",
                      animation:"fadeUp 0.15s ease",
                    }}>{faq.a}</div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* ── BOTTOM CTA ─────────────────────────────────────────────────────── */}
      <div style={{
        borderTop:"1px solid rgba(255,255,255,0.04)",
        padding:"48px 20px 64px", textAlign:"center",
      }}>
        <div style={{ maxWidth:400, margin:"0 auto" }}>
          <div style={{ fontFamily:FD, fontSize:24, fontWeight:700, letterSpacing:"-0.4px", marginBottom:12 }}>
            Ready to prove you know ball?
          </div>
          <div style={{ fontSize:14, color:"rgba(255,255,255,0.35)", marginBottom:28, lineHeight:1.6 }}>
            Join thousands of sports fans making free predictions every day.
          </div>
          <button onClick={() => setShowLogin(true)} style={{
            padding:"15px 40px", borderRadius:14, border:"none",
            background:T.coral,
            color:"#fff", fontSize:16, fontWeight:700, cursor:"pointer",
            boxShadow:"0 6px 28px rgba(255,107,61,0.35)",
            marginBottom:12,
          }}>Sign Up Free</button>
          <div style={{ fontSize:12, color:"rgba(255,255,255,0.36)" }}>
            Already have an account? <span onClick={() => setShowLogin(true)} style={{ color:T.coral, cursor:"pointer", fontWeight:600 }}>Log in</span>
          </div>

          {/* ── App Store badge (web only — hidden inside the iOS app) ───── */}
          {!IS_NATIVE_APP && (
            <div style={{ marginTop:32, display:"flex", flexDirection:"column", alignItems:"center", gap:12 }}>
              <div style={{ height:1, width:48, background:"rgba(255,255,255,0.08)", marginBottom:4 }}/>
              <div style={{ fontSize:12, color:"rgba(255,255,255,0.35)" }}>
                Prefer the iOS app?
              </div>
              <a
                href="https://apps.apple.com/us/app/torch-picks/id6761868223"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display:"inline-flex", alignItems:"center", gap:10,
                  padding:"10px 18px", borderRadius:11,
                  background:"#000", border:"1px solid rgba(255,255,255,0.18)",
                  textDecoration:"none", transition:"transform 0.15s, border-color 0.15s",
                  boxShadow:"0 3px 14px rgba(0,0,0,0.35)",
                }}
                onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-1px)"; e.currentTarget.style.borderColor = "rgba(255,255,255,0.32)"; }}
                onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.borderColor = "rgba(255,255,255,0.18)"; }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="#fff" aria-hidden="true">
                  <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
                </svg>
                <div style={{ display:"flex", flexDirection:"column", alignItems:"flex-start", lineHeight:1 }}>
                  <div style={{ fontSize:9, color:"rgba(255,255,255,0.78)", fontWeight:400, letterSpacing:"0.02em", marginBottom:2 }}>Download on the</div>
                  <div style={{ fontSize:15, color:"#fff", fontWeight:600, letterSpacing:"-0.01em", fontFamily:"-apple-system, BlinkMacSystemFont, 'SF Pro Display', sans-serif" }}>App Store</div>
                </div>
              </a>
            </div>
          )}
        </div>
      </div>

      {/* ── LOGIN MODAL ────────────────────────────────────────────────────── */}
      {showLogin && (
        <div onClick={() => setShowLogin(false)} style={{
          position:"fixed", inset:0, zIndex:200,
          background:"rgba(10,12,18,0.94)", backdropFilter:"blur(16px)",
          display:"flex", alignItems:"center", justifyContent:"center",
          padding:20, animation:"fadeUp 0.2s ease",
        }}>
          <div onClick={e => e.stopPropagation()} style={{ width:"100%", maxWidth:420 }}>
            <LoginPage embedded onClose={() => setShowLogin(false)} />
          </div>
        </div>
      )}

      <style>{`
        input::placeholder{color:rgba(242,243,247,0.22);}
      `}</style>
    </div>
  );
}

function useAuth() {
  const [user, setUser]       = useState(undefined); // undefined = still loading
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Timeout fallback: if Firebase doesn't respond in 5s, show login screen
    const timeout = setTimeout(() => {
      setLoading(false);
    }, 5000);

    const unsub = onAuthStateChanged(auth, (u) => {
      clearTimeout(timeout);
      setUser(u);
      setLoading(false);
      if (u?.uid) {
        registerIosPushNotifications(u.uid).catch(() => {});
      }
    });

    return () => {
      clearTimeout(timeout);
      unsub();
    };
  }, []);

  return { user, loading };
}

/* ===========================================
   LOGIN PAGE
=========================================== */
function LoginPage({ embedded = false, onClose }) {
  const [mode, setMode]       = useState("signin"); // signin | signup
  const [step, setStep]       = useState(1);        // signup: 1=creds, 2=username
  const [email, setEmail]     = useState("");
  const [pw, setPw]           = useState("");
  const [username, setUname]  = useState("");
  const [error, setError]     = useState("");
  const [loading, setLoading] = useState(false);
  const [showPw, setShowPw]   = useState(false);
  const [forgotMode, setForgotMode] = useState(false);
  const [resetSent, setResetSent]   = useState(false);

  // iOS-only: when an existing Google-only account is detected on the native
  // app, we surface an inline "Set your password" prompt instead of the generic
  // error message — because native Google Sign-In isn't available in the
  // WKWebView (Google blocks OAuth from embedded browsers per their "Use
  // secure browsers" policy). The user gets a password setup email; once they
  // set a password, they can sign in here with email/password.
  const [googleAcctEmail,   setGoogleAcctEmail]   = useState("");   // "" = not detected; set to email when detected
  const [googleResetSent,   setGoogleResetSent]   = useState(false); // true after the setup email is sent

  const pwStrength = pw.length === 0 ? 0 : pw.length < 6 ? 1 : pw.length < 10 ? 2 : 3;
  const strengthColor = [T.border, T.red, T.gold, T.green][pwStrength];
  const strengthLabel = ["", "Weak", "Fair", "Strong"][pwStrength];

  // Handle Google redirect result on page load (desktop fallback)
  useEffect(() => {
    getRedirectResult(auth).then(result => {
      if (result?.user) {
        // Auth state listener picks it up — nothing else needed
      }
    }).catch(() => {});
  }, []);

  const handleGoogleSignIn = async () => {
    setError(""); setLoading(true);

    // On iOS/Android native (Capacitor): signInWithPopup hangs in WKWebView.
    // Skip popup entirely and use redirect — top-level nav works fine.
    // getRedirectResult() in the useEffect above picks up the result
    // when the user returns to torchpicks.com.
    if (IS_NATIVE_APP) {
      try {
        await signInWithRedirect(auth, googleProvider);
        return; // page will navigate away — control won't return here
      } catch {
        setError("Google sign-in failed. Try again or use email/password.");
        setLoading(false);
      }
      return;
    }

    // Web path — try popup first, fall back to redirect if blocked.
    // Safety timeout: if Google popup hangs for any reason, reset state
    // after 15s so the UI isn't stuck forever.
    const hangGuard = setTimeout(() => {
      setLoading(false);
      setError("Google sign-in is taking too long. Please try email or Apple sign-in instead.");
    }, 15000);
    try {
      // Try popup first; if blocked, fall back to redirect (common on desktop)
      await signInWithPopup(auth, googleProvider);
      clearTimeout(hangGuard);
      logTorchEvent("sign_up", { method: "google", utm_source: readStoredUtm().utm_source || "(direct)" });
    } catch (e) {
      clearTimeout(hangGuard);
      if (
        e.code === "auth/popup-blocked" ||
        e.code === "auth/popup-closed-by-user" ||
        e.code === "auth/cancelled-popup-request"
      ) {
        // Popup blocked (common on desktop) — fall back to full-page redirect
        try {
          await signInWithRedirect(auth, googleProvider);
          return; // page will navigate away
        } catch {
          setError("Google sign-in failed. Try again or use email/password.");
        }
      } else {
        setError("Google sign-in failed. Please try again.");
      }
      setLoading(false);
    }
  };

  // ── Sign in with Apple — Apple Guideline 4.8 compliance ──
  const handleAppleSignIn = async () => {
    setError(""); setLoading(true);
    try {
      const appleProvider = new OAuthProvider("apple.com");
      appleProvider.addScope("email");
      appleProvider.addScope("name");
      await signInWithPopup(auth, appleProvider);
      logTorchEvent("sign_up", { method: "apple", utm_source: readStoredUtm().utm_source || "(direct)" });
    } catch (e) {
      if (
        e.code === "auth/popup-blocked" ||
        e.code === "auth/popup-closed-by-user" ||
        e.code === "auth/cancelled-popup-request"
      ) {
        try {
          const appleProvider = new OAuthProvider("apple.com");
          appleProvider.addScope("email");
          appleProvider.addScope("name");
          await signInWithRedirect(auth, appleProvider);
          return;
        } catch {
          setError("Apple sign-in failed. Try again or use email/password.");
        }
      } else {
        setError("Apple sign-in failed. Please try again.");
      }
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!email.trim()) { setError("Enter your email address above first."); return; }
    setError(""); setLoading(true);
    try {
      // actionCodeSettings: routes the post-reset "Continue" button back to
      // torchpicks.com instead of leaving the user on Firebase's hosted page.
      // Bigger win: providing actionCodeSettings.url forces Firebase to include
      // a `continueUrl` param in the email's action link, which iOS Mail and
      // Gmail use as a deliverability signal — links containing a recognized
      // brand domain auto-link far more reliably than bare firebaseapp.com URLs.
      // The action URL itself still lives on torch-e1c29.firebaseapp.com until
      // we configure a custom domain in Firebase Console → Auth → Templates.
      await sendPasswordResetEmail(auth, email.trim(), {
        url: "https://torchpicks.com/",
        handleCodeInApp: false,
      });
      setResetSent(true);
    } catch (e) {
      const msgs = {
        "auth/user-not-found": "No account found with that email.",
        "auth/invalid-email":  "Please enter a valid email.",
      };
      setError(msgs[e.code] || "Couldn't send reset email. Try again.");
    } finally { setLoading(false); }
  };

  // iOS-only: send a password setup email to a Google-account user so they
  // can set a password and sign in with email/password in the native app.
  // Firebase's sendPasswordResetEmail works for OAuth-only accounts and adds
  // password as an additional sign-in method when the user completes it.
  const handleSendGoogleSetupEmail = async () => {
    if (!googleAcctEmail) return;
    setError(""); setLoading(true);
    try {
      // Same actionCodeSettings rationale as handleForgotPassword — route
      // the continue URL through torchpicks.com so the email link auto-links
      // in iOS Mail / Gmail.
      await sendPasswordResetEmail(auth, googleAcctEmail, {
        url: "https://torchpicks.com/",
        handleCodeInApp: false,
      });
      setGoogleResetSent(true);
    } catch (e) {
      const msgs = {
        "auth/invalid-email":     "Please enter a valid email.",
        "auth/too-many-requests": "Too many attempts. Wait a minute and try again.",
      };
      setError(msgs[e.code] || "Couldn't send setup email. Try again.");
    } finally { setLoading(false); }
  };

  const handleSignIn = async () => {
    if (!email || !pw) { setError("Please fill in all fields."); return; }
    setError(""); setLoading(true);
    try {
      await signInWithEmailAndPassword(auth, email, pw);
    } catch (e) {
      // Check if this email is registered via Google
      if (
        e.code === "auth/wrong-password" ||
        e.code === "auth/invalid-credential" ||
        e.code === "auth/invalid-login-credentials"
      ) {
        try {
          const methods = await fetchSignInMethodsForEmail(auth, email);
          if (methods.includes("google.com")) {
            if (IS_NATIVE_APP) {
              // iOS app: native Google Sign-In is blocked by Google's
              // "Use secure browsers" policy in WKWebView. Surface the
              // inline "Set your password" prompt instead of an error.
              setGoogleAcctEmail(email);
              setGoogleResetSent(false);
              setError("");
              setLoading(false);
              return;
            }
            // Web: Google button works — point them to it.
            setError("This email is linked to Google. Tap \"Continue with Google\" below to sign in.");
            setLoading(false);
            return;
          }
        } catch {}
      }
      const msgs = {
        "auth/user-not-found":    "No account found. Check your email or create an account.",
        "auth/wrong-password":    "Incorrect password. Use 'Forgot password?' to reset it.",
        "auth/invalid-credential":"Incorrect email or password. Use 'Forgot password?' if needed.",
        "auth/invalid-email":     "Please enter a valid email.",
        "auth/too-many-requests": "Too many attempts. Try again later or reset your password.",
      };
      setError(msgs[e.code] || "Sign-in failed. Please try again.");
    } finally { setLoading(false); }
  };

  const handleSignUp = async () => {
    if (!username.trim()) { setError("Please enter a username."); return; }
    setError(""); setLoading(true);

    // Safety timeout — if the whole flow hasn't completed in 15s, unstick the UI.
    const timeout = setTimeout(() => {
      setLoading(false);
      setError("Taking too long — check your connection and try again.");
    }, 15000);

    try {
      const result = await createUserWithEmailAndPassword(auth, email, pw);

      // User created in Firebase Auth. Set display name.
      // updateProfile is now statically imported — no dynamic import that can hang.
      try {
        await updateProfile(result.user, { displayName: username.trim() });
      } catch (profileErr) {
        // Profile update failed but the user WAS created. Don't block sign-in —
        // the displayName will fall back to email prefix. Log it and move on.
        console.warn("[Torch] updateProfile failed after sign-up:", profileErr.message);
      }

      logTorchEvent("sign_up", { method: "email", utm_source: readStoredUtm().utm_source || "(direct)" });
      // Meta Pixel: standard signup conversion (web funnel).
      torchFbq("CompleteRegistration", { content_name: "torch_signup", status: true });
      // onAuthStateChanged fires automatically and transitions to the main app.
      // If it doesn't fire within the timeout above, the user gets an error message
      // instead of being stuck on "Creating account..." forever.
    } catch (e) {
      clearTimeout(timeout);
      if (e.code === "auth/email-already-in-use") {
        setError("__already_exists__");
      } else {
        const msgs = {
          "auth/invalid-email":  "Please enter a valid email.",
          "auth/weak-password":  "Password must be at least 6 characters.",
          "auth/network-request-failed": "Network error — check your connection.",
          "auth/too-many-requests": "Too many attempts. Wait a minute and try again.",
        };
        setError(msgs[e.code] || "Sign-up failed. Please try again.");
      }
      setLoading(false);
    }
  };

  // Inputs use a focus-glow pattern (orange ring + 3px halo) consistent
  // with the Create League form. Refs needed since we use inline handlers.
  const inp = (extra = {}) => ({
    style: {
      width: "100%", padding: "14px 14px",
      background: "rgba(255,255,255,0.03)",
      border: "1px solid rgba(255,255,255,0.06)",
      borderRadius: 12, color: T.ink, fontSize: 16,
      outline: "none", fontFamily: FB,
      colorScheme: "dark",
      WebkitAppearance: "none",
      boxSizing: "border-box",
      transition: "border-color 0.15s, box-shadow 0.15s",
      ...extra,
    },
    onFocus: (e) => {
      e.currentTarget.style.borderColor = "rgba(255,107,61,0.50)";
      e.currentTarget.style.boxShadow = "0 0 0 3px rgba(255,107,61,0.10)";
    },
    onBlur: (e) => {
      e.currentTarget.style.borderColor = "rgba(255,255,255,0.06)";
      e.currentTarget.style.boxShadow = "none";
    },
  });

  return (
    <div style={{
      minHeight: embedded ? "auto" : "100vh",
      background: embedded ? "transparent" : T.bg,
      color: T.ink,
      display: "flex", flexDirection: "column",
      alignItems: "center", justifyContent: "center",
      padding: embedded ? 8 : 24, fontFamily: FB,
    }}>
      {/* Ambient glow — standalone only */}
      {!embedded && <div style={{
        position: "fixed", top: "10%", left: "50%", transform: "translateX(-50%)",
        width: 480, height: 360, borderRadius: "50%", pointerEvents: "none",
        background: "radial-gradient(ellipse, rgba(255,107,61,0.05) 0%, transparent 70%)",
      }}/>}

      <div style={{ width: "100%", maxWidth: 400, position: "relative", zIndex: 1 }}>
        {!embedded && (
          <div style={{ textAlign: "center", marginBottom: 32 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10, marginBottom: 14 }}>
              {/* Torch flame with ambient glow — matches the home logo treatment */}
              <div style={{ position:"relative", width:30, height:30 }}>
                <div style={{ position:"absolute", inset:-4, background:"radial-gradient(circle, rgba(255,107,61,0.45), transparent 65%)", filter:"blur(6px)", pointerEvents:"none" }}/>
                <svg width="30" height="30" viewBox="0 0 24 24" fill={T.ember} style={{ position:"relative" }}>
                  <path d="M12 2C12 2 5 9 5 14a7 7 0 0014 0c0-5-7-12-7-12z"/>
                </svg>
              </div>
              <span style={{ fontFamily: FD, fontSize: 32, fontWeight: 800, letterSpacing: "-1.5px", color:"#fff" }}>torch</span>
            </div>
            <p style={{ fontSize: 13.5, color:"rgba(255,255,255,0.55)", lineHeight: 1.6, fontWeight:500, letterSpacing:"-0.005em" }}>
              Free sports picks. No money, no ads.<br/>Just bragging rights.
            </p>
          </div>
        )}

        {/* Step dots for signup */}
        {mode === "signup" && (
          <div style={{ display: "flex", justifyContent: "center", gap: 6, marginBottom: 22 }}>
            {[1, 2].map(s => (
              <div key={s} style={{
                height: 3, width: step >= s ? 36 : 22, borderRadius: 2,
                background: step >= s ? T.coral : "rgba(255,255,255,0.10)",
                boxShadow: step >= s ? "0 0 6px rgba(255,107,61,0.5)" : "none",
                transition: "all 0.3s",
              }}/>
            ))}
          </div>
        )}

        {/* Card */}
        <div style={{
          background: "#131520",
          border: "1px solid rgba(255,255,255,0.06)",
          borderRadius: 20, padding: "26px 22px",
          boxShadow: "0 1px 0 rgba(255,255,255,0.04) inset, 0 12px 40px -12px rgba(0,0,0,0.5)",
          animation: "fadeUp 0.25s ease",
        }}>

          {/* -- SIGN IN -- */}
          {mode === "signin" && (
            <>
              <h2 style={{ fontFamily: FD, fontSize: 22, fontWeight: 700, marginBottom: 22, letterSpacing:"-0.4px", color:"#fff" }}>Welcome back</h2>
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 10.5, fontWeight: 700, color: "rgba(255,255,255,0.40)", marginBottom: 7, textTransform:"uppercase", letterSpacing:"0.10em" }}>Email</div>
                <input {...inp()} type="email" placeholder="you@example.com"
                  value={email} onChange={e => { setEmail(e.target.value); if (googleAcctEmail) { setGoogleAcctEmail(""); setGoogleResetSent(false); } }}
                  onKeyDown={e => e.key === "Enter" && handleSignIn()}
                />
              </div>
              <div style={{ marginBottom: 8 }}>
                <div style={{ fontSize: 10.5, fontWeight: 700, color: "rgba(255,255,255,0.40)", marginBottom: 7, textTransform:"uppercase", letterSpacing:"0.10em" }}>Password</div>
                <div style={{ position: "relative" }}>
                  <input {...inp({ paddingRight: 56 })} type={showPw ? "text" : "password"}
                    placeholder="••••••••" value={pw}
                    onChange={e => setPw(e.target.value)}
                    onKeyDown={e => e.key === "Enter" && handleSignIn()}
                  />
                  <button onClick={() => setShowPw(!showPw)} style={{
                    position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)",
                    background: "none", border: "none", cursor: "pointer",
                    color: "rgba(255,255,255,0.45)", fontSize: 11.5, fontWeight: 600,
                    padding:"6px 10px", borderRadius:8, transition:"color 0.15s",
                  }}
                    onMouseEnter={(e)=>(e.currentTarget.style.color="rgba(255,255,255,0.85)")}
                    onMouseLeave={(e)=>(e.currentTarget.style.color="rgba(255,255,255,0.45)")}
                  >{showPw ? "Hide" : "Show"}</button>
                </div>
              </div>
              <div style={{ textAlign: "right", marginBottom: 18 }}>
                <button onClick={handleForgotPassword} disabled={loading}
                  style={{ background: "none", border: "none", cursor: "pointer", fontSize: 12,
                    color: T.coral, fontWeight: 600, padding:"4px 6px", letterSpacing:"-0.005em" }}>
                  Forgot password?
                </button>
              </div>
              {resetSent && (
                <div style={{ fontSize: 13, color: "#4ade80", background: "rgba(74,222,128,0.08)", border:"1px solid rgba(74,222,128,0.20)",
                  padding: "10px 14px", borderRadius: 12, marginBottom: 14, fontWeight: 500, display:"flex", alignItems:"center", gap:8 }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#4ade80" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                  Reset email sent — check your inbox (and spam).
                </div>
              )}

              {/* iOS-only: Google-account detected — inline "Set your password" card */}
              {googleAcctEmail && IS_NATIVE_APP && !googleResetSent && (
                <div style={{
                  background: "rgba(255,107,61,0.06)", border: "1px solid rgba(255,107,61,0.22)",
                  padding: "14px 16px", borderRadius: 14, marginBottom: 14,
                }}>
                  <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:8 }}>
                    <svg width="16" height="16" viewBox="0 0 24 24">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"/>
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                    </svg>
                    <div style={{ fontSize:13, fontWeight:700, color:"#fff", letterSpacing:"-0.005em" }}>
                      You signed up with Google
                    </div>
                  </div>
                  <div style={{ fontSize:12.5, color:"rgba(255,255,255,0.62)", lineHeight:1.55, marginBottom:12 }}>
                    Google sign-in isn't supported in the iOS app. Set a password and you'll be able to sign in here with your email.
                  </div>
                  <button
                    onClick={handleSendGoogleSetupEmail}
                    disabled={loading}
                    className="no-press"
                    style={{
                      width:"100%", padding:"11px", borderRadius:10,
                      background:T.coral,
                      border:"none", color:"#fff", fontSize:13.5, fontWeight:700,
                      cursor: loading ? "default" : "pointer", opacity: loading ? 0.6 : 1,
                      letterSpacing:"-0.005em",
                      boxShadow:"0 2px 8px rgba(0,0,0,0.3)",
                    }}
                  >
                    {loading ? "Sending…" : "Email me a password setup link"}
                  </button>
                </div>
              )}

              {/* iOS-only: confirmation that the setup email was sent */}
              {googleAcctEmail && IS_NATIVE_APP && googleResetSent && (
                <div style={{
                  background: "rgba(74,222,128,0.07)", border:"1px solid rgba(74,222,128,0.22)",
                  padding: "12px 14px", borderRadius: 12, marginBottom: 14,
                }}>
                  <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:6 }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#4ade80" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                    <div style={{ fontSize:13, fontWeight:700, color:"#4ade80" }}>Setup email sent</div>
                  </div>
                  <div style={{ fontSize:12, color:"rgba(255,255,255,0.62)", lineHeight:1.55 }}>
                    Check <span style={{ color:"#fff", fontWeight:600 }}>{googleAcctEmail}</span> (and spam) for the link. Once you set a password, come back here and sign in with your email.
                  </div>
                </div>
              )}

              {error && <div style={{ fontSize: 13, color: T.red, background: "rgba(199,101,97,0.10)", border:"1px solid rgba(199,101,97,0.20)", padding: "10px 14px", borderRadius: 12, marginBottom: 14 }}>{error}</div>}
              <button onClick={handleSignIn} disabled={loading} className="no-press" style={{ ...primaryBtn, opacity: loading ? 0.6 : 1 }}
                onMouseDown={(e)=>!loading && (e.currentTarget.style.transform="scale(0.98) translateY(1px)")}
                onMouseUp={(e)=>(e.currentTarget.style.transform="scale(1)")}
                onMouseLeave={(e)=>(e.currentTarget.style.transform="scale(1)")}
                onTouchStart={(e)=>!loading && (e.currentTarget.style.transform="scale(0.98) translateY(1px)")}
                onTouchEnd={(e)=>(e.currentTarget.style.transform="scale(1)")}
              >
                {loading ? "Signing in…" : "Sign in"}
              </button>
              {!IS_NATIVE_APP && (
              <div style={{ display: "flex", alignItems: "center", gap: 12, margin: "20px 0" }}>
                <div style={{ flex: 1, height: 1, background: "rgba(255,255,255,0.06)" }}/>
                <span style={{ fontSize: 11, color: "rgba(255,255,255,0.30)", fontWeight:600, letterSpacing:"0.10em", textTransform:"uppercase" }}>or</span>
                <div style={{ flex: 1, height: 1, background: "rgba(255,255,255,0.06)" }}/>
              </div>
              )}
              {!IS_NATIVE_APP && (
                <button onClick={handleGoogleSignIn} disabled={loading} className="no-press" style={{
                  width: "100%", padding: "13px", borderRadius: 12,
                  background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)",
                  color: "rgba(255,255,255,0.90)", fontSize: 14, fontWeight: 600, cursor: "pointer",
                  display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
                  letterSpacing:"-0.005em",
                  transition:"background 0.15s, transform 0.12s",
                }}
                  onMouseDown={(e)=>(e.currentTarget.style.transform="scale(0.99)")}
                  onMouseUp={(e)=>(e.currentTarget.style.transform="scale(1)")}
                  onMouseLeave={(e)=>(e.currentTarget.style.transform="scale(1)")}
                >
                  <svg width="17" height="17" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  Continue with Google
                </button>
              )}
              {!IS_NATIVE_APP && (
              <button onClick={handleAppleSignIn} disabled={loading} className="no-press" style={{
                width: "100%", padding: "13px", borderRadius: 12, marginTop: IS_NATIVE_APP ? 0 : 10,
                background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)",
                color: "rgba(255,255,255,0.90)", fontSize: 14, fontWeight: 600, cursor: "pointer",
                display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
                letterSpacing:"-0.005em",
                transition:"background 0.15s, transform 0.12s",
              }}
                onMouseDown={(e)=>(e.currentTarget.style.transform="scale(0.99)")}
                onMouseUp={(e)=>(e.currentTarget.style.transform="scale(1)")}
                onMouseLeave={(e)=>(e.currentTarget.style.transform="scale(1)")}
              >
                <svg width="17" height="17" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
                </svg>
                Continue with Apple
              </button>
              )}
            </>
          )}

          {/* -- SIGN UP STEP 1 -- */}
          {mode === "signup" && step === 1 && (
            <>
              <h2 style={{ fontFamily: FD, fontSize: 22, fontWeight: 700, marginBottom: 6 }}>Create account</h2>
              <p style={{ fontSize: 13, color: T.inkSec, marginBottom: 22 }}>Free forever. No credit card.</p>
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: T.inkSec, marginBottom: 6 }}>Email</div>
                <input {...inp()} type="email" placeholder="you@example.com"
                  value={email} onChange={e => setEmail(e.target.value)} />
              </div>
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: T.inkSec, marginBottom: 6 }}>Password</div>
                <input {...inp()} type="password" placeholder="Min 6 characters"
                  value={pw} onChange={e => setPw(e.target.value)} />
                {pw.length > 0 && (
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 7 }}>
                    <div style={{ flex: 1, height: 2, borderRadius: 1, background: T.border, overflow: "hidden" }}>
                      <div style={{ width: `${(pwStrength/3)*100}%`, height: "100%", background: strengthColor, transition: "all 0.3s" }}/>
                    </div>
                    <span style={{ fontSize: 11, color: strengthColor, fontWeight: 600, width: 40 }}>{strengthLabel}</span>
                  </div>
                )}
              </div>
              {error && <div style={{ fontSize: 13, color: T.red, background: T.redDim, padding: "10px 12px", borderRadius: 8, marginBottom: 14 }}>{error}</div>}
              <button onClick={() => {
                if (!email || pw.length < 6) { setError("Enter a valid email and password (min 6 chars)."); return; }
                setError(""); setStep(2);
              }} style={primaryBtn}>Continue →</button>
              {!IS_NATIVE_APP && (
              <div style={{ display: "flex", alignItems: "center", gap: 12, margin: "18px 0" }}>
                <div style={{ flex: 1, height: 1, background: T.border }}/><span style={{ fontSize: 12, color: T.inkMuted }}>or</span><div style={{ flex: 1, height: 1, background: T.border }}/>
              </div>
              )}
              {!IS_NATIVE_APP && (
                <button onClick={handleGoogleSignIn} disabled={loading} style={{
                  width: "100%", padding: "13px", borderRadius: 12,
                  background: "transparent", border: `1px solid ${T.border}`,
                  color: T.ink, fontSize: 14, fontWeight: 600, cursor: "pointer",
                  display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
                }}>
                  <svg width="18" height="18" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  Sign up with Google
                </button>
              )}
              {!IS_NATIVE_APP && (
              <button onClick={handleAppleSignIn} disabled={loading} style={{
                width: "100%", padding: "13px", borderRadius: 12, marginTop: IS_NATIVE_APP ? 0 : 10,
                background: "transparent", border: `1px solid ${T.border}`,
                color: T.ink, fontSize: 14, fontWeight: 600, cursor: "pointer",
                display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
              }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
                </svg>
                Sign up with Apple
              </button>
              )}
            </>
          )}

          {/* -- SIGN UP STEP 2 -- */}
          {mode === "signup" && step === 2 && (
            <>
              <h2 style={{ fontFamily: FD, fontSize: 22, fontWeight: 700, marginBottom: 6 }}>Pick your name</h2>
              <p style={{ fontSize: 13, color: T.inkSec, marginBottom: 22 }}>This is how you'll appear on leaderboards.</p>
              <div style={{ marginBottom: 20 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: T.inkSec, marginBottom: 6 }}>Username</div>
                <input {...inp()} placeholder="e.g. sharppicks99" value={username}
                  onChange={e => setUname(e.target.value.replace(/[^a-zA-Z0-9_]/g, ""))}
                  maxLength={20}
                />
                {username && <div style={{ fontSize: 11, color: T.inkMuted, marginTop: 5 }}>torchpicks.com/u/{username}</div>}
              </div>
              {error === "__already_exists__" ? (
                <div style={{ fontSize: 13, background: T.redDim, border: `1px solid ${T.red}`, padding: "12px 14px", borderRadius: 8, marginBottom: 14 }}>
                  <div style={{ color: T.red, fontWeight: 700, marginBottom: 6 }}>An account with this email already exists.</div>
                  <div style={{ color: T.inkSec, marginBottom: 10 }}>Did you mean to sign in? Your password may be different.</div>
                  <div style={{ display: "flex", gap: 8 }}>
                    <button onClick={() => { setMode("signin"); setError(""); setStep(1); }} style={{
                      flex: 1, padding: "9px", borderRadius: 8,
                      background: T.coral, border: "none", color: "#fff",
                      fontSize: 13, fontWeight: 700, cursor: "pointer",
                    }}>Sign in instead →</button>
                    <button onClick={() => { setStep(1); setError(""); }} style={{
                      flex: 1, padding: "9px", borderRadius: 8,
                      background: "transparent", border: `1px solid ${T.border}`,
                      color: T.inkSec, fontSize: 13, cursor: "pointer",
                    }}>Use different email</button>
                  </div>
                </div>
              ) : error ? (
                <div style={{ fontSize: 13, color: T.red, background: T.redDim, padding: "10px 12px", borderRadius: 8, marginBottom: 14 }}>{error}</div>
              ) : null}
              <button onClick={handleSignUp} disabled={loading || !username || error === "__already_exists__"} style={{ ...primaryBtn, opacity: loading || !username || error === "__already_exists__" ? 0.5 : 1 }}>
                {loading ? "Creating account…" : "Create account"}
              </button>
              <button onClick={() => { setStep(1); setError(""); }} style={{ width: "100%", marginTop: 10, padding: "10px", background: "none", border: "none", cursor: "pointer", fontSize: 13, color: T.inkMuted }}>
                ← Back
              </button>
            </>
          )}
        </div>

        {/* Toggle signin/signup */}
        <p style={{ textAlign: "center", marginTop: 20, fontSize: 13, color: T.inkSec }}>
          {mode === "signin" ? "New to Torch? " : "Already have an account? "}
          <button onClick={() => { setMode(mode === "signin" ? "signup" : "signin"); setError(""); setStep(1); }}
            style={{ background: "none", border: "none", cursor: "pointer", color: T.ember, fontWeight: 700, fontSize: 13 }}>
            {mode === "signin" ? "Create account" : "Sign in"}
          </button>
        </p>
        <p style={{ textAlign: "center", marginTop: 12, fontSize: 11, color: T.inkMuted, lineHeight: 1.6 }}>
          No real money. Ever.
        </p>
      </div>
    </div>
  );
}

/* ===========================================
   ROOT APP
=========================================== */

// -----------------------------------------------------------------------------
//  OnboardingOverlay — shown once to new users on first login
//  3-step swipeable card deck explaining: what Torch is → how to pick → leagues
//  Stored in localStorage so it only shows once per device.
// -----------------------------------------------------------------------------
function OnboardingOverlay({ onDone, onGoToPick, onGoToLeague }) {
  const [step, setStep] = useState(0);

  const STEPS = [
    {
      iconType: "flame",
      title:    "Welcome to Torch",
      body:     "Make picks on real games. Track your record. Compete with friends using virtual points.",
      disclaimer: "Torch is NOT a gambling app. No real money is involved — ever. Points have zero monetary value and cannot be redeemed for cash, prizes, or anything of value. This is a free prediction game for entertainment only.",
      cta:      "I understand",
      sub:      null,
    },
    {
      iconType: "target",
      title:    "How it works",
      body:     "You start with 100 pts. Pick spreads, totals, or moneylines. Win pts if you're right. Your league captain sets the payday schedule.",
      cta:      "Make my first pick →",
      sub:      "Skip for now",
      ctaAction: "pick",
    },
    {
      iconType: "trophy",
      title:    "Compete with friends",
      body:     "Create a private league, share a code, and see who really knows their sports. Leaderboards update live as games settle.",
      cta:      "Create a league →",
      sub:      "Maybe later",
      ctaAction: "league",
    },
  ];

  const current = STEPS[step];
  const isLast  = step === STEPS.length - 1;

  const handleCta = () => {
    if (current.ctaAction === "pick") {
      onDone();
      onGoToPick?.();
    } else if (current.ctaAction === "league") {
      onDone();
      onGoToLeague?.();
    } else if (isLast) {
      onDone();
    } else {
      setStep(s => s + 1);
    }
  };

  const handleSub = () => {
    if (step < STEPS.length - 1) {
      setStep(s => s + 1);
    } else {
      onDone();
    }
  };

  return (
    <div style={{
      position:"fixed", inset:0, zIndex:300,
      background:"rgba(8,9,13,0.94)",
      backdropFilter:"blur(20px) saturate(180%)",
      display:"flex", flexDirection:"column",
      alignItems:"center", justifyContent:"center",
      padding:"24px 20px",
    }}>
      {/* Ambient orbs */}
      <div style={{ position:"absolute", top:"15%", left:"20%", width:420, height:420, borderRadius:"50%",
        background:"radial-gradient(circle, rgba(255,107,61,0.55) 0%, transparent 50%)",
        animation:"orbFloat1 12s ease-in-out infinite", pointerEvents:"none" }}/>
      <div style={{ position:"absolute", bottom:"20%", right:"15%", width:320, height:320, borderRadius:"50%",
        background:"radial-gradient(circle, rgba(139,92,246,0.06) 0%, transparent 70%)",
        animation:"orbFloat2 15s ease-in-out infinite", pointerEvents:"none" }}/>

      {/* Card */}
      <div style={{
        width:"100%", maxWidth:400,
        background:"linear-gradient(160deg, #14161F 0%, #0E0E12 100%)",
        border:"1px solid rgba(255,255,255,0.06)",
        borderRadius:28, padding:"40px 28px 32px",
        boxShadow:"0 32px 100px rgba(0,0,0,0.7), 0 1px 0 rgba(255,255,255,0.04) inset",
        textAlign:"center",
        position:"relative", overflow:"hidden",
        animation:"fadeSlideUp 0.35s ease",
      }}>
        {/* Step icon */}
        <div style={{ marginBottom:20, lineHeight:1, display:"flex", justifyContent:"center" }}>
          {current.iconType === "flame" && (
            <svg width="52" height="52" viewBox="0 0 24 24" fill="none" stroke={T.coral} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 2C12 2 5 9 5 14a7 7 0 0014 0c0-5-7-12-7-12z"/>
            </svg>
          )}
          {current.iconType === "target" && (
            <svg width="52" height="52" viewBox="0 0 24 24" fill="none" stroke={T.coral} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="9"/>
              <circle cx="12" cy="12" r="5"/>
              <circle cx="12" cy="12" r="1.6" fill={T.coral} stroke="none"/>
            </svg>
          )}
          {current.iconType === "trophy" && (
            <svg width="52" height="52" viewBox="0 0 24 24" fill="none" stroke={T.coral} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
              <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6"/>
              <path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18"/>
              <path d="M4 22h16"/>
              <path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22"/>
              <path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22"/>
              <path d="M18 2H6v7a6 6 0 0 0 12 0V2Z"/>
            </svg>
          )}
        </div>

        {/* Title */}
        <div style={{
          fontFamily:FB,
          fontSize:26, fontWeight:700, color:"#fff",
          letterSpacing:"-0.5px", marginBottom:14, lineHeight:1.15,
        }}>
          {current.title}
        </div>

        {/* Body */}
        <div style={{
          fontSize:15, color:"rgba(255,255,255,0.62)",
          lineHeight:1.65, marginBottom: current.disclaimer ? 16 : 32,
        }}>
          {current.body}
        </div>

        {/* Legal disclaimer — Apple Guideline 5.3.4 compliance */}
        {current.disclaimer && (
          <div style={{
            fontSize:11, color:"rgba(255,255,255,0.35)",
            lineHeight:1.6, marginBottom:28,
            padding:"12px 14px", borderRadius:12,
            background:"rgba(255,255,255,0.03)",
            border:"1px solid rgba(255,255,255,0.06)",
            textAlign:"left",
          }}>
            {current.disclaimer}
          </div>
        )}

        {/* Step dots */}
        <div style={{ display:"flex", justifyContent:"center", gap:6, marginBottom:28 }}>
          {STEPS.map((_, i) => (
            <div key={i} style={{
              width: i === step ? 18 : 6, height:6, borderRadius:3,
              background: i === step ? "#DC2626" : "rgba(255,255,255,0.18)",
              transition:"all 0.25s ease",
            }}/>
          ))}
        </div>

        {/* Primary CTA */}
        <button onClick={handleCta} style={{
          width:"100%", padding:"14px",
          background:"linear-gradient(135deg,#DC2626,#b91c1c)",
          border:"none", borderRadius:12, cursor:"pointer",
          fontSize:15, fontWeight:700, color:"#fff",
          letterSpacing:"0.01em",
          boxShadow:"0 4px 20px rgba(255,107,61,0.25)",
          marginBottom: current.sub ? 12 : 0,
        }}>
          {current.cta}
        </button>

        {/* Skip / secondary */}
        {current.sub && (
          <button onClick={handleSub} style={{
            width:"100%", padding:"10px",
            background:"transparent", border:"none", cursor:"pointer",
            fontSize:13, color:"rgba(255,255,255,0.35)", fontWeight:600,
          }}>
            {current.sub}
          </button>
        )}
      </div>

      {/* Dismiss hint */}
      <button onClick={onDone} style={{
        marginTop:20, background:"none", border:"none",
        color:"rgba(255,255,255,0.40)", fontSize:12, cursor:"pointer",
      }}>
        Skip intro
      </button>

      <style>{`
        @keyframes fadeSlideUp {
          from { opacity:0; transform:translateY(24px); }
          to   { opacity:1; transform:translateY(0); }
        }
      `}</style>
    </div>
  );
}

// ── useNavStack — browser-back-button-aware navigation stack ─────────────────
// Single-page apps that don't think about navigation hijack the browser back
// button: hitting Back exits the entire site, which feels broken to users
// (especially on mobile / PWA). This hook gives us an opt-in stack we can push
// to whenever the user enters a "scope" they'd expect Back to undo:
//   - opening a league room from the Leagues list
//   - opening a modal/drawer (game detail, profile, slip, settings)
//   - switching tabs (only if Back-to-prev-tab is desired)
//
// Usage:
//   const nav = useNavStack();
//   nav.push("game_detail", () => setSelectedGameDetail(null));
//
// Browser Back will pop the most recent frame and run its onPop callback.
// onPop is called with no args; the caller should close whatever they opened.
// If the stack is empty when Back fires, the browser does its default thing
// (which on desktop is "go back in browser history", on iOS PWA is "exit").
function useNavStack() {
  // Use a ref so we can read the current stack synchronously inside popstate
  // without re-binding the listener on every push/pop (which would race with
  // the actual popstate event).
  const stackRef = React.useRef([]);
  // Track a "version" used to ignore programmatic pushState calls — popstate
  // fires for both user-initiated Back AND for our own pushState replays after
  // the back fires (some browsers do this). The marker prevents double-pop.
  const ignoreNextPopRef = React.useRef(false);

  React.useEffect(() => {
    // Seed history with one extra entry on mount. This is the trick that lets
    // us catch the first Back press — without it, the very first Back would
    // exit the page since there's nothing on the history stack to pop yet.
    try { window.history.pushState({ torchNav: true, depth: 0 }, ""); } catch {}

    const onPop = () => {
      if (ignoreNextPopRef.current) { ignoreNextPopRef.current = false; return; }
      const top = stackRef.current.pop();
      if (top && typeof top.onPop === "function") {
        try { top.onPop(); } catch (e) { console.warn("[NavStack] onPop threw:", e); }
        // After the onPop runs we need to restore the synthetic seed entry so
        // the NEXT Back press is also catchable. Otherwise the user closes one
        // overlay, hits Back again, and exits the site.
        try {
          ignoreNextPopRef.current = true;
          window.history.pushState({ torchNav: true, depth: 0 }, "");
        } catch {}
      }
      // If stack is empty, let the browser do its default (which usually means
      // exit / leave site). We still re-seed so a refresh + immediate Back has
      // something to grab.
    };

    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  const push = React.useCallback((id, onPop) => {
    // Avoid pushing duplicate consecutive frames (e.g. tab switch fires twice)
    const top = stackRef.current[stackRef.current.length - 1];
    if (top && top.id === id) return;
    stackRef.current.push({ id, onPop });
    try { window.history.pushState({ torchNav: true, id, depth: stackRef.current.length }, ""); } catch {}
  }, []);

  // Manually pop from the stack WITHOUT triggering popstate (e.g. user closed
  // the modal via the X button instead of Back). Keeps the stack in sync so
  // Back later doesn't try to pop a frame whose UI is already gone.
  const popSilent = React.useCallback((id) => {
    const idx = stackRef.current.findIndex(f => f.id === id);
    if (idx === -1) return;
    stackRef.current.splice(idx, 1);
    // Don't touch window.history here — the browser still has the entry; we
    // just won't run onPop again because the frame is gone from our stack.
  }, []);

  return { push, popSilent };
}


// =============================================================================
// PracticeCompleteWall
// -----------------------------------------------------------------------------
// Full-screen takeover that renders when a user has burned through their 100
// practice pts and has no leagues to fall back on. Replaces the entire app
// shell (no TopNav, no BottomNav) — only exit is to create or join a league.
//
// Trigger conditions (evaluated at the App root):
//   • balance === 0
//   • userLeagues.length === 0
//   • no pending practice picks (status === "pending" && !leagueId)
// =============================================================================

function PracticeCompleteWall({ onCreateLeague, onJoinLeague, userStats = {}, onSignOut }) {
  const totalPicks = userStats?.totalPicks ?? 0;
  const wins       = userStats?.wins ?? 0;
  const losses     = userStats?.losses ?? 0;
  const decided    = wins + losses;
  const winRate    = decided > 0 ? Math.round((wins / decided) * 100) : null;

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 100,
      background: T.bg, color: T.ink,
      display: "flex", flexDirection: "column",
      paddingTop: "env(safe-area-inset-top, 0px)",
      paddingBottom: "env(safe-area-inset-bottom, 24px)",
      overflowY: "auto", WebkitOverflowScrolling: "touch",
    }}>
      <div style={{
        position: "absolute", top: 0, left: 0, right: 0, height: 320,
        background: "radial-gradient(ellipse 80% 60% at 50% 0%, rgba(255,107,61,0.18), transparent 70%)",
        pointerEvents: "none",
      }}/>

      <div style={{
        position: "relative", flex: 1,
        display: "flex", flexDirection: "column", alignItems: "center",
        justifyContent: "center",
        padding: `${S.xxl}px ${S.page}px`,
        textAlign: "center", minHeight: 360,
      }}>
        <div style={{
          marginBottom: S.lg,
          width: 72, height: 72, borderRadius: "50%",
          display: "flex", alignItems: "center", justifyContent: "center",
          background: "radial-gradient(circle at 50% 60%, rgba(255,107,61,0.28), rgba(255,107,61,0.06) 70%)",
          border: "1px solid rgba(255,107,61,0.30)",
          boxShadow: "0 8px 32px rgba(255,107,61,0.18), 0 0 0 8px rgba(255,107,61,0.04)",
        }}>
          <TorchLogo size={32} />
        </div>

        <div style={{
          fontSize: 10, fontWeight: 700, letterSpacing: "0.18em",
          textTransform: "uppercase", color: T.coral,
          marginBottom: 10, fontFamily: FB,
        }}>
          Practice complete
        </div>

        <h1 style={{
          fontSize: 28, fontWeight: 700, lineHeight: 1.15,
          letterSpacing: "-0.018em",
          margin: 0, marginBottom: 12,
          fontFamily: FD, color: T.ink, maxWidth: 320,
        }}>
          You've spent your starter pts. Now bring some friends.
        </h1>

        <p style={{
          fontSize: 15, lineHeight: 1.5,
          color: T.inkSec, margin: 0, maxWidth: 340, fontFamily: FB,
        }}>
          The 100 pts you started with were yours to learn the picks. The real game is a league with people you know.
        </p>

        {totalPicks > 0 && (
          <div style={{
            marginTop: S.xxl,
            display: "flex", alignItems: "stretch", gap: 0,
            background: "rgba(255,255,255,0.03)",
            border: "1px solid rgba(255,255,255,0.06)",
            borderRadius: S.radius, overflow: "hidden",
          }}>
            <PracticeWallStatCell label="Picks made" value={totalPicks} />
            <PracticeWallDivider />
            <PracticeWallStatCell label="Record" value={`${wins}-${losses}`} />
            {winRate !== null && (
              <>
                <PracticeWallDivider />
                <PracticeWallStatCell label="Win rate" value={`${winRate}%`} accent={winRate >= 50} />
              </>
            )}
          </div>
        )}
      </div>

      <div style={{
        position: "relative",
        padding: `${S.lg}px ${S.page}px ${S.xl}px`,
        display: "flex", flexDirection: "column", gap: 10,
      }}>
        <button onClick={() => { try { haptic.medium?.(); } catch {} onCreateLeague?.(); }} style={{
          width: "100%", height: 54,
          borderRadius: S.radius, cursor: "pointer",
          background: `linear-gradient(180deg, ${T.coral}, ${T.coralDeep})`,
          border: "none", color: "#fff", fontSize: 16, fontWeight: 700,
          fontFamily: FB, letterSpacing: "-0.005em",
          boxShadow: "0 8px 24px rgba(255,107,61,0.32), 0 1px 0 rgba(255,255,255,0.15) inset",
          display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
        }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 5v14"/><path d="M5 12h14"/>
          </svg>
          Create a league
        </button>

        <button onClick={() => { try { haptic.light?.(); } catch {} onJoinLeague?.(); }} style={{
          width: "100%", height: 50,
          borderRadius: S.radius, cursor: "pointer",
          background: "rgba(255,255,255,0.04)",
          border: `1px solid ${T.borderMid}`,
          color: T.ink, fontSize: 15, fontWeight: 600,
          fontFamily: FB, letterSpacing: "-0.005em",
          display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
        }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/><polyline points="10 17 15 12 10 7"/><line x1="15" y1="12" x2="3" y2="12"/>
          </svg>
          Join with a code
        </button>

        {onSignOut && (
          <button onClick={() => { try { haptic.light?.(); } catch {} onSignOut(); }} style={{
            marginTop: 6,
            background: "none", border: "none", cursor: "pointer",
            color: T.inkMuted, fontSize: 12, fontWeight: 500,
            fontFamily: FB, padding: "8px 12px",
          }}>
            Sign out
          </button>
        )}
      </div>
    </div>
  );
}

function PracticeWallStatCell({ label, value, accent = false }) {
  return (
    <div style={{
      flex: 1, padding: "14px 8px",
      display: "flex", flexDirection: "column", alignItems: "center", gap: 4,
    }}>
      <div style={{
        fontFamily: FM, fontSize: 20, fontWeight: 700,
        color: accent ? T.green : T.ink,
        letterSpacing: "-0.01em", fontVariantNumeric: "tabular-nums",
      }}>
        {value}
      </div>
      <div style={{
        fontSize: 9, fontWeight: 700, letterSpacing: "0.10em",
        textTransform: "uppercase", color: T.inkMuted,
      }}>
        {label}
      </div>
    </div>
  );
}

function PracticeWallDivider() {
  return <div style={{ width: 1, background: "rgba(255,255,255,0.06)" }}/>;
}

// ── CLIENT ERROR REPORTER (Aug 29, 2026) ────────────────────────────────────
// Launch-week visibility: uncaught errors and unhandled promise rejections
// write to the `clientErrors` collection (write-only for clients; review in
// the Firebase console). Without this, a member's crash is invisible until
// they email support. Fire-and-forget: the reporter itself can never break
// the app — every path is wrapped, capped at 10 reports/session, deduped by
// message. Requires the matching firestore.rules block (create-only).
// Installed at module load so errors during boot are caught too.
function installClientErrorReporter() {
  try {
    if (typeof window === "undefined" || window.__torchErrRepInstalled) return;
    window.__torchErrRepInstalled = true;
    const seen = new Set();
    let sent = 0;
    const report = (kind, message, stack) => {
      try {
        if (sent >= 10) return;
        const msg = String(message || "unknown").slice(0, 500);
        const key = msg.slice(0, 120);
        if (seen.has(key)) return;
        seen.add(key);
        sent++;
        import("firebase/firestore").then(({ getFirestore, collection, addDoc, serverTimestamp }) => {
          const db = getFirestore(app);
          addDoc(collection(db, "clientErrors"), {
            kind,
            message: msg,
            stack: String(stack || "").slice(0, 1500),
            href: (window.location && window.location.href) || "",
            ua: (navigator && navigator.userAgent) || "",
            uid: (auth.currentUser && auth.currentUser.uid) || null,
            ts: serverTimestamp(),
          }).catch(() => {});
        }).catch(() => {});
      } catch {}
    };
    window.addEventListener("error", (e) => {
      report("error", (e && (e.message || (e.error && e.error.message))) || "unknown", e && e.error && e.error.stack);
    });
    window.addEventListener("unhandledrejection", (e) => {
      const r = e && e.reason;
      report("unhandledrejection", (r && r.message) || String(r), r && r.stack);
    });
  } catch {}
}
installClientErrorReporter();

export default function TorchApp() {
  const { user, loading } = useAuth();
  const [activePage, setActivePage] = useState(() => {
    // Deep link: if URL has ?join=CODE (fresh invite click) OR sessionStorage
    // has a pending join (user just came back from a Google web redirect that
    // wiped URL params), land on Den so the LeaguesPage join modal opens.
    const params = new URLSearchParams(window.location.search);
    let hasPendingJoin = !!params.get("join");
    if (!hasPendingJoin) {
      try { hasPendingJoin = !!sessionStorage.getItem("torch_pending_join"); } catch {}
    }
    if (hasPendingJoin) return "Den";
    // Den intent set by Home page quick actions OR the public landing page
    // "Start a Bracket Pool" CTA. Routes to Den so LeaguesPage's intent
    // handler can consume it (open Create modal, pre-fill WC template, etc.).
    try {
      if (localStorage.getItem("torch_den_intent")) return "Den";
    } catch {}
    // Restore last page — localStorage survives both refresh AND tab close.
    // Legacy migrations: old "Home" tab is now Pulse. Hub was deleted in
    // cut #5 — anyone saved on Hub gets routed to Den (the new landing).
    // Default for first-time users is also Den.
    try {
      const saved = localStorage.getItem("torch_page");
      const valid = ["Pulse", "Den", "Games", "Picks"];
      if (saved === "Home") return "Pulse";
      if (saved === "Hub") return "Den";
      return (saved && valid.includes(saved)) ? saved : "Den";
    } catch { return "Den"; }
  });

  // ── VISITED PAGES — mount-once, toggle-visibility routing ────────────────
  // Page switching used to fully unmount the previous page and mount the next
  // (because `<PageComponent>` is a different component per page). That meant
  // every tap on "Den" tore down ~95 hooks/listeners in LeaguesPage and rebuilt
  // them from scratch — visibly slow. Now we track which pages have been
  // visited, mount each one on first visit, and toggle CSS visibility after.
  // Result: second visit to any page is essentially instant — no re-fetch, no
  // listener resubscribe, scroll position preserved. Trade-off: backgrounded
  // pages keep their Firestore listeners alive (more reads, more memory) — but
  // for current user count this is well under any meaningful cost.
  const [visitedPages, setVisitedPages] = useState(() => new Set([
    // Always pre-mount the page we're landing on so first render is unchanged.
    (() => {
      try {
        const saved = localStorage.getItem("torch_page");
        const valid = ["Pulse", "Den", "Games", "Picks"];
        if (saved === "Home") return "Pulse"; // legacy migration
        if (saved === "Hub") return "Den";    // cut #5 migration
        return (saved && valid.includes(saved)) ? saved : "Den";
      } catch { return "Den"; }
    })()
  ]));
  useEffect(() => {
    setVisitedPages(prev => {
      if (prev.has(activePage)) return prev;
      const next = new Set(prev);
      next.add(activePage);
      return next;
    });
  }, [activePage]);

  // POST-SIGNUP ROUTING — when a logged-out user with pending state completes
  // signup, route them to Den so the appropriate LeaguesPage handler fires.
  // Without this, the WC marketing CTA flow breaks: user clicks "Start a
  // Bracket Pool" → sets torch_den_intent → signs up via native plugin (no
  // page reload) → main app renders with activePage="Home" from initial
  // mount → Home page shown, intent sits in localStorage unconsumed.
  // This effect bridges the gap, watching user.uid for the null→uid flip.
  useEffect(() => {
    if (!user?.uid) return;
    try {
      const hasJoin = !!sessionStorage.getItem("torch_pending_join")
        || !!new URLSearchParams(window.location.search).get("join");
      const hasIntent = !!localStorage.getItem("torch_den_intent");
      if (hasJoin || hasIntent) setActivePage("Den");
    } catch {}
  }, [user?.uid]);

  // PUBLIC INVITE LINK — capture ?join= before any auth gating. Stash to
  // sessionStorage so it survives logged-out → signup transitions, native auth
  // plugin handoffs, and Google web redirects (which return to bare origin and
  // drop URL params). LeaguesPage's join handler reads sessionStorage as a
  // fallback. Runs on every TorchApp mount, including PublicLandingPage renders.
  useEffect(() => {
    try {
      const qCode = new URLSearchParams(window.location.search).get("join");
      if (qCode) {
        sessionStorage.setItem("torch_pending_join", qCode.toUpperCase());
      } else {
        const m = window.location.pathname.match(/\/join\/([A-Z0-9]{4,8})/i);
        if (m) sessionStorage.setItem("torch_pending_join", m[1].toUpperCase());
      }
    } catch {}
  }, []);

  // UTM CAPTURE — last-touch attribution stored in localStorage so multi-
  // session conversions still get credited (TikTok click Monday, signup
  // Tuesday). Each UTM-carrying visit overwrites the prior capture, so the
  // most recent campaign that touched the user wins attribution. localStorage
  // (not sessionStorage) is intentional — TikTok→app conversions often take
  // multiple sessions, especially with native install flows.
  //
  // Stored shape:
  //   { utm_source, utm_campaign, utm_medium, utm_content, utm_term,
  //     landing_path, referrer, captured_at }
  //
  // Captured BEFORE auth gating so it fires for PublicLandingPage visits.
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const utmKeys = ["utm_source", "utm_campaign", "utm_medium", "utm_content", "utm_term"];
      const utms = {};
      for (const k of utmKeys) {
        const v = params.get(k);
        if (v) utms[k] = String(v).slice(0, 100); // defensive length cap
      }
      if (Object.keys(utms).length === 0) return;
      const capture = {
        ...utms,
        landing_path: window.location.pathname?.slice(0, 200) || "/",
        referrer: document.referrer?.slice(0, 200) || null,
        captured_at: Date.now(),
      };
      localStorage.setItem("torch_utm", JSON.stringify(capture));
    } catch {}
  }, []);

  // SIGNUP ATTRIBUTION — fires `signup_attributed` exactly once per freshly-
  // created user with the stored UTM payload. Detection: Firebase Auth's
  // creationTime vs lastSignInTime metadata — within 60s means this is a
  // brand-new account, not a returning login. A localStorage dedup key
  // keyed by uid prevents double-firing if the user.uid identity is
  // somehow re-triggered (e.g. token refresh that re-renders this tree).
  //
  // Independent of the per-method `sign_up` events (which fire from
  // LoginPage handlers). Those tell us HOW someone signed up; this one
  // tells us WHY.
  useEffect(() => {
    if (!user?.uid) return;
    const meta = user.metadata;
    if (!meta?.creationTime || !meta?.lastSignInTime) return;
    const created = new Date(meta.creationTime).getTime();
    const lastSignIn = new Date(meta.lastSignInTime).getTime();
    const isFreshSignup = Math.abs(lastSignIn - created) < 60_000;
    if (!isFreshSignup) return;

    const dedupKey = `torch_attributed_${user.uid}`;
    try {
      if (localStorage.getItem(dedupKey)) return;
      localStorage.setItem(dedupKey, String(Date.now()));
    } catch {}

    const a = readStoredUtm();
    const hasUtm = !!a.utm_source;
    logTorchEvent("signup_attributed", {
      has_utm: hasUtm,
      utm_source:   a.utm_source   || "(direct)",
      utm_campaign: a.utm_campaign || null,
      utm_medium:   a.utm_medium   || null,
      utm_content:  a.utm_content  || null,
      utm_term:     a.utm_term     || null,
      landing_path: a.landing_path || null,
      referrer:     a.referrer     || null,
      // Time-to-convert: how long after the marketing touch did this user
      // actually sign up? Useful for understanding which channels drive
      // immediate vs delayed conversion.
      capture_to_signup_ms: (hasUtm && a.captured_at) ? Date.now() - a.captured_at : null,
    });
  }, [user?.uid, user?.metadata?.creationTime, user?.metadata?.lastSignInTime]);

  // Browser back-button support — see useNavStack definition above. Pushing tab
  // changes onto the stack lets users navigate Home → Games → Back → Home
  // instead of Home → Games → Back → Exit Site (the previous behavior).
  const nav = useNavStack();
  const prevPageRef = React.useRef(activePage);
  // Per-page scroll memory: the mount-once router keeps every visited page
  // alive, but they all share the single document scroll — so switching tabs
  // used to hard-jump to 0 every time. Save the outgoing page's scrollY here
  // and restore the incoming page's saved spot (native tab-bar behavior).
  // Restore lives in a layout effect below: scrolling synchronously before
  // React commits the display toggle would clamp against the OLD page's
  // height and silently fail for long pages.
  const pageScrollMemRef = React.useRef({});

  // Keep localStorage in sync whenever the page changes
  const setActivePagePersist = useCallback((page) => {
    try { localStorage.setItem("torch_page", page); } catch {}
    if (page === prevPageRef.current) {
      // Tapping the already-active tab: keep the pre-existing
      // scroll-to-top behavior (native tab-bar convention).
      pageScrollMemRef.current[page] = 0;
      window.scrollTo(0, 0);
      return;
    }
    // Save the outgoing page's scroll position before switching away.
    if (prevPageRef.current) {
      pageScrollMemRef.current[prevPageRef.current] = window.scrollY;
    }
    // Push the previous page onto the nav stack BEFORE switching, so Back
    // returns to where you were. Skip if it's the same page (defensive).
    const prev = prevPageRef.current;
    if (prev && prev !== page) {
      nav.push(`page:${prev}`, () => {
        try { localStorage.setItem("torch_page", prev); } catch {}
        // Back is a page switch too: save the page we're leaving so its
        // position survives, then let the layout effect restore prev's.
        if (prevPageRef.current) {
          pageScrollMemRef.current[prevPageRef.current] = window.scrollY;
        }
        setActivePage(prev);
        prevPageRef.current = prev;
      });
    }
    prevPageRef.current = page;
    setActivePage(page);
    // Scroll restore happens in the layout effect on activePage, post-commit.
  }, [nav]);
  // Post-commit restore — runs after the display toggle is in the DOM, so
  // the incoming page's full height exists and the restore can't clamp.
  React.useLayoutEffect(() => {
    window.scrollTo(0, pageScrollMemRef.current[activePage] || 0);
  }, [activePage]);
  const [picks, setPicks]           = useState({});
  // ── PERSISTED CACHE (eliminates wrong-data flash on cold start) ──────────
  // userLeagues/balance/leaguesLoaded hydrate from localStorage if the cached
  // uid matches the (eventual) signed-in user. This kills the "Practice mode"
  // banner flash and the "0 pts" flash for returning users: by the time React
  // first paints, we already have last-known-good values on screen. The real
  // Firestore fetches still run on mount and overwrite these with authoritative
  // data ~moments later. If the cached uid mismatches the user that ultimately
  // signs in, an effect below resets state to defaults.
  const _readCache = (key, fallback) => {
    try {
      const raw = localStorage.getItem(key);
      if (raw == null) return fallback;
      return JSON.parse(raw);
    } catch { return fallback; }
  };
  const _cachedUid = (() => { try { return localStorage.getItem("torch_cache_uid"); } catch { return null; } })();
  const [userLeagues, setUserLeagues] = useState(() => _readCache("torch_cache_leagues", []));
  // leaguesLoaded starts true if we have cached leagues — prevents the "Practice
  // mode" banner from flashing while the real loadMyLeagues() call is in flight.
  const [leaguesLoaded, setLeaguesLoaded] = useState(() => {
    try { return localStorage.getItem("torch_cache_leagues") != null; } catch { return false; }
  });
  const [practicePendingCount, setPracticePendingCount] = useState(null);
  const [slipOpen, setSlipOpen]     = useState(false);
  const [leagueResetKey, setLeagueResetKey] = useState(0); // incremented when bottom nav "Leagues" is tapped
  const [picksRefreshKey, setPicksRefreshKey] = useState(0);
  const [pendingPickCount, setPendingPickCount] = useState(0);
  const [games, setGames]           = useState([]);
  const [balance, setBalance]       = useState(() => _readCache("torch_cache_balance", 0));
  const pickCount = Object.keys(picks).length;

  // ── PERSIST CACHE on every change ────────────────────────────────────────
  // Keep localStorage in sync so the NEXT cold start hydrates correctly.
  useEffect(() => {
    try { localStorage.setItem("torch_cache_balance", JSON.stringify(balance)); } catch {}
  }, [balance]);
  useEffect(() => {
    try { localStorage.setItem("torch_cache_leagues", JSON.stringify(userLeagues)); } catch {}
  }, [userLeagues]);

  // ── INVALIDATE CACHE on uid mismatch ─────────────────────────────────────
  // If a different user signs in than the one whose data we cached, reset
  // state to defaults so we don't show the previous user's leagues/balance.
  useEffect(() => {
    if (!user?.uid) return;
    if (_cachedUid && _cachedUid !== user.uid) {
      setUserLeagues([]);
      setLeaguesLoaded(false);
      setBalance(0);
    }
    try { localStorage.setItem("torch_cache_uid", user.uid); } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.uid]);

  // Onboarding: show once per device for new users (no picks yet)
  // localStorage key ensures it never shows again after dismissed
  const [showOnboarding, setShowOnboarding] = useState(false);

  const { stats: userStats, refresh: refreshStats } = useUserStats(user?.uid);

  const handleLeagueDeleted = useCallback(
    (leagueId) => setUserLeagues(prev => prev.filter(l => l.id !== leagueId)),
    []
  );
  const handleLeagueBalanceChange = useCallback(
    (lid, delta) => setUserLeagues(prev =>
      prev.map(l => {
        if (l.id !== lid) return l;
        // Defensive: l.balance can be undefined/null/NaN for newly-joined
        // leagues whose member doc was just provisioned and hasn't been
        // re-fetched into root state yet. Coerce to a finite number before
        // applying the delta, otherwise (undefined + -100) = NaN propagates
        // through to the home-page total and renders nothing changed.
        const current = Number.isFinite(l.balance) ? l.balance : 0;
        const next = Math.max(0, current + (Number(delta) || 0));
        return { ...l, balance: next };
      })
    ),
    []
  );

  // Re-fetch league list after settlement so nav balances stay fresh
  const refreshLeagues = useCallback(() => {
    if (!user?.uid) return;
    loadMyLeagues(user.uid).then(ls => { setUserLeagues(ls); setLeaguesLoaded(true); }).catch(() => setLeaguesLoaded(true));
  }, [user?.uid]);

  // Stable callback for child settlement code. Was previously an inline arrow
  // (`() => setPicksRefreshKey(k => k + 1)`) which recreated every render, which
  // in turn caused settlement useCallbacks to either churn (if listed as a dep)
  // or capture a stale reference (if omitted). useCallback with empty deps is
  // safe here because setPicksRefreshKey is a stable React state setter.
  const handleAfterSettle = useCallback(() => {
    setPicksRefreshKey(k => k + 1);
  }, []);

  // ── Visibility refresh ──────────────────────────────────────────────────
  // When the user returns to the app from the background (or switches back
  // to the tab on web), re-fetch leagues so the home-page balance reflects
  // any picks placed elsewhere (other devices) or any settlement that ran
  // while the app was in the background. The optimistic in-place balance
  // updates handle the same-device case; this catches everything else.
  useEffect(() => {
    if (!user?.uid) return;
    const onVisible = () => {
      if (document.visibilityState === "visible") refreshLeagues();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [user?.uid, refreshLeagues]);

  // ── Practice-pending listener ─────────────────────────────────────────────
  // Tracks pending practice picks (leagueId == null) for the wall trigger.
  // Wall renders when balance === 0 && userLeagues.length === 0 && this is 0.
  useEffect(() => {
    if (!user?.uid) { setPracticePendingCount(null); return; }
    let cancelled = false;
    let unsub = null;
    (async () => {
      try {
        const { getFirestore, collection, query, where, onSnapshot } = await import("firebase/firestore");
        const dbInst = getFirestore(app);
        const q = query(
          collection(dbInst, "picks", user.uid, "userPicks"),
          where("status", "==", "pending")
        );
        unsub = onSnapshot(q, (snap) => {
          if (cancelled) return;
          let count = 0;
          snap.forEach(d => { if (!d.data().leagueId) count++; });
          setPracticePendingCount(count);
        }, () => { if (!cancelled) setPracticePendingCount(0); });
      } catch (e) {
        console.warn("[Torch] practice-pending listener failed:", e.message);
        if (!cancelled) setPracticePendingCount(0);
      }
    })();
    return () => { cancelled = true; if (unsub) unsub(); };
  }, [user?.uid]);


  // On login: init stats + load/seed balance + check payday
  useEffect(() => {
    if (!user) return;
    const name = user.displayName || user.email?.split("@")[0] || "Anonymous";
    updateUserStats(user.uid, name).then(refreshStats).catch(() => {});

    // Load balance once — use for both display and onboarding check (was 2 separate calls)
    const seenKey = `torch_onboarded_${user.uid}`;
    let needsOnboardingCheck = false;
    try { needsOnboardingCheck = !localStorage.getItem(seenKey); } catch {}
    getUserBalance(user.uid).then(async bal => {
      // ── Just-for-Fun seeding rule ───────────────────────────────────────
      // Every user is guaranteed a starting balance of at least 100 pts in
      // their global wallet — used for Practice mode picks (leagueId === null).
      // Tracked by a `seededAt` flag on the user
      // doc so we never re-seed (which would overwrite legitimate winnings or
      // erase legitimate losses).
      //
      // For EXISTING users who already had > 100 from prior wins/prior seeding,
      // we leave their balance alone and only stamp `seededAt` so future logins
      // skip this branch. They keep their winnings; new users still get the 100
      // floor. NOTE: this is forward-only — users seeded under the old 1,000
      // floor are already stamped `seededAt` and are NOT touched here.
      const FUN_FLOOR = 100;
      // SERVER-OWNED-BALANCES (fix 4.1 step 2, 2026-09-28): the one-time
      // 100-pt practice seed runs in seedPracticeBalanceCallable (same rule:
      // max(current, 100) once, stamped seededAt, never re-seeded). The app
      // still writes the profile fields it owns (name, email).
      try {
        const { getFunctions, httpsCallable } = await import("firebase/functions");
        const seed = await httpsCallable(getFunctions(app), "seedPracticeBalanceCallable")({});
        if (typeof seed?.data?.balance === "number") bal = seed.data.balance;
        if (seed?.data?.seeded) {
          const { getFirestore, doc, setDoc } = await import("firebase/firestore");
          await setDoc(doc(getFirestore(app), "users", user.uid), {
            displayName: name, email: user.email || null,
          }, { merge: true }).catch(() => {});
        }
      } catch (seedErr) {
        console.warn("[Torch] Practice seed failed:", seedErr.message);
        if (bal === null) bal = FUN_FLOOR; // display fallback only; the server seeds on next login
      }
      setBalance(bal ?? 0);
      // Show onboarding overlay for new users — only once per device
      if (needsOnboardingCheck && (bal === null || bal === FUN_FLOOR)) {
        setShowOnboarding(true);
      }

    }).catch(() => setBalance(0));

    // Register for push notifications if already granted (no prompt here — done after settlement)
    if (typeof Notification !== "undefined" && Notification.permission === "granted") {
      registerFCMToken(user.uid).catch(() => {});
    }

    // ── League paydays (leagues/{lid}/members/{uid}.balance for each league) ──
    // Uses each league's own payday settings (amount, day, frequency)
    // Each user credits only their own member doc — no permission issues.
    loadMyLeagues(user.uid).then(async (leagues) => {
      setUserLeagues(leagues);
      setLeaguesLoaded(true);
      const paid = await runLeaguePaydays(user.uid, leagues);
      if (paid.length > 0) {
        const displayName = user.displayName || user.email?.split("@")[0] || "Anonymous";
        // Write Den activity event for each paid league
        for (const p of paid) {
          writeLeagueActivity(user.uid, [p.leagueId], {
            type: "payday",
            username: displayName,
            label: `received +${p.amount} pts payday`,
            sport: "",
            points: p.amount,
            wager: p.amount,
          }).catch(() => {});
        }
        // Optimistically update local state immediately
        const paidIds = new Set(paid.map(p => p.leagueId));
        setUserLeagues(prev => prev.map(l =>
          paidIds.has(l.id) ? { ...l, balance: (l.balance ?? 0) + (paid.find(p => p.leagueId === l.id)?.amount || 0) } : l
        ));
        // Then refresh from Firestore to get authoritative balances
        setTimeout(() => {
          loadMyLeagues(user.uid).then(setUserLeagues).catch(() => {});
        }, 1500);
        const totalPts = paid.reduce((s, p) => s + p.amount, 0);
        const names = paid.map(p => p.leagueName).join(", ");
        setTimeout(() => window.torchToast?.(
          `💰 Payday! +${totalPts} pts in ${paid.length > 1 ? paid.length + " leagues" : names}`,
          "payday"
        ), 800);
      }
    }).catch(e => {
      console.error('[Torch/Payday] League payday failed:', e.message, e);
    });
  }, [user?.uid]);

  // -- ROOT SETTLEMENT RUNNER ---------------------------------------------------
  // Runs pick settlement from the root component so it fires on EVERY app load,
  // regardless of which page/tab the user is on. Previously settlement only ran
  // inside MyPicksPage which is only mounted under Leagues → My Picks tab.
  //
  // This runner is deliberately lightweight: it just loads picks + settles.
  // Full UI state (allPicks, liveScores) stays in MyPicksPage.
  const rootSettlingRef = useRef(false);
  const rootPendingRef = useRef(null); // SERVER-SETTLEMENT (fix 4.1 step 2): pending picks seen last pass
  // BOUNDED-READS (fix 2.2): the stuck-activity repair below walks settled
  // picks and queries league activity for each one. It ran on EVERY 90-second
  // pass over the user's ENTIRE history -- one or two queries per settled
  // league pick, forever. Now: once per session, last 7 days only.
  const activityRepairDoneRef = useRef(false);

  const runRootSettlement = useCallback(async () => {
    if (!user?.uid || rootSettlingRef.current) return;
    rootSettlingRef.current = true;
    try {
      // BOUNDED-READS (fix 2.2, 2026-09-28): settlement only needs PENDING picks.
      // The full history is read once per session, for the activity repair.
      const runActivityRepair = !activityRepairDoneRef.current;
      const rawPicks = runActivityRepair ? await loadPicks(user.uid) : await loadPendingPicks(user.uid);

      // FIX #3: normalize rawPicks before filtering so commenceTime + all fields are correct
      const allNormalized = normalizePicks(rawPicks);
      const now = Date.now();

      // Resolve any Firestore timestamp format to ms
      const resolveTs = (ts) => {
        if (!ts) return null;
        if (ts?.toDate) return ts.toDate().getTime();
        if (ts?.seconds) return ts.seconds * 1000;
        if (ts?._seconds) return ts._seconds * 1000;
        const ms = new Date(ts).getTime();
        return isNaN(ms) ? null : ms;
      };

      // ROUND 8: the root stale-void block was removed. It voided at 4h what
      // the server's grace window would still settle, lacked the isLiveBet
      // exemption (re-shipping a bug already fixed in findStalePicks), and
      // refunded co-staked torch/spot-me wagers entirely to the borrower.
      // The `pending` settlement pass below already covers every pick the
      // deleted block tried to settle; voiding belongs to settleAllPicks.

      // SERVER-SETTLEMENT (fix 4.1 step 2, 2026-09-28): the server cron settles
      // and credits every pick. Each pass remembers which picks were pending;
      // any that have since left "pending" were settled by the server, so the
      // app refreshes balance / stats / Den for them. No grading, no writes to
      // balances or W/L.
      const pendingNow = allNormalized.filter(p => p.status === 'pending');
      const prevPending = rootPendingRef.current;
      rootPendingRef.current = pendingNow;
      const pendingIds = new Set(pendingNow.map(p => p.id));
      const candidates = prevPending ? prevPending.filter(p => !pendingIds.has(p.id)) : [];
      if (candidates.length > 0) {
        const nowSettled = await fetchServerSettledPicks(user.uid, candidates);
        if (nowSettled.length > 0) {
          // Refresh balance from Firestore (source of truth)
          const fresh = await getUserBalance(user.uid);
          if (fresh !== null) setBalance(fresh);
          // Update W/L stats (server-side recompute)
          updateUserStats(user.uid, user.displayName || user.email).then(refreshStats).catch(() => {});

          // FIX #2: Update-or-create activity events (prevents duplicate Den feed entries)
          try {
            const { getFirestore, collection, query, where, getDocs, updateDoc } = await import('firebase/firestore');
            const dbAct = getFirestore(app);
            const uname = user.displayName || user.email?.split('@')[0] || 'Anonymous';
            for (const s of nowSettled) {
              if (!s.leagueId) continue;
              try {
                const evCol = collection(dbAct, 'leagues', s.leagueId, 'activity');
                let evSnap = await getDocs(query(evCol, where('pickId', '==', s.id)));
                if (evSnap.empty && s.label) {
                  evSnap = await getDocs(query(evCol, where('userId', '==', user.uid), where('label', '==', s.label)));
                }
                if (!evSnap.empty) {
                  await updateDoc(evSnap.docs[0].ref, {
                    status: s.status, points: s.points ?? 0, settledAt: new Date(),
                    pickId: s.id, // backfill for future lookups
                  });
                } else {
                  await writeLeagueActivity(user.uid, [s.leagueId], {
                    type: s.type || 'pick', status: s.status, username: uname,
                    label: s.label, sport: s.sport,
                    gameLabel: s.gameLabel || '',
                    odds: s.odds || null,
                    points: s.points, wager: s.wager,
                    pickId: s.id,
                  });
                }
              } catch {}
            }
          } catch {}

          // Refresh league list so standings reflect new balance
          refreshLeagues();
        }
      }
      // ── RECONCILER REMOVED ──────────────────────────────────────────────
      // The brute-force balance reconciler has been removed. It was a band-aid
      // for the missing deductLeagueWager bug (now fixed at the source in
      // PickSlipDrawer). The reconciler was actively harmful: it skipped pending
      // picks when computing expectedBalance, so any deducted wager on a pending
      // pick looked like a "short" balance and got incorrectly refunded.

      // ── FIX STUCK ACTIVITY EVENTS ──────────────────────────────────────────
      // Settled picks whose activity events never got updated (old pickId bug).
      // Finds events by userId+label fallback and writes the correct status.
      try {
        const { getFirestore, collection, query, where, getDocs, updateDoc: udAct } = await import('firebase/firestore');
        const dbFix = getFirestore(app);
        const uname = user.displayName || user.email?.split('@')[0] || 'Anonymous';
        const repairCutoff = Date.now() - 7 * 24 * 60 * 60 * 1000;
        const settledWithLeague = !runActivityRepair ? [] : allNormalized.filter(p =>
          p.leagueId && (p.status === 'won' || p.status === 'lost' || p.status === 'voided' || p.status === 'push')
          && (p.submittedAt?.getTime?.() ?? 0) > repairCutoff
        );
        activityRepairDoneRef.current = true;
        let fixedCount = 0;
        for (const p of settledWithLeague) {
          try {
            const evCol = collection(dbFix, 'leagues', p.leagueId, 'activity');
            // Try pickId first
            let evSnap = await getDocs(query(evCol, where('pickId', '==', p.id)));
            if (evSnap.empty && p.label) {
              // Fallback: match by userId + label
              evSnap = await getDocs(query(evCol, where('userId', '==', user.uid), where('label', '==', p.label)));
            }
            if (!evSnap.empty) {
              const evData = evSnap.docs[0].data();
              // Only update if the event is still showing as pending/live
              const evStatus = evData.status || (evData.type === 'won' || evData.type === 'lost' ? evData.type : null);
              if (!evStatus || evStatus === 'pending') {
                await udAct(evSnap.docs[0].ref, {
                  status: p.status,
                  points: p.points ?? 0,
                  settledAt: new Date(),
                  pickId: p.id,
                });
                fixedCount++;
              }
            }
          } catch {}
        }
        if (fixedCount > 0) {
        }
      } catch (e) {
        console.warn('[Torch/Root] Stuck activity fixer failed:', e.message);
      }
    } catch (e) {
      console.warn('[Torch/Root] Settlement runner failed:', e.message);
    } finally {
      rootSettlingRef.current = false;
    }
  }, [user?.uid]);

  // Run on login
  useEffect(() => {
    if (!user?.uid) return;
    // Small delay so auth + Firestore are fully ready
    const t = setTimeout(() => runRootSettlement(), 1500);
    return () => clearTimeout(t);
  }, [user?.uid, runRootSettlement]);

  // Re-run when user returns to the tab
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      runRootSettlement();

      // FIX: check payday on tab focus — catches users who stay logged in over payday midnight.
      // Throttled via localStorage so we only check once per hour (not every tab focus).
      if (user?.uid) {
        const throttleKey = `torch_payday_check_${user.uid}`;
        let lastCheck = 0;
        try { lastCheck = parseInt(localStorage.getItem(throttleKey) || '0', 10); } catch {}
        if (Date.now() - lastCheck > 60 * 60 * 1000) { // once per hour max
          try { localStorage.setItem(throttleKey, String(Date.now())); } catch {}
          // League paydays — same logic as login
          loadMyLeagues(user.uid).then(async (leagues) => {
            setUserLeagues(leagues);
            const paid = await runLeaguePaydays(user.uid, leagues);
            if (paid.length > 0) {
              const displayName = user.displayName || user.email?.split("@")[0] || "Anonymous";
              for (const p of paid) {
                writeLeagueActivity(user.uid, [p.leagueId], {
                  type: "payday", username: displayName,
                  label: `received +${p.amount} pts payday`,
                  sport: "", points: p.amount, wager: p.amount,
                }).catch(() => {});
              }
              const paidIds = new Set(paid.map(p => p.leagueId));
              setUserLeagues(prev => prev.map(l =>
                paidIds.has(l.id) ? { ...l, balance: (l.balance ?? 0) + (paid.find(p => p.leagueId === l.id)?.amount || 0) } : l
              ));
              setTimeout(() => {
                loadMyLeagues(user.uid).then(setUserLeagues).catch(() => {});
              }, 1500);
              const totalPts = paid.reduce((s, p) => s + p.amount, 0);
              setTimeout(() => window.torchToast?.(`Payday — +${totalPts} pts added.`, "payday"), 800);
            }
          }).catch(() => {});
        }
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [runRootSettlement, user?.uid]);

  // Continuous background poll — runs every 90s so live picks settle even when
  // MyPicksPage isn't mounted (user is on Games or Leagues page watching a live game).
  // The rootSettlingRef guard prevents overlap with the on-login / tab-focus runs.
  useEffect(() => {
    if (!user?.uid) return;
    const interval = setInterval(() => {
      if (document.visibilityState === 'visible') runRootSettlement();
    }, 90 * 1000);
    return () => clearInterval(interval);
  }, [user?.uid, runRootSettlement]);

  // Profile modal state — must be before any early return (Rules of Hooks)
  const [viewingProfile, setViewingProfile] = useState(null); // { uid, username } | null
  const [torchModalOpen, setTorchModalOpen] = useState(false);
  const [torchModalData, setTorchModalData] = useState(null); // { leagueId, standings }
  const [torchInfoOpen, setTorchInfoOpen] = useState(false);
  const [torchInfoData, setTorchInfoData] = useState(null); // { leagueId, standings } — for Try It CTA
  const [torchDraft, setTorchDraft] = useState(null); // { lenderUid, lenderName, amount, leagueId, message }

  // When the torch draft banner is visible on the Games page, expose its height
  // as a CSS custom property so sticky elements below (sport filter row) can
  // offset themselves and not hide behind the banner. Cleared on unmount so the
  // property doesn't leak between navigation states.
  useEffect(() => {
    const active = Boolean(torchDraft && activePage === "Games");
    document.documentElement.style.setProperty("--torch-banner-h", active ? "52px" : "0px");
    return () => document.documentElement.style.setProperty("--torch-banner-h", "0px");
  }, [torchDraft, activePage]);

  const [faqOpen, setFaqOpen] = useState(false);
  const [deleteAccountModal, setDeleteAccountModal] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState("");
  const [deletePassword, setDeletePassword] = useState("");
  const handleViewProfile = useCallback((uid, username) => {
    setViewingProfile({ uid, username });
  }, []);

  // Wire browser-back into the major root-level overlays. Each effect pushes
  // a frame onto the nav stack when the overlay opens, and silently removes
  // it when the overlay closes via in-app means (X button, success action,
  // etc). If the user hits Back, the popstate handler runs the closer.
  // NOTE: These useEffects MUST be declared above any conditional return
  // (e.g. loading / signed-out states) to satisfy React's Rules of Hooks.
  React.useEffect(() => {
    if (!viewingProfile) return;
    nav.push("modal:profile", () => setViewingProfile(null));
    return () => nav.popSilent("modal:profile");
  }, [viewingProfile, nav]);

  React.useEffect(() => {
    if (!slipOpen) return;
    nav.push("drawer:slip", () => setSlipOpen(false));
    return () => nav.popSilent("drawer:slip");
  }, [slipOpen, nav]);

  React.useEffect(() => {
    if (!torchModalOpen) return;
    nav.push("modal:torch_request", () => { setTorchModalOpen(false); setTorchModalData(null); });
    return () => nav.popSilent("modal:torch_request");
  }, [torchModalOpen, nav]);

  React.useEffect(() => {
    if (!torchInfoOpen) return;
    nav.push("modal:torch_info", () => setTorchInfoOpen(false));
    return () => nav.popSilent("modal:torch_info");
  }, [torchInfoOpen, nav]);

  React.useEffect(() => {
    if (!deleteAccountModal) return;
    nav.push("modal:delete_account", () => setDeleteAccountModal(false));
    return () => nav.popSilent("modal:delete_account");
  }, [deleteAccountModal, nav]);

  // Pull-to-refresh — clears API cache and re-fetches data
  const handlePullRefresh = useCallback(async () => {
    clearCache();
    try {
      const raw = injectSpecialEvents(await fetchLiveGames());
      setGames(raw);
    } catch {}
    if (user?.uid) {
      refreshStats();
      refreshLeagues();
    }
  }, [user?.uid, refreshStats, refreshLeagues]);

  // Escape key closes modals
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") {
        // Close the topmost open modal/drawer by dispatching a custom event
        window.dispatchEvent(new CustomEvent("torch-escape"));
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // ── iOS keyboard handling (Capacitor) ──
  // Goal: when the keyboard opens in the Den (or anywhere), give it iMessage feel:
  //   1) Hide the iOS native accessory toolbar (^ v ✓ above keyboard) — chat apps suppress this
  //   2) Toggle body.keyboard-open class so CSS can hide bottom nav + Make a Pick CTA
  //   3) Anchor the composer to bottom:0 (handled via CSS override on .den-composer)
  // No-ops on web. Safe if the Keyboard plugin isn't installed yet — falls back gracefully.
  useEffect(() => {
    if (!IS_NATIVE_APP) return;
    const Cap = window.Capacitor;
    if (!Cap || Cap.getPlatform?.() !== "ios") return;

    const Keyboard = Cap.Plugins?.Keyboard;
    if (!Keyboard) {
      console.warn("[torch] Capacitor Keyboard plugin not found — install @capacitor/keyboard and run npx cap sync ios");
      return;
    }

    // 1) Kill the form accessory bar (^v✓) — this is the "form" feel making the input look messy
    Keyboard.setAccessoryBarVisible({ isVisible: false }).catch(() => {});
    // Match dark theme + native resize so position:fixed bottom:0 sits flush on the keyboard
    Keyboard.setStyle?.({ style: "DARK" }).catch(() => {});
    Keyboard.setResizeMode?.({ mode: "native" }).catch(() => {});

    // 2) Body class for CSS hooks
    const onShow = () => {
      document.body.classList.add("keyboard-open");
      // Auto-scroll the page to the bottom so newest message stays in view above the input
      requestAnimationFrame(() => {
        window.scrollTo({ top: document.documentElement.scrollHeight, behavior: "smooth" });
      });
    };
    const onHide = () => {
      document.body.classList.remove("keyboard-open");
    };

    let showHandle, hideHandle;
    Promise.resolve(Keyboard.addListener("keyboardWillShow", onShow)).then(h => { showHandle = h; }).catch(() => {});
    Promise.resolve(Keyboard.addListener("keyboardWillHide", onHide)).then(h => { hideHandle = h; }).catch(() => {});

    return () => {
      try { showHandle?.remove?.(); } catch {}
      try { hideHandle?.remove?.(); } catch {}
      document.body.classList.remove("keyboard-open");
    };
  }, []);

  const { pulling, pullDistance, refreshing } = usePullToRefresh(handlePullRefresh);

  if (loading) {
    return (
      <div style={{ minHeight:"100vh", background:T.bg, display:"flex", alignItems:"center", justifyContent:"center", position:"relative", overflow:"hidden" }}>
        {/* Cinematic ambient orbs */}
        <div style={{ position:"absolute", top:"20%", left:"30%", width:420, height:420, borderRadius:"50%",
          background:"radial-gradient(circle, rgba(255,107,61,0.08) 0%, transparent 70%)",
          animation:"orbFloat1 12s ease-in-out infinite", pointerEvents:"none" }}/>
        <div style={{ position:"absolute", bottom:"20%", right:"25%", width:250, height:250, borderRadius:"50%",
          background:"radial-gradient(circle, rgba(96,165,250,0.05) 0%, transparent 70%)",
          animation:"orbFloat2 15s ease-in-out infinite", pointerEvents:"none" }}/>
        {/* Grid */}
        <div className="grid-bg" style={{ opacity:0.02 }}/>
        {/* Noise texture */}
        <div className="noise-overlay"/>

        <div style={{ textAlign:"center", display:"flex", flexDirection:"column", alignItems:"center", position:"relative", zIndex:1 }}>
          <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:24 }}>
            <TorchLogo size={120} />
          </div>
          <div style={{ width:160, height:2, background:"rgba(255,255,255,0.06)", borderRadius:2, overflow:"hidden" }}>
            <div style={{
              height:"100%", borderRadius:2,
              background:`linear-gradient(90deg,transparent,${T.coral},transparent)`,
              animation:"loadBar 1.8s ease-in-out infinite",
            }}/>
          </div>
          <div style={{ fontSize:11, fontWeight:500, color:"rgba(255,255,255,0.36)", marginTop:16, letterSpacing:"0.12em", fontFamily:FM }}>LOADING</div>
        </div>
        <style>{`@keyframes pulse{0%,100%{opacity:1}50%{opacity:0.3}}
        @keyframes flameOuter{
          0%{transform:scaleX(1) scaleY(1) skewX(0deg)}
          33%{transform:scaleX(0.96) scaleY(1.04) skewX(-1.5deg)}
          66%{transform:scaleX(1.03) scaleY(0.97) skewX(1deg)}
          100%{transform:scaleX(0.98) scaleY(1.02) skewX(-0.5deg)}
        }
        @keyframes flameLeft{
          0%{transform:scaleX(1) rotate(-1deg);opacity:0.65}
          50%{transform:scaleX(0.9) rotate(-3deg);opacity:0.5}
          100%{transform:scaleX(1.05) rotate(0deg);opacity:0.7}
        }
        @keyframes flameRight{
          0%{transform:scaleX(1) rotate(1deg);opacity:0.55}
          50%{transform:scaleX(1.1) rotate(3deg);opacity:0.7}
          100%{transform:scaleX(0.92) rotate(0deg);opacity:0.45}
        }
        @keyframes flameCore{
          0%{transform:scaleY(1) scaleX(1);opacity:0.85}
          50%{transform:scaleY(1.06) scaleX(0.92);opacity:1}
          100%{transform:scaleY(0.96) scaleX(1.04);opacity:0.75}
        }
        @keyframes flameBase{
          0%{rx:9;opacity:0.25}50%{rx:11;opacity:0.35}100%{rx:8;opacity:0.2}
        }
        @keyframes loadBar{
          0%{width:0%;margin-left:0}
          50%{width:70%;margin-left:15%}
          100%{width:0%;margin-left:100%}
        }
        @keyframes orbFloat1{0%,100%{transform:translate(0,0) scale(1)}25%{transform:translate(10px,-15px) scale(1.05)}50%{transform:translate(-5px,-25px) scale(0.95)}75%{transform:translate(-15px,-10px) scale(1.02)}}
        @keyframes orbFloat2{0%,100%{transform:translate(0,0) scale(1)}33%{transform:translate(-12px,10px) scale(0.97)}66%{transform:translate(8px,-8px) scale(1.04)}}
        `}</style>
      </div>
    );
  }

  if (!user) return <PublicLandingPage />;

  const handleSignOut = () => {
    // FIX #3: clear picks so they don't bleed into the next user's session
    setPicks({});
    setUserLeagues([]);
    setBalance(0);
    setLeaguesLoaded(false);
    // Clear persisted cache — next user's first paint should NOT show this user's data
    try {
      localStorage.removeItem("torch_cache_uid");
      localStorage.removeItem("torch_cache_balance");
      localStorage.removeItem("torch_cache_leagues");
    } catch {}
    signOut(auth);
  };

  const handleDeleteAccount = async () => {
    if (!user) return;
    setDeleteLoading(true);
    setDeleteError("");
    try {
      const { getFirestore, doc, deleteDoc, collection, getDocs, updateDoc, arrayRemove } = await import("firebase/firestore");
      const db = getFirestore(app);

      // Clean up Firestore user data
      const cleanups = [
        deleteDoc(doc(db, "users", user.uid)).catch(() => {}),
        deleteDoc(doc(db, "stats", user.uid)).catch(() => {}),
        deleteDoc(doc(db, "balances", user.uid)).catch(() => {}),
        deleteDoc(doc(db, "fcmTokens", user.uid)).catch(() => {}),
      ];

      // Remove user from all league memberIds arrays
      try {
        for (const league of userLeagues) {
          cleanups.push(
            updateDoc(doc(db, "leagues", league.id), {
              memberIds: arrayRemove(user.uid),
            }).catch(() => {})
          );
          // Also clean up the member subdocument.
          // SERVER-OWNED-LOCK (fix 4.1 step 3): the rules only allow deleting
          // your own member doc together with a matching formerMembers record
          // in the same batch (so self-deletion can't reset a balance on
          // rejoin). Account deletion writes that record, then deletes.
          cleanups.push((async () => {
            const { getDoc, writeBatch } = await import("firebase/firestore");
            const mRef = doc(db, "leagues", league.id, "members", user.uid);
            const [mSnap, lSnap] = await Promise.all([getDoc(mRef), getDoc(doc(db, "leagues", league.id))]);
            if (!mSnap.exists()) return;
            const m = mSnap.data() || {};
            const season = (Array.isArray(lSnap.data()?.previousSeasons) ? lSnap.data().previousSeasons.length : 0) + 1;
            const b = writeBatch(db);
            b.set(doc(db, "leagues", league.id, "formerMembers", user.uid), {
              username: m.username || "", balance: m.balance ?? 0, wins: m.wins ?? 0, losses: m.losses ?? 0,
              streak: m.streak ?? 0, season, leftAt: new Date(), accountDeleted: true,
            });
            b.delete(mRef);
            await b.commit();
          })().catch(() => {}));
        }
      } catch {}

      // Delete user's picks subcollection
      try {
        const picksSnap = await getDocs(collection(db, "picks", user.uid, "userPicks"));
        picksSnap.forEach(d => cleanups.push(deleteDoc(d.ref).catch(() => {})));
      } catch {}

      await Promise.all(cleanups);

      // Delete the Firebase Auth user
      // This may throw "requires-recent-login" — handle with reauth
      try {
        await deleteUser(user);
      } catch (e) {
        if (e.code === "auth/requires-recent-login") {
          // Try reauth based on provider
          const isGoogle = user.providerData?.some(p => p.providerId === "google.com");
          if (isGoogle) {
            try {
              await reauthenticateWithPopup(user, googleProvider);
              await deleteUser(user);
            } catch (e2) {
              setDeleteError("Please sign out and sign back in, then try deleting again.");
              setDeleteLoading(false);
              return;
            }
          } else {
            // Email/password user — need password
            if (!deletePassword) {
              setDeleteError("Enter your password to confirm deletion.");
              setDeleteLoading(false);
              return;
            }
            try {
              const cred = EmailAuthProvider.credential(user.email, deletePassword);
              await reauthenticateWithCredential(user, cred);
              await deleteUser(user);
            } catch (e2) {
              setDeleteError(e2.code === "auth/wrong-password" || e2.code === "auth/invalid-credential"
                ? "Incorrect password. Please try again."
                : "Deletion failed. Sign out, sign back in, and try again.");
              setDeleteLoading(false);
              return;
            }
          }
        } else {
          throw e;
        }
      }

      // Success — clear local state
      setPicks({});
      setUserLeagues([]);
      setBalance(0);
      setDeleteAccountModal(false);
      window.torchToast?.("Account deleted", "success");
    } catch (e) {
      console.error("[Torch] Account deletion failed:", e);
      setDeleteError("Something went wrong. Please try again.");
    } finally {
      setDeleteLoading(false);
    }
  };
  const pages = { Pulse:PulsePage, Den:LeaguesPage, Games:GamesPage, Picks:MyPicksPage, Profile:ProfilePage, FAQ:FAQPage };
  const PageComponent = pages[activePage] || PulsePage;

  // ── Wall gate — practice complete, no leagues, no pending picks ─────────
  const showPracticeWall = (
    !!user &&
    leaguesLoaded &&
    userLeagues.length === 0 &&
    (balance ?? 0) === 0 &&
    practicePendingCount === 0
  );
  if (showPracticeWall) {
    return (
      <ErrorBoundary>
        <ToastContainer />
        <ConfirmDialog />
        <PracticeCompleteWall
          userStats={userStats}
          onSignOut={handleSignOut}
          onCreateLeague={() => {
            try {
              localStorage.removeItem("torch_active_league");
              localStorage.setItem("torch_den_intent", "create");
            } catch {}
            setActivePagePersist("Den");
          }}
          onJoinLeague={() => {
            try {
              localStorage.removeItem("torch_active_league");
              localStorage.setItem("torch_den_intent", "join");
            } catch {}
            setActivePagePersist("Den");
          }}
        />
      </ErrorBoundary>
    );
  }


  return (
    <ErrorBoundary>
    <div id="torch-root" style={{ background:T.bg, minHeight:"100vh", color:T.ink, fontFamily:FB }}>
      <PullToRefreshIndicator pullDistance={pullDistance} refreshing={refreshing} />
      <OfflineBanner />
      <ToastContainer />
      <ConfirmDialog />

      {showOnboarding && (
        <OnboardingOverlay
          onDone={() => {
            setShowOnboarding(false);
            if (user?.uid) try { localStorage.setItem(`torch_onboarded_${user.uid}`, "1"); } catch {}
          }}
          onGoToPick={() => setActivePagePersist("Games")}
          onGoToLeague={() => setActivePagePersist("Den")}
        />
      )}

      <TopNav
        activePage={activePage} setActivePage={setActivePagePersist}
        pickCount={pickCount} setSlipOpen={setSlipOpen}
        onSignOut={handleSignOut} user={user} userStats={userStats}
        userLeagues={userLeagues} balance={balance}
        pendingPickCount={pendingPickCount}
      />

      <main className="pageWrapper" style={{ paddingBottom:"calc(64px + env(safe-area-inset-bottom, 0px))", position:"relative", zIndex:1 }}>
        {/* MOUNT-ONCE ROUTER: render each visited page exactly once and toggle
            visibility via CSS. Previously this used `<div key={activePage}>`
            which forced a full unmount/remount on every tab switch — causing
            all useEffects and Firestore listeners (95 of them in LeaguesPage
            alone) to tear down and rebuild. Now switches are essentially free.
            Inactive pages keep their state, scroll position, and live data. */}
        {Array.from(visitedPages).map(pageKey => {
          const PageComp = pages[pageKey];
          if (!PageComp) return null;
          const isActive = pageKey === activePage;
          return (
            <div
              key={pageKey}
              style={{
                display: isActive ? "block" : "none",
                // animation-name toggles none → pageEnter each time this page
                // becomes active, restarting a ~150ms fade that masks the
                // hard display cut and the instant scroll restore. Opacity-
                // only, so it composites on the GPU (WKWebView-safe). While
                // the page stays active across re-renders the value doesn't
                // change, so it never re-fires mid-use.
                animation: isActive ? "pageEnter 0.15s ease-out" : "none",
              }}
              aria-hidden={!isActive}
            >
              <StaticWhenHidden active={isActive}>
              <PageComp
                picks={picks} setPicks={setPicks} onSignOut={handleSignOut}
                onDeleteAccount={() => setDeleteAccountModal(true)}
                user={user} userStats={userStats} refreshStats={refreshStats}
                onGamesLoaded={setGames} setActivePage={setActivePagePersist}
                activePage={activePage}
                games={games}
                balance={balance} setBalance={setBalance}
                onBalanceChange={setBalance}
                onLeagueDeleted={handleLeagueDeleted}
                refreshLeagues={refreshLeagues}
                userLeagues={userLeagues}
                leaguesLoaded={leaguesLoaded}
                onViewProfile={handleViewProfile}
                picksRefreshKey={picksRefreshKey}
                onAfterSettle={handleAfterSettle}
                setSlipOpen={setSlipOpen}
                onPendingCountChange={setPendingPickCount}
                onOpenTorchModal={async (leagueId, cachedStandings, leagueMinWager = 0) => {
                  // Always fetch fresh standings — the cached array only live-updates
                  // the current user's doc (via onSnapshot), so other members' balances
                  // can be stale (e.g. showing 0 when they actually have 741 pts).
                  let fresh = cachedStandings;
                  try {
                    fresh = await loadLeagueStandings(leagueId, (userLeagues || []).find(l => l.id === leagueId)?.scoringMode || "wager");
                  } catch (e) {
                    console.warn("[Torch] Fresh standings fetch failed, using cache:", e.message);
                  }
                  setTorchModalData({ leagueId, standings: fresh, leagueMinWager });
                  setTorchModalOpen(true);
                }}
                onOpenTorchInfo={(leagueId, cachedStandings, leagueMinWager = 0) => {
                  // Stash context in case the user taps "Try It" — we can transition
                  // straight into the request modal without re-fetching.
                  setTorchInfoData({ leagueId, standings: cachedStandings || [], leagueMinWager });
                  setTorchInfoOpen(true);
                }}
                leagueResetKey={leagueResetKey}
                nav={nav}
              />
              </StaticWhenHidden>
            </div>
          );
        })}
      </main>

      {/* WC-only mode: when the user is exclusively in World Cup bracket pools
          (no all-sports leagues), the Games + Picks tabs are useless to them
          (no league to score against), so we collapse the bottom nav to just
          Home + Leagues. The auto-redirect below covers the edge case where
          the user is currently ON one of the hidden tabs (e.g. they leave
          their last non-WC league mid-session) — bounce them to Home. */}
      {(() => {
        const wcOnly = userLeagues.length > 0 && userLeagues.every(isWorldCup2026League);
        if (wcOnly && (activePage === "Games" || activePage === "Picks")) {
          // Defer the redirect to next tick so we don't setState during render.
          setTimeout(() => setActivePagePersist("Pulse"), 0);
        }
        return (
          <BottomNav
            activePage={activePage}
            setActivePage={setActivePagePersist}
            pickCount={pickCount}
            setSlipOpen={setSlipOpen}
            onResetLeagues={() => setLeagueResetKey(k => k + 1)}
            hasLiveGames={games.some(g => g.isLive && !g.completed)}
            wcOnly={wcOnly}
          />
        );
      })()}

      {slipOpen && (
        <PickSlipDrawer
          picks={picks} setPicks={setPicks}
          onClose={() => {
            setSlipOpen(false);
            // Exit any active Pass-the-Torch flow when the slip is dismissed.
            // Without this, torchDraft persists across slip open/close cycles
            // and the next slip open is still locked into torch mode -- the
            // wager input stays capped at the torch amount even when the user
            // has way more in their league balance. Idempotent: clearing a
            // null draft is a no-op. Successful submits already call
            // onTorchComplete which nulls this same state, so this doesn't
            // double-clear; it just covers the cancel/back path.
            setTorchDraft(null);
          }}
          user={user} games={games} userStats={userStats}
          balance={balance} onBalanceChange={setBalance}
          userLeagues={userLeagues} onLeagueBalanceChange={handleLeagueBalanceChange}
          refreshLeagues={refreshLeagues}
          torchDraft={torchDraft}
          onTorchComplete={() => setTorchDraft(null)}
          onAfterSubmit={(leagueId) => {
            // Defer past the sheet's exit animation. These two calls re-render
            // every mounted page (mount-once router + fresh prop bag, no page
            // memoization) and kick off LeaguesPage's standings/activity/inbox
            // fetches — landing that on the dismissal frames janks the close
            // on iOS. 300ms puts the cascade just after the sheet is gone.
            setTimeout(() => {
              setPicksRefreshKey(k => k + 1);
              refreshLeagues?.();
            }, 300);
          }}
        />
      )}

      {/* Pass the Torch modal — rendered at ROOT level to avoid backdrop-filter clipping */}
      {torchModalOpen && torchModalData && (
        <TorchRequestModal
          onClose={() => { setTorchModalOpen(false); setTorchModalData(null); }}
          user={user}
          leagueId={torchModalData.leagueId}
          leagueMinWager={torchModalData.leagueMinWager || 0}
          standings={torchModalData.standings || []}
          myBalance={(() => {
            // Look up the current user's balance in the league standings so offer-mode
            // can bound the wager by what we actually have to spend.
            const me = (torchModalData.standings || []).find(m => m.id === user?.uid);
            return me?.balance || 0;
          })()}
          onGoToGames={() => setActivePagePersist("Games")}
          onSubmit={(draft) => {
            setTorchDraft(draft);
            setTorchModalOpen(false);
            setTorchModalData(null);
            const verb = draft.offerMode ? "spotting" : "splitting";
            const counterpartName = draft.offerMode ? draft.recipientName : draft.lenderName;
            window.torchToast?.(`Now pick a game — ${verb} ${counterpartName}'s pick is ready to lock in!`, "success");
          }}
        />
      )}

      {/* Torch Info modal — how-it-works explainer */}
      {torchInfoOpen && (
        <TorchInfoModal
          onClose={() => { setTorchInfoOpen(false); }}
          onStart={torchInfoData ? async () => {
            // Transition straight from info → request. Fetch fresh standings
            // for the request modal (same as onOpenTorchModal does).
            setTorchInfoOpen(false);
            let fresh = torchInfoData.standings;
            try { fresh = await loadLeagueStandings(torchInfoData.leagueId, (userLeagues || []).find(l => l.id === torchInfoData.leagueId)?.scoringMode || "wager"); } catch {}
            setTorchModalData({ leagueId: torchInfoData.leagueId, standings: fresh, leagueMinWager: torchInfoData.leagueMinWager || 0 });
            setTorchModalOpen(true);
          } : undefined}
        />
      )}

      {/* Torch draft banner — shown on Games page when user has a pending torch pick */}
      {torchDraft && activePage === "Games" && (
        <div style={{
          position:"fixed", top:56, left:0, right:0, zIndex:45,
          background: torchDraft.offerMode
            ? "linear-gradient(135deg, rgba(217,164,65,0.12), rgba(217,164,65,0.06))"
            : "linear-gradient(135deg, rgba(255,107,61,0.12), rgba(255,107,61,0.06))",
          borderBottom: torchDraft.offerMode
            ? "1px solid rgba(217,164,65,0.2)"
            : "1px solid rgba(255,107,61,0.2)",
          padding:"10px 16px",
          display:"flex", alignItems:"center", justifyContent:"space-between",
          animation:"fadeUp 0.2s ease",
        }}>
          <div style={{ display:"flex", alignItems:"center", gap:8 }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={torchDraft.offerMode ? T.gold : T.coral} strokeWidth="1.8">
              <path d="M12 22c-4.97 0-9-2.69-9-6v-.18C4.32 12.47 7.6 10 8 6c.02-.17.17-.3.34-.28C10.09 5.96 12 8 12 8s1.91-2.04 3.66-2.28c.17-.02.32.11.34.28.4 4 3.68 6.47 5 9.82V16c0 3.31-4.03 6-9 6z"/>
            </svg>
            <div>
              <div style={{ fontSize:12, fontWeight:700, color: torchDraft.offerMode ? T.gold : T.coral }}>
                {torchDraft.offerMode ? "Offering a Torch" : "Splitting a Pick"}
              </div>
              <div style={{ fontSize:11, color:"rgba(255,255,255,0.4)" }}>
                {torchDraft.amount} pts ·
                {torchDraft.offerMode
                  ? ` spotting ${torchDraft.recipientName}`
                  : ` with ${torchDraft.lenderName}`}
              </div>
            </div>
          </div>
          <button onClick={() => setTorchDraft(null)} style={{
            background:"none", border:"none", cursor:"pointer", color:"rgba(255,255,255,0.42)", fontSize:11, fontWeight:600,
          }}>Cancel</button>
        </div>
      )}

      {/* User Profile Modal — rendered from leaderboard taps */}
      {viewingProfile && (
        <UserProfileModal
          profileUid={viewingProfile.uid}
          profileUsername={viewingProfile.username}
          currentUser={user}
          viewerLeagues={userLeagues}
          onClose={() => setViewingProfile(null)}
        />
      )}

      {/* ── Delete Account Modal ── */}
      {deleteAccountModal && (
        <div onClick={() => { if (!deleteLoading) { setDeleteAccountModal(false); setDeleteError(""); setDeletePassword(""); }}} style={{
          position:"fixed", inset:0, zIndex:200, background:"rgba(0,0,0,0.7)",
          backdropFilter:"blur(20px)", display:"flex", alignItems:"center", justifyContent:"center",
          padding:20, animation:"fadeUp 0.2s ease",
        }}>
          <div onClick={e => e.stopPropagation()} style={{
            width:"100%", maxWidth:400, background:T.card, borderRadius:20,
            border:`1px solid ${T.border}`, padding:28,
            boxShadow:"0 24px 80px rgba(0,0,0,0.6)",
          }}>
            <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:20 }}>
              <div style={{
                width:44, height:44, borderRadius:14, flexShrink:0,
                background:"rgba(239,68,68,0.1)", border:"1px solid rgba(239,68,68,0.2)",
                display:"flex", alignItems:"center", justifyContent:"center",
              }}>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#EF4444" strokeWidth="2" strokeLinecap="round">
                  <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
                </svg>
              </div>
              <div>
                <div style={{ fontSize:18, fontWeight:700, color:"#fff" }}>Delete account</div>
                <div style={{ fontSize:12, color:"rgba(255,255,255,0.35)", marginTop:2 }}>This cannot be undone</div>
              </div>
            </div>

            <div style={{ fontSize:13, color:"rgba(255,255,255,0.5)", lineHeight:1.6, marginBottom:20 }}>
              This will permanently delete your account, all picks, stats, league memberships, and balance. Your data cannot be recovered.
            </div>

            {/* Password field for email/password users */}
            {user?.providerData?.some(p => p.providerId === "password") && (
              <div style={{ marginBottom:16 }}>
                <div style={{ fontSize:12, fontWeight:600, color:"rgba(255,255,255,0.4)", marginBottom:6 }}>Confirm your password</div>
                <input
                  type="password"
                  value={deletePassword}
                  onChange={e => setDeletePassword(e.target.value)}
                  placeholder="Enter password"
                  style={{
                    width:"100%", padding:"12px 14px", borderRadius:12,
                    background:"rgba(255,255,255,0.04)", border:`1px solid ${T.border}`,
                    color:"#fff", fontSize:14, outline:"none",
                  }}
                />
              </div>
            )}

            {deleteError && (
              <div style={{
                padding:"10px 14px", borderRadius:10, marginBottom:16,
                background:"rgba(239,68,68,0.08)", border:"1px solid rgba(239,68,68,0.15)",
                fontSize:12, color:"#EF4444", lineHeight:1.4,
              }}>{deleteError}</div>
            )}

            <div style={{ display:"flex", gap:10 }}>
              <button
                onClick={() => { setDeleteAccountModal(false); setDeleteError(""); setDeletePassword(""); }}
                disabled={deleteLoading}
                style={{
                  flex:1, padding:"12px", borderRadius:12, cursor:"pointer",
                  background:"rgba(255,255,255,0.06)", border:`1px solid ${T.border}`,
                  color:"rgba(255,255,255,0.6)", fontSize:13, fontWeight:600,
                }}
              >Cancel</button>
              <button
                onClick={handleDeleteAccount}
                disabled={deleteLoading}
                style={{
                  flex:1, padding:"12px", borderRadius:12, cursor:"pointer",
                  background:"rgba(239,68,68,0.15)", border:"1px solid rgba(239,68,68,0.3)",
                  color:"#EF4444", fontSize:13, fontWeight:700,
                  opacity: deleteLoading ? 0.5 : 1,
                }}
              >{deleteLoading ? "Deleting…" : "Delete my account"}</button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        *{box-sizing:border-box;margin:0;padding:0;-webkit-tap-highlight-color:transparent;}
        /* ── White-bottom-strip fix ──
           html had no background, so on iOS WKWebView, the default white
           leaks through at the bottom of the viewport (below the bottom nav,
           in the safe-area inset region) when content doesn't fill 100dvh.
           Setting html bg + min-height + #root anchor closes that gap. */
        html{background:${T.bg};min-height:100%;scroll-behavior:smooth;-webkit-text-size-adjust:100%;-webkit-overflow-scrolling:touch;}
        body{min-height:100vh;min-height:100dvh;touch-action:pan-y pan-x;background:${T.bg};color:${T.ink};font-family:${FB};overscroll-behavior-y:none;-webkit-font-smoothing:antialiased;-moz-osx-font-smoothing:grayscale;-webkit-user-select:none;user-select:none;scrollbar-width:thin;scrollbar-color:rgba(255,255,255,0.12) transparent;}
        #root{min-height:100vh;min-height:100dvh;background:${T.bg};}
        p,span,div,h1,h2,h3,h4{-webkit-user-select:text;user-select:text;}
        input,textarea{-webkit-user-select:auto;user-select:auto;}
        [style*="overflow-y"]{-webkit-overflow-scrolling:touch;-ms-overflow-style:-ms-autohiding-scrollbar;}
        [style*="overflow-x"]{-webkit-overflow-scrolling:touch;}
        .scroll-touch{-webkit-overflow-scrolling:touch !important;overflow-y:auto !important;overscroll-behavior-y:contain;}
        ::-webkit-scrollbar{width:4px;height:0;}
        ::-webkit-scrollbar-track{background:transparent;}
        ::-webkit-scrollbar-thumb{background:rgba(255,255,255,0.12);border-radius:4px;}
        ::-webkit-scrollbar-thumb:hover{background:rgba(255,255,255,0.22);}
        .hide-scrollbar::-webkit-scrollbar{display:none;}
        .hide-scrollbar{scrollbar-width:none;}
        .scroll-fade{position:relative;}
        .scroll-fade::after{content:'';position:absolute;bottom:0;left:0;right:0;height:32px;background:linear-gradient(transparent,var(--scroll-fade-bg,#06080E));pointer-events:none;z-index:2;border-radius:0 0 20px 20px;}
        button{font-family:${FB};touch-action:manipulation;-webkit-font-smoothing:antialiased;cursor:pointer;-webkit-user-select:none;user-select:none;}
        input,select,textarea{font-family:${FB};font-size:16px!important;}
        input:-webkit-autofill,input:-webkit-autofill:hover,input:-webkit-autofill:focus{
          -webkit-box-shadow:0 0 0px 1000px ${T.card} inset!important;
          -webkit-text-fill-color:${T.ink}!important;caret-color:${T.ink};
          transition:background-color 5000s ease-in-out 0s;
        }
        .desktopNav{display:flex;}
        @media(max-width:600px){.desktopNav{display:none!important;}}
        .bottomNav{display:flex;}
        @media(min-width:601px){.bottomNav{display:none!important;}}
        @media(min-width:601px){.pageWrapper{padding-bottom:0!important;}}
        @media(min-width:601px){.mobileNavSpacer{display:none!important;}}

        /* ── iOS keyboard-open layout (iMessage-style) ──
           When body.keyboard-open is set by the Capacitor Keyboard listener,
           hide the bottom nav + Make a Pick CTA and snap the composer to bottom:0.
           These overrides use !important to win against inline styles. */
        body.keyboard-open .bottomNav{display:none!important;}
        body.keyboard-open .den-make-pick{display:none!important;}
        body.keyboard-open .den-composer{
          bottom:0!important;
          padding-bottom:6px!important;
        }
        /* Tighten feed bottom padding when nav is hidden so messages sit just above input */
        body.keyboard-open .den-feed{padding-bottom:88px!important;}

        /* ── Modal-open state ─────────────────────────────────────
           When useModalOpen() is active, body.modal-open is set.
           Slide the bottom nav off-screen so any bottom-anchored sheet
           gets the entire viewport, with no chance of clipping the
           submit/CTA button against the nav.
           Also hide the floating "Review Slip" button — it would visually
           collide with whatever sheet is open. */
        body.modal-open .bottomNav{
          transform:translateY(110%);
          opacity:0;
          pointer-events:none;
        }
        body.modal-open .floating-pick-cta{
          opacity:0;
          pointer-events:none;
        }

        @keyframes fadeUp{from{opacity:0;transform:translateY(18px) scale(0.97);}to{opacity:1;transform:translateY(0) scale(1);}}
        @keyframes pageIn{from{opacity:0;}to{opacity:1;}}
        @keyframes fadeIn{from{opacity:0;}to{opacity:1;}}
        @keyframes spin{from{transform:rotate(0deg);}to{transform:rotate(360deg);}}
        /* Checkmark morph for submit button success state. checkPop bounces the
           SVG in (slight overshoot for tactile feel); checkDraw strokes the
           polyline like a real pen-stroke instead of just appearing. */
        @keyframes checkPop{0%{transform:scale(0);opacity:0;}55%{transform:scale(1.18);opacity:1;}100%{transform:scale(1);opacity:1;}}
        @keyframes checkDraw{to{stroke-dashoffset:0;}}
        @keyframes popIn{from{opacity:0;transform:scale(0.85);}to{opacity:1;transform:scale(1);}}
        @keyframes slideUp{from{transform:translateX(-50%) translateY(100%);}to{transform:translateX(-50%) translateY(0);}}
        /* PickSlipDrawer enter/exit. Uniquely named because @keyframes slideUp
           is defined twice in this block and the later (translateY(20px), no
           translateX) definition wins document-wide — see the sheet's style
           comment. Exit is transform-only; the backdrop owns the fade. */
        @keyframes slipEnterUp{from{transform:translateX(-50%) translateY(100%);}to{transform:translateX(-50%) translateY(0);}}
        @keyframes slipExitDown{from{transform:translateX(-50%) translateY(0);}to{transform:translateX(-50%) translateY(110%);}}
        @keyframes slipFadeOut{from{opacity:1;}to{opacity:0;}}
        /* Sheet-exit system keyframes (see useSheetClose). sheetEnterUp is
           the non-centered bottom-sheet entry — the collided slideUp only
           rose 20px, so sheets weren't actually sliding up from below. */
        @keyframes sheetEnterUp{from{transform:translateY(100%);}to{transform:translateY(0);}}
        @keyframes sheetExitDown{from{transform:translateY(0);}to{transform:translateY(110%);}}
        @keyframes fadeExitDown{from{opacity:1;transform:translateY(0);}to{opacity:0;transform:translateY(14px);}}
        /* Tab-switch enter fade for the mount-once router pages. */
        @keyframes pageEnter{from{opacity:0;}to{opacity:1;}}
        @keyframes pulse{0%,100%{opacity:1;}50%{opacity:0.25;}}
        /* Subtle shimmer for skeleton placeholders while data loads. The
           opacity oscillation is gentle (1 → 0.55) so it reads as "loading,
           not broken." Each card uses its own animation-delay for stagger. */
        @keyframes skeletonPulse{0%,100%{opacity:1;}50%{opacity:0.55;}}
        /* Fresh-settled reveal: card fades up + scales slightly + lifts the
           shadow over 700ms. Slightly more drama than the standard fadeUp so
           the eye notices "this one's new." */
        @keyframes settledReveal{
          0%{opacity:0;transform:translateY(14px) scale(0.97);}
          60%{opacity:1;transform:translateY(-2px) scale(1.01);}
          100%{opacity:1;transform:translateY(0) scale(1);}
        }
        /* Points "ka-ching" pop on fresh wins. Fires AFTER the card reveal so
           the eye lands on the number last. Brief overshoot for tactile feel. */
        @keyframes pointsPop{
          0%{opacity:0;transform:scale(0.5);}
          55%{opacity:1;transform:scale(1.18);}
          100%{opacity:1;transform:scale(1);}
        }
        @keyframes shine{0%{transform:translateX(-100%);}100%{transform:translateX(200%);}}
        @keyframes glowPulse{0%,100%{box-shadow:0 0 0 0 rgba(255,107,61,0.5);}50%{box-shadow:0 0 0 12px rgba(255,107,61,0);}}
        @keyframes shimmer{0%{background-position:-200% 0;}100%{background-position:200% 0;}}
        @keyframes liveGlow{0%,100%{box-shadow:0 0 8px rgba(74,222,128,0.2);}50%{box-shadow:0 0 20px rgba(74,222,128,0.4), 0 0 40px rgba(74,222,128,0.1);}}
        @keyframes neonFlicker{0%,100%{opacity:1;}92%{opacity:1;}93%{opacity:0.7;}94%{opacity:1;}96%{opacity:0.8;}97%{opacity:1;}}
        @keyframes float{0%,100%{transform:translateY(0) rotate(0deg);}33%{transform:translateY(-10px) rotate(0.5deg);}66%{transform:translateY(-4px) rotate(-0.3deg);}}
        @keyframes gradientShift{0%{background-position:0% 50%;}50%{background-position:100% 50%;}100%{background-position:0% 50%;}}
        @keyframes borderGlow{0%,100%{border-color:rgba(255,107,61,0.1);}50%{border-color:rgba(255,107,61,0.45);}}
        @keyframes countUp{from{opacity:0;transform:translateY(12px) scale(0.9);}to{opacity:1;transform:translateY(0) scale(1);}}
        @keyframes orbFloat1{0%,100%{transform:translate(0,0) scale(1);}25%{transform:translate(20px,-25px) scale(1.1);}50%{transform:translate(-10px,-40px) scale(0.92);}75%{transform:translate(-25px,-15px) scale(1.05);}}
        @keyframes orbFloat2{0%,100%{transform:translate(0,0) scale(1);}33%{transform:translate(-18px,15px) scale(0.94);}66%{transform:translate(14px,-14px) scale(1.08);}}
        @keyframes orbFloat3{0%,100%{transform:translate(0,0) scale(1) rotate(0deg);}50%{transform:translate(12px,-20px) scale(1.06) rotate(2deg);}}
        @keyframes scanline{from{top:-50%;}to{top:150%;}}
        @keyframes textGlow{0%,100%{text-shadow:0 0 30px rgba(255,107,61,0.2),0 0 60px rgba(255,107,61,0.1);}50%{text-shadow:0 0 60px rgba(255,107,61,0.5),0 0 120px rgba(255,107,61,0.2);}}
        @keyframes breathe{0%,100%{opacity:0.3;transform:scale(1);}50%{opacity:0.7;transform:scale(1.02);}}
        @keyframes haloExpand{0%{transform:scale(0.8);opacity:0.5;}100%{transform:scale(2);opacity:0;}}
        @keyframes winPop{0%{transform:scale(0.85);opacity:0}50%{transform:scale(1.03)}100%{transform:scale(1);opacity:1}}
        @keyframes borderSpin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}

        @keyframes scoreFlash{0%{transform:scale(1);color:inherit}15%{transform:scale(1.35);color:#fff;text-shadow:0 0 30px rgba(255,255,255,0.8), 0 0 60px rgba(0,212,255,0.3)}100%{transform:scale(1);color:inherit;text-shadow:none}}
        @keyframes sonarPing{0%{transform:scale(1);opacity:1}100%{transform:scale(6);opacity:0}}
        @keyframes winGlow{0%{box-shadow:0 0 16px rgba(74,222,128,0.35), 0 0 40px rgba(74,222,128,0.12)}50%{box-shadow:0 0 36px rgba(74,222,128,0.65), 0 0 80px rgba(74,222,128,0.22), 0 0 120px rgba(74,222,128,0.06)}100%{box-shadow:0 0 16px rgba(74,222,128,0.35), 0 0 40px rgba(74,222,128,0.12)}}
        @keyframes loseFlash{0%{opacity:1}25%{opacity:0.7}50%{opacity:1}100%{opacity:1}}
        @keyframes meshDrift{0%{background-position:0% 50%}50%{background-position:100% 50%}100%{background-position:0% 50%}}
        @keyframes countTick{0%{transform:translateY(8px);opacity:0}100%{transform:translateY(0);opacity:1}}
        @keyframes flameBorder{0%{border-color:rgba(255,107,61,0.3)}33%{border-color:rgba(255,215,0,0.4)}66%{border-color:rgba(255,107,61,0.5)}100%{border-color:rgba(255,107,61,0.3)}}
        @keyframes sparkBurst{0%{transform:scale(0);opacity:1}50%{transform:scale(1.2);opacity:0.8}100%{transform:scale(1.5);opacity:0}}
        @keyframes pickSweep{0%{opacity:0;transform:translateX(-100%)}30%{opacity:1}70%{opacity:1}100%{opacity:0;transform:translateX(100%)}}
        @keyframes pickBounce{0%{transform:scale(1)}30%{transform:scale(0.94)}60%{transform:scale(1.04)}100%{transform:scale(1)}}
        @keyframes countPop{0%{transform:scale(1)}50%{transform:scale(1.35)}100%{transform:scale(1)}}
        /* One-shot radial flash that emits from a PickButton's center on selection.
           Starts with a soft glow filling the button and fades to nothing. The cubic
           easing front-loads the burst so the eye catches the moment, then drains
           gracefully so it doesn't compete with the persistent active state. */
        @keyframes oddsFlash{
          0%{opacity:0;transform:scale(0.5);}
          25%{opacity:1;transform:scale(1.05);}
          100%{opacity:0;transform:scale(1.15);}
        }
        @keyframes loseShake{0%,100%{transform:translateX(0)}20%{transform:translateX(-3px)}40%{transform:translateX(3px)}60%{transform:translateX(-2px)}80%{transform:translateX(2px)}}
        @keyframes shimmer{0%{background-position:200% 0}100%{background-position:-200% 0}}
        @keyframes torchShimmer{0%{transform:translateX(-120%) skewX(-20deg)}60%,100%{transform:translateX(220%) skewX(-20deg)}}
        @keyframes torchFlamePulse{0%,100%{transform:scale(1);box-shadow:0 4px 16px rgba(255,107,61,0.35), 0 0 0 0 rgba(255,107,61,0.25)}50%{transform:scale(1.07);box-shadow:0 6px 22px rgba(255,107,61,0.55), 0 0 0 6px rgba(255,107,61,0)}}
        @keyframes borderGlow{0%,100%{border-color:rgba(255,107,61,0.15)}50%{border-color:rgba(255,107,61,0.35)}}
        @keyframes livePulse{0%,100%{opacity:1}50%{opacity:0.4}}
        @keyframes slideUp{from{transform:translateY(20px);opacity:0}to{transform:translateY(0);opacity:1}}
        @keyframes glowPulse{0%,100%{box-shadow:0 0 8px rgba(74,222,128,0.2)}50%{box-shadow:0 0 20px rgba(74,222,128,0.4)}}
        .game-card{transition:transform 0.15s ease,box-shadow 0.15s ease}
        .game-card:active{transform:scale(0.985) !important}
        .glass{background:rgba(16,18,26,0.65);backdrop-filter:blur(24px) saturate(180%);border:1px solid rgba(255,255,255,0.06)}
        .glass-hover:hover{border-color:rgba(255,255,255,0.12);box-shadow:0 8px 32px rgba(0,0,0,0.4)}
        @keyframes slideIn{from{opacity:0;transform:translateX(-20px);}to{opacity:1;transform:translateX(0);}}

        .game-card{transition:transform 0.35s cubic-bezier(0.34,1.56,0.64,1),box-shadow 0.35s ease,border-color 0.35s ease;}
        .game-card:active{transform:scale(0.98);}
        .game-card:hover{transform:translateY(-6px) scale(1.012);box-shadow:0 20px 50px rgba(0,0,0,0.5),0 0 30px rgba(0,212,255,0.08),0 1px 0 rgba(255,255,255,0.04) inset !important;border-color:rgba(255,255,255,0.10) !important;}

        .glass{background:rgba(12,14,22,0.60);backdrop-filter:blur(40px) saturate(180%);-webkit-backdrop-filter:blur(40px) saturate(180%);border:1px solid rgba(255,255,255,0.06);box-shadow:0 1px 0 rgba(255,255,255,0.03) inset, 0 -1px 0 rgba(0,0,0,0.2) inset, 0 8px 32px rgba(0,0,0,0.25);}
        .glass-hover{transition:all 0.3s cubic-bezier(0.34,1.56,0.64,1);}
        .glass-hover:hover{border-color:rgba(255,255,255,0.10);background:rgba(16,18,28,0.70);transform:translateY(-3px);box-shadow:0 16px 48px rgba(0,0,0,0.4),0 0 24px rgba(0,212,255,0.05),0 1px 0 rgba(255,255,255,0.05) inset;}

        /* ── NEON GLASSMORPHISM ── */
        .neon-card{
          background:rgba(10,12,20,0.55);
          backdrop-filter:blur(40px) saturate(160%);
          -webkit-backdrop-filter:blur(40px) saturate(160%);
          border:1px solid rgba(255,255,255,0.06);
          border-top:1px solid rgba(255,255,255,0.08);
          box-shadow:0 8px 32px rgba(0,0,0,0.3), 0 1px 0 rgba(255,255,255,0.04) inset;
          transition:all 0.3s cubic-bezier(0.34,1.56,0.64,1);
        }
        .neon-card:hover{
          border-color:rgba(255,255,255,0.10);
          box-shadow:0 12px 40px rgba(0,0,0,0.4), 0 0 16px rgba(0,212,255,0.04), 0 1px 0 rgba(255,255,255,0.06) inset;
          transform:translateY(-2px);
        }

        /* ── SUBTLE GLOW TEXT ── */
        .neon-text-cyan{text-shadow:0 0 6px rgba(0,212,255,0.25), 0 0 16px rgba(0,212,255,0.08);}
        .neon-text-orange{text-shadow:0 0 6px rgba(255,107,61,0.30), 0 0 16px rgba(255,107,61,0.10);}
        .neon-text-green{text-shadow:0 0 6px rgba(74,222,128,0.30), 0 0 16px rgba(74,222,128,0.10);}
        .neon-text-magenta{text-shadow:0 0 6px rgba(236,72,153,0.30), 0 0 16px rgba(236,72,153,0.10);}
        .neon-text-gold{text-shadow:0 0 6px rgba(255,215,0,0.30), 0 0 16px rgba(255,215,0,0.10);}

        /* ── GLASS SURFACE PANELS — refined layered glass ── */
        .luma-panel{
          background:rgba(14,14,18,0.50);
          backdrop-filter:blur(40px) saturate(160%);
          -webkit-backdrop-filter:blur(40px) saturate(160%);
          border:1px solid rgba(255,255,255,0.06);
          border-top:1px solid rgba(255,255,255,0.08);
          box-shadow:0 1px 0 rgba(255,255,255,0.03) inset, 0 8px 24px rgba(0,0,0,0.2);
          transition:all 0.3s cubic-bezier(0.34,1.56,0.64,1);
        }
        .luma-panel:hover{
          background:rgba(16,18,28,0.60);
          border-color:rgba(255,255,255,0.10);
          box-shadow:0 1px 0 rgba(255,255,255,0.05) inset, 0 12px 32px rgba(0,0,0,0.3);
          transform:translateY(-2px);
        }
        .luma-bright{
          background:rgba(16,18,28,0.55);
          backdrop-filter:blur(40px) saturate(170%);
          -webkit-backdrop-filter:blur(40px) saturate(170%);
          border:1px solid rgba(255,255,255,0.07);
          border-top:1px solid rgba(255,255,255,0.10);
          box-shadow:0 1px 0 rgba(255,255,255,0.04) inset, 0 8px 24px rgba(0,0,0,0.18);
        }
        .luma-glow{box-shadow:0 0 16px rgba(255,255,255,0.02);}
        @keyframes lumaBreath{0%,100%{box-shadow:0 0 20px rgba(255,255,255,0.06), 0 1px 0 rgba(255,255,255,0.08) inset}50%{box-shadow:0 0 40px rgba(255,255,255,0.12), 0 1px 0 rgba(255,255,255,0.14) inset}}
        @keyframes lumaShine{0%{left:-100%}100%{left:200%}}

        /* ── CYBER BUTTONS ── */
        .btn-neon{
          position:relative;overflow:hidden;
          background:rgba(14,14,18,0.60);
          border:1px solid rgba(255,255,255,0.08);
          box-shadow:0 4px 16px rgba(0,0,0,0.25), 0 1px 0 rgba(255,255,255,0.04) inset;
          transition:all 0.25s cubic-bezier(0.34,1.56,0.64,1);
          backdrop-filter:blur(20px);-webkit-backdrop-filter:blur(20px);
        }
        .btn-neon:hover{
          border-color:rgba(255,255,255,0.14);
          box-shadow:0 8px 24px rgba(0,0,0,0.35), 0 0 16px rgba(0,212,255,0.06), 0 1px 0 rgba(255,255,255,0.06) inset;
          transform:translateY(-2px) scale(1.02);
        }
        .btn-neon::after{
          content:'';position:absolute;top:0;left:-100%;width:60%;height:100%;
          background:linear-gradient(105deg, transparent 20%, rgba(255,255,255,0.06) 50%, transparent 80%);
          transition:left 0.5s ease;
        }
        .btn-neon:hover::after{left:120%;}

        .btn-primary{position:relative;overflow:hidden;transition:all 0.3s cubic-bezier(0.34,1.56,0.64,1);box-shadow:0 6px 24px rgba(255,107,61,0.25),0 1px 0 rgba(255,255,255,0.10) inset;}
        .btn-primary:hover{transform:translateY(-2px) scale(1.02);box-shadow:0 10px 36px rgba(255,107,61,0.35),0 1px 0 rgba(255,255,255,0.12) inset;}
        .btn-primary:active{transform:translateY(0) scale(0.98);}
        .btn-primary::after{content:'';position:absolute;inset:0;background:linear-gradient(105deg,transparent 40%,rgba(255,255,255,0.08) 50%,transparent 60%);background-size:200% 100%;animation:shimmer 3s ease infinite;}

        .btn-glass{transition:all 0.3s cubic-bezier(0.34,1.56,0.64,1);border:1px solid rgba(255,255,255,0.05);background:rgba(255,255,255,0.03);backdrop-filter:blur(16px);-webkit-backdrop-filter:blur(16px);box-shadow:0 1px 0 rgba(255,255,255,0.02) inset;}
        .btn-glass:hover{border-color:rgba(255,255,255,0.10);background:rgba(255,255,255,0.05);transform:translateY(-2px);box-shadow:0 8px 24px rgba(0,0,0,0.25),0 1px 0 rgba(255,255,255,0.04) inset;}

        /* ── Universal button press feedback ──
           Every <button> in the app gets a subtle scale-on-press unless it
           opts out with .no-press. Adds tactile feel without changing layouts.
           Excludes buttons that already have a :active rule (.btn-primary, etc).
           The cubic-bezier overshoots slightly on release for a "bounce back"
           feel that's distinctly human, not robotic linear ease. */
        button{transition:transform 0.12s cubic-bezier(0.32,0.72,0,1.18);}
        button:active:not(.no-press):not(:disabled){transform:scale(0.96);}
        button:disabled{cursor:not-allowed;}

        /* ── SUBTLE BORDER EFFECTS ── */
        @keyframes neonPulse{0%,100%{box-shadow:0 0 12px rgba(0,212,255,0.15)}50%{box-shadow:0 0 24px rgba(0,212,255,0.25)}}
        @keyframes neonBorderCycle{0%{border-color:rgba(255,255,255,0.06)}50%{border-color:rgba(255,255,255,0.12)}100%{border-color:rgba(255,255,255,0.06)}}
        @keyframes holoSweep{0%{left:-100%}100%{left:200%}}
        @keyframes cyberScan{0%{top:-2px;opacity:0}10%{opacity:1}90%{opacity:1}100%{top:calc(100% + 2px);opacity:0}}
        @keyframes neonFlicker{0%,100%{opacity:1}92%{opacity:1}93%{opacity:0.6}94%{opacity:1}96%{opacity:0.8}97%{opacity:1}}
        @keyframes dataFloat{0%{transform:translateY(100vh) scale(0.5);opacity:0}10%{opacity:0.15}90%{opacity:0.15}100%{transform:translateY(-10vh) scale(1);opacity:0}}

        /* ── SUBTLE GRID BG ── */
        .cyber-grid{
          position:fixed;inset:0;pointer-events:none;z-index:0;opacity:0.04;
          background-image:
            linear-gradient(rgba(255,255,255,0.3) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,255,255,0.3) 1px, transparent 1px);
          background-size:60px 60px;
        }

        .stat-card{transition:transform 0.3s cubic-bezier(0.34,1.56,0.64,1),box-shadow 0.3s ease;}
        .stat-card:hover{transform:translateY(-3px);box-shadow:0 12px 36px rgba(0,0,0,0.35);}

        .glow-line{height:1px;background:linear-gradient(90deg,transparent 5%,rgba(255,255,255,0.06) 50%,transparent 95%);}

        .torch-gradient-text{background:linear-gradient(135deg,#FF6B3D 0%,#FF8A65 40%,#FFB74D 60%,#FF8A65 80%,#FF6B3D 100%);background-size:200% auto;-webkit-background-clip:text;-webkit-text-fill-color:transparent;}

        .noise-overlay{position:absolute;inset:0;opacity:0.015;pointer-events:none;mix-blend-mode:overlay;background-image:url("data:image/svg+xml,%3Csvg viewBox='0 0 512 512' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='5' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E");}

        .grid-bg{position:absolute;inset:0;opacity:0.02;pointer-events:none;background-image:linear-gradient(rgba(255,255,255,0.2) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,0.2) 1px,transparent 1px);background-size:36px 36px;}



        .btn-primary{position:relative;overflow:hidden;transition:all 0.3s cubic-bezier(0.34,1.56,0.64,1);box-shadow:0 6px 24px rgba(255,107,61,0.25),0 1px 0 rgba(255,255,255,0.10) inset;}
        .btn-primary:hover{transform:translateY(-2px) scale(1.02);box-shadow:0 10px 36px rgba(255,107,61,0.35),0 1px 0 rgba(255,255,255,0.12) inset;}
        .btn-primary:active{transform:translateY(0) scale(0.98);}
        .btn-primary::after{content:'';position:absolute;inset:0;background:linear-gradient(105deg,transparent 40%,rgba(255,255,255,0.08) 50%,transparent 60%);background-size:200% 100%;animation:shimmer 3s ease infinite;}

        .btn-glass{transition:all 0.3s cubic-bezier(0.34,1.56,0.64,1);border:1px solid rgba(255,255,255,0.05);background:rgba(255,255,255,0.03);box-shadow:0 1px 0 rgba(255,255,255,0.02) inset;}
        .btn-glass:hover{border-color:rgba(255,255,255,0.10);background:rgba(255,255,255,0.05);transform:translateY(-2px);box-shadow:0 8px 24px rgba(0,0,0,0.25),0 1px 0 rgba(255,255,255,0.04) inset;}

      `}</style>
    </div>
    </ErrorBoundary>
  );
}
